import React, { createContext, useContext, useState, useEffect } from 'react';

// Types pour les traductions
export type Language = 'fr' | 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, ...args: any[]) => string;
  dir: 'ltr' | 'rtl';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Dictionnaire de traductions
const translations = {
  fr: {
    'login.title': 'Connexion',
    'login.subtitle': 'Système de Gestion Moderne',
    'login.email': 'Adresse email',
    'login.password': 'Mot de passe',
    'login.agency': 'Code d\'agence',
    'login.connect': 'Se connecter',
    'login.forgotPassword': 'Mot de passe oublié ?',
    'login.resetPassword': 'Réinitialiser le mot de passe',
    'login.resetPasswordTitle': 'Mot de passe oublié',
    'login.resetPasswordSubtitle': 'Entrez votre adresse email pour recevoir un nouveau mot de passe',
    'login.resetPasswordButton': 'Envoyer le nouveau mot de passe',
    'login.backToLogin': 'Retour à la connexion',
    'login.language': 'Langue',
    'login.copyright': '© 2026 BLOBTIC. Tous droits réservés.',
    'login.sso.or': 'OU',
    'login.sso.continueWith': 'Ou continuer avec',
    'login.sso.google': 'Continuer avec Google',
    'login.sso.microsoft': 'Continuer avec Microsoft',
    'login.sso.oidc': 'Continuer avec {0}',
    'login.sso.authenticating': 'Authentification SSO en cours...',
    'login.sso.selectAgencyTitle': 'Sélectionnez votre agence',
    'login.sso.selectAgencyDesc': 'Plusieurs agences sont associées à votre compte. Veuillez sélectionner celle à laquelle vous souhaitez vous connecter :',
    'login.sso.confirmAgency': 'Accéder à l\'agence',
    'login.sso.error': 'Échec de la connexion SSO',
    'login.sso.noAccount': 'Aucun compte BLOBTIC n\'est associé à cette adresse email.',
    'login.sso.backToLogin': 'Retour à la page de connexion',
    'error.email.required': 'L\'email est requis',
    'error.email.invalid': 'Format d\'email invalide',
    'error.password.required': 'Le mot de passe est requis',
    'error.password.minLength': 'Le mot de passe doit contenir au moins 6 caractères',
    'error.agency.required': 'Le code d\'agence est requis',
    'error.agency.minLength': 'Le code d\'agence doit contenir au moins 3 caractères',
    'error.resetPassword.success': 'Un nouveau mot de passe a été envoyé à votre adresse email',
    'error.resetPassword.failed': 'Erreur lors de l\'envoi du nouveau mot de passe',
    'error.resetPassword.userNotFound': 'Aucun utilisateur trouvé avec cette adresse email',
    'zoom.agency.title': 'Sélection d\'agence',
    'zoom.agency.placeholder': 'Cliquez sur le bouton zoom pour sélectionner une agence',
    'zoom.agency.disabled': 'Remplissez d\'abord l\'email et le mot de passe',
    'language.fr': 'Français',
    'language.en': 'English',
    'language.ar': 'العربية',

    // Theme Customizer
    'theme.settings.title': 'Apparence',
    'theme.mode.label': 'Mode',
    'theme.mode.dark': 'Mode Sombre',
    'theme.mode.light': 'Mode Clair',
    'theme.color.label': 'Couleur Principale',
    'common.actions.customize': 'Personnaliser',
    'common.backToList': 'Retour à la liste',
    'common.code': 'Code',
    'common.stage': 'Étape Pipeline',
    'common.address': 'Adresse',
    'common.french': 'Français',
    'common.arabic': 'Arabe',
    'common.english': 'Anglais',
    'common.confirm': 'Confirmation',
    'common.none': 'Aucun',
    'common.autoGenerated': '(Automatique)',
    'common.notes': 'Notes',
    'common.viewAll': 'Voir tout',
    'crm.opportunities.transferredKam': 'Transféré au KAM (Chantier Actif)',
    'crm.opportunities.readyTransferKam': 'Prêt pour passation KAM',
    'CLIENT': 'Client',
    'client': 'Client',
    'PROSPECT': 'Prospect',
    'prospect': 'Prospect',
    'PARTNER': 'Partenaire',
    'partner': 'Partenaire',
    'VENDOR': 'Fournisseur',
    'vendor': 'Fournisseur',
    'LOW': 'Faible',
    'MEDIUM': 'Moyenne',
    'HIGH': 'Élevée',
    'CRITICAL': 'Critique',
    'BLOCKING': 'Bloquante',
    'OPEN': 'Ouverte',
    'INVESTIGATING': 'En instruction',
    'IN_PROGRESS': 'En cours',
    'RESOLVED': 'Résolue',
    'CLOSED': 'Clôturée',
    'DEFAUT_POSE': 'Défaut de pose',
    'DEFAUT_PRODUIT': 'Défaut produit',
    'RETARD_LIVRAISON': 'Retard de livraison',
    'NON_CONFORMITE_VITRAGE': 'Non-conformité vitrage',
    'ACCESSOIRES_MANQUANTS': 'Accessoires manquants',
    'AUTRE': 'Autre',
    'PRESCRIPTEUR': 'Prescripteur',
    'KAM': 'Key Account Manager',
    'PROSPECTION': 'Prospection',
    'PRESCRIPTION_TECHNIQUE': 'Prescription Technique',
    'SUIVI_CHANTIER': 'Suivi de Chantier',
    'REUNION_ETUDES': 'Réunion Études',
    'NEGOCIATION': 'Négociation',
    'SAV_LIVRAISON': 'SAV & Livraison',
    'PLANIFIEE': 'Planifiée',
    'REALISEE': 'Réalisée',
    'ANNULEE': 'Annulée',
    'REPORTEE': 'Reportée',
    'PLANNED': 'Planifiée',
    'COMPLETED': 'Terminée',
    'CANCELLED': 'Annulée',
    'RESCHEDULED': 'Reportée',
    'DRAFT': 'Brouillon',
    'TRANSMIS': 'Transmis',
    'EN_REVISION': 'En révision',
    'INTEGRE_AU_CCTP': 'Intégré au CCTP',
    'VALIDE': 'Validé',
    'REJETE': 'Rejeté',
    'SUBMITTED': 'Soumis',
    'VALIDATED': 'Validé',
    'SIGNED': 'Signé',
    'REJECTED': 'Rejeté',
    'URBANIA': 'Urbania',
    'FUTURA': 'Futura',
    'DOORIA': 'Dooria',
    'MIXTE': 'Mixte',
    'URBANIA_ALU': 'Urbania Alu',
    'FUTURA_PVC': 'Futura PVC',
    'DOORIA_INTERIEUR': 'Dooria Portes Intérieures',
    'MIXTE_ALU_BOIS': 'Mixte Alu-Bois',
    'DEMARRAGE': 'Démarrage',
    'PREPARATION': 'Préparation',
    'EN_COURS': 'En cours',
    'POSE': 'Pose',
    'RECEPTION': 'Réception',
    'CLOTURE': 'Clôturé',
    'EN_PAUSE': 'En pause',
    'ARRETE': 'Arrêté',
    'LIVRE': 'Livré',
    'ON_SCHEDULE': 'Dans les délais',
    'PARTIALLY_DELIVERED': 'Partiellement livré',
    'DELIVERED': 'Intégralement livré',
    'DELAYED': 'En retard',
    'PROMOTION_IMMOBILIERE': 'Promotion Immobilière',
    'PROMOTION': 'Promotion Immobilière',
    'AADL': 'AADL',
    'LPP': 'LPP',
    'LOGEMENT_INDIVIDUEL': 'Logement Individuel',
    'EQUIPEMENT_PUBLIC': 'Équipement Public',
    'TERTIAIRE': 'Tertiaire',
    'PROSPECTING': 'Prospection',
    'QUALIFICATION': 'Qualification',
    'PROPOSAL': 'Proposition / Devis',
    'PROPOSITION_DEVIS': 'Proposition / Devis',
    'NEGOTIATION': 'Négociation',
    'WON': 'Gagné',
    'LOST': 'Perdu',
    'ABANDONNE': 'Abandonné',
    'A_L_ARRET': 'À l\'arrêt',
    'TRANSFERRED': 'Transféré au KAM',
    'PENDING_TRANSFER': 'Prêt pour passation',
    'NON_TRANSFERE': 'Non transféré',
    'TRANSFERE_KAM': 'Transféré au KAM',
    'FAIBLE': 'Faible',
    'MOYENNE': 'Moyenne',
    'ELEVEE': 'Élevée',
    'MOYEN': 'Moyen',
    'ELEVE': 'Élevé',
    'BET': 'Bureau d\'Études (BET)',
    'ARCHITECTE': 'Architecte',
    'PROMOTEUR': 'Promoteur Immobilier',
    'MO': 'Maître d\'Ouvrage (MO)',
    'ENT': 'Entreprise Générale',
    'INSTALLATEUR': 'Menuisier / Installateur',
    'PETITE': 'Petite structure',
    'GRANDE': 'Grande entreprise',
    'CALL': 'Appel',
    'MEETING': 'Rendez-vous',
    'TECH_MEETING': 'Réunion technique',
    'EMAIL': 'Email',
    'VISIT_SHOWROOM': 'Visite Showroom',
    'SITE_SURVEY': 'Visite Chantier',
    'DEMO': 'Démonstration',
    'common.languages': 'Langues',
    'common.actions.userManual': 'Manuel Utilisateur',
    'common.actions.switchApp': 'Changer d\'application',
    'common.default_view_mode': 'Affichage par défaut',
    'common.view_grid': 'Vue Grille',
    'common.view_table': 'Vue Tableau',
    'common.refresh': 'Actualiser',
    'common.refreshed': 'Données actualisées avec succès.',

    // Dialog Titles
    'dialog.changePassword.title': 'Changer le mot de passe',
    'dialog.forgotPassword.title': 'Mot de passe oublié',

    // Change Password Dialog
    'dialog.changePassword.step.email': 'Pour changer votre mot de passe, nous devons d\'abord vérifier votre identité. Saisissez votre adresse email pour recevoir un code de vérification.',
    'dialog.changePassword.step.verify': 'Un code de vérification a été envoyé à {0}. Saisissez ce code pour continuer.',
    'dialog.changePassword.step.new': 'Saisissez votre mot de passe actuel et votre nouveau mot de passe.',
    'dialog.changePassword.success': 'Votre mot de passe a été modifié avec succès !',
    'label.currentPassword': 'Mot de passe actuel',
    'label.newPassword': 'Nouveau mot de passe',
    'label.confirmPassword': 'Confirmer le nouveau mot de passe',
    'label.verificationCode': 'Code de vérification',

    // Forgot Password Dialog
    'dialog.forgotPassword.steps.1': 'Demande de code',
    'dialog.forgotPassword.steps.2': 'Vérification OTP',
    'dialog.forgotPassword.steps.3': 'Nouveau mot de passe',
    'dialog.forgotPassword.step1.desc': 'Entrez votre adresse email pour recevoir un code de vérification.',
    'dialog.forgotPassword.step2.desc': 'Entrez le code de vérification envoyé à votre email.',
    'dialog.forgotPassword.step3.desc': 'Entrez votre nouveau mot de passe.',
    'dialog.forgotPassword.success': 'Votre mot de passe a été réinitialisé avec succès !',

    // Common Actions
    'btn.cancel': 'Annuler',
    'btn.sendCode': 'Envoyer le code',
    'btn.verifyCode': 'Vérifier le code',
    'btn.verify': 'Vérifier',
    'btn.back': 'Retour',
    'btn.changePassword': 'Changer le mot de passe',
    'btn.reset': 'Réinitialiser',
    'btn.close': 'Fermer',

    // Validation & Messages
    'error.email.empty': 'Veuillez saisir votre adresse email',
    'error.otp.send': 'Erreur lors de l\'envoi du code de vérification. Veuillez réessayer.',
    'error.otp.empty': 'Veuillez saisir le code de vérification',
    'error.otp.length': 'Le code OTP doit contenir 6 chiffres',
    'error.password.empty': 'Veuillez saisir votre mot de passe',
    'error.currentPassword.empty': 'Veuillez saisir votre mot de passe actuel',
    'error.newPassword.empty': 'Veuillez saisir votre nouveau mot de passe',
    'error.password.mismatch': 'Les mots de passe ne correspondent pas',
    'error.connection': 'Erreur de connexion. Veuillez réessayer.',
    'success.codeSent': 'Code de vérification envoyé',
    'success.codeSentDesc': 'Un code de vérification a été envoyé à votre adresse email.',
    'success.codeVerified': 'Code vérifié',
    'success.codeVerifiedDesc': 'Code de vérification validé. Vous pouvez maintenant saisir votre nouveau mot de passe.',
    'success.passwordChanged': 'Mot de passe modifié',

    // Password Rules
    'rule.password.length': 'Le mot de passe doit contenir au moins 8 caractères',
    'rule.password.lower': 'Le mot de passe doit contenir au moins une lettre minuscule',
    'rule.password.upper': 'Le mot de passe doit contenir au moins une lettre majuscule',
    'rule.password.digit': 'Le mot de passe doit contenir au moins un chiffre',
    'rule.password.special': 'Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*)',

    // Zoom Interface
    'zoom.title.default': 'Zoom - {0}',
    'zoom.title.agency': 'Sélection d\'agence',
    'zoom.title.user': 'Sélection d\'utilisateur',
    'zoom.title.profile': 'Sélection de profil',
    'zoom.title.employee': 'Sélection de salarié',
    'zoom.title.license': 'Sélection de licence',
    'zoom.search.placeholder': 'Rechercher dans les données...',
    'zoom.action.refresh': 'Actualiser les données',
    'zoom.action.export': 'Exporter les données',
    'zoom.action.exportCsv': 'Exporter en CSV',
    'zoom.action.exportExcel': 'Exporter en Excel',
    'zoom.status.results': '{0} résultat(s) sur {1} total',
    'zoom.status.filtered': 'Filtré par: "{0}"',
    'zoom.error.loading': '❌ Erreur lors du chargement des données',
    'zoom.error.unknown': 'Erreur inconnue',
    'zoom.error.validation': 'Erreur de validation',
    'zoom.error.advice': 'Vérifiez vos paramètres et réessayez, ou contactez l\'administrateur si le problème persiste.',
    'zoom.empty.search': 'Aucun résultat trouvé pour votre recherche',
    'zoom.empty.data': 'Aucune donnée disponible',
    'zoom.hint.select': 'Cliquez sur une ligne pour sélectionner',
    'zoom.action.cancel': 'Annuler',
    'zoom.action.select': 'Sélectionner',
    'zoom.tooltip.clear': 'Effacer la sélection',
    'zoom.tooltip.open': 'Ouvrir le zoom {0}',
    'zoom.tooltip.disabled': 'Remplissez d\'abord l\'email et le mot de passe',
    'zoom.check.fillRequired': 'Merci de remplir les champs obligatoires',

    // Backend Messages
    'LOGIN_SUCCESS': 'Connexion réussie',
    'INVALID_PASSWORD': 'Mot de passe incorrect',
    'USER_NOT_FOUND': 'Utilisateur non trouvé',
    'MISSING_PARAMETERS': 'Paramètres manquants',
    'INVALID_DOSSIER': 'Dossier invalide',
    'INVALID_LICENSE': 'Licence invalide',
    'OTP_SENT': 'Code OTP envoyé',
    'OTP_EXPIRED': 'Code OTP expiré',
    'INVALID_OTP': 'Code OTP invalide',
    'DOUBLE_AUTH_REQUIRED': 'Double authentification requise',
    'SESSION_LOST': 'Session expirée',
    'OTP_BLOCKED_RECENTLY': 'Trop de tentatives, veuillez patienter',
    'EMAIL_SEND_ERROR': 'Erreur lors de l\'envoi de l\'email',
    'OTP_GENERATION_ERROR': 'Erreur de génération OTP',
    'OTP_NOT_FOUND': 'OTP non trouvé',
    'OTP_BLOCKED': 'OTP bloqué',
    'OTP_VALIDATED': 'OTP validé',
    'OTP_VALIDATION_ERROR': 'Erreur de validation OTP',
    'JWT_CONFIG_RETRIEVED': 'Configuration JWT récupérée',
    'JWT_CONFIG_ERROR': 'Erreur configuration JWT',
    'PARAMETER_MANAGER_NULL': 'Erreur système (Paramètres)',
    'DOUBLE_AUTH_STATUS_RETRIEVED': 'Statut double auth récupéré',
    'DOUBLE_AUTH_STATUS_ERROR': 'Erreur statut double auth',

    // Auth Context Messages
    'auth.error.noPending': 'Aucune connexion en attente',
    'auth.success.login.title': 'Connexion réussie',
    'auth.success.login.welcome': 'Bienvenue {0} !',
    'auth.error.invalidResponse': 'Réponse de connexion invalide',
    'auth.error.loginTitle': 'Erreur de connexion',
    'auth.error.doubleAuthTitle': 'Erreur de double authentification',
    'auth.error.unexpected': 'Une erreur inattendue s\'est produite.',
    'auth.error.server': 'Erreur serveur. Veuillez réessayer plus tard.',
    'auth.error.network': 'Impossible de contacter le serveur. Vérifiez votre connexion.',
    'auth.info.doubleAuthRequired': 'Double authentification requise',
    'auth.info.otpSent': 'Un code de vérification a été envoyé à votre adresse email.',
    'auth.error.credentialsTitle': 'Identifiants incorrects',
    'auth.error.credentials': 'Email, mot de passe ou agence incorrects.',
    'auth.error.otpBlockedRecentlyTitle': 'Double authentification temporairement bloquée',
    'auth.error.otpBlockedRecentlyDesc': 'Trop de tentatives récentes. Veuillez attendre 30 minutes avant de réessayer.',
    'auth.error.otpBlockedTitle': 'Code de double authentification bloqué',
    'auth.error.otpBlockedDesc': 'Trop de tentatives incorrectes. Le code a été bloqué pour des raisons de sécurité.',
    'auth.error.otpExpiredTitle': 'Code de double authentification expiré',
    'auth.error.otpExpiredDesc': 'Le code a expiré. Veuillez demander un nouveau code.',
    'auth.error.invalidOtpTitle': 'Code de double authentification incorrect',
    'auth.error.invalidOtpDesc': 'Le code saisi est incorrect. Veuillez vérifier et réessayer.',
    'auth.success.logout.title': 'Déconnexion',
    'auth.success.logout.message': 'Vous avez été déconnecté avec succès.',
    'auth.session_expired.title': 'Session expirée',
    'auth.session_expired.message': 'Votre session a expiré ou est invalide. Redirection vers la page de connexion...',
    'auth.session_expired.auto_close_text': 'Fermeture automatique (Session expirée ou invalide)',

    // Double Auth Dialog
    'doubleAuth.title': 'Double Authentification',
    'doubleAuth.body': 'Un code de vérification a été envoyé à votre adresse email. Veuillez saisir ce code pour compléter votre connexion.',
    'doubleAuth.code.label': 'Code de vérification',
    'doubleAuth.code.placeholder': '123456',
    'doubleAuth.security.label': 'Sécurité :',
    'doubleAuth.security.desc': 'Le code expire dans 10 minutes. Après 3 tentatives incorrectes, le code sera bloqué pendant 30 minutes.',
    'doubleAuth.btn.moreInfo': 'Plus d\'infos',
    'doubleAuth.btn.resend': 'Renvoyer le code',
    'doubleAuth.btn.verify': 'Vérifier',
    'doubleAuth.tip.label': 'Conseil :',
    'doubleAuth.alert.blockedDesc': 'Attendez 30 minutes ou contactez l\'administrateur si le problème persiste.',
    'doubleAuth.alert.expiredDesc': 'Cliquez sur "Renvoyer le code" pour recevoir un nouveau code.',
    'doubleAuth.delay.label': 'Délai de sécurité :',
    'doubleAuth.delay.wait': 'Vous pouvez demander un nouveau code dans',
    'doubleAuth.delay.now': 'maintenant',
    'doubleAuth.delay.protectionLabel': 'Protection anti-abus :',
    'doubleAuth.delay.protectionDesc': 'Délai de 60 secondes entre chaque demande',
    'doubleAuth.attempts.label': 'Tentatives :',
    'doubleAuth.attempts.lastChance': 'Dernière chance !',
    'doubleAuth.attempts.remaining': 'Tentatives restantes',
    'doubleAuth.error.required': 'Code requis',
    'doubleAuth.error.requiredDesc': 'Veuillez saisir le code de vérification',
    'doubleAuth.error.invalid': 'Code invalide',
    'doubleAuth.error.invalidDesc': 'Le code de vérification doit contenir 6 chiffres',
    'doubleAuth.success.resend': 'Code renvoyé',
    'doubleAuth.success.resendDesc': 'Un nouveau code de vérification a été envoyé à votre email.',
    'doubleAuth.error.resend': 'Erreur',
    'doubleAuth.error.resendDesc': 'Impossible de renvoyer le code. Veuillez réessayer.',

    // Security Info Dialog
    'securityInfo.title': 'Sécurité de la Double Authentification',
    'securityInfo.protection.title': 'Protection renforcée :',
    'securityInfo.protection.desc': 'Votre compte est protégé par plusieurs niveaux de sécurité pour prévenir les accès non autorisés.',
    'securityInfo.measures.title': '🔐 Mesures de Sécurité Actives',
    'securityInfo.measure.email.title': 'Code OTP par Email',
    'securityInfo.measure.email.desc': 'Un code unique de 6 chiffres est envoyé à votre adresse email enregistrée',
    'securityInfo.measure.expiry.title': 'Expiration Automatique',
    'securityInfo.measure.expiry.desc': 'Le code expire automatiquement après 10 minutes pour des raisons de sécurité',
    'securityInfo.measure.limit.title': 'Limitation des Tentatives',
    'securityInfo.measure.limit.desc': 'Après 3 tentatives incorrectes, le code est bloqué pendant 30 minutes',
    'securityInfo.measure.abuse.title': 'Protection Anti-Abus',
    'securityInfo.measure.abuse.desc': 'Délai de 60 secondes entre chaque demande de nouveau code',
    'securityInfo.help.title': '⚠️ Que faire en cas de problème ?',
    'securityInfo.help.expired.title': 'Code expiré',
    'securityInfo.help.expired.desc': 'Cliquez sur "Renvoyer le code" pour recevoir un nouveau code',
    'securityInfo.help.blocked.title': 'Code bloqué',
    'securityInfo.help.blocked.desc': 'Attendez 30 minutes ou contactez l\'administrateur si le problème persiste',
    'securityInfo.help.email.title': 'Email non reçu',
    'securityInfo.help.email.desc': 'Vérifiez votre dossier spam et assurez-vous que l\'adresse email est correcte',
    'securityInfo.tip.label': 'Conseil :',
    'securityInfo.tip.desc': 'Gardez votre adresse email à jour et vérifiez régulièrement vos emails pour une expérience optimale.',
    'securityInfo.btn.understand': 'Compris',

    // Global / Sidebar
    'auth.logout': 'Déconnexion',
    'sidebar.search.placeholder': 'Rechercher...',
    'footer.copyright': '© 2026 BLOBTIC. Tous droits réservés.',
    'menu.planning.admin.configKpi': 'Affectation KPIs Profil',
    'admin.config_kpi.select_app': 'Application',
    'admin.config_kpi.select_profile': 'Sélectionner un Profil Utilisateur',
    'admin.config_kpi.search_profile': 'Rechercher un profil...',
    'admin.config_kpi.title': 'Configuration des KPIs',
    'admin.config_kpi.save': 'Enregistrer',
    'admin.config_kpi.saving': 'Enregistrement...',
    'admin.config_kpi.load_error': 'Erreur lors du chargement des KPIs',
    'admin.config_kpi.select_profile_required': 'Veuillez sélectionner un profil',
    'admin.config_kpi.save_success': 'Configuration des KPIs enregistrée avec succès',
    'admin.config_kpi.save_error': 'Erreur lors de l\'enregistrement',
    'admin.config_kpi.save_server_error': 'Erreur serveur lors de la sauvegarde',
    'admin.config_kpi.kpi_count': '{app} — {enabled}/{total} actifs',
    'admin.config_kpi.mode_config': 'Configuration',
    'admin.config_kpi.mode_live': 'Mode Live (test)',
    'admin.config_kpi.select_all': 'Tout sélectionner',
    'admin.config_kpi.deselect_all': 'Tout décocher',
    'admin.config_kpi.no_kpi': 'Aucun KPI disponible pour cette application.',
    'admin.config_kpi.live_preview': 'Aperçu avec données fictives — rendu réel dépend des données.',
    'admin.config_kpi.type_widget': 'Widget',
    'admin.config_kpi.type_card': 'Carte',
    'admin.config_kpi.order': 'Ordre',
    'admin.config_kpi.width': 'Largeur',
    'admin.config_kpi.span.quarter': '1/4 ligne',
    'admin.config_kpi.span.third': '1/3 ligne',
    'admin.config_kpi.span.half': '1/2 ligne',
    'admin.config_kpi.span.two_thirds': '2/3 ligne',
    'admin.config_kpi.span.full': 'Ligne complète',
    'admin.config_kpi.widget.today_prereg': 'Pré-inscriptions du jour',
    'admin.config_kpi.widget.today_schedule': 'Planning du jour',
    'admin.config_kpi.widget.low_attendance': 'Alerte faible présence',
    'admin.config_kpi.widget.outstanding_debt': 'Reste à payer (Dette)',
    'academy.menu.system': 'Système & Logs',

    // Profiles
    'page.profiles.title': 'Gestion des Profils',
    'page.profiles.new_title': 'Nouveau Profil',
    'page.profiles.edit_title': "Modifier le profil : {0}",
    'page.profiles.add_new': 'Nouveau Profil',
    'page.profiles.back_to_list': 'Retour à la liste',
    'page.profiles.confirm_delete': "Êtes-vous sûr de vouloir supprimer le profil {0} ?",
    'page.profiles.search_placeholder': 'Rechercher un profil...',
    'page.profiles.view_grid': 'Vue Grille',
    'page.profiles.view_table': 'Vue Tableau',
    'page.profiles.status.admin': 'Administrateur',
    'page.profiles.status.standard': 'Standard',
    'page.profiles.details': 'Détails du Profil',
    'page.profiles.zoom.label': 'Sélectionner un Profil (Zoom)',
    'page.profiles.zoom.helper': 'Cliquez sur la loupe pour chercher un profil',
    'page.profiles.field.code': 'Code Profil',
    'page.profiles.field.label': 'Libellé Profil',
    'page.profiles.field.level': 'Niveau',
    'page.profiles.field.admin': 'Administrateur',
    'page.profiles.validation.required': 'Code et Libellé sont obligatoires',
    'page.profiles.popup.title': 'Liste des Profils',

    // Agency
    'page.agency.title': 'Agences',
    'page.agency.new_title': 'Nouvelle Agence',
    'page.agency.edit_title': "Modifier l'agence : {0}",
    'page.agency.add_new': 'Nouvelle Agence',
    'page.agency.back_to_list': 'Retour à la liste',
    'page.agency.confirm_delete': "Êtes-vous sûr de vouloir supprimer l'agence {0} ?",
    'page.agency.search_placeholder': 'Rechercher une agence...',
    'page.agency.view_grid': 'Vue Grille',
    'page.agency.view_table': 'Vue Tableau',
    'page.agency.zoom.label': 'Rechercher une agence',
    'page.agency.zoom.helper': 'Saisissez le code ou le nom de l\'agence pour rechercher',
    'page.agency.details': 'Détails Agence',
    'page.agency.field.code': 'Code',
    'page.agency.field.lib': 'Nom / Raison Sociale',
    'page.agency.field.email': 'Email',
    'page.agency.field.phone': 'Téléphone',
    'page.agency.field.address1': 'Adresse 1',
    'page.agency.field.address2': 'Adresse 2',
    'page.agency.field.pays_legal': 'Pays Légal',
    'page.agency.field.legal_country': 'Pays Légal',
    'page.agency.field.mobile_phone': 'Téléphone Portable',
    'page.agency.field.start_date': 'Date Début',
    'page.agency.field.end_date': 'Date Fin',
    'page.agency.field.closed_plng': 'Date Clôture Planning / Agence',
    'page.agency.helper.closed_plng': 'Verrouille le planning et les factures antérieurs ou égaux à cette date',
    'page.agency.field.memo': 'Commentaire',
    'page.agency.validation.required': 'Le Code et le Nom sont obligatoires',
    'page.agency.validation.image_size': "L'image doit faire moins de 10MB",
    'page.agency.fiscal_identifiers': 'Identifiants Fiscaux & Légaux',
    'page.agency.field.nif': 'NIF (Numéro d\'Identification Fiscale)',
    'page.agency.helper.nif': 'Numéro d\'Identification Fiscale (15 chiffres)',
    'page.agency.field.nis': 'NIS (Numéro d\'Identification Statistique)',
    'page.agency.helper.nis': 'Numéro d\'Identification Statistique (11 chiffres)',
    'page.agency.field.rc': 'Registre de Commerce (RC)',
    'page.agency.helper.rc': 'Numéro du Registre de Commerce',
    'page.agency.field.ai': 'Article d\'Imposition (AI)',
    'page.agency.helper.ai': 'Article d\'Imposition (13 chiffres)',
    'page.agency.field.logo': 'Logo de l\'Agence',
    'page.agency.logo': 'Logo / Schéma',
    'page.agency.no_logo': 'Aucun logo',
    'page.agency.upload_logo': 'Charger Logo',
    'page.agency.choose_image': 'Choisir une image',

    // Pages - Users
    'page.users.title': 'Gestion des Utilisateurs',
    'page.users.new_title': 'Nouvel Utilisateur',
    'page.users.edit_title': "Modifier l'utilisateur : {0}",
    'page.users.add_new': 'Nouvel Utilisateur',
    'page.users.back_to_list': 'Retour à la liste',
    'page.users.confirm_delete': "Êtes-vous sûr de vouloir supprimer l'utilisateur {0} ?",
    'page.users.search_placeholder': 'Rechercher un utilisateur...',
    'page.users.view_grid': 'Vue Grille',
    'page.users.view_table': 'Vue Tableau',
    'page.users.details': 'Détails Utilisateur',
    'page.users.zoom.label': 'Sélectionner un utilisateur',
    'page.users.zoom.helper': 'Cliquez pour rechercher un utilisateur',
    'page.users.popup.title': 'Liste des Utilisateurs',
    'page.users.field.code': 'Code Utilisateur',
    'page.users.field.lib': 'Nom Utilisateur',
    'page.users.field.password': 'Mot de passe',
    'page.users.field.profile': 'Profil',
    'page.users.field.profile_lib': 'Libellé Profil',
    'page.users.field.license': 'Licence',
    'page.users.field.license_lib': 'Libellé Licence',
    'page.users.field.start_date': 'Date Début',
    'page.users.field.end_date': 'Date Fin',
    'page.users.field.email': 'Email',
    'page.users.field.mobile': 'Téléphone Portable',
    'page.users.field.pass_date': 'Expiration Mot de passe',
    'page.users.field.pass_days': 'Jours Alerte Expiration',
    'page.users.validation.required': 'Code et Nom sont requis',
    'page.users.field.memo': 'Commentaire',
    'LOGIN_PASSWORD_EXPIRED': 'Votre mot de passe a expiré.',
    'LOGIN_ACCOUNT_EXPIRED': 'Compte expiré.',
    'LOGIN_PASSWORD_EXPIRING_SOON': 'Votre mot de passe expire bientôt.',




    'menu.planning.admin.accessRights.sysParams': 'Paramètres Système',
    'menu.planning.admin.accessRights.securityWeb': 'Sécurité Web',

    // Page Security Web
    'page.security.title': 'Sécurité Web (Profils & Actions)',
    'page.security.profile.label': 'Profil',
    'page.security.table.selectProfile': 'Sélectionner un profil',
    'page.security.table.objectAction': 'Objet/Action',
    'page.security.table.type': 'Type',
    'page.security.table.load': 'Charger',
    'page.security.table.select': 'Sélectionner',
    'page.security.table.update': 'Modifier',
    'page.security.table.insert': 'Ajouter',
    'page.security.table.delete': 'Supprimer',
    'page.security.table.execute': 'Exécuter',
    'page.security.table.empty': 'Aucun objet trouvé',
    'page.security.action.refresh': 'Actualiser',
    'page.security.action.updateAll': 'Mise à jour globale',
    'page.security.dialog.bulkTitle': 'Mise à jour globale des droits',
    'page.security.dialog.apply': 'Appliquer à tous',

    'common.profile': 'Profil',
    'common.loading': 'Chargement...',
    'common.loadingServer': 'Chargement des données depuis le serveur',
    'common.loadingServerHint': 'Veuillez patienter un instant…',
    'common.saving': 'Enregistrement...',
    'common.save': 'Enregistrer',
    'common.success.saved': 'Modifications enregistrées avec succès',
    'common.error.saving': 'Erreur lors de l\'enregistrement des droits',
    'common.error.loading': 'Erreur lors du chargement des données',

    'page.sys_parameters.title': 'Paramètres Système',
    'page.sys_parameters.subtitle': 'Gestion centralisée des paramètres de l\'application',
    'page.sys_parameters.action.reset': 'Réinitialiser',
    'page.sys_parameters.confirm_reset': 'Êtes-vous sûr de vouloir réinitialiser tous les paramètres aux valeurs par défaut ? Cette action est irréversible.',
    'page.sys_parameters.success.reset': 'Paramètres réinitialisés avec succès',
    'page.sys_parameters.error.reset': 'Erreur lors de la réinitialisation',
    'page.sys_parameters.success.update': 'Mise à jour réussie',
    'page.sys_parameters.success.message': 'Le paramètre {0} a été mis à jour avec succès',
    'page.sys_parameters.meta.info': 'Mis à jour le : {0} par {1}',

    'action.update': 'Mettre à jour',
    'action.save': 'Enregistrer',
    'action.refresh': 'Actualiser',
    'SYSPAR_CODE': 'Code Paramètre',

    'page.sys_parameters.group.messaging': 'Messagerie (SMTP)',
    'page.sys_parameters.group.security': 'Sécurité & Authentification',
    'page.sys_parameters.group.performance': 'Performance & Cache',
    'page.sys_parameters.group.licensing': 'Licence & Limites',
    'page.sys_parameters.group.maintenance': 'Maintenance & Nettoyage',
    'page.sys_parameters.group.system': 'Système & Tests',
    'page.sys_parameters.group.gps': 'Suivi GPS',
    'page.sys_parameters.group.sso': 'Authentification SSO (Google, Microsoft, OIDC)',
    'page.sys_parameters.group.other': 'Autres Paramètres Système',

    // Parameter Labels (SYSPAR_LIB)
    'page.sys_parameters.lib.String_Envoi_Mail': 'Configuration SMTP pour envoi d\'emails',
    'page.sys_parameters.lib.Session_Expiration_Minutes': 'Durée d\'expiration des sessions en minutes',
    'page.sys_parameters.lib.JWT_Expiration_Minutes': 'Durée d\'expiration des tokens JWT en minutes',
    'page.sys_parameters.lib.Sync_Notifications_Timer_Seconds': 'Fréquence de rafraîchissement des notifications et chat en secondes (0 = désactivé)',
    'page.sys_parameters.lib.Default_App_Name': 'Nom de l\'application par défaut',
    'page.sys_parameters.lib.Licensed_Applications': 'Liste des applications sous licence',
    'page.sys_parameters.lib.ObjectPool_MaxSize': 'Taille maximale du pool d\'objets',
    'page.sys_parameters.lib.DictionaryPool_MaxSize': 'Taille maximale du pool de dictionnaires',
    'page.sys_parameters.lib.ListPool_MaxSize': 'Taille maximale du pool de listes',
    'page.sys_parameters.lib.DataManager_Cache_Duration_Minutes': 'Durée de cache des DataManagers en minutes',
    'page.sys_parameters.lib.Compression_Threshold_Bytes': 'Seuil de compression en octets',
    'page.sys_parameters.lib.Response_Cache_Duration_Seconds': 'Durée de cache des réponses en secondes',
    'page.sys_parameters.lib.Pool_Hit_Rate_Threshold': 'Seuil de taux de réussite du pool (%)',
    'page.sys_parameters.lib.Cache_Hit_Rate_Threshold': 'Seuil de taux de réussite du cache (%)',
    'page.sys_parameters.lib.Max_Creation_Time_Ms': 'Temps maximum de création en millisecondes',
    'page.sys_parameters.lib.Alert_Cooldown_Minutes': 'Délai de refroidissement des alertes en minutes',
    'page.sys_parameters.lib.License_Max_Users': 'Nombre maximum d\'utilisateurs par licence',
    'page.sys_parameters.lib.License_Max_Dossiers': 'Nombre maximum de dossiers par licence',
    'page.sys_parameters.lib.License_Storage_Limit_GB': 'Limite de stockage par licence en GB',
    'page.sys_parameters.lib.LoadTest_Iterations': 'Nombre d\'itérations pour les tests de charge',
    'page.sys_parameters.lib.LoadTest_Operations_Per_Iteration': 'Nombre d\'opérations par itération',
    'page.sys_parameters.lib.LoadTest_Batch_Size': 'Taille des lots pour les tests de charge',
    'page.sys_parameters.lib.LoadTest_Batch_Delay_Ms': 'Délai entre les lots en millisecondes',
    'page.sys_parameters.lib.License_Monitoring_Interval_Minutes': 'Intervalle de monitoring des licences en minutes',
    'page.sys_parameters.lib.Auto_Cleanup_Expired_OTP_Hours': 'Intervalle de nettoyage des OTP expirés en heures',
    'page.sys_parameters.lib.Auto_Cleanup_Expired_Sessions_Hours': 'Intervalle de nettoyage des sessions expirées en heures',
    'page.sys_parameters.lib.GPS_API_Key': 'Clé API GPS',
    'page.sys_parameters.lib.GPS_Endpoint_URL': 'URL du point de terminaison GPS',
    'page.sys_parameters.lib.GPS_Provider': 'Fournisseur GPS',
    'page.sys_parameters.lib.Log_Retention_Days': 'Durée de rétention des logs en jours',
    'page.sys_parameters.lib.Performance_Metrics_Retention_Hours': 'Durée de rétention des métriques de performance en heures',
    'page.sys_parameters.lib.OTP_Expiration_Minutes': 'Durée de validité des codes OTP en minutes',
    'page.sys_parameters.lib.OTP_Max_Attempts': 'Nombre maximum de tentatives pour les codes OTP',
    'page.sys_parameters.lib.Password_Min_Length': 'Longueur minimale des mots de passe',
    'page.sys_parameters.lib.Double_Authentication_Enabled': 'Activation de la double authentification (0=Désactivé, 1=Activé)',
    'page.sys_parameters.lib.WEB_SECURITY_ENABLED': 'Activation de la sécurité web (0=Désactivé, 1=Activé)',
    'page.sys_parameters.lib.Double_Auth_Method': 'Méthode de double authentification (OTP, SMS, EMAIL)',
    'page.sys_parameters.lib.Currency_Symbol': 'Symbole monétaire (DZD,$, €, £, etc.)',
    'page.sys_parameters.lib.Geo_Restriction_Enabled': 'Activation de la restriction géographique par IP (0=Désactivé, 1=Activé)',
    'page.sys_parameters.lib.Geo_Restriction_Mode': 'Mode de restriction géographique (Autoriser uniquement / Bloquer uniquement)',
    'page.sys_parameters.lib.Geo_Restriction_Countries': 'Codes pays ISO autorisés/bloqués séparés par des virgules (ex: DZ pour Algérie)',

    'page.sys_parameters.lib.IDXDB_CACHE_ENABLED': 'Cache base locale côté client (0=Désactivé, 1=Activé)',
    'page.sys_parameters.lib.SSO_ENABLED': 'Activation globale du Single Sign-On (0=Désactivé, 1=Activé)',
    'page.sys_parameters.lib.SSO_DEFAULT_PROVIDER': 'Fournisseur SSO par défaut (google, microsoft, oidc)',
    'page.sys_parameters.lib.SSO_GOOGLE_ENABLED': 'Activation du SSO Google Workspace (0=Désactivé, 1=Activé)',
    'page.sys_parameters.lib.SSO_GOOGLE_CLIENT_ID': 'Client ID OAuth 2.0 Google Workspace',
    'page.sys_parameters.lib.SSO_GOOGLE_CLIENT_SECRET': 'Client Secret OAuth 2.0 Google Workspace',
    'page.sys_parameters.lib.SSO_MICROSOFT_ENABLED': 'Activation du SSO Microsoft Entra ID (0=Désactivé, 1=Activé)',
    'page.sys_parameters.lib.SSO_MICROSOFT_CLIENT_ID': 'Client ID / Application ID Microsoft Entra ID',
    'page.sys_parameters.lib.SSO_MICROSOFT_CLIENT_SECRET': 'Client Secret Microsoft Entra ID',
    'page.sys_parameters.lib.SSO_MICROSOFT_TENANT_ID': 'Tenant ID Microsoft (common, organizations ou GUID du tenant)',
    'page.sys_parameters.lib.SSO_OIDC_ENABLED': 'Activation du fournisseur générique OpenID Connect (0=Désactivé, 1=Activé)',
    'page.sys_parameters.lib.SSO_OIDC_NAME': 'Nom affiché pour le fournisseur OpenID Connect',
    'page.sys_parameters.lib.SSO_OIDC_AUTHORITY': 'URL racine de l\'autorité OIDC / Realm',
    'page.sys_parameters.lib.SSO_OIDC_CLIENT_ID': 'Client ID pour fournisseur OpenID Connect générique',
    'page.sys_parameters.lib.SSO_OIDC_CLIENT_SECRET': 'Client Secret pour fournisseur OpenID Connect générique',
    // Dropdown Options
    'page.sys_parameters.option.enabled': 'Activé',
    'page.sys_parameters.option.disabled': 'Désactivé',
    'page.sys_parameters.option.geo.allow_only': 'Autoriser uniquement les pays spécifiés',
    'page.sys_parameters.option.geo.block_only': 'Bloquer uniquement les pays spécifiés',
    'page.sys_parameters.option.method.otp': 'OTP (Code unique)',
    'page.sys_parameters.option.method.sms': 'SMS',
    'page.sys_parameters.option.method.email': 'Email',

    'page.parameters.title': 'Paramètres Système (Ancien)',
    'page.parameters.subtitle': 'Gestion des configurations globales de l\'application',
    'page.parameters.table.code': 'Code',
    'page.parameters.table.label': 'Libellé',
    'page.parameters.table.value': 'Valeur',
    'page.parameters.table.type': 'Type',
    'page.parameters.table.actions': 'Actions',
    'page.parameters.table.empty': 'Aucun paramètre trouvé',
    'page.parameters.action.refresh': 'Actualiser',
    'page.parameters.action.edit': 'Modifier',
    'page.parameters.dialog.title': 'Modifier le paramètre {0}',
    'page.parameters.dialog.description': 'Description',
    'page.parameters.dialog.value': 'Valeur',
    'page.parameters.dialog.save': 'Sauvegarder',
    'page.parameters.dialog.cancel': 'Annuler',
    'page.parameters.success.title': 'Paramètre mis à jour',
    'page.parameters.success.message': 'Le paramètre {0} a été modifié.',
    'page.parameters.error.title': 'Erreur',
    'page.parameters.error.update': 'Impossible de mettre à jour le paramètre.',
    'page.parameters.error.load': 'Impossible de charger les paramètres.',
    'traffic.table.country': 'Pays',
    'traffic.stats.distinct_countries': 'Pays distincts',

    // Common / GenericPage
    'traffic.table.headers': 'En-têtes',
    'traffic.request_headers': 'En-têtes de la requête',
    'common.actions.new': 'Nouveau',
    'common.actions.edit': 'Modifier',
    'common.actions.delete': 'Supprimer',
    'common.actions.save': 'Enregistrer',
    'common.actions.update': 'Mettre à jour',
    'common.actions.insert': 'Insérer',
    'common.actions.insert_copy': 'Dupliquer (Insérer comme nouveau)',
    'common.actions.duplicate': 'Dupliquer',
    'common.actions.confirm': 'Confirmer',
    'common.actions.close': 'Fermer',
    'common.actions.cancel': 'Annuler',
    'common.back_to_list': 'Retour à la liste',
    'common.select': 'Sélectionner...',
    'common.status': 'Statut',
    'common.phone': 'Téléphone',
    'common.email': 'Email',
    // boubaker 23/08/2026 - pages d'édition (GenericPage type print)
    'common.actions.print': 'Imprimer',
    'common.actions.excel': 'Excel',
    'common.actions.word': 'Word',
    'common.actions.filters': 'Afficher/Masquer les filtres',
    'common.confirm.title.delete': 'Confirmer la suppression',
    'common.confirm.title.update': 'Confirmer la mise à jour',
    'common.confirm.title.insert': 'Confirmer l\'insertion',
    'common.confirm.delete': 'Êtes-vous sûr de vouloir supprimer cet élément ?',
    'common.confirm.update': 'Êtes-vous sûr de vouloir modifier cet élément ?',
    'common.confirm.insert': 'Êtes-vous sûr de vouloir insérer cet élément comme nouvel enregistrement ?',
    'common.labels.lastUpdate': 'Dernière mise à jour:',
    'common.labels.user': 'Utilisateur:',
    'common.messages.success': 'Succès',
    'common.messages.warning': 'Attention',
    'common.messages.error': 'Erreur',
    'common.messages.saved': 'Mise à jour effectuée',
    'common.messages.created': 'Création effectuée',
    'common.messages.deleted_success': 'Suppression effectuée',
    'common.messages.save_error': 'Erreur lors de la sauvegarde',
    'common.messages.delete_error': 'Erreur lors de la suppression',
    'common.messages.comm_error': 'Erreur de communication',

    // Locations de salle (Room Rentals)
    'academy.room_rentals.title': 'Locations de salle',
    'academy.room_rentals.new_rental': 'Nouvelle Location',
    'academy.room_rentals.create_title': 'Nouvelle Location de Salle',
    'academy.room_rentals.edit_title': 'Modifier la location : {name}',
    'academy.room_rentals.empty_title': 'Aucune location enregistrée',
    'academy.room_rentals.empty_subtitle': 'Créez une nouvelle location pour réserver une salle.',
    'academy.room_rentals.search_placeholder': 'Rechercher une location, client, salle...',
    'academy.room_rentals.confirm_delete': 'Êtes-vous sûr de vouloir supprimer la location "{name}" ?',
    'academy.room_rentals.deleted_success': 'Location supprimée avec succès.',
    'academy.room_rentals.room': 'Salle',
    'academy.room_rentals.client': 'Client / Organisme',
    'academy.room_rentals.client_name': 'Nom du Client / Organisme',
    'academy.room_rentals.lib': 'Intitulé / Motif de la location',
    'academy.room_rentals.dates': 'Période de Location',
    'academy.room_rentals.start_date': 'Date & Heure Début',
    'academy.room_rentals.end_date': 'Date & Heure Fin',
    'academy.room_rentals.code': 'Code Référence',
    'academy.room_rentals.price': 'Tarif Total (DZD)',
    'academy.room_rentals.deposit': 'Acompte Versé (DZD)',
    'academy.room_rentals.notes': 'Notes & Équipements Requis',
    'academy.room_rentals.notes_title': 'Notes & Équipements Requis',
    'academy.room_rentals.section_room': 'Salle & Créneau',
    'academy.room_rentals.section_client': 'Client & Tarification',
    'academy.room_rentals.room_helper': 'Sélectionnez la salle disponible pour cette location',
    'academy.room_rentals.validation.room_required': 'Veuillez sélectionner une salle.',
    'academy.room_rentals.validation.client_required': 'Le nom du client / organisme est obligatoire.',
    'academy.room_rentals.validation.start_date_required': 'La date de début est obligatoire.',
    'academy.room_rentals.validation.end_date_required': 'La date de fin est obligatoire.',
    'academy.room_rentals.validation.date_order': 'La date de fin doit être postérieure à la date de début.',

    // Dates
    'common.date.today': 'Aujourd\'hui',
    'common.date.yesterday': 'Hier',
    'common.date_time': 'Date et heure',

    // Top Navigation & Actions
    'common.actions.chat': 'Chat',
    'common.actions.notifications': 'Notifications',
    'common.actions.lightMode': 'Mode Clair',
    'common.actions.darkMode': 'Mode Sombre',
    'common.actions.profile': 'Profil',
    'common.actions.markAllRead': 'Tout marquer comme lu',
    'common.actions.clear': 'Effacer tout',
    'common.actions.enterFullScreen': 'Plein écran',
    'common.actions.exitFullScreen': 'Quitter plein écran',
    'common.messages.noNotifications': 'Aucune nouvelle notification',
    'common.placeholder.message': 'Tapez un message...',

    // Menu Planning
    'menu.planning.commande': 'Commande',
    'menu.planning.planning': 'Planning',
    'menu.planning.facturation': 'Facturation',
    'menu.planning.editions': 'Editions',
    'menu.planning.entite': 'Entité',
    'menu.planning.administration': 'Administration',
    'menu.planning.admin.configMenu': 'Configuration menu',
    'menu.planning.admin.securityWeb': 'Sécurité web',
    'menu.planning.admin.societe': 'Société',
    'menu.planning.admin.societe.agence': 'Agence',
    'menu.planning.admin.accessRights': 'Droits d\'accès',
    'menu.planning.admin.accessRights.profiles': 'Profils',
    'menu.planning.admin.accessRights.users': 'Utilisateurs',
    'menu.planning.admin.parameters': 'Paramètres',
    'menu.planning.admin.userDossiers': 'Affectation Dossiers',

    // Menu Maker
    'menu.maker.projects': 'Projets',
    'menu.maker.estimates': 'Devis',
    'menu.maker.design': 'Conception',
    'menu.maker.production': 'Production',
    'menu.maker.materials': 'Matériaux',
    'menu.maker.customers': 'Clients',
    'menu.maker.reports': 'Rapports',
    'menu.maker.profiles': 'Profilés',
    'menu.maker.dashboard': 'Tableau de bord',
    'menu.maker.libraries': 'Bibliothèques',
    'menu.maker.components': 'Composants',
    'menu.maker.templates': 'Modèles',

    // Menu Parc
    'menu.parc.dashboard': 'Tableau de bord',
    'menu.parc.vehicles': 'Véhicules',
    'menu.parc.drivers': 'Conducteurs',
    'menu.parc.maintenance': 'Maintenance',
    'menu.parc.repairs': 'Réparations',
    'menu.parc.trips': 'Trajets',
    'menu.parc.documents': 'Documents',
    'menu.parc.notifications': 'Notifications',
    'menu.parc.settings': 'Paramètres',
    'menu.parc.gps': 'GPS & SUIVI',
    'menu.parc.gps.tracking': 'Suivi GPS',
    'menu.parc.gps.alerts': 'Alertes GPS',
    'menu.parc.fuel': 'Carburant',
    'menu.parc.fuelcards': 'Cartes Carburant',
    'menu.parc.mileage': 'Kilométrage',
    'menu.parc.analyses': 'Analyses & Rentabilité',
    'menu.parc.analysis.general': 'Analyse Générale',
    'menu.parc.analysis.tco': 'Analyse TCO',
    'menu.parc.alerts': "Message d'alertes",
    'menu.parc.lookups': 'Tables de Référence',

    // Menu Academy
    'academy.menu.dashboard': 'Tableau de bord',
    'academy.menu.rooms': 'Salles & Équipements',
    'academy.menu.room_rentals': 'Locations de salle',
    'academy.menu.teachers': 'Enseignants',
    'academy.menu.students': 'Apprenants & Inscriptions',
    'academy.menu.course_templates': 'Modèles de Cours',
    'academy.menu.groups': 'Groupes & Cohortes',
    'academy.menu.scheduler': 'Planning Hebdomadaire',
    'academy.menu.planning_theorique_crud': 'Gestion du planning théorique',
    'academy.menu.planning_theorique_matrix': 'Planning théorique',
    'academy.menu.planning_generation': 'Génération planning',
    'academy.menu.planning_deletion': 'Suppression planning',
    'academy.menu.attendance': 'Présences & Séances',
    'academy.menu.financial': 'Finance & Règlements',
    'academy.menu.reports': 'Rapports & Analyses',
    'academy.menu.quizzes': 'Quiz & Tests',
    'academy.menu.quizzes.list': 'Liste des Quiz & Tests',
    'academy.menu.quizzes.quiz_questions': 'Questions de Quiz',
    'academy.menu.quizzes.quiz_results': 'Résultats de Quiz',
    'academy.menu.quiz_questions': 'Questions de Quiz',
    'academy.menu.quiz_results': 'Résultats de Quiz',
    'academy.menu.planning_controls': 'Contrôles Planning',
    'academy.menu.discounts': 'Politique des Remises',
    'academy.menu.discount_validations': 'Validation des Remises',
    'academy.menu.parameters': 'Paramètres Académie',
    'academy.menu.data_exchange': 'Données & Échanges',
    'academy.menu.import_data': 'Importer des données',
    'academy.menu.export_data': 'Exporter des données',

    // Import & Export Données - FR
    'academy.import.page_title': 'Importation de Données',
    'academy.import.refresh': 'Actualiser',
    'academy.import.step1_title': "1. Sélectionnez l'entité à importer",
    'academy.import.in_database': 'en base',
    'academy.import.step2_title': '2. Téléchargez le modèle vierge',
    'academy.import.step2_desc': 'Utilisez notre modèle prêt à l\'emploi contenant les en-têtes préconfigurés et une ligne d\'exemple pour {0}.',
    'academy.import.btn_excel': 'Modèle Excel (.xlsx)',
    'academy.import.btn_csv': 'Modèle CSV (.csv)',
    'academy.import.view_rules': 'Voir les colonnes et règles ({0} champs)',
    'academy.import.step3_title': '3. Sélectionnez votre fichier à importer',
    'academy.import.drop_title': 'Glissez-déposez votre fichier ici, ou cliquez pour parcourir',
    'academy.import.file_selected': 'Fichier sélectionné : {0} ({1} Ko)',
    'academy.import.accepted_formats': 'Formats acceptés : Microsoft Excel (.xlsx, .xls) ou CSV (.csv UTF-8)',
    'academy.import.update_existing': 'Mettre à jour si le code existe déjà',
    'academy.import.btn_import': 'Importer {0} ligne(s)',
    'academy.import.importing': 'Importation en cours...',
    'academy.import.preview_title': 'Prévisualisation & Validation ({0} lignes)',
    'academy.import.preview_desc': 'Vérifiez la conformité des données avant de déclencher l\'écriture en base.',
    'academy.import.filter_all': 'Toutes ({0})',
    'academy.import.filter_valid': 'Valides ({0})',
    'academy.import.filter_errors': 'Erreurs ({0})',
    'academy.import.status_valid': 'Valide',
    'academy.import.status_incomplete': 'Incomplet',
    'academy.import.col_index': '#',
    'academy.import.col_status': 'Statut',
    'academy.import.preview_limit': 'Affichage limité aux 100 premières lignes sur {0}. L\'importation traitera l\'ensemble du fichier.',
    'academy.import.dialog_rules_title': 'Structure du fichier d\'import : {0}',
    'academy.import.dialog_rules_subtitle': 'Entité : {0}. Identifiant de référence : {1}.',
    'academy.import.col_file_header': 'En-tête dans le fichier',
    'academy.import.col_data_type': 'Type de données',
    'academy.import.col_required': 'Obligatoire',
    'academy.import.col_sample': 'Exemple',
    'academy.import.col_description': 'Description',
    'academy.import.required_yes': 'Oui',
    'academy.import.required_optional': 'Optionnel',
    'academy.import.dialog_close': 'Fermer',
    'academy.import.report_title': 'Rapport d\'Importation',
    'academy.import.report_processed': 'Traité(s)',
    'academy.import.report_created': 'Créé(s)',
    'academy.import.report_updated': 'Mis à jour',
    'academy.import.report_errors': 'Erreur(s)',
    'academy.import.report_errors_detail': 'Détails des rejets :',
    'academy.import.report_row_error': 'Ligne {0} ({1}): {2}',
    'academy.import.report_finish': 'Terminer',
    'academy.import.msg_no_rows': 'Le fichier ne contient aucune ligne de données exploitable.',
    'academy.import.msg_rows_detected': '{0} ligne(s) détectée(s) et analysée(s).',
    'academy.import.msg_success': 'Importation réussie : {0} créations, {1} mises à jour.',
    'academy.import.msg_warning': 'Importation terminée avec {0} avertissement(s)/erreur(s).',
    'academy.import.required_missing': 'Le champ obligatoire "{0}" est manquant.',

    'academy.export.page_title': 'Exportation de Données',
    'academy.export.header_title': 'Exportation des Données de l\'Agence',
    'academy.export.header_subtitle': 'Téléchargez l\'ensemble des données de votre agence connectée aux formats Microsoft Excel (.xlsx) ou CSV standard.',
    'academy.export.refresh': 'Actualiser',
    'academy.export.lines_count': '{0} lignes',
    'academy.export.columns_count': '{0} colonnes incluses',
    'academy.export.view_columns': 'Voir colonnes',
    'academy.export.generating': 'Génération de l\'export en cours...',
    'academy.export.btn_excel': 'Excel (.xlsx)',
    'academy.export.btn_csv': 'CSV (.csv)',
    'academy.export.dialog_title': 'Colonnes exportées : {0}',
    'academy.export.col_header': 'Colonne dans l\'export',
    'academy.export.col_type': 'Type',
    'academy.export.dialog_close': 'Fermer',
    'academy.export.msg_no_data': 'Aucune donnée à exporter pour "{0}" dans cette agence.',
    'academy.export.msg_success': 'Export de {0} ligne(s) terminé avec succès ({1}).',

    // Entités Academy - Titres & Descriptions - FR
    'academy.entity.rooms.title': 'Salles & Ressources',
    'academy.entity.rooms.desc': 'Gestion des salles de cours, laboratoires et équipements pédagogiques',
    'academy.entity.teachers.title': 'Enseignants & Formateurs',
    'academy.entity.teachers.desc': 'Corps professoral, formateurs externes et spécialistes pédagogiques',
    'academy.entity.students.title': 'Étudiants & Apprenants',
    'academy.entity.students.desc': 'Dossiers des étudiants inscrits, coordonnées et contacts d\'urgence',
    'academy.entity.groups.title': 'Groupes & Promotions',
    'academy.entity.groups.desc': 'Groupes de classe, cohortes et promotions d\'étudiants',
    'academy.entity.courses.title': 'Fiches Modules & Cours',
    'academy.entity.courses.desc': 'Catalogue des modules de formation, volumes horaires et tarification',

    // Libellés des colonnes (Champs) - FR
    'academy.field.ARM_ROOM_NUMBER.label': 'Numéro / Nom de Salle',
    'academy.field.ARM_ROOM_NUMBER.desc': 'Identifiant ou numéro unique de la salle',
    'academy.field.ARM_FLOOR.label': 'Étage / Bâtiment',
    'academy.field.ARM_FLOOR.desc': 'Localisation dans l\'établissement',
    'academy.field.ARM_CAPACITY.label': 'Capacité d\'accueil',
    'academy.field.ARM_CAPACITY.desc': 'Nombre maximal de places assises',
    'academy.field.ARM_EQUIPMENT.label': 'Équipements',
    'academy.field.ARM_EQUIPMENT.desc': 'Matériel disponible',
    'academy.field.ARM_STATUS.label': 'Statut',
    'academy.field.ARM_STATUS.desc': 'Active, Under Maintenance ou Archived',

    'academy.field.TCH_FULL_NAME.label': 'Nom et Prénom',
    'academy.field.TCH_FULL_NAME.desc': 'Nom complet de l\'enseignant',
    'academy.field.TCH_EMAIL.label': 'Adresse Email',
    'academy.field.TCH_EMAIL.desc': 'Email professionnel unique',
    'academy.field.TCH_PHONE.label': 'Téléphone',
    'academy.field.TCH_PHONE.desc': 'Numéro de contact',
    'academy.field.TCH_SPECIALIZATION.label': 'Spécialité',
    'academy.field.TCH_SPECIALIZATION.desc': 'Matière ou domaine d\'expertise',
    'academy.field.TCH_HOURLY_RATE.label': 'Tarif horaire (DZD)',
    'academy.field.TCH_HOURLY_RATE.desc': 'Rémunération horaire de base',
    'academy.field.TCH_BIO.label': 'Biographie / Notes',
    'academy.field.TCH_BIO.desc': 'Profil et expérience',

    'academy.field.STD_FIRST_NAME.label': 'Prénom',
    'academy.field.STD_FIRST_NAME.desc': 'Prénom de l\'étudiant',
    'academy.field.STD_LAST_NAME.label': 'Nom de Famille',
    'academy.field.STD_LAST_NAME.desc': 'Nom de famille de l\'étudiant',
    'academy.field.STD_EMAIL.label': 'Adresse Email',
    'academy.field.STD_EMAIL.desc': 'Email personnel de l\'étudiant (identifiant unique)',
    'academy.field.STD_PHONE.label': 'Numéro de Téléphone',
    'academy.field.STD_PHONE.desc': 'Téléphone mobile',
    'academy.field.STD_DATE_OF_BIRTH.label': 'Date de Naissance (AAAA-MM-JJ)',
    'academy.field.STD_DATE_OF_BIRTH.desc': 'Format YYYY-MM-DD',
    'academy.field.STD_EMERGENCY_CONTACT.label': 'Contact d\'Urgence',
    'academy.field.STD_EMERGENCY_CONTACT.desc': 'Personne à contacter en cas d\'urgence',

    'academy.field.STD_CATEGORY.label': 'Catégorie (adult / junior)',
    'academy.field.STD_CATEGORY.desc': 'Catégorie d\'apprenant : adult ou junior',
    'academy.field.STD_GENDER.label': 'Genre (male / female)',
    'academy.field.STD_GENDER.desc': 'Genre de l\'apprenant : male ou female',
    'academy.field.STD_ADDRESS.label': 'Adresse Résidentielle',
    'academy.field.STD_ADDRESS.desc': 'Adresse de résidence complète',
    'academy.field.STD_REGISTRATION_STATUS.label': 'Statut d\'Inscription',
    'academy.field.STD_REGISTRATION_STATUS.desc': 'Active, Pre-Registered ou Refused',
    'academy.field.STD_FINANCIAL_HOLD.label': 'Blocage Financier (1/0)',
    'academy.field.STD_FINANCIAL_HOLD.desc': '1 = Bloqué pour impayé, 0 = En règle',
    'academy.field.STD_PROFESSION.label': 'Profession (Adulte)',
    'academy.field.STD_PROFESSION.desc': 'Métier ou statut professionnel',
    'academy.field.STD_SCHOOL_LEVEL.label': 'Niveau Scolaire (Junior)',
    'academy.field.STD_SCHOOL_LEVEL.desc': 'Maternelle, Primaire, CEM, Lycée, Universitaire ou Autre',
    'academy.field.STD_BLOOD_GROUP.label': 'Groupe Sanguin',
    'academy.field.STD_BLOOD_GROUP.desc': 'A+, A-, B+, B-, AB+, AB-, O+, O-',
    'academy.field.STD_HAS_HEALTH_ISSUES.label': 'Problèmes de Santé (1/0)',
    'academy.field.STD_HAS_HEALTH_ISSUES.desc': '1 = Oui, 0 = Non',
    'academy.field.STD_HEALTH_CONDITIONS.label': 'Pathologies / Affections',
    'academy.field.STD_HEALTH_CONDITIONS.desc': 'Liste des affections ou pathologies',
    'academy.field.STD_HEALTH_OTHER_DETAIL.label': 'Détail Autres Problèmes Santé',
    'academy.field.STD_HEALTH_OTHER_DETAIL.desc': 'Précisions sur la santé',
    'academy.field.STD_HAS_ALLERGIES.label': 'Allergies (1/0)',
    'academy.field.STD_HAS_ALLERGIES.desc': '1 = Oui, 0 = Non',
    'academy.field.STD_ALLERGY_DETAILS.label': 'Détails des Allergies',
    'academy.field.STD_ALLERGY_DETAILS.desc': 'Précisions sur les substances allergènes',
    'academy.field.STD_IMAGE_RIGHT.label': 'Droit à l\'Image (1/0)',
    'academy.field.STD_IMAGE_RIGHT.desc': '1 = Autorisé, 0 = Refusé',
    'academy.field.STD_GUARDIAN_FIRST_NAME.label': 'Prénom du Tuteur (Junior)',
    'academy.field.STD_GUARDIAN_FIRST_NAME.desc': 'Prénom du parent ou tuteur',
    'academy.field.STD_GUARDIAN_LAST_NAME.label': 'Nom du Tuteur (Junior)',
    'academy.field.STD_GUARDIAN_LAST_NAME.desc': 'Nom du parent ou tuteur',
    'academy.field.STD_GUARDIAN_RELATION.label': 'Lien de Parenté (Junior)',
    'academy.field.STD_GUARDIAN_RELATION.desc': 'Père, Mère, Tuteur légal ou Autre',
    'academy.field.STD_GUARDIAN_PHONE.label': 'Téléphone du Tuteur',
    'academy.field.STD_GUARDIAN_PHONE.desc': 'Numéro de téléphone du tuteur',
    'academy.field.STD_GUARDIAN_EMAIL.label': 'Email du Tuteur',
    'academy.field.STD_GUARDIAN_EMAIL.desc': 'Adresse email du tuteur',
    'academy.field.STD_AUTHORIZED_PICKUP.label': 'Personnes Autorisées Récupération',
    'academy.field.STD_AUTHORIZED_PICKUP.desc': 'Personnes autorisées à récupérer l\'élève',
    'academy.field.STD_TIME_PREFERENCE.label': 'Préférence Horaire',
    'academy.field.STD_TIME_PREFERENCE.desc': 'Morning, Afternoon, Evening, Weekend',

    'academy.field.GRP_NAME.label': 'Nom du Groupe / Promotion',
    'academy.field.GRP_NAME.desc': 'Identifiant ou nom du groupe',
    'academy.field.GRP_MAX_CAPACITY.label': 'Capacité Maximale',
    'academy.field.GRP_MAX_CAPACITY.desc': 'Nombre maximal d\'étudiants dans le groupe',
    'academy.field.GRP_STATUS.label': 'Statut',
    'academy.field.GRP_STATUS.desc': 'Upcoming, Ongoing ou Completed',

    'academy.field.CRS_TITLE.label': 'Intitulé du Module / Cours',
    'academy.field.CRS_TITLE.desc': 'Titre de la formation',
    'academy.field.CRS_TOTAL_HOURS.label': 'Volume Horaire (Heures)',
    'academy.field.CRS_TOTAL_HOURS.desc': 'Durée totale de la formation en heures',
    'academy.field.CRS_PRICE.label': 'Tarif Indicatif (DZD)',
    'academy.field.CRS_PRICE.desc': 'Prix de référence',
    'academy.field.CRS_DESCRIPTION.label': 'Description du Programme',
    'academy.field.CRS_DESCRIPTION.desc': 'Objectifs et compétences visées',

    // Types de données - FR
    'academy.dataType.string': 'texte',
    'academy.dataType.int': 'entier',
    'academy.dataType.decimal': 'décimal',
    'academy.dataType.date': 'date',
    'academy.dataType.boolean': 'booléen',

    // Remises commerciales (Commercial Discounts) - FR
    'academy.discounts.title': 'Politique des Remises Commerciales',
    'academy.discounts.subtitle': 'Gestion des catégories de remises, de l\'affectation des apprenants et des règles de validation.',
    'academy.discounts.add_new': 'Nouvelle Remise',
    'academy.discounts.back_to_list': 'Retour à la Liste',
    'academy.discounts.create_title': 'Nouvelle Remise Commerciale',
    'academy.discounts.edit_title': 'Modifier la Remise: {0}',
    'academy.discounts.title_new': 'Nouvelle Remise Commerciale',
    'academy.discounts.title_edit': 'Modifier la Remise ({code})',
    'academy.discounts.total_count': 'Total des Remises Paramétrées',
    'academy.discounts.auto_count': 'Remises à Application Automatique',
    'academy.discounts.validation_count': 'Validation Admin / Directeur Requise',
    'academy.discounts.empty_title': 'Aucune remise configurée',
    'academy.discounts.empty_subtitle': 'Créez une nouvelle remise pour encourager les inscriptions.',
    'academy.discounts.search_placeholder': 'Rechercher par code, nom, description...',
    'academy.discounts.code': 'Code Remise',
    'academy.discounts.name': 'Nom de la Remise',
    'academy.discounts.percentage': 'Pourcentage (%)',
    'academy.discounts.min_courses': 'Nombre Min. Formations',
    'academy.discounts.application': 'Application',
    'academy.discounts.validation': 'Validation Requise',
    'academy.discounts.validation_role': 'Rôle Requis pour la Validation',
    'academy.discounts.role_auto': 'Auto (Validation automatique)',
    'academy.discounts.role_planner': 'Planificateur',
    'academy.discounts.role_admin': 'Admin',
    'academy.discounts.requires_val_check': 'Exige une validation formelle avant application',
    'academy.discounts.is_auto_applied_check': 'Appliquer automatiquement si les conditions sont remplies',
    'academy.discounts.auto_apply_check': 'Appliquer automatiquement en cas d\'éligibilité',
    'academy.discounts.is_active': 'Remise Active',
    'academy.discounts.description': 'Description & Conditions',
    'academy.discounts.desc_label': 'Description et critères d\'application',
    'academy.discounts.assigned_students': 'Apprenants affectés',
    'academy.discounts.used_invoices': 'Factures utilisées',
    'academy.discounts.linked_invoices': 'Factures Liées',
    'academy.discounts.auto_label': 'Automatique',
    'academy.discounts.manual_label': 'Manuelle / Affectée',
    'academy.discounts.no_validation': 'Aucune',
    'academy.discounts.btn_assign_students': 'Affecter aux apprenants',
    'academy.discounts.assign_dialog_title': 'Affecter la Remise aux Apprenants',
    'academy.discounts.assign_dialog_desc': 'Sélectionnez les apprenants bénéficiant de la remise "{0}" (-{1}%).',
    'academy.discounts.validation.code_required': 'Le code de la remise est obligatoire.',
    'academy.discounts.validation.name_required': 'Le nom de la remise est obligatoire.',
    'academy.discounts.assign_success': 'Remise "{0}" affectée avec succès à {1} apprenant(s).',
    'academy.discounts.assign_error': "Échec de l'affectation des apprenants.",
    'academy.discounts.confirm_delete': 'Êtes-vous sûr de vouloir supprimer la remise "{0}" ?',
    'academy.discounts.msg_saved': 'Remise enregistrée avec succès.',
    'academy.discounts.msg_deleted': 'Remise {code} supprimée avec succès.',
    'academy.discounts.deleted_success': 'Remise supprimée avec succès.',
    'academy.discounts.msg_blocked_delete': 'Action bloquée : La remise {code} ({name}) a déjà été affectée à des apprenants ou appliquée sur des factures.',
    'academy.discounts.assign_search_placeholder': 'Rechercher un apprenant (nom, email, téléphone)...',
    'academy.discounts.assign_no_results': 'Aucun apprenant ne correspond à votre recherche.',
    'academy.discounts.assign_assigned_count': '{0} / {1} affecté(s)',
    'academy.discounts.assign_select_all': 'Tout sélectionner',
    'academy.discounts.assign_deselect_all': 'Tout déselectionner',

    // Validation des Remises (Discount Validations) - FR
    'academy.discount_validations.title': 'Validation des Remises sur Factures',
    'academy.discount_validations.access_denied_title': 'Accès Réservé aux Administrateurs',
    'academy.discount_validations.access_denied_desc': "Cette page est strictement réservée à l'équipe de Direction et d'Administration pour la validation des demandes de remises commerciales soumises sur les factures.",
    'academy.discount_validations.kpi_pending': 'EN ATTENTE',
    'academy.discount_validations.kpi_approved': 'VALIDÉES',
    'academy.discount_validations.kpi_rejected': 'REFUSÉES',
    'academy.discount_validations.kpi_total_pending': 'TOTAL REMISES EN ATTENTE',
    'academy.discount_validations.tab_pending': 'En attente ({0})',
    'academy.discount_validations.tab_approved': 'Validées ({0})',
    'academy.discount_validations.tab_rejected': 'Refusées ({0})',
    'academy.discount_validations.tab_all': 'Toutes ({0})',
    'academy.discount_validations.search_placeholder': 'Rechercher par apprenant, facture, code remise, motif...',
    'academy.discount_validations.empty_title': 'Aucune demande de remise trouvée',
    'academy.discount_validations.empty_pending_desc': 'Toutes les demandes de remise ont été traitées.',
    'academy.discount_validations.empty_filter_desc': 'Aucune entrée pour ce filtre.',
    'academy.discount_validations.col_date_requester': 'Date & Demandeur',
    'academy.discount_validations.col_invoice_student': 'Facture & Apprenant',
    'academy.discount_validations.col_requested_discount': 'Remise Demandée',
    'academy.discount_validations.col_financial_impact': 'Impact Financier',
    'academy.discount_validations.col_justification': 'Motif / Justification',
    'academy.discount_validations.col_status': 'Statut',
    'academy.discount_validations.col_actions': 'Actions Décisionnelles',
    'academy.discount_validations.by_requester': 'Par : {0}',
    'academy.discount_validations.gross_label': 'Brut : ',
    'academy.discount_validations.net_label': 'Net : ',
    'academy.discount_validations.savings_label': 'Économie : -',
    'academy.discount_validations.no_reason': 'Aucun motif précisé',
    'academy.discount_validations.rejection_reason_label': 'Motif rejet : ',
    'academy.discount_validations.status_pending': 'En attente',
    'academy.discount_validations.status_approved': 'Validée',
    'academy.discount_validations.status_rejected': 'Refusée',
    'academy.discount_validations.status_processed': 'Traité',
    'academy.discount_validations.btn_approve': 'Valider',
    'academy.discount_validations.btn_reject': 'Refuser',
    'academy.discount_validations.dialog_approve_title': 'Confirmer la Validation',
    'academy.discount_validations.dialog_approve_alert': 'En validant cette demande, le montant net de la facture {0} sera mis à jour de {1} à {2} (-{3}%).',
    'academy.discount_validations.dialog_approve_confirm': 'Confirmer la Validation',
    'academy.discount_validations.dialog_reject_title': 'Refuser la Remise',
    'academy.discount_validations.dialog_reject_alert': "La remise sera refusée et la facture {0} conservera son montant d'origine ({1}).",
    'academy.discount_validations.dialog_reject_field_label': 'Motif du refus',
    'academy.discount_validations.dialog_reject_placeholder': 'Précisez la raison du refus (ex: justificatif manquant, politique non applicable)...',
    'academy.discount_validations.dialog_reject_required': 'Le motif du refus est obligatoire.',
    'academy.discount_validations.dialog_reject_confirm': 'Confirmer le Refus',
    'academy.discount_validations.approve_success': 'La remise de -{0}% sur la facture {1} a été validée.',
    'academy.discount_validations.reject_info_title': 'Demande refusée',
    'academy.discount_validations.reject_info_msg': 'La demande de remise sur la facture {0} a été refusée.',
    'academy.discount_validations.approve_error': 'Échec de la validation de la remise.',
    'academy.discount_validations.reject_error': 'Échec du refus de la remise.',

    // Gestion des remises sur factures (Financial Invoice Discounts) - FR
    'academy.financial.discount.dialog_title': 'Gérer la Remise sur Facture',
    'academy.financial.discount.student_label': 'Apprenant',
    'academy.financial.discount.initial_gross': 'Montant Brut Initial',
    'academy.financial.discount.already_paid': 'Déjà Réglé',
    'academy.financial.discount.policy_label': 'Politique de remise commerciale',
    'academy.financial.discount.policy_helper': 'Sélectionnez une remise du catalogue ou saisissez un pourcentage personnalisé',
    'academy.financial.discount.none_option': 'Aucune remise (0% / Supprimer la remise)',
    'academy.financial.discount.custom_option': 'Pourcentage personnalisé...',
    'academy.financial.discount.custom_percent_label': 'Pourcentage de remise (%)',
    'academy.financial.discount.simulation_title': 'Simulation Financière',
    'academy.financial.discount.gross_label': 'Montant Brut :',
    'academy.financial.discount.deduction_label': 'Déduction Remise (-{0}%) :',
    'academy.financial.discount.new_net_invoiced': 'Nouveau Net Facturé :',
    'academy.financial.discount.new_due_rest': 'Nouveau Solde Restant Dû :',
    'academy.financial.discount.justification_label': 'Motif ou justification de la remise',
    'academy.financial.discount.justification_placeholder': 'Précisez la raison de cette remise (ex: accord direction, parrainage, fratrie, régularisation commerciale)...',
    'academy.financial.discount.justification_required': 'Le motif ou justification de la remise est obligatoire.',
    'academy.financial.discount.direct_admin_notice': "En tant qu'administrateur, cette remise sera appliquée directement sur la facture.",
    'academy.financial.discount.direct_planner_notice': 'Cette remise (Planificateur / Auto) sera appliquée immédiatement sur la facture.',
    'academy.financial.discount.pending_admin_notice': "Cette remise requiert une validation par l'administration (Admin). Votre demande sera placée en attente et transmise aux administrateurs.",
    'academy.financial.discount.btn_apply_direct': 'Appliquer Immédiatement',
    'academy.financial.discount.btn_submit_pending': 'Soumettre pour Validation',
    'academy.financial.discount.manage_tooltip': 'Gérer la remise sur cette facture',
    'academy.financial.discount.pending_tooltip': 'Demande en attente de validation (-{0}%) : {1}',
    'academy.financial.discount.pending_badge': 'En attente (-{0}%)',
    'academy.financial.discount.validation_auto': 'Validation auto',
    'academy.financial.discount.validation_admin': 'Validation admin',
    'academy.financial.discount.success_applied': 'La remise a été appliquée avec succès sur la facture.',
    'academy.financial.discount.success_submitted': 'Demande de remise soumise avec succès pour validation.',

    // boubaker 25/08/2026 - groupes menu restructurés
    'academy.menu.resources': 'Ressources',
    'academy.menu.students_groups': 'Apprenants & Groupes',
    'academy.menu.planning': 'Planning',
    'academy.menu.pedagogy': 'Pédagogie',
    'academy.menu.finance': 'Finance',
    'academy.menu.monitoring': 'Suivi & Indicateurs',

    // boubaker 23/08/2026 - menu Impressions Academy
    'academy.menu.prints': 'Impressions',
    'academy.menu.prints.planning': 'Planning',
    'academy.menu.prints.invoices': 'Factures',
    'academy.menu.prints.receipts': 'Reçus de Paiement',
    'academy.menu.prints.daily_cash': 'Rapport de Caisse Journalier',
    'academy.menu.prints.hub': "Centre d'éditions",
    'academy.menu.prints.group_documents': 'Documents & Certificats',
    'academy.menu.prints.group_lists': 'Listes & Appels',
    'academy.menu.prints.group_correspondence': 'Correspondance & Médical',
    'academy.menu.prints.group_finance': 'Finances & Planning',
    'academy.menu.prints.attendance_sheet': 'Feuille de présence',
    'academy.menu.prints.school_certificate': 'Certificat de scolarité',
    'academy.menu.prints.registration_form': "Fiche d'inscription",
    'academy.menu.prints.students_list': 'Liste des apprenants',
    'academy.menu.prints.information_sheet': 'Fiche de renseignements',
    'academy.menu.prints.student_cards': "Badge d'apprenant",
    'academy.menu.prints.student_badge': "Badge d'apprenant",
    'academy.menu.prints.cancellation_certificate': 'Certificat de radiation',
    'academy.menu.prints.conduct_certificate': 'Certificat de bonne conduite',
    'academy.menu.prints.parent_convocation': 'Convocation du parent',
    'academy.menu.prints.emergency_form': "Fiche d'urgence et soins",

    // boubaker 23/08/2026 - pages d'impression Academy
    'academy.print.planning': 'Planning des Séances',
    'academy.print.invoices': 'Impression des Factures',
    'academy.print.receipts': 'Impression des Reçus de Paiement',
    'academy.print.daily_cash': 'Rapport de Caisse Journalier',
    'academy.print.status_all': 'Tous les statuts',
    'academy.print.method_all': 'Tous les modes de paiement',
    'academy.print.col_invoice': 'N° Facture',
    'academy.print.col_student': 'Apprenant',
    'academy.print.col_issued': 'Émise le',
    'academy.print.col_due': 'Échéance',
    'academy.print.col_total': 'Total',
    'academy.print.col_paid': 'Payé',
    'academy.print.col_status': 'Statut',
    'academy.print.col_receipt': 'N° Reçu',
    'academy.print.col_date': 'Date',
    'academy.print.col_method': 'Mode',
    'academy.print.col_amount': 'Montant',
    'academy.print.total': 'TOTAL',
    'academy.print.receipts_count': 'Nombre de reçus',
    'academy.print.cash': 'Espèces',
    'academy.print.card': 'Carte bancaire',
    'academy.print.transfer': 'Virement',
    'academy.print.no_data': 'Aucune donnée. Ajustez les filtres puis cliquez sur « Charger ».',
    'academy.print.status.Paid': 'Payée',
    'academy.print.status.Unpaid': 'Non payée',
    'academy.print.status.Partial': 'Partielle',

    // boubaker 25/08/2026 - Indicateur : traçabilité des envois mail
    'academy.menu.indicateur': 'Indicateurs',
    'academy.menu.indicateur.mail_log': 'Traçabilité Envois Mail',
    'academy.indicateur.mail_log': 'Traçabilité des Envois Mail',
    'academy.indicateur.col_date': 'Date',
    'academy.indicateur.col_to': 'Destinataire',
    'academy.indicateur.col_subject': 'Sujet / Corps',
    'academy.indicateur.col_status': 'Statut',
    'academy.indicateur.col_smtp': 'Serveur SMTP',
    'academy.indicateur.col_action': 'Action',
    'academy.indicateur.view_body': 'Voir le corps du mail',
    'academy.indicateur.mail.status': 'Statut',
    'academy.indicateur.mail.to_filter': 'Filtrer par email...',
    'academy.indicateur.status.sent': 'Envoyé',
    'academy.indicateur.status.failed': 'Échoué',
    'academy.indicateur.status.pending': 'En attente',
    'academy.indicateur.col_type': 'Type',
    'academy.indicateur.mail.type_filter': 'Type d\'email',
    'academy.indicateur.mail_type.all': 'Tous les types',
    'academy.indicateur.mail_type.debt_reminder': 'Relance Dette',
    'academy.indicateur.mail_type.auth_otp': 'Code OTP / Connexion',
    'academy.indicateur.mail_type.change_password': 'Changement Mot de Passe',
    'academy.indicateur.mail_type.reset_password': 'Réinitialisation Mot de Passe',
    'academy.indicateur.mail_type.general': 'Général',

    // Academy Scheduler
    'academy.scheduler.title': 'Matrice du Planning (Jour, Semaine, Mois)',
    'academy.scheduler.view_day': 'Vue Jour',
    'academy.scheduler.view_week': 'Vue Semaine',
    'academy.scheduler.view_month': 'Vue Mois',
    'academy.scheduler.group_by': 'Grouper par :',
    'academy.scheduler.group_by_room': 'Salles & Équipements',
    'academy.scheduler.group_by_group': 'Groupes & Cohortes',
    'academy.scheduler.group_by_teacher': 'Enseignants',
    'academy.scheduler.select_date': 'Date',
    'academy.scheduler.select_month': 'Mois & Année',
    'academy.scheduler.today': 'Aujourd\'hui',
    'academy.scheduler.book_slot': '+ Réserver',
    'academy.scheduler.schedule_btn': 'Planifier une Séance',
    'academy.scheduler.filters': 'Filtres :',
    'academy.scheduler.all_rooms': 'Toutes les Salles',
    'academy.scheduler.all_teachers': 'Tous les Enseignants',
    'academy.scheduler.all_groups': 'Tous les Groupes',
    'academy.scheduler.session_details': 'Détails de la Séance',
    'academy.scheduler.cancel_session': 'Annuler la séance',
    'academy.scheduler.confirm_cancel': 'Êtes-vous sûr de vouloir annuler cette séance ?',
    'academy.scheduler.msg_session_cancelled': 'Séance annulée et supprimée avec succès.',

    // boubaker 22/08/2026 - onglets planning + impression + drag&drop
    'academy.scheduler.tab_new': 'Nouveau planning',
    'academy.scheduler.load': 'Charger',
    'academy.scheduler.start_date': 'Date de début',
    'academy.scheduler.end_date': 'Date de fin',
    'academy.scheduler.not_loaded': 'Sélectionnez vos filtres puis cliquez sur « Charger » pour afficher le planning.',
    'academy.scheduler.print': 'Imprimer',
    'academy.scheduler.print_title': 'Impression du Planning',
    'academy.scheduler.edit_session': 'Modifier',
    'academy.scheduler.save_changes': 'Enregistrer les modifications',
    'academy.scheduler.session_updated': 'Séance mise à jour avec succès.',
    'academy.scheduler.drag_hint': 'Astuce : glissez-déposez une séance pour changer de jour (vue semaine/mois) ou d\'horaire (vue jour).',
    'academy.scheduler.print.col_day': 'Jour',
    'academy.scheduler.print.col_start': 'Début',
    'academy.scheduler.print.col_end': 'Fin',
    'academy.scheduler.print.col_room': 'Salle',
    'academy.scheduler.print.col_teacher': 'Enseignant',
    'academy.scheduler.print.col_group': 'Groupe',
    'academy.scheduler.print.month_label': 'Mois',

    // Absence & Session status management - FR
    'academy.scheduler.session_status': 'Statut de la séance',
    'academy.scheduler.status.scheduled': 'Programmée',
    'academy.scheduler.status.completed': 'Terminée',
    'academy.scheduler.status.cancelled': 'Annulée',
    'academy.scheduler.status.postponed': 'Reportée',
    'academy.scheduler.status_reason': 'Motif / Justification',
    'academy.scheduler.postponed_date': 'Nouvelle date de report',
    'academy.scheduler.postponed_time': 'Nouvel horaire',
    'academy.scheduler.teacher_absent': 'Enseignant absent',
    'academy.scheduler.postponed_to': 'Reportée au {0} {1}',
    'academy.teachers.absence_requests': 'Demandes d\'absence',
    'academy.teachers.absence_requests_title': 'Demandes d\'absence des enseignants',
    'academy.teachers.filter_all': 'Toutes',
    'academy.teachers.filter_pending': 'En attente',
    'academy.teachers.filter_approved': 'Validées',
    'academy.teachers.filter_rejected': 'Refusées',
    'academy.teachers.request_date': 'Date de demande',
    'academy.teachers.absence_reason': 'Motif de l\'absence',
    'academy.teachers.attached_document': 'Document justificatif',
    'academy.teachers.view_document': 'Voir le document',
    'academy.teachers.admin_comment': 'Commentaire Admin',
    'academy.teachers.admin_comment_placeholder': 'Saisir un commentaire ou instruction pour l\'enseignant...',
    'academy.teachers.approve_absence': 'Valider l\'absence',
    'academy.teachers.reject_absence': 'Refuser la demande',
    'academy.teachers.update_session_prompt': 'Mettre à jour la séance liée en Annulée',
    'academy.teachers.absence_approved_success': 'La demande d\'absence a été validée avec succès.',
    'academy.teachers.absence_rejected_success': 'La demande d\'absence a été refusée.',
    'academy.teachers.no_absence_requests': 'Aucune demande d\'absence trouvée.',

    // Academy Planning Théorique - FR
    'academy.planning_theorique.title': 'Gestion du planning théorique',
    'academy.planning_theorique.subtitle': 'Définition des plannings types par salle, nombre de semaines et type de jour',
    'academy.planning_theorique.new': 'Nouveau Planning Théorique',
    'academy.planning_theorique.edit': 'Modifier Planning Théorique',
    'academy.planning_theorique.confirmDelete': 'Êtes-vous sûr de vouloir supprimer ce planning théorique ? Les créneaux associés seront également supprimés.',
    'academy.planning_theorique.col_room': 'Salle',
    'academy.planning_theorique.col_num_week': 'Nbr Semaines',
    'academy.planning_theorique.col_start_date': 'Date Début',
    'academy.planning_theorique.col_end_date': 'Date Fin',
    'academy.planning_theorique.col_day_type': 'Type de Jour',
    'academy.planning_theorique.day_normal': 'Normal',
    'academy.planning_theorique.day_holiday': 'Jour Férié',
    'academy.planning_theorique.section_general': 'Informations Générales',
    'academy.planning_theorique.field_room': 'Salle de cours',
    'academy.planning_theorique.field_num_week': 'Nombre de semaines du cycle',
    'academy.planning_theorique.field_start_date': 'Date de début de validité',
    'academy.planning_theorique.field_end_date': 'Date de fin de validité (optionnelle)',
    'academy.planning_theorique.field_day_type': 'Type de jour',
    'academy.planning_theorique.room_required': 'Veuillez sélectionner une salle.',
    'academy.planning_theorique.num_week_min': 'Le nombre de semaines doit être au moins de 1.',
    'academy.planning_theorique.start_date_required': 'La date de début est requise.',
    'academy.planning_theorique.end_before_start': 'La date de fin ne peut pas être antérieure à la date de début.',
    'academy.planning_theorique.open_matrix': 'Voir la Matrice',
    'academy.planning_theorique.empty': 'Aucun planning théorique configuré.',

    'academy.planning_theorique.manage_title': 'Gestion du planning théorique',
    'academy.planning_theorique.generation_title': 'Génération du planning réel',
    'academy.planning_theorique.deletion_title': 'Suppression des séances de planning',
    'academy.planning_theorique.new_title': 'Nouveau planning théorique',
    'academy.planning_theorique.edit_title': 'Modifier le planning théorique',
    'academy.planning_theorique.form_subtitle': 'Définissez la salle, le cycle de semaines et la validité du modèle',
    'academy.planning_theorique.new_button': 'Nouveau planning',
    'academy.planning_theorique.generate_button': 'Générer le planning',
    'academy.planning_theorique.delete_button': 'Supprimer les séances sélectionnées',

    'academy.planning_theorique.room': 'Salle',
    'academy.planning_theorique.nbr_weeks': 'Nombre de semaines',
    'academy.planning_theorique.start_date': 'Date de début',
    'academy.planning_theorique.end_date': 'Date de fin',
    'academy.planning_theorique.day_type': 'Type de jour',
    'academy.planning_theorique.type_normal': 'Normal',
    'academy.planning_theorique.type_holiday': 'Jour férié',
    'academy.planning_theorique.vacs_count': 'Nombre de créneaux',
    'academy.planning_theorique.week': 'Semaine',
    'academy.planning_theorique.weeks': 'Semaines',
    'academy.planning_theorique.indefinite': 'Indéfini',
    'academy.planning_theorique.period': 'Période',
    'academy.planning_theorique.date': 'Date',
    'academy.planning_theorique.hours': 'Horaires',
    'academy.planning_theorique.status': 'Statut',
    'academy.planning_theorique.group': 'Groupe',
    'academy.planning_theorique.teacher': 'Enseignant',
    'academy.planning_theorique.none': 'Aucun',
    'academy.planning_theorique.search_placeholder': 'Rechercher...',

    'academy.planning_theorique.room_helper': 'Salle attribuée à ce modèle de planning',
    'academy.planning_theorique.weeks_helper': 'Nombre de semaines composant la rotation (ex: 1 pour hebdomadaire récurrent)',
    'academy.planning_theorique.end_date_helper': 'Laissez vide si le planning théorique est à durée indéterminée',

    'academy.planning_theorique.msg_created': 'Planning théorique créé avec succès.',
    'academy.planning_theorique.msg_updated': 'Planning théorique mis à jour avec succès.',
    'academy.planning_theorique.msg_deleted': 'Planning théorique supprimé avec succès.',
    'academy.planning_theorique.no_data': 'Aucun planning théorique configuré pour le moment.',

    'academy.planning_theorique.validation.room_required': 'Veuillez sélectionner une salle.',
    'academy.planning_theorique.validation.weeks_required': 'Le nombre de semaines doit être supérieur ou égal à 1.',
    'academy.planning_theorique.validation.start_date_required': 'La date de début est obligatoire.',
    'academy.planning_theorique.validation.invalid_date_range': 'La date de fin ne peut pas être antérieure à la date de début.',
    'academy.planning_theorique.validation.hours_required': 'Les heures de début et de fin sont obligatoires.',
    'academy.planning_theorique.validation.invalid_time_range': 'L\'heure de fin doit être postérieure à l\'heure de début.',
    'academy.planning_theorique.validation.dates_required': 'Veuillez renseigner les dates de début et de fin.',

    'academy.planning_theorique.matrix_title': 'Planning Théorique',
    'academy.planning_theorique.matrix_subtitle': 'Grille hebdomadaire type des vacations',
    'academy.planning_theorique.matrix_config_btn': 'Paramètres d\'affichage',
    'academy.planning_theorique.filter_popup_title': 'Sélection du Planning Théorique',
    'academy.planning_theorique.select_theo': 'Choisir un planning théorique',
    'academy.planning_theorique.select_theo_helper': 'Sélectionnez le planning théorique par salle et période pour afficher sa matrice',
    'academy.planning_theorique.apply_filter': 'Charger le Planning',
    'academy.planning_theorique.filter_required': 'Veuillez sélectionner un planning théorique.',
    'academy.planning_theorique.slot_new': 'Nouveau créneau type',
    'academy.planning_theorique.slot_edit': 'Modifier le créneau',
    'academy.planning_theorique.slot_group': 'Groupe',
    'academy.planning_theorique.slot_teacher': 'Enseignant',
    'academy.planning_theorique.slot_start_time': 'Heure Début',
    'academy.planning_theorique.slot_end_time': 'Heure Fin',
    'academy.planning_theorique.slot_confirm_delete': 'Supprimer ce créneau théorique ?',
    'academy.planning_theorique.matrix_no_data': 'Veuillez sélectionner un planning théorique pour afficher sa grille.',
    'academy.planning_theorique.week_label': 'Semaine {0}',
    'academy.planning_theorique.filter_btn': 'Filtres d\'affichage',
    'academy.planning_theorique.refresh': 'Actualiser',
    'academy.planning_theorique.filter_dialog_title': 'Filtrer la matrice théorique',
    'academy.planning_theorique.filter_dialog_desc': 'Sélectionnez la période et les salles pour charger les plannings correspondants.',
    'academy.planning_theorique.filter_rooms': 'Salles',
    'academy.planning_theorique.select_all': 'Tout sélectionner',
    'academy.planning_theorique.deselect_all': 'Tout désélectionner',
    'academy.planning_theorique.load_btn': 'Charger',
    'academy.planning_theorique.not_loaded_title': 'Aucune donnée affichée',
    'academy.planning_theorique.not_loaded_desc': 'Veuillez appliquer les filtres pour afficher la matrice des plannings théoriques.',
    'academy.planning_theorique.open_filters': 'Ouvrir les filtres',
    'academy.planning_theorique.add_vac': 'Ajouter créneau',
    'academy.planning_theorique.no_matrix_data': 'Aucun créneau théorique pour ce planning.',
    'academy.planning_theorique.new_vac_title': 'Nouveau créneau théorique',
    'academy.planning_theorique.edit_vac_title': 'Modifier le créneau théorique',
    'academy.planning_theorique.day_num': 'Jour du cycle',
    'academy.planning_theorique.start_time': 'Heure de début',
    'academy.planning_theorique.end_time': 'Heure de fin',
    'academy.planning_theorique.msg_vac_created': 'Créneau théorique ajouté avec succès.',
    'academy.planning_theorique.msg_vac_updated': 'Créneau théorique modifié avec succès.',
    'academy.planning_theorique.msg_vac_deleted': 'Créneau théorique supprimé avec succès.',

    'academy.planning_theorique.gen_title': 'Génération du Planning Réel',
    'academy.planning_theorique.gen_subtitle': 'Instancier automatiquement les vacations réelles à partir des plannings théoriques',
    'academy.planning_theorique.gen_step1': '1. Sélectionner les Plannings Théoriques',
    'academy.planning_theorique.gen_step2': '2. Définir la Période Cible & Paramètres',
    'academy.planning_theorique.gen_target_start': 'Date de début de génération',
    'academy.planning_theorique.gen_target_end': 'Date de fin de génération',
    'academy.planning_theorique.gen_btn_run': 'Lancer la Génération',
    'academy.planning_theorique.gen_running': 'Génération en cours...',
    'academy.planning_theorique.gen_select_at_least_one': 'Veuillez sélectionner au moins un planning théorique.',
    'academy.planning_theorique.gen_dates_required': 'Veuillez spécifier la date de début et la date de fin.',
    'academy.planning_theorique.gen_confirm': 'Voulez-vous générer les sessions pour les plannings sélectionnés entre le {0} et le {1} ?',
    'academy.planning_theorique.gen_success': '{0} session(s) générée(s) avec succès !',
    'academy.planning_theorique.target_start_date': 'Date de début cible',
    'academy.planning_theorique.target_end_date': 'Date de fin cible',
    'academy.planning_theorique.all_rooms': 'Toutes les salles',
    'academy.planning_theorique.room_filter': 'Filtrer par salle',
    'academy.planning_theorique.all_selected': 'Tous les plannings sélectionnés',
    'academy.planning_theorique.plannings_selected': 'planning(s) sélectionné(s)',
    'academy.planning_theorique.select_at_least_one': 'Veuillez sélectionner au moins un planning théorique à générer.',
    'academy.planning_theorique.no_data_to_generate': 'Aucun planning théorique disponible',
    'academy.planning_theorique.no_data_to_generate_desc': 'Aucun planning théorique actif n\'a été trouvé pour la période spécifiée.',
    'academy.planning_theorique.generation_success_title': 'Génération du planning terminée avec succès !',
    'academy.planning_theorique.generation_success_subtitle': '{0} séance(s) créée(s) pour la période du {1} au {2}.',
    'academy.planning_theorique.generation_success_msg': '{0} séance(s) générée(s) avec succès.',
    'academy.planning_theorique.generation_success_with_skipped': '{0} séance(s) générée(s) avec succès ({1} créneau(x) en conflit ignoré(s)).',
    'academy.planning_theorique.sessions_generated': 'séance(s) générée(s)',
    'academy.planning_theorique.conflicts_skipped': 'créneau(x) en conflit ignoré(s)',
    'academy.planning_theorique.view_scheduler': "Consulter l'emploi du temps",

    'academy.planning_theorique.del_title': 'Suppression du Planning',
    'academy.planning_theorique.del_subtitle': 'Filtrer et supprimer en masse des vacations ou sessions réelles',
    'academy.planning_theorique.del_filter_title': 'Critères de sélection des sessions à supprimer',
    'academy.planning_theorique.del_date_from': 'Du',
    'academy.planning_theorique.del_date_to': 'Au',
    'academy.planning_theorique.del_all_rooms': 'Toutes les salles',
    'academy.planning_theorique.del_all_teachers': 'Tous les enseignants',
    'academy.planning_theorique.del_all_groups': 'Tous les groupes',
    'academy.planning_theorique.del_btn_search': 'Rechercher les sessions',
    'academy.planning_theorique.del_btn_delete': 'Supprimer la sélection',
    'academy.planning_theorique.del_confirm': 'ATTENTION : Voulez-vous vraiment supprimer définitivement les {0} session(s) sélectionnée(s) ? Cette action est irréversible.',
    'academy.planning_theorique.del_success': '{0} session(s) supprimée(s) avec succès.',
    'academy.planning_theorique.del_no_sessions': 'Aucune session trouvée pour ces critères.',
    'academy.planning_theorique.del_select_to_delete': 'Veuillez cocher au moins une session à supprimer.',
    'academy.planning_theorique.all_groups': 'Tous les groupes',
    'academy.planning_theorique.group_filter': 'Filtrer par groupe',
    'academy.planning_theorique.all_teachers': 'Tous les enseignants',
    'academy.planning_theorique.teacher_filter': 'Filtrer par enseignant',
    'academy.planning_theorique.sessions_selected': 'séance(s) sélectionnée(s)',
    'academy.planning_theorique.select_at_least_one_session': 'Veuillez cocher au moins une séance à supprimer.',
    'academy.planning_theorique.no_sessions_found': 'Aucune séance trouvée',
    'academy.planning_theorique.no_sessions_found_desc': 'Aucune séance de planning ne correspond aux critères de recherche sélectionnés.',
    'academy.planning_theorique.deletion_success_title': 'Suppression terminée avec succès !',
    'academy.planning_theorique.deletion_success_subtitle': '{0} séance(s) supprimée(s) définitivement du planning.',
    'academy.planning_theorique.deletion_success_msg': '{0} séance(s) supprimée(s) avec succès.',
    'academy.planning_theorique.sessions_deleted': 'séance(s) supprimée(s)',

    // Common Aliases - FR
    'commun.error': 'Erreur',
    'commun.warning': 'Attention',
    'commun.success': 'Succès',
    'commun.confirmation': 'Confirmation',
    'commun.cancel': 'Annuler',
    'commun.save': 'Enregistrer',
    'commun.edit': 'Modifier',
    'commun.delete': 'Supprimer',
    'commun.back_to_list': 'Retour à la liste',
    'commun.view_grid': 'Vue Grille',
    'commun.view_table': 'Vue Tableau',

    // Academy Planning Controls
    'academy.planning_controls.title': 'Contrôles & Règles du Planning',
    'academy.planning_controls.subtitle': 'Paramétrez les règles de validation applicables lors de la création, modification et annulation de séances.',
    'academy.planning_controls.add_new': 'Nouveau Contrôle',
    'academy.planning_controls.active_count': 'Contrôles Actifs',
    'academy.planning_controls.blocking_count': 'Règles Bloquantes (Refus)',
    'academy.planning_controls.warning_count': 'Règles Avertissement',
    'academy.planning_controls.rules_info': 'Règles de fonctionnement des contrôles :',
    'academy.planning_controls.code': 'Code Contrôle',
    'academy.planning_controls.lib': 'Intitulé / Libellé',
    'academy.planning_controls.description': 'Description & Impact',
    'academy.planning_controls.active': 'Actif',
    'academy.planning_controls.blocking': 'Bloquant (Block)',
    'academy.planning_controls.status': 'Statut',
    'academy.planning_controls.status_disabled': 'Désactivé',
    'academy.planning_controls.status_blocking': 'Bloquant',
    'academy.planning_controls.status_warning': 'Avertissement',
    'academy.planning_controls.modal_title_add': 'Nouveau Contrôle du Planning',
    'academy.planning_controls.modal_title_edit': 'Modifier le Contrôle',
    'academy.planning_controls.confirm_delete': 'Êtes-vous sûr de vouloir supprimer ce contrôle ?',
    'academy.planning_controls.back_to_list': 'Retour à la Liste',
    'academy.planning_controls.msg_active_updated': 'Contrôle {code} mis à jour : Actif = {active}',
    'academy.planning_controls.msg_blocking_updated': 'Contrôle {code} mis à jour : Bloquant = {blocking}',
    'academy.planning_controls.msg_saved': 'Contrôle du planning enregistré avec succès.',
    'academy.planning_controls.msg_deleted': 'Contrôle {code} supprimé.',
    'academy.planning_controls.validation.code_required': 'Le code du contrôle est obligatoire.',
    'academy.planning_controls.validation.lib_required': 'Le libellé du contrôle est obligatoire.',
    'academy.planning_controls.view_table': 'Vue Tableau',
    'academy.planning_controls.view_grid': 'Vue Grille',
    'academy.planning_controls.no_description': 'Aucune description spécifiée.',
    'academy.planning_controls.form_title_edit': 'Modifier le Contrôle ({code})',
    'academy.planning_controls.form_title_new': 'Définir un Nouveau Contrôle de Validation',
    'academy.planning_controls.code_helper': 'Code unique identifiant la règle (ex: CTL_CUSTOM_101)',
    'academy.planning_controls.lib_helper': 'Intitulé compréhensible affiché lors des alertes ou vérifications',
    'academy.planning_controls.desc_helper': 'Description explicative de l\'impact de ce contrôle',
    'academy.planning_controls.active_desc': 'Active ou désactive la vérification automatique de ce contrôle',
    'academy.planning_controls.blocking_desc': 'Si activé, l\'action est strictement bloquée avec refus. Sinon, simple avertissement.',
    'academy.common.yes': 'Oui',
    'academy.common.no': 'Non',

    // Academy Parameters
    'academy.parameters.pageTitle': 'Paramètres Académie',
    'academy.parameters.matrix_title': 'Paramètres de la Matrice du Planning (Créneaux & Affichage)',
    'academy.parameters.matrix_desc': 'Définissez l\'amplitude horaire, la durée des créneaux, le premier jour de la semaine et le mode d\'affichage par défaut.',
    'academy.parameters.start_time': 'Heure de Début',
    'academy.parameters.end_time': 'Heure de Fin',
    'academy.parameters.slot_duration': 'Durée du Créneau (min)',
    'academy.parameters.first_day': 'Premier Jour Semaine',
    'academy.parameters.default_view': 'Affichage Planning par Défaut',
    'academy.parameters.weekend_days': 'Jours du Week-end',
    'academy.parameters.weekend_color': 'Couleur de Fond du Week-end',
    'academy.parameters.agencyTitle': 'Paramètres Agence #{0}',
    'academy.parameters.saveTooltip': 'Enregistrer ce paramètre',
    'academy.parameters.saveSuccess': 'Le paramètre « {0} » a été sauvegardé avec succès.',
    'academy.parameters.saveError': 'Impossible de sauvegarder le paramètre « {0} ».',
    'academy.parameters.timerTitle': 'Rafraîchissement du Tableau de Bord',
    'academy.parameters.timerDesc': 'Fréquence en secondes de mise à jour automatique des statistiques du Dashboard.',
    'academy.parameters.timerLabel': 'Minuteur de rafraîchissement (Secondes)',
    'academy.parameters.timerUnit': 'sec',
    'academy.parameters.timerDisabledHelper': '0 = Désactivé (données chargées uniquement à l\'ouverture)',
    'academy.parameters.timerActiveHelper': 'Mise à jour toutes les {0} secondes',
    'academy.parameters.quickPresets': 'Raccourcis rapides :',
    'academy.parameters.chipDisabled': 'Désactivé (0s)',
    'academy.parameters.autoApproveTitle': 'Validation des Pré-inscriptions',
    'academy.parameters.autoApproveDesc': 'Définit si les apprenants pré-inscrits en ligne sont automatiquement validés comme apprenants actifs.',
    'academy.parameters.autoApproveEnabled': 'Validation Automatique Activée',
    'academy.parameters.autoApproveDisabled': 'Validation Manuelle (Recommandé)',
    'academy.parameters.autoApproveAlertTrue': 'Les nouvelles pré-inscriptions deviendront immédiatement actives sans validation.',
    'academy.parameters.autoApproveAlertFalse': 'Les pré-inscriptions restent en attente jusqu\'à validation manuelle.',
    'academy.parameters.quizScoreTitle': 'Score Minimal de Réussite des Quiz',
    'academy.parameters.quizScoreDesc': 'Pourcentage minimal requis par défaut pour réussir les quiz d\'évaluation.',
    'academy.parameters.quizScoreLabel': 'Score minimal de réussite (%)',
    'academy.parameters.quizScoreHelper': 'Les apprenants obtenant au moins {0}% sont considérés comme reçus.',
    'academy.parameters.maxStudentsTitle': 'Limite d\'Apprenants par Groupe',
    'academy.parameters.maxStudentsDesc': 'Nombre maximal recommandé d\'apprenants actifs pouvant être inscrits dans un même groupe.',
    'academy.parameters.maxStudentsLabel': 'Max apprenants par classe',
    'academy.parameters.maxStudentsUnit': 'apprenants',
    'academy.parameters.maxStudentsHelper': 'Alerte affichée lorsque la classe dépasse {0} apprenants.',
    'academy.parameters.modifiedBy': 'Modifié par :',
    'academy.parameters.lastUpdate': 'MAJ :',
    'academy.parameters.systemUser': 'Système',
    'academy.parameters.showUserEditionsTitle': 'Affichage de l\'Agent sur les Éditions',
    'academy.parameters.showUserEditionsDesc': 'Afficher automatiquement le nom et rôle de l\'utilisateur connecté au bas des factures, reçus et rapports imprimés.',
    'academy.parameters.showUserEditionsEnabled': 'Affichage de l\'agent activé au bas des éditions',
    'academy.parameters.showUserEditionsDisabled': 'Affichage de l\'agent désactivé (Éditions anonymes)',
    'academy.parameters.numbering_invoice_title': 'Numérotation des Factures',
    'academy.parameters.numbering_invoice_desc': 'Personnalisez le format, préfixe, code agence, date, longueur, remise à zéro et l\'ordre des éléments des numéros de facture.',
    'academy.parameters.numbering_payment_title': 'Numérotation des Reçus de Paiement',
    'academy.parameters.numbering_payment_desc': 'Personnalisez le format, préfixe, code agence, date, longueur, remise à zéro et l\'ordre des éléments des reçus de paiement.',
    'academy.parameters.numbering_prefix': 'Préfixe',
    'academy.parameters.numbering_agency_length': 'Taille Code Agence',
    'academy.parameters.numbering_agency_length_helper': 'Nb max de caractères du code agence (0 = tout)',
    'academy.parameters.numbering_date_format': 'Format de Date',
    'academy.parameters.numbering_padding': 'Longueur du Numéro (Zéros)',
    'academy.parameters.numbering_suffix': 'Post-fixe / Suffixe',
    'academy.parameters.numbering_separator': 'Séparateur',
    'academy.parameters.numbering_order': 'Ordre des Éléments',
    'academy.parameters.numbering_reset_period': 'Remise à Zéro Automatique',
    'academy.parameters.numbering_next_counter': 'Prochain Compteur',
    'academy.parameters.numbering_reset_btn': 'Réinitialiser à 1',
    'academy.parameters.numbering_reset_confirm': 'Voulez-vous réinitialiser le compteur à 1 ?',
    'academy.parameters.numbering_preview': 'Aperçu en Direct du Prochain Numéro :',
    'academy.parameters.numbering_reset_success': 'Compteur réinitialisé avec succès à 1.',
    'academy.parameters.order_prefix_agency_date_number_suffix': 'Préfixe - Code Agence - Date - Numéro - Post-fixe',
    'academy.parameters.order_prefix_date_number_suffix': 'Préfixe - Date - Numéro - Post-fixe',
    'academy.parameters.order_prefix_agency_number_date': 'Préfixe - Code Agence - Numéro - Date',
    'academy.parameters.order_prefix_number_date': 'Préfixe - Numéro - Date',
    'academy.parameters.order_date_prefix_number': 'Date - Préfixe - Numéro',
    'academy.parameters.order_prefix_number_suffix': 'Préfixe - Numéro - Post-fixe',
    'academy.parameters.order_prefix_agency_number_suffix': 'Préfixe - Code Agence - Numéro - Post-fixe',
    'academy.parameters.sep_dash': 'Tiret -',
    'academy.parameters.sep_slash': 'Slash /',
    'academy.parameters.sep_dot': 'Point .',
    'academy.parameters.sep_underscore': 'Underscore _',
    'academy.parameters.sep_none': 'Aucun (Pas de séparateur)',
    'academy.parameters.datefmt_none': 'Aucun (None)',
    'academy.parameters.datefmt_yyyy': 'YYYY (Ex: 2026)',
    'academy.parameters.datefmt_yyyy_mm': 'YYYY-MM (Ex: 2026-08)',
    'academy.parameters.datefmt_yyyy_slash_mm': 'YYYY/MM (Ex: 2026/08)',
    'academy.parameters.datefmt_yyyymmdd': 'YYYYMMDD (Ex: 20260819)',
    'academy.parameters.datefmt_yymm': 'YYMM (Ex: 2608)',
    'academy.parameters.reset_yearly': 'Annuel (Chaque 1er Janvier)',
    'academy.parameters.reset_monthly': 'Mensuel (Chaque 1er du Mois)',
    'academy.parameters.reset_daily': 'Journalier (Chaque Jour)',
    'academy.parameters.reset_manual': 'Manuel Uniquement',
    'academy.parameters.reset_never': 'Jamais (Continu)',
    'academy.parameters.msg_invoice_saved': 'Numérotation des factures sauvegardée avec succès.',
    'academy.parameters.msg_invoice_error': 'Erreur lors de la sauvegarde des paramètres de facture.',
    'academy.parameters.msg_payment_saved': 'Numérotation des reçus sauvegardée avec succès.',
    'academy.parameters.msg_payment_error': 'Erreur lors de la sauvegarde des paramètres de reçu.',
    'academy.parameters.groupDeleteCascadeTitle': 'Suppression des Groupes & Créneaux Planifiés',
    'academy.parameters.groupDeleteCascadeDesc': 'Définissez la règle lors de la suppression d\'un groupe : supprimer automatiquement en cascade tous ses créneaux planifiés ou bloquer strictement la suppression si des créneaux existent.',
    'academy.parameters.groupDeleteCascadeLabel': 'Suppression en Cascade des Créneaux',
    'academy.parameters.groupDeleteCascadeEnabled': 'Suppression en Cascade Activée (Recommandé)',
    'academy.parameters.groupDeleteCascadeDisabled': 'Contrôle Strict (Bloquer la suppression si des créneaux existent)',
    'academy.parameters.groupDeleteCascadeAlertTrue': 'La suppression d\'un groupe supprimera automatiquement tous ses créneaux planifiés associés.',
    'academy.parameters.groupDeleteCascadeAlertFalse': 'La suppression d\'un groupe sera bloquée tant qu\'il possède des créneaux planifiés actifs.',
    'academy.groups.confirm_delete_with_sessions': 'Voulez-vous supprimer le groupe « {0} » ainsi que ses {1} créneaux planifiés associés ?',
    'academy.groups.blocked_delete_has_sessions': 'Action bloquée : Le groupe « {0} » possède {1} créneaux planifiés actifs. Veuillez supprimer ses créneaux d\'abord ou activer la suppression en cascade dans les Paramètres Académie.',





    // Academy Quizzes
    'academy.quizzes.title': 'Quiz & Tests',
    'academy.quizzes.add_new': 'Nouveau Quiz',
    'academy.quizzes.back_to_list': 'Retour à la liste',
    'academy.quizzes.empty': 'Aucun quiz créé pour le moment. Cliquez sur "Nouveau Quiz" pour commencer.',
    'academy.quizzes.search_placeholder': 'Rechercher un quiz...',
    'academy.quizzes.list.grid': 'Vue grille',
    'academy.quizzes.list.table': 'Vue tableau',
    'academy.quizzes.duration_label': '{0} min',
    'academy.quizzes.status_draft': 'Brouillon',
    'academy.quizzes.status_active': 'Actif',
    'academy.quizzes.status_archived': 'Archivé',
    'academy.quizzes.confirm_delete': 'Supprimer le quiz « {0} » et toutes ses questions/résultats ?',
    'academy.quizzes.section_info': 'Informations du quiz',
    'academy.quizzes.section_levels': 'Niveaux / Grille de score',
    'academy.quizzes.add_level': 'Ajouter un niveau',
    'academy.quizzes.levels_empty': 'Aucun niveau défini. Ajoutez des paliers de score pour afficher un niveau personnalisé.',
    'academy.quizzes.level.code': 'Code',
    'academy.quizzes.level.title': 'Titre',
    'academy.quizzes.level.min': 'Score min',
    'academy.quizzes.level.max': 'Score max',
    'academy.quizzes.level.message': 'Message',
    'academy.quizzes.level.add': 'Ajouter',
    'academy.quizzes.level.update': 'Mettre à jour',
    'academy.quizzes.table.code': 'Code',
    'academy.quizzes.table.title': 'Titre',
    'academy.quizzes.table.duration': 'Durée',
    'academy.quizzes.table.status': 'Statut',
    'academy.quizzes.table.validity': 'Validité',
    'academy.quizzes.actions.questions': 'Questions',
    'academy.quizzes.actions.results': 'Résultats',
    'academy.quizzes.actions.share': 'Partager le lien',
    'academy.quizzes.actions.edit': 'Modifier',
    'academy.quizzes.actions.delete': 'Supprimer',
    'academy.quizzes.form.title_new': 'Nouveau Quiz',
    'academy.quizzes.form.title_edit': 'Modifier : {0}',
    'academy.quizzes.form.code': 'Code',
    'academy.quizzes.form.title': 'Titre',
    'academy.quizzes.form.duration': 'Durée (minutes)',
    'academy.quizzes.form.expiry_days': 'Expiration (jours)',
    'academy.quizzes.form.status': 'Statut',
    'academy.quizzes.form.display': "Type d'affichage",
    'academy.quizzes.display_list': 'Liste',
    'academy.quizzes.display_assistant': 'Assistant (une question à la fois)',
    'academy.quizzes.form.start_date': 'Date de début',
    'academy.quizzes.form.end_date': 'Date de fin',
    'academy.quizzes.form.whatsapp': 'WhatsApp (contact)',
    'academy.quizzes.form.register_link': 'Lien d\'inscription',
    'academy.quizzes.form.description': 'Description',
    'academy.quizzes.val_title': 'Le titre du quiz est obligatoire.',
    'academy.quizzes.val_duration': 'La durée doit être supérieure à 0.',
    'academy.quizzes.val_date_range': 'La date de début doit précéder la date de fin.',
    'academy.quizzes.error_load': 'Erreur de chargement des quiz',
    'academy.quizzes.error_delete': 'Erreur de suppression du quiz',
    'academy.quizzes.error_save_level': 'Erreur de sauvegarde du niveau',
    'academy.quizzes.error_delete_level': 'Erreur de suppression du niveau',
    'academy.quizzes.error_load_levels': 'Erreur de chargement des niveaux',
    'academy.quizzes.share.title': 'Partager : {0}',
    'academy.quizzes.share.error': 'Impossible de générer le lien public.',
    'academy.quizzes.share.error_generic': 'Une erreur est survenue.',
    'academy.quizzes.share.url_label': 'Lien public du quiz',
    'academy.quizzes.share.copy': 'Copier',
    'academy.quizzes.share.copied': 'Copié !',
    'academy.quizzes.share.whatsapp': 'Partager WhatsApp',
    'academy.quizzes.share.whatsapp_text': 'Quiz de niveau - passez le test : ',
    'academy.quizzes.share.regenerate': 'Générer un nouveau lien',
    'academy.quizzes.share.generate': 'Générer le lien',
    'academy.quizzes.share.generating': 'Génération...',
    'academy.quizzes.share.empty_title': 'Lien public',
    'academy.quizzes.share.empty_desc': 'Générez un lien public à partager avec vos candidats.',
    'academy.quiz_questions.title': 'Questions de Quiz',
    'academy.quiz_questions.back_to_quizzes': 'Retour aux quiz',
    'academy.quiz_questions.add_new': 'Nouvelle question',
    'academy.quiz_questions.select_quiz': 'Quiz',
    'academy.quiz_questions.select_quiz_placeholder': 'Sélectionner un quiz',
    'academy.quiz_questions.no_quiz_selected': 'Sélectionnez un quiz pour gérer ses questions.',
    'academy.quiz_questions.search_placeholder': 'Rechercher une question...',
    'academy.quiz_questions.q_count': '{0} question(s)',
    'academy.quiz_questions.total_points': '{0} points au total',
    'academy.quiz_questions.table.order': 'Ordre',
    'academy.quiz_questions.table.text': 'Question',
    'academy.quiz_questions.table.correct': 'Bonne réponse',
    'academy.quiz_questions.table.points': 'Points',
    'academy.quiz_questions.dialog.new': 'Nouvelle question',
    'academy.quiz_questions.dialog.edit': 'Modifier la question',
    'academy.quiz_questions.form.text': 'Intitulé de la question',
    'academy.quiz_questions.form.order': 'Ordre',
    'academy.quiz_questions.form.points': 'Points',
    'academy.quiz_questions.confirm_delete': 'Supprimer cette question ?',
    'academy.quiz_questions.val_text': 'L\'intitulé de la question est obligatoire.',
    'academy.quiz_questions.error_load': 'Erreur de chargement des questions',
    'academy.quiz_questions.error_save': 'Erreur de sauvegarde de la question',
    'academy.quiz_questions.error_delete': 'Erreur de suppression de la question',
    'academy.quiz_questions.table.type': 'Type',
    'academy.quiz_questions.table.manual': 'Manuelle',
    'academy.quiz_questions.form.type': 'Type de question',
    'academy.quiz_questions.form.type_single': 'Choix unique (radio)',
    'academy.quiz_questions.form.type_multi': 'Choix multiple (cases)',
    'academy.quiz_questions.form.type_boolean': 'Vrai / Faux',
    'academy.quiz_questions.form.type_text': 'Réponse libre (texte)',
    'academy.quiz_questions.form.options': 'Options',
    'academy.quiz_questions.form.option_label': 'Libellé de l\'option',
    'academy.quiz_questions.form.correct': 'Correcte',
    'academy.quiz_questions.form.add_option': 'Ajouter une option',
    'academy.quiz_questions.form.multiple_correct': 'Plusieurs réponses correctes',
    'academy.quiz_questions.form.true_label': 'Vrai',
    'academy.quiz_questions.form.false_label': 'Faux',
    'academy.quiz_questions.form.text_hint': 'Question à réponse libre : le candidat écrit son texte. Elle est évaluée manuellement et ne compte pas dans le score automatique.',
    'academy.quiz_questions.val_options': 'Ajoutez au moins 2 options avec un libellé.',
    'academy.quiz_questions.val_correct': 'Sélectionnez au moins une bonne réponse.',
    'academy.quiz_public.text_answer_placeholder': 'Votre réponse...',
    'academy.quiz_results.title': 'Résultats de Quiz',
    'academy.quiz_results.back_to_quizzes': 'Retour aux quiz',
    'academy.quiz_results.select_quiz': 'Quiz',
    'academy.quiz_results.select_quiz_placeholder': 'Sélectionner un quiz',
    'academy.quiz_results.no_quiz_selected': 'Sélectionnez un quiz pour voir les résultats.',
    'academy.quiz_results.search_placeholder': 'Rechercher un participant...',
    'academy.quiz_results.count': '{0} tentative(s)',
    'academy.quiz_results.avg': 'Moyenne : {0}',
    'academy.quiz_results.table.ref': 'Référence',
    'academy.quiz_results.table.name': 'Participant',
    'academy.quiz_results.table.phone': 'Téléphone',
    'academy.quiz_results.table.email': 'Email',
    'academy.quiz_results.table.score': 'Score',
    'academy.quiz_results.table.level': 'Niveau',
    'academy.quiz_results.table.duration': 'Durée',
    'academy.quiz_results.table.date': 'Date',
    'academy.quiz_results.error_load': 'Erreur de chargement des résultats',
    'academy.menu.traffic': 'Trafic API',
    'traffic.title': 'Trafic API',
    'traffic.error_load': 'Erreur de chargement du trafic',
    'traffic.refresh': 'Actualiser',
    'traffic.close': 'Fermer',
    'traffic.details': 'Détails',
    'traffic.details_title': 'Détails de la requête',
    'traffic.anomaly': 'Anomalie',
    'traffic.anomaly_flags': 'Anomalies détectées',
    'traffic.request_body': 'Corps de la requête',
    'traffic.correlation_id': 'ID de corrélation',
    'traffic.search_placeholder': 'Rechercher (chemin, IP, email...)',
    'traffic.stats.total': 'Requêtes',
    'traffic.stats.anomalies': 'Anomalies',
    'traffic.stats.server_errors': 'Erreurs 5xx',
    'traffic.stats.auth_failures': 'Échecs connexion',
    'traffic.stats.not_found': '404',
    'traffic.stats.rate_limited': 'Rate limit',
    'traffic.stats.avg_duration': 'Durée moy.',
    'traffic.stats.distinct_ips': 'IP distinctes',
    'traffic.filter.period': 'Période',
    'traffic.filter.last_hour': 'Dernière heure',
    'traffic.filter.last_24h': '24 dernières heures',
    'traffic.filter.last_7d': '7 derniers jours',
    'traffic.filter.last_30d': '30 derniers jours',
    'traffic.filter.status': 'Statut',
    'traffic.filter.all': 'Tous',
    'traffic.filter.anomalies_only': 'Anomalies uniquement',
    'traffic.filter.count': '{0} entrée(s)',
    'traffic.table.date': 'Date',
    'traffic.table.method': 'Méthode',
    'traffic.table.path': 'Chemin',
    'traffic.table.status': 'Statut',
    'traffic.table.duration': 'Durée',
    'traffic.table.ip': 'IP',
    'traffic.table.user': 'Utilisateur',
    'traffic.table.source': 'Source',
    'traffic.table.anomalies': 'Anomalies',
    'traffic.table.user_agent': 'User-Agent',
    'academy.menu.user_actions': 'Actions Utilisateur',
    'useraction.title': 'Actions Utilisateur',
    'useraction.error_load': 'Erreur de chargement des actions',
    'useraction.refresh': 'Actualiser',
    'useraction.close': 'Fermer',
    'useraction.details': 'Détails',
    'useraction.details_title': 'Détails de l\'action',
    'useraction.request_body': 'Corps de la requête',
    'useraction.correlation_id': 'ID de corrélation',
    'useraction.search_placeholder': 'Rechercher (chemin, email, IP...)',
    'useraction.stats.total': 'Actions',
    'useraction.stats.connected': 'Connectées',
    'useraction.stats.public': 'Publiques',
    'useraction.stats.internal': 'Internes',
    'useraction.stats.avg_duration': 'Durée moy.',
    'useraction.stats.server_errors': 'Erreurs 5xx',
    'useraction.stats.distinct_users': 'Utilisateurs',
    'useraction.stats.distinct_ips': 'IP distinctes',
    'useraction.stats.distinct_okeys': 'OKeys',
    'useraction.filter.period': 'Période',
    'useraction.filter.last_hour': 'Dernière heure',
    'useraction.filter.last_24h': '24 dernières heures',
    'useraction.filter.last_7d': '7 derniers jours',
    'useraction.filter.last_30d': '30 derniers jours',
    'useraction.filter.status': 'Statut',
    'useraction.filter.all': 'Tous',
    'useraction.filter.connected_only': 'Connectés uniquement',
    'useraction.filter.source': 'Source',
    'useraction.filter.method': 'Méthode',
    'useraction.filter.okey': 'OKey',
    'useraction.filter.count': '{0} entrée(s)',
    'useraction.table.date': 'Date',
    'useraction.table.method': 'Méthode',
    'useraction.table.path': 'Chemin',
    'useraction.table.okey': 'OKey',
    'useraction.table.status': 'Statut',
    'useraction.table.duration': 'Durée',
    'useraction.table.ip': 'IP',
    'useraction.table.user': 'Utilisateur',
    'useraction.table.source': 'Source',
    'useraction.table.user_agent': 'User-Agent',
    'useraction.table.token_hash': 'Token (hash)',
    'useraction.table.error': 'Erreur',
    'useraction.table.cnx': 'Connexion',
    'useraction.times.start': 'Début',
    'useraction.times.end': 'Fin',
    'academy.menu.user_connexions': 'Connexions Utilisateur',
    'userconnex.title': 'Connexions Utilisateur',
    'userconnex.refresh': 'Actualiser',
    'userconnex.back_to_list': 'Retour à la liste',
    'userconnex.close': 'Fermer',
    'userconnex.detail': 'Détail',
    'userconnex.load_actions': 'Charger les actions',
    'userconnex.actions': 'actions',
    'userconnex.actions_title': 'Actions de la connexion',
    'userconnex.actions_empty': 'Aucune action enregistrée pour cette connexion.',
    'userconnex.details_title': 'Détails de la connexion',
    'userconnex.error_no_user': 'Veuillez sélectionner un utilisateur.',
    'userconnex.error_load': 'Erreur de chargement des connexions',
    'userconnex.error_load_actions': 'Erreur de chargement des actions',
    'userconnex.no_data': 'Aucune connexion trouvée pour cet utilisateur et cette période.',
    'userconnex.search_placeholder': 'Rechercher (chemin, statut...)',
    'userconnex.filter.user': 'Utilisateur',
    'userconnex.filter.user_placeholder': 'Rechercher un utilisateur',
    'userconnex.filter.start': 'Date début',
    'userconnex.filter.end': 'Date fin',
    'userconnex.filter.load': 'Charger',
    'userconnex.filter.count': '{0} connexion(s)',
    'userconnex.table.cnx': 'Connexion',
    'userconnex.table.start': 'Début',
    'userconnex.table.end': 'Fin',
    'userconnex.table.state': 'État',
    'userconnex.table.app': 'Application',
    'userconnex.table.dossier': 'Dossier',
    'userconnex.table.actions_count': 'Actions',
    'userconnex.table.date': 'Date',
    'userconnex.table.method': 'Méthode',
    'userconnex.table.path': 'Chemin',
    'userconnex.table.status': 'Statut',
    'userconnex.table.duration': 'Durée',
    'userconnex.field.num': 'N° connexion',
    'userconnex.field.start': 'Date de début',
    'userconnex.field.end': 'Date de fin',
    'userconnex.field.last_activity': 'Dernière activité',
    'userconnex.field.app': 'Application',
    'userconnex.field.dossier': 'Dossier',
    'userconnex.field.ip': 'IP',
    'userconnex.field.host': 'Hôte',
    'userconnex.field.computer': 'Ordinateur',
    'userconnex.field.language': 'Langue',
    'userconnex.field.referer': 'Référent',
    'userconnex.field.wid': 'Session WID',
    'userconnex.field.text': 'Texte',
    'common.copyright': '© 2026 BLOBTIC. Tous droits réservés.',
    'academy.prereg.page.error_contact_admin_help': 'Veuillez contacter l\'administration de l\'établissement pour obtenir un lien de pré-inscription valide.',
    'academy.prereg.page.establishment_label': 'Établissement : {0}',
    'academy.quiz_public.offered_by': 'Quiz proposé par {0}',
    'academy.students.portal_section': 'Accès au Portail Étudiant & Parent',
    'academy.students.portal_active': 'Activer l\'accès au portail',
    'academy.students.portal_password': 'Mot de passe du portail',
    'academy.students.portal_generate_pwd': 'Générer',
    'academy.students.portal_copy_link': 'Copier le lien public',
    'academy.students.portal_link_copied': 'Lien du portail copié !',
    'academy.students.portal_badge_active': 'Portail Actif',
    'academy.students.portal_badge_inactive': 'Portail Inactif',
    'academy.students.portal_login_hint': 'L\'élève ou son tuteur peut se connecter avec son email, téléphone ou code élève et ce mot de passe.',
    'academy.teachers.portal_section': 'Accès au Portail Enseignant',
    'academy.teachers.portal_active': 'Activer l\'accès au portail',
    'academy.teachers.portal_can_attendance': 'Autoriser la saisie des présences & scan de badge',
    'academy.teachers.portal_password': 'Mot de passe du portail',
    'academy.teachers.portal_generate_pwd': 'Générer',
    'academy.teachers.portal_copy_link': 'Copier le lien public',
    'academy.teachers.portal_link_copied': 'Lien du portail copié !',
    'academy.teachers.portal_badge_active': 'Portail Actif',
    'academy.teachers.portal_badge_inactive': 'Portail Inactif',
    'academy.teachers.portal_login_hint': 'L\'enseignant peut se connecter avec son email ou téléphone et ce mot de passe.',
    'academy.students.validation.first_name_required': 'Le prénom est obligatoire.',
    'academy.students.validation.last_name_required': 'Le nom est obligatoire.',
    'academy.students.validation.gender_required': 'Le genre est obligatoire.',
    'academy.students.validation.dob_required': 'La date de naissance est obligatoire.',
    'academy.students.validation.health_issues_required': 'Précisez les problèmes de santé.',
    'academy.students.validation.allergies_required': 'Précisez les allergies.',
    'academy.students.validation.guardian_first_name_required': 'Prénom du responsable obligatoire.',
    'academy.students.validation.guardian_last_name_required': 'Nom du responsable obligatoire.',
    'academy.students.validation.guardian_relation_required': 'Lien avec l\'enfant obligatoire.',
    'academy.students.validation.guardian_phone_required': 'Téléphone du responsable obligatoire.',
    'academy.students.validation.image_right_required': 'Droit à l\'image obligatoire.',
    'academy.teachers.validation.email_required': 'L\'adresse email est obligatoire.',
    'academy.teachers.validation.phone_required': 'Le numéro de téléphone est obligatoire.',
    'academy.teachers.validation.specialization_required': 'La spécialité est obligatoire.',
    'academy.teachers.validation.hourly_rate_required': 'Le montant du tarif est obligatoire.',
    'academy.prereg.page.health_question': 'L\'enfant présente-t-il des problèmes de santé ou une maladie chronique ?',
    'academy.prereg.page.health_explanation': 'Ces informations permettent à l\'équipe éducative d\'assurer la sécurité et le bien-être de votre enfant au sein de l\'établissement.',
    'academy.prereg.page.health_specify_label': '(Si Oui) Précisez :',
    'academy.prereg.page.condition_asthma': 'Asthme',
    'academy.prereg.page.condition_diabetes': 'Diabète',
    'academy.prereg.page.condition_epilepsy': 'Épilepsie',
    'academy.prereg.page.condition_other': 'Autre',
    'academy.prereg.page.health_placeholder': 'Précisez la condition médicale ou les précautions à prendre...',
    'academy.prereg.page.allergies_header': 'Allergies connues',
    'academy.prereg.page.allergies_question': 'L\'enfant a-t-il des allergies connues (alimentaires, médicamenteuses, etc.) ?',
    'academy.prereg.page.allergies_explanation': 'Indiquez toute allergie alimentaire ou médicamenteuse afin de prévenir tout risque lors des activités et pauses.',
    'academy.prereg.page.allergies_placeholder': 'Précisez les allergies (ex: arachides, lactose, pénicilline...)',
    'academy.prereg.page.image_rights_header': 'Droit à l\'image',
    'academy.prereg.page.image_rights_question': 'Autorisez-vous la prise et publication de photos/vidéos de l\'apprenant ?',
    'academy.prereg.page.image_rights_explanation': 'Autorisation de photographier ou filmer l\'enfant dans le cadre des activités pédagogiques et projets officiels de l\'académie.',
    'academy.prereg.page.yes': 'Oui',
    'academy.prereg.page.no': 'Non',
    'academy.prereg.time_slot.morning': 'Matin (08h00 - 12h00)',
    'academy.prereg.time_slot.afternoon': 'Après-midi (13h00 - 17h00)',
    'academy.prereg.time_slot.evening': 'Soir (17h00 - 20h00)',
    'academy.prereg.time_slot.weekend': 'Fin de semaine / Weekend',
    'academy.prereg.time_slot.flexible': 'Sur mesure / Flexible',
    'academy.prereg.page.select_course_required': 'Veuillez sélectionner un cours ou une formation.',
    'academy.prereg.page.select_time_preference_required': 'Veuillez choisir un créneau horaire préféré.',
    'academy.quizzes.form.auto_next': 'Avancer automatiquement à la question suivante lors du choix d\'une option',
    'academy.quizzes.form.max_attempts_link': 'Nombre max d\'essais total pour le lien',
    'academy.quizzes.form.max_attempts_user': 'Nombre max d\'essais par personne (IP / Tél)',
    'academy.quiz_public.public_portal': 'Portail Public de Quiz',
    'academy.quiz_public.error_invalid_token': 'Lien de quiz invalide ou expiré.',
    'academy.quiz_public.error_load_failed': 'Impossible de charger le quiz.',
    'academy.quiz_public.error_no_token': 'Aucun jeton de quiz fourni.',
    'academy.quiz_public.error_submit_failed': 'Échec de l\'envoi des réponses.',
    'academy.quiz_public.error_submit_generic': 'Une erreur est survenue lors de l\'envoi.',
    'academy.quiz_public.error_contact_required': 'Veuillez renseigner votre nom et votre téléphone.',
    'academy.quiz_public.error_all_questions': 'Veuillez répondre à toutes les questions.',
    'academy.quiz_public.error_name_invalid': 'Veuillez saisir un nom complet valide (minimum 3 caractères).',
    'academy.quiz_public.error_phone_invalid': 'Le numéro de téléphone n\'est pas valide (minimum 8 chiffres requis).',
    'academy.quiz_public.error_email_invalid': 'L\'adresse email n\'est pas valide (exemple: nom@domaine.com).',
    'academy.quiz_public.duration_label': 'Durée : {0} minutes',
    'academy.quiz_public.questions_count': '{0} questions',
    'academy.quiz_public.contact_header': 'Vos informations',
    'academy.quiz_public.full_name': 'Nom complet',
    'academy.quiz_public.phone': 'Téléphone',
    'academy.quiz_public.email': 'Email',
    'academy.quiz_public.age_band': 'Tranche d\'âge',
    'academy.quiz_public.age_band_under_12': 'Moins de 12 ans',
    'academy.quiz_public.age_band_12_15': '12 - 15 ans',
    'academy.quiz_public.age_band_16_25': '16 - 25 ans',
    'academy.quiz_public.age_band_26_35': '26 - 35 ans',
    'academy.quiz_public.age_band_36_50': '36 - 50 ans',
    'academy.quiz_public.age_band_over_50': 'Plus de 50 ans',
    'academy.quiz_public.start_btn': 'Commencer le quiz',
    'academy.quiz_public.submit_btn': 'Valider mes réponses',
    'academy.quiz_public.continue_btn': 'Continuer',
    'academy.quiz_public.details_subtitle': 'Renseignez vos informations pour voir votre résultat.',
    'academy.quiz_public.submitting_btn': 'Envoi...',
    'academy.quiz_public.prev_btn': 'Précédent',
    'academy.quiz_public.next_btn': 'Suivant',
    'academy.quiz_public.question_of': 'Question {0} / {1}',
    'academy.quiz_public.result_title': 'Quiz terminé !',
    'academy.quiz_public.result_ref': 'Référence : {0}',
    'academy.quiz_public.result_score': 'Score : {0} / {1}',
    'academy.quiz_public.result_level': 'Votre niveau : {0}',
    'academy.quiz_public.whatsapp_btn': 'Contacter l\'établissement',
    'academy.quiz_public.whatsapp_result_text': 'J\'ai passé le quiz de niveau (réf. {0}). Je souhaite m\'inscrire.',
    'academy.quiz_public.register_btn': "S'inscrire à une formation",

    // Academy Application Strings
    'academy.dashboard.title': 'Tableau de bord Mission Control',
    'academy.dashboard.subtitle': 'Aperçu global de la santé de l\'académie en 5 secondes.',
    'academy.dashboard.kpi.active_students': 'Total Apprenants Actifs',
    'academy.dashboard.kpi.today_prereg': 'Pré-inscriptions du jour',
    'academy.dashboard.kpi.today_schedule': 'Planning du jour',
    'academy.dashboard.kpi.low_attendance': 'Alerte faible présence',
    'academy.dashboard.kpi.on_hold': '— {0} suspendu{1}',
    'academy.dashboard.kpi.teachers': 'Total Enseignants',
    'academy.dashboard.kpi.rooms': 'Total Salles Actives',
    'academy.dashboard.kpi.monthly_revenue': 'Revenu du Mois',
    'academy.dashboard.kpi.courses': 'Formations & Cours',
    'academy.dashboard.kpi.groups': 'Groupes d\'étude',
    'academy.dashboard.kpi.sessions': 'Séances programmées',
    'academy.dashboard.kpi.quizzes': 'Évaluations & Tests',
    'academy.dashboard.kpi.revenue': 'Recettes encaissées',
    'academy.dashboard.kpi.pending_invoices': 'Factures en attente',
    'academy.dashboard.kpi.pending_prereg': 'Pré-inscriptions en attente',
    'academy.dashboard.kpi.accepted_prereg': 'Pré-inscriptions acceptées',
    'academy.dashboard.kpi.refused_prereg': 'Pré-inscriptions refusées',
    'academy.dashboard.kpi.desc.active_students': 'Apprenants inscrits et actifs',
    'academy.dashboard.kpi.desc.teachers': 'Formateurs actifs',
    'academy.dashboard.kpi.desc.courses': 'Catalogue proposé',
    'academy.dashboard.kpi.desc.groups': 'Groupes en cours',
    'academy.dashboard.kpi.desc.rooms': 'Salles actives',
    'academy.dashboard.kpi.desc.sessions': 'Toutes séances planifiées',
    'academy.dashboard.kpi.desc.quizzes': 'Quiz disponibles',
    'academy.dashboard.kpi.desc.revenue': 'Paiements reçus',
    'academy.dashboard.kpi.desc.pending_invoices': 'Impayées ou partielles',
    'academy.dashboard.kpi.desc.pending_prereg': 'À traiter',
    'academy.dashboard.kpi.desc.accepted_prereg': 'Confirmées',
    'academy.dashboard.kpi.desc.refused_prereg': 'Rejetées',
    'academy.dashboard.kpi.desc.today_prereg': 'Reçues aujourd\'hui',
    'academy.dashboard.kpi.desc.today_schedule': 'Séances et présences',
    'academy.dashboard.kpi.desc.low_attendance': 'Hier, < 75% de présence',
    'academy.dashboard.widget.outstanding_debt': 'Reste à Payer (Dette globale)',
    'academy.dashboard.schedule.today': 'Planning des Séances d\'Aujourd\'hui',
    'academy.dashboard.alerts.low_attendance': 'Alertes Faible Taux de Présence (<75%)',
    'academy.dashboard.refresh_tooltip': 'Actualisation automatique',
    'academy.dashboard.refresh_tooltip_disabled': 'Actualisation automatique désactivée',
    'academy.dashboard.refresh_tooltip_sec': 'Actualisation automatique toutes les {0} sec',
    'academy.dashboard.refresh_tooltip_min': 'Actualisation automatique toutes les {0} min',
    'academy.dashboard.refresh_btn': 'Actualiser ({0}m {1}s)',
    'academy.dashboard.init_btn': 'Initialiser',
    'academy.dashboard.init_tooltip': 'Réinitialiser le stock local',
    'academy.dashboard.kpi.sub_active_students': 'Inscrits dans les cohortes actives',
    'academy.dashboard.kpi.sub_teachers': 'Corps professoral actif',
    'academy.dashboard.kpi.sub_rooms': 'Espaces d\'apprentissage physiques',
    'academy.dashboard.kpi.sub_monthly_revenue': 'Perçu durant le mois en cours',
    'academy.dashboard.widget.debt_desc': 'Total des factures non perçues pour l\'ensemble des apprenants inscrits.',
    'academy.dashboard.widget.manage_debtors': 'Cliquer pour gérer les relances & règlements →',
    'academy.dashboard.status.healthy': 'Sain',
    'academy.dashboard.status.critical': 'ALERTE DETTE CRITIQUE',
    'academy.dashboard.status.warning': 'AVERTISSEMENT DETTE MODÉRÉE',
    'academy.groups.status.upcoming': 'À venir',
    'academy.groups.status.ongoing': 'En cours',
    'academy.groups.status.completed': 'Terminé',
    'academy.students.status.enrolled': 'Inscrit',
    'academy.students.status.dropped': 'Abandonné',
    'days.sun': 'Dimanche',
    'days.mon': 'Lundi',
    'days.tue': 'Mardi',
    'days.wed': 'Mercredi',
    'days.thu': 'Jeudi',
    'days.fri': 'Vendredi',
    'days.sat': 'Samedi',
    'days.short.sun': 'Dim',
    'days.short.mon': 'Lun',
    'days.short.tue': 'Mar',
    'days.short.wed': 'Mer',
    'days.short.thu': 'Jeu',
    'days.short.fri': 'Ven',
    'days.short.sat': 'Sam',
    'days.single.sun': 'D',
    'days.single.mon': 'L',
    'days.single.tue': 'M',
    'days.single.wed': 'M',
    'days.single.thu': 'J',
    'days.single.fri': 'V',
    'days.single.sat': 'S',
    'academy.dashboard.schedule.today_title': 'Planning d\'Aujourd\'hui ({0} séances)',
    'academy.dashboard.schedule.full_scheduler': 'Planning Complet',
    'academy.dashboard.schedule.empty': 'Aucune séance programmée pour aujourd\'hui.',
    'academy.dashboard.schedule.instructor': 'Instructeur :',
    'academy.dashboard.schedule.room': 'Salle :',
    'academy.dashboard.schedule.att_recorded': 'Présences Enregistrées',
    'academy.dashboard.schedule.take_att': 'Prendre les Présences',
    'academy.dashboard.alerts.title': 'Alerte Faible Présence (<75%)',
    'academy.dashboard.alerts.subtitle': 'Séances d\'hier avec un taux de présence inférieur au seuil requis.',
    'academy.dashboard.alerts.all_clear': 'Tout est en ordre !',
    'academy.dashboard.alerts.all_clear_sub': 'Aucune alerte de faible présence pour les cours d\'hier.',
    'academy.dashboard.alerts.rate': '{0}% de Présence',
    'academy.dashboard.alerts.present_count': 'Présents : {0} / {1} Apprenants',
    'academy.dashboard.prereg.stats_pending': 'Pré-Inscriptions en Attente',
    'academy.dashboard.prereg.stats_accepted': 'Pré-Inscriptions Acceptées',
    'academy.dashboard.prereg.stats_refused': 'Pré-Inscriptions Refusées',
    'academy.dashboard.prereg.stats_sub_pending': 'Demandes publiques en attente de traitement',
    'academy.dashboard.prereg.stats_sub_accepted': 'Demandes validées et inscrites',
    'academy.dashboard.prereg.stats_sub_refused': 'Demandes refusées par l\'administration',
    'academy.dashboard.prereg.today_title': 'Pré-Inscriptions du Jour ({0})',
    'academy.dashboard.prereg.today_empty': 'Aucune pré-inscription reçue aujourd\'hui.',
    'academy.dashboard.prereg.view_all': 'Voir les Apprenants',
    'academy.dashboard.prereg.course': 'Formation',
    'academy.dashboard.prereg.time_preference': 'Créneau souhaité',

    'academy.search.placeholder': 'Recherche globale apprenant (Nom ou Email)...',
    'academy.search.debt': 'Dette :',
    'academy.search.financial_overview': 'Aperçu Financier',
    'academy.search.invoiced': 'Facturé',
    'academy.search.total_paid': 'Total Payé',
    'academy.search.rest_to_pay': 'Reste à Payer',
    'academy.search.dob': 'Date de Naissance',
    'academy.search.emergency_contact': 'Contact d\'Urgence',
    'academy.search.hold_active': 'Blocage Financier Actif',
    'academy.search.hold_desc': 'La dette dépasse 30% de la facture depuis plus de 15 jours. L\'apprenant est bloqué pour la prise de présence.',


    'academy.teachers.title': 'Gestion des Enseignants & Corps Professeur',
    'academy.teachers.subtitle': 'Suivi des enseignants, tarifs horaires et planning d\'occupation.',
    'academy.teachers.add_new': 'Ajouter un Enseignant',
    'academy.teachers.fullname': 'Nom Complet',
    'academy.teachers.specialization': 'Spécialité',
    'academy.teachers.hourly_rate': 'Tarif Horaire ($/h)',
    'academy.teachers.hour': 'heure',
    'academy.teachers.busy_map': 'Matrice d\'Occupabilité Hebdomadaire',
    'academy.teachers.drawer.booked': 'Réservé',
    'academy.teachers.confirm_delete': 'Voulez-vous vraiment supprimer cet enseignant ?',

    'academy.students.title': 'Apprenants & Inscriptions',
    'academy.students.subtitle': 'Dossiers apprenants, historique d\'inscriptions et blocage financier.',
    'academy.students.add_new': 'Inscrire un Apprenant',
    'academy.students.firstname': 'Prénom',
    'academy.students.lastname': 'Nom',
    'academy.students.email': 'Email',
    'academy.students.phone': 'Téléphone',
    'academy.students.financial_hold': 'Blocage Financier',
    'academy.students.hold_badge': 'BLOCAGE FINANCIER',
    'academy.students.prereg_link': 'Lien Pré-Inscription',

    // Attendance & Absence Justification (FR)
    'academy.attendance.justified': 'Absence Justifiée',
    'academy.attendance.unjustified': 'Non justifiée',
    'academy.attendance.justify': 'Justifier l\'absence',
    'academy.attendance.view_justification': 'Voir justificatif',
    'academy.attendance.justification_details': 'Détails de la justification d\'absence',
    'academy.attendance.reason_label': 'Motif de l\'absence',
    'academy.attendance.reason_placeholder': 'Précisez le motif médical ou personnel...',
    'academy.attendance.doc_label': 'Document justificatif (certificat, ordonnance...)',
    'academy.attendance.download_doc': 'Télécharger le document',
    'academy.attendance.no_doc': 'Aucun document joint',
    'academy.attendance.submission_date': 'Date de justification',
    'academy.attendance.save_justification': 'Enregistrer la justification',
    'academy.attendance.justification_saved': 'Justification enregistrée avec succès.',
    'academy.attendance.filter_all': 'Tous',
    'academy.attendance.filter_present': 'Présents',
    'academy.attendance.filter_late': 'Retards',
    'academy.attendance.filter_absent': 'Tous les absents',
    'academy.attendance.filter_absent_justified': 'Absents justifiés',
    'academy.attendance.filter_absent_unjustified': 'Absents non justifiés',
    'academy.attendance.filter_excused': 'Excusés',

    // Pre-Registration Modal & Page (FR)
    'academy.prereg.modal.title': 'Générer Lien Pré-Inscription Apprenant',
    'academy.prereg.modal.subtitle': 'Créez un lien d\'inscription public pour votre agence à partager sur vos réseaux sociaux, site web ou par SMS/WhatsApp.',
    'academy.prereg.modal.error_generate': 'Impossible de générer le lien public.',
    'academy.prereg.modal.error_generic': 'Erreur lors de la génération du lien.',
    'academy.prereg.modal.custom_link_title': 'Lien Public Personnalisé',
    'academy.prereg.modal.custom_link_desc': 'Le formulaire guidera l\'apprenant avec un assistant intelligent (Junior / Adulte) et enregistrera automatiquement sa pré-inscription dans votre espace agence.',
    'academy.prereg.modal.btn_generate': 'Générer le Lien Public',
    'academy.prereg.modal.btn_generating': 'Génération...',
    'academy.prereg.modal.success': 'Lien public créé avec succès pour votre agence !',
    'academy.prereg.modal.url_label': 'URL du Formulaire Public',
    'academy.prereg.modal.tooltip_copy': 'Copier le lien',
    'academy.prereg.modal.tooltip_copied': 'Copié !',
    'academy.prereg.modal.btn_copy': 'Copier le Lien',
    'academy.prereg.modal.btn_copied': 'Lien Copié !',
    'academy.prereg.modal.btn_whatsapp': 'Partager WhatsApp',
    'academy.prereg.modal.whatsapp_text': 'Formulaire de pré-inscription : ',
    'academy.prereg.modal.is_existing_notice': 'Lien de pré-inscription actif de votre agence :',
    'academy.prereg.modal.btn_regenerate': 'Générer un nouveau lien',
    'academy.prereg.page.public_portal': 'Portail Public de Pré-Inscription Apprenant',
    'academy.prereg.page.status_junior': 'Statut : Junior (<16 ans)',
    'academy.prereg.page.status_adult': 'Statut : Adulte (≥16 ans)',
    'academy.prereg.page.step1_title': 'Informations Personnelles',
    'academy.prereg.page.step2_junior_title': 'Tuteur & Récupération',
    'academy.prereg.page.step2_adult_title': 'Profession & Urgence',
    'academy.prereg.page.step3_title': 'Santé & Médical',
    'academy.prereg.page.step4_title': 'Choix Formation',
    'academy.prereg.page.step5_title': 'Confirmation',
    'academy.prereg.page.step_mobile_label': 'Étape {0} sur {1} : {2}',
    'academy.prereg.page.first_name': 'Prénom',
    'academy.prereg.page.last_name': 'Nom',
    'academy.prereg.page.gender': 'Genre',
    'academy.prereg.page.gender_male': 'Masculin',
    'academy.prereg.page.gender_female': 'Féminin',
    'academy.prereg.page.dob': 'Date de Naissance',
    'academy.prereg.page.phone': 'N° Téléphone',
    'academy.prereg.page.email': 'Adresse Email',
    'academy.prereg.page.address': 'Adresse Résidentielle',
    'academy.prereg.page.guardian_header': 'Tuteur Légal & Personne Responsable',
    'academy.prereg.page.guardian_first_name': 'Prénom du Tuteur',
    'academy.prereg.page.guardian_last_name': 'Nom du Tuteur',
    'academy.prereg.page.guardian_relation': 'Lien de Parenté',
    'academy.prereg.page.guardian_relation_father': 'Père',
    'academy.prereg.page.guardian_relation_mother': 'Mère',
    'academy.prereg.page.guardian_relation_legal': 'Tuteur Légal',
    'academy.prereg.page.guardian_relation_uncle': 'Oncle/Tante',
    'academy.prereg.page.guardian_relation_grandparent': 'Grand-Parent',
    'academy.prereg.page.guardian_phone': 'N° Téléphone Tuteur',
    'academy.prereg.page.guardian_email': 'Adresse Email du Tuteur / Responsable',
    'academy.students.form.guardian_email': 'Email du Tuteur / Responsable',
    'academy.prereg.page.authorized_pickup': 'Personnes autorisées à récupérer l\'enfant',
    'academy.prereg.page.authorized_pickup_placeholder': 'Format : Nom & Prénom + Lien (une personne par ligne)',
    'academy.prereg.page.adult_info_header': 'Informations Professionnelles & Urgence',
    'academy.prereg.page.profession': 'Profession / Statut Actuel',
    'academy.prereg.page.profession_placeholder': 'ex: Apprenant universitaire, Ingénieur, Enseignant...',
    'academy.prereg.page.emergency_contact': 'Contact en Cas d\'Urgence (Nom & Tél)',
    'academy.prereg.page.health_header': 'Informations Médicales (Optionnel)',
    'academy.prereg.page.health_issues_check': 'Présence de soucis de santé particuliers (Asthme, Diabète, etc.)',
    'academy.prereg.page.health_issues_detail': 'Précisions sur les problèmes de santé',
    'academy.prereg.page.allergies_check': 'Présence d\'allergies connues',
    'academy.prereg.page.allergies_detail': 'Détails des allergies',
    'academy.prereg.page.course_selection_header': 'Sélection de la Formation Souhaitée',
    'academy.prereg.page.no_courses': 'Aucune formation spécifique listée pour le moment. Vous pourrez la choisir avec l\'administration.',
    'academy.prereg.page.summary_header': 'Récapitulatif de votre Demande',
    'academy.prereg.page.terms_consent': 'Je certifie l\'exactitude des informations fournies et demande ma pré-inscription auprès de l\'établissement.',
    'academy.prereg.page.terms_required_error': 'Veuillez accepter les conditions d\'inscription.',
    'academy.prereg.page.success_title': 'Pré-Inscription Reçue !',
    'academy.prereg.page.success_msg': 'Votre demande de pré-inscription a été transmise avec succès à l\'administration de {0}.',
    'academy.prereg.page.reference_code': 'Référence : {0}',
    'academy.prereg.page.next_steps_title': 'Prochaines étapes :',
    'academy.prereg.page.next_steps_msg': 'L\'équipe administrative examinera votre dossier et prendra contact avec vous rapidement pour finaliser votre inscription définitive.',
    'academy.prereg.page.new_registration': 'Nouvelle Inscription',
    'academy.prereg.page.btn_prev': 'Précédent',
    'academy.prereg.page.btn_next': 'Suivant',
    'academy.prereg.page.btn_submit': 'Valider ma Pré-Inscription',
    'academy.prereg.page.btn_submitting': 'Validation...',
    'academy.prereg.page.error_invalid_token': 'Lien de pré-inscription invalide ou expiré.',
    'academy.prereg.page.error_load_failed': 'Impossible de charger les informations de l\'agence.',
    'academy.prereg.page.error_no_token': 'Aucun jeton de pré-inscription fourni.',
    'academy.prereg.page.error_submit_failed': 'Échec de la soumission de pré-inscription.',
    'academy.prereg.page.error_submit_generic': 'Une erreur est survenue lors de la validation.',
    'academy.prereg.page.recommended_courses': 'Formations recommandées pour votre âge',
    'academy.prereg.page.other_courses': 'Autres formations disponibles',
    'academy.prereg.page.recommended_badge': 'Recommandé',
    'academy.prereg.page.programs_header': 'Programmes / Cohortes disponibles',
    'academy.prereg.page.select_program': 'Choisir une cohorte (optionnel)',
    'academy.prereg.page.schedules_label': 'Créneaux planifiés',
    'academy.prereg.page.time_preference': 'Créneau horaire souhaité',
    'academy.prereg.page.time_preference_required': 'Veuillez sélectionner un créneau horaire.',
    'academy.prereg.page.price_label': 'Tarif : {0}',
    'academy.prereg.page.hours_label': '{0} heures',
    'academy.prereg.page.loi_18_07_consent': 'Je consens au traitement de mes données personnelles conformément à la Loi algérienne 18-07 relative à la protection des données à caractère personnel.',
    'academy.prereg.page.loi_18_07_required': 'Le consentement à la Loi 18-07 est obligatoire.',
    'academy.prereg.page.summary_course': 'Formation choisie',
    'academy.prereg.page.summary_program': 'Cohorte choisie',
    'academy.prereg.page.summary_time_pref': 'Créneau souhaité',
    'academy.prereg.page.no_programs': 'Aucune cohorte planifiée pour cette formation. Choisissez un créneau horaire ci-dessous.',

    'academy.groups.title': 'Planning des Groupes & Cohortes',
    'academy.groups.subtitle': 'Tableau Kanban des cohortes, cours et facturation automatique.',
    'academy.groups.add_new': 'Créer une Cohorte',
    'academy.groups.kanban.upcoming': 'À Venir',
    'academy.groups.kanban.ongoing': 'En Cours',
    'academy.groups.kanban.completed': 'Terminé',
    'academy.groups.add_course_template': '+ Nouveau Modèle',
    'academy.groups.create_course_title': 'Créer un Modèle de Cours',
    'academy.groups.course_title': 'Intitulé du Cours',
    'academy.groups.course_price': 'Frais de Formation ({0})',
    'academy.groups.course_hours': 'Durée Totale (heures)',
    'academy.groups.course_desc': 'Description & Programme',
    'academy.groups.save_course': 'Enregistrer le Modèle',
    'academy.groups.course_preview': 'Détails du Modèle Sélectionné',

    'academy.scheduler.subtitle': 'Matrice Heures x Salles avec vérification automatique des conflits.',
    'academy.scheduler.add_session': 'Programmer une Séance',

    'academy.attendance.title': 'Présences & Exécution des Séances',
    'academy.attendance.subtitle': 'Saisie tactile des présences et règle des 75% pour le diplôme.',
    'academy.attendance.present': 'Présent',
    'academy.attendance.absent': 'Absent',
    'academy.attendance.late': 'En Retard',
    'academy.attendance.excused': 'Excusé',

    'academy.financial.title': 'Gestion Financière & Dette',
    'academy.financial.subtitle': 'Factures, règlements FIFO et rapport de caisse quotidien.',
    'academy.financial.receive_payment': 'Encaisser un Règlement',
    'academy.financial.daily_report': 'Rapport de Caisse',
    'academy.financial.rest_to_pay': 'Reste à Payer',
    'academy.financial.kpi.debt': 'Dette Totale (Reste à Payer)',
    'academy.financial.kpi.monthly': 'Revenu Mensuel',
    'academy.financial.kpi.today': 'Encaissé Aujourd\'hui',
    'academy.financial.tab.debts': 'Débiteurs & Reste à Payer',
    'academy.financial.tab.invoices': 'Livre Journal des Factures',
    'academy.financial.tab.payments': 'Historique des Reçus',
    'academy.financial.debtors_list': 'Liste des Débiteurs (Reste à Payer > 0)',
    'academy.financial.send_bulk_reminder': 'Envoyer Relance Email Groupée ({0})',
    'academy.financial.bulk_reminder_sending': 'Envoi des relances par email en cours...',
    'academy.financial.bulk_reminder_result': '{0} email(s) de relance envoyé(s) avec succès. {1} ignoré(s) (sans email).',
    'academy.financial.bulk_reminder_no_selection': 'Veuillez sélectionner au moins un apprenant débiteur.',
    'academy.financial.table.student': 'Apprenant',
    'academy.financial.table.invoiced': 'Facturé',
    'academy.financial.table.paid': 'Payé',
    'academy.financial.table.badge': 'Badge Reste à Payer',
    'academy.financial.table.action': 'Action',
    'academy.financial.table.invoice_num': 'N° Facture',
    'academy.financial.table.total_amount': 'Montant Total',
    'academy.financial.table.due_date': 'Date d\'Échéance',
    'academy.financial.table.status': 'Statut',
    'academy.financial.table.receipt_num': 'N° Reçu',
    'academy.financial.table.amount': 'Montant',
    'academy.financial.table.method': 'Mode de Règlement',
    'academy.financial.table.date': 'Date',
    'academy.financial.modal.receive_title': 'Encaisser un Règlement Apprenant',
    'academy.financial.modal.select_student': 'Sélectionner l\'Apprenant',
    'academy.financial.modal.amount': 'Montant du Règlement ({0})',
    'academy.financial.modal.method': 'Mode de Paiement',
    'academy.financial.modal.cash': 'Espèces',
    'academy.financial.modal.card': 'Carte Bancaire',
    'academy.financial.modal.transfer': 'Virement Bancaire',
    'academy.financial.modal.fifo_note': 'Règle FIFO : Les règlements sont automatiquement affectés à la facture impayée la plus ancienne.',
    'academy.financial.modal.process': 'Valider le Règlement',
    'academy.financial.report.title': 'Rapport Quotidien d\'Encaissement & Caisse',
    'academy.financial.report.date': 'Date du Rapport : {0}',
    'academy.financial.report.cash': 'Encaissé en Espèces',
    'academy.financial.report.card': 'Encaissé par Carte',
    'academy.financial.report.transfer': 'Virements Reçus',
    'academy.financial.report.total': 'TOTAL ENCAISSÉ AUJOURD\'HUI : {0}',
    'academy.financial.report.print': 'Imprimer / Exporter PDF',
    'academy.financial.phone_label': 'Tél. :',
    'academy.financial.email_label': 'Email :',
    'academy.financial.official': 'OFFICIEL',
    'academy.financial.invoice_no': 'FACTURE N°',
    'academy.financial.receipt_no': 'REÇU N°',
    'academy.financial.due_date_label': 'Date d\'échéance',
    'academy.financial.status_label': 'Statut',
    'academy.financial.date_label': 'Date',
    'academy.financial.mode_label': 'Mode de Règlement',
    'academy.financial.student_group_info': 'Informations Apprenant & Groupe',
    'academy.financial.student_info': 'Informations Apprenant',
    'academy.financial.full_name': 'Nom & Prénom :',
    'academy.financial.course_group_label': 'Groupe / Cours :',
    'academy.financial.designation': 'Désignation',
    'academy.financial.description': 'Description',
    'academy.financial.amount': 'Montant',
    'academy.financial.gross_amount_course': 'Montant de la formation',
    'academy.financial.payment_amount': 'Montant Versé',
    'academy.financial.total_paid_today': 'Total versé à ce jour',
    'academy.financial.stamp_signature': 'Cachet et Signature',
    'academy.financial.direction': 'La Direction',
    'academy.financial.daily_report_sub': 'Synthèse de la caisse quotidienne',
    'academy.financial.delete_payment_tooltip': 'Supprimer ce paiement',

    // boubaker 23/08/2026 - suppression facture (contrôles serveur : paiements / rattachement groupe)
    'academy.financial.delete_invoice_tooltip': 'Supprimer cette facture',
    'academy.financial.error.delete_invoice': 'Échec de la suppression de la facture.',
    'academy.financial.delete_invoice_confirm': 'Êtes-vous sûr de vouloir supprimer la facture {0} de l\'apprenant {1} ?',
    'academy.financial.msg_invoice_deleted': 'Facture {0} supprimée avec succès.',
    'academy.financial.error.delete_payment': 'Échec de la suppression du paiement.',
    'academy.financial.status.paid': 'Payée',
    'academy.financial.status.partial': 'Partielle',
    'academy.financial.status.unpaid': 'Non Payée',
    'academy.financial.method.cash': 'Espèces',
    'academy.financial.method.card': 'Carte Bancaire',
    'academy.financial.method.transfer': 'Virement Bancaire',
    'academy.financial.amount_paid': 'Montant Payé',
    'academy.financial.applied_discount': 'Remise Appliquée',
    'academy.financial.delete_payment_confirm': 'Êtes-vous sûr de vouloir supprimer le paiement {0} d\'un montant de {1} {2} pour l\'apprenant {3} ?',
    'academy.financial.error.no_invoices': 'Cet apprenant n\'a aucune facture impayée en attente.',
    'academy.financial.error.payment': 'Échec de l\'enregistrement du paiement.',
    'academy.financial.gross_amount': 'Montant Brut',
    'academy.financial.invoice_details': 'Détails de la Facture Officielle',
    'academy.financial.manual_discount_info': 'Affectation des remises manuelles : Les remises attribuées sur la page "Politique des remises" s\'appliquent directement sur les factures générées.',
    'academy.financial.msg_bulk_reminder_sent': 'Succès : Rappels de dette envoyés par email à {0} apprenant(s) !',
    'academy.financial.msg_payment_deleted': 'Paiement {0} supprimé avec succès. Solde de la facture ajusté.',
    'academy.financial.net_invoiced': 'Net Facturé',
    'academy.financial.no_discount': 'Sans remise',
    'academy.financial.print_invoice': 'Imprimer la Facture',
    'academy.financial.print_receipt': 'Imprimer le Reçu',
    'academy.financial.receipt_details': 'Reçu de Paiement Officiel',
    'academy.financial.success.allocated': 'Paiement enregistré et affecté aux factures avec succès.',
    'academy.financial.success.overpayment': 'Paiement enregistré avec succès. Un trop-perçu de {0} a été ajouté au solde.',
    'academy.financial.invoice_type_title': 'FACTURE',
    'academy.financial.receipt_type_title': 'REÇU DE PAIEMENT',
    'academy.financial.report_type_title': 'RAPPORT QUOTIDIEN DE CAISSE',
    'academy.financial.issued_by': 'Émis / Agent :',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.error': 'Erreur',

    'academy.groups.course': 'Cours :',
    'academy.groups.instructor': 'Instructeur :',
    'academy.groups.unassigned': 'Non Assigné',
    'academy.groups.timeline': 'Période :',
    'academy.groups.enrolled_count': 'Inscrits : {0} / {1} Apprenants',
    'academy.groups.bulk_enroll': 'Inscrire en Masse',
    'academy.groups.move_stage': 'Changer d\'Étape :',
    'academy.groups.complete_group': 'Clôturer le Groupe',
    'academy.groups.create_title': 'Créer une Nouvelle Cohorte d\'Apprentissage',
    'academy.groups.name_label': 'Nom du Groupe (ex: Web Fullstack - Sept 2026)',
    'academy.groups.course_template': 'Modèle de Cours',
    'academy.groups.primary_teacher': 'Enseignant Principal',
    'academy.groups.max_capacity': 'Capacité Maximale (Apprenants)',
    'academy.groups.start_date': 'Date de Début',
    'academy.groups.expected_end_date': 'Date de Fin Prévue',
    'academy.groups.create_btn': 'Créer le Groupe',
    'academy.groups.enroll_title': 'Inscription en Masse : {0}',
    'academy.groups.enroll_desc': 'Cochez un apprenant pour l\'inscrire immédiatement, ou décochez-le pour le retirer. L\'inscription génère automatiquement une facture ({0}).',
    'academy.groups.enroll_search_placeholder': 'Rechercher un apprenant (nom, email, téléphone)...',
    'academy.groups.enroll_no_results': 'Aucun apprenant ne correspond à votre recherche.',
    'academy.groups.enroll_assigned_count': '{0} / {1} inscrit(s)',
    'academy.groups.confirm_enroll': 'Confirmer les Inscriptions',
    'academy.groups.edit_title': 'Modifier la Cohorte',
    'academy.groups.save_btn': 'Enregistrer les Modifications',
    'academy.groups.save_success': 'Cohorte mise à jour avec succès.',
    'academy.groups.enroll_success': '{0} apprenant(s) inscrit(s) avec succès. Factures générées.',
    'academy.groups.enroll_none_selected': 'Aucun nouvel apprenant sélectionné à inscrire.',
    'academy.groups.error.enroll': 'Échec de l\'inscription d\'un ou plusieurs apprenants.',
    'academy.groups.error.unenroll': 'Échec du retrait de l\'inscription.',
    'academy.groups.error.unenroll_has_payments': 'Impossible de retirer cet apprenant : une facture liée a déjà des paiements.',
    'academy.groups.error.capacity': 'Capacité maximale du groupe atteinte.',
    'academy.groups.list.grid': 'Grille',
    'academy.groups.list.table': 'Tableau',
    'academy.groups.confirm_delete': 'Voulez-vous supprimer la cohorte « {0} » ?',
    'academy.groups.section_info': 'Informations de la Cohorte',
    'academy.groups.section_dossier': 'Informations Dossier',
    'academy.groups.back_to_list': 'Retour à la liste',
    'academy.groups.search_placeholder': 'Rechercher par nom, cours, enseignant...',
    'academy.groups.table.name': 'Nom',
    'academy.groups.table.course': 'Modèle de Cours',
    'academy.groups.table.teacher': 'Enseignant',
    'academy.groups.table.status': 'Statut',
    'academy.groups.table.enrolled': 'Inscrits',
    'academy.groups.table.actions': 'Actions',
    'academy.groups.form.dossier_code': 'Code Dossier',
    'academy.groups.form.dossier_lib': 'Dossier',
    'academy.groups.val_name': 'Le nom du groupe est obligatoire.',
    'academy.groups.val_course': 'Sélectionnez un modèle de cours.',
    'academy.groups.val_teacher': 'Sélectionnez un enseignant.',
    'academy.groups.val_capacity': 'La capacité doit être supérieure à 0.',

    'academy.students.search_placeholder': 'Rechercher un apprenant...',
    'academy.students.table.name': 'Nom de l\'Apprenant',
    'academy.students.table.contact': 'Coordonnées',
    'academy.students.table.emergency': 'Contact d\'Urgence',
    'academy.students.table.financial': 'Aperçu Financier',
    'academy.students.table.status': 'Statut & Blocage',
    'academy.students.dob_prefix': 'DOB :',
    'academy.students.modal.edit_title': 'Modifier l\'Apprenant',
    'academy.students.modal.add_title': 'Inscrire un Nouvel Apprenant',
    'academy.students.modal.first_name': 'Prénom',
    'academy.students.modal.last_name': 'Nom',
    'academy.students.modal.email': 'Adresse Email',
    'academy.students.modal.phone': 'Numéro de Téléphone',
    'academy.students.modal.dob': 'Date de Naissance',
    'academy.students.modal.emergency': 'Contact d\'Urgence (Tuteur / Parent)',
    'academy.students.modal.emergency_helper': 'ex: Nom & Téléphone du parent',
    'academy.students.modal.save': 'Enregistrer l\'Apprenant',
    'academy.students.drawer.history': 'Historique d\'Inscription & Chronologie',
    'academy.students.drawer.enrolled_on': 'Inscrit le :',

    // ── StudentsPage — new keys (liste, formulaire, tableau) ──────────────
    'academy.students.new_student': 'Nouvel Apprenant',
    'academy.students.back_to_list': 'Retour à la liste',
    'academy.students.no_students': 'Aucun apprenant enregistré',
    'academy.students.confirm_delete': 'Supprimer "{0} {1}" ?',
    'academy.students.confirm_edit': 'Voulez-vous modifier les informations de {0} {1} ?',
    'academy.students.confirm_accept_title': 'Valider la pré-inscription',
    'academy.students.confirm_accept_msg': 'Voulez-vous accepter et valider la pré-inscription de {0} {1} ?',
    'academy.students.success_accept_title': 'Pré-inscription Validée !',
    'academy.students.success_accept_msg': 'L\'apprenant {0} {1} est désormais actif.',
    'academy.students.error_accept': 'Échec de la validation.',
    'academy.students.confirm_refuse_title': 'Refuser la pré-inscription',
    'academy.students.confirm_refuse_msg': 'Voulez-vous vraiment refuser la pré-inscription de {0} {1} ?',
    'academy.students.success_refuse_title': 'Pré-inscription Refusée',
    'academy.students.success_refuse_msg': 'La demande de {0} {1} a été refusée.',
    'academy.students.error_refuse': 'Échec du refus.',
    'academy.students.val_firstname': 'Le Nom est obligatoire.',
    'academy.students.val_lastname': 'Le Prénom est obligatoire.',
    'academy.students.val_gender': 'Le Genre est obligatoire.',
    'academy.students.val_dob': 'La Date de naissance est obligatoire.',
    'academy.students.val_health': 'Précisez les problèmes de santé.',
    'academy.students.val_allergies': 'Précisez les allergies.',
    'academy.students.val_guardian_first': 'Nom du responsable obligatoire.',
    'academy.students.val_guardian_last': 'Prénom du responsable obligatoire.',
    'academy.students.val_guardian_rel': 'Lien avec l\'enfant obligatoire.',
    'academy.students.val_guardian_phone': 'Téléphone du responsable obligatoire.',
    'academy.students.val_image_right': 'Droit à l\'image obligatoire.',
    'academy.students.table.registration_status': 'Inscription',
    'academy.students.action_accept': 'Accepter & Valider',
    'academy.students.action_refuse': 'Refuser',
    'academy.students.list.grid': 'Grille',
    'academy.students.list.table': 'Tableau',
    'academy.students.table.actions': 'Actions',
    'academy.students.table.dob_prefix': 'DDN :',
    'academy.students.table.invoiced': 'Facturé',
    'academy.students.table.total_paid': 'Total Payé',
    'academy.students.table.rest_to_pay': 'Reste à Payer',
    'academy.students.form.title_new': 'Nouvel Apprenant / Inscription',
    'academy.students.form.title_edit': 'Modifier : {0} {1}',
    'academy.students.form.category': 'Catégorie',
    'academy.students.form.adult': 'Adulte',
    'academy.students.form.junior': 'Junior',
    'academy.students.form.photo': 'Photo',
    'academy.students.form.change_photo': 'Changer la photo',
    'academy.students.form.attachment': 'Fichier joint',
    'academy.students.form.attach_file': 'Joindre un fichier',
    'academy.students.form.view_file': 'Cliquer pour ouvrir le fichier',

    'academy.students.form.dossier': 'Dossier',
    'academy.students.form.dos_code': 'Code Dossier',
    'academy.students.form.dos_lib': 'Libellé Dossier',
    'academy.students.form.last_modified': 'Dernière modification',
    'academy.students.form.modified_by': 'par',
    'academy.students.form.emergency_contact': 'Contact d\'urgence',
    'academy.students.financial_snapshot': 'Aperçu Financier',
    'academy.students.card.invoiced': 'Facturé',
    'academy.students.card.paid': 'Payé',
    'academy.students.card.rest': 'Reste',

    // Form — champs détaillés
    'academy.students.form.photo_caption': 'Photo de profil',
    'academy.students.form.file_label': 'Pièce jointe (CI, médical, autre…)',
    'academy.students.form.section_personal': 'Informations personnelles',
    'academy.students.form.registration_status': 'Statut d\'inscription',
    'academy.students.field.course_template': 'Modèle de cours (pré-inscription)',
    'academy.students.field.time_preference': 'Créneau horaire souhaité',
    'academy.students.field.loi_consent': 'Consentement Loi 18-07',
    'academy.students.form.section_prereg': 'Informations de pré-inscription',
    'academy.students.status.active': 'Actif',
    'academy.students.status.preregistered': 'Pré-Inscrit',
    'academy.students.status.refused': 'Refusé',
    'academy.students.form.firstname': 'Nom',
    'academy.students.form.lastname': 'Prénom',
    'academy.students.form.gender': 'Genre / Sexe',
    'academy.students.form.gender_male': 'Mâle',
    'academy.students.form.gender_female': 'Femelle',
    'academy.students.form.dob': 'Date de naissance',
    'academy.students.form.address': 'Adresse',
    'academy.students.form.tab_adult': 'Adulte (+16 ans)',
    'academy.students.form.tab_junior': 'Junior / Kid',
    'academy.students.form.section_adult': 'Informations Adulte',
    'academy.students.form.profession': 'Profession',
    'academy.students.form.email': 'Email (pas obligatoire)',
    'academy.students.form.phone': 'N° de tél',
    'academy.students.form.section_junior': 'Informations Junior',
    'academy.students.form.school_level': 'Niveau scolaire',
    'academy.students.form.blood_group': 'Groupage Sanguin',
    'academy.students.form.blood_group_none': 'Non précisé',
    'academy.students.form.health_issues': 'L\'enfant présente-t-il des problèmes de santé ?',
    'academy.students.form.health_specify': '(Si Oui) Préciser :',
    'academy.students.form.health_other_detail': 'Préciser la maladie',
    'academy.students.form.health_other_detail_placeholder': 'Décrire la maladie ou le problème de santé...',
    'academy.students.form.health_condition.asthma': 'Asthme',
    'academy.students.form.health_condition.diabetes': 'Diabète',
    'academy.students.form.health_condition.epilepsy': 'Épilepsie',
    'academy.students.form.health_condition.other': 'Autre',
    'academy.students.form.guardian_relation.parent': 'Père / Mère',
    'academy.students.form.guardian_relation.sibling': 'Frère / Sœur',
    'academy.students.form.guardian_relation.grandparent': 'Grand-père / Grand-mère',
    'academy.students.form.guardian_relation.uncle_aunt': 'Oncle / Tante',
    'academy.students.form.guardian_relation.guardian': 'Tuteur / Tutrice',
    'academy.students.form.guardian_relation.other': 'Autre',
    'academy.students.form.school_level.primary_1': '1ère Année Primaire',
    'academy.students.form.school_level.primary_2': '2ème Année Primaire',
    'academy.students.form.school_level.primary_3': '3ème Année Primaire',
    'academy.students.form.school_level.primary_4': '4ème Année Primaire',
    'academy.students.form.school_level.primary_5': '5ème Année Primaire',
    'academy.students.form.school_level.middle_1': '1ère Année Moyenne',
    'academy.students.form.school_level.middle_2': '2ème Année Moyenne',
    'academy.students.form.school_level.middle_3': '3ème Année Moyenne',
    'academy.students.form.school_level.middle_4': '4ème Année Moyenne',
    'academy.students.form.allergies': 'Allergies',
    'academy.students.form.allergy_details': 'Préciser les allergies',
    'academy.students.form.allergy_details_placeholder': 'Décrire les allergies (aliments, médicaments, etc.)...',
    'academy.students.form.image_right': 'Droit à l\'image',
    'academy.students.form.yes': 'Oui',
    'academy.students.form.no': 'Non',
    'academy.students.form.guardian_title': 'Personne responsable',
    'academy.students.form.guardian_firstname': 'Nom',
    'academy.students.form.guardian_lastname': 'Prénom',
    'academy.students.form.guardian_relation': 'Lien avec l\'enfant',
    'academy.students.form.guardian_phone': 'Téléphone',
    'academy.students.form.authorized_pickup': 'Personnes autorisées à récupérer l\'enfant',
    'academy.students.form.authorized_pickup_hint': 'Format : Nom & prénom + Lien',
    'academy.students.form.authorized_pickup_placeholder': 'Nom & prénom + Lien (une personne par ligne)',
    'academy.students.section_dossier': 'Informations Dossier',


    'academy.teachers.busy_map_summary': 'Grille d\'Occupabilité ({0} Créneaux Réservés) :',
    'academy.teachers.view_profile': 'Voir Profil & Planning',
    'academy.teachers.modal.edit_title': 'Modifier l\'Enseignant',
    'academy.teachers.modal.add_title': 'Ajouter un Nouvel Enseignant',
    'academy.teachers.modal.fullname': 'Nom Complet',
    'academy.teachers.modal.email': 'Adresse Email',
    'academy.teachers.modal.phone': 'Numéro de Téléphone',
    'academy.teachers.modal.specialization': 'Spécialité (ex: Web Fullstack, IA)',
    'academy.teachers.modal.hourly_rate': 'Tarif Horaire Paie ({0})',
    'academy.teachers.modal.bio': 'Bio / Remarques',
    'academy.teachers.modal.save': 'Enregistrer l\'Enseignant',
    'academy.teachers.drawer.groups': 'Groupes & Cohortes Affectés',
    'academy.teachers.drawer.timeline': 'Planning Visuel Hebdomadaire',
    'academy.teachers.drawer.free': 'Libre',
    'academy.teachers.form.title_new': 'Nouveau Profil Enseignant',
    'academy.teachers.form.title_edit': 'Modifier l\'Enseignant : {0} {1}',
    'academy.teachers.form.firstname': 'Prénom',
    'academy.teachers.form.lastname': 'Nom',
    'academy.teachers.form.gender': 'Genre / Sexe',
    'academy.teachers.form.gender_male': 'Mâle',
    'academy.teachers.form.gender_female': 'Femelle',
    'academy.teachers.form.dob': 'Date de naissance',
    'academy.teachers.form.email': 'Adresse Email',
    'academy.teachers.form.phone': 'N° de téléphone',
    'academy.teachers.form.specialization': 'Spécialités / Formations',
    'academy.teachers.form.hire_date': 'Date d\'embauche',
    'academy.teachers.form.status': 'Statut',
    'academy.teachers.form.dossier_code': 'Code Dossier',
    'academy.teachers.form.dossier_lib': 'Dossier',
    'academy.teachers.form.rate_type': 'Type de tarif',
    'academy.teachers.form.rate_type.hourly': 'Heure',
    'academy.teachers.form.rate_type.daily': 'Jour',
    'academy.teachers.form.rate_type.monthly': 'Mois',
    'academy.teachers.form.rate_type.percentage': 'Pourcentage',
    'academy.teachers.form.rate_type.fixed': 'Montant (valeur fixe)',
    'academy.teachers.form.rate_amount': 'Montant du tarif',
    'academy.teachers.form.bio': 'Bio / Remarques',
    'academy.teachers.form.cv_file': 'CV / Pièce Jointe',
    'academy.teachers.form.attach_cv': 'Joindre un CV (PDF/Doc)',
    'academy.teachers.form.view_cv': 'Consulter le CV',
    'academy.teachers.form.photo_caption': 'Photo de profil',
    'academy.teachers.form.avatar_url': 'URL Avatar / Photo',
    'academy.teachers.table.teacher': 'Enseignant',
    'academy.teachers.table.contact': 'Contact',
    'academy.teachers.table.specialization': 'Spécialité',
    'academy.teachers.table.rate': 'Tarif',
    'academy.teachers.table.status': 'Statut',
    'academy.teachers.table.cv': 'CV',
    'academy.teachers.search_placeholder': 'Rechercher par nom, spécialité, email...',
    'academy.teachers.back_to_list': 'Retour à la liste',
    'academy.teachers.active_badge': 'Actif',
    'academy.teachers.inactive_badge': 'Inactif',
    'academy.teachers.section_personal': 'Informations Personnelles',
    'academy.teachers.section_professional': 'Informations Professionnelles',
    'academy.teachers.section_dossier': 'Informations Dossier',

    'academy.rooms.title': 'Gestion des Salles & Ressources',
    'academy.rooms.subtitle': 'Gérez vos espaces de cours, équipements et capacités d\'accueil.',
    'academy.rooms.add_new': 'Ajouter une salle',
    'academy.rooms.back_to_list': 'Retour à la liste',
    'academy.rooms.confirm_delete': 'Voulez-vous vraiment supprimer cette salle ?',
    'academy.rooms.search_placeholder': 'Rechercher par numéro, étage, équipements...',
    'academy.rooms.room_number': 'Numéro de salle',
    'academy.rooms.floor': 'Étage',
    'academy.rooms.capacity': 'Capacité',
    'academy.rooms.equipment': 'Équipements',
    'academy.rooms.status': 'Statut',
    'academy.rooms.status.active': 'Active',
    'academy.rooms.status.maintenance': 'En Maintenance',
    'academy.rooms.status.archived': 'Archivée',
    'academy.rooms.section_info': 'Informations de la Salle',
    'academy.rooms.section_dossier': 'Informations Dossier',
    'academy.rooms.form.title_new': 'Nouvelle Salle',
    'academy.rooms.form.title_edit': 'Modifier la Salle : {0}',
    'academy.rooms.form.dossier_code': 'Code Dossier',
    'academy.rooms.form.dossier_lib': 'Dossier',
    'academy.rooms.min_capacity': 'Capacité Min.',
    'academy.rooms.all_capacities': 'Toutes Capacités',
    'academy.rooms.capacity_plus': '{0}+ Apprenants',
    'academy.rooms.view_grid': 'Grille',
    'academy.rooms.view_table': 'Tableau',
    'academy.rooms.room_prefix': 'Salle {0}',
    'academy.rooms.location': 'Emplacement :',
    'academy.rooms.capacity_label': 'Capacité : {0} Apprenants',
    'academy.rooms.weekly_util': 'Utilisation Hebdomadaire',
    'academy.rooms.modal.edit_title': 'Modifier la Salle',
    'academy.rooms.modal.add_title': 'Créer une Nouvelle Salle',
    'academy.rooms.modal.number': 'Numéro de Salle (ex: A-101)',
    'academy.rooms.modal.floor': 'Étage (ex: 1er Étage)',
    'academy.rooms.modal.capacity': 'Capacité (Nombre entier)',
    'academy.rooms.modal.equipment': 'Équipements (Séparés par des virgules)',
    'academy.rooms.modal.equipment_helper': 'ex: Projecteur, Tableau, PCs, WiFi',
    'academy.rooms.modal.save': 'Enregistrer la Salle',

    'academy.courses.title': 'Modèles de Cours',
    'academy.courses.subtitle': 'Gérez les programmes, tarifs et durées des formations.',
    'academy.courses.add_new': 'Nouveau Modèle',
    'academy.courses.back_to_list': 'Retour à la liste',
    'academy.courses.confirm_delete': 'Voulez-vous supprimer le modèle « {0} » ?',
    'academy.courses.section_info': 'Informations du Modèle',
    'academy.courses.section_dossier': 'Informations Dossier',
    'academy.courses.search_placeholder': 'Rechercher par titre, description...',
    'academy.courses.hours_label': '{0} h',
    'academy.courses.course_preview': 'Aperçu du Modèle',
    'academy.courses.list.grid': 'Grille',
    'academy.courses.list.table': 'Tableau',
    'academy.courses.table.title': 'Intitulé',
    'academy.courses.table.hours': 'Durée (h)',
    'academy.courses.table.price': 'Tarif',
    'academy.courses.table.description': 'Description',
    'academy.courses.form.title_new': 'Nouveau Modèle de Cours',
    'academy.courses.form.title_edit': 'Modifier : {0}',
    'academy.courses.form.title': 'Intitulé du Cours',
    'academy.courses.form.hours': 'Durée Totale (heures)',
    'academy.courses.form.price': 'Tarif ({0})',
    'academy.courses.form.description': 'Description & Programme',
    'academy.courses.form.dossier_code': 'Code Dossier',
    'academy.courses.form.dossier_lib': 'Dossier',
    'academy.courses.val_title': 'L\'intitulé est obligatoire.',
    'academy.courses.val_hours': 'La durée doit être supérieure à 0.',
    'academy.courses.val_price': 'Le tarif ne peut pas être négatif.',
    'academy.courses.form.start_date': 'Date de début (validité)',
    'academy.courses.form.end_date': 'Date de fin (validité)',
    'academy.courses.form.show_price': 'Afficher le tarif sur le lien de pré-inscription',
    'academy.courses.form.age_band': 'Tranche d\'âge (libellé)',
    'academy.courses.form.age_min': 'Âge minimum',
    'academy.courses.form.age_max': 'Âge maximum',
    'academy.courses.val_age_range': 'L\'âge minimum doit être inférieur ou égal à l\'âge maximum.',
    'academy.courses.val_date_range': 'La date de début doit être antérieure ou égale à la date de fin.',
    'academy.courses.table.age_band': 'Tranche d\'âge',
    'academy.courses.table.validity': 'Validité',

    'academy.scheduler.day_label': 'Jour de la Semaine',
    'academy.scheduler.matrix_title': 'Salles (Axe Y) \\ Heures (Axe X)',
    'academy.scheduler.cap_floor': 'Cap: {0} | {1}',
    'academy.scheduler.modal.title': 'Programmation de Séance',
    'academy.scheduler.modal.select_room': 'Sélectionner la Salle',
    'academy.scheduler.modal.select_group': 'Sélectionner la Cohorte',
    'academy.scheduler.modal.select_teacher': 'Assigner l\'Enseignant Principal',
    'academy.scheduler.modal.start_time': 'Heure de Début',
    'academy.scheduler.modal.end_time': 'Heure de Fin',
    'academy.scheduler.modal.recurring': 'Séances Récurrentes Hebdomadaires',
    'academy.scheduler.modal.repeat_weeks': 'Répéter pendant N Semaines',
    'academy.scheduler.modal.confirm': 'Valider le Planning',

    'academy.attendance.teacher_mode': 'Mode Connexion Restreinte Enseignant',
    'academy.attendance.my_sessions': 'Mes Séances d\'Aujourd\'hui',
    'academy.attendance.all_sessions': 'Toutes les Séances Programmées',
    'academy.attendance.taken': 'Présences Prises',
    'academy.attendance.pending': 'En Attente',
    'academy.attendance.room_instructor': 'Salle : {0} • Instructeur : {1}',
    'academy.attendance.enrolled_count': '{0} Apprenants Inscrits',
    'academy.attendance.table.student': 'Apprenant',
    'academy.attendance.table.pass_rate': 'Taux de Réussite Cours',
    'academy.attendance.table.toggle': 'Saisie des Présences (Présent / Absent / Retard / Excusé)',
    'academy.attendance.passing': 'En Règle (>75%)',
    'academy.attendance.at_risk': 'À Risque',
    'academy.attendance.select_prompt': 'Sélectionnez une séance pour prendre les présences.',
    'academy.attendance.no_sessions': 'Aucune séance programmée.',
    'academy.attendance.no_students': 'Aucun apprenant inscrit dans ce groupe.',

    'academy.reports.title': 'Rapports & Analyses Stratégiques',
    'academy.reports.subtitle': 'Revenus, performances des professeurs et taux d\'utilisation des salles.',
    'academy.reports.export_csv': 'Exporter CSV',
    'academy.reports.print_pdf': 'Imprimer / PDF',
    'academy.reports.teacher_perf_title': 'Performance de Présence des Enseignants',
    'academy.reports.teacher_perf_sub': 'Enseignants avec les plus hauts taux de présence dans leurs cours.',
    'academy.reports.rate_label': 'Taux {0}%',
    'academy.reports.room_util_title': 'Taux d\'Utilisation des Salles',
    'academy.reports.room_util_sub': 'Efficacité hebdomadaire de réservation (heures réservées vs capacité 40h).',
    'academy.reports.booked_label': '{0}% Réseré',
    'academy.reports.churn_title': 'Rapport d\'Abandon & Churn Apprenant',
    'academy.reports.churn_sub': 'Liste des apprenants ayant abandonné leurs cours ou interrompu leurs présences.',
    'academy.reports.zero_churn': 'Aucun Abandon Apprenant !',
    'academy.reports.zero_churn_sub': 'Tous les apprenants inscrits assistent régulièrement aux cours.',
    'academy.reports.table.student': 'Apprenant',
    'academy.reports.table.group': 'Groupe / Cohorte',
    'academy.reports.table.enrollment_date': 'Date d\'Inscription',
    'academy.reports.table.status': 'Statut',

    'academy.common.group_number': 'Groupe #{0}',
    'academy.common.room': 'Salle',
    'academy.common.teacher': 'Enseignant',
    'academy.common.group': 'Groupe',
    'academy.common.group_session': 'Séance de groupe',
    'academy.common.general': 'Général',
    'academy.common.unassigned_floor': 'Étage non assigné',
    'academy.common.students_count': '{0} apprenants',
    'academy.teachers.confirm_deactivate': 'Désactiver cet enseignant ?',
    'academy.attendance.error.hold_block': 'Impossible de marquer l\'apprenant présent (blocage financier).',
    'academy.scheduler.error.conflict': 'Conflit de planning détecté.',
    'academy.scheduler.tooltip.session': 'Groupe : {0} | Enseignant : {1} | {2} apprenants',
    'academy.groups.error.status_change': 'Impossible de changer le statut du groupe.',
    'academy.reports.capacity_short': 'Cap',

    // Maker - Dashboard
    'maker.dashboard.title': 'Tableau de bord',
    'maker.dashboard.welcome': 'Bienvenue sur BLOBTIC Maker - Logiciel de menuiserie',
    'maker.dashboard.stats.projects': 'Projets',
    'maker.dashboard.stats.estimates': 'Devis',
    'maker.dashboard.stats.customers': 'Clients',
    'maker.dashboard.stats.materials': 'Matériaux',
    'maker.dashboard.quick_actions': 'Actions Rapides',
    'maker.dashboard.recent_activity': 'Activité Récente',
    'maker.dashboard.no_activity': 'Aucune activité récente',
    'maker.dashboard.loading': 'Chargement...',

    // Maker - Profiles
    'maker.gamme.title': 'Gammes',
    'maker.profiles.title': 'Gestion des Profilés',
    'maker.profiles.description': 'Les profilés sont des pièces de matériaux (Aluminium, PVC, etc.) utilisées pour fabriquer les cadres des fenêtres et des portes.',
    'maker.profiles.add': 'Ajouter un profilé',
    'maker.profiles.edit': 'Modifier le profilé',
    'maker.profiles.table.name': 'Nom',
    'maker.profiles.table.code': 'Code',
    'maker.profiles.table.material': 'Matériau',
    'maker.profiles.table.dimensions': 'Dimensions (LxHxP)',
    'maker.profiles.table.price': 'Prix',
    'maker.profiles.table.stock': 'Stock',
    'maker.profiles.table.actions': 'Actions',
    'maker.profiles.loading': 'Chargement...',
    'maker.profiles.empty': 'Aucun profilé trouvé',
    'maker.profiles.delete.title': 'Confirmer la suppression',
    'maker.profiles.delete.confirm': 'Êtes-vous sûr de vouloir supprimer ce profilé ?',
    'maker.profiles.delete.action': 'Supprimer',

    // Maker - Design
    'maker.design.title': 'Studio de Conception',
    'maker.design.select_project': 'Sélectionner un projet',
    'maker.design.new': 'Nouveau Design',
    'maker.design.templates': 'Modèles',
    'maker.design.components': 'Composants',
    'maker.design.export_cnc': 'Exporter CNC',
    'maker.design.reports': 'Rapports',
    'maker.design.tab.design': 'Vue Conception',
    'maker.design.tab.sheet': 'Feuille & Devis',
    'maker.design.tab.windows': 'Fenêtres ({0})',
    'maker.design.tab.doors': 'Portes ({0})',
    'maker.design.items_list': 'Éléments',
    'maker.design.no_items': 'Aucun élément trouvé',
    'maker.design.select_prompt': 'Veuillez sélectionner un projet pour commencer la conception',
    'maker.design.select_item_prompt': 'Veuillez sélectionner un élément dans la liste ou en créer un nouveau',
    'maker.design.no_item_selected': 'Aucun élément sélectionné',
    'maker.design.properties': 'Propriétés',
    'maker.design.field.width': 'Largeur (mm)',
    'maker.design.field.height': 'Hauteur (mm)',
    'maker.design.field.material': 'Matériau',
    'maker.design.field.profile': 'Profilé',
    'maker.design.action.save': 'Enregistrer',
    'maker.design.delete.confirm': 'Êtes-vous sûr de vouloir supprimer ce design ?',
    'maker.design.modal.title': 'Créer {0}',
    'maker.design.modal.type': 'Type',
    'maker.design.modal.shape': 'Forme',
    'maker.design.modal.cancel': 'Annuler',
    'maker.design.modal.create': 'Créer',
    'maker.design.validation.project': 'Veuillez sélectionner un projet',

    'maker.design.validation.dimensions': 'Les dimensions doivent être positives et inférieures à 5000mm',
    'maker.design.validation.profile': 'Veuillez sélectionner un profil pour le matériau choisi',

    // Maker - Reports
    'maker.reports.title': 'Rapports & Analyses',
    'maker.reports.type.label': 'Type de rapport',
    'maker.reports.type.sales': 'Rapport des ventes',
    'maker.reports.type.projects': 'Rapport des projets',
    'maker.reports.type.materials': 'Rapport des matériaux',
    'maker.reports.type.customers': 'Rapport des clients',
    'maker.reports.type.production': 'Rapport de production',
    'maker.reports.export': 'Exporter CSV',
    'maker.reports.date.from': 'De',
    'maker.reports.date.to': 'À',
    'maker.reports.stat.revenue': 'Revenu',
    'maker.reports.stat.estimates': 'Devis',
    'maker.reports.stat.approved': 'Approuvé',
    'maker.reports.stat.pending': 'En attente',
    'maker.reports.stat.avg_value': 'Valeur Moy.',
    'maker.reports.stat.total_projects': 'Total Projets',
    'maker.reports.stat.completed': 'Terminé',
    'maker.reports.stat.total_items': 'Total Articles',
    'maker.reports.stat.total_materials': 'Total Matériaux',
    'maker.reports.stat.stock_value': 'Valeur Stock',
    'maker.reports.stat.active': 'Actif',
    'maker.reports.stat.total_customers': 'Total Clients',
    'maker.reports.stat.project_value': 'Valeur Projet',
    'maker.reports.stat.doors': 'Portes',
    'maker.reports.stat.windows': 'Fenêtres',
    'maker.reports.table.estimate': 'Devis #',
    'maker.reports.table.date': 'Date',
    'maker.reports.table.project': 'Projet',
    'maker.reports.table.status': 'Statut',
    'maker.reports.table.total': 'Total',
    'maker.reports.table.customer': 'Client',
    'maker.reports.table.created': 'Créé le',
    'maker.reports.table.items': 'Articles',
    'maker.reports.table.name': 'Nom',
    'maker.reports.table.code': 'Code',
    'maker.reports.table.stock': 'Stock',
    'maker.reports.table.value': 'Valeur',
    'maker.reports.table.usage': 'Utilisation',
    'maker.reports.table.company': 'Société',
    'maker.reports.table.estimates': 'Devis',
    'maker.reports.dist.material': 'Distribution des matériaux',


    // Maker - Design Types
    'maker.design.type.casement': 'Battant',
    'maker.design.type.tilt_turn': 'Oscillo-battant',
    'maker.design.type.sliding': 'Coulissant',
    'maker.design.type.french': 'Porte-fenêtre',
    'maker.design.type.italian': 'Italienne',
    'maker.design.type.awning': 'Auvent',
    'maker.design.type.parallel_sliding': 'Coulissant parallèle',
    'maker.design.type.pivot': 'Pivot',
    'maker.design.type.accordion': 'Accordéon',
    'maker.design.type.lift_sliding': 'Levant-coulissant',
    'maker.design.type.single': 'Simple',
    'maker.design.type.double': 'Double',
    'maker.design.type.folding': 'Pliante',
    'maker.design.type.revolving': 'Tournante',
    'maker.design.type.unknown': 'Inconnu',

    // Maker - Projects
    'maker.projects.title': 'Projets',
    'maker.projects.new': 'Nouveau Projet',
    'maker.projects.search': 'Rechercher un projet...',
    'maker.projects.filter.status': 'Tous les statuts',
    'maker.projects.filter.active': 'Actif',
    'maker.projects.filter.inactive': 'Inactif',
    'maker.projects.loading': 'Chargement des projets...',
    'maker.projects.empty.title': 'Aucun projet trouvé',
    'maker.projects.empty.desc': 'Créez votre premier projet pour commencer',
    'maker.projects.create': 'Créer un projet',
    'maker.projects.card.noDesc': 'Aucune description',
    'maker.projects.meta.created': 'Créé le',

    // Maker - Design Canvas
    'maker.design.canvas.select': 'Sélectionner',
    'maker.design.canvas.vsplit': 'V-Split',
    'maker.design.canvas.hsplit': 'H-Split',
    'maker.design.canvas.sash': 'Ouvrant',
    'maker.design.canvas.delete': 'Supprimer',
    'maker.design.canvas.clear': 'Effacer',
    'maker.design.canvas.dimensions': 'Dimensions',
    'maker.design.canvas.splitters': 'Séparateurs',
    'maker.design.canvas.sashes': 'Ouvrants',
    'maker.design.canvas.title': 'Dessin Technique 2D',

    // Maker - Templates
    'maker.design.templates.prompt': 'Sélectionnez un modèle prédéfini ou créez une géométrie personnalisée',
    'maker.design.templates.custom': 'Créer une géométrie personnalisée',
    'maker.design.template.custom.name': 'Personnalisé',
    'maker.design.template.custom.desc': 'Dessiner une géométrie personnalisée',
    'maker.design.template.casement_single': 'Battant Simple',
    'maker.design.template.casement_single.desc': 'Fenêtre standard à un vantail',
    'maker.design.template.casement_double': 'Battant Double',
    'maker.design.template.casement_double.desc': 'Fenêtre à deux vantaux',
    'maker.design.template.tilt_turn': 'Oscillo-battant',
    'maker.design.template.tilt_turn.desc': 'Fonctionnalité oscillo-battante',
    'maker.design.template.sliding': 'Fenêtre Coulissante',
    'maker.design.template.sliding.desc': 'Fenêtre coulissante horizontale',
    'maker.design.template.round': 'Fenêtre Ronde',
    'maker.design.template.round.desc': 'Fenêtre circulaire',
    'maker.design.template.french': 'Porte-fenêtre',
    'maker.design.template.french.desc': 'Style porte-fenêtre',
    'maker.design.template.door_single': 'Porte Simple',
    'maker.design.template.door_single.desc': 'Porte standard simple',
    'maker.design.template.door_double': 'Porte Double',
    'maker.design.template.door_double.desc': 'Porte à deux vantaux',
    'maker.design.template.door_sliding': 'Porte Coulissante',
    'maker.design.template.door_sliding.desc': 'Système de porte coulissante',
    'maker.design.template.door_folding': 'Porte Pliante',
    'maker.design.template.door_folding.desc': 'Système de porte pliante',

    // Maker - Components
    'maker.design.components.prompt': 'Ajoutez quincaillerie, volets, moustiquaires et accessoires',
    'maker.design.components.pool': 'Lot de {0}',
    'maker.design.components.cat.hardware': 'Quincaillerie',
    'maker.design.components.cat.shutter': 'Volets',
    'maker.design.components.cat.screen': 'Moustiquaires',
    'maker.design.components.cat.roller': 'Volets Roulants',
    // Component Items (Examples)
    'maker.component.handle_std': 'Poignée Standard',
    'maker.component.handle_std.desc': 'Poignée standard fenêtre/porte',
    'maker.component.handle_prem': 'Poignée Premium',
    'maker.component.handle_prem.desc': 'Poignée finition premium',
    'maker.component.lock_multi': 'Serrure Multi-points',
    'maker.component.lock_multi.desc': 'Système de verrouillage 3 points',
    'maker.component.lock_dead': 'Serrure Pêne Dormant',
    'maker.component.lock_dead.desc': 'Pêne dormant standard',
    'maker.component.hinge_butt': 'Paumelle',
    'maker.component.hinge_butt.desc': 'Paumelle standard',
    'maker.component.hinge_conc': 'Charnière Invisible',
    'maker.component.hinge_conc.desc': 'Système de charnière invisible',
    'maker.component.stay': 'Entrebâilleur',
    'maker.component.stay.desc': 'Entrebâilleur de fenêtre',
    'maker.component.shutter_louver': 'Volet Persienne',
    'maker.component.shutter_louver.desc': 'Volet à lames orientables',
    'maker.component.shutter_panel': 'Volet Panneau',
    'maker.component.shutter_panel.desc': 'Volet panneau plein',
    'maker.component.shutter_batten': 'Volet Battant',
    'maker.component.shutter_batten.desc': 'Style battant traditionnel',
    'maker.component.screen_std': 'Moustiquaire Standard',
    'maker.component.screen_std.desc': 'Moustiquaire maillage standard',
    'maker.component.screen_retr': 'Moustiquaire Enroulable',
    'maker.component.screen_retr.desc': 'Moustiquaire enroulable',
    'maker.component.screen_mag': 'Moustiquaire Magnétique',
    'maker.component.screen_mag.desc': 'Fixation magnétique',
    'maker.component.roller_man': 'Volet Roulant Manuel',
    'maker.component.roller_man.desc': 'Volet roulant manuel',
    'maker.component.roller_elec': 'Volet Roulant Électrique',
    'maker.component.roller_elec.desc': 'Volet roulant motorisé',
    'maker.component.roller_solar': 'Volet Roulant Solaire',
    'maker.component.roller_solar.desc': 'Alimentation solaire',

    // Maker - Shapes
    'maker.shape.rectangular': 'Rectangulaire',
    'maker.shape.round': 'Rond',
    'maker.shape.trapezoid': 'Trapèze',
    'maker.shape.custom': 'Personnalisé',

    // Maker - Fields
    'maker.field.name': 'Nom',
    'maker.field.description': 'Description',
    'maker.field.customer': 'Client',
    'maker.field.project': 'Projet',
    'maker.field.code': 'Code',
    'maker.field.unitPrice': 'Prix Unitaire',
    'maker.field.unit': 'Unité',
    'maker.field.stockQuantity': 'Quantité Stock',
    'maker.field.active': 'Actif',
    'maker.field.width': 'Largeur (mm)',
    'maker.field.height': 'Hauteur (mm)',
    'maker.field.depth': 'Profondeur (mm)',
    'maker.field.company': 'Société',
    'maker.field.email': 'Email',
    'maker.field.phone': 'Téléphone',
    'maker.field.address': 'Adresse',
    'maker.field.city': 'Ville',
    'maker.field.country': 'Pays',
    'maker.field.postalCode': 'Code Postal',
    'maker.field.materialType': 'Type Matériau',
    'maker.field.photo': 'Photo',

    // Maker - Actions & Messages
    'action.create': 'Créer',
    'maker.action.upload_photo': 'Télécharger une photo',
    'action.generating': 'Génération...',
    'maker.msg.success.create': 'Création réussie',
    'maker.msg.success.generate': 'Génération réussie !',
    'maker.msg.error.create': 'Échec de la création',
    'maker.msg.error.generate': 'Échec de la génération',
    'maker.msg.error.required': 'Ce champ est requis',
    'maker.msg.noProjects': 'Aucun projet disponible',
    'maker.msg.failedSave': 'Échec de l\'enregistrement : {0}',

    // Maker - Customers
    'maker.customers.title': 'Clients',
    'maker.customers.new': 'Nouveau Client',
    'maker.customers.search': 'Rechercher un client',
    'maker.customers.edit': 'Modifier le client',
    'maker.customers.loading': 'Chargement des clients...',
    'maker.customers.empty': 'Aucun client trouvé. Créez-en un pour commencer.',
    'maker.customers.delete.title': 'Confirmer la suppression',
    'maker.customers.delete.confirm': 'Êtes-vous sûr de vouloir supprimer {0} ?',
    'maker.customers.delete.action': 'Supprimer',

    // Maker - Materials
    'maker.materials.title': 'Matériaux',
    'maker.materials.add': 'Ajouter un matériau',
    'maker.materials.edit': 'Modifier le matériau',
    'maker.materials.search': 'Rechercher...',
    'maker.materials.filter.all': 'Tous les types',
    'maker.materials.table.code': 'Code',
    'maker.materials.table.name': 'Nom',
    'maker.materials.table.type': 'Type',
    'maker.materials.table.price': 'Prix Unitaire',
    'maker.materials.table.unit': 'Unité',
    'maker.materials.table.stock': 'Stock',
    'maker.materials.table.status': 'Statut',
    'maker.materials.table.actions': 'Actions',
    'maker.materials.loading': 'Chargement...',
    'maker.materials.empty': 'Aucun matériau trouvé',
    'maker.materials.type.aluminum': 'Aluminium',
    'maker.materials.type.pvc': 'PVC',
    'maker.materials.type.wood': 'Bois',
    'maker.materials.type.steel': 'Acier',
    'maker.materials.type.unknown': 'Inconnu',
    'maker.materials.delete.title': 'Confirmer la suppression',
    'maker.materials.delete.confirm': 'Êtes-vous sûr de vouloir supprimer {0} ?',

    // Maker - Estimates
    'maker.estimates.title': 'Devis',
    'maker.estimates.new': 'Nouveau Devis',
    'maker.estimates.loading': 'Chargement des devis...',
    'maker.estimates.empty.title': 'Pas encore de devis',
    'maker.estimates.empty.desc': 'Créez et gérez les devis pour vos projets',
    'maker.estimates.generate': 'Générer un devis',
    'maker.estimates.table.number': 'N° Devis',
    'maker.estimates.table.project': 'Projet',
    'maker.estimates.table.date': 'Date',
    'maker.estimates.table.subtotal': 'Sous-total',
    'maker.estimates.table.tax': 'TVA',
    'maker.estimates.table.total': 'Total',
    'maker.estimates.table.status': 'Statut',
    'maker.estimates.table.validity': 'Validité',

    // Maker - Production
    'maker.production.title': 'Planification Production',
    'maker.production.tabs.projects': 'Projets',
    'maker.production.tabs.bom': 'Nomenclature (BOM)',
    'maker.production.tabs.cutting': 'Plan de Coupe',
    'maker.production.select_project': 'Sélectionner un projet',
    'maker.production.loading': 'Chargement...',
    'maker.production.door_count': 'Portes: {0}',
    'maker.production.window_count': 'Fenêtres: {0}',
    'maker.production.bom.generate': 'Générer BOM',
    'maker.production.cutting.optimize': 'Optimiser Coupe',
    'maker.production.bom.total_materials': 'Total Matériaux',
    'maker.production.bom.total_cost': 'Coût Total',
    'maker.production.bom.print': 'Imprimer',
    'maker.production.cutting.efficiency': 'Efficacité Moy.',
    'maker.production.cutting.waste': 'Déchets Total',
    'maker.production.cutting.required': 'Requis: {0}',
    'maker.production.cutting.stock': 'Stock: {0}',
    'maker.production.cutting.waste_label': 'Chute: {0}',
    'maker.production.cutting.layout': 'Plan de coupe (Barre: {0})',

    // Menu Default
    'menu.default.dashboard': 'Tableau de bord',

    // Page - Menu Config
    'page.menuConfig.title': 'Configuration du Menu',
    'page.menuConfig.profil.label': 'Profil',
    'page.menuConfig.profil.placeholder': 'Sélectionner un profil',
    'page.menuConfig.profil.helper': 'Cliquez sur la loupe pour chercher',
    'page.menuConfig.structure.title': 'Structure du menu pour {0}',
    'page.menuConfig.structure.empty': 'Veuillez sélectionner un profil',
    'page.menuConfig.success.save': 'Configuration du menu enregistrée',
    'page.menuConfig.error.load': 'Impossible de charger la configuration du menu',
    'page.menuConfig.error.save': 'Erreur lors de l\'enregistrement',
    'page.menuConfig.accessDenied': 'Accès refusé. Réservé aux administrateurs.',

    // Design Sheet
    'maker.design.sheet.title': 'Feuille de Conception & Devis',
    'maker.design.sheet.empty.title': 'Aucune Conception Sélectionnée',
    'maker.design.sheet.empty.desc': 'Sélectionnez une fenêtre ou une porte pour voir la feuille de conception',
    'maker.design.sheet.section.dimensions': 'Dimensions',
    'maker.design.sheet.section.specs': 'Spécifications',
    'maker.design.sheet.section.components': 'Composants',
    'maker.design.sheet.section.quotation': 'Devis',
    'maker.design.sheet.label.width': 'Largeur (mm)',
    'maker.design.sheet.label.height': 'Hauteur (mm)',
    'maker.design.sheet.label.area': 'Surface',
    'maker.design.sheet.label.type': 'Type',
    'maker.design.sheet.label.shape': 'Forme',
    'maker.design.sheet.label.material': 'Matériau',
    'maker.design.sheet.label.profile': 'Profilé',
    'maker.design.sheet.quotation.unitPrice': 'Prix Unitaire',
    'maker.design.sheet.quotation.quantity': 'Quantité',
    'maker.design.sheet.quotation.discount': 'Remise (%)',
    'maker.design.sheet.quotation.basePrice': 'Prix de Base',
    'maker.design.sheet.quotation.subtotal': 'Sous-total',
    'maker.design.sheet.quotation.total': 'Total',
    'maker.design.sheet.quotation.notes': 'Notes ou instructions spéciales...',
    'maker.design.sheet.noComponents': 'Aucun composant ajouté',
    // Module Parc (Extrait auto)
    'parc.alerts.fetchError': 'Impossible de charger les alertes GPS',
    'parc.alerts.resolveError': 'Impossible de résoudre l\'alerte',
    'parc.alerts.vehicle': 'Véhicule',
    'parc.alerts.type': 'Type d\'alerte',
    'parc.alerts.message': 'Message',
    'parc.alerts.time': 'Heure',
    'parc.alerts.status': 'Statut',
    'parc.alerts.statusResolved': 'Résolu',
    'parc.alerts.statusActive': 'Actif',
    'common.actions.refresh': 'Actualiser',
    'parc.alerts.title': 'Alertes GPS',
    'parc.alerts.resolveAlertTitle': 'Résoudre l\'alerte GPS',
    'parc.dashboard.fetchError': 'Impossible de charger les données du tableau de bord',
    'parc.dashboard.title': 'Tableau de bord',
    'parc.dashboard.overview': 'Vue d\'ensemble de votre parc automobile',
    'parc.dashboard.vehicles': 'Véhicules',
    'parc.dashboard.totalRegistered': 'Total enregistré',
    'parc.dashboard.drivers': 'Conducteurs',
    'parc.dashboard.maintenances': 'Maintenances',
    'parc.dashboard.pendingOrInProgress': 'En attente ou en cours',
    'parc.dashboard.repairs': 'Réparations',
    'parc.dashboard.toCheckOrInProgress': 'À vérifier ou en cours',
    'parc.dashboard.vehicleStatus': 'Statut des véhicules',
    'common.status.noData': 'Aucune donnée disponible',
    'parc.dashboard.monthlyPerformance': 'Performance mensuelle (Trajets)',
    'parc.dashboard.trips': 'Trajets',
    'parc.dashboard.alerts': 'Alertes',
    'parc.dashboard.noAlerts': 'Aucune alerte pour le moment',
    'parc.dashboard.recentActivity': 'Activité récente',
    'parc.dashboard.noRecentActivity': 'Aucune activité récente',
    'parc.documents.fetchError': 'Impossible de charger les données',
    'common.actions.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer cet élément ?',
    'common.actions.actions': 'Actions',
    'common.messages.deleted': 'Supprimé avec succès',
    'parc.documents.docTitle': 'Titre',
    'parc.documents.type': 'Type',
    'parc.documents.vehicle': 'Véhicule',
    'parc.documents.driver': 'Conducteur',
    'parc.documents.expiryDate': 'Date d\'expiration',
    'parc.documents.newDocument': 'Nouveau document',
    'parc.documents.title': 'Documents',
    'parc.documents.editDocument': 'Modifier le document',
    'parc.documents.typeInsurance': 'Assurance',
    'parc.documents.typeTechControl': 'Contrôle technique',
    'parc.documents.typeRegistration': 'Gris d\'immatriculation',
    'parc.documents.typeContract': 'Location/Contrat',
    'parc.documents.typeOther': 'Autre',
    'parc.documents.assignVehicle': 'Affecter au véhicule',
    'common.labels.none': 'N/A',
    'parc.documents.assignDriver': 'Affecter au conducteur',
    'common.messages.unknownError': 'Erreur inconnue',
    'parc.drivers.fetchError': 'Impossible de charger les conducteurs',
    'parc.drivers.firstName': 'Prénom',
    'parc.drivers.lastName': 'Nom',
    'parc.drivers.licenseNumber': 'Numéro de permis',
    'parc.drivers.licenseType': 'Type de permis',
    'parc.drivers.experienceYears': 'Expérience (Années)',
    'parc.drivers.status': 'Statut',
    'parc.drivers.newDriver': 'Nouveau conducteur',
    'parc.drivers.title': 'Conducteurs',
    'parc.drivers.editDriver': 'Modifier le conducteur',
    'parc.drivers.statusActive': 'Actif',
    'parc.drivers.statusInactive': 'Inactif',
    'parc.drivers.statusSuspended': 'Suspendu',
    'parc.drivers.statusOnLeave': 'En congé',
    'parc.maintenance.fetchError': 'Impossible de charger les données de maintenance',
    'parc.maintenance.mainTitle': 'Titre',
    'parc.maintenance.vehicle': 'Véhicule',
    'parc.maintenance.type': 'Type',
    'parc.maintenance.category': 'Catégorie',
    'parc.maintenance.status': 'Statut',
    'parc.maintenance.priority': 'Priorité',
    'parc.maintenance.newMaintenance': 'Nouvelle maintenance',
    'parc.maintenance.title': 'Maintenance',
    'parc.maintenance.editMaintenance': 'Modifier la maintenance',
    'parc.maintenance.typePreventive': 'Préventive',
    'parc.maintenance.typeCorrective': 'Corrective',
    'parc.maintenance.typeEmergency': 'Urgence',
    'parc.maintenance.catEngine': 'Moteur',
    'parc.maintenance.catTransmission': 'Transmission',
    'parc.maintenance.catBrakes': 'Freins',
    'parc.maintenance.catElectrical': 'Électrique',
    'parc.maintenance.catBody': 'Carrosserie',
    'parc.maintenance.catTires': 'Pneus',
    'parc.maintenance.catOther': 'Autre',
    'parc.maintenance.priorityLow': 'Faible',
    'parc.maintenance.priorityMedium': 'Moyenne',
    'parc.maintenance.priorityHigh': 'Haute',
    'parc.maintenance.priorityUrgent': 'Urgente',
    'parc.maintenance.statusScheduled': 'Planifiée',
    'parc.maintenance.statusInProgress': 'En cours',
    'parc.maintenance.statusCompleted': 'Terminée',
    'parc.maintenance.statusCancelled': 'Annulée',
    'parc.maintenance.cost': 'Coût',
    'parc.repairs.fetchError': 'Impossible de charger les données',
    'parc.repairs.vehicle': 'Véhicule',
    'parc.repairs.date': 'Date',
    'parc.repairs.garage': 'Garage',
    'parc.repairs.cost': 'Coût',
    'parc.repairs.status': 'Statut',
    'parc.repairs.newRepair': 'Nouvelle réparation',
    'parc.repairs.title': 'Réparations',
    'parc.repairs.editRepair': 'Modifier la réparation',
    'parc.repairs.statusPlanned': 'Planifiée',
    'parc.repairs.statusInProgress': 'En cours',
    'parc.repairs.statusCompleted': 'Terminée',
    'parc.repairs.statusCancelled': 'Annulée',
    'parc.repairs.description': 'Description',
    'parc.gpsTracking.fetchError': 'Impossible de charger les positions GPS',
    'parc.gpsTracking.vehicle': 'Véhicule',
    'parc.gpsTracking.brand': 'Marque',
    'parc.gpsTracking.latitude': 'Latitude',
    'parc.gpsTracking.longitude': 'Longitude',
    'parc.gpsTracking.speedCol': 'Vitesse (km/h)',
    'parc.gpsTracking.lastUpdate': 'Dernière mise à jour',
    'parc.gpsTracking.title': 'Suivi GPS',
    'parc.gpsTracking.unknownVehicle': 'Véhicule inconnu',
    'parc.gpsTracking.speedVal': 'Vitesse :',
    'parc.gpsTracking.updatedVal': 'Mis à jour :',
    'parc.trips.fetchError': 'Impossible de charger les données des trajets',
    'parc.trips.tripTitle': 'Titre',
    'parc.trips.vehicle': 'Véhicule',
    'parc.trips.driver': 'Conducteur',
    'parc.trips.purpose': 'Objectif',
    'parc.trips.status': 'Statut',
    'parc.trips.newTrip': 'Nouveau trajet',
    'parc.trips.title': 'Trajets',
    'parc.trips.editTrip': 'Modifier le trajet',
    'parc.trips.purposeBusiness': 'Professionnel',
    'parc.trips.purposePersonal': 'Personnel',
    'parc.trips.purposeMaintenance': 'Maintenance',
    'parc.trips.purposeDelivery': 'Livraison',
    'parc.trips.purposeOther': 'Autre',
    'parc.trips.statusScheduled': 'Planifié',
    'parc.trips.statusInProgress': 'En cours',
    'parc.trips.statusCompleted': 'Terminé',
    'parc.trips.statusCancelled': 'Annulé',
    'parc.vehicles.fetchError': 'Impossible de charger les véhicules',
    'parc.vehicles.registration': 'Immatriculation',
    'parc.vehicles.brand': 'Marque',
    'parc.vehicles.model': 'Modèle',
    'parc.vehicles.year': 'Année',
    'parc.vehicles.status': 'Statut',
    'parc.vehicles.newVehicle': 'Nouveau véhicule',
    'parc.vehicles.title': 'Véhicules',
    'parc.vehicles.editVehicle': 'Modifier le véhicule',
    'parc.vehicles.statusActive': 'Actif',
    'parc.vehicles.statusMaintenance': 'En maintenance',
    'parc.vehicles.statusOutOfService': 'Hors service',
    'parc.vehicles.mileage': 'Kilométrage',
    'parc.vehicles.fuelType': 'Type de carburant',
    'parc.vehicles.fuelDiesel': 'Diesel',
    'parc.vehicles.fuelGasoline': 'Essence',
    'parc.vehicles.fuelElectric': 'Électrique',
    'parc.vehicles.fuelHybrid': 'Hybride',
    'parc.vehicles.transmission': 'Transmission',
    'parc.vehicles.transManual': 'Manuelle',
    'parc.vehicles.transAutomatic': 'Automatique',
    'grid.filters.title': 'Filtres',
    'grid.search.placeholder': 'Rechercher...',
    'grid.filters.all': 'Tous',
    'grid.filters.clear': 'Effacer',
    'grid.counter.item': 'élément',
    'grid.counter.items': 'éléments',
    'grid.noData': 'Aucun élément ne correspond à votre recherche.',
    'grid.pagination.info': 'Affichage {0}-{1} sur {2} entrées',
    'grid.pagination.pageSize': 'Éléments par page:',
    'parc.fuel.newTransaction': 'Enregistrer un plein',
    'parc.fuel.editTransaction': 'Modifier la transaction',
    'parc.fuel.station': 'Station Service',
    'parc.fuel.product': 'Produit (ex: Gazole, Sans Plomb)',
    'parc.fuel.liters': 'Litres',
    'parc.fuel.pricePerLiter': 'Prix par Litre',
    'parc.fuel.cost': 'Coût Total (DZD)',
    'parc.fuel.odometer': 'Kilométrage (Odomètre)',
    'parc.fuel.fuelCard': 'Carte Carburant',
    'parc.fuel.none': 'Aucune',
    'parc.fuelcards.cardNumber': 'Numéro de Carte',
    'parc.fuelcards.vehicle': 'Véhicule Assigné',
    'parc.fuelcards.driver': 'Conducteur Assigné',
    'parc.fuelcards.globalCost': 'Coût Global Consommé',
    'parc.fuelcards.pricePerLiter': 'Prix de base/Litre',
    'parc.fuelcards.newCard': 'Nouvelle Carte',
    'parc.fuelcards.editCard': 'Modifier la carte',
    'parc.fuelcards.addCard': 'Ajouter une carte carburant',
    'parc.mileage.newLog': 'Nouveau Relevé',
    'parc.mileage.editLog': 'Modifier le relevé',
    'parc.mileage.addLog': 'Ajouter un relevé de kilométrage',
    'parc.mileage.startDate': 'Date Début',
    'parc.mileage.endDate': 'Date Fin',
    'parc.mileage.startKm': 'KM Début',
    'parc.mileage.endKm': 'KM Fin',
    'parc.mileage.distance': 'Distance',
    'parc.mileage.errorEndKmLessThanStartKm': 'Le kilométrage de fin ne peut pas être inférieur à celui de début.',
    'parc.lookups.title': 'Tables de Référence',
    'parc.lookups.proprietors': 'Propriétaires',
    'parc.lookups.fuelTypes': 'Types de Carburant',
    'parc.lookups.affectations': 'Affectations / Services',
    'parc.lookups.docTypes': 'Types de Documents',
    'parc.lookups.mntTypes': 'Types de Maintenance',
    'parc.gpsalerts.title': 'Alertes GPS',
    'parc.gpsalerts.newAlert': 'Nouvelle Alerte',
    'parc.gpsalerts.editAlert': 'Modifier l\'Alerte',
    'parc.gpsalerts.date': 'Date & Heure',
    'parc.gpsalerts.type': 'Type',
    'parc.gpsalerts.message': 'Message',
    'parc.gpsalerts.status': 'Statut',
    'parc.gpsalerts.type.speeding': 'Excès de vitesse',
    'parc.gpsalerts.type.geofence': 'Zone géographique',
    'parc.gpsalerts.type.maintenance': 'Maintenance',
    'parc.gpsalerts.type.system': 'Système',
    'parc.gpsalerts.type.other': 'Autre',
    'parc.gpsalerts.status.active': 'Actif',
    'parc.gpsalerts.status.resolved': 'Résolu',
    'parc.gpsalerts.status.ignored': 'Ignoré',
    'parc.analysis.generalTitle': 'Analyse Générale de la Flotte',
    'parc.analysis.kmsTraveled': 'Kilomètres Parcourus',
    'parc.analysis.fuelExpenses': 'Dépenses Carburant',
    'parc.analysis.availabilityIndex': 'Index de Disponibilité de la Flotte',
    'parc.analysis.optimizations': 'Optimisations & Estimations',
    'parc.analysis.avgConsumption': 'Consommation moyenne de la flotte :',
    'parc.analysis.estAnnualFuel': 'Estimation coût annuel carburant :',
    'parc.analysis.nextOilChange': 'Date prévisionnelle prochaine vidange :',
    'parc.analysis.warningTitle': 'Attention - Statut Flotte',
    'parc.analysis.warningMaintOrOut': '{0}% de la flotte est en maintenance ou hors service.',
    'parc.analysis.vehiclesActiveImmobilized': '{0} actifs / {1} immobilisés',
    'parc.analysis.kmsCumulatedDesc': "Cumulé de l'historique de la flotte",
    'parc.analysis.litersConsumedTotal': '{0} Litres consommés au total',
    'parc.analysis.availabilityIndexDesc': 'Cet index indique la proportion de véhicules actuellement opérationnels par rapport à la taille totale du parc. Un score supérieur à 90% indique une excellente planification de maintenance.',
    'parc.analysis.insufficientData': 'Données insuffisantes',
    'parc.analysis.nextOilChangeValue': 'Dans 45 jours (Estimé)',
    'parc.analysis.tcoTitle': 'Analyse TCO (Coût Total de Possession)',
    'parc.analysis.globalTco': 'TCO Global',
    'parc.analysis.totalFuel': 'Total Carburant',
    'parc.analysis.totalMaint': 'Total Maintenance',
    'parc.analysis.totalRepairs': 'Total Réparations',
    'parc.analysis.costPerKm': 'Coût / km',
    'parc.dashboard.totalFleetCost': 'COÛT TOTAL FLOTTE',
    'parc.dashboard.fuelCostSummary': '⛽ {0}K DA carb. · 🔧 {1}K DA maint.',
    'parc.dashboard.activeFleet': 'FLOTTE ACTIVE',
    'parc.dashboard.activeVehiclesCount': '{0} / {1} véh.',
    'parc.dashboard.avgCostPerVehicle': 'Coût moyen : {0} DA / véh.',
    'parc.dashboard.mileageSummary': '{0} DA/km · {1} DA/L (moy.)',
    'parc.dashboard.activeAlerts': 'ALERTES ACTIVES',
    'parc.dashboard.alertsSummary': '📋 {0} doc. · 🔧 {1} maint. · ⛽ {2} conso.',
    'parc.dashboard.distributionByProprietor': 'Répartition par Propriétaire',
    'parc.dashboard.oxxoProperty': 'PROPRIÉTÉ OXXO',
    'parc.dashboard.vehiclesCount': '{0} véh.',
    'parc.dashboard.totalCostKDA': 'Coût total: {0} K DA',
    'parc.dashboard.cevitalRental': 'LOCATION CEVITAL MTP',
    'parc.dashboard.otherOwner': 'AUTRE PROPRIÉTAIRE',
    'parc.dashboard.monthlyCostsCumulated': 'Coûts Mensuels cumulés',
    'parc.dashboard.monthlyCostsCumulatedDesc': 'Carburant + Maintenance & Réparations des 6 derniers mois (DZD)',
    'parc.dashboard.expenseBreakdown': 'Répartition des Dépenses',
    'parc.dashboard.expenseBreakdownDesc': 'Volume global cumulé par poste de dépense',
    'parc.dashboard.top6Vehicles': 'Top 6 — Véhicules les plus coûteux',
    'parc.dashboard.top6VehiclesDesc': 'Coût total cumulé (Carburant + Entretien) en DA',
    'parc.dashboard.consumptionEfficiency': 'Rendement Consommation L/100km',
    'parc.dashboard.consumptionEfficiencyDesc': 'Consommation moyenne par véhicule comparée aux seuils critiques (litres/100km)',
    'parc.dashboard.insufficientMileageData': 'Données de kilométrage insuffisantes',
    'parc.dashboard.checkFuelMileageHistory': 'Veuillez vérifier vos historiques de carburant et de kilométrage pour recalculer',
    'parc.dashboard.catVidange': 'Vidange',
    'parc.dashboard.catRevision': 'Révision',
    'parc.dashboard.catBattery': 'Batterie',
    'parc.dashboard.catBelt': 'Courroie',
    'parc.dashboard.catInjection': 'Injection',
    'parc.alerts.typeConfiguration': 'Configuration',
    'parc.alerts.noTelegramChatId': "Pas d'identifiant de Chat Telegram configuré.",
    'parc.alerts.techControlExpiringSoon': 'Visite technique du véhicule assigné expire dans 5 jours.',
    'parc.alerts.dispatchError': 'Erreur de dispatch',
    'parc.alerts.noChatIdAssociated': 'Aucun Telegram Chat ID associé à ce conducteur.',
    'parc.alerts.telegramSentSuccess': 'Alerte Telegram envoyée avec succès à {0} !',
    'parc.alerts.sendFailed': "Échec de l'envoi",
    'parc.alerts.telegramContactError': 'Impossible de contacter le serveur Telegram.',
    'parc.alerts.selectDriverAndMessage': 'Veuillez sélectionner un conducteur et saisir un message.',
    'parc.alerts.noChatId': 'Pas de Chat ID',
    'parc.alerts.driverNoTelegram': 'Ce conducteur ne possède pas de compte Telegram enregistré.',
    'parc.alerts.customMessageSentSuccess': 'Message personnalisé envoyé sur Telegram à {0}.',
    'parc.alerts.sendErrorOccurred': "Une erreur s'est produite lors de l'envoi.",
    'parc.alerts.consoleTitle': "Console d'Alerte et Messages Bot",
    'parc.alerts.telegramConfigInfo': "Pour recevoir des notifications automatiques, configurez le Chat ID Telegram dans le profil de chaque conducteur. Les conducteurs doivent d'abord lancer le bot de la flotte.",
    'parc.alerts.activeFleetAlerts': 'Alertes Flotte Actives',
    'parc.alerts.target': 'Cible',
    'parc.alerts.notify': 'Notifier',
    'parc.alerts.noAlertsPending': 'Aucune alerte en attente',
    'parc.alerts.sendManualMessage': 'Envoyer un Message Manuel',
    'parc.alerts.sendManualMessageDesc': 'Envoyez une directive instantanée à un conducteur sur son canal Telegram personnel.',
    'parc.alerts.recipientDriver': 'Conducteur destinataire',
    'parc.alerts.noTelegram': 'Aucun Telegram',
    'parc.alerts.messagePlaceholder': 'Entrez vos consignes ici...',
    'parc.alerts.sendMessage': 'Envoyer le message',

    // Clôture Agence & Planning
    'academy.scheduler.closed_chip': "Clôturé",
    'academy.scheduler.closed_warning': "Ce créneau fait partie d'une période clôturée. Modification et annulation impossibles.",
    'academy.scheduler.error_closed_planning': "Impossible de modifier : le planning est clôturé jusqu'au {0}.",
    'academy.financial.error_closed_invoices': "Impossible de modifier : l'agence est clôturée jusqu'au {0}.",
    'academy.financial.invoice_locked_tooltip': "Facture verrouillée (période d'agence clôturée)",

    // Student Card & QR Attendance
    'academy.students.print_card': "Badge d'apprenant",
    'academy.students.card_title': "CARTE D'ÉTUDIANT",
    'academy.students.card_subtitle': "Badge d'apprenant",
    'academy.students.print_badge': "Imprimer le Badge",
    'academy.students.student_code': "Code Étudiant",
    'academy.students.emergency_short': "Urgence",
    'academy.attendance.scan_qr': "Scanner Présence QR",
    'academy.attendance.scanner_title': "Scanner de Présence QR",
    'academy.attendance.scanner_desc': "Scannez le badge étudiant avec la caméra ou un lecteur code-barres USB pour pointer automatiquement la présence.",
    'academy.attendance.manual_code_placeholder': "Entrez ou scannez le code (ex: BLO-STD-...)...",
    'academy.attendance.mark_present_success': "{0} marqué(e) Présent(e) avec succès !",
    'academy.attendance.already_present': "{0} est déjà marqué(e) Présent(e).",
    'academy.attendance.student_not_enrolled': "L'étudiant {0} n'est pas inscrit dans cette session.",
    'academy.attendance.mark_all_present': "Tout marquer Présent",
    'academy.attendance.mark_all_present_confirm': "Voulez-vous marquer tous les étudiants inscrits de cette session comme Présents ?",
    'academy.attendance.stats_present': "Présents",
    'academy.attendance.stats_late': "En retard",
    'academy.attendance.stats_absent': "Absents",
    'academy.attendance.stats_excused': "Excusés",
    'academy.attendance.stats_rate': "Taux de présence",
    'academy.attendance.search_student': "Rechercher un étudiant par nom, code...",
    'academy.attendance.select_session_hint': "Veuillez sélectionner une session pour gérer les présences.",
    'academy.attendance.camera_start_error': "Impossible d'accéder à la caméra. Vérifiez les autorisations de votre navigateur.",
    'academy.attendance.camera_switch': "Changer de caméra",
    'academy.menu.email_templates': "Modèles d'Emails",
    'academy.email_templates.title': "Modèles d'Emails & Notifications",
    'academy.email_templates.new_title': "Nouveau Modèle d'Email",
    'academy.email_templates.edit_title': "Modifier le modèle : {0}",
    'academy.email_templates.btn_new': 'Nouveau Modèle',
    'academy.email_templates.btn_refresh': 'Actualiser',
    'academy.email_templates.btn_back': 'Retour à la liste',
    'academy.email_templates.btn_reset': 'Réinitialiser',
    'academy.email_templates.btn_save': 'Enregistrer les modifications',
    'academy.email_templates.btn_create': 'Créer le modèle',
    'academy.email_templates.filter_type': "Filtrer par type d'email",
    'academy.email_templates.filter_type_all': "Tous les types d'email",
    'academy.email_templates.filter_lang': 'Filtrer par langue',
    'academy.email_templates.filter_lang_all': 'Toutes les langues',
    'academy.email_templates.filter_status': 'Statut',
    'academy.email_templates.filter_status_all': 'Tous les statuts',
    'academy.email_templates.filter_active': 'Actif uniquement',
    'academy.email_templates.filter_inactive': 'Désactivé uniquement',
    'academy.email_templates.results': '{0} modèle(s) trouvé(s)',
    'academy.email_templates.col_status': 'Statut',
    'academy.email_templates.col_lang': 'Langue',
    'academy.email_templates.col_type': "Type d'email",
    'academy.email_templates.col_name': 'Nom du Modèle',
    'academy.email_templates.col_subject': 'Objet / Sujet',
    'academy.email_templates.col_updated': 'Dernière MàJ',
    'academy.email_templates.status_active': 'Actif',
    'academy.email_templates.status_inactive': 'Désactivé',
    'academy.email_templates.card_code': 'Code : {0}',
    'academy.email_templates.card_subject': 'Objet :',
    'academy.email_templates.card_vars': 'Variables ({0}) :',
    'academy.email_templates.card_updated': 'MàJ: {0}',
    'academy.email_templates.card_edit': 'Modifier le modèle',
    'academy.email_templates.card_delete': 'Supprimer',
    'academy.email_templates.search_placeholder': 'Rechercher par nom, objet ou code...',
    'academy.email_templates.section1': '1. Configuration du Modèle',
    'academy.email_templates.field_name': "Nom du modèle d'email",
    'academy.email_templates.field_name_placeholder': 'Ex: Rappel de paiement solde restant dû (Français)',
    'academy.email_templates.field_code': 'Code Unique',
    'academy.email_templates.field_code_helper': 'Ex: DEBT_REMINDER_ADULT_FR',
    'academy.email_templates.field_type': "Type d'email (Événement déclencheur)",
    'academy.email_templates.field_lang': 'Langue du modèle',
    'academy.email_templates.lang_fr': '🇫🇷 Français (FR)',
    'academy.email_templates.lang_ar': '🇸🇦 Arabe (العربية - AR)',
    'academy.email_templates.lang_en': '🇬🇧 Anglais (English - EN)',
    'academy.email_templates.active_label': 'Modèle Actif (Envoi autorisé)',
    'academy.email_templates.inactive_label': 'Modèle Désactivé',
    'academy.email_templates.active_hint': 'Les emails correspondant à ce type seront automatiquement envoyés.',
    'academy.email_templates.inactive_hint': "⚠️ Quand désactivé, le système n'enverra aucun email pour ce type.",
    'academy.email_templates.field_desc': 'Description / Remarques internes',
    'academy.email_templates.field_desc_placeholder': 'Usage interne ou explications sur ce modèle...',
    'academy.email_templates.section2': '2. Objet & Sujet du Message',
    'academy.email_templates.field_subject': "Objet de l'email (Subject)",
    'academy.email_templates.field_subject_placeholder': 'Ex: [{agencyName}] Rappel de paiement - Solde restant : {totalBalanceDue} {currency}',
    'academy.email_templates.field_subject_helper': 'Vous pouvez inclure des variables telles que {agencyName}, {student.fullName}, etc.',
    'academy.email_templates.section3': '3. Corps du Message HTML & Variables',
    'academy.email_templates.tags_hint': "Cliquer sur une variable pour l'insérer dans le corps :",
    'academy.email_templates.field_body': 'Code HTML du message',
    'academy.email_templates.field_body_placeholder': '<!DOCTYPE html><html><body>...</body></html>',
    'academy.email_templates.preview_title': 'Aperçu du Rendu Réel',
    'academy.email_templates.preview_hint': 'Les variables {\'...\'} sont automatiquement remplacées par des données exemples ci-dessous.',
    'academy.email_templates.preview_subject': "Aperçu de l'Objet :",
    'academy.email_templates.preview_empty': '(Aucun objet renseigné)',
    'academy.email_templates.confirm_delete_title': 'Supprimer ce modèle ?',
    'academy.email_templates.confirm_delete_msg': 'Voulez-vous vraiment supprimer le modèle \'{0}\' ?',
    'academy.email_templates.msg_created': 'Modèle créé avec succès.',
    'academy.email_templates.msg_updated': 'Modèle mis à jour avec succès.',
    'academy.email_templates.msg_deleted': 'Modèle supprimé avec succès.',
    'academy.email_templates.err_name': 'Veuillez saisir le nom du modèle.',
    'academy.email_templates.err_type': "Veuillez sélectionner un type d'email.",
    'academy.email_templates.err_subject': "Veuillez renseigner l'objet / sujet de l'email.",
    'academy.email_templates.err_body': 'Le corps HTML du message est obligatoire.',
    'academy.email_templates.lang_ar_flag': '🇸🇦 AR',
    'academy.email_templates.lang_en_flag': '🇬🇧 EN',
    'academy.email_templates.lang_fr_flag': '🇫🇷 FR',
    'academy.email_templates.lang_ar_rtl': '🇸🇦 Arabe (RTL)',
    'academy.email_templates.lang_en_chip': '🇬🇧 Anglais',
    'academy.email_templates.lang_fr_chip': '🇫🇷 Français',
    'academy.email_templates.grid_view': 'Vue Grille',
    'academy.email_templates.table_view': 'Vue Tableau',

    // Academy Subscription & SATIM
    'academy.menu.subscription': 'Abonnement & Facturation',
    'subscription.pageTitle': 'Gestion de l’Abonnement & Facturation',
    'subscription.tabUsage': 'Consommation & Quotas',
    'subscription.tabPlans': 'Nos Forfaits & Tarifs',
    'subscription.loading': 'Chargement des détails de l’abonnement...',
    'subscription.successTitle': 'Abonnement activé !',
    'subscription.successDesc': 'Votre paiement SATIM a été validé avec succès (Réf: {0}). Vos quotas ont été mis à jour.',
    'subscription.changePlan': 'Changer ou Mettre à niveau',
    'subscription.securedBy': 'Paiements sécurisés via',
    'subscription.plan': 'Forfait sélectionné',
    'subscription.yearly': 'Facturation Annuelle',
    'subscription.monthly': 'Facturation Mensuelle',
    'subscription.totalDue': 'Montant total à débiter',
    'subscription.currentPlan': 'Forfait Actuel',
    'subscription.chooseFree': 'Choisir Gratuit',
    'subscription.payWithSatim': 'Payer par SATIM',
    'subscription.upgradeNow': 'Mettre à niveau mon abonnement',
    'subscription.usage': 'Utilisation du quota',
    'subscription.status.trial': 'Essai Gratuit ({0} jours restants)',
    'subscription.status.active': 'Abonnement Actif',
    'subscription.status.expired': 'Abonnement Expiré',
    'subscription.quotasTitle': 'Quotas & Utilisation en Direct',
    'subscription.quota.students': 'Apprenants',
    'subscription.quota.studentsSub': 'Inscrits dans l’académie',
    'subscription.quota.teachers': 'Enseignants',
    'subscription.quota.teachersSub': 'Formateurs actifs',
    'subscription.quota.users': 'Utilisateurs & Staff',
    'subscription.quota.usersSub': 'Comptes d’accès',
    'subscription.quota.agencies': 'Agences & Succursales',
    'subscription.quota.agenciesSub': 'Centres de formation',
    'subscription.quota.storage': 'Stockage Cloud',
    'subscription.quota.storageSub': 'Photos, PDFs & Sauvegardes',
    'subscription.quota.sms': 'Crédits SMS',
    'subscription.quota.smsSub': 'Notifications & Rappels',
    'subscription.invoicesTitle': 'Historique des Règlements & Factures',
    'subscription.pricingTitle': 'Choisissez le forfait adapté à votre académie',
    'subscription.pricingSubtitle': 'Paiement direct et sécurisé par carte CIB ou Edahabia (SATIM). Activation instantanée.',
    'subscription.satim.testSandbox': 'Mode Test Sandbox (Remplissage rapide) :',
    'subscription.satim.cardNumber': 'Numéro de carte (16 chiffres)',
    'subscription.satim.cardExpiry': 'Date d’expiration (MM/AA)',
    'subscription.satim.cardCvv': 'CVV2 (3 chiffres)',
    'subscription.satim.cardHolder': 'Nom complet du porteur',
    'subscription.satim.enterOtp': 'Code de vérification (OTP)',
    'subscription.satim.processing': 'Connexion SATIM...',
    'subscription.satim.payNow': 'Procéder au Paiement',
    'subscription.satim.validating': 'Vérification...',
    'subscription.satim.confirmPayment': 'Confirmer et Débiter',
    'subscription.satim.continueToPortal': 'Accéder à mon Académie',
    'subscription.satim.invalidCardNumber': 'Numéro de carte invalide (16 chiffres requis).',
    'subscription.satim.invalidExpiry': 'Date d’expiration invalide (MM/AA).',
    'subscription.satim.invalidCvv': 'Cryptogramme visuel (CVV2) invalide.',
    'subscription.satim.invalidOtp': 'Veuillez saisir le code SMS reçu.',
    'subscription.limit.studentsTitle': 'Limite d’apprenants atteinte',
    'subscription.limit.teachersTitle': 'Limite d’enseignants atteinte',
    'subscription.limit.storageTitle': 'Espace de stockage plein',
    'subscription.limit.featureTitle': 'Fonctionnalité Verrouillée',
    'subscription.limit.studentsDesc': 'Votre forfait actuel ({0}) est limité à {1} apprenants. Pour inscrire de nouveaux élèves, mettez à niveau votre forfait.',
    'subscription.limit.teachersDesc': 'Votre forfait actuel ({0}) est limité à {1} enseignants. Passez à la formule supérieure pour en ajouter d’autres.',
    'subscription.limit.featureDesc': 'La fonctionnalité "{0}" n’est pas disponible sur le forfait {1}. Débloquez-la en mettant à niveau votre abonnement.',
    'subscription.limit.instantActivation': 'Activation instantanée par paiement SATIM (CIB / Edahabia).',

    // CRM
    'crm.menu.dashboard': 'Tableau de bord CRM',
    'crm.menu.prescription': 'Prescription',
    'crm.menu.visits': 'Visites Terrain',
    'crm.menu.cdc': 'Cahiers des Charges (CDC)',
    'crm.menu.pipeline': 'Projets & Pipeline',
    'crm.menu.opportunities': 'Projets & Opportunités',
    'crm.menu.leads': 'Pistes & Leads',
    'crm.menu.chantiers_sav': 'Chantiers & SAV',
    'crm.menu.chantiers': 'Chantiers Actifs',
    'crm.menu.reclamations': 'Réclamations & SAV',
    'crm.menu.ecosystem': 'Écosystème Prescription',
    'crm.menu.organisations': 'Organisations (BET / Promo)',
    'crm.menu.contacts': 'Contacts & Décideurs',
    'crm.menu.activities': 'Activités & RDV',
    'crm.cdc.deleteSuccess': 'Cahier des charges supprimé avec succès.',
    'crm.cdc.secIdent': 'Identification du Document',
    'crm.cdc.titleField': 'Titre / Objet du Cahier des Charges',
    'crm.cdc.unitsCount': 'Nombre d\'Unités / Ouvrants',
    'crm.cdc.estimatedBudget': 'Budget Estimatif (DZD)',
    'crm.cdc.technicalConstraints': 'Exigences Techniques (AEV, Acoustique, Thermique, Vitrage)',
    'crm.chantiers.deleteSuccess': 'Chantier supprimé avec succès.',
    'crm.chantiers.clientRequired': 'Le nom du client / maître d\'ouvrage est requis.',
    'crm.leads.deleteError': 'Erreur lors de la suppression.',
    'crm.leads.status.new': 'Nouveau',
    'crm.leads.status.contacted': 'Contacté',
    'crm.leads.status.qualified': 'Qualifié',
    'crm.leads.status.proposal': 'Proposition',
    'crm.leads.status.won': 'Gagné',
    'crm.leads.status.lost': 'Perdu',
    'crm.opportunities.stageOnHold': 'À l\'arrêt',
    'crm.opportunities.transferBtn': 'Transférer au KAM',
    'crm.opportunities.refInterne': 'Référence Interne Dossier',
    'crm.opportunities.organisation': 'Organisation (BET / Promo)',
    'crm.opportunities.wilaya': 'Wilaya / Région',
    'crm.opportunities.chantierAddress': 'Adresse Précise du Chantier',
    'crm.opportunities.secSpecs': 'Détails Techniques & Chiffrage Devis',
    'crm.opportunities.gamme': 'Gamme de Produits',
    'crm.opportunities.units': 'Unités de Menuiserie',
    'crm.opportunities.unitPrice': 'Prix HT Unitaire (DZD)',
    'crm.opportunities.discount': 'Remise Accordée (%)',
    'crm.opportunities.extraDiscount': 'Remise Supplémentaire (%)',
    'crm.opportunities.secTransfer': 'Suivi Commercial & Transmission KAM',
    'crm.opportunities.actionRequired': 'Prochaine Action Demandée',
    'crm.opportunities.progress': 'Avancement',
    'crm.opportunities.search': 'Rechercher un projet, gamme, prescripteur...',
    'crm.organisations.deleteSuccess': 'Organisation supprimée avec succès.',
    'crm.organisations.website': 'Site Web / Portfolio',
    'crm.organisations.search': 'Rechercher une organisation (BET, Architecte, Promoteur)...',
    'crm.reclamations.deleteSuccess': 'Réclamation supprimée avec succès.',
    'crm.visits.deleteSuccess': 'Visite supprimée avec succès.',
    'crm.visits.objectiveRequired': 'L\'objectif de la visite est obligatoire.',
    'crm.visits.secPlanning': 'Cadrage & Planification de la Visite',
    'crm.visits.datetime': 'Date & Heure Planifiée',
    'crm.visits.contact': 'Interlocuteur Clé',
    'crm.visits.secTerrain': 'Exécution Terrain & Pointage Géolocalisé',
    'crm.visits.checkinBtn': 'Check-in Sur Site (GPS)',
    'crm.visits.checkoutBtn': 'Clôturer Visite & Calculer Durée',
    'crm.visits.checkinTime': 'Heure d\'Arrivée (Check-in)',
    'crm.visits.checkoutTime': 'Heure de Départ (Check-out)',
    'crm.visits.duration': 'Durée de l\'Entretien (Minutes)',
    'crm.visits.checkinLat': 'Latitude GPS',
    'crm.visits.checkinLon': 'Longitude GPS',
    'crm.visits.topics': 'Sujets Abordés',
    'crm.visits.commitments': 'Engagements Pris (de part et d\'autre)',
    'crm.visits.nextSteps': 'Prochaines Étapes / Relance',
    'crm.dashboard.prescripteurSubtitle': 'Pilotage de la Prescription Amont, Détection BET & Suivi des CDC',
    'crm.dashboard.kamSubtitle': 'Pilotage Opérationnel Chantiers, Commandes & SAV',
    'crm.dashboard.globalSubtitle': 'Vue Consolidée de la Performance Commerciale',
    'crm.dashboard.viewPrescripteur': 'Vue Prescripteur',
    'crm.dashboard.viewKam': 'Vue KAM (Chantiers)',
    'crm.dashboard.viewGlobal': 'Vue Globale',
    'crm.dashboard.visitesTerrain': 'VISITES TERRAIN',
    'crm.dashboard.visitesSub': 'Réalisées vs Planifiées ce mois',
    'crm.dashboard.tauxVisiteCdc': 'Taux de conversion Visite → CDC',
    'crm.dashboard.cahiersCharges': 'CAHIERS DES CHARGES',
    'crm.dashboard.cdcSub': 'CDC signés / validés',
    'crm.dashboard.enCours': 'en cours',
    'crm.dashboard.tauxPreco': 'Taux d\'intégration préconisations',
    'crm.dashboard.projetsGagnes': 'PROJETS GAGNÉS',
    'crm.dashboard.cumulAnnee': 'Cumul année',
    'crm.dashboard.ceMois': 'ce mois-ci',
    'crm.dashboard.tauxCdcGagne': 'Taux de succès CDC → Gagné',
    'crm.dashboard.ecosystemePrescription': 'ÉCOSYSTÈME PRESCRIPTION',
    'crm.dashboard.ecosystemeSub': 'Organisations actives (BET, Architectes, Promoteurs)',
    'crm.dashboard.satisfactionPartenaires': 'Satisfaction partenaires',
    'crm.dashboard.alertesPrioritaires': 'Alertes & Actions Prioritaires Prescripteur',
    'crm.dashboard.visitesRetard': 'Visites terrain en retard',
    'crm.dashboard.enRetard': 'en retard',
    'crm.dashboard.contactsRelancer': 'Contacts clés BET à relancer',
    'crm.dashboard.aRelancer': 'à relancer',
    'crm.dashboard.transfertsAttente': 'Projets gagnés prêts pour transfert au KAM',
    'crm.dashboard.enAttente': 'en attente',
    'crm.dashboard.raccourcis': 'Raccourcis & Actions Rapides',
    'crm.dashboard.planifierVisite': 'Planifier une Visite',
    'crm.dashboard.nouveauCdc': 'Nouveau CDC',
    'crm.dashboard.projetsPipeline': 'Projets en Pipeline',
    'crm.dashboard.chantiersActifs': 'CHANTIERS ACTIFS',
    'crm.dashboard.enDemarrage': 'en démarrage',
    'crm.dashboard.termines': 'terminés',
    'crm.dashboard.projetsTransferes': 'projet(s) transféré(s) de la Prescription',
    'crm.dashboard.commandesTraitees': 'COMMANDES TRAITÉES',
    'crm.dashboard.commandesRetardCount': 'commande(s) en retard',
    'crm.dashboard.livraisonsConformes': 'Livraisons conformes',
    'crm.dashboard.chiffreAffaires': 'CHIFFRE D\'AFFAIRES',
    'crm.dashboard.margeMoy': 'Marge moy',
    'crm.dashboard.encaisse': 'Encaissé',
    'crm.dashboard.satisfactionClient': 'SATISFACTION CLIENT',
    'crm.dashboard.satisfactionChantiersSub': 'Satisfaction globale sur les chantiers livrés',
    'crm.dashboard.resolues': 'résolue(s)',
    'crm.dashboard.activiteTerrain': 'Activité Opérationnelle Terrain',
    'crm.dashboard.visitesChantier': 'Visites Chantier',
    'crm.dashboard.heuresTerrain': 'Heures Terrain',
    'crm.dashboard.problemesResolus': 'Problèmes Résolus',
    'crm.dashboard.actionsKam': 'Actions Rapides KAM',
    'crm.dashboard.gererChantiers': 'Gérer les Chantiers',
    'crm.dashboard.reclamationsSav': 'Réclamations & SAV',
    'crm.dashboard.totalProspects': 'Total Prospects',
    'crm.dashboard.contactsClients': 'Contacts Clients',
    'crm.dashboard.affairesGagnees': 'Affaires Gagnées',
    'crm.dashboard.projetsRecents': 'Projets & Opportunités Récents',
    'crm.dashboard.pistesRecentes': 'Pistes & Détections Récentes',
    'crm.organisations.title': 'Écosystème Prescription & Organisations',
    'crm.organisations.newTitle': 'Nouvelle Organisation',
    'crm.organisations.editTitle': 'Modifier l\'Organisation',
    'crm.organisations.secIdent': 'Identification & Typologie',
    'crm.organisations.secCoords': 'Coordonnées & Siège',
    'crm.organisations.secHistory': 'Historique & Partenariat',
    'crm.organisations.nameRequired': 'La raison sociale est requise.',
    'crm.organisations.confirmDelete': 'Êtes-vous sûr de vouloir supprimer cette organisation ?',
    'crm.organisations.projects': 'projet(s)',
    'crm.organisations.contacts': 'contact(s)',
    'crm.organisations.searchPlaceholder': 'Rechercher une organisation (BET, Architecte, Promoteur...)...',
    'crm.organisations.historyNotes': 'Notes de partenariat, projets antérieurs prescrits, préconisations',
    'crm.organisations.historyPlaceholder': 'Détaillez ici l\'historique relationnel, la fidélité, les préférences architecturales ou les gammes privilégiées...',
    'crm.organisations.zoneGeoPlaceholder': 'Ex: Alger Centre, Oran, Constantine, National',
    'crm.organisations.typeBet': 'Bureau d\'Études Techniques (BET)',
    'crm.organisations.typeArchitecte': 'Cabinet d\'Architecture',
    'crm.organisations.typePromoteur': 'Promoteur Immobilier',
    'crm.organisations.typeMo': 'Maître d\'Ouvrage / Donneur d\'Ordre',
    'crm.organisations.typeEnt': 'Entreprise Générale de Bâtiment',
    'crm.organisations.typeInstallateur': 'Installateur / Menuisier Partenaire',
    'crm.organisations.typeAutre': 'Autre Partenaire',
    'crm.organisations.specResidentiel': 'Résidentiel (Logement, Tours)',
    'crm.organisations.specTertiaire': 'Tertiaire & Bureaux',
    'crm.organisations.specIndustriel': 'Industriel & Logistique',
    'crm.organisations.specMixte': 'Mixte / Polyvalent',
    'crm.organisations.sizePetit': 'Petite Structure (< 10 collaborateurs)',
    'crm.organisations.sizeMoyen': 'Moyenne Structure (10 - 50 collaborateurs)',
    'crm.organisations.sizeGrand': 'Grande Structure (> 50 collaborateurs)',
    'crm.organisations.name': 'Raison Sociale / Nom',
    'crm.organisations.type': 'Typologie d\'Organisation',
    'crm.organisations.specialty': 'Spécialité Principale',
    'crm.organisations.size': 'Envergure / Taille',
    'crm.organisations.zoneGeo': 'Zone Géographique d’Intervention',
    'crm.visits.title': 'Visites Terrain & Prescription',
    'crm.visits.newTitle': 'Planifier une Visite',
    'crm.visits.editTitle': 'Modifier la Visite',
    'crm.visits.secIdent': 'Cadrage de la Visite',
    'crm.visits.secReport': 'Compte-Rendu & Suivi d’Action',
    'crm.visits.subjectRequired': 'L\'objet de la visite est requis.',
    'crm.visits.confirmDelete': 'Êtes-vous sûr de vouloir supprimer cette visite ?',
    'crm.visits.search': 'Rechercher une visite, BET, projet, interlocuteur...',
    'crm.visits.rolePrescripteur': 'Prescripteur (Amont / BET / Architecte)',
    'crm.visits.roleKam': 'KAM (Chantier / Promoteur / Réception)',
    'crm.visits.typeProspection': 'Prospection & Détection initiale',
    'crm.visits.typePreconisation': 'Présentation Gammes & Préconisation Technique',
    'crm.visits.typePrescription': 'Élaboration / Validation CDC Prescription',
    'crm.visits.typeSuiviChantier': 'Suivi Chantier & Démarrage Pose',
    'crm.visits.typeReunionTechnique': 'Réunion Technique de Résolution',
    'crm.visits.typeReception': 'Visite de Fin de Pose / Réception',
    'crm.visits.statusPlanned': 'Planifiée',
    'crm.visits.statusInProgress': 'En Cours (Sur place)',
    'crm.visits.statusCompleted': 'Réalisée avec Compte-Rendu',
    'crm.visits.statusRescheduled': 'Reportée',
    'crm.visits.statusCancelled': 'Annulée',
    'crm.visits.noOrg': 'Visite libre (Sans organisation liée)',
    'crm.visits.noProject': 'Prospection amont (Sans projet identifié)',
    'crm.visits.objectivePlaceholder': 'Précisez l\'objectif concret (ex: présenter la gamme Futura, vérifier le calepinage...)',
    'crm.visits.checklistPlaceholder': 'Éléments contrôlés (plans remis, échantillons montrés, fiches techniques remises...)',
    'crm.visits.personsMetPlaceholder': 'Ex: M. Benali (Chef de projet), Mme Mansouri (Architecte d\'intérieur)...',
    'crm.visits.evalScore': 'Évaluation Intérêt / Réceptivité :',
    'crm.visits.noGps': 'Non géolocalisé',
    'crm.visits.role': 'Rôle / Profil Visiteur',
    'crm.visits.type': 'Typologie de Visite',
    'crm.visits.status': 'Statut de la Visite',
    'crm.visits.subject': 'Objet de la Visite',
    'crm.visits.organisation': 'Organisation / BET / Promoteur Visité',
    'crm.visits.project': 'Projet Associé',
    'crm.visits.interlocuteur': 'Interlocuteur Rencontré',
    'crm.visits.date': 'Date & Heure de Passage',
    'crm.visits.objective': 'Objectif de la Rencontre',
    'crm.visits.gps': 'Coordonnées GPS / Pointage Terrain',
    'crm.visits.score': 'Score de Réceptivité (1-5 ⭐)',
    'crm.visits.personsMet': 'Personnes Présentes & Fonctions',
    'crm.visits.checklist': 'Checklist Terrain / Préconisations Validées',
    'crm.visits.report': 'Compte-Rendu Détaillé de la Visite',
    'crm.visits.nextAction': 'Prochaine Action / Relance Décidée',
    'crm.cdc.title': 'Cahiers des Charges (CDC) Prescription',
    'crm.cdc.newTitle': 'Nouveau Cahier des Charges',
    'crm.cdc.editTitle': 'Modifier le Cahier des Charges',
    'crm.cdc.secProject': 'Rattachement Projet & Acteurs',
    'crm.cdc.secSpecs': 'Spécifications Techniques & Gammes Préconisées',
    'crm.cdc.titleRequired': 'Le titre du CDC est requis.',
    'crm.cdc.confirmDelete': 'Êtes-vous sûr de vouloir supprimer ce cahier des charges ?',
    'crm.cdc.search': 'Rechercher un CDC, projet, BET, gamme...',
    'crm.cdc.selectProject': 'Sélectionner un projet',
    'crm.cdc.selectOrg': 'Sélectionner une organisation',
    'crm.cdc.selectContact': 'Sélectionner un contact',
    'crm.cdc.statusDraft': 'Brouillon (En rédaction)',
    'crm.cdc.statusSubmitted': 'Soumis au BET / Architecte',
    'crm.cdc.statusValidated': 'Validé par la Maîtrise d’Œuvre',
    'crm.cdc.statusSigned': 'Signé / Intégré au Marché',
    'crm.cdc.statusRejected': 'Rejeté / Variante refusée',
    'crm.cdc.constraintsPlaceholder': 'Ex: Isolation acoustique renforcée 38dB, vitrage contrôle solaire 4S, teintes texturées, hauteur sous plafond...',
    'crm.cdc.prescripteur': 'Prescripteur',
    'crm.cdc.signedOn': 'Signé le',
    'crm.cdc.notSigned': 'Non signé',
    'crm.cdc.units': 'unités',
    'crm.cdc.cdcTitle': 'Titre du Cahier des Charges',
    'crm.cdc.project': 'Projet Associé',
    'crm.cdc.organisation': 'Prescripteur / BET Rédacteur',
    'crm.cdc.contact': 'Interlocuteur BET',
    'crm.cdc.gamme': 'Gamme de Produits Préconisée',
    'crm.cdc.volume': 'Volume Estimé (Unités menuiseries / surfaces)',
    'crm.cdc.status': 'Statut du Cahier des Charges',
    'crm.cdc.constraints': 'Contraintes Techniques & Spécifications Exigées',
    'crm.cdc.dateCreation': 'Date de Création',
    'crm.cdc.dateSignature': 'Date de Validation / Signature',
    'crm.gamme.productGamme': 'Gamme de Produits',
    'crm.gamme.urbania': 'URBANIA (Haute Performance Résidentielle)',
    'crm.gamme.futura': 'FUTURA (Tertiaire & Baies Coulissantes)',
    'crm.gamme.dooria': 'DOORIA (Portes & Entrées Prestigieuses)',
    'crm.gamme.mixte': 'MIXTE (Solutions Hybrides Sur Mesure)',
    'crm.gamme.urbaniaShort': 'URBANIA',
    'crm.gamme.futuraShort': 'FUTURA',
    'crm.gamme.dooriaShort': 'DOORIA',
    'crm.gamme.mixteShort': 'MIXTE',
    'crm.opportunities.secIdent': 'Identification de l\'Affaire',
    'crm.opportunities.secGamme': 'Produits, Gammes & Chiffrage',
    'crm.opportunities.secFollowup': 'Acteurs, Passation & Suivi Commercial',
    'crm.opportunities.titleField': 'Titre de l\'Affaire / Projet',
    'crm.opportunities.typeProjet': 'Type de Projet',
    'crm.opportunities.segment': 'Segment Marché',
    'crm.opportunities.priority': 'Priorité Commerciale',
    'crm.opportunities.risk': 'Niveau de Risque',
    'crm.opportunities.transferStatus': 'Statut de Passation (Prescription → KAM)',
    'crm.opportunities.commercialProgress': 'Avancement Commercial :',
    'crm.opportunities.actionPlaceholder': 'Ex: Envoi offre financière, calage technique des menuiseries avec le BET...',
    'crm.opportunities.notesPlaceholder': 'Historique des échanges, exigences spécifiques du promoteur, planning d\'approvisionnement...',
    'crm.opportunities.typePromotion': 'Promotion Immobilière Privée',
    'crm.opportunities.typeAadl': 'Logements AADL',
    'crm.opportunities.typeLpp': 'Logements LPP',
    'crm.opportunities.typePublic': 'Équipement Public / Éducation / Santé',
    'crm.opportunities.typePrive': 'Tertiaire / Bureaux / Privé',
    'crm.opportunities.typeIndustriel': 'Industriel & Hangars',
    'crm.opportunities.segmentPrive': 'Secteur Privé',
    'crm.opportunities.segmentPublic': 'Secteur Public / Marché d’État',
    'crm.opportunities.transferNone': 'En cours de Prescription (Non transféré)',
    'crm.opportunities.transferPending': 'Prêt pour Transfert au KAM',
    'crm.opportunities.transferTransferred': 'Transféré & Pris en Charge par le KAM',
    'crm.opportunities.transferDirectKam': 'Prospection Directe KAM (Sans prescription)',
    'crm.opportunities.volumeEstime': 'Volume Estimé (Unités menuiseries)',
    'crm.opportunities.valeurEstimee': 'Valeur Estimée de l’Affaire (DZD)',
    'crm.opportunities.prescripteur': 'Prescripteur Pilote',
    'crm.opportunities.kamAssigne': 'KAM Assigné (Suivi Chantier)',
    'crm.opportunities.dateCloture': 'Date de Clôture Prévisionnelle',
    'crm.opportunities.prochaineAction': 'Prochaine Action Commerciale',
    'crm.stage.prospecting': 'Prospection / Détection',
    'crm.stage.qualification': 'Qualification Technique BET',
    'crm.stage.proposal': 'Proposition / CDC Rédigé',
    'crm.stage.negotiation': 'Négociation & Alignement',
    'crm.stage.won': 'Affaire Gagnée (Signée)',
    'crm.stage.lost': 'Perdue / Concurrence',
    'crm.stage.onHold': 'En Attente / Projet Gelé',
    'crm.priority.low': 'Basse',
    'crm.priority.medium': 'Moyenne',
    'crm.priority.high': 'Haute / Stratégique',
    'crm.risk.low': 'Risque Faible',
    'crm.risk.medium': 'Risque Modéré',
    'crm.risk.high': 'Risque Élevé',
    'crm.chantiers.title': 'Suivi Opérationnel des Chantiers KAM',
    'crm.chantiers.newTitle': 'Nouveau Chantier KAM',
    'crm.chantiers.editTitle': 'Suivi du Chantier Actif',
    'crm.chantiers.secIdent': 'Identification du Chantier & Affaire',
    'crm.chantiers.secPlanning': 'Planning & Avancement',
    'crm.chantiers.secOrders': 'Commandes & Livraisons',
    'crm.chantiers.secFinancial': 'Indicateurs Financiers & Encaissements',
    'crm.chantiers.titleRequired': 'Le titre du chantier est requis.',
    'crm.chantiers.confirmDelete': 'Êtes-vous sûr de vouloir supprimer ce chantier ?',
    'crm.chantiers.search': 'Rechercher un chantier actif, client, KAM...',
    'crm.chantiers.titleField': 'Intitulé du Chantier',
    'crm.chantiers.clientName': 'Client / Promoteur',
    'crm.chantiers.project': 'Projet Initial (Passation)',
    'crm.chantiers.directKamProject': 'Prospection directe KAM (Sans prescription)',
    'crm.chantiers.kamName': 'KAM Responsable',
    'crm.chantiers.wilaya': 'Wilaya',
    'crm.chantiers.startDate': 'Date Démarrage',
    'crm.chantiers.estimatedEndDate': 'Échéance Prévisionnelle',
    'crm.chantiers.operationalProgress': 'Avancement Opérationnel',
    'crm.chantiers.progressTitle': 'Avancement Chantier',
    'crm.chantiers.satisfaction': 'Score Satisfaction Client (0-100)',
    'crm.chantiers.satisfactionLabel': 'Satisfaction',
    'crm.chantiers.ordersCount': 'Commandes Traitées',
    'crm.chantiers.ordersPending': 'Commandes En Cours',
    'crm.chantiers.ordersLate': 'Commandes En Retard',
    'crm.chantiers.deliveryStatus': 'État des Livraisons',
    'crm.chantiers.totalCa': 'Chiffre d\'Affaires Total (DZD)',
    'crm.chantiers.marginPct': 'Taux de Marge Réalisée (%)',
    'crm.chantiers.collectedPct': 'Taux d\'Encaissement / Règlements (%)',
    'crm.chantiers.client': 'Client',
    'crm.chantiers.assigned': 'Attitré',
    'crm.chantiers.cmdCount': 'cmd(s)',
    'crm.chantiers.enRetard': 'en retard',
    'crm.chantiers.margin': 'Marge',
    'crm.chantiers.collected': 'Encaissé',
    'crm.chantiers.reclamationCount': 'réclamation(s)',
    'crm.chantiers.zeroReclamation': '0 réclamation',
    'crm.chantiers.progress': 'Avancement',
    'crm.chantiers.statusDemarrage': 'En Démarrage (Préparation)',
    'crm.chantiers.statusEnCours': 'En Cours d\'Exécution',
    'crm.chantiers.statusPose': 'En Cours de Pose Menuiseries',
    'crm.chantiers.statusReception': 'Phase Réception / Levée de réserves',
    'crm.chantiers.statusCloture': 'Clôturé avec Succès',
    'crm.chantiers.statusArrete': 'À l\'Arrêt',
    'crm.chantiers.delivOnSchedule': 'Conforme au Planning',
    'crm.chantiers.delivPartially': 'Partiellement Livré',
    'crm.chantiers.delivDelivered': 'Intégralement Livré sur Chantier',
    'crm.chantiers.delivDelayed': 'Livraison en Retard Usine / Transport',
    'crm.reclamations.title': 'SAV & Réclamations Clients Chantiers',
    'crm.reclamations.newTitle': 'Déclarer une Anomalie / Réclamation',
    'crm.reclamations.editTitle': 'Traitement de la Réclamation SAV',
    'crm.reclamations.secIdent': 'Détails de l\'Anomalie Chantier',
    'crm.reclamations.secResolution': 'Traitement & Résolution Technique',
    'crm.reclamations.subjectRequired': 'L\'objet de la réclamation est requis.',
    'crm.reclamations.confirmDelete': 'Êtes-vous sûr de vouloir supprimer cette réclamation ?',
    'crm.reclamations.search': 'Rechercher une réclamation, chantier...',
    'crm.reclamations.subject': 'Objet / Description synthétique du litige',
    'crm.reclamations.chantier': 'Chantier Concerné',
    'crm.reclamations.contact': 'Interlocuteur Déclarant',
    'crm.reclamations.type': 'Nature de l\'Anomalie',
    'crm.reclamations.severity': 'Niveau de Gravité / Sévérité',
    'crm.reclamations.dateReported': 'Date de Signalement',
    'crm.reclamations.dateResolved': 'Date de Résolution',
    'crm.reclamations.resolutionNotes': 'Notes de Résolution & Actions Correctives',
    'crm.reclamations.resolutionPlaceholder': 'Détaillez les pièces remplacées, l\'intervention du technicien SAV, la levée des réserves...',
    'crm.reclamations.selectChantier': 'Sélectionner un chantier',
    'crm.reclamations.selectContact': 'Sélectionner un contact',
    'crm.reclamations.typeDefautPose': 'Défaut de Pose / Fixation / Calage',
    'crm.reclamations.typeDefautProduit': 'Défaut Produit / Rayure / Vitrage',
    'crm.reclamations.typeRetardLivraison': 'Retard de Livraison Chantier',
    'crm.reclamations.typeAccessoiresManquants': 'Accessoires / Quincaillerie Manquante',
    'crm.reclamations.typeAutre': 'Autre Réclamation',
    'crm.reclamations.sevLow': 'Faible (Impact mineur)',
    'crm.reclamations.sevMedium': 'Moyenne (À traiter sous 48h)',
    'crm.reclamations.sevHigh': 'Élevée (Bloque la réception)',
    'crm.reclamations.sevCritical': 'Critique (Arrêt immédiat de pose)',
    'crm.reclamations.statusOpen': 'Ouverte (En attente)',
    'crm.reclamations.statusInvestigating': 'En Cours d\'Analyse Usine / SAV',
    'crm.reclamations.statusInProgress': 'Intervention Technique Planifiée',
    'crm.reclamations.statusResolved': 'Résolue & Réceptionnée',
    'crm.reclamations.statusClosed': 'Dossier Fermé',
    'crm.reclamations.client': 'Client',
    'crm.reclamations.reportedOn': 'Signalé le',
    'crm.reclamations.resolvedOn': 'Résolu le',
    'crm.reclamations.notResolved': 'Non résolu',
    'crm.contacts.title': 'Contacts & Décideurs Clés',
    'crm.contacts.detailsSection': 'Informations Personnelles & Professionnelles',
    'crm.contacts.field.firstName': 'Prénom',
    'crm.contacts.field.lastName': 'Nom',
    'crm.contacts.field.fullName': 'Nom Complet',
    'crm.contacts.field.organisation': 'Organisation Rattachée (BET / Promo / MO)',
    'crm.contacts.field.company': 'Entreprise / Société',
    'crm.contacts.field.role': 'Fonction / Poste',
    'crm.contacts.field.email': 'Email',
    'crm.contacts.field.phone': 'Téléphone',
    'crm.contacts.field.mobile': 'Mobile',
    'crm.contacts.field.prefComm': 'Canal de Communication Préféré',
    'crm.contacts.field.prefHours': 'Horaires Préférés',
    'crm.contacts.field.prefLang': 'Langue Préférée',
    'crm.contacts.field.type': 'Type de contact',
    'crm.contacts.field.city': 'Ville',
    'crm.contacts.field.address': 'Adresse',
    'crm.contacts.noOrgDirect': 'Aucune / Entreprise directe',
    'crm.contacts.prefEmail': 'Email',
    'crm.contacts.prefPhone': 'Téléphone',
    'crm.contacts.prefMeeting': 'Rendez-vous physique',
    'crm.contacts.prefWhatsapp': 'WhatsApp / SMS',
    'crm.contacts.hoursPlaceholder': 'Ex: Matinées 9h-12h',
    'crm.contacts.influence': 'Niveau d\'Influence Décisionnelle :',
    'crm.contacts.noRole': 'Rôle non spécifié',
    'crm.contacts.dispo': 'Dispo',
    'crm.leads.newLeadBtn': 'Nouveau Prospect',
    'crm.leads.informationSection': 'Informations du Prospect',
    'crm.leads.field.name': 'Nom complet',
    'crm.leads.field.company': 'Entreprise',
    'crm.leads.field.email': 'Email',
    'crm.leads.field.phone': 'Téléphone',
    'crm.leads.field.status': 'Statut',
    'crm.leads.field.source': 'Source',
    'crm.leads.field.value': 'Valeur Estimée (DZD)',
    'crm.leads.field.notes': 'Notes & Remarques',
    'crm.leads.sourceLabel': 'Source',
    'crm.leads.sourceDirect': 'Direct',
    'crm.leads.noData': 'Aucun prospect enregistré. Cliquez sur "Nouveau Prospect" pour commencer.',
    'crm.leads.search': 'Rechercher un prospect...',
    'crm.leads.col.code': 'Code',
    'crm.leads.col.name': 'Nom',
    'crm.leads.col.company': 'Entreprise',
    'crm.leads.col.email': 'Email',
    'crm.leads.col.phone': 'Téléphone',
    'crm.leads.col.value': 'Valeur',
    'crm.leads.col.status': 'Statut',
    'crm.status.new': 'Nouveau',
    'crm.status.contacted': 'Contacté',
    'crm.status.qualified': 'Qualifié',
    'crm.status.proposal': 'Proposition',
    'crm.status.won': 'Gagné',
    'crm.status.lost': 'Perdu',
    'crm.source.website': 'Site Web',
    'crm.source.referral': 'Recommandation',
    'crm.source.social': 'Réseaux Sociaux',
    'crm.source.event': 'Événement / Salon',
    'crm.source.other': 'Autre',
    'crm.type.client': 'Client',
    'crm.type.prospect': 'Prospect',
    'crm.type.partner': 'Partenaire',
    'crm.type.vendor': 'Fournisseur',
    'crm.dashboard.title': 'Tableau de Bord Commercial & CRM',
    'crm.dashboard.subtitle': 'Suivi de la performance commerciale, portefeuille prospects et opportunités',
    'crm.dashboard.customizeKpi': 'Paramétrer les KPIs',
    'crm.dashboard.newLead': 'Nouveau Prospect',
    'crm.dashboard.newOpp': 'Nouvelle Opportunité',
    'crm.dashboard.activeOpportunities': 'Affaires en Négociation',
    'crm.dashboard.noContact': 'Sans contact',
    'crm.dashboard.totalLeads': 'Prospects (Leads)',
    'crm.dashboard.newLeads': 'nouveaux',
    'crm.dashboard.totalContacts': 'Contacts & Clients',
    'crm.dashboard.totalOpportunities': 'Opportunités',
    'crm.dashboard.pipelineAmount': 'Pipeline Commercial',
    'crm.dashboard.wonAmount': 'Chiffre Gagné',
    'crm.dashboard.pendingActivities': 'RDV & Activités à venir',
    'crm.dashboard.recentLeads': 'Derniers Prospects',
    'crm.dashboard.recentOpportunities': 'Opportunités Récentes',
    'crm.dashboard.noLeads': 'Aucun prospect enregistré.',
    'crm.dashboard.noOpportunities': 'Aucune opportunité enregistrée.',

    // CRM KPIs & Widgets (Parameterized)
    'crm.dashboard.kpi.total_leads': 'Total Prospects',
    'crm.dashboard.kpi.desc.total_leads': 'Tous les prospects enregistrés',
    'crm.dashboard.kpi.new_leads': 'Nouveaux Prospects',
    'crm.dashboard.kpi.desc.new_leads': 'Prospects récents à contacter',
    'crm.dashboard.kpi.total_contacts': 'Contacts Clients',
    'crm.dashboard.kpi.desc.total_contacts': 'Répertoire de contacts actifs',
    'crm.dashboard.kpi.total_opportunities': 'Opportunités Actives',
    'crm.dashboard.kpi.desc.total_opportunities': 'Dossiers d\'affaires ouverts',
    'crm.dashboard.kpi.pipeline_amount': 'Valeur Pipeline',
    'crm.dashboard.kpi.desc.pipeline_amount': 'Montant total pondéré des opportunités',
    'crm.dashboard.kpi.won_amount': 'Affaires Gagnées',
    'crm.dashboard.kpi.desc.won_amount': 'Chiffre d\'affaires clos remporté',
    'crm.dashboard.kpi.pending_activities': 'Activités en Attente',
    'crm.dashboard.kpi.desc.pending_activities': 'Tâches, relances et réunions à traiter',
    'crm.dashboard.widget.recent_leads': 'Derniers Prospects',
    'crm.dashboard.widget.desc.recent_leads': 'Liste et suivi des prospects récents',
    'crm.dashboard.widget.pipeline_stages': 'Pipeline de Vente',
    'crm.dashboard.widget.desc.pipeline_stages': 'Répartition des montants par étape',
    'crm.dashboard.widget.recent_activities': 'Activités Récentes',
    'crm.dashboard.widget.desc.recent_activities': 'Historique et prochaines actions',

    // CRM Stats Badges
    'crm.stats.newThisMonth': 'nouveaux',
    'crm.stats.toContact': 'À contacter',
    'crm.stats.activeAccounts': 'Comptes actifs',
    'crm.stats.activeDeals': 'affaires en cours',
    'crm.stats.weightedPipeline': 'Portefeuille actif',
    'crm.stats.closedWon': 'CA sécurisé',
    'crm.stats.actionRequired': 'Relances à traiter',
    'crm.leads.pageTitle': 'Gestion des Prospects (Leads)',
    'crm.leads.new': 'Nouveau Prospect',
    'crm.leads.editTitle': 'Modifier le Prospect',
    'crm.leads.newTitle': 'Nouveau Prospect',
    'crm.leads.details': 'Informations du prospect',
    'crm.leads.name': 'Nom & Prénom',
    'crm.leads.company': 'Entreprise',
    'crm.leads.email': 'Email',
    'crm.leads.phone': 'Téléphone',
    'crm.leads.status': 'Statut',
    'crm.leads.source': 'Source d\'acquisition',
    'crm.leads.value': 'Valeur estimée',
    'crm.leads.notes': 'Remarques / Besoins',
    'crm.leads.confirmDelete': 'Êtes-vous sûr de vouloir supprimer ce prospect ?',
    'crm.leads.deleteSuccess': 'Prospect supprimé avec succès.',
    'crm.leads.updateSuccess': 'Prospect mis à jour avec succès.',
    'crm.leads.createSuccess': 'Prospect créé avec succès.',
    'crm.leads.nameRequired': 'Le nom du prospect est requis.',
    'crm.leads.searchPlaceholder': 'Rechercher un prospect par nom, société, email...',
    'crm.leads.empty': 'Aucun prospect trouvé. Cliquez sur "Nouveau Prospect" pour commencer.',
    'crm.contacts.pageTitle': 'Répertoire Contacts & Clients',
    'crm.contacts.new': 'Nouveau Contact',
    'crm.contacts.editTitle': 'Modifier le Contact / Décideur',
    'crm.contacts.newTitle': 'Nouveau Contact & Décideur',
    'crm.contacts.details': 'Informations du contact',
    'crm.contacts.firstName': 'Prénom',
    'crm.contacts.lastName': 'Nom de famille',
    'crm.contacts.company': 'Entreprise',
    'crm.contacts.role': 'Fonction / Poste',
    'crm.contacts.email': 'Email',
    'crm.contacts.phone': 'Téléphone fixe',
    'crm.contacts.mobile': 'Téléphone mobile',
    'crm.contacts.address': 'Adresse',
    'crm.contacts.city': 'Ville',
    'crm.contacts.type': 'Type de relation',
    'crm.contacts.confirmDelete': 'Êtes-vous sûr de vouloir supprimer ce contact ?',
    'crm.contacts.deleteSuccess': 'Contact supprimé avec succès.',
    'crm.contacts.updateSuccess': 'Contact mis à jour avec succès.',
    'crm.contacts.createSuccess': 'Contact créé avec succès.',
    'crm.contacts.lastNameRequired': 'Le nom de famille est requis.',
    'crm.contacts.searchPlaceholder': 'Rechercher un contact, organisation, email...',
    'crm.contacts.empty': 'Aucun contact trouvé. Cliquez sur "Nouveau Contact" pour commencer.',
    'crm.opportunities.pageTitle': 'Opportunités Commerciales',
    'crm.opportunities.new': 'Nouvelle Opportunité',
    'crm.opportunities.editTitle': 'Modifier l\'Opportunité',
    'crm.opportunities.newTitle': 'Nouvelle Opportunité',
    'crm.opportunities.details': 'Détails de l\'opportunité',
    'crm.opportunities.title': 'Opportunités & Pipeline d’Affaires',
    'crm.opportunities.contact': 'Contact associé',
    'crm.opportunities.stage': 'Étape Pipeline',
    'crm.opportunities.amount': 'Montant (DZD)',
    'crm.opportunities.probability': 'Probabilité (%)',
    'crm.opportunities.closeDate': 'Date de clôture prévue',
    'crm.opportunities.closeTarget': 'Prévue',
    'crm.opportunities.notes': 'Notes, Historique & Exigences Particulières',
    'crm.opportunities.stageProspecting': 'Prospection',
    'crm.opportunities.stageQualification': 'Qualification',
    'crm.opportunities.stageProposal': 'Proposition',
    'crm.opportunities.stageNegotiation': 'Négociation',
    'crm.opportunities.stageWon': 'Gagnée',
    'crm.opportunities.stageLost': 'Perdue',
    'crm.opportunities.confirmDelete': 'Êtes-vous sûr de vouloir supprimer cette opportunité ?',
    'crm.opportunities.deleteSuccess': 'Opportunité supprimée avec succès.',
    'crm.opportunities.updateSuccess': 'Opportunité mise à jour avec succès.',
    'crm.opportunities.createSuccess': 'Opportunité créée avec succès.',
    'crm.opportunities.titleRequired': 'Le titre de l\'opportunité est requis.',
    'crm.opportunities.searchPlaceholder': 'Rechercher une affaire, promoteur, projet, prescripteur...',
    'crm.opportunities.empty': 'Aucune opportunité trouvée. Cliquez sur "Nouvelle Opportunité" pour commencer.',
    'crm.activities.pageTitle': 'Activités & Suivis',
    'crm.activities.new': 'Nouvelle Activité',
    'crm.activities.editTitle': 'Modifier l\'Activité',
    'crm.activities.newTitle': 'Nouvelle Activité',
    'crm.activities.details': 'Détails de l\'activité',
    'crm.activities.type': 'Type d\'activité',
    'crm.activities.subject': 'Sujet / Titre',
    'crm.activities.date': 'Date & Heure',
    'crm.activities.status': 'Statut',
    'crm.activities.priority': 'Priorité',
    'crm.activities.contact': 'Contact associé',
    'crm.activities.opportunity': 'Opportunité associée',
    'crm.activities.notes': 'Notes / Résumé de l\'échange',
    'crm.activities.relatedTo': 'Lié à',
    'crm.activities.typeCall': 'Appel téléphonique',
    'crm.activities.typeMeeting': 'Réunion / RDV',
    'crm.activities.typeEmail': 'Email',
    'crm.activities.typeTask': 'Tâche',
    'crm.activities.typeNote': 'Note',
    'crm.activities.statusPending': 'En attente',
    'crm.activities.statusCompleted': 'Terminé',
    'crm.activities.statusCancelled': 'Annulé',
    'crm.activities.priorityLow': 'Basse',
    'crm.activities.priorityMedium': 'Moyenne',
    'crm.activities.priorityHigh': 'Haute',
    'crm.activities.markPending': 'Marquer en attente',
    'crm.activities.markCompleted': 'Marquer terminé',
    'crm.activities.confirmDelete': 'Êtes-vous sûr de vouloir supprimer cette activité ?',
    'crm.activities.deleteSuccess': 'Activité supprimée avec succès.',
    'crm.activities.updateSuccess': 'Activité mise à jour avec succès.',
    'crm.activities.createSuccess': 'Activité créée avec succès.',
    'crm.activities.subjectRequired': 'Le sujet de l\'activité est requis.',
    'crm.activities.searchPlaceholder': 'Rechercher une activité par sujet, contact, opportunité...',
    'crm.activities.empty': 'Aucune activité trouvée. Cliquez sur "Nouvelle Activité" pour planifier un appel, RDV ou tâche.',

    // ProjectManagement
    'project.menu.dashboard': 'Tableau de bord Projets',
    'project.menu.projects': 'Portefeuille de Projets',
    'project.menu.tasks': 'Tâches & Activités',
    'project.menu.milestones': 'Jalons & Livrables',
    'project.menu.team': 'Équipe & Ressources',
    'project.dashboard.title': 'Tableau de bord - Gestion de Projet',
    'project.dashboard.customizeKpi': 'Paramétrer les KPIs',
    'project.dashboard.totalProjects': 'Projets',
    'project.dashboard.active': 'actifs',
    'project.dashboard.inProgress': 'En production',
    'project.dashboard.completedRate': 'complétées',
    'project.dashboard.actionNeeded': 'Action requise',
    'project.dashboard.onTrack': 'À jour',
    'project.dashboard.budgetAllocated': 'Enveloppe allouée',
    'project.dashboard.consumed': 'consommé',
    'project.dashboard.upcomingMilestones': 'À valider',
    'project.dashboard.recentProjects': 'Projets en cours',
    'project.dashboard.recentTasks': 'Tâches Récentes',
    'project.dashboard.overBudget': 'Dépassement',
    'project.dashboard.tasksCompleted': 'Tâches Terminées',
    'project.dashboard.budgetSpent': 'Budget Consommé',
    'project.dashboard.milestones': 'Jalons à venir',
    'project.dashboard.overdueTasks': 'tâches en retard',
    'project.dashboard.activeProjectsTitle': 'Projets en cours',
    'project.dashboard.recentTasksTitle': 'Tâches prioritaires',
    'project.dashboard.noProjects': 'Aucun projet enregistré.',
    'project.dashboard.noTasks': 'Aucune tâche enregistrée.',

    // ProjectManagement KPIs & Widgets (Parameterized)
    'project.dashboard.kpi.total_projects': 'Total Projets',
    'project.dashboard.kpi.desc.total_projects': 'Portefeuille complet de projets',
    'project.dashboard.kpi.active_projects': 'Projets en Cours',
    'project.dashboard.kpi.desc.active_projects': 'Projets actuellement actifs',
    'project.dashboard.kpi.total_tasks': 'Tâches Totales',
    'project.dashboard.kpi.desc.total_tasks': 'Volume global de travail planifié',
    'project.dashboard.kpi.done_tasks': 'Tâches Terminées',
    'project.dashboard.kpi.desc.done_tasks': 'Tâches complétées avec succès',
    'project.dashboard.kpi.overdue_tasks': 'Tâches en Retard',
    'project.dashboard.kpi.desc.overdue_tasks': 'Tâches non soldées après l\'échéance',
    'project.dashboard.kpi.total_budget': 'Budget Global',
    'project.dashboard.kpi.desc.total_budget': 'Budget financier prévisionnel alloué',
    'project.dashboard.kpi.total_spent': 'Budget Consommé',
    'project.dashboard.kpi.desc.total_spent': 'Total des dépenses engagées',
    'project.dashboard.kpi.pending_milestones': 'Jalons Clés',
    'project.dashboard.kpi.desc.pending_milestones': 'Jalons majeurs en attente de validation',
    'project.dashboard.widget.project_progress': 'Avancement des Projets',
    'project.dashboard.widget.desc.project_progress': 'Suivi visuel du taux d\'achèvement',
    'project.dashboard.widget.urgent_tasks': 'Tâches Prioritaires',
    'project.dashboard.widget.desc.urgent_tasks': 'Tâches critiques nécessitant attention',
    'project.projects.pageTitle': 'Portefeuille de Projets',
    'project.projects.new': 'Nouveau Projet',
    'project.projects.editTitle': 'Modifier le Projet',
    'project.projects.newTitle': 'Nouveau Projet',
    'project.projects.generalInfo': 'Informations Générales',
    'project.projects.code': 'Code',
    'project.projects.title': 'Titre du projet',
    'project.projects.client': 'Client / Bénéficiaire',
    'project.projects.status': 'Statut',
    'project.projects.priority': 'Priorité',
    'project.projects.manager': 'Chef de projet / Responsable',
    'project.projects.startDate': 'Date de début',
    'project.projects.endDate': 'Date de fin prévue',
    'project.projects.budget': 'Budget total',
    'project.projects.spent': 'Dépenses réelles',
    'project.projects.progress': 'Progression',
    'project.projects.description': 'Description / Objectifs du projet',
    'project.projects.statusPlanning': 'Planification',
    'project.projects.statusInProgress': 'En cours',
    'project.projects.statusOnHold': 'En pause',
    'project.projects.statusCompleted': 'Terminé',
    'project.projects.statusCancelled': 'Annulé',
    'project.projects.priorityLow': 'Basse',
    'project.projects.priorityMedium': 'Moyenne',
    'project.projects.priorityHigh': 'Haute',
    'project.projects.priorityUrgent': 'Urgente',
    'project.projects.confirmDelete': 'Êtes-vous sûr de vouloir supprimer ce projet ?',
    'project.projects.deleteSuccess': 'Projet supprimé avec succès.',
    'project.projects.updateSuccess': 'Projet mis à jour avec succès.',
    'project.projects.createSuccess': 'Projet créé avec succès.',
    'project.projects.titleRequired': 'Le titre du projet est requis.',
    'project.projects.searchPlaceholder': 'Rechercher un projet par titre, client, responsable...',
    'project.projects.empty': 'Aucun projet trouvé. Cliquez sur "Nouveau Projet" pour commencer.',
    'project.tasks.pageTitle': 'Gestion des Tâches',
    'project.tasks.new': 'Nouvelle Tâche',
    'project.tasks.editTitle': 'Modifier la Tâche',
    'project.tasks.newTitle': 'Nouvelle Tâche',
    'project.tasks.details': 'Détails de la tâche',
    'project.tasks.code': 'Code',
    'project.tasks.project': 'Projet associé',
    'project.tasks.title': 'Titre de la tâche',
    'project.tasks.status': 'Statut',
    'project.tasks.priority': 'Priorité',
    'project.tasks.assignee': 'Assigné à',
    'project.tasks.dueDate': 'Échéance',
    'project.tasks.due': 'Échéance',
    'project.tasks.estimatedHours': 'Heures estimées',
    'project.tasks.actualHours': 'Heures passées',
    'project.tasks.progress': 'Progression',
    'project.tasks.hours': 'estimées',
    'project.tasks.description': 'Description détaillée',
    'project.tasks.statusTodo': 'À faire',
    'project.tasks.statusInProgress': 'En cours',
    'project.tasks.statusReview': 'En révision',
    'project.tasks.statusDone': 'Terminé',
    'project.tasks.statusBlocked': 'Bloqué',
    'project.tasks.priorityLow': 'Basse',
    'project.tasks.priorityMedium': 'Moyenne',
    'project.tasks.priorityHigh': 'Haute',
    'project.tasks.priorityCritical': 'Critique',
    'project.tasks.markIncomplete': 'Marquer non terminée',
    'project.tasks.markDone': 'Marquer terminée',
    'project.tasks.confirmDelete': 'Êtes-vous sûr de vouloir supprimer cette tâche ?',
    'project.tasks.deleteSuccess': 'Tâche supprimée avec succès.',
    'project.tasks.updateSuccess': 'Tâche mise à jour avec succès.',
    'project.tasks.createSuccess': 'Tâche créée avec succès.',
    'project.tasks.titleRequired': 'Le titre de la tâche est requis.',
    'project.tasks.projectRequired': 'Veuillez sélectionner un projet associé.',
    'project.tasks.searchPlaceholder': 'Rechercher une tâche par titre, projet, assigné...',
    'project.tasks.empty': 'Aucune tâche trouvée. Cliquez sur "Nouvelle Tâche" pour commencer.',
    'project.milestones.pageTitle': 'Jalons & Échéances Clés',
    'project.milestones.new': 'Nouveau Jalon',
    'project.milestones.editTitle': 'Modifier le Jalon',
    'project.milestones.newTitle': 'Nouveau Jalon',
    'project.milestones.details': 'Détails du jalon',
    'project.milestones.code': 'Code',
    'project.milestones.project': 'Projet associé',
    'project.milestones.title': 'Titre du jalon',
    'project.milestones.date': 'Date cible',
    'project.milestones.status': 'Statut',
    'project.milestones.description': 'Description du jalon',
    'project.milestones.statusPending': 'En attente',
    'project.milestones.statusReached': 'Atteint',
    'project.milestones.statusDelayed': 'En retard',
    'project.milestones.markPending': 'Marquer en attente',
    'project.milestones.markReached': 'Marquer atteint',
    'project.milestones.confirmDelete': 'Êtes-vous sûr de vouloir supprimer ce jalon ?',
    'project.milestones.deleteSuccess': 'Jalon supprimé avec succès.',
    'project.milestones.updateSuccess': 'Jalon mis à jour avec succès.',
    'project.milestones.createSuccess': 'Jalon créé avec succès.',
    'project.milestones.titleRequired': 'Le titre du jalon est requis.',
    'project.milestones.projectRequired': 'Veuillez sélectionner un projet associé.',
    'project.milestones.searchPlaceholder': 'Rechercher un jalon par titre, projet...',
    'project.milestones.empty': 'Aucun jalon trouvé. Cliquez sur "Nouveau Jalon" pour définir un objectif clé.',
    'project.team.pageTitle': 'Équipe & Ressources Projet',
    'project.team.new': 'Nouveau Membre',
    'project.team.editTitle': 'Modifier le Membre',
    'project.team.newTitle': 'Nouveau Membre',
    'project.team.details': 'Détails du membre de l\'équipe',
    'project.team.project': 'Projet affecté',
    'project.team.name': 'Nom et prénom',
    'project.team.role': 'Rôle / Fonction',
    'project.team.email': 'Adresse Email',
    'project.team.allocation': 'Taux d\'allocation (%)',
    'project.team.confirmDelete': 'Êtes-vous sûr de vouloir retirer ce membre du projet ?',
    'project.team.deleteSuccess': 'Membre retiré avec succès.',
    'project.team.updateSuccess': 'Membre mis à jour avec succès.',
    'project.team.createSuccess': 'Membre ajouté avec succès.',
    'project.team.nameRequired': 'Le nom du membre est requis.',
    'project.team.projectRequired': 'Veuillez sélectionner un projet associé.',
    'project.team.searchPlaceholder': 'Rechercher un membre par nom, rôle, projet...',
    'project.team.empty': 'Aucun membre d\'équipe enregistré. Cliquez sur "Nouveau Membre" pour affecter une ressource.',
  },
  en: {
    'login.title': 'Login',
    'login.subtitle': 'Modern Management System',
    'login.email': 'Email address',
    'login.password': 'Password',
    'login.agency': 'Agency code',
    'login.connect': 'Connect',
    'login.forgotPassword': 'Forgot password?',
    'login.resetPassword': 'Reset password',
    'login.resetPasswordTitle': 'Forgot password',
    'login.resetPasswordSubtitle': 'Enter your email address to receive a new password',
    'login.resetPasswordButton': 'Send new password',
    'login.backToLogin': 'Back to login',
    'login.language': 'Language',
    'login.copyright': '© 2026 BLOBTIC. All rights reserved.',
    'login.sso.or': 'OR',
    'login.sso.continueWith': 'Or continue with',
    'login.sso.google': 'Continue with Google',
    'login.sso.microsoft': 'Continue with Microsoft',
    'login.sso.oidc': 'Continue with {0}',
    'login.sso.authenticating': 'SSO Authentication in progress...',
    'login.sso.selectAgencyTitle': 'Select your agency',
    'login.sso.selectAgencyDesc': 'Multiple agencies are associated with your account. Please select the one you wish to connect to:',
    'login.sso.confirmAgency': 'Access Agency',
    'login.sso.error': 'SSO Authentication Failed',
    'login.sso.noAccount': 'No BLOBTIC account is associated with this email address.',
    'login.sso.backToLogin': 'Back to Login',
    'error.email.required': 'Email is required',
    'error.email.invalid': 'Invalid email format',
    'error.password.required': 'Password is required',
    'error.password.minLength': 'Password must contain at least 6 characters',
    'error.agency.required': 'Agency code is required',
    'error.agency.minLength': 'Agency code must contain at least 3 characters',
    'error.resetPassword.success': 'A new password has been sent to your email address',
    'error.resetPassword.failed': 'Error sending new password',
    'error.resetPassword.userNotFound': 'No user found with this email address',
    'zoom.agency.title': 'Agency selection',
    'zoom.agency.placeholder': 'Click the zoom button to select an agency',
    'zoom.agency.disabled': 'Please fill in email and password first',
    'language.fr': 'Français',
    'language.en': 'English',
    'language.ar': 'العربية',

    // Theme Customizer
    'theme.settings.title': 'Appearance',
    'theme.mode.label': 'Mode',
    'theme.mode.dark': 'Dark Mode',
    'theme.mode.light': 'Light Mode',
    'theme.color.label': 'Primary Color',
    'common.actions.customize': 'Customize',
    'common.backToList': 'Back to list',
    'common.code': 'Code',
    'common.stage': 'Pipeline Stage',
    'common.address': 'Address',
    'common.french': 'French',
    'common.arabic': 'Arabic',
    'common.english': 'English',
    'common.confirm': 'Confirmation',
    'common.none': 'None',
    'common.autoGenerated': '(Automatic)',
    'common.notes': 'Notes',
    'common.viewAll': 'View All',
    'crm.opportunities.transferredKam': 'Transferred to KAM (Active Site)',
    'crm.opportunities.readyTransferKam': 'Ready for KAM Handover',
    'CLIENT': 'Client',
    'client': 'Client',
    'PROSPECT': 'Prospect',
    'prospect': 'Prospect',
    'PARTNER': 'Partner',
    'partner': 'Partner',
    'VENDOR': 'Vendor',
    'vendor': 'Vendor',
    'LOW': 'Low',
    'MEDIUM': 'Medium',
    'HIGH': 'High',
    'CRITICAL': 'Critical',
    'BLOCKING': 'Blocking',
    'OPEN': 'Open',
    'INVESTIGATING': 'Investigating',
    'IN_PROGRESS': 'In Progress',
    'RESOLVED': 'Resolved',
    'CLOSED': 'Closed',
    'DEFAUT_POSE': 'Installation defect',
    'DEFAUT_PRODUIT': 'Product defect',
    'RETARD_LIVRAISON': 'Delivery delay',
    'NON_CONFORMITE_VITRAGE': 'Glazing non-compliance',
    'ACCESSOIRES_MANQUANTS': 'Missing accessories',
    'AUTRE': 'Other',
    'PRESCRIPTEUR': 'Prescriber',
    'KAM': 'Key Account Manager',
    'PROSPECTION': 'Prospecting',
    'PRESCRIPTION_TECHNIQUE': 'Technical Prescription',
    'SUIVI_CHANTIER': 'Site Follow-up',
    'REUNION_ETUDES': 'Studies Meeting',
    'NEGOCIATION': 'Negotiation',
    'SAV_LIVRAISON': 'After-Sales & Delivery',
    'PLANIFIEE': 'Planned',
    'REALISEE': 'Completed',
    'ANNULEE': 'Cancelled',
    'REPORTEE': 'Postponed',
    'PLANNED': 'Planned',
    'COMPLETED': 'Completed',
    'CANCELLED': 'Cancelled',
    'RESCHEDULED': 'Rescheduled',
    'DRAFT': 'Draft',
    'TRANSMIS': 'Submitted',
    'EN_REVISION': 'Under Review',
    'INTEGRE_AU_CCTP': 'Integrated into CCTP',
    'VALIDE': 'Validated',
    'REJETE': 'Rejected',
    'SUBMITTED': 'Submitted',
    'VALIDATED': 'Validated',
    'SIGNED': 'Signed',
    'REJECTED': 'Rejected',
    'URBANIA': 'Urbania',
    'FUTURA': 'Futura',
    'DOORIA': 'Dooria',
    'MIXTE': 'Mixed',
    'URBANIA_ALU': 'Urbania Alu',
    'FUTURA_PVC': 'Futura PVC',
    'DOORIA_INTERIEUR': 'Dooria Interior Doors',
    'MIXTE_ALU_BOIS': 'Wood-Alu Combo',
    'DEMARRAGE': 'Kickoff',
    'PREPARATION': 'Preparation',
    'EN_COURS': 'In Progress',
    'POSE': 'Installation',
    'RECEPTION': 'Handover',
    'CLOTURE': 'Closed',
    'EN_PAUSE': 'On Pause',
    'ARRETE': 'Stopped',
    'LIVRE': 'Delivered',
    'ON_SCHEDULE': 'On Schedule',
    'PARTIALLY_DELIVERED': 'Partially Delivered',
    'DELIVERED': 'Fully Delivered',
    'DELAYED': 'Delayed',
    'PROMOTION_IMMOBILIERE': 'Real Estate Development',
    'PROMOTION': 'Real Estate Development',
    'AADL': 'AADL',
    'LPP': 'LPP',
    'LOGEMENT_INDIVIDUEL': 'Individual Housing',
    'EQUIPEMENT_PUBLIC': 'Public Facility',
    'TERTIAIRE': 'Commercial',
    'PROSPECTING': 'Prospecting',
    'QUALIFICATION': 'Qualification',
    'PROPOSAL': 'Proposal / Quote',
    'PROPOSITION_DEVIS': 'Proposal / Quote',
    'NEGOTIATION': 'Negotiation',
    'WON': 'Won',
    'LOST': 'Lost',
    'ABANDONNE': 'Abandoned',
    'A_L_ARRET': 'On Hold',
    'TRANSFERRED': 'Transferred to KAM',
    'PENDING_TRANSFER': 'Ready for Handover',
    'NON_TRANSFERE': 'Not Transferred',
    'TRANSFERE_KAM': 'Transferred to KAM',
    'FAIBLE': 'Low',
    'MOYENNE': 'Medium',
    'ELEVEE': 'High',
    'MOYEN': 'Medium',
    'ELEVE': 'High',
    'BET': 'Engineering Office (BET)',
    'ARCHITECTE': 'Architect',
    'PROMOTEUR': 'Real Estate Developer',
    'MO': 'Project Owner (MO)',
    'ENT': 'General Contractor',
    'INSTALLATEUR': 'Joiner / Installer',
    'PETITE': 'Small structure',
    'GRANDE': 'Large enterprise',
    'CALL': 'Call',
    'MEETING': 'Meeting',
    'TECH_MEETING': 'Technical Meeting',
    'EMAIL': 'Email',
    'VISIT_SHOWROOM': 'Showroom Visit',
    'SITE_SURVEY': 'Site Survey',
    'DEMO': 'Demo',
    'common.languages': 'Languages',
    'common.actions.userManual': 'User Manual',
    'common.actions.switchApp': 'Switch Application',
    'common.default_view_mode': 'Default Display',
    'common.view_grid': 'Grid View',
    'common.view_table': 'Table View',
    'common.refresh': 'Refresh',
    'common.refreshed': 'Data refreshed successfully.',

    // Dialog Titles
    'dialog.changePassword.title': 'Change Password',
    'dialog.forgotPassword.title': 'Forgot Password',

    // Change Password Dialog
    'dialog.changePassword.step.email': 'To change your password, we must first verify your identity. Enter your email address to receive a verification code.',
    'dialog.changePassword.step.verify': 'A verification code has been sent to {0}. Enter this code to continue.',
    'dialog.changePassword.step.new': 'Enter your current password and your new password.',
    'dialog.changePassword.success': 'Your password has been changed successfully!',
    'label.currentPassword': 'Current Password',
    'label.newPassword': 'New Password',
    'label.confirmPassword': 'Confirm New Password',
    'label.verificationCode': 'Verification Code',

    // Forgot Password Dialog
    'dialog.forgotPassword.steps.1': 'Code Request',
    'dialog.forgotPassword.steps.2': 'OTP Verification',
    'dialog.forgotPassword.steps.3': 'New Password',
    'dialog.forgotPassword.step1.desc': 'Enter your email address to receive a verification code.',
    'dialog.forgotPassword.step2.desc': 'Enter the verification code sent to your email.',
    'dialog.forgotPassword.step3.desc': 'Enter your new password.',
    'dialog.forgotPassword.success': 'Your password has been reset successfully!',

    // Common Actions
    'btn.cancel': 'Cancel',
    'btn.sendCode': 'Send Code',
    'btn.verifyCode': 'Verify Code',
    'btn.verify': 'Verify',
    'btn.back': 'Back',
    'btn.changePassword': 'Change Password',
    'btn.reset': 'Reset',
    'btn.close': 'Close',

    // Validation & Messages
    'error.email.empty': 'Please enter your email address',
    'error.otp.send': 'Error sending verification code. Please try again.',
    'error.otp.empty': 'Please enter the verification code',
    'error.otp.length': 'OTP code must contain 6 digits',
    'error.password.empty': 'Please enter your password',

    // Maker - Design Canvas
    'maker.design.canvas.select': 'Select',
    'maker.design.canvas.vsplit': 'V-Split',
    'maker.design.canvas.hsplit': 'H-Split',
    'maker.design.canvas.sash': 'Sash',
    'maker.design.canvas.delete': 'Delete',
    'maker.design.canvas.clear': 'Clear',
    'maker.design.canvas.dimensions': 'Dimensions',
    'maker.design.canvas.splitters': 'Splitters',
    'maker.design.canvas.sashes': 'Sashes',
    'maker.design.canvas.title': '2D Technical Drawing',

    // Maker - Templates
    'maker.design.templates.prompt': 'Select a predefined template or create custom geometry',
    'maker.design.templates.custom': 'Create Custom Geometry',
    'maker.design.template.custom.name': 'Custom',
    'maker.design.template.custom.desc': 'Draw custom geometry',
    'maker.design.template.casement_single': 'Single Casement',
    'maker.design.template.casement_single.desc': 'Standard single casement window',
    'maker.design.template.casement_double': 'Double Casement',
    'maker.design.template.casement_double.desc': 'Two-panel casement window',
    'maker.design.template.tilt_turn': 'Tilt & Turn',
    'maker.design.template.tilt_turn.desc': 'Tilt and turn functionality',
    'maker.design.template.sliding': 'Sliding Window',
    'maker.design.template.sliding.desc': 'Horizontal sliding window',
    'maker.design.template.round': 'Round Window',
    'maker.design.template.round.desc': 'Circular window',
    'maker.design.template.french': 'French Window',
    'maker.design.template.french.desc': 'French style window',
    'maker.design.template.door_single': 'Single Door',
    'maker.design.template.door_single.desc': 'Standard single door',
    'maker.design.template.door_double': 'Double Door',
    'maker.design.template.door_double.desc': 'Two-panel door',
    'maker.design.template.door_sliding': 'Sliding Door',
    'maker.design.template.door_sliding.desc': 'Sliding door system',
    'maker.design.template.door_folding': 'Folding Door',
    'maker.design.template.door_folding.desc': 'Folding door system',

    // Maker - Components
    'maker.design.components.prompt': 'Add hardware, shutters, screens, and accessories',
    'maker.design.components.pool': 'Pool of {0}',
    'maker.design.components.cat.hardware': 'Hardware',
    'maker.design.components.cat.shutter': 'Shutters',
    'maker.design.components.cat.screen': 'Screens',
    'maker.design.components.cat.roller': 'Roller Shutters',
    // Component Items
    'maker.component.handle_std': 'Standard Handle',
    'maker.component.handle_std.desc': 'Standard window/door handle',
    'maker.component.handle_prem': 'Premium Handle',
    'maker.component.handle_prem.desc': 'Premium finish handle',
    'maker.component.lock_multi': 'Multi-Point Lock',
    'maker.component.lock_multi.desc': '3-point locking system',
    'maker.component.lock_dead': 'Deadbolt Lock',
    'maker.component.lock_dead.desc': 'Standard deadbolt',
    'maker.component.hinge_butt': 'Butt Hinge',
    'maker.component.hinge_butt.desc': 'Standard butt hinge',
    'maker.component.hinge_conc': 'Concealed Hinge',
    'maker.component.hinge_conc.desc': 'Concealed hinge system',
    'maker.component.stay': 'Window Stay',
    'maker.component.stay.desc': 'Window opening stay',
    'maker.component.shutter_louver': 'Louver Shutter',
    'maker.component.shutter_louver.desc': 'Adjustable louver shutter',
    'maker.component.shutter_panel': 'Panel Shutter',
    'maker.component.shutter_panel.desc': 'Solid panel shutter',
    'maker.component.shutter_batten': 'Batten Shutter',
    'maker.component.shutter_batten.desc': 'Traditional batten style',
    'maker.component.screen_std': 'Standard Insect Screen',
    'maker.component.screen_std.desc': 'Standard mesh insect screen',
    'maker.component.screen_retr': 'Retractable Screen',
    'maker.component.screen_retr.desc': 'Roll-up retractable screen',
    'maker.component.screen_mag': 'Magnetic Screen',
    'maker.component.screen_mag.desc': 'Magnetic attachment screen',
    'maker.component.roller_man': 'Manual Roller Shutter',
    'maker.component.roller_man.desc': 'Manual operation roller shutter',
    'maker.component.roller_elec': 'Electric Roller Shutter',
    'maker.component.roller_elec.desc': 'Motorized roller shutter',
    'maker.component.roller_solar': 'Solar Roller Shutter',
    'maker.component.roller_solar.desc': 'Solar-powered roller shutter',

    // Maker - Shapes
    'maker.shape.rectangular': 'Rectangular',
    'maker.shape.round': 'Round',
    'maker.shape.trapezoid': 'Trapezoid',
    'maker.shape.custom': 'Custom',
    'error.currentPassword.empty': 'Please enter your current password',
    'error.newPassword.empty': 'Please enter your new password',
    'error.password.mismatch': 'Passwords do not match',
    'error.connection': 'Connection error. Please try again.',
    'success.codeSent': 'Verification code sent',
    'success.codeSentDesc': 'A verification code has been sent to your email address.',
    'success.codeVerified': 'Code verified',
    'success.codeVerifiedDesc': 'Verification code validated. You can now enter your new password.',
    'success.passwordChanged': 'Password changed',

    // Password Rules
    'rule.password.length': 'Password must contain at least 8 characters',
    'rule.password.lower': 'Password must contain at least one lowercase letter',
    'rule.password.upper': 'Password must contain at least one uppercase letter',
    'rule.password.digit': 'Password must contain at least one digit',
    'rule.password.special': 'Password must contain at least one special character (!@#$%^&*)',

    // Zoom Interface
    'zoom.title.default': 'Zoom - {0}',
    'zoom.title.agency': 'Agency Selection',
    'zoom.title.user': 'User Selection',
    'zoom.title.profile': 'Profile Selection',
    'zoom.title.employee': 'Employee Selection',
    'zoom.title.license': 'License Selection',
    'zoom.search.placeholder': 'Search data...',
    'zoom.action.refresh': 'Refresh data',
    'zoom.action.export': 'Export data',
    'zoom.action.exportCsv': 'Export to CSV',
    'zoom.action.exportExcel': 'Export to Excel',
    'zoom.status.results': '{0} result(s) out of {1} total',
    'zoom.status.filtered': 'Filtered by: "{0}"',
    'zoom.error.loading': '❌ Error loading data',
    'zoom.error.unknown': 'Unknown error',
    'zoom.error.validation': 'Validation error',
    'zoom.error.advice': 'Check your parameters and try again, or contact administrator if problem persists.',
    'zoom.empty.search': 'No results found for your search',
    'zoom.empty.data': 'No data available',
    'zoom.hint.select': 'Click on a row to select',
    'zoom.action.cancel': 'Cancel',
    'zoom.action.select': 'Select',
    'zoom.tooltip.clear': 'Clear selection',
    'zoom.tooltip.open': 'Open zoom {0}',
    'zoom.tooltip.disabled': 'Please fill in email and password first',
    'zoom.check.fillRequired': 'Please fill in required fields',

    // Backend Messages
    'LOGIN_SUCCESS': 'Login successful',
    'INVALID_PASSWORD': 'Invalid password',
    'USER_NOT_FOUND': 'User not found',
    'MISSING_PARAMETERS': 'Missing parameters',
    'INVALID_DOSSIER': 'Invalid dossier',
    'INVALID_LICENSE': 'Invalid license',
    'OTP_SENT': 'OTP code sent',
    'OTP_EXPIRED': 'OTP code expired',
    'INVALID_OTP': 'Invalid OTP code',
    'DOUBLE_AUTH_REQUIRED': 'Two-factor authentication required',
    'SESSION_LOST': 'Session expired',
    'OTP_BLOCKED_RECENTLY': 'Too many attempts, please wait',
    'EMAIL_SEND_ERROR': 'Error sending email',
    'OTP_GENERATION_ERROR': 'OTP generation error',
    'OTP_NOT_FOUND': 'OTP not found',
    'OTP_BLOCKED': 'OTP blocked',
    'OTP_VALIDATED': 'OTP validated',
    'OTP_VALIDATION_ERROR': 'OTP validation error',
    'JWT_CONFIG_RETRIEVED': 'JWT config retrieved',
    'JWT_CONFIG_ERROR': 'JWT config error',
    'PARAMETER_MANAGER_NULL': 'System error (Parameters)',
    'DOUBLE_AUTH_STATUS_RETRIEVED': 'Double auth status retrieved',
    'DOUBLE_AUTH_STATUS_ERROR': 'Double auth status error',

    // Auth Context Messages
    'auth.error.noPending': 'No pending connection',
    'auth.success.login.title': 'Login Successful',
    'auth.success.login.welcome': 'Welcome {0}!',
    'auth.error.invalidResponse': 'Invalid login response',
    'auth.error.loginTitle': 'Login Error',
    'auth.error.doubleAuthTitle': 'Two-Factor Authentication Error',
    'auth.error.unexpected': 'An unexpected error occurred.',
    'auth.error.server': 'Server error. Please try again later.',
    'auth.error.network': 'Cannot contact server. Check your connection.',
    'auth.info.doubleAuthRequired': 'Two-Factor Authentication Required',
    'auth.info.otpSent': 'A verification code has been sent to your email address.',
    'auth.error.credentialsTitle': 'Invalid Credentials',
    'auth.error.credentials': 'Incorrect email, password, or agency.',
    'auth.error.otpBlockedRecentlyTitle': 'Two-Factor Authentication Temporarily Blocked',
    'auth.error.otpBlockedRecentlyDesc': 'Too many recent attempts. Please wait 30 minutes before trying again.',
    'auth.error.otpBlockedTitle': 'Two-Factor Authentication Code Blocked',
    'auth.error.otpBlockedDesc': 'Too many incorrect attempts. Code blocked for security reasons.',
    'auth.error.otpExpiredTitle': 'Two-Factor Authentication Code Expired',
    'auth.error.otpExpiredDesc': 'Code expired. Please request a new code.',
    'auth.error.invalidOtpTitle': 'Incorrect Two-Factor Authentication Code',
    'auth.error.invalidOtpDesc': 'Entered code is incorrect. Please check and try again.',
    'auth.success.logout.title': 'Logout',
    'auth.success.logout.message': 'You have been logged out successfully.',
    'auth.session_expired.title': 'Session Expired',
    'auth.session_expired.message': 'Your session has expired or is invalid. Redirecting to login page...',
    'auth.session_expired.auto_close_text': 'Automatic close (Session expired or invalid)',

    // Double Auth Dialog
    'doubleAuth.title': 'Two-Factor Authentication',
    'doubleAuth.body': 'A verification code has been sent to your email address. Please enter this code to complete your login.',
    'doubleAuth.code.label': 'Verification Code',
    'doubleAuth.code.placeholder': '123456',
    'doubleAuth.security.label': 'Security:',
    'doubleAuth.security.desc': 'The code expires in 10 minutes. After 3 incorrect attempts, the code will be blocked for 30 minutes.',
    'doubleAuth.btn.moreInfo': 'More info',
    'doubleAuth.btn.resend': 'Resend Code',
    'doubleAuth.btn.verify': 'Verify',
    'doubleAuth.tip.label': 'Tip:',
    'doubleAuth.alert.blockedDesc': 'Wait 30 minutes or contact administrator if the problem persists.',
    'doubleAuth.alert.expiredDesc': 'Click "Resend Code" to receive a new code.',
    'doubleAuth.delay.label': 'Security Delay:',
    'doubleAuth.delay.wait': 'You can request a new code in',
    'doubleAuth.delay.now': 'now',
    'doubleAuth.delay.protectionLabel': 'Anti-abuse protection:',
    'doubleAuth.delay.protectionDesc': '60-second delay between requests',
    'doubleAuth.attempts.label': 'Attempts:',
    'doubleAuth.attempts.lastChance': 'Last chance!',
    'doubleAuth.attempts.remaining': 'Attempts remaining',
    'doubleAuth.error.required': 'Code required',
    'doubleAuth.error.requiredDesc': 'Please enter the verification code',
    'doubleAuth.error.invalid': 'Invalid code',
    'doubleAuth.error.invalidDesc': 'Verification code must contain 6 digits',
    'doubleAuth.success.resend': 'Code resent',
    'doubleAuth.success.resendDesc': 'A new verification code has been sent to your email.',
    'doubleAuth.error.resend': 'Error',
    'doubleAuth.error.resendDesc': 'Unable to resend code. Please try again.',

    // Security Info Dialog
    'securityInfo.title': 'Two-Factor Authentication Security',
    'securityInfo.protection.title': 'Enhanced Protection:',
    'securityInfo.protection.desc': 'Your account is protected by multiple security levels to prevent unauthorized access.',
    'securityInfo.measures.title': '🔐 Active Security Measures',
    'securityInfo.measure.email.title': 'Email OTP Code',
    'securityInfo.measure.email.desc': 'A unique 6-digit code is sent to your registered email address',
    'securityInfo.measure.expiry.title': 'Automatic Expiration',
    'securityInfo.measure.expiry.desc': 'Code expires automatically after 10 minutes for security reasons',
    'securityInfo.measure.limit.title': 'Attempt Limit',
    'securityInfo.measure.limit.desc': 'After 3 incorrect attempts, the code is blocked for 30 minutes',
    'securityInfo.measure.abuse.title': 'Anti-Abuse Protection',
    'securityInfo.measure.abuse.desc': '60-second delay between new code requests',
    'securityInfo.help.title': '⚠️ What to do in case of problem?',
    'securityInfo.help.expired.title': 'Code expired',
    'securityInfo.help.expired.desc': 'Click "Resend Code" to receive a new code',
    'securityInfo.help.blocked.title': 'Code blocked',
    'securityInfo.help.blocked.desc': 'Wait 30 minutes or contact administrator if problem persists',
    'securityInfo.help.email.title': 'Email not received',
    'securityInfo.help.email.desc': 'Check your spam folder and ensure email address is correct',
    'securityInfo.tip.label': 'Tip:',
    'securityInfo.tip.desc': 'Keep your email address up to date and check your emails regularly for optimal experience.',
    'securityInfo.btn.understand': 'Understood',

    // Global / Sidebar
    'auth.logout': 'Logout',
    'sidebar.search.placeholder': 'Search...',
    'common.actions.enterFullScreen': 'Full Screen',
    'common.actions.exitFullScreen': 'Exit Full Screen',
    'footer.copyright': '© 2026 BLOBTIC. All rights reserved.',

    // Profiles
    'page.profiles.title': 'Profiles Management',
    'page.profiles.new_title': 'New Profile',
    'page.profiles.edit_title': 'Edit Profile: {0}',
    'page.profiles.add_new': 'New Profile',
    'page.profiles.back_to_list': 'Back to list',
    'page.profiles.confirm_delete': 'Are you sure you want to delete profile {0}?',
    'page.profiles.search_placeholder': 'Search profile...',
    'page.profiles.view_grid': 'Grid View',
    'page.profiles.view_table': 'Table View',
    'page.profiles.status.admin': 'Administrator',
    'page.profiles.status.standard': 'Standard',
    'page.profiles.details': 'Profile Details',
    'page.profiles.zoom.label': 'Select a Profile (Zoom)',
    'page.profiles.zoom.helper': 'Click the magnifying glass to search for a profile',
    'page.profiles.field.code': 'Profile Code',
    'page.profiles.field.label': 'Profile Label',
    'page.profiles.field.level': 'Level',
    'page.profiles.field.admin': 'Administrator',
    'page.profiles.validation.required': 'Code and Label are required',
    'page.profiles.popup.title': 'Profiles List',

    // Agency
    'page.agency.title': 'Agencies',
    'page.agency.new_title': 'New Agency',
    'page.agency.edit_title': 'Edit Agency: {0}',
    'page.agency.add_new': 'New Agency',
    'page.agency.back_to_list': 'Back to list',
    'page.agency.confirm_delete': 'Are you sure you want to delete agency {0}?',
    'page.agency.search_placeholder': 'Search agency...',
    'page.agency.view_grid': 'Grid View',
    'page.agency.view_table': 'Table View',
    'page.agency.zoom.label': 'Search an agency',
    'page.agency.zoom.helper': 'Type an agency code or name to look up',
    'page.agency.details': 'Agency Details',
    'page.agency.field.code': 'Code',
    'page.agency.field.lib': 'Name / Company Name',
    'page.agency.field.email': 'Email',
    'page.agency.field.phone': 'Phone',
    'page.agency.field.address1': 'Address 1',
    'page.agency.field.address2': 'Address 2',
    'page.agency.field.pays_legal': 'Legal Country',
    'page.agency.field.legal_country': 'Legal Country',
    'page.agency.field.mobile_phone': 'Mobile Phone',
    'page.agency.field.start_date': 'Start Date',
    'page.agency.field.end_date': 'End Date',
    'page.agency.field.closed_plng': 'Planning / Agency Closing Date',
    'page.agency.helper.closed_plng': 'Locks planning and invoices on or before this date',
    'page.agency.field.memo': 'Memo',
    'page.agency.validation.required': 'Code and Name are required',
    'page.agency.validation.image_size': 'Image must be under 10MB',
    'page.agency.fiscal_identifiers': 'Fiscal & Legal Identifiers',
    'page.agency.field.nif': 'Tax Identification Number (NIF)',
    'page.agency.helper.nif': '15 digits',
    'page.agency.field.nis': 'Statistical Identification Number (NIS)',
    'page.agency.helper.nis': '11 digits',
    'page.agency.field.rc': 'Commercial Register (RC)',
    'page.agency.helper.rc': 'Commercial Register Number',
    'page.agency.field.ai': 'Tax Article (AI)',
    'page.agency.helper.ai': '13 digits',
    'page.agency.field.logo': 'Agency Logo',
    'page.agency.logo': 'Logo',
    'page.agency.no_logo': 'No logo',
    'page.agency.upload_logo': 'Upload Logo',
    'page.agency.choose_image': 'Choose Image',

    // Pages - Users
    'page.users.title': 'Users Management',
    'page.users.new_title': 'New User',
    'page.users.edit_title': 'Edit User: {0}',
    'page.users.add_new': 'New User',
    'page.users.back_to_list': 'Back to list',
    'page.users.confirm_delete': 'Are you sure you want to delete user {0}?',
    'page.users.search_placeholder': 'Search user...',
    'page.users.view_grid': 'Grid View',
    'page.users.view_table': 'Table View',
    'page.users.details': 'User Details',
    'page.users.zoom.label': 'Select User',
    'page.users.zoom.helper': 'Click to search for a user',
    'page.users.popup.title': 'Users List',
    'page.users.field.code': 'User Code',
    'page.users.field.lib': 'User Name',
    'page.users.field.password': 'Password',
    'page.users.field.profile': 'Profile',
    'page.users.field.profile_lib': 'Profile Label',
    'page.users.field.license': 'License',
    'page.users.field.license_lib': 'License Label',
    'page.users.field.start_date': 'Start Date',
    'page.users.field.end_date': 'End Date',
    'page.users.field.email': 'Email',
    'page.users.field.mobile': 'Mobile Phone',
    'page.users.field.pass_date': 'Password Expiration',
    'page.users.field.pass_days': 'Expiration Warning Days',
    'page.users.validation.required': 'Code and Name are required',
    'page.users.field.memo': 'Memo',
    'LOGIN_PASSWORD_EXPIRED': 'Your password has expired.',
    'LOGIN_ACCOUNT_EXPIRED': 'Account expired.',
    'LOGIN_PASSWORD_EXPIRING_SOON': 'Your password will expire soon.',



    'menu.planning.admin.accessRights.sysParams': 'System Parameters',
    'menu.planning.admin.accessRights.securityWeb': 'Web Security',

    // Page Security Web
    'page.security.title': 'Web Security (Profiles & Actions)',
    'page.security.profile.label': 'Profile',
    'page.security.table.selectProfile': 'Select a profile',
    'page.security.table.objectAction': 'Object/Action',
    'page.security.table.type': 'Type',
    'page.security.table.load': 'Load',
    'page.security.table.select': 'Select',
    'page.security.table.update': 'Update',
    'page.security.table.insert': 'Insert',
    'page.security.table.delete': 'Delete',
    'page.security.table.execute': 'Execute',
    'page.security.table.empty': 'No objects found',
    'page.security.action.refresh': 'Refresh',
    'page.security.action.updateAll': 'Global Update',
    'page.security.dialog.bulkTitle': 'Global Rights Update',
    'page.security.dialog.apply': 'Apply to All',

    'common.profile': 'Profile',
    'common.back_to_list': 'Back to list',
    'common.select': 'Select...',
    'common.status': 'Status',
    'common.phone': 'Phone',
    'common.email': 'Email',
    'common.loading': 'Loading...',
    'common.loadingServer': 'Loading data from server',
    'common.loadingServerHint': 'Please wait a moment…',
    'common.saving': 'Saving...',
    'common.save': 'Save',
    'common.success.saved': 'Changes saved successfully',
    'common.error.saving': 'Error saving rights',
    'common.error.loading': 'Error loading data',

    'page.sys_parameters.title': 'System Parameters',
    'page.sys_parameters.subtitle': 'Centralized application parameters management',
    'page.sys_parameters.action.reset': 'Reset Defaults',
    'page.sys_parameters.confirm_reset': 'Are you sure you want to reset all parameters to default values? This action cannot be undone.',
    'page.sys_parameters.success.reset': 'Parameters reset successfully',
    'page.sys_parameters.error.reset': 'Error during reset',
    'page.sys_parameters.success.update': 'Update successful',
    'page.sys_parameters.success.message': 'Parameter {0} successfully updated',
    'page.sys_parameters.meta.info': 'Updated: {0} by {1}',

    'action.update': 'Update',
    'action.save': 'Save',
    'action.refresh': 'Refresh',
    'SYSPAR_CODE': 'Parameter Code',

    'page.sys_parameters.group.messaging': 'Messaging (SMTP)',
    'page.sys_parameters.group.security': 'Security & Authentication',

    // Menu Maker
    'menu.maker.projects': 'Projects',
    'menu.maker.estimates': 'Estimates',
    'menu.maker.design': 'Design',
    'menu.maker.production': 'Production',
    'menu.maker.materials': 'Materials',
    'menu.maker.customers': 'Customers',
    'menu.maker.reports': 'Reports',
    'menu.maker.profiles': 'Profiles',
    'menu.maker.dashboard': 'Dashboard',
    'menu.maker.libraries': 'Libraries',
    'menu.maker.components': 'Components',
    'menu.maker.templates': 'Templates',

    // Locations de salle (Room Rentals) - EN
    'academy.room_rentals.title': 'Room Rentals',
    'academy.room_rentals.new_rental': 'New Rental',
    'academy.room_rentals.create_title': 'New Room Rental',
    'academy.room_rentals.edit_title': 'Edit Rental: {name}',
    'academy.room_rentals.empty_title': 'No rentals recorded',
    'academy.room_rentals.empty_subtitle': 'Create a new rental to book a room.',
    'academy.room_rentals.search_placeholder': 'Search rental, client, room...',
    'academy.room_rentals.confirm_delete': 'Are you sure you want to delete rental "{name}"?',
    'academy.room_rentals.deleted_success': 'Rental deleted successfully.',
    'academy.room_rentals.room': 'Room',
    'academy.room_rentals.client': 'Client / Organization',
    'academy.room_rentals.client_name': 'Client / Organization Name',
    'academy.room_rentals.lib': 'Title / Rental Purpose',
    'academy.room_rentals.dates': 'Rental Period',
    'academy.room_rentals.start_date': 'Start Date & Time',
    'academy.room_rentals.end_date': 'End Date & Time',
    'academy.room_rentals.code': 'Reference Code',
    'academy.room_rentals.price': 'Total Price (DZD)',
    'academy.room_rentals.deposit': 'Deposit Paid (DZD)',
    'academy.room_rentals.notes': 'Notes & Equipment Needed',
    'academy.room_rentals.notes_title': 'Notes & Equipment Needed',
    'academy.room_rentals.section_room': 'Room & Schedule',
    'academy.room_rentals.section_client': 'Client & Pricing',
    'academy.room_rentals.room_helper': 'Select the available room for this rental',
    'academy.room_rentals.validation.room_required': 'Please select a room.',
    'academy.room_rentals.validation.client_required': 'Client / organization name is required.',
    'academy.room_rentals.validation.start_date_required': 'Start date is required.',
    'academy.room_rentals.validation.end_date_required': 'End date is required.',
    'academy.room_rentals.validation.date_order': 'End date must be after start date.',

    // boubaker 25/08/2026 - restructured menu groups
    'academy.menu.resources': 'Resources',
    'academy.menu.students_groups': 'Students & Groups',
    'academy.menu.planning': 'Schedule',
    'academy.menu.pedagogy': 'Pedagogy',
    'academy.menu.finance': 'Finance',
    'academy.menu.monitoring': 'Monitoring & Indicators',
    'academy.menu.system': 'System & Logs',
    'academy.menu.data_exchange': 'Data & Exchanges',
    'academy.menu.import_data': 'Import Data',
    'academy.menu.export_data': 'Export Data',

    // Import & Export Data - EN
    'academy.import.page_title': 'Data Import',
    'academy.import.refresh': 'Refresh',
    'academy.import.step1_title': '1. Select entity to import',
    'academy.import.in_database': 'in database',
    'academy.import.step2_title': '2. Download blank template',
    'academy.import.step2_desc': 'Use our ready-to-use template containing pre-configured headers and a sample row for {0}.',
    'academy.import.btn_excel': 'Excel Template (.xlsx)',
    'academy.import.btn_csv': 'CSV Template (.csv)',
    'academy.import.view_rules': 'View columns and rules ({0} fields)',
    'academy.import.step3_title': '3. Select your file to import',
    'academy.import.drop_title': 'Drag and drop your file here, or click to browse',
    'academy.import.file_selected': 'Selected file: {0} ({1} KB)',
    'academy.import.accepted_formats': 'Accepted formats: Microsoft Excel (.xlsx, .xls) or CSV (.csv UTF-8)',
    'academy.import.update_existing': 'Update if code already exists',
    'academy.import.btn_import': 'Import {0} row(s)',
    'academy.import.importing': 'Importing in progress...',
    'academy.import.preview_title': 'Preview & Validation ({0} rows)',
    'academy.import.preview_desc': 'Check data compliance before initiating database save.',
    'academy.import.filter_all': 'All ({0})',
    'academy.import.filter_valid': 'Valid ({0})',
    'academy.import.filter_errors': 'Errors ({0})',
    'academy.import.status_valid': 'Valid',
    'academy.import.status_incomplete': 'Incomplete',
    'academy.import.col_index': '#',
    'academy.import.col_status': 'Status',
    'academy.import.preview_limit': 'Display limited to first 100 rows out of {0}. Import will process entire file.',
    'academy.import.dialog_rules_title': 'Import file structure: {0}',
    'academy.import.dialog_rules_subtitle': 'Target entity: {0}. Reference identifier: {1}.',
    'academy.import.col_file_header': 'File Header',
    'academy.import.col_data_type': 'Data Type',
    'academy.import.col_required': 'Required',
    'academy.import.col_sample': 'Sample',
    'academy.import.col_description': 'Description',
    'academy.import.required_yes': 'Yes',
    'academy.import.required_optional': 'Optional',
    'academy.import.dialog_close': 'Close',
    'academy.import.report_title': 'Import Report',
    'academy.import.report_processed': 'Processed',
    'academy.import.report_created': 'Created',
    'academy.import.report_updated': 'Updated',
    'academy.import.report_errors': 'Errors',
    'academy.import.report_errors_detail': 'Rejection details:',
    'academy.import.report_row_error': 'Row {0} ({1}): {2}',
    'academy.import.report_finish': 'Finish',
    'academy.import.msg_no_rows': 'The file does not contain any usable data rows.',
    'academy.import.msg_rows_detected': '{0} row(s) detected and analyzed.',
    'academy.import.msg_success': 'Import successful: {0} creations, {1} updates.',
    'academy.import.msg_warning': 'Import completed with {0} warning(s)/error(s).',
    'academy.import.required_missing': 'Required field "{0}" is missing.',

    'academy.export.page_title': 'Data Export',
    'academy.export.header_title': 'Agency Data Export',
    'academy.export.header_subtitle': 'Download all data for your connected agency in Microsoft Excel (.xlsx) or standard CSV formats.',
    'academy.export.refresh': 'Refresh',
    'academy.export.lines_count': '{0} rows',
    'academy.export.columns_count': '{0} columns included',
    'academy.export.view_columns': 'View columns',
    'academy.export.generating': 'Export generation in progress...',
    'academy.export.btn_excel': 'Excel (.xlsx)',
    'academy.export.btn_csv': 'CSV (.csv)',
    'academy.export.dialog_title': 'Exported columns: {0}',
    'academy.export.col_header': 'Column in export',
    'academy.export.col_type': 'Type',
    'academy.export.dialog_close': 'Close',
    'academy.export.msg_no_data': 'No data to export for "{0}" in this agency.',
    'academy.export.msg_success': 'Export of {0} row(s) completed successfully ({1}).',

    // Academy Entities - Titles & Descriptions - EN
    'academy.entity.rooms.title': 'Rooms & Resources',
    'academy.entity.rooms.desc': 'Classrooms, laboratories and educational equipment management',
    'academy.entity.teachers.title': 'Teachers & Trainers',
    'academy.entity.teachers.desc': 'Teaching faculty, external trainers and educational specialists',
    'academy.entity.students.title': 'Students & Learners',
    'academy.entity.students.desc': 'Enrolled student records, contact details and emergency contacts',
    'academy.entity.groups.title': 'Groups & Cohorts',
    'academy.entity.groups.desc': 'Class groups, cohorts and student promotion batches',
    'academy.entity.courses.title': 'Course Modules & Templates',
    'academy.entity.courses.desc': 'Training module catalog, hour volumes and pricing',

    // Column Labels (Fields) - EN
    'academy.field.ARM_ROOM_NUMBER.label': 'Room Number / Name',
    'academy.field.ARM_ROOM_NUMBER.desc': 'Unique room identifier or number',
    'academy.field.ARM_FLOOR.label': 'Floor / Building',
    'academy.field.ARM_FLOOR.desc': 'Location within the institution',
    'academy.field.ARM_CAPACITY.label': 'Capacity',
    'academy.field.ARM_CAPACITY.desc': 'Maximum number of seating places',
    'academy.field.ARM_EQUIPMENT.label': 'Equipment',
    'academy.field.ARM_EQUIPMENT.desc': 'Available equipment',
    'academy.field.ARM_STATUS.label': 'Status',
    'academy.field.ARM_STATUS.desc': 'Active, Under Maintenance or Archived',

    'academy.field.TCH_FULL_NAME.label': 'Full Name',
    'academy.field.TCH_FULL_NAME.desc': 'Teacher full name',
    'academy.field.TCH_EMAIL.label': 'Email Address',
    'academy.field.TCH_EMAIL.desc': 'Unique professional email',
    'academy.field.TCH_PHONE.label': 'Phone',
    'academy.field.TCH_PHONE.desc': 'Contact phone number',
    'academy.field.TCH_SPECIALIZATION.label': 'Specialization',
    'academy.field.TCH_SPECIALIZATION.desc': 'Subject or field of expertise',
    'academy.field.TCH_HOURLY_RATE.label': 'Hourly Rate (DZD)',
    'academy.field.TCH_HOURLY_RATE.desc': 'Base hourly compensation',
    'academy.field.TCH_BIO.label': 'Biography / Notes',
    'academy.field.TCH_BIO.desc': 'Profile and experience',

    'academy.field.STD_FIRST_NAME.label': 'First Name',
    'academy.field.STD_FIRST_NAME.desc': 'Student first name',
    'academy.field.STD_LAST_NAME.label': 'Last Name',
    'academy.field.STD_LAST_NAME.desc': 'Student last name',
    'academy.field.STD_EMAIL.label': 'Email Address',
    'academy.field.STD_EMAIL.desc': 'Student personal email (unique identifier)',
    'academy.field.STD_PHONE.label': 'Phone Number',
    'academy.field.STD_PHONE.desc': 'Mobile phone number',
    'academy.field.STD_DATE_OF_BIRTH.label': 'Date of Birth (YYYY-MM-DD)',
    'academy.field.STD_DATE_OF_BIRTH.desc': 'Format YYYY-MM-DD',
    'academy.field.STD_EMERGENCY_CONTACT.label': 'Emergency Contact',
    'academy.field.STD_EMERGENCY_CONTACT.desc': 'Person to contact in case of emergency',

    'academy.field.STD_CATEGORY.label': 'Category (adult / junior)',
    'academy.field.STD_CATEGORY.desc': 'Student category: adult or junior',
    'academy.field.STD_GENDER.label': 'Gender (male / female)',
    'academy.field.STD_GENDER.desc': 'Student gender: male or female',
    'academy.field.STD_ADDRESS.label': 'Residential Address',
    'academy.field.STD_ADDRESS.desc': 'Full residential address',
    'academy.field.STD_REGISTRATION_STATUS.label': 'Registration Status',
    'academy.field.STD_REGISTRATION_STATUS.desc': 'Active, Pre-Registered or Refused',
    'academy.field.STD_FINANCIAL_HOLD.label': 'Financial Hold (1/0)',
    'academy.field.STD_FINANCIAL_HOLD.desc': '1 = Blocked for debt, 0 = Good standing',
    'academy.field.STD_PROFESSION.label': 'Profession (Adult)',
    'academy.field.STD_PROFESSION.desc': 'Job or professional title',
    'academy.field.STD_SCHOOL_LEVEL.label': 'School Level (Junior)',
    'academy.field.STD_SCHOOL_LEVEL.desc': 'Preschool, Primary, Middle, High School, University, Other',
    'academy.field.STD_BLOOD_GROUP.label': 'Blood Group',
    'academy.field.STD_BLOOD_GROUP.desc': 'A+, A-, B+, B-, AB+, AB-, O+, O-',
    'academy.field.STD_HAS_HEALTH_ISSUES.label': 'Health Issues (1/0)',
    'academy.field.STD_HAS_HEALTH_ISSUES.desc': '1 = Yes, 0 = No',
    'academy.field.STD_HEALTH_CONDITIONS.label': 'Health Conditions',
    'academy.field.STD_HEALTH_CONDITIONS.desc': 'Known conditions or medical issues',
    'academy.field.STD_HEALTH_OTHER_DETAIL.label': 'Other Health Details',
    'academy.field.STD_HEALTH_OTHER_DETAIL.desc': 'Additional health notes',
    'academy.field.STD_HAS_ALLERGIES.label': 'Allergies (1/0)',
    'academy.field.STD_HAS_ALLERGIES.desc': '1 = Yes, 0 = No',
    'academy.field.STD_ALLERGY_DETAILS.label': 'Allergy Details',
    'academy.field.STD_ALLERGY_DETAILS.desc': 'Specific allergens',
    'academy.field.STD_IMAGE_RIGHT.label': 'Image Rights (1/0)',
    'academy.field.STD_IMAGE_RIGHT.desc': '1 = Granted, 0 = Denied',
    'academy.field.STD_GUARDIAN_FIRST_NAME.label': 'Guardian First Name (Junior)',
    'academy.field.STD_GUARDIAN_FIRST_NAME.desc': 'Parent or guardian first name',
    'academy.field.STD_GUARDIAN_LAST_NAME.label': 'Guardian Last Name (Junior)',
    'academy.field.STD_GUARDIAN_LAST_NAME.desc': 'Parent or guardian last name',
    'academy.field.STD_GUARDIAN_RELATION.label': 'Guardian Relation (Junior)',
    'academy.field.STD_GUARDIAN_RELATION.desc': 'Father, Mother, Legal guardian, Other',
    'academy.field.STD_GUARDIAN_PHONE.label': 'Guardian Phone',
    'academy.field.STD_GUARDIAN_PHONE.desc': 'Parent or guardian phone number',
    'academy.field.STD_GUARDIAN_EMAIL.label': 'Guardian Email',
    'academy.field.STD_GUARDIAN_EMAIL.desc': 'Parent or guardian email',
    'academy.field.STD_AUTHORIZED_PICKUP.label': 'Authorized Pickups',
    'academy.field.STD_AUTHORIZED_PICKUP.desc': 'Persons authorized to pick up student',
    'academy.field.STD_TIME_PREFERENCE.label': 'Time Preference',
    'academy.field.STD_TIME_PREFERENCE.desc': 'Morning, Afternoon, Evening, Weekend',

    'academy.field.GRP_NAME.label': 'Group / Cohort Name',
    'academy.field.GRP_NAME.desc': 'Group identifier or name',
    'academy.field.GRP_MAX_CAPACITY.label': 'Maximum Capacity',
    'academy.field.GRP_MAX_CAPACITY.desc': 'Maximum number of students in the group',
    'academy.field.GRP_STATUS.label': 'Status',
    'academy.field.GRP_STATUS.desc': 'Upcoming, Ongoing or Completed',

    'academy.field.CRS_TITLE.label': 'Course / Module Title',
    'academy.field.CRS_TITLE.desc': 'Training title',
    'academy.field.CRS_TOTAL_HOURS.label': 'Hour Volume (Hours)',
    'academy.field.CRS_TOTAL_HOURS.desc': 'Total training duration in hours',
    'academy.field.CRS_PRICE.label': 'Reference Price (DZD)',
    'academy.field.CRS_PRICE.desc': 'Reference price',
    'academy.field.CRS_DESCRIPTION.label': 'Program Description',
    'academy.field.CRS_DESCRIPTION.desc': 'Goals and targeted skills',

    // Data types - EN
    'academy.dataType.string': 'string',
    'academy.dataType.int': 'int',
    'academy.dataType.decimal': 'decimal',
    'academy.dataType.date': 'date',
    'academy.dataType.boolean': 'boolean',

    // boubaker 23/08/2026 - Academy print menu
    'academy.menu.prints': 'Print',
    'academy.menu.prints.planning': 'Planning',
    'academy.menu.prints.invoices': 'Invoices',
    'academy.menu.prints.receipts': 'Payment Receipts',
    'academy.menu.prints.daily_cash': 'Daily Cash Balance Report',
    'academy.menu.prints.hub': 'Editions Center',
    'academy.menu.prints.group_documents': 'Documents & Certificates',
    'academy.menu.prints.group_lists': 'Lists & Attendance',
    'academy.menu.prints.group_correspondence': 'Correspondence & Medical',
    'academy.menu.prints.group_finance': 'Finance & Planning',
    'academy.menu.prints.attendance_sheet': 'Attendance Sheet',
    'academy.menu.prints.school_certificate': 'School Certificate',
    'academy.menu.prints.registration_form': 'Registration Form',
    'academy.menu.prints.students_list': 'Students List',
    'academy.menu.prints.information_sheet': 'Information Sheet',
    'academy.menu.prints.student_cards': 'Student Badge',
    'academy.menu.prints.student_badge': 'Student Badge',
    'academy.menu.prints.cancellation_certificate': 'Cancellation Certificate',
    'academy.menu.prints.conduct_certificate': 'Good Conduct Certificate',
    'academy.menu.prints.parent_convocation': 'Parent Convocation',
    'academy.menu.prints.emergency_form': 'Emergency Form',
    'academy.financial.table.total_amount': 'Total Amount',

    // boubaker 23/08/2026 - Academy print pages
    'academy.print.planning': 'Sessions Planning',
    'academy.print.invoices': 'Invoices Print',
    'academy.print.receipts': 'Payment Receipts Print',
    'academy.print.daily_cash': 'Daily Cash Balance Report',
    'academy.print.status_all': 'All statuses',
    'academy.print.method_all': 'All payment methods',
    'academy.print.col_invoice': 'Invoice No.',
    'academy.print.col_student': 'Student',
    'academy.print.col_issued': 'Issued on',
    'academy.print.col_due': 'Due date',
    'academy.print.col_total': 'Total',
    'academy.print.col_paid': 'Paid',
    'academy.print.col_status': 'Status',
    'academy.print.col_receipt': 'Receipt No.',
    'academy.print.col_date': 'Date',
    'academy.print.col_method': 'Method',
    'academy.print.col_amount': 'Amount',
    'academy.print.total': 'TOTAL',
    'academy.print.receipts_count': 'Number of receipts',
    'academy.print.cash': 'Cash',
    'academy.print.card': 'Card',
    'academy.print.transfer': 'Transfer',
    'academy.print.no_data': 'No data. Adjust the filters then click "Load".',
    'academy.print.status.Paid': 'Paid',
    'academy.print.status.Unpaid': 'Unpaid',
    'academy.print.status.Partial': 'Partial',

    // boubaker 25/08/2026 - Indicateur: mail trace
    'academy.menu.indicateur': 'Indicators',
    'academy.menu.indicateur.mail_log': 'Mail Trace',
    'academy.indicateur.mail_log': 'Mail Send Trace',
    'academy.indicateur.col_date': 'Date',
    'academy.indicateur.col_to': 'Recipient',
    'academy.indicateur.col_subject': 'Subject / Body',
    'academy.indicateur.col_status': 'Status',
    'academy.indicateur.col_smtp': 'SMTP Server',
    'academy.indicateur.col_action': 'Action',
    'academy.indicateur.view_body': 'View mail body',
    'academy.indicateur.mail.status': 'Status',
    'academy.indicateur.mail.to_filter': 'Filter by email...',
    'academy.indicateur.status.sent': 'Sent',
    'academy.indicateur.status.failed': 'Failed',
    'academy.indicateur.status.pending': 'Pending',
    'academy.indicateur.col_type': 'Type',
    'academy.indicateur.mail.type_filter': 'Email Type',
    'academy.indicateur.mail_type.all': 'All Types',
    'academy.indicateur.mail_type.debt_reminder': 'Debt Reminder',
    'academy.indicateur.mail_type.auth_otp': 'OTP / Login',
    'academy.indicateur.mail_type.change_password': 'Password Change',
    'academy.indicateur.mail_type.reset_password': 'Password Reset',
    'academy.indicateur.mail_type.general': 'General',

    // Academy Scheduler
    'academy.scheduler.title': 'Planning Matrix (Day, Week, Month)',
    'academy.scheduler.view_day': 'Day View',
    'academy.scheduler.view_week': 'Week View',
    'academy.scheduler.view_month': 'Month View',
    'academy.scheduler.group_by': 'Group by:',
    'academy.scheduler.group_by_room': 'Rooms & Facilities',
    'academy.scheduler.group_by_group': 'Groups & Cohorts',
    'academy.scheduler.group_by_teacher': 'Teachers',
    'academy.scheduler.select_date': 'Date',
    'academy.scheduler.select_month': 'Month & Year',
    'academy.scheduler.today': 'Today',
    'academy.scheduler.book_slot': '+ Book Slot',
    'academy.scheduler.schedule_btn': 'Schedule Session',
    'academy.scheduler.filters': 'Filters:',
    'academy.scheduler.all_rooms': 'All Rooms',
    'academy.scheduler.all_teachers': 'All Teachers',
    'academy.scheduler.all_groups': 'All Groups',
    'academy.scheduler.session_details': 'Session Details',
    'academy.scheduler.cancel_session': 'Cancel Session',
    'academy.scheduler.confirm_cancel': 'Are you sure you want to cancel this session?',
    'academy.scheduler.msg_session_cancelled': 'Session cancelled and deleted successfully.',

    // boubaker 22/08/2026 - planner tabs + print + drag&drop
    'academy.scheduler.tab_new': 'New planning',
    'academy.scheduler.load': 'Load',
    'academy.scheduler.start_date': 'Start Date',
    'academy.scheduler.end_date': 'End Date',
    'academy.scheduler.not_loaded': 'Set your filters then click "Load" to display the planning.',
    'academy.scheduler.print': 'Print',
    'academy.scheduler.print_title': 'Planning Printout',
    'academy.scheduler.edit_session': 'Edit',
    'academy.scheduler.save_changes': 'Save Changes',
    'academy.scheduler.session_updated': 'Session updated successfully.',
    'academy.scheduler.drag_hint': 'Tip: drag & drop a session to change its day (week/month view) or time (day view).',
    'academy.scheduler.print.col_day': 'Day',
    'academy.scheduler.print.col_start': 'Start',
    'academy.scheduler.print.col_end': 'End',
    'academy.scheduler.print.col_room': 'Room',
    'academy.scheduler.print.col_teacher': 'Teacher',
    'academy.scheduler.print.col_group': 'Group',
    'academy.scheduler.print.month_label': 'Month',

    // Absence & Session status management - EN
    'academy.scheduler.session_status': 'Session Status',
    'academy.scheduler.status.scheduled': 'Scheduled',
    'academy.scheduler.status.completed': 'Completed',
    'academy.scheduler.status.cancelled': 'Cancelled',
    'academy.scheduler.status.postponed': 'Postponed',
    'academy.scheduler.status_reason': 'Reason / Justification',
    'academy.scheduler.postponed_date': 'Postponed Date',
    'academy.scheduler.postponed_time': 'Postponed Time',
    'academy.scheduler.teacher_absent': 'Teacher Absent',
    'academy.scheduler.postponed_to': 'Postponed to {0} {1}',
    'academy.teachers.absence_requests': 'Absence Requests',
    'academy.teachers.absence_requests_title': 'Teacher Absence Requests',
    'academy.teachers.filter_all': 'All',
    'academy.teachers.filter_pending': 'Pending',
    'academy.teachers.filter_approved': 'Approved',
    'academy.teachers.filter_rejected': 'Rejected',
    'academy.teachers.request_date': 'Request Date',
    'academy.teachers.absence_reason': 'Absence Reason',
    'academy.teachers.attached_document': 'Supporting Document',
    'academy.teachers.view_document': 'View Document',
    'academy.teachers.admin_comment': 'Admin Comment',
    'academy.teachers.admin_comment_placeholder': 'Enter a comment or instructions for the teacher...',
    'academy.teachers.approve_absence': 'Approve Absence',
    'academy.teachers.reject_absence': 'Reject Request',
    'academy.teachers.update_session_prompt': 'Update linked session to Cancelled',
    'academy.teachers.absence_approved_success': 'Absence request approved successfully.',
    'academy.teachers.absence_rejected_success': 'Absence request has been rejected.',
    'academy.teachers.no_absence_requests': 'No absence requests found.',

    // Academy Theoretical Planning - EN
    'academy.planning_theorique.title': 'Theoretical Planning Management',
    'academy.planning_theorique.subtitle': 'Definition of master schedules by room, number of weeks and day type',
    'academy.planning_theorique.new': 'New Theoretical Schedule',
    'academy.planning_theorique.edit': 'Edit Theoretical Schedule',
    'academy.planning_theorique.confirmDelete': 'Are you sure you want to delete this theoretical schedule? Associated slots will also be removed.',
    'academy.planning_theorique.col_room': 'Room',
    'academy.planning_theorique.col_num_week': 'Nbr Weeks',
    'academy.planning_theorique.col_start_date': 'Start Date',
    'academy.planning_theorique.col_end_date': 'End Date',
    'academy.planning_theorique.col_day_type': 'Day Type',
    'academy.planning_theorique.day_normal': 'Normal',
    'academy.planning_theorique.day_holiday': 'Holiday',
    'academy.planning_theorique.section_general': 'General Information',
    'academy.planning_theorique.field_room': 'Classroom',
    'academy.planning_theorique.field_num_week': 'Cycle number of weeks',
    'academy.planning_theorique.field_start_date': 'Validity start date',
    'academy.planning_theorique.field_end_date': 'Validity end date (optional)',
    'academy.planning_theorique.field_day_type': 'Day type',
    'academy.planning_theorique.room_required': 'Please select a room.',
    'academy.planning_theorique.num_week_min': 'Number of weeks must be at least 1.',
    'academy.planning_theorique.start_date_required': 'Start date is required.',
    'academy.planning_theorique.end_before_start': 'End date cannot be earlier than start date.',
    'academy.planning_theorique.open_matrix': 'View Matrix',
    'academy.planning_theorique.empty': 'No theoretical schedule configured.',

    'academy.planning_theorique.manage_title': 'Theoretical Planning Management',
    'academy.planning_theorique.generation_title': 'Real Schedule Generation',
    'academy.planning_theorique.deletion_title': 'Schedule Sessions Deletion',
    'academy.planning_theorique.new_title': 'New Theoretical Schedule',
    'academy.planning_theorique.edit_title': 'Edit Theoretical Schedule',
    'academy.planning_theorique.form_subtitle': 'Define the classroom, week cycle and validity period of the template',
    'academy.planning_theorique.new_button': 'New Schedule',
    'academy.planning_theorique.generate_button': 'Generate Schedule',
    'academy.planning_theorique.delete_button': 'Delete Selected Sessions',

    'academy.planning_theorique.room': 'Room',
    'academy.planning_theorique.nbr_weeks': 'Number of Weeks',
    'academy.planning_theorique.start_date': 'Start Date',
    'academy.planning_theorique.end_date': 'End Date',
    'academy.planning_theorique.day_type': 'Day Type',
    'academy.planning_theorique.type_normal': 'Normal',
    'academy.planning_theorique.type_holiday': 'Holiday',
    'academy.planning_theorique.vacs_count': 'Slots Count',
    'academy.planning_theorique.week': 'Week',
    'academy.planning_theorique.weeks': 'Weeks',
    'academy.planning_theorique.indefinite': 'Indefinite',
    'academy.planning_theorique.period': 'Period',
    'academy.planning_theorique.date': 'Date',
    'academy.planning_theorique.hours': 'Hours',
    'academy.planning_theorique.status': 'Status',
    'academy.planning_theorique.group': 'Group',
    'academy.planning_theorique.teacher': 'Teacher',
    'academy.planning_theorique.none': 'None',
    'academy.planning_theorique.search_placeholder': 'Search...',

    'academy.planning_theorique.room_helper': 'Classroom assigned to this schedule template',
    'academy.planning_theorique.weeks_helper': 'Number of weeks in the cycle (e.g. 1 for weekly recurring)',
    'academy.planning_theorique.end_date_helper': 'Leave blank if theoretical schedule has open-ended validity',

    'academy.planning_theorique.msg_created': 'Theoretical schedule created successfully.',
    'academy.planning_theorique.msg_updated': 'Theoretical schedule updated successfully.',
    'academy.planning_theorique.msg_deleted': 'Theoretical schedule deleted successfully.',
    'academy.planning_theorique.no_data': 'No theoretical schedules configured yet.',

    'academy.planning_theorique.validation.room_required': 'Please select a room.',
    'academy.planning_theorique.validation.weeks_required': 'Number of weeks must be at least 1.',
    'academy.planning_theorique.validation.start_date_required': 'Start date is required.',
    'academy.planning_theorique.validation.invalid_date_range': 'End date cannot be earlier than start date.',
    'academy.planning_theorique.validation.hours_required': 'Start time and end time are required.',
    'academy.planning_theorique.validation.invalid_time_range': 'End time must be after start time.',
    'academy.planning_theorique.validation.dates_required': 'Please provide both start and end dates.',

    'academy.planning_theorique.matrix_title': 'Theoretical Planning',
    'academy.planning_theorique.matrix_subtitle': 'Standard weekly grid of slots',
    'academy.planning_theorique.matrix_config_btn': 'Display Settings',
    'academy.planning_theorique.filter_popup_title': 'Select Theoretical Schedule',
    'academy.planning_theorique.select_theo': 'Choose a theoretical schedule',
    'academy.planning_theorique.select_theo_helper': 'Select the theoretical schedule by room and period to load its matrix',
    'academy.planning_theorique.apply_filter': 'Load Schedule',
    'academy.planning_theorique.filter_required': 'Please select a theoretical schedule.',
    'academy.planning_theorique.slot_new': 'New Master Slot',
    'academy.planning_theorique.slot_edit': 'Edit Slot',
    'academy.planning_theorique.slot_group': 'Group',
    'academy.planning_theorique.slot_teacher': 'Teacher',
    'academy.planning_theorique.slot_start_time': 'Start Time',
    'academy.planning_theorique.slot_end_time': 'End Time',
    'academy.planning_theorique.slot_confirm_delete': 'Delete this theoretical slot?',
    'academy.planning_theorique.matrix_no_data': 'Please select a theoretical schedule to display its grid.',
    'academy.planning_theorique.week_label': 'Week {0}',
    'academy.planning_theorique.filter_btn': 'Display Filters',
    'academy.planning_theorique.refresh': 'Refresh',
    'academy.planning_theorique.filter_dialog_title': 'Filter Theoretical Matrix',
    'academy.planning_theorique.filter_dialog_desc': 'Select date range and rooms to load corresponding templates.',
    'academy.planning_theorique.filter_rooms': 'Rooms',
    'academy.planning_theorique.select_all': 'Select All',
    'academy.planning_theorique.deselect_all': 'Deselect All',
    'academy.planning_theorique.load_btn': 'Load',
    'academy.planning_theorique.not_loaded_title': 'No Data Displayed',
    'academy.planning_theorique.not_loaded_desc': 'Please apply filters to view the theoretical schedule matrix.',
    'academy.planning_theorique.open_filters': 'Open Filters',
    'academy.planning_theorique.add_vac': 'Add Slot',
    'academy.planning_theorique.no_matrix_data': 'No theoretical slots for this schedule.',
    'academy.planning_theorique.new_vac_title': 'New Theoretical Slot',
    'academy.planning_theorique.edit_vac_title': 'Edit Theoretical Slot',
    'academy.planning_theorique.day_num': 'Cycle Day',
    'academy.planning_theorique.start_time': 'Start Time',
    'academy.planning_theorique.end_time': 'End Time',
    'academy.planning_theorique.msg_vac_created': 'Theoretical slot added successfully.',
    'academy.planning_theorique.msg_vac_updated': 'Theoretical slot updated successfully.',
    'academy.planning_theorique.msg_vac_deleted': 'Theoretical slot deleted successfully.',

    'academy.planning_theorique.gen_title': 'Real Planning Generation',
    'academy.planning_theorique.gen_subtitle': 'Automatically instantiate real class sessions from theoretical schedules',
    'academy.planning_theorique.gen_step1': '1. Select Theoretical Schedules',
    'academy.planning_theorique.gen_step2': '2. Define Target Period & Parameters',
    'academy.planning_theorique.gen_target_start': 'Generation start date',
    'academy.planning_theorique.gen_target_end': 'Generation end date',
    'academy.planning_theorique.gen_btn_run': 'Run Generation',
    'academy.planning_theorique.gen_running': 'Generating sessions...',
    'academy.planning_theorique.gen_select_at_least_one': 'Please select at least one theoretical schedule.',
    'academy.planning_theorique.gen_dates_required': 'Please provide both start date and end date.',
    'academy.planning_theorique.gen_confirm': 'Do you want to generate sessions for the selected schedules between {0} and {1}?',
    'academy.planning_theorique.gen_success': '{0} session(s) generated successfully!',
    'academy.planning_theorique.target_start_date': 'Target Start Date',
    'academy.planning_theorique.target_end_date': 'Target End Date',
    'academy.planning_theorique.all_rooms': 'All Rooms',
    'academy.planning_theorique.room_filter': 'Filter by Room',
    'academy.planning_theorique.all_selected': 'All schedules selected',
    'academy.planning_theorique.plannings_selected': 'schedule(s) selected',
    'academy.planning_theorique.select_at_least_one': 'Please select at least one theoretical schedule to generate.',
    'academy.planning_theorique.no_data_to_generate': 'No theoretical schedule available',
    'academy.planning_theorique.no_data_to_generate_desc': 'No active theoretical schedules found for the specified period.',
    'academy.planning_theorique.generation_success_title': 'Schedule generation completed successfully!',
    'academy.planning_theorique.generation_success_subtitle': '{0} session(s) created for the period from {1} to {2}.',
    'academy.planning_theorique.generation_success_msg': '{0} session(s) generated successfully.',
    'academy.planning_theorique.generation_success_with_skipped': '{0} session(s) generated successfully ({1} conflicting slot(s) skipped).',
    'academy.planning_theorique.sessions_generated': 'session(s) generated',
    'academy.planning_theorique.conflicts_skipped': 'conflicting slot(s) skipped',
    'academy.planning_theorique.view_scheduler': 'View Schedule Matrix',

    'academy.planning_theorique.del_title': 'Planning Deletion',
    'academy.planning_theorique.del_subtitle': 'Filter and batch delete real sessions and slots',
    'academy.planning_theorique.del_filter_title': 'Session filtering criteria for deletion',
    'academy.planning_theorique.del_date_from': 'From',
    'academy.planning_theorique.del_date_to': 'To',
    'academy.planning_theorique.del_all_rooms': 'All rooms',
    'academy.planning_theorique.del_all_teachers': 'All teachers',
    'academy.planning_theorique.del_all_groups': 'All groups',
    'academy.planning_theorique.del_btn_search': 'Search Sessions',
    'academy.planning_theorique.del_btn_delete': 'Delete Selection',
    'academy.planning_theorique.del_confirm': 'WARNING: Are you sure you want to permanently delete the {0} selected session(s)? This action cannot be undone.',
    'academy.planning_theorique.del_success': '{0} session(s) deleted successfully.',
    'academy.planning_theorique.del_no_sessions': 'No sessions found matching these criteria.',
    'academy.planning_theorique.del_select_to_delete': 'Please select at least one session to delete.',
    'academy.planning_theorique.all_groups': 'All Groups',
    'academy.planning_theorique.group_filter': 'Filter by Group',
    'academy.planning_theorique.all_teachers': 'All Teachers',
    'academy.planning_theorique.teacher_filter': 'Filter by Teacher',
    'academy.planning_theorique.sessions_selected': 'session(s) selected',
    'academy.planning_theorique.select_at_least_one_session': 'Please select at least one session to delete.',
    'academy.planning_theorique.no_sessions_found': 'No sessions found',
    'academy.planning_theorique.no_sessions_found_desc': 'No schedule sessions match the selected filter criteria.',
    'academy.planning_theorique.deletion_success_title': 'Deletion completed successfully!',
    'academy.planning_theorique.deletion_success_subtitle': '{0} session(s) permanently deleted from schedule.',
    'academy.planning_theorique.deletion_success_msg': '{0} session(s) deleted successfully.',
    'academy.planning_theorique.sessions_deleted': 'session(s) deleted',

    // Common Aliases - EN
    'commun.error': 'Error',
    'commun.warning': 'Warning',
    'commun.success': 'Success',
    'commun.confirmation': 'Confirmation',
    'commun.cancel': 'Cancel',
    'commun.save': 'Save',
    'commun.edit': 'Edit',
    'commun.delete': 'Delete',
    'commun.back_to_list': 'Back to List',
    'commun.view_grid': 'Grid View',
    'commun.view_table': 'Table View',

    // Academy Planning Controls
    'academy.planning_controls.title': 'Planning Controls & Validation Rules',
    'academy.planning_controls.subtitle': 'Configure validation rules applied when creating, modifying, and canceling sessions.',
    'academy.planning_controls.add_new': 'New Control Rule',
    'academy.planning_controls.active_count': 'Active Controls',
    'academy.planning_controls.blocking_count': 'Blocking Rules (Reject)',
    'academy.planning_controls.warning_count': 'Warning Rules',
    'academy.planning_controls.rules_info': 'Control Rules Logic:',
    'academy.planning_controls.code': 'Control Code',
    'academy.planning_controls.lib': 'Control Label',
    'academy.planning_controls.description': 'Description & Impact',
    'academy.planning_controls.active': 'Active',
    'academy.planning_controls.blocking': 'Blocking (Block)',
    'academy.planning_controls.status': 'Status',
    'academy.planning_controls.status_disabled': 'Disabled',
    'academy.planning_controls.status_blocking': 'Blocking',
    'academy.planning_controls.status_warning': 'Warning',
    'academy.planning_controls.modal_title_add': 'New Planning Control Rule',
    'academy.planning_controls.modal_title_edit': 'Edit Planning Control',
    'academy.planning_controls.confirm_delete': 'Are you sure you want to delete this control rule?',
    'academy.planning_controls.back_to_list': 'Back to List',
    'academy.planning_controls.msg_active_updated': 'Control {code} updated: Active = {active}',
    'academy.planning_controls.msg_blocking_updated': 'Control {code} updated: Blocking = {blocking}',
    'academy.planning_controls.msg_saved': 'Planning control saved successfully.',
    'academy.planning_controls.msg_deleted': 'Control {code} deleted.',
    'academy.planning_controls.validation.code_required': 'Control code is required.',
    'academy.planning_controls.validation.lib_required': 'Control label is required.',
    'academy.planning_controls.view_table': 'Table View',
    'academy.planning_controls.view_grid': 'Grid View',
    'academy.planning_controls.no_description': 'No description specified.',
    'academy.planning_controls.form_title_edit': 'Edit Control ({code})',
    'academy.planning_controls.form_title_new': 'Define New Validation Control',
    'academy.planning_controls.code_helper': 'Unique rule code identifier (e.g., CTL_CUSTOM_101)',
    'academy.planning_controls.lib_helper': 'Clear label displayed during alerts or checks',
    'academy.planning_controls.desc_helper': 'Explanatory description of this control impact',
    'academy.planning_controls.active_desc': 'Enable or disable automatic checking for this rule',
    'academy.planning_controls.blocking_desc': 'If enabled, action is strictly blocked with rejection. Otherwise, a warning is shown.',
    'academy.common.yes': 'Yes',
    'academy.common.no': 'No',

    // Academy Financial Management & Debt Control
    'academy.financial.title': 'Financial Management & Debt Control',
    'academy.financial.subtitle': 'Track invoices, student payments, and daily cash balances.',
    'academy.financial.daily_report': 'Daily Cash Balance (Report)',
    'academy.financial.receive_payment': 'Receive Payment',
    'academy.financial.kpi.debt': 'Total Outstanding Debt',
    'academy.financial.kpi.monthly': 'Current Month Revenue',
    'academy.financial.kpi.today': 'Today\'s Cash Collection',
    'academy.financial.tab.debts': 'Debts & Payment Schedule',
    'academy.financial.tab.invoices': 'Issued Invoices Ledger',
    'academy.financial.tab.payments': 'Received Payments History',
    'academy.financial.debtors_list': 'Debtor Students List',
    'academy.financial.send_bulk_reminder': 'Send Bulk Debt Email Reminder ({0})',
    'academy.financial.bulk_reminder_sending': 'Sending debt reminder emails...',
    'academy.financial.bulk_reminder_result': '{0} debt reminder email(s) sent successfully. {1} skipped (no email).',
    'academy.financial.bulk_reminder_no_selection': 'Please select at least one debtor student.',
    'academy.financial.rest_to_pay': 'Remaining Due',
    'academy.financial.gross_amount': 'Gross Amount',
    'academy.financial.applied_discount': 'Applied Discount',
    'academy.financial.net_invoiced': 'Net Invoiced',
    'academy.financial.amount_paid': 'Amount Paid',
    'academy.financial.invoice_details': 'Official Invoice Details',
    'academy.financial.receipt_details': 'Official Payment Receipt',
    'academy.financial.print_invoice': 'Print Invoice',
    'academy.financial.print_receipt': 'Print Receipt',
    'academy.financial.delete_payment_confirm': 'Are you sure you want to delete payment {0} of {1} {2} for student {3}?',
    'academy.financial.msg_payment_deleted': 'Payment {0} deleted successfully. Invoice balance adjusted.',
    'academy.financial.msg_bulk_reminder_sent': 'Success: Debt reminder emails sent to {0} student(s)!',
    'academy.financial.no_discount': 'No discount',
    'academy.financial.manual_discount_info': 'Manual Discounts Affectation: Manual discounts assigned on the Discounts page apply directly onto generated invoices.',
    'academy.financial.modal.receive_title': 'Record Student Payment',
    'academy.financial.modal.select_student': 'Select Student',
    'academy.financial.modal.amount': 'Payment Amount ({0})',
    'academy.financial.modal.method': 'Payment Method',
    'academy.financial.modal.cash': 'Cash',
    'academy.financial.modal.card': 'POS / Credit Card',
    'academy.financial.modal.transfer': 'Bank Transfer / Check',
    'academy.financial.modal.fifo_note': 'FIFO Rule: The payment will be automatically applied to the student\'s oldest unpaid invoice.',
    'academy.financial.modal.process': 'Process Payment',
    'academy.financial.report.title': 'Daily Cash Balance Report',
    'academy.financial.report.date': 'Daily Report for: {0}',
    'academy.financial.report.cash': 'Total Cash',
    'academy.financial.report.card': 'Total Card / POS',
    'academy.financial.report.transfer': 'Total Transfer / Check',
    'academy.financial.report.total': 'Grand Total Collected: {0}',
    'academy.financial.report.print': 'Print Report',
    'academy.financial.table.student': 'Student',
    'academy.financial.table.invoiced': 'Total Invoiced',
    'academy.financial.table.paid': 'Already Paid',
    'academy.financial.table.badge': 'Debt Status',
    'academy.financial.table.action': 'Action',
    'academy.financial.table.invoice_num': 'Invoice #',
    'academy.financial.table.receipt_num': 'Receipt #',
    'academy.financial.table.due_date': 'Due Date',
    'academy.financial.table.status': 'Status',
    'academy.financial.table.amount': 'Amount',
    'academy.financial.table.method': 'Method',
    'academy.financial.table.date': 'Date & Time',
    'academy.financial.success.overpayment': 'Payment recorded successfully. An overpayment of {0} was added to balance.',
    'academy.financial.success.allocated': 'Payment recorded and allocated to invoices successfully.',
    'academy.financial.error.no_invoices': 'This student has no pending unpaid invoices.',
    'academy.financial.error.payment': 'Failed to record payment.',
    'academy.financial.phone_label': 'Phone:',
    'academy.financial.email_label': 'Email:',
    'academy.financial.official': 'OFFICIAL',
    'academy.financial.invoice_no': 'INVOICE NO.',
    'academy.financial.receipt_no': 'RECEIPT NO.',
    'academy.financial.due_date_label': 'Due Date',
    'academy.financial.status_label': 'Status',
    'academy.financial.date_label': 'Date',
    'academy.financial.mode_label': 'Payment Method',
    'academy.financial.student_group_info': 'Student & Group Information',
    'academy.financial.student_info': 'Student Information',
    'academy.financial.full_name': 'Full Name:',
    'academy.financial.course_group_label': 'Course / Group:',
    'academy.financial.designation': 'Designation',
    'academy.financial.description': 'Description',
    'academy.financial.amount': 'Amount',
    'academy.financial.gross_amount_course': 'Course Original Amount',
    'academy.financial.payment_amount': 'Amount Paid',
    'academy.financial.total_paid_today': 'Total Paid to Date',
    'academy.financial.stamp_signature': 'Stamp & Signature',
    'academy.financial.direction': 'Management',
    'academy.financial.daily_report_sub': 'Daily Cash Summary',
    'academy.financial.delete_payment_tooltip': 'Delete this payment',

    // boubaker 23/08/2026 - invoice deletion (server controls: payments / group enrollment)
    'academy.financial.delete_invoice_tooltip': 'Delete this invoice',
    'academy.financial.error.delete_invoice': 'Failed to delete the invoice.',
    'academy.financial.delete_invoice_confirm': 'Are you sure you want to delete invoice {0} of student {1}?',
    'academy.financial.msg_invoice_deleted': 'Invoice {0} deleted successfully.',
    'academy.financial.error.delete_payment': 'Failed to delete payment.',
    'academy.financial.status.paid': 'Paid',
    'academy.financial.status.partial': 'Partial',
    'academy.financial.status.unpaid': 'Unpaid',
    'academy.financial.method.cash': 'Cash',
    'academy.financial.method.card': 'POS / Credit Card',
    'academy.financial.method.transfer': 'Bank Transfer / Check',
    'academy.financial.invoice_type_title': 'INVOICE',
    'academy.financial.receipt_type_title': 'PAYMENT RECEIPT',
    'academy.financial.report_type_title': 'DAILY CASH REPORT',
    'academy.financial.issued_by': 'Issued by:',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.error': 'Error',

    // Academy Parameters
    'academy.parameters.pageTitle': 'Academy Settings',
    'academy.parameters.matrix_title': 'Planning Matrix Parameters (Slots & Display)',
    'academy.parameters.matrix_desc': 'Define time range, slot duration, first day of week, and default view.',
    'academy.parameters.start_time': 'Start Time',
    'academy.parameters.end_time': 'End Time',
    'academy.parameters.slot_duration': 'Slot Duration (min)',
    'academy.parameters.first_day': 'First Day of Week',
    'academy.parameters.default_view': 'Default Planning View',
    'academy.parameters.weekend_days': 'Weekend Days',
    'academy.parameters.weekend_color': 'Weekend Background Color',
    'academy.parameters.numbering_invoice_title': 'Invoice Numbering',
    'academy.parameters.numbering_invoice_desc': 'Customize format, prefix, agency code, date, zero padding, reset period, and element order for invoice number generation.',
    'academy.parameters.numbering_payment_title': 'Payment Receipt Numbering',
    'academy.parameters.numbering_payment_desc': 'Customize format, prefix, agency code, date, zero padding, reset period, and element order for payment receipt numbers.',
    'academy.parameters.numbering_prefix': 'Prefix',
    'academy.parameters.numbering_agency_length': 'Agency Code Chars',
    'academy.parameters.numbering_agency_length_helper': 'Max agency code chars (0 = full)',
    'academy.parameters.numbering_date_format': 'Date Format',
    'academy.parameters.numbering_padding': 'Number Padding (Zeros)',
    'academy.parameters.numbering_suffix': 'Suffix / Post-fix',
    'academy.parameters.numbering_separator': 'Separator',
    'academy.parameters.numbering_order': 'Element Order',
    'academy.parameters.numbering_reset_period': 'Auto-Reset Period',
    'academy.parameters.numbering_next_counter': 'Next Counter',
    'academy.parameters.numbering_reset_btn': 'Reset to 1',
    'academy.parameters.numbering_reset_confirm': 'Are you sure you want to reset the counter to 1?',
    'academy.parameters.numbering_preview': 'Live Preview of Next Number:',
    'academy.parameters.numbering_reset_success': 'Counter successfully reset to 1.',
    'academy.parameters.order_prefix_agency_date_number_suffix': 'Prefix - Agency Code - Date - Number - Suffix',
    'academy.parameters.order_prefix_date_number_suffix': 'Prefix - Date - Number - Suffix',
    'academy.parameters.order_prefix_agency_number_date': 'Prefix - Agency Code - Number - Date',
    'academy.parameters.order_prefix_number_date': 'Prefix - Number - Date',
    'academy.parameters.order_date_prefix_number': 'Date - Prefix - Number',
    'academy.parameters.order_prefix_number_suffix': 'Prefix - Number - Suffix',
    'academy.parameters.order_prefix_agency_number_suffix': 'Prefix - Agency Code - Number - Suffix',
    'academy.parameters.sep_dash': 'Dash -',
    'academy.parameters.sep_slash': 'Slash /',
    'academy.parameters.sep_dot': 'Dot .',
    'academy.parameters.sep_underscore': 'Underscore _',
    'academy.parameters.sep_none': 'None (No separator)',
    'academy.parameters.datefmt_none': 'None',
    'academy.parameters.datefmt_yyyy': 'YYYY (Ex: 2026)',
    'academy.parameters.datefmt_yyyy_mm': 'YYYY-MM (Ex: 2026-08)',
    'academy.parameters.datefmt_yyyy_slash_mm': 'YYYY/MM (Ex: 2026/08)',
    'academy.parameters.datefmt_yyyymmdd': 'YYYYMMDD (Ex: 20260819)',
    'academy.parameters.datefmt_yymm': 'YYMM (Ex: 2608)',
    'academy.parameters.reset_yearly': 'Yearly (Every Jan 1st)',
    'academy.parameters.reset_monthly': 'Monthly (1st of each Month)',
    'academy.parameters.reset_daily': 'Daily (Every Day)',
    'academy.parameters.reset_manual': 'Manual Only',
    'academy.parameters.reset_never': 'Never (Continuous)',
    'academy.parameters.msg_invoice_saved': 'Invoice numbering parameters saved successfully.',
    'academy.parameters.msg_invoice_error': 'Error saving invoice parameters.',
    'academy.parameters.msg_payment_saved': 'Payment receipt numbering parameters saved successfully.',
    'academy.parameters.msg_payment_error': 'Error saving payment parameters.',
    'academy.parameters.groupDeleteCascadeTitle': 'Group & Scheduled Sessions Deletion',
    'academy.parameters.groupDeleteCascadeDesc': 'Define rule when deleting a group: automatically cascade delete all its scheduled planning sessions  or strictly block deletion if sessions exist.',
    'academy.parameters.groupDeleteCascadeLabel': 'Cascade Session Deletion',
    'academy.parameters.groupDeleteCascadeEnabled': 'Cascade Deletion Enabled (Recommended)',
    'academy.parameters.groupDeleteCascadeDisabled': 'Strict Control (Block deletion if scheduled sessions exist)',
    'academy.parameters.groupDeleteCascadeAlertTrue': 'Deleting a group will automatically delete all its associated scheduled sessions.',
    'academy.parameters.groupDeleteCascadeAlertFalse': 'Deleting a group will be blocked as long as it has active scheduled sessions.',
    'academy.groups.confirm_delete_with_sessions': 'Are you sure you want to delete group "{0}" and its {1} associated scheduled sessions?',
    'academy.groups.blocked_delete_has_sessions': 'Action blocked: Group "{0}" has {1} active scheduled sessions. Please delete its sessions first or enable Cascade Deletion in Academy Settings.',




    // Menu Parc
    'menu.parc.dashboard': 'Dashboard',
    'menu.parc.vehicles': 'Vehicles',
    'menu.parc.drivers': 'Drivers',
    'menu.parc.maintenance': 'Maintenance',
    'menu.parc.repairs': 'Repairs',
    'menu.parc.trips': 'Trips',
    'menu.parc.documents': 'Documents',
    'menu.parc.notifications': 'Notifications',
    'menu.parc.settings': 'Settings',
    'menu.parc.gps': 'GPS & TRACKING',
    'menu.parc.gps.tracking': 'GPS Tracking',
    'menu.parc.gps.alerts': 'GPS Alerts',
    'menu.parc.fuel': 'Fuel',
    'menu.parc.fuelcards': 'Fuel Cards',
    'menu.parc.mileage': 'Odometer Logs',
    'menu.parc.analyses': 'Analytics & Profitability',
    'menu.parc.analysis.general': 'General Analysis',
    'menu.parc.analysis.tco': 'TCO Analysis',
    'menu.parc.alerts': 'Alert Messages',
    'menu.parc.lookups': 'Reference Tables',

    // Menu Academy
    'academy.menu.dashboard': 'Dashboard',
    'academy.menu.rooms': 'Rooms & Resources',
    'academy.menu.room_rentals': 'Room Rentals',
    'academy.menu.teachers': 'Teachers',
    'academy.menu.students': 'Students & Enrollments',
    'academy.menu.course_templates': 'Course Templates',
    'academy.menu.groups': 'Groups & Cohorts',
    'academy.menu.scheduler': 'Weekly Scheduler',
    'academy.menu.planning_theorique_crud': 'Theoretical Planning Management',
    'academy.menu.planning_theorique_matrix': 'Theoretical Planning',
    'academy.menu.planning_generation': 'Planning Generation',
    'academy.menu.planning_deletion': 'Planning Deletion',
    'academy.menu.attendance': 'Attendance & Sessions',
    'academy.menu.financial': 'Financial & Payments',
    'academy.menu.reports': 'Reports & Analytics',
    'academy.menu.quizzes': 'Quizzes & Tests',
    'academy.menu.quizzes.list': 'List of Quizzes & Tests',
    'academy.menu.quizzes.quiz_questions': 'Quiz Questions',
    'academy.menu.quizzes.quiz_results': 'Quiz Results',
    'academy.menu.quiz_questions': 'Quiz Questions',
    'academy.menu.quiz_results': 'Quiz Results',
    'academy.menu.planning_controls': 'Planning Controls',
    'academy.menu.discounts': 'Commercial Discounts',
    'academy.menu.discount_validations': 'Discount Validations',
    'academy.menu.parameters': 'Academy Settings',

    // Commercial Discounts - EN
    'academy.discounts.title': 'Commercial Discounts Policy',
    'academy.discounts.subtitle': 'Management of discount categories, student assignment, and validation rules.',
    'academy.discounts.add_new': 'New Discount',
    'academy.discounts.back_to_list': 'Back to List',
    'academy.discounts.create_title': 'New Commercial Discount',
    'academy.discounts.edit_title': 'Edit Discount: {0}',
    'academy.discounts.title_new': 'New Commercial Discount',
    'academy.discounts.title_edit': 'Edit Discount ({code})',
    'academy.discounts.total_count': 'Total Defined Discounts',
    'academy.discounts.auto_count': 'Auto-Applied Discounts',
    'academy.discounts.validation_count': 'Admin / Director Validation Required',
    'academy.discounts.empty_title': 'No discounts configured',
    'academy.discounts.empty_subtitle': 'Create a new discount to encourage enrollments.',
    'academy.discounts.search_placeholder': 'Search by code, name, description...',
    'academy.discounts.code': 'Discount Code',
    'academy.discounts.name': 'Full Discount Name',
    'academy.discounts.percentage': 'Discount Rate (%)',
    'academy.discounts.min_courses': 'Min. Courses Count',
    'academy.discounts.application': 'Application Mode',
    'academy.discounts.validation': 'Validation Required',
    'academy.discounts.validation_role': 'Required Validation Role',
    'academy.discounts.role_auto': 'Auto (Automatic validation)',
    'academy.discounts.role_planner': 'Planner',
    'academy.discounts.role_admin': 'Admin',
    'academy.discounts.requires_val_check': 'Requires formal validation before application',
    'academy.discounts.is_auto_applied_check': 'Apply automatically if conditions are met',
    'academy.discounts.auto_apply_check': 'Apply automatically when eligible',
    'academy.discounts.is_active': 'Active Discount',
    'academy.discounts.description': 'Description & Conditions',
    'academy.discounts.desc_label': 'Description and criteria',
    'academy.discounts.assigned_students': 'Assigned students',
    'academy.discounts.used_invoices': 'Invoices used',
    'academy.discounts.linked_invoices': 'Linked Invoices',
    'academy.discounts.auto_label': 'Automatic',
    'academy.discounts.manual_label': 'Manual / Assigned',
    'academy.discounts.no_validation': 'None',
    'academy.discounts.btn_assign_students': 'Assign to students',
    'academy.discounts.assign_dialog_title': 'Assign Discount to Students',
    'academy.discounts.assign_dialog_desc': 'Select students eligible for discount "{0}" (-{1}%).',
    'academy.discounts.validation.code_required': 'Discount code is required.',
    'academy.discounts.validation.name_required': 'Discount name is required.',
    'academy.discounts.assign_success': 'Discount "{0}" successfully assigned to {1} student(s).',
    'academy.discounts.assign_error': 'Failed to assign students.',
    'academy.discounts.confirm_delete': 'Are you sure you want to delete discount "{0}"?',
    'academy.discounts.msg_saved': 'Discount saved successfully.',
    'academy.discounts.msg_deleted': 'Discount {code} deleted successfully.',
    'academy.discounts.deleted_success': 'Discount deleted successfully.',
    'academy.discounts.msg_blocked_delete': 'Action blocked: Discount {code} ({name}) is assigned to students or used in invoices.',
    'academy.discounts.assign_search_placeholder': 'Search for a student (name, email, phone)...',
    'academy.discounts.assign_no_results': 'No students match your search.',
    'academy.discounts.assign_assigned_count': '{0} / {1} assigned',
    'academy.discounts.assign_select_all': 'Select all',
    'academy.discounts.assign_deselect_all': 'Deselect all',

    // Discount Validations - EN
    'academy.discount_validations.title': 'Invoice Discount Validations',
    'academy.discount_validations.access_denied_title': 'Admin Access Only',
    'academy.discount_validations.access_denied_desc': 'This page is strictly reserved for Management and Administration to validate discount requests submitted on invoices.',
    'academy.discount_validations.kpi_pending': 'PENDING',
    'academy.discount_validations.kpi_approved': 'APPROVED',
    'academy.discount_validations.kpi_rejected': 'REJECTED',
    'academy.discount_validations.kpi_total_pending': 'TOTAL PENDING DISCOUNTS',
    'academy.discount_validations.tab_pending': 'Pending ({0})',
    'academy.discount_validations.tab_approved': 'Approved ({0})',
    'academy.discount_validations.tab_rejected': 'Rejected ({0})',
    'academy.discount_validations.tab_all': 'All ({0})',
    'academy.discount_validations.search_placeholder': 'Search by student, invoice, discount code, reason...',
    'academy.discount_validations.empty_title': 'No discount requests found',
    'academy.discount_validations.empty_pending_desc': 'All discount requests have been processed.',
    'academy.discount_validations.empty_filter_desc': 'No entries for this filter.',
    'academy.discount_validations.col_date_requester': 'Date & Requester',
    'academy.discount_validations.col_invoice_student': 'Invoice & Student',
    'academy.discount_validations.col_requested_discount': 'Requested Discount',
    'academy.discount_validations.col_financial_impact': 'Financial Impact',
    'academy.discount_validations.col_justification': 'Reason / Justification',
    'academy.discount_validations.col_status': 'Status',
    'academy.discount_validations.col_actions': 'Decision Actions',
    'academy.discount_validations.by_requester': 'By: {0}',
    'academy.discount_validations.gross_label': 'Gross: ',
    'academy.discount_validations.net_label': 'Net: ',
    'academy.discount_validations.savings_label': 'Savings: -',
    'academy.discount_validations.no_reason': 'No reason specified',
    'academy.discount_validations.rejection_reason_label': 'Rejection reason: ',
    'academy.discount_validations.status_pending': 'Pending',
    'academy.discount_validations.status_approved': 'Approved',
    'academy.discount_validations.status_rejected': 'Rejected',
    'academy.discount_validations.status_processed': 'Processed',
    'academy.discount_validations.btn_approve': 'Approve',
    'academy.discount_validations.btn_reject': 'Reject',
    'academy.discount_validations.dialog_approve_title': 'Confirm Approval',
    'academy.discount_validations.dialog_approve_alert': 'By approving this request, the net amount of invoice {0} will be updated from {1} to {2} (-{3}%).',
    'academy.discount_validations.dialog_approve_confirm': 'Confirm Approval',
    'academy.discount_validations.dialog_reject_title': 'Reject Discount',
    'academy.discount_validations.dialog_reject_alert': 'The discount will be rejected and invoice {0} will retain its original amount ({1}).',
    'academy.discount_validations.dialog_reject_field_label': 'Rejection reason',
    'academy.discount_validations.dialog_reject_placeholder': 'Specify the reason for rejection (e.g., missing proof, policy not applicable)...',
    'academy.discount_validations.dialog_reject_required': 'Rejection reason is required.',
    'academy.discount_validations.dialog_reject_confirm': 'Confirm Rejection',
    'academy.discount_validations.approve_success': 'Discount of -{0}% on invoice {1} has been approved.',
    'academy.discount_validations.reject_info_title': 'Request rejected',
    'academy.discount_validations.reject_info_msg': 'Discount request for invoice {0} was rejected.',
    'academy.discount_validations.approve_error': 'Failed to approve discount.',
    'academy.discount_validations.reject_error': 'Failed to reject discount.',

    // Financial Invoice Discounts - EN
    'academy.financial.discount.dialog_title': 'Manage Invoice Discount',
    'academy.financial.discount.student_label': 'Student',
    'academy.financial.discount.initial_gross': 'Initial Gross Amount',
    'academy.financial.discount.already_paid': 'Already Paid',
    'academy.financial.discount.policy_label': 'Commercial discount policy',
    'academy.financial.discount.policy_helper': 'Select a discount from catalogue or enter a custom percentage',
    'academy.financial.discount.none_option': 'No discount (0% / Remove discount)',
    'academy.financial.discount.custom_option': 'Custom percentage...',
    'academy.financial.discount.custom_percent_label': 'Discount percentage (%)',
    'academy.financial.discount.simulation_title': 'Financial Simulation',
    'academy.financial.discount.gross_label': 'Gross Amount:',
    'academy.financial.discount.deduction_label': 'Discount Deduction (-{0}%):',
    'academy.financial.discount.new_net_invoiced': 'New Invoiced Net:',
    'academy.financial.discount.new_due_rest': 'New Balance Remaining:',
    'academy.financial.discount.justification_label': 'Discount reason or justification',
    'academy.financial.discount.justification_placeholder': 'Specify the reason for this discount (e.g., management approval, referral, siblings, adjustment)...',
    'academy.financial.discount.justification_required': 'Discount justification is required.',
    'academy.financial.discount.direct_admin_notice': 'As an administrator, this discount will be applied directly to the invoice.',
    'academy.financial.discount.direct_planner_notice': 'This discount (Planner / Auto) will be applied immediately to the invoice.',
    'academy.financial.discount.pending_admin_notice': 'This discount requires admin validation. Your request will be queued as pending and forwarded to administrators.',
    'academy.financial.discount.btn_apply_direct': 'Apply Immediately',
    'academy.financial.discount.btn_submit_pending': 'Submit for Approval',
    'academy.financial.discount.manage_tooltip': 'Manage discount on this invoice',
    'academy.financial.discount.pending_tooltip': 'Pending approval request (-{0}%): {1}',
    'academy.financial.discount.pending_badge': 'Pending (-{0}%)',
    'academy.financial.discount.validation_auto': 'Auto validation',
    'academy.financial.discount.validation_admin': 'Admin validation',
    'academy.financial.discount.success_applied': 'Discount applied successfully to invoice.',
    'academy.financial.discount.success_submitted': 'Discount request submitted successfully for approval.',

    // Academy Dashboard Refresh
    'academy.dashboard.refresh_tooltip': 'Automatic refresh',
    'academy.dashboard.refresh_tooltip_disabled': 'Automatic refresh disabled',
    'academy.dashboard.refresh_tooltip_sec': 'Automatic refresh every {0} sec',
    'academy.dashboard.refresh_tooltip_min': 'Automatic refresh every {0} min',

    // Academy Parameters
    'academy.parameters.agencyTitle': 'Agency Settings #{0}',
    'academy.parameters.saveTooltip': 'Save this parameter',
    'academy.parameters.saveSuccess': 'Parameter "{0}" saved successfully.',
    'academy.parameters.saveError': 'Unable to save parameter "{0}".',
    'academy.parameters.timerTitle': 'Dashboard Refresh Frequency',
    'academy.parameters.timerDesc': 'Frequency in seconds for automatic dashboard statistics update.',
    'academy.parameters.timerLabel': 'Refresh timer (Seconds)',
    'academy.parameters.timerUnit': 'sec',
    'academy.parameters.timerDisabledHelper': '0 = Disabled (data loaded on open only)',
    'academy.parameters.timerActiveHelper': 'Updates every {0} seconds',
    'academy.parameters.quickPresets': 'Quick Presets:',
    'academy.parameters.chipDisabled': 'Disabled (0s)',
    'academy.parameters.autoApproveTitle': 'Pre-registration Approval',
    'academy.parameters.autoApproveDesc': 'Defines whether online pre-registered students are automatically approved as active students.',
    'academy.parameters.autoApproveEnabled': 'Automatic Approval Enabled',
    'academy.parameters.autoApproveDisabled': 'Manual Approval (Recommended)',
    'academy.parameters.autoApproveAlertTrue': 'New pre-registrations will become active immediately without manual review.',
    'academy.parameters.autoApproveAlertFalse': 'Pre-registrations remain pending until manually reviewed.',
    'academy.parameters.quizScoreTitle': 'Default Quiz Passing Score',
    'academy.parameters.quizScoreDesc': 'Default minimum percentage required to pass evaluation quizzes.',
    'academy.parameters.quizScoreLabel': 'Minimum passing score (%)',
    'academy.parameters.quizScoreHelper': 'Students scoring at least {0}% pass the quiz.',
    'academy.parameters.maxStudentsTitle': 'Max Students Per Group',
    'academy.parameters.maxStudentsDesc': 'Recommended maximum number of active students per group.',
    'academy.parameters.maxStudentsLabel': 'Max students per class',
    'academy.parameters.maxStudentsUnit': 'students',
    'academy.parameters.maxStudentsHelper': 'Alert displayed when class exceeds {0} students.',
    'academy.parameters.modifiedBy': 'Modified by:',
    'academy.parameters.lastUpdate': 'Updated:',
    'academy.parameters.systemUser': 'System',
    'academy.parameters.showUserEditionsTitle': 'Connected User Display on Editions',
    'academy.parameters.showUserEditionsDesc': 'Automatically display the connected user name and profile role at the bottom footer of printed invoices, receipts, and reports.',
    'academy.parameters.showUserEditionsEnabled': 'Connected user display enabled on edition footers',
    'academy.parameters.showUserEditionsDisabled': 'Connected user display disabled (Anonymous editions)',


    // Academy Quizzes
    'academy.quizzes.title': 'Quizzes & Tests',
    'academy.quizzes.add_new': 'New Quiz',
    'academy.quizzes.back_to_list': 'Back to list',
    'academy.quizzes.empty': 'No quiz created yet. Click "New Quiz" to get started.',
    'academy.quizzes.search_placeholder': 'Search a quiz...',
    'academy.quizzes.list.grid': 'Grid view',
    'academy.quizzes.list.table': 'Table view',
    'academy.quizzes.duration_label': '{0} min',
    'academy.quizzes.status_draft': 'Draft',
    'academy.quizzes.status_active': 'Active',
    'academy.quizzes.status_archived': 'Archived',
    'academy.quizzes.confirm_delete': 'Delete quiz "{0}" and all its questions/results?',
    'academy.quizzes.section_info': 'Quiz information',
    'academy.quizzes.section_levels': 'Levels / Score grid',
    'academy.quizzes.add_level': 'Add level',
    'academy.quizzes.levels_empty': 'No level defined. Add score bands to display a custom level.',
    'academy.quizzes.level.code': 'Code',
    'academy.quizzes.level.title': 'Title',
    'academy.quizzes.level.min': 'Min score',
    'academy.quizzes.level.max': 'Max score',
    'academy.quizzes.level.message': 'Message',
    'academy.quizzes.level.add': 'Add',
    'academy.quizzes.level.update': 'Update',
    'academy.quizzes.table.code': 'Code',
    'academy.quizzes.table.title': 'Title',
    'academy.quizzes.table.duration': 'Duration',
    'academy.quizzes.table.status': 'Status',
    'academy.quizzes.table.validity': 'Validity',
    'academy.quizzes.actions.questions': 'Questions',
    'academy.quizzes.actions.results': 'Results',
    'academy.quizzes.actions.share': 'Share link',
    'academy.quizzes.actions.edit': 'Edit',
    'academy.quizzes.actions.delete': 'Delete',
    'academy.quizzes.form.title_new': 'New Quiz',
    'academy.quizzes.form.title_edit': 'Edit: {0}',
    'academy.quizzes.form.code': 'Code',
    'academy.quizzes.form.title': 'Title',
    'academy.quizzes.form.duration': 'Duration (minutes)',
    'academy.quizzes.form.expiry_days': 'Expiry (days)',
    'academy.quizzes.form.status': 'Status',
    'academy.quizzes.form.display': 'Display type',
    'academy.quizzes.display_list': 'List',
    'academy.quizzes.display_assistant': 'Assistant (one question at a time)',
    'academy.quizzes.form.start_date': 'Start date',
    'academy.quizzes.form.end_date': 'End date',
    'academy.quizzes.form.whatsapp': 'WhatsApp (contact)',
    'academy.quizzes.form.register_link': 'Registration link',
    'academy.quizzes.form.description': 'Description',
    'academy.quizzes.val_title': 'Quiz title is required.',
    'academy.quizzes.val_duration': 'Duration must be greater than 0.',
    'academy.quizzes.val_date_range': 'Start date must precede end date.',
    'academy.quizzes.error_load': 'Failed to load quizzes',
    'academy.quizzes.error_delete': 'Failed to delete quiz',
    'academy.quizzes.error_save_level': 'Failed to save level',
    'academy.quizzes.error_delete_level': 'Failed to delete level',
    'academy.quizzes.error_load_levels': 'Failed to load levels',
    'academy.quizzes.share.title': 'Share: {0}',
    'academy.quizzes.share.error': 'Unable to generate the public link.',
    'academy.quizzes.share.error_generic': 'An error occurred.',
    'academy.quizzes.share.url_label': 'Public quiz link',
    'academy.quizzes.share.copy': 'Copy',
    'academy.quizzes.share.copied': 'Copied!',
    'academy.quizzes.share.whatsapp': 'Share on WhatsApp',
    'academy.quizzes.share.whatsapp_text': 'Level quiz - take the test: ',
    'academy.quizzes.share.regenerate': 'Generate a new link',
    'academy.quizzes.share.generate': 'Generate link',
    'academy.quizzes.share.generating': 'Generating...',
    'academy.quizzes.share.empty_title': 'Public link',
    'academy.quizzes.share.empty_desc': 'Generate a public link to share with your candidates.',
    'academy.quiz_questions.title': 'Quiz Questions',
    'academy.quiz_questions.back_to_quizzes': 'Back to quizzes',
    'academy.quiz_questions.add_new': 'New question',
    'academy.quiz_questions.select_quiz': 'Quiz',
    'academy.quiz_questions.select_quiz_placeholder': 'Select a quiz',
    'academy.quiz_questions.no_quiz_selected': 'Select a quiz to manage its questions.',
    'academy.quiz_questions.search_placeholder': 'Search a question...',
    'academy.quiz_questions.q_count': '{0} question(s)',
    'academy.quiz_questions.total_points': '{0} total points',
    'academy.quiz_questions.table.order': 'Order',
    'academy.quiz_questions.table.text': 'Question',
    'academy.quiz_questions.table.correct': 'Correct answer',
    'academy.quiz_questions.table.points': 'Points',
    'academy.quiz_questions.dialog.new': 'New question',
    'academy.quiz_questions.dialog.edit': 'Edit question',
    'academy.quiz_questions.form.text': 'Question text',
    'academy.quiz_questions.form.order': 'Order',
    'academy.quiz_questions.form.points': 'Points',
    'academy.quiz_questions.confirm_delete': 'Delete this question?',
    'academy.quiz_questions.val_text': 'Question text is required.',
    'academy.quiz_questions.error_load': 'Failed to load questions',
    'academy.quiz_questions.error_save': 'Failed to save question',
    'academy.quiz_questions.error_delete': 'Failed to delete question',
    'academy.quiz_questions.table.type': 'Type',
    'academy.quiz_questions.table.manual': 'Manual',
    'academy.quiz_questions.form.type': 'Question type',
    'academy.quiz_questions.form.type_single': 'Single choice (radio)',
    'academy.quiz_questions.form.type_multi': 'Multiple choice (checkboxes)',
    'academy.quiz_questions.form.type_boolean': 'True / False',
    'academy.quiz_questions.form.type_text': 'Free text answer',
    'academy.quiz_questions.form.options': 'Options',
    'academy.quiz_questions.form.option_label': 'Option label',
    'academy.quiz_questions.form.correct': 'Correct',
    'academy.quiz_questions.form.add_option': 'Add option',
    'academy.quiz_questions.form.multiple_correct': 'Multiple correct answers',
    'academy.quiz_questions.form.true_label': 'True',
    'academy.quiz_questions.form.false_label': 'False',
    'academy.quiz_questions.form.text_hint': 'Free-text question: the candidate writes an answer. It is graded manually and is not included in the automatic score.',
    'academy.quiz_questions.val_options': 'Add at least 2 options with a label.',
    'academy.quiz_questions.val_correct': 'Select at least one correct answer.',
    'academy.quiz_public.text_answer_placeholder': 'Your answer...',
    'academy.quiz_results.title': 'Quiz Results',
    'academy.quiz_results.back_to_quizzes': 'Back to quizzes',
    'academy.quiz_results.select_quiz': 'Quiz',
    'academy.quiz_results.select_quiz_placeholder': 'Select a quiz',
    'academy.quiz_results.no_quiz_selected': 'Select a quiz to view results.',
    'academy.quiz_results.search_placeholder': 'Search a participant...',
    'academy.quiz_results.count': '{0} attempt(s)',
    'academy.quiz_results.avg': 'Average: {0}',
    'academy.quiz_results.table.ref': 'Reference',
    'academy.quiz_results.table.name': 'Participant',
    'academy.quiz_results.table.phone': 'Phone',
    'academy.quiz_results.table.email': 'Email',
    'academy.quiz_results.table.score': 'Score',
    'academy.quiz_results.table.level': 'Level',
    'academy.quiz_results.table.duration': 'Duration',
    'academy.quiz_results.table.date': 'Date',
    'academy.quiz_results.error_load': 'Failed to load results',
    'academy.menu.traffic': 'API Traffic',
    'traffic.title': 'API Traffic',
    'traffic.error_load': 'Failed to load traffic',
    'traffic.refresh': 'Refresh',
    'traffic.close': 'Close',
    'traffic.details': 'Details',
    'traffic.details_title': 'Request details',
    'traffic.anomaly': 'Anomaly',
    'traffic.anomaly_flags': 'Detected anomalies',
    'traffic.request_body': 'Request body',
    'traffic.correlation_id': 'Correlation ID',
    'traffic.search_placeholder': 'Search (path, IP, email...)',
    'traffic.stats.total': 'Requests',
    'traffic.stats.anomalies': 'Anomalies',
    'traffic.stats.server_errors': '5xx errors',
    'traffic.stats.auth_failures': 'Auth failures',
    'traffic.stats.not_found': '404',
    'traffic.stats.rate_limited': 'Rate limited',
    'traffic.stats.avg_duration': 'Avg duration',
    'traffic.stats.distinct_ips': 'Distinct IPs',
    'traffic.filter.period': 'Period',
    'traffic.filter.last_hour': 'Last hour',
    'traffic.filter.last_24h': 'Last 24 hours',
    'traffic.filter.last_7d': 'Last 7 days',
    'traffic.filter.last_30d': 'Last 30 days',
    'traffic.filter.status': 'Status',
    'traffic.filter.all': 'All',
    'traffic.filter.anomalies_only': 'Anomalies only',
    'traffic.filter.count': '{0} entrie(s)',
    'traffic.table.date': 'Date',
    'traffic.table.method': 'Method',
    'traffic.table.path': 'Path',
    'traffic.table.status': 'Status',
    'traffic.table.duration': 'Duration',
    'traffic.table.ip': 'IP',
    'traffic.table.user': 'User',
    'traffic.table.source': 'Source',
    'traffic.table.anomalies': 'Anomalies',
    'traffic.table.user_agent': 'User-Agent',
    'academy.menu.user_actions': 'User Actions',
    'useraction.title': 'User Actions',
    'useraction.error_load': 'Failed to load actions',
    'useraction.refresh': 'Refresh',
    'useraction.close': 'Close',
    'useraction.details': 'Details',
    'useraction.details_title': 'Action details',
    'useraction.request_body': 'Request body',
    'useraction.correlation_id': 'Correlation ID',
    'useraction.search_placeholder': 'Search (path, email, IP...)',
    'useraction.stats.total': 'Actions',
    'useraction.stats.connected': 'Connected',
    'useraction.stats.public': 'Public',
    'useraction.stats.internal': 'Internal',
    'useraction.stats.avg_duration': 'Avg duration',
    'useraction.stats.server_errors': '5xx errors',
    'useraction.stats.distinct_users': 'Users',
    'useraction.stats.distinct_ips': 'Distinct IPs',
    'useraction.stats.distinct_okeys': 'OKeys',
    'useraction.filter.period': 'Period',
    'useraction.filter.last_hour': 'Last hour',
    'useraction.filter.last_24h': 'Last 24 hours',
    'useraction.filter.last_7d': 'Last 7 days',
    'useraction.filter.last_30d': 'Last 30 days',
    'useraction.filter.status': 'Status',
    'useraction.filter.all': 'All',
    'useraction.filter.connected_only': 'Connected only',
    'useraction.filter.source': 'Source',
    'useraction.filter.method': 'Method',
    'useraction.filter.okey': 'OKey',
    'useraction.filter.count': '{0} entrie(s)',
    'useraction.table.date': 'Date',
    'useraction.table.method': 'Method',
    'useraction.table.path': 'Path',
    'useraction.table.okey': 'OKey',
    'useraction.table.status': 'Status',
    'useraction.table.duration': 'Duration',
    'useraction.table.ip': 'IP',
    'useraction.table.user': 'User',
    'useraction.table.source': 'Source',
    'useraction.table.user_agent': 'User-Agent',
    'useraction.table.token_hash': 'Token (hash)',
    'useraction.table.error': 'Error',
    'useraction.table.cnx': 'Connection',
    'useraction.times.start': 'Start',
    'useraction.times.end': 'End',
    'academy.menu.user_connexions': 'User Connexions',
    'userconnex.title': 'User Connexions',
    'userconnex.refresh': 'Refresh',
    'userconnex.back_to_list': 'Back to list',
    'userconnex.close': 'Close',
    'userconnex.detail': 'Detail',
    'userconnex.load_actions': 'Load actions',
    'userconnex.actions': 'actions',
    'userconnex.actions_title': 'Connexion actions',
    'userconnex.actions_empty': 'No actions recorded for this connexion.',
    'userconnex.details_title': 'Connexion details',
    'userconnex.error_no_user': 'Please select a user.',
    'userconnex.error_load': 'Failed to load connexions',
    'userconnex.error_load_actions': 'Failed to load actions',
    'userconnex.no_data': 'No connexions found for this user and period.',
    'userconnex.search_placeholder': 'Search (path, status...)',
    'userconnex.filter.user': 'User',
    'userconnex.filter.user_placeholder': 'Search a user',
    'userconnex.filter.start': 'Start date',
    'userconnex.filter.end': 'End date',
    'userconnex.filter.load': 'Load',
    'userconnex.filter.count': '{0} connexion(s)',
    'userconnex.table.cnx': 'Connexion',
    'userconnex.table.start': 'Start',
    'userconnex.table.end': 'End',
    'userconnex.table.state': 'State',
    'userconnex.table.app': 'Application',
    'userconnex.table.dossier': 'Dossier',
    'userconnex.table.actions_count': 'Actions',
    'userconnex.table.date': 'Date',
    'userconnex.table.method': 'Method',
    'userconnex.table.path': 'Path',
    'userconnex.table.status': 'Status',
    'userconnex.table.duration': 'Duration',
    'userconnex.field.num': 'Connexion #',
    'userconnex.field.start': 'Start date',
    'userconnex.field.end': 'End date',
    'userconnex.field.last_activity': 'Last activity',
    'userconnex.field.app': 'Application',
    'userconnex.field.dossier': 'Dossier',
    'userconnex.field.ip': 'IP',
    'userconnex.field.host': 'Host',
    'userconnex.field.computer': 'Computer',
    'userconnex.field.language': 'Language',
    'userconnex.field.referer': 'Referer',
    'userconnex.field.wid': 'Session WID',
    'userconnex.field.text': 'Text',
    'common.copyright': '© 2026 BLOBTIC. All rights reserved.',
    'academy.prereg.page.error_contact_admin_help': 'Please contact the institution administration to obtain a valid pre-registration link.',
    'academy.prereg.page.establishment_label': 'Institution: {0}',
    'academy.quiz_public.offered_by': 'Quiz offered by {0}',
    'academy.students.portal_section': 'Student & Parent Portal Access',
    'academy.students.portal_active': 'Enable portal access',
    'academy.students.portal_password': 'Portal password',
    'academy.students.portal_generate_pwd': 'Generate',
    'academy.students.portal_copy_link': 'Copy public link',
    'academy.students.portal_link_copied': 'Portal link copied!',
    'academy.students.portal_badge_active': 'Portal Active',
    'academy.students.portal_badge_inactive': 'Portal Inactive',
    'academy.students.portal_login_hint': 'The student or guardian can log in using email, phone, or student code with this password.',
    'academy.teachers.portal_section': 'Teacher Portal Access',
    'academy.teachers.portal_active': 'Enable portal access',
    'academy.teachers.portal_can_attendance': 'Allow attendance taking & badge scanning',
    'academy.teachers.portal_password': 'Portal password',
    'academy.teachers.portal_generate_pwd': 'Generate',
    'academy.teachers.portal_copy_link': 'Copy public link',
    'academy.teachers.portal_link_copied': 'Portal link copied!',
    'academy.teachers.portal_badge_active': 'Portal Active',
    'academy.teachers.portal_badge_inactive': 'Portal Inactive',
    'academy.teachers.portal_login_hint': 'The teacher can log in using email or phone with this password.',
    'academy.students.validation.first_name_required': 'First name is required.',
    'academy.students.validation.last_name_required': 'Last name is required.',
    'academy.students.validation.gender_required': 'Gender is required.',
    'academy.students.validation.dob_required': 'Date of birth is required.',
    'academy.students.validation.health_issues_required': 'Please specify health issues.',
    'academy.students.validation.allergies_required': 'Please specify allergies.',
    'academy.students.validation.guardian_first_name_required': 'Guardian first name is required.',
    'academy.students.validation.guardian_last_name_required': 'Guardian last name is required.',
    'academy.students.validation.guardian_relation_required': 'Relation with child is required.',
    'academy.students.validation.guardian_phone_required': 'Guardian phone is required.',
    'academy.students.validation.image_right_required': 'Image rights consent is required.',
    'academy.teachers.validation.email_required': 'Email address is required.',
    'academy.teachers.validation.phone_required': 'Phone number is required.',
    'academy.teachers.validation.specialization_required': 'Specialization is required.',
    'academy.teachers.validation.hourly_rate_required': 'Rate amount is required.',
    'academy.prereg.page.health_question': 'Does the child have health issues or chronic conditions?',
    'academy.prereg.page.health_explanation': 'This information helps our staff ensure your child\'s safety and well-being during all academy activities.',
    'academy.prereg.page.health_specify_label': '(If Yes) Specify:',
    'academy.prereg.page.condition_asthma': 'Asthma',
    'academy.prereg.page.condition_diabetes': 'Diabetes',
    'academy.prereg.page.condition_epilepsy': 'Epilepsy',
    'academy.prereg.page.condition_other': 'Other',
    'academy.prereg.page.health_placeholder': 'Specify the condition or special care needed...',
    'academy.prereg.page.allergies_header': 'Known Allergies',
    'academy.prereg.page.allergies_question': 'Does the child have any known allergies?',
    'academy.prereg.page.allergies_explanation': 'Please list any food or environmental allergies to avoid risks during snacks and activities.',
    'academy.prereg.page.allergies_placeholder': 'Specify allergies (e.g. peanuts, lactose, penicillin...)',
    'academy.prereg.page.image_rights_header': 'Image Rights',
    'academy.prereg.page.image_rights_question': 'Do you grant permission for student photos/videos?',
    'academy.prereg.page.image_rights_explanation': 'Permission to include the student in educational group photos/videos and official academy publications.',
    'academy.prereg.page.yes': 'Yes',
    'academy.prereg.page.no': 'No',
    'academy.prereg.time_slot.morning': 'Morning (08:00 AM - 12:00 PM)',
    'academy.prereg.time_slot.afternoon': 'Afternoon (01:00 PM - 05:00 PM)',
    'academy.prereg.time_slot.evening': 'Evening (05:00 PM - 08:00 PM)',
    'academy.prereg.time_slot.weekend': 'Weekend',
    'academy.prereg.time_slot.flexible': 'Flexible',
    'academy.prereg.page.select_course_required': 'Please select a course or program.',
    'academy.prereg.page.select_time_preference_required': 'Please select a preferred time slot.',
    'academy.quizzes.form.auto_next': 'Automatically advance to the next question when an option is selected',
    'academy.quizzes.form.max_attempts_link': 'Max total attempts for the link',
    'academy.quizzes.form.max_attempts_user': 'Max attempts per person (IP / Phone)',
    'academy.quiz_public.public_portal': 'Public Quiz Portal',
    'academy.quiz_public.error_invalid_token': 'Invalid or expired quiz link.',
    'academy.quiz_public.error_load_failed': 'Unable to load the quiz.',
    'academy.quiz_public.error_no_token': 'No quiz token provided.',
    'academy.quiz_public.error_submit_failed': 'Failed to submit answers.',
    'academy.quiz_public.error_submit_generic': 'An error occurred while submitting.',
    'academy.quiz_public.error_contact_required': 'Please fill in your name and phone number.',
    'academy.quiz_public.error_all_questions': 'Please answer all questions.',
    'academy.quiz_public.error_name_invalid': 'Please enter a valid full name (minimum 3 characters).',
    'academy.quiz_public.error_phone_invalid': 'Phone number is invalid (minimum 8 digits required).',
    'academy.quiz_public.error_email_invalid': 'Email address is invalid (e.g. name@domain.com).',
    'academy.quiz_public.duration_label': 'Duration: {0} minutes',
    'academy.quiz_public.questions_count': '{0} questions',
    'academy.quiz_public.contact_header': 'Your information',
    'academy.quiz_public.full_name': 'Full name',
    'academy.quiz_public.phone': 'Phone',
    'academy.quiz_public.email': 'Email',
    'academy.quiz_public.age_band': 'Age band',
    'academy.quiz_public.age_band_under_12': 'Under 12',
    'academy.quiz_public.age_band_12_15': '12 - 15',
    'academy.quiz_public.age_band_16_25': '16 - 25',
    'academy.quiz_public.age_band_26_35': '26 - 35',
    'academy.quiz_public.age_band_36_50': '36 - 50',
    'academy.quiz_public.age_band_over_50': 'Over 50',
    'academy.quiz_public.start_btn': 'Start the quiz',
    'academy.quiz_public.submit_btn': 'Submit my answers',
    'academy.quiz_public.continue_btn': 'Continue',
    'academy.quiz_public.details_subtitle': 'Fill in your details to see your result.',
    'academy.quiz_public.submitting_btn': 'Submitting...',
    'academy.quiz_public.prev_btn': 'Previous',
    'academy.quiz_public.next_btn': 'Next',
    'academy.quiz_public.question_of': 'Question {0} / {1}',
    'academy.quiz_public.result_title': 'Quiz completed!',
    'academy.quiz_public.result_ref': 'Reference: {0}',
    'academy.quiz_public.result_score': 'Score: {0} / {1}',
    'academy.quiz_public.result_level': 'Your level: {0}',
    'academy.quiz_public.whatsapp_btn': 'Contact the school',
    'academy.quiz_public.whatsapp_result_text': 'I took the level quiz (ref. {0}). I would like to enroll.',
    'academy.quiz_public.register_btn': 'Register for a course',

    // Academy Application Strings
    'academy.dashboard.title': 'Mission Control Dashboard',
    'academy.dashboard.subtitle': '5-second snapshot of academy overall health.',
    'academy.dashboard.kpi.active_students': 'Total Active Students',
    'academy.dashboard.kpi.today_prereg': "Today's Pre-Registrations",
    'academy.dashboard.kpi.today_schedule': "Today's Schedule",
    'academy.dashboard.kpi.low_attendance': 'Low Attendance Alert',
    'academy.dashboard.kpi.on_hold': '— {0} suspended{1}',
    'academy.dashboard.kpi.teachers': 'Total Teachers',
    'academy.dashboard.kpi.rooms': 'Total Active Rooms',
    'academy.dashboard.kpi.monthly_revenue': 'Monthly Revenue',
    'academy.dashboard.kpi.courses': 'Courses & Training',
    'academy.dashboard.kpi.groups': 'Study Groups',
    'academy.dashboard.kpi.sessions': 'Scheduled Sessions',
    'academy.dashboard.kpi.quizzes': 'Quizzes & Tests',
    'academy.dashboard.kpi.revenue': 'Collected Revenue',
    'academy.dashboard.kpi.pending_invoices': 'Pending Invoices',
    'academy.dashboard.kpi.pending_prereg': 'Pending Pre-Registrations',
    'academy.dashboard.kpi.accepted_prereg': 'Accepted Pre-Registrations',
    'academy.dashboard.kpi.refused_prereg': 'Refused Pre-Registrations',
    'academy.dashboard.kpi.desc.active_students': 'Enrolled and active students',
    'academy.dashboard.kpi.desc.teachers': 'Active teachers',
    'academy.dashboard.kpi.desc.courses': 'Available catalogue',
    'academy.dashboard.kpi.desc.groups': 'Ongoing groups',
    'academy.dashboard.kpi.desc.rooms': 'Active rooms',
    'academy.dashboard.kpi.desc.sessions': 'All planned sessions',
    'academy.dashboard.kpi.desc.quizzes': 'Available quizzes',
    'academy.dashboard.kpi.desc.revenue': 'Payments received',
    'academy.dashboard.kpi.desc.pending_invoices': 'Unpaid or partial',
    'academy.dashboard.kpi.desc.pending_prereg': 'To process',
    'academy.dashboard.kpi.desc.accepted_prereg': 'Confirmed',
    'academy.dashboard.kpi.desc.refused_prereg': 'Rejected',
    'academy.dashboard.kpi.desc.today_prereg': 'Received today',
    'academy.dashboard.kpi.desc.today_schedule': 'Sessions and attendance',
    'academy.dashboard.kpi.desc.low_attendance': 'Yesterday, < 75% attendance',
    'academy.dashboard.widget.outstanding_debt': 'Total Outstanding Debt (Rest-to-Pay)',
    'academy.dashboard.schedule.today': 'Today\'s Scheduled Sessions',
    'academy.dashboard.alerts.low_attendance': 'Low Attendance Alert (<75%)',
    'academy.dashboard.refresh_btn': 'Refresh ({0}m {1}s)',
    'academy.dashboard.init_btn': 'Initialize',
    'academy.dashboard.init_tooltip': 'Reset local DB cache',
    'academy.dashboard.kpi.sub_active_students': 'Enrolled across active cohorts',
    'academy.dashboard.kpi.sub_teachers': 'Active faculty staff',
    'academy.dashboard.kpi.sub_rooms': 'Physical learning spaces',
    'academy.dashboard.kpi.sub_monthly_revenue': 'Collected in current month',
    'academy.dashboard.widget.debt_desc': 'Total uncollected invoices across all enrolled students.',
    'academy.dashboard.widget.manage_debtors': 'Click to manage debtor reminders & payments →',
    'academy.dashboard.status.healthy': 'Healthy',
    'academy.dashboard.status.critical': 'CRITICAL DEBT ALERT',
    'academy.dashboard.status.warning': 'MODERATE DEBT WARNING',
    'academy.groups.status.upcoming': 'Upcoming',
    'academy.groups.status.ongoing': 'Ongoing',
    'academy.groups.status.completed': 'Completed',
    'academy.students.status.enrolled': 'Enrolled',
    'academy.students.status.dropped': 'Dropped',
    'days.sun': 'Sunday',
    'days.mon': 'Monday',
    'days.tue': 'Tuesday',
    'days.wed': 'Wednesday',
    'days.thu': 'Thursday',
    'days.fri': 'Friday',
    'days.sat': 'Saturday',
    'days.short.sun': 'Sun',
    'days.short.mon': 'Mon',
    'days.short.tue': 'Tue',
    'days.short.wed': 'Wed',
    'days.short.thu': 'Thu',
    'days.short.fri': 'Fri',
    'days.short.sat': 'Sat',
    'days.single.sun': 'S',
    'days.single.mon': 'M',
    'days.single.tue': 'T',
    'days.single.wed': 'W',
    'days.single.thu': 'T',
    'days.single.fri': 'F',
    'days.single.sat': 'S',
    'academy.dashboard.schedule.today_title': 'Today\'s Schedule ({0} sessions)',
    'academy.dashboard.schedule.full_scheduler': 'Full Scheduler',
    'academy.dashboard.schedule.empty': 'No sessions scheduled for today.',
    'academy.dashboard.schedule.instructor': 'Instructor:',
    'academy.dashboard.schedule.room': 'Room:',
    'academy.dashboard.schedule.att_recorded': 'Attendance Recorded',
    'academy.dashboard.schedule.take_att': 'Take Attendance',
    'academy.dashboard.alerts.title': 'Low Attendance Alert (<75%)',
    'academy.dashboard.alerts.subtitle': 'Sessions from yesterday with attendance rates below passing threshold.',
    'academy.dashboard.alerts.all_clear': 'All Clear!',
    'academy.dashboard.alerts.all_clear_sub': 'No low attendance alerts from yesterday\'s classes.',
    'academy.dashboard.alerts.rate': '{0}% Attendance',
    'academy.dashboard.alerts.present_count': 'Present: {0} / {1} Students',
    'academy.dashboard.prereg.stats_pending': 'Pending Pre-Registrations',
    'academy.dashboard.prereg.stats_accepted': 'Accepted Pre-Registrations',
    'academy.dashboard.prereg.stats_refused': 'Refused Pre-Registrations',
    'academy.dashboard.prereg.stats_sub_pending': 'Public requests awaiting review',
    'academy.dashboard.prereg.stats_sub_accepted': 'Requests validated and enrolled',
    'academy.dashboard.prereg.stats_sub_refused': 'Requests declined by administration',
    'academy.dashboard.prereg.today_title': 'Today\'s Pre-Registrations ({0})',
    'academy.dashboard.prereg.today_empty': 'No pre-registrations received today.',
    'academy.dashboard.prereg.view_all': 'View Students',
    'academy.dashboard.prereg.course': 'Course',
    'academy.dashboard.prereg.time_preference': 'Preferred time slot',

    'academy.search.placeholder': 'Global Student Search (Name or Email)...',
    'academy.search.debt': 'Debt:',
    'academy.search.financial_overview': 'Financial Overview',
    'academy.search.invoiced': 'Invoiced',
    'academy.search.total_paid': 'Total Paid',
    'academy.search.rest_to_pay': 'Rest-to-Pay',
    'academy.search.dob': 'Date of Birth',
    'academy.search.emergency_contact': 'Emergency Contact',
    'academy.search.hold_active': 'Financial Hold Active',
    'academy.search.hold_desc': 'Debt exceeds 30% of total invoice for over 15 days. Student is blocked from being marked Present in attendance.',

    'academy.rooms.title': 'Room & Resource Management',
    'academy.rooms.subtitle': 'Manage your classrooms, equipment, and seating capacity.',
    'academy.rooms.add_new': 'Add Room',
    'academy.rooms.back_to_list': 'Back to list',
    'academy.rooms.confirm_delete': 'Are you sure you want to delete this room?',
    'academy.rooms.search_placeholder': 'Search by room number, floor, equipment...',
    'academy.rooms.room_number': 'Room Number',
    'academy.rooms.floor': 'Floor',
    'academy.rooms.capacity': 'Capacity',
    'academy.rooms.equipment': 'Equipment',
    'academy.rooms.status': 'Status',
    'academy.rooms.status.active': 'Active',
    'academy.rooms.status.maintenance': 'Under Maintenance',
    'academy.rooms.status.archived': 'Archived',
    'academy.rooms.section_info': 'Room Details',
    'academy.rooms.section_dossier': 'Dossier Information',
    'academy.rooms.form.title_new': 'New Room',
    'academy.rooms.form.title_edit': 'Edit Room: {0}',
    'academy.rooms.form.dossier_code': 'Dossier Code',
    'academy.rooms.form.dossier_lib': 'Dossier Label',
    'academy.rooms.min_capacity': 'Min Capacity',
    'academy.rooms.all_capacities': 'All Capacities',
    'academy.rooms.capacity_plus': '{0}+ Students',
    'academy.rooms.view_grid': 'Grid',
    'academy.rooms.view_table': 'Table',
    'academy.rooms.room_prefix': 'Room {0}',
    'academy.rooms.location': 'Location:',
    'academy.rooms.capacity_label': 'Capacity: {0} Students',
    'academy.rooms.weekly_util': 'Weekly Utilization',
    'academy.rooms.modal.edit_title': 'Edit Room',
    'academy.rooms.modal.add_title': 'Create New Room',
    'academy.rooms.modal.number': 'Room Number (e.g. A-101)',
    'academy.rooms.modal.floor': 'Floor (e.g. 1st Floor)',
    'academy.rooms.modal.capacity': 'Capacity (Integer)',
    'academy.rooms.modal.equipment': 'Equipment (Comma separated)',
    'academy.rooms.modal.equipment_helper': 'e.g. Projector, Whiteboard, PCs, WiFi',
    'academy.rooms.modal.save': 'Save Room',

    'academy.courses.title': 'Course Templates',
    'academy.courses.subtitle': 'Manage training programs, pricing, and durations.',
    'academy.courses.add_new': 'New Template',
    'academy.courses.back_to_list': 'Back to list',
    'academy.courses.confirm_delete': 'Are you sure you want to delete template "{0}"?',
    'academy.courses.section_info': 'Template Information',
    'academy.courses.section_dossier': 'Dossier Information',
    'academy.courses.search_placeholder': 'Search by title, description...',
    'academy.courses.hours_label': '{0} h',
    'academy.courses.course_preview': 'Template Preview',
    'academy.courses.list.grid': 'Grid',
    'academy.courses.list.table': 'Table',
    'academy.courses.table.title': 'Title',
    'academy.courses.table.hours': 'Duration (h)',
    'academy.courses.table.price': 'Price',
    'academy.courses.table.description': 'Description',
    'academy.courses.form.title_new': 'New Course Template',
    'academy.courses.form.title_edit': 'Edit: {0}',
    'academy.courses.form.title': 'Course Title',
    'academy.courses.form.hours': 'Total Hours',
    'academy.courses.form.price': 'Price ({0})',
    'academy.courses.form.description': 'Syllabus & Description',
    'academy.courses.form.dossier_code': 'Dossier Code',
    'academy.courses.form.dossier_lib': 'Dossier Label',
    'academy.courses.val_title': 'Title is required.',
    'academy.courses.val_hours': 'Duration must be greater than 0.',
    'academy.courses.val_price': 'Price cannot be negative.',
    'academy.courses.form.start_date': 'Start date (validity)',
    'academy.courses.form.end_date': 'End date (validity)',
    'academy.courses.form.show_price': 'Show price on pre-registration link',
    'academy.courses.form.age_band': 'Age band (label)',
    'academy.courses.form.age_min': 'Minimum age',
    'academy.courses.form.age_max': 'Maximum age',
    'academy.courses.val_age_range': 'Minimum age must be less than or equal to maximum age.',
    'academy.courses.val_date_range': 'Start date must be before or equal to end date.',
    'academy.courses.table.age_band': 'Age band',
    'academy.courses.table.validity': 'Validity',

    'academy.teachers.title': 'Teacher & Faculty Management',
    'academy.teachers.subtitle': 'Manage instructors, hourly payroll rates & busy map.',
    'academy.teachers.add_new': 'Add New Teacher',
    'academy.teachers.fullname': 'Full Name',
    'academy.teachers.specialization': 'Specialization',
    'academy.teachers.hourly_rate': 'Hourly Rate ($/h)',
    'academy.teachers.hour': 'hour',
    'academy.teachers.busy_map': 'Weekly Busy Map Matrix',
    'academy.teachers.drawer.booked': 'Booked',
    'academy.teachers.confirm_delete': 'Are you sure you want to delete this teacher?',

    'academy.students.title': 'Student & Enrollment Management',
    'academy.students.subtitle': 'Student profiles, enrollment history & financial hold rules.',
    'academy.students.add_new': 'Register Student',
    'academy.students.firstname': 'First Name',
    'academy.students.lastname': 'Last Name',
    'academy.students.email': 'Email',
    'academy.students.phone': 'Phone',
    'academy.students.financial_hold': 'Financial Hold',
    'academy.students.hold_badge': 'FINANCIAL HOLD',
    'academy.students.prereg_link': 'Pre-Registration Link',

    // Attendance & Absence Justification (EN)
    'academy.attendance.justified': 'Justified Absence',
    'academy.attendance.unjustified': 'Unjustified',
    'academy.attendance.justify': 'Justify Absence',
    'academy.attendance.view_justification': 'View Justification',
    'academy.attendance.justification_details': 'Absence Justification Details',
    'academy.attendance.reason_label': 'Absence Reason',
    'academy.attendance.reason_placeholder': 'Specify medical or personal reason...',
    'academy.attendance.doc_label': 'Supporting Document (certificate, medical note...)',
    'academy.attendance.download_doc': 'Download Document',
    'academy.attendance.no_doc': 'No document attached',
    'academy.attendance.submission_date': 'Submission Date',
    'academy.attendance.save_justification': 'Save Justification',
    'academy.attendance.justification_saved': 'Justification saved successfully.',
    'academy.attendance.filter_all': 'All',
    'academy.attendance.filter_present': 'Present',
    'academy.attendance.filter_late': 'Late',
    'academy.attendance.filter_absent': 'All Absent',
    'academy.attendance.filter_absent_justified': 'Justified Absent',
    'academy.attendance.filter_absent_unjustified': 'Unjustified Absent',
    'academy.attendance.filter_excused': 'Excused',

    // Pre-Registration Modal & Page (EN)
    'academy.prereg.modal.title': 'Generate Student Pre-Registration Link',
    'academy.prereg.modal.subtitle': 'Create a public registration link for your agency to share on social media, website, or via SMS/WhatsApp.',
    'academy.prereg.modal.error_generate': 'Unable to generate public link.',
    'academy.prereg.modal.error_generic': 'Error generating link.',
    'academy.prereg.modal.custom_link_title': 'Custom Public Link',
    'academy.prereg.modal.custom_link_desc': 'The form will guide the student with a smart assistant (Junior / Adult) and automatically register their pre-registration in your agency space.',
    'academy.prereg.modal.btn_generate': 'Generate Public Link',
    'academy.prereg.modal.btn_generating': 'Generating...',
    'academy.prereg.modal.success': 'Public link created successfully for your agency!',
    'academy.prereg.modal.url_label': 'Public Form URL',
    'academy.prereg.modal.tooltip_copy': 'Copy link',
    'academy.prereg.modal.tooltip_copied': 'Copied!',
    'academy.prereg.modal.btn_copy': 'Copy Link',
    'academy.prereg.modal.btn_copied': 'Link Copied!',
    'academy.prereg.modal.btn_whatsapp': 'Share on WhatsApp',
    'academy.prereg.modal.whatsapp_text': 'Pre-registration form: ',
    'academy.prereg.modal.is_existing_notice': 'Active pre-registration link for your agency:',
    'academy.prereg.modal.btn_regenerate': 'Generate a new link',
    'academy.prereg.page.public_portal': 'Public Student Pre-Registration Portal',
    'academy.prereg.page.status_junior': 'Status: Junior (<16 yrs)',
    'academy.prereg.page.status_adult': 'Status: Adult (≥16 yrs)',
    'academy.prereg.page.step1_title': 'Personal Information',
    'academy.prereg.page.step2_junior_title': 'Guardian & Pickup',
    'academy.prereg.page.step2_adult_title': 'Profession & Emergency',
    'academy.prereg.page.step3_title': 'Health & Medical',
    'academy.prereg.page.step4_title': 'Course Selection',
    'academy.prereg.page.step5_title': 'Confirmation',
    'academy.prereg.page.step_mobile_label': 'Step {0} of {1}: {2}',
    'academy.prereg.page.first_name': 'First Name',
    'academy.prereg.page.last_name': 'Last Name',
    'academy.prereg.page.gender': 'Gender',
    'academy.prereg.page.gender_male': 'Male',
    'academy.prereg.page.gender_female': 'Female',
    'academy.prereg.page.dob': 'Date of Birth',
    'academy.prereg.page.phone': 'Phone Number',
    'academy.prereg.page.email': 'Email Address',
    'academy.prereg.page.address': 'Residential Address',
    'academy.prereg.page.guardian_header': 'Legal Guardian & Responsible Person',
    'academy.prereg.page.guardian_first_name': 'Guardian First Name',
    'academy.prereg.page.guardian_last_name': 'Guardian Last Name',
    'academy.prereg.page.guardian_relation': 'Relationship',
    'academy.prereg.page.guardian_relation_father': 'Father',
    'academy.prereg.page.guardian_relation_mother': 'Mother',
    'academy.prereg.page.guardian_relation_legal': 'Legal Guardian',
    'academy.prereg.page.guardian_relation_uncle': 'Uncle/Aunt',
    'academy.prereg.page.guardian_relation_grandparent': 'Grandparent',
    'academy.prereg.page.guardian_phone': 'Guardian Phone Number',
    'academy.prereg.page.guardian_email': 'Guardian Email Address',
    'academy.students.form.guardian_email': 'Guardian Email',
    'academy.prereg.page.authorized_pickup': 'Authorized persons to pick up child',
    'academy.prereg.page.authorized_pickup_placeholder': 'Format: Full Name + Relationship (one person per line)',
    'academy.prereg.page.adult_info_header': 'Professional Info & Emergency',
    'academy.prereg.page.profession': 'Profession / Current Status',
    'academy.prereg.page.profession_placeholder': 'e.g. University student, Engineer, Teacher...',
    'academy.prereg.page.emergency_contact': 'Emergency Contact (Name & Phone)',
    'academy.prereg.page.health_header': 'Medical Information (Optional)',
    'academy.prereg.page.health_issues_check': 'Special health concerns (Asthma, Diabetes, etc.)',
    'academy.prereg.page.health_issues_detail': 'Health issues details',
    'academy.prereg.page.allergies_check': 'Known allergies',
    'academy.prereg.page.allergies_detail': 'Allergy details',
    'academy.prereg.page.course_selection_header': 'Select Desired Course',
    'academy.prereg.page.no_courses': 'No specific course listed right now. You can choose with the administration.',
    'academy.prereg.page.summary_header': 'Request Summary',
    'academy.prereg.page.terms_consent': 'I certify the accuracy of the provided information and request my pre-registration.',
    'academy.prereg.page.terms_required_error': 'Please accept the registration terms.',
    'academy.prereg.page.success_title': 'Pre-Registration Received!',
    'academy.prereg.page.success_msg': 'Your pre-registration request was successfully sent to the administration of {0}.',
    'academy.prereg.page.reference_code': 'Reference: {0}',
    'academy.prereg.page.next_steps_title': 'Next steps:',
    'academy.prereg.page.next_steps_msg': 'The administrative team will review your file and contact you shortly to finalize your registration.',
    'academy.prereg.page.new_registration': 'New Registration',
    'academy.prereg.page.btn_prev': 'Previous',
    'academy.prereg.page.btn_next': 'Next',
    'academy.prereg.page.btn_submit': 'Submit Pre-Registration',
    'academy.prereg.page.btn_submitting': 'Submitting...',
    'academy.prereg.page.error_invalid_token': 'Invalid or expired pre-registration link.',
    'academy.prereg.page.error_load_failed': 'Unable to load agency information.',
    'academy.prereg.page.error_no_token': 'No pre-registration token provided.',
    'academy.prereg.page.error_submit_failed': 'Pre-registration submission failed.',
    'academy.prereg.page.error_submit_generic': 'An error occurred during submission validation.',
    'academy.prereg.page.recommended_courses': 'Courses recommended for your age',
    'academy.prereg.page.other_courses': 'Other available courses',
    'academy.prereg.page.recommended_badge': 'Recommended',
    'academy.prereg.page.programs_header': 'Available programs / cohorts',
    'academy.prereg.page.select_program': 'Choose a cohort (optional)',
    'academy.prereg.page.schedules_label': 'Scheduled time slots',
    'academy.prereg.page.time_preference': 'Preferred time slot',
    'academy.prereg.page.time_preference_required': 'Please select a preferred time slot.',
    'academy.prereg.page.price_label': 'Price: {0}',
    'academy.prereg.page.hours_label': '{0} hours',
    'academy.prereg.page.loi_18_07_consent': 'I consent to the processing of my personal data in accordance with Algerian Law 18-07 relating to the protection of personal data.',
    'academy.prereg.page.loi_18_07_required': 'Consent to Law 18-07 is required.',
    'academy.prereg.page.summary_course': 'Selected course',
    'academy.prereg.page.summary_program': 'Selected cohort',
    'academy.prereg.page.summary_time_pref': 'Preferred time slot',
    'academy.prereg.page.no_programs': 'No cohort scheduled for this course. Please choose a time slot below.',

    'academy.groups.title': 'Group & Cohort Planning',
    'academy.groups.subtitle': 'Cohort Kanban board, courses & auto-invoicing.',
    'academy.groups.add_new': 'Create New Group',
    'academy.groups.kanban.upcoming': 'Upcoming',
    'academy.groups.kanban.ongoing': 'Ongoing',
    'academy.groups.kanban.completed': 'Completed',
    'academy.groups.add_course_template': '+ New Template',
    'academy.groups.create_course_title': 'Create Course Template',
    'academy.groups.course_title': 'Course Title',
    'academy.groups.course_price': 'Tuition Fee ({0})',
    'academy.groups.course_hours': 'Total Hours',
    'academy.groups.course_desc': 'Syllabus & Description',
    'academy.groups.save_course': 'Save Template',
    'academy.groups.course_preview': 'Selected Course Template Details',

    'academy.scheduler.subtitle': 'Time x Room matrix with automated conflict checks.',
    'academy.scheduler.add_session': 'Schedule Session',

    'academy.attendance.title': 'Attendance & Session Execution',
    'academy.attendance.subtitle': 'Touch-friendly attendance checklist & 75% pass rate rule.',
    'academy.attendance.present': 'Present',
    'academy.attendance.absent': 'Absent',
    'academy.attendance.late': 'Late',
    'academy.attendance.excused': 'Excused',

    'academy.reports.title': 'Reports & Business Analytics',
    'academy.reports.subtitle': 'Revenue trends, teacher ranking & room utilization.',
    'academy.reports.export_csv': 'Export CSV',
    'academy.reports.print_pdf': 'Print / PDF',

    'academy.groups.course': 'Course:',
    'academy.groups.instructor': 'Instructor:',
    'academy.groups.unassigned': 'Unassigned',
    'academy.groups.timeline': 'Timeline:',
    'academy.groups.enrolled_count': 'Enrolled: {0} / {1} Students',
    'academy.groups.bulk_enroll': 'Bulk Enroll',
    'academy.groups.move_stage': 'Move Stage:',
    'academy.groups.complete_group': 'Complete Group',
    'academy.groups.create_title': 'Create New Learning Group',
    'academy.groups.name_label': 'Group Name (e.g. Fullstack Web - Sept 2026)',
    'academy.groups.course_template': 'Course Template',
    'academy.groups.primary_teacher': 'Primary Teacher',
    'academy.groups.max_capacity': 'Max Capacity (Students)',
    'academy.groups.start_date': 'Start Date',
    'academy.groups.expected_end_date': 'Expected End Date',
    'academy.groups.create_btn': 'Create Group',
    'academy.groups.enroll_title': 'Bulk Enroll Students: {0}',
    'academy.groups.enroll_desc': 'Check a student to enroll them immediately, or uncheck to remove them. Enrolling auto-generates an invoice for the course price ({0}).',
    'academy.groups.enroll_search_placeholder': 'Search for a student (name, email, phone)...',
    'academy.groups.enroll_no_results': 'No students match your search.',
    'academy.groups.enroll_assigned_count': '{0} / {1} enrolled',
    'academy.groups.confirm_enroll': 'Confirm Enrollments',
    'academy.groups.edit_title': 'Edit Learning Group',
    'academy.groups.save_btn': 'Save Changes',
    'academy.groups.save_success': 'Group updated successfully.',
    'academy.groups.enroll_success': '{0} student(s) enrolled successfully. Invoices generated.',
    'academy.groups.enroll_none_selected': 'No new students selected to enroll.',
    'academy.groups.error.enroll': 'Failed to enroll one or more students.',
    'academy.groups.error.unenroll': 'Failed to remove the enrollment.',
    'academy.groups.error.unenroll_has_payments': 'Cannot unenroll this student: a linked invoice already has payments.',
    'academy.groups.error.capacity': 'Group maximum capacity reached.',
    'academy.groups.list.grid': 'Grid',
    'academy.groups.list.table': 'Table',
    'academy.groups.confirm_delete': 'Are you sure you want to delete cohort "{0}"?',
    'academy.groups.section_info': 'Cohort Information',
    'academy.groups.section_dossier': 'Dossier Information',
    'academy.groups.back_to_list': 'Back to list',
    'academy.groups.search_placeholder': 'Search by name, course, teacher...',
    'academy.groups.table.name': 'Name',
    'academy.groups.table.course': 'Course Template',
    'academy.groups.table.teacher': 'Teacher',
    'academy.groups.table.status': 'Status',
    'academy.groups.table.enrolled': 'Enrolled',
    'academy.groups.table.actions': 'Actions',
    'academy.groups.form.dossier_code': 'Dossier Code',
    'academy.groups.form.dossier_lib': 'Dossier Label',
    'academy.groups.val_name': 'Group name is required.',
    'academy.groups.val_course': 'Please select a course template.',
    'academy.groups.val_teacher': 'Please select a teacher.',
    'academy.groups.val_capacity': 'Capacity must be greater than 0.',

    'academy.students.search_placeholder': 'Search students...',
    'academy.students.table.name': 'Student Name',
    'academy.students.table.contact': 'Contact Details',
    'academy.students.table.emergency': 'Emergency Contact',
    'academy.students.table.financial': 'Financial Snapshot',
    'academy.students.table.status': 'Status & Hold',
    'academy.students.dob_prefix': 'DOB:',
    'academy.students.modal.edit_title': 'Edit Student',
    'academy.students.modal.add_title': 'Add New Student',
    'academy.students.modal.first_name': 'First Name',
    'academy.students.modal.last_name': 'Last Name',
    'academy.students.modal.email': 'Email Address',
    'academy.students.modal.phone': 'Phone Number',
    'academy.students.modal.dob': 'Date of Birth',
    'academy.students.modal.emergency': 'Emergency Contact (Guardian Info)',
    'academy.students.modal.emergency_helper': 'e.g. Parent/Guardian name & phone',
    'academy.students.modal.save': 'Save Student',
    'academy.students.drawer.history': 'Enrollment History Timeline',
    'academy.students.drawer.enrolled_on': 'Enrolled on:',

    // ── StudentsPage — new keys (list, form, table) ───────────────────────
    'academy.students.new_student': 'New Student',
    'academy.students.back_to_list': 'Back to List',
    'academy.students.no_students': 'No students registered',
    'academy.students.confirm_delete': 'Delete "{0} {1}"?',
    'academy.students.confirm_edit': 'Do you want to edit information for {0} {1}?',
    'academy.students.confirm_accept_title': 'Validate Pre-Registration',
    'academy.students.confirm_accept_msg': 'Do you want to accept and validate the pre-registration of {0} {1}?',
    'academy.students.success_accept_title': 'Pre-Registration Validated!',
    'academy.students.success_accept_msg': 'Student {0} {1} is now active.',
    'academy.students.error_accept': 'Validation failed.',
    'academy.students.confirm_refuse_title': 'Refuse Pre-Registration',
    'academy.students.confirm_refuse_msg': 'Are you sure you want to refuse the pre-registration of {0} {1}?',
    'academy.students.success_refuse_title': 'Pre-Registration Refused',
    'academy.students.success_refuse_msg': 'The request for {0} {1} has been refused.',
    'academy.students.error_refuse': 'Refusal failed.',
    'academy.students.val_firstname': 'First name is required.',
    'academy.students.val_lastname': 'Last name is required.',
    'academy.students.val_gender': 'Gender is required.',
    'academy.students.val_dob': 'Date of birth is required.',
    'academy.students.val_health': 'Specify health issues.',
    'academy.students.val_allergies': 'Specify allergies.',
    'academy.students.val_guardian_first': 'Guardian first name is required.',
    'academy.students.val_guardian_last': 'Guardian last name is required.',
    'academy.students.val_guardian_rel': 'Relationship to child is required.',
    'academy.students.val_guardian_phone': 'Guardian phone is required.',
    'academy.students.val_image_right': 'Image rights consent is required.',
    'academy.students.table.registration_status': 'Registration',
    'academy.students.action_accept': 'Accept & Validate',
    'academy.students.action_refuse': 'Refuse',
    'academy.students.list.grid': 'Grid',
    'academy.students.list.table': 'Table',
    'academy.students.table.actions': 'Actions',
    'academy.students.table.dob_prefix': 'DOB:',
    'academy.students.table.invoiced': 'Invoiced',
    'academy.students.table.total_paid': 'Total Paid',
    'academy.students.table.rest_to_pay': 'Rest-to-Pay',
    'academy.students.form.title_new': 'New Student / Enrollment',
    'academy.students.form.title_edit': 'Edit: {0} {1}',
    'academy.students.form.category': 'Category',
    'academy.students.form.adult': 'Adult',
    'academy.students.form.junior': 'Junior',
    'academy.students.form.photo': 'Photo',
    'academy.students.form.change_photo': 'Change Photo',
    'academy.students.form.attachment': 'Attached File',
    'academy.students.form.attach_file': 'Attach File',
    'academy.students.form.view_file': 'Click to open file',

    'academy.students.form.dossier': 'Dossier',
    'academy.students.form.dos_code': 'Dossier Code',
    'academy.students.form.dos_lib': 'Dossier Label',
    'academy.students.form.last_modified': 'Last modified',
    'academy.students.form.modified_by': 'by',
    'academy.students.form.emergency_contact': 'Emergency Contact',
    'academy.students.financial_snapshot': 'Financial Snapshot',
    'academy.students.card.invoiced': 'Invoiced',
    'academy.students.card.paid': 'Paid',
    'academy.students.card.rest': 'Rest',

    // Form — detailed fields
    'academy.students.form.photo_caption': 'Profile Photo',
    'academy.students.form.file_label': 'Attachment (ID, medical, other…)',
    'academy.students.form.section_personal': 'Personal Information',
    'academy.students.form.registration_status': 'Registration Status',
    'academy.students.field.course_template': 'Course template (pre-registration)',
    'academy.students.field.time_preference': 'Preferred time slot',
    'academy.students.field.loi_consent': 'Law 18-07 consent',
    'academy.students.form.section_prereg': 'Pre-registration information',
    'academy.students.status.active': 'Active',
    'academy.students.status.preregistered': 'Pre-Registered',
    'academy.students.status.refused': 'Refused',
    'academy.students.form.firstname': 'First Name',
    'academy.students.form.lastname': 'Last Name',
    'academy.students.form.gender': 'Gender',
    'academy.students.form.gender_male': 'Male',
    'academy.students.form.gender_female': 'Female',
    'academy.students.form.dob': 'Date of Birth',
    'academy.students.form.address': 'Address',
    'academy.students.form.tab_adult': 'Adult (16+)',
    'academy.students.form.tab_junior': 'Junior / Kid',
    'academy.students.form.section_adult': 'Adult Information',
    'academy.students.form.profession': 'Profession',
    'academy.students.form.email': 'Email (optional)',
    'academy.students.form.phone': 'Phone',
    'academy.students.form.section_junior': 'Junior Information',
    'academy.students.form.school_level': 'School Level',
    'academy.students.form.blood_group': 'Blood Group',
    'academy.students.form.blood_group_none': 'Not specified',
    'academy.students.form.health_issues': 'Does the child have health issues?',
    'academy.students.form.health_specify': '(If Yes) Specify:',
    'academy.students.form.health_other_detail': 'Specify the condition',
    'academy.students.form.health_other_detail_placeholder': 'Describe the illness or health condition...',
    'academy.students.form.health_condition.asthma': 'Asthma',
    'academy.students.form.health_condition.diabetes': 'Diabetes',
    'academy.students.form.health_condition.epilepsy': 'Epilepsy',
    'academy.students.form.health_condition.other': 'Other',
    'academy.students.form.guardian_relation.parent': 'Father / Mother',
    'academy.students.form.guardian_relation.sibling': 'Brother / Sister',
    'academy.students.form.guardian_relation.grandparent': 'Grandfather / Grandmother',
    'academy.students.form.guardian_relation.uncle_aunt': 'Uncle / Aunt',
    'academy.students.form.guardian_relation.guardian': 'Tutor / Guardian',
    'academy.students.form.guardian_relation.other': 'Other',
    'academy.students.form.school_level.primary_1': '1st Year Primary',
    'academy.students.form.school_level.primary_2': '2nd Year Primary',
    'academy.students.form.school_level.primary_3': '3rd Year Primary',
    'academy.students.form.school_level.primary_4': '4th Year Primary',
    'academy.students.form.school_level.primary_5': '5th Year Primary',
    'academy.students.form.school_level.middle_1': '1st Year Middle School',
    'academy.students.form.school_level.middle_2': '2nd Year Middle School',
    'academy.students.form.school_level.middle_3': '3rd Year Middle School',
    'academy.students.form.school_level.middle_4': '4th Year Middle School',
    'academy.students.form.allergies': 'Allergies',
    'academy.students.form.allergy_details': 'Specify allergies',
    'academy.students.form.allergy_details_placeholder': 'Describe allergies (food, medication, etc.)...',
    'academy.students.form.image_right': 'Image Rights',
    'academy.students.form.yes': 'Yes',
    'academy.students.form.no': 'No',
    'academy.students.form.guardian_title': 'Guardian / Responsible Person',
    'academy.students.form.guardian_firstname': 'First Name',
    'academy.students.form.guardian_lastname': 'Last Name',
    'academy.students.form.guardian_relation': 'Relation to child',
    'academy.students.form.guardian_phone': 'Phone',
    'academy.students.form.authorized_pickup': 'Persons authorized to pick up the child',
    'academy.students.form.authorized_pickup_hint': 'Format: Full name + Relation',
    'academy.students.form.authorized_pickup_placeholder': 'Full name + Relation (one per line)',
    'academy.students.section_dossier': 'Dossier Information',


    'academy.teachers.busy_map_summary': 'Weekly Busy Map ({0} Booked Slots):',
    'academy.teachers.view_profile': 'View Profile & Schedule',
    'academy.teachers.modal.edit_title': 'Edit Teacher',
    'academy.teachers.modal.add_title': 'Add New Teacher',
    'academy.teachers.modal.fullname': 'Full Name',
    'academy.teachers.modal.email': 'Email Address',
    'academy.teachers.modal.phone': 'Phone Number',
    'academy.teachers.modal.specialization': 'Specialization (e.g. Fullstack Web, AI)',
    'academy.teachers.modal.hourly_rate': 'Payroll Hourly Rate ({0})',
    'academy.teachers.modal.bio': 'Bio / Notes',
    'academy.teachers.modal.save': 'Save Teacher',
    'academy.teachers.drawer.groups': 'Assigned Groups & Cohorts',
    'academy.teachers.drawer.timeline': 'Weekly Visual Busy Timeline',
    'academy.teachers.drawer.free': 'Free',
    'academy.teachers.form.title_new': 'New Teacher Profile',
    'academy.teachers.form.title_edit': 'Edit Teacher: {0} {1}',
    'academy.teachers.form.firstname': 'First Name',
    'academy.teachers.form.lastname': 'Last Name',
    'academy.teachers.form.gender': 'Gender / Sex',
    'academy.teachers.form.gender_male': 'Male',
    'academy.teachers.form.gender_female': 'Female',
    'academy.teachers.form.dob': 'Date of Birth',
    'academy.teachers.form.email': 'Email Address',
    'academy.teachers.form.phone': 'Phone Number',
    'academy.teachers.form.specialization': 'Specialties / Training',
    'academy.teachers.form.hire_date': 'Hire Date',
    'academy.teachers.form.status': 'Status',
    'academy.teachers.form.dossier_code': 'Dossier Code',
    'academy.teachers.form.dossier_lib': 'Dossier Name',
    'academy.teachers.form.rate_type': 'Rate Type',
    'academy.teachers.form.rate_type.hourly': 'Hourly',
    'academy.teachers.form.rate_type.daily': 'Daily',
    'academy.teachers.form.rate_type.monthly': 'Monthly',
    'academy.teachers.form.rate_type.percentage': 'Percentage',
    'academy.teachers.form.rate_type.fixed': 'Fixed amount',
    'academy.teachers.form.rate_amount': 'Rate Amount',
    'academy.teachers.form.bio': 'Bio / Remarks',
    'academy.teachers.form.cv_file': 'CV / Attachment',
    'academy.teachers.form.attach_cv': 'Attach CV (PDF/Doc)',
    'academy.teachers.form.view_cv': 'View CV',
    'academy.teachers.form.photo_caption': 'Profile photo',
    'academy.teachers.form.avatar_url': 'Avatar / Photo URL',
    'academy.teachers.table.teacher': 'Teacher',
    'academy.teachers.table.contact': 'Contact',
    'academy.teachers.table.specialization': 'Specialty',
    'academy.teachers.table.rate': 'Rate',
    'academy.teachers.table.status': 'Status',
    'academy.teachers.table.cv': 'CV',
    'academy.teachers.search_placeholder': 'Search by name, specialty, email...',
    'academy.teachers.back_to_list': 'Back to list',
    'academy.teachers.active_badge': 'Active',
    'academy.teachers.inactive_badge': 'Inactive',
    'academy.teachers.section_personal': 'Personal Information',
    'academy.teachers.section_professional': 'Professional Information',
    'academy.teachers.section_dossier': 'Dossier Information',


    'academy.scheduler.day_label': 'Day of Week',
    'academy.scheduler.matrix_title': 'Rooms (Y-Axis) \\ Time (X-Axis)',
    'academy.scheduler.cap_floor': 'Cap: {0} | {1}',
    'academy.scheduler.modal.title': 'Schedule Session Matrix',
    'academy.scheduler.modal.select_room': 'Select Room',
    'academy.scheduler.modal.select_group': 'Select Group / Cohort',
    'academy.scheduler.modal.select_teacher': 'Assign Primary Teacher',
    'academy.scheduler.modal.start_time': 'Start Time',
    'academy.scheduler.modal.end_time': 'End Time',
    'academy.scheduler.modal.recurring': 'Recurring Weekly Sessions',
    'academy.scheduler.modal.repeat_weeks': 'Repeat for N Weeks',
    'academy.scheduler.modal.confirm': 'Confirm Schedule',

    'academy.attendance.teacher_mode': 'Teacher Restricted Login Mode',
    'academy.attendance.my_sessions': 'My Today\'s Sessions',
    'academy.attendance.all_sessions': 'All Scheduled Sessions',
    'academy.attendance.taken': 'Attendance Taken',
    'academy.attendance.pending': 'Pending',
    'academy.attendance.room_instructor': 'Room: {0} • Instructor: {1}',
    'academy.attendance.enrolled_count': '{0} Enrolled Students',
    'academy.attendance.table.student': 'Student',
    'academy.attendance.table.pass_rate': 'Course Pass Rate',
    'academy.attendance.table.toggle': 'Attendance Toggle (Present / Absent / Late / Excused)',
    'academy.attendance.passing': 'Passing (>75%)',
    'academy.attendance.at_risk': 'At Risk',
    'academy.attendance.select_prompt': 'Select a session to take attendance.',
    'academy.attendance.no_sessions': 'No scheduled sessions.',
    'academy.attendance.no_students': 'No enrolled students in this group.',

    'academy.reports.teacher_perf_title': 'Teacher Attendance Performance',
    'academy.reports.teacher_perf_sub': 'Instructors with the highest student attendance rates in their classes.',
    'academy.reports.rate_label': '{0}% Rate',
    'academy.reports.room_util_title': 'Room Utilization Rates',
    'academy.reports.room_util_sub': 'Weekly booking efficiency (hours booked vs available 40h capacity).',
    'academy.reports.booked_label': '{0}% Booked',
    'academy.reports.churn_title': 'Student Churn & Dropped Report',
    'academy.reports.churn_sub': 'List of students who have dropped courses or discontinued attendance.',
    'academy.reports.zero_churn': 'Zero Student Churn!',
    'academy.reports.zero_churn_sub': 'All enrolled students are actively attending classes.',
    'academy.reports.table.student': 'Student',
    'academy.reports.table.group': 'Group / Cohort',
    'academy.reports.table.enrollment_date': 'Enrollment Date',
    'academy.reports.table.status': 'Status',

    'academy.common.group_number': 'Group #{0}',
    'academy.common.room': 'Room',
    'academy.common.teacher': 'Teacher',
    'academy.common.group': 'Group',
    'academy.common.group_session': 'Group Session',
    'academy.common.general': 'General',
    'academy.common.unassigned_floor': 'Unassigned Floor',
    'academy.common.students_count': '{0} Students',
    'academy.teachers.confirm_deactivate': 'Are you sure you want to deactivate this teacher?',
    'academy.attendance.error.hold_block': 'Cannot mark student present (financial hold).',
    'academy.scheduler.error.conflict': 'Scheduling conflict detected.',
    'academy.scheduler.tooltip.session': 'Group: {0} | Teacher: {1} | {2} Students',
    'academy.groups.error.status_change': 'Cannot change group status.',
    'academy.reports.capacity_short': 'Cap',

    // Maker - Projects
    'maker.projects.title': 'Projects',
    'maker.projects.new': 'New Project',
    'maker.projects.search': 'Search projects...',
    'maker.projects.filter.status': 'All Status',
    'maker.projects.filter.active': 'Active',
    'maker.projects.filter.inactive': 'Inactive',
    'maker.projects.loading': 'Loading projects...',
    'maker.projects.empty.title': 'No projects found',
    'maker.projects.empty.desc': 'Create your first project to get started',
    'maker.projects.create': 'Create Project',
    'maker.projects.card.noDesc': 'No description',
    'maker.projects.meta.created': 'Created on',

    // Maker - Fields
    'maker.field.name': 'Name',
    'maker.field.description': 'Description',
    'maker.field.customer': 'Customer',
    'maker.field.project': 'Project',
    'maker.field.code': 'Code',
    'maker.field.unitPrice': 'Unit Price',
    'maker.field.unit': 'Unit',
    'maker.field.stockQuantity': 'Stock Quantity',
    'maker.field.active': 'Active',
    'maker.field.width': 'Width (mm)',
    'maker.field.height': 'Height (mm)',
    'maker.field.depth': 'Depth (mm)',
    'maker.field.company': 'Company',
    'maker.field.email': 'Email',
    'maker.field.phone': 'Phone',
    'maker.field.address': 'Address',
    'maker.field.city': 'City',
    'maker.field.country': 'Country',
    'maker.field.postalCode': 'Postal Code',
    'maker.field.materialType': 'Material Type',
    'maker.field.photo': 'Photo',

    // Maker - Actions & Messages
    'action.create': 'Create',
    'maker.action.upload_photo': 'Upload Photo',
    'action.generating': 'Generating...',
    'maker.msg.success.create': 'Created successfully',
    'maker.msg.success.generate': 'Generated successfully!',
    'maker.msg.error.create': 'Creation failed',
    'maker.msg.error.generate': 'Generation failed',
    'maker.msg.error.required': 'Field is required',
    'maker.msg.noProjects': 'No projects available',
    'maker.msg.failedSave': 'Failed to save: {0}',

    // Maker - Customers
    'maker.customers.title': 'Customers',
    'maker.customers.new': 'New Customer',
    'maker.customers.search': 'Search Customer',
    'maker.customers.edit': 'Edit Customer',
    'maker.customers.loading': 'Loading customers...',
    'maker.customers.empty': 'No customers found. Create one to get started.',
    'maker.customers.delete.title': 'Confirm Delete',
    'maker.customers.delete.confirm': 'Are you sure you want to delete {0}?',
    'maker.customers.delete.action': 'Delete',

    // Maker - Materials
    'maker.materials.title': 'Materials',
    'maker.materials.add': 'Add Material',
    'maker.materials.edit': 'Edit Material',
    'maker.materials.search': 'Search materials...',
    'maker.materials.filter.all': 'All Types',
    'maker.materials.table.code': 'Code',
    'maker.materials.table.name': 'Name',
    'maker.materials.table.type': 'Type',
    'maker.materials.table.price': 'Unit Price',
    'maker.materials.table.unit': 'Unit',
    'maker.materials.table.stock': 'Stock',
    'maker.materials.table.status': 'Status',
    'maker.materials.table.actions': 'Actions',
    'maker.materials.loading': 'Loading...',
    'maker.materials.empty': 'No materials found',
    'maker.materials.type.aluminum': 'Aluminum',
    'maker.materials.type.pvc': 'PVC',
    'maker.materials.type.wood': 'Wood',
    'maker.materials.type.steel': 'Steel',
    'maker.materials.type.unknown': 'Unknown',
    'maker.materials.delete.title': 'Confirm Delete',
    'maker.materials.delete.confirm': 'Are you sure you want to delete {0}?',

    // Maker - Estimates
    'maker.estimates.title': 'Estimates',
    'maker.estimates.new': 'New Estimate',
    'maker.estimates.loading': 'Loading estimates...',
    'maker.estimates.empty.title': 'No estimates yet',
    'maker.estimates.empty.desc': 'Create and manage estimates for your projects',
    'maker.estimates.generate': 'Generate Estimate',
    'maker.estimates.table.number': 'Estimate Number',
    'maker.estimates.table.project': 'Project',
    'maker.estimates.table.date': 'Date',
    'maker.estimates.table.subtotal': 'Subtotal',
    'maker.estimates.table.tax': 'Tax',
    'maker.estimates.table.total': 'Total',
    'maker.estimates.table.status': 'Status',
    'maker.estimates.table.validity': 'Valid Until',

    // Maker - Production
    'maker.production.title': 'Production Planning',
    'maker.production.tabs.projects': 'Projects',
    'maker.production.tabs.bom': 'Bill of Materials',
    'maker.production.tabs.cutting': 'Cutting Plan',
    'maker.production.select_project': 'Select Project',
    'maker.production.loading': 'Loading...',
    'maker.production.door_count': 'Doors: {0}',
    'maker.production.window_count': 'Windows: {0}',
    'maker.production.bom.generate': 'Generate BOM',
    'maker.production.cutting.optimize': 'Optimize Cutting',
    'maker.production.bom.total_materials': 'Total Materials',
    'maker.production.bom.total_cost': 'Total Cost',
    'maker.production.bom.print': 'Print BOM',
    'maker.production.cutting.efficiency': 'Avg Efficiency',
    'maker.production.cutting.waste': 'Total Waste',
    'maker.production.cutting.required': 'Required: {0}',
    'maker.production.cutting.stock': 'Stock: {0}',
    'maker.production.cutting.waste_label': 'Waste: {0}',
    'maker.production.cutting.layout': 'Cutting Layout (Stock Length: {0})',

    // Maker - Design
    'maker.design.title': 'Design Studio',
    'maker.design.select_project': 'Select Project',
    'maker.design.new': 'New Design',
    'maker.design.templates': 'Templates',
    'maker.design.components': 'Components',
    'maker.design.export_cnc': 'Export CNC',
    'maker.design.reports': 'Reports',
    'maker.design.tab.design': 'Design View',
    'maker.design.tab.sheet': 'Sheet & Quote',
    'maker.design.tab.windows': 'Windows ({0})',
    'maker.design.tab.doors': 'Doors ({0})',
    'maker.design.items_list': 'Items',
    'maker.design.no_items': 'No items found',
    'maker.design.select_prompt': 'Please select a project to start designing',
    'maker.design.select_item_prompt': 'Please select an item from the list or create a new one',
    'maker.design.no_item_selected': 'No item selected',
    'maker.design.properties': 'Properties',
    'maker.design.field.width': 'Width (mm)',
    'maker.design.field.height': 'Height (mm)',
    'maker.design.field.material': 'Material',
    'maker.design.field.profile': 'Profile',
    'maker.design.action.save': 'Save',
    'maker.design.delete.confirm': 'Are you sure you want to delete this design?',
    'maker.design.modal.title': 'Create New {0}',
    'maker.design.modal.type': 'Type',
    'maker.design.modal.shape': 'Shape',
    'maker.design.modal.cancel': 'Cancel',
    'maker.design.modal.create': 'Create',
    'maker.design.validation.project': 'Please select a project',
    'maker.design.validation.dimensions': 'Dimensions must be positive and less than 5000mm',
    'maker.design.validation.profile': 'Please select a profile for the selected material',

    // Maker - Reports
    'maker.reports.title': 'Reports & Analytics',
    'maker.reports.type.label': 'Report Type',
    'maker.reports.type.sales': 'Sales Report',
    'maker.reports.type.projects': 'Projects Report',
    'maker.reports.type.materials': 'Materials Report',
    'maker.reports.type.customers': 'Customers Report',
    'maker.reports.type.production': 'Production Report',
    'maker.reports.export': 'Export CSV',
    'maker.reports.date.from': 'From',
    'maker.reports.date.to': 'To',
    'maker.reports.stat.revenue': 'Revenue',
    'maker.reports.stat.estimates': 'Estimates',
    'maker.reports.stat.approved': 'Approved',
    'maker.reports.stat.pending': 'Pending',
    'maker.reports.stat.avg_value': 'Avg Value',
    'maker.reports.stat.total_projects': 'Total Projects',
    'maker.reports.stat.completed': 'Completed',
    'maker.reports.stat.total_items': 'Total Items',
    'maker.reports.stat.total_materials': 'Total Materials',
    'maker.reports.stat.stock_value': 'Stock Value',
    'maker.reports.stat.active': 'Active',
    'maker.reports.stat.total_customers': 'Total Customers',
    'maker.reports.stat.project_value': 'Project Value',
    'maker.reports.stat.doors': 'Doors',
    'maker.reports.stat.windows': 'Windows',
    'maker.reports.table.estimate': 'Estimate #',
    'maker.reports.table.date': 'Date',
    'maker.reports.table.project': 'Project',
    'maker.reports.table.status': 'Status',
    'maker.reports.table.total': 'Total',
    'maker.reports.table.customer': 'Customer',
    'maker.reports.table.created': 'Created',
    'maker.reports.table.items': 'Items',
    'maker.reports.table.name': 'Name',
    'maker.reports.table.code': 'Code',
    'maker.reports.table.stock': 'Stock',
    'maker.reports.table.value': 'Value',
    'maker.reports.table.usage': 'Usage',
    'maker.reports.table.company': 'Company',
    'maker.reports.table.estimates': 'Estimates',
    'maker.reports.dist.material': 'Material Distribution',


    // Maker - Design Types
    'maker.design.type.casement': 'Casement',
    'maker.design.type.tilt_turn': 'Tilt & Turn',
    'maker.design.type.sliding': 'Sliding',
    'maker.design.type.french': 'French',
    'maker.design.type.italian': 'Italian',
    'maker.design.type.awning': 'Awning',
    'maker.design.type.parallel_sliding': 'Parallel Sliding',
    'maker.design.type.pivot': 'Pivot',
    'maker.design.type.accordion': 'Accordion',
    'maker.design.type.lift_sliding': 'Lift Sliding',
    'maker.design.type.single': 'Single',
    'maker.design.type.double': 'Double',
    'maker.design.type.folding': 'Folding',
    'maker.design.type.revolving': 'Revolving',
    'maker.design.type.unknown': 'Unknown',

    // Maker - Dashboard
    'maker.dashboard.title': 'Dashboard',
    'maker.dashboard.welcome': 'Welcome to BLOBTIC Maker - Door & Window Manufacturing Software',
    'maker.dashboard.stats.projects': 'Projects',
    'maker.dashboard.stats.estimates': 'Estimates',
    'maker.dashboard.stats.customers': 'Customers',
    'maker.dashboard.stats.materials': 'Materials',
    'maker.dashboard.quick_actions': 'Quick Actions',
    'maker.dashboard.recent_activity': 'Recent Activity',
    'maker.dashboard.no_activity': 'No recent activity',
    'maker.dashboard.loading': 'Loading...',

    // Maker - Profiles
    'maker.gamme.title': 'Gammes',
    'maker.profiles.title': 'Profiles Management',
    'maker.profiles.description': 'Profiles are pieces of material (Aluminum, PVC, etc.) used to manufacture window and door frames.',
    'maker.profiles.add': 'Add Profile',
    'maker.profiles.edit': 'Edit Profile',
    'maker.profiles.table.name': 'Name',
    'maker.profiles.table.code': 'Code',
    'maker.profiles.table.material': 'Material',
    'maker.profiles.table.dimensions': 'Dimensions (WxHxD)',
    'maker.profiles.table.price': 'Price',
    'maker.profiles.table.stock': 'Stock',
    'maker.profiles.table.actions': 'Actions',
    'maker.profiles.loading': 'Loading...',
    'maker.profiles.empty': 'No profiles found',
    'maker.profiles.delete.title': 'Confirm Delete',
    'maker.profiles.delete.confirm': 'Are you sure you want to delete this profile?',
    'maker.profiles.delete.action': 'Delete',
    'page.sys_parameters.group.performance': 'Performance & Cache',
    'page.sys_parameters.group.licensing': 'Licensing & Limits',
    'page.sys_parameters.group.maintenance': 'Maintenance & Cleanup',
    'page.sys_parameters.group.system': 'System & Tests',
    'page.sys_parameters.group.gps': 'GPS Tracking',
    'page.sys_parameters.group.sso': 'SSO Authentication (Google, Microsoft, OIDC)',
    'page.sys_parameters.group.other': 'Other System Parameters',

    // Parameter Labels (SYSPAR_LIB)
    'page.sys_parameters.lib.String_Envoi_Mail': 'SMTP Configuration for email sending',
    'page.sys_parameters.lib.Session_Expiration_Minutes': 'Session expiration time in minutes',
    'page.sys_parameters.lib.JWT_Expiration_Minutes': 'JWT expiration time in minutes',
    'page.sys_parameters.lib.Sync_Notifications_Timer_Seconds': 'Notification and chat refresh interval in seconds (0 = disabled)',
    'page.sys_parameters.lib.Default_App_Name': 'Default application name',
    'page.sys_parameters.lib.Licensed_Applications': 'List of licensed applications',
    'page.sys_parameters.lib.ObjectPool_MaxSize': 'Max object pool size',
    'page.sys_parameters.lib.DictionaryPool_MaxSize': 'Max dictionary pool size',
    'page.sys_parameters.lib.ListPool_MaxSize': 'Max list pool size',
    'page.sys_parameters.lib.DataManager_Cache_Duration_Minutes': 'DataManager cache duration in minutes',
    'page.sys_parameters.lib.Compression_Threshold_Bytes': 'Compression threshold in bytes',
    'page.sys_parameters.lib.Response_Cache_Duration_Seconds': 'Response cache duration in seconds',
    'page.sys_parameters.lib.Pool_Hit_Rate_Threshold': 'Pool hit rate threshold (%)',
    'page.sys_parameters.lib.Cache_Hit_Rate_Threshold': 'Cache hit rate threshold (%)',
    'page.sys_parameters.lib.Max_Creation_Time_Ms': 'Max creation time in milliseconds',
    'page.sys_parameters.lib.Alert_Cooldown_Minutes': 'Alert cooldown in minutes',
    'page.sys_parameters.lib.License_Max_Users': 'Max users per license',
    'page.sys_parameters.lib.License_Max_Dossiers': 'Max dossiers per license',
    'page.sys_parameters.lib.License_Storage_Limit_GB': 'Storage limit per license in GB',
    'page.sys_parameters.lib.LoadTest_Iterations': 'Number of iterations for load tests',
    'page.sys_parameters.lib.LoadTest_Operations_Per_Iteration': 'Number of operations per iteration',
    'page.sys_parameters.lib.LoadTest_Batch_Size': 'Batch size for load tests',
    'page.sys_parameters.lib.LoadTest_Batch_Delay_Ms': 'Delay between batches in milliseconds',
    'page.sys_parameters.lib.License_Monitoring_Interval_Minutes': 'License monitoring interval in minutes',
    'page.sys_parameters.lib.Auto_Cleanup_Expired_OTP_Hours': 'Expired OTP cleanup interval in hours',
    'page.sys_parameters.lib.Auto_Cleanup_Expired_Sessions_Hours': 'Expired session cleanup interval in hours',
    'page.sys_parameters.lib.GPS_API_Key': 'GPS API Key',
    'page.sys_parameters.lib.GPS_Endpoint_URL': 'GPS Endpoint URL',
    'page.sys_parameters.lib.GPS_Provider': 'GPS Provider',
    'page.sys_parameters.lib.Log_Retention_Days': 'Log retention duration in days',
    'page.sys_parameters.lib.Performance_Metrics_Retention_Hours': 'Performance metrics retention in hours',
    'page.sys_parameters.lib.OTP_Expiration_Minutes': 'OTP validity duration in minutes',
    'page.sys_parameters.lib.OTP_Max_Attempts': 'Max attempts for OTP codes',
    'page.sys_parameters.lib.Password_Min_Length': 'Minimum password length',
    'page.sys_parameters.lib.Double_Authentication_Enabled': 'Two-factor authentication enabled (0=Disabled, 1=Enabled)',
    'page.sys_parameters.lib.WEB_SECURITY_ENABLED': 'Web Security Enabled (0=Disabled, 1=Enabled)',
    'page.sys_parameters.lib.Double_Auth_Method': 'Two-factor auth method (OTP, SMS, EMAIL)',
    'page.sys_parameters.lib.Currency_Symbol': 'Currency Symbol (DZD,$, €, £, etc.)',
    'page.sys_parameters.lib.Geo_Restriction_Enabled': 'Geographic IP restriction enabled (0=Disabled, 1=Enabled)',
    'page.sys_parameters.lib.Geo_Restriction_Mode': 'Geographic restriction mode (Allow specified / Block specified)',
    'page.sys_parameters.lib.Geo_Restriction_Countries': 'Comma-separated ISO country codes to allow/block (e.g. DZ for Algeria)',

    'page.sys_parameters.lib.IDXDB_CACHE_ENABLED': 'Client-side local DB cache (0=Disabled, 1=Enabled)',
    'page.sys_parameters.lib.SSO_ENABLED': 'Global Single Sign-On Enabled (0=Disabled, 1=Enabled)',
    'page.sys_parameters.lib.SSO_DEFAULT_PROVIDER': 'Default SSO Provider (google, microsoft, oidc)',
    'page.sys_parameters.lib.SSO_GOOGLE_ENABLED': 'Google Workspace SSO Enabled (0=Disabled, 1=Enabled)',
    'page.sys_parameters.lib.SSO_GOOGLE_CLIENT_ID': 'Google Workspace OAuth 2.0 Client ID',
    'page.sys_parameters.lib.SSO_GOOGLE_CLIENT_SECRET': 'Google Workspace OAuth 2.0 Client Secret',
    'page.sys_parameters.lib.SSO_MICROSOFT_ENABLED': 'Microsoft Entra ID SSO Enabled (0=Disabled, 1=Enabled)',
    'page.sys_parameters.lib.SSO_MICROSOFT_CLIENT_ID': 'Microsoft Entra ID Client ID / App ID',
    'page.sys_parameters.lib.SSO_MICROSOFT_CLIENT_SECRET': 'Microsoft Entra ID Client Secret',
    'page.sys_parameters.lib.SSO_MICROSOFT_TENANT_ID': 'Microsoft Tenant ID (common, organizations, or tenant GUID)',
    'page.sys_parameters.lib.SSO_OIDC_ENABLED': 'Generic OpenID Connect SSO Enabled (0=Disabled, 1=Enabled)',
    'page.sys_parameters.lib.SSO_OIDC_NAME': 'Display name for Generic OpenID Connect provider',
    'page.sys_parameters.lib.SSO_OIDC_AUTHORITY': 'OIDC Authority / Realm root URL',
    'page.sys_parameters.lib.SSO_OIDC_CLIENT_ID': 'Generic OpenID Connect Client ID',
    'page.sys_parameters.lib.SSO_OIDC_CLIENT_SECRET': 'Generic OpenID Connect Client Secret',
    // Dropdown Options
    'page.sys_parameters.option.enabled': 'Enabled',
    'page.sys_parameters.option.disabled': 'Disabled',
    'page.sys_parameters.option.geo.allow_only': 'Allow specified countries only',
    'page.sys_parameters.option.geo.block_only': 'Block specified countries only',
    'page.sys_parameters.option.method.otp': 'OTP (One-Time Password)',
    'page.sys_parameters.option.method.sms': 'SMS',
    'page.sys_parameters.option.method.email': 'Email',

    'page.parameters.title': 'System Parameters (Legacy)',
    'page.parameters.subtitle': 'Global application configuration management',
    'page.parameters.table.code': 'Code',
    'page.parameters.table.label': 'Label',
    'page.parameters.table.value': 'Value',
    'page.parameters.table.type': 'Type',
    'page.parameters.table.actions': 'Actions',
    'page.parameters.table.empty': 'No parameters found',
    'page.parameters.action.refresh': 'Refresh',
    'page.parameters.action.edit': 'Edit',
    'page.parameters.dialog.title': 'Edit parameter {0}',
    'page.parameters.dialog.description': 'Description',
    'page.parameters.dialog.value': 'Value',
    'page.parameters.dialog.save': 'Save',
    'page.parameters.dialog.cancel': 'Cancel',
    'page.parameters.success.title': 'Parameter updated',
    'page.parameters.success.message': 'Parameter {0} has been modified.',
    'page.parameters.error.title': 'Error',
    'page.parameters.error.update': 'Unable to update parameter.',
    'page.parameters.error.load': 'Unable to load parameters.',
    'traffic.table.country': 'Country',
    'traffic.stats.distinct_countries': 'Distinct Countries',

    // Common / GenericPage
    'common.actions.new': 'New',
    'common.actions.edit': 'Edit',
    'common.actions.delete': 'Delete',
    'common.actions.save': 'Save',
    'common.actions.update': 'Update',
    'common.actions.insert': 'Insert',
    'common.actions.insert_copy': 'Duplicate (Insert as new)',
    'common.actions.duplicate': 'Duplicate',
    'common.actions.confirm': 'Confirm',
    'common.actions.close': 'Close',
    'common.actions.cancel': 'Cancel',
    // boubaker 23/08/2026 - print pages (GenericPage type print)
    'common.actions.print': 'Print',
    'common.actions.excel': 'Excel',
    'common.actions.word': 'Word',
    'common.actions.filters': 'Show/Hide filters',
    'common.confirm.title.delete': 'Confirm Deletion',
    'common.confirm.title.update': 'Confirm Update',
    'common.confirm.title.insert': 'Confirm Insertion',
    'common.confirm.delete': 'Are you sure you want to delete this item?',
    'common.confirm.update': 'Are you sure you want to update this item?',
    'common.confirm.insert': 'Are you sure you want to insert this item as a new record?',
    'common.labels.lastUpdate': 'Last update:',
    'common.labels.user': 'User:',
    'common.messages.success': 'Success',
    'common.messages.warning': 'Warning',
    'common.messages.error': 'Error',
    'common.messages.saved': 'Update completed',
    'common.messages.created': 'Creation completed',
    'common.messages.deleted_success': 'Deletion completed',
    'common.messages.save_error': 'Error during save',
    'common.messages.delete_error': 'Error during deletion',
    'common.messages.comm_error': 'Communication error',

    // Dates
    'common.date.today': 'Today',
    'common.date.yesterday': 'Yesterday',
    'common.date_time': 'Date and time',

    // Top Navigation & Actions
    'common.actions.chat': 'Chat',
    'common.actions.notifications': 'Notifications',
    'common.actions.lightMode': 'Light Mode',
    'common.actions.darkMode': 'Dark Mode',
    'common.actions.profile': 'Profile',
    'common.actions.markAllRead': 'Mark all read',
    'common.actions.clear': 'Clear all',
    'common.messages.noNotifications': 'No new notifications',
    'common.placeholder.message': 'Type a message...',

    // Menu Planning
    'menu.planning.commande': 'Order',
    'menu.planning.planning': 'Planning',
    'menu.planning.facturation': 'Billing',
    'menu.planning.editions': 'Editions',
    'menu.planning.entite': 'Entity',
    'menu.planning.administration': 'Administration',
    'menu.planning.admin.configMenu': 'Menu Configuration',
    'menu.planning.admin.configKpi': 'KPI Profile Assignment',
    'admin.config_kpi.select_app': 'Application',
    'admin.config_kpi.select_profile': 'Select a User Profile',
    'admin.config_kpi.search_profile': 'Search for a profile...',
    'admin.config_kpi.title': 'KPI Configuration',
    'admin.config_kpi.save': 'Save',
    'admin.config_kpi.saving': 'Saving...',
    'admin.config_kpi.load_error': 'Error loading KPIs',
    'admin.config_kpi.select_profile_required': 'Please select a profile',
    'admin.config_kpi.save_success': 'KPI configuration saved successfully',
    'admin.config_kpi.save_error': 'Error saving configuration',
    'admin.config_kpi.save_server_error': 'Server error while saving',
    'admin.config_kpi.kpi_count': '{app} — {enabled}/{total} active',
    'admin.config_kpi.mode_config': 'Configuration',
    'admin.config_kpi.mode_live': 'Live Mode (test)',
    'admin.config_kpi.select_all': 'Select All',
    'admin.config_kpi.deselect_all': 'Deselect All',
    'admin.config_kpi.no_kpi': 'No KPIs available for this application.',
    'admin.config_kpi.live_preview': 'Preview with sample data — actual render depends on data.',
    'admin.config_kpi.type_widget': 'Widget',
    'admin.config_kpi.type_card': 'Card',
    'admin.config_kpi.order': 'Order',
    'admin.config_kpi.width': 'Width',
    'admin.config_kpi.span.quarter': '1/4 row',
    'admin.config_kpi.span.third': '1/3 row',
    'admin.config_kpi.span.half': '1/2 row',
    'admin.config_kpi.span.two_thirds': '2/3 row',
    'admin.config_kpi.span.full': 'Full row',
    'admin.config_kpi.widget.today_prereg': "Today's Pre-Registrations",
    'admin.config_kpi.widget.today_schedule': "Today's Schedule",
    'admin.config_kpi.widget.low_attendance': 'Low Attendance Alert',
    'admin.config_kpi.widget.outstanding_debt': 'Outstanding Debt',
    'menu.planning.admin.securityWeb': 'Web Security',
    'menu.planning.admin.societe': 'Company',
    'menu.planning.admin.societe.agence': 'Agency',
    'menu.planning.admin.accessRights': 'Access Rights',
    'menu.planning.admin.accessRights.profiles': 'Profiles',
    'menu.planning.admin.accessRights.users': 'Users',
    'menu.planning.admin.parameters': 'Parameters',
    'menu.planning.admin.userDossiers': 'User Dossiers',



    // Menu Default
    'menu.default.dashboard': 'Dashboard',

    // Page - Menu Config
    'page.menuConfig.title': 'Menu Configuration',
    'page.menuConfig.profil.label': 'Profile',
    'page.menuConfig.profil.placeholder': 'Select a profile',
    'page.menuConfig.profil.helper': 'Click the magnifying glass to search',
    'page.menuConfig.structure.title': 'Menu structure for {0}',
    'page.menuConfig.structure.empty': 'Please select a profile',
    'page.menuConfig.success.save': 'Menu configuration saved',
    'page.menuConfig.error.load': 'Unable to load menu configuration',
    'page.menuConfig.error.save': 'Error saving configuration',
    'page.menuConfig.accessDenied': 'Access denied. Administrators only.',

    // Design Sheet
    'maker.design.sheet.title': 'Design Sheet & Quotation',
    'maker.design.sheet.empty.title': 'No Design Selected',
    'maker.design.sheet.empty.desc': 'Select a window or door to view the design sheet',
    'maker.design.sheet.section.dimensions': 'Dimensions',
    'maker.design.sheet.section.specs': 'Specifications',
    'maker.design.sheet.section.components': 'Components',
    'maker.design.sheet.section.quotation': 'Quotation',
    'maker.design.sheet.label.width': 'Width (mm)',
    'maker.design.sheet.label.height': 'Height (mm)',
    'maker.design.sheet.label.area': 'Area',
    'maker.design.sheet.label.type': 'Type',
    'maker.design.sheet.label.shape': 'Shape',
    'maker.design.sheet.label.material': 'Material',
    'maker.design.sheet.label.profile': 'Profile',
    'maker.design.sheet.quotation.unitPrice': 'Unit Price',
    'maker.design.sheet.quotation.quantity': 'Quantity',
    'maker.design.sheet.quotation.discount': 'Discount (%)',
    'maker.design.sheet.quotation.basePrice': 'Base Price',
    'maker.design.sheet.quotation.subtotal': 'Subtotal',
    'maker.design.sheet.quotation.total': 'Total',
    'maker.design.sheet.quotation.notes': 'Add notes or special instructions...',
    'maker.design.sheet.noComponents': 'No components added',
    // Parc Module (Auto-extracted)
    'parc.alerts.fetchError': 'Failed to load GPS alerts',
    'parc.alerts.resolveError': 'Failed to resolve alert',
    'parc.alerts.vehicle': 'Vehicle',
    'parc.alerts.type': 'Alert Type',
    'parc.alerts.message': 'Message',
    'parc.alerts.time': 'Time',
    'parc.alerts.status': 'Status',
    'parc.alerts.statusResolved': 'Resolved',
    'parc.alerts.statusActive': 'Active',
    'common.actions.refresh': 'Refresh',
    'parc.alerts.title': 'GPS Alerts',
    'parc.alerts.resolveAlertTitle': 'Resolve GPS Alert',
    'parc.dashboard.fetchError': 'Failed to load dashboard data',
    'parc.dashboard.title': 'Dashboard',
    'parc.dashboard.overview': 'Overview of your fleet',
    'parc.dashboard.vehicles': 'Vehicles',
    'parc.dashboard.totalRegistered': 'Total registered',
    'parc.dashboard.drivers': 'Drivers',
    'parc.dashboard.maintenances': 'Maintenances',
    'parc.dashboard.pendingOrInProgress': 'Pending or in progress',
    'parc.dashboard.repairs': 'Repairs',
    'parc.dashboard.toCheckOrInProgress': 'To check or in progress',
    'parc.dashboard.vehicleStatus': 'Vehicle Status',
    'common.status.noData': 'No data available',
    'parc.dashboard.monthlyPerformance': 'Monthly Performance (Trips)',
    'parc.dashboard.trips': 'Trips',
    'parc.dashboard.alerts': 'Alerts',
    'parc.dashboard.noAlerts': 'No alerts at the moment',
    'parc.dashboard.recentActivity': 'Recent Activity',
    'parc.dashboard.noRecentActivity': 'No recent activity',
    'parc.documents.fetchError': 'Failed to load data',
    'common.actions.deleteConfirm': 'Are you sure you want to delete this item?',
    'common.actions.actions': 'Actions',
    'common.messages.deleted': 'Deleted successfully',
    'parc.documents.docTitle': 'Title',
    'parc.documents.type': 'Type',
    'parc.documents.vehicle': 'Vehicle',
    'parc.documents.driver': 'Driver',
    'parc.documents.expiryDate': 'Expiry Date',
    'parc.documents.newDocument': 'New Document',
    'parc.documents.title': 'Documents',
    'parc.documents.editDocument': 'Edit Document',
    'parc.documents.typeInsurance': 'Insurance',
    'parc.documents.typeTechControl': 'Technical Control',
    'parc.documents.typeRegistration': 'Registration',
    'parc.documents.typeContract': 'Lease/Contract',
    'parc.documents.typeOther': 'Other',
    'parc.documents.assignVehicle': 'Assign to Vehicle',
    'common.labels.none': 'N/A',
    'parc.documents.assignDriver': 'Assign to Driver',
    'common.messages.unknownError': 'Unknown error',
    'parc.drivers.fetchError': 'Failed to load drivers',
    'parc.drivers.firstName': 'First Name',
    'parc.drivers.lastName': 'Last Name',
    'parc.drivers.licenseNumber': 'License Number',
    'parc.drivers.licenseType': 'License Type',
    'parc.drivers.experienceYears': 'Experience (Years)',
    'parc.drivers.status': 'Status',
    'parc.drivers.newDriver': 'New Driver',
    'parc.drivers.title': 'Drivers',
    'parc.drivers.editDriver': 'Edit Driver',
    'parc.drivers.statusActive': 'Active',
    'parc.drivers.statusInactive': 'Inactive',
    'parc.drivers.statusSuspended': 'Suspended',
    'parc.drivers.statusOnLeave': 'On Leave',
    'parc.maintenance.fetchError': 'Failed to load maintenance data',
    'parc.maintenance.mainTitle': 'Title',
    'parc.maintenance.vehicle': 'Vehicle',
    'parc.maintenance.type': 'Type',
    'parc.maintenance.category': 'Category',
    'parc.maintenance.status': 'Status',
    'parc.maintenance.priority': 'Priority',
    'parc.maintenance.newMaintenance': 'New Maintenance',
    'parc.maintenance.title': 'Maintenance',
    'parc.maintenance.editMaintenance': 'Edit Maintenance',
    'parc.maintenance.typePreventive': 'Preventive',
    'parc.maintenance.typeCorrective': 'Corrective',
    'parc.maintenance.typeEmergency': 'Emergency',
    'parc.maintenance.catEngine': 'Engine',
    'parc.maintenance.catTransmission': 'Transmission',
    'parc.maintenance.catBrakes': 'Brakes',
    'parc.maintenance.catElectrical': 'Electrical',
    'parc.maintenance.catBody': 'Body',
    'parc.maintenance.catTires': 'Tires',
    'parc.maintenance.catOther': 'Other',
    'parc.maintenance.priorityLow': 'Low',
    'parc.maintenance.priorityMedium': 'Medium',
    'parc.maintenance.priorityHigh': 'High',
    'parc.maintenance.priorityUrgent': 'Urgent',
    'parc.maintenance.statusScheduled': 'Scheduled',
    'parc.maintenance.statusInProgress': 'In Progress',
    'parc.maintenance.statusCompleted': 'Completed',
    'parc.maintenance.statusCancelled': 'Cancelled',
    'parc.maintenance.cost': 'Cost',
    'parc.repairs.fetchError': 'Failed to load data',
    'parc.repairs.vehicle': 'Vehicle',
    'parc.repairs.date': 'Date',
    'parc.repairs.garage': 'Garage',
    'parc.repairs.cost': 'Cost',
    'parc.repairs.status': 'Status',
    'parc.repairs.newRepair': 'New Repair',
    'parc.repairs.title': 'Repairs',
    'parc.repairs.editRepair': 'Edit Repair',
    'parc.repairs.statusPlanned': 'Planned',
    'parc.repairs.statusInProgress': 'In Progress',
    'parc.repairs.statusCompleted': 'Completed',
    'parc.repairs.statusCancelled': 'Cancelled',
    'parc.repairs.description': 'Description',
    'parc.gpsTracking.fetchError': 'Failed to load GPS positions',
    'parc.gpsTracking.vehicle': 'Vehicle',
    'parc.gpsTracking.brand': 'Brand',
    'parc.gpsTracking.latitude': 'Latitude',
    'parc.gpsTracking.longitude': 'Longitude',
    'parc.gpsTracking.speedCol': 'Speed (km/h)',
    'parc.gpsTracking.lastUpdate': 'Last Update',
    'parc.gpsTracking.title': 'Suivi GPS',
    'parc.gpsTracking.unknownVehicle': 'Unknown Vehicle',
    'parc.gpsTracking.speedVal': 'Speed:',
    'parc.gpsTracking.updatedVal': 'Updated:',
    'parc.trips.fetchError': 'Failed to load trips data',
    'parc.trips.tripTitle': 'Title',
    'parc.trips.vehicle': 'Vehicle',
    'parc.trips.driver': 'Driver',
    'parc.trips.purpose': 'Purpose',
    'parc.trips.status': 'Status',
    'parc.trips.newTrip': 'New Trip',
    'parc.trips.title': 'Trips',
    'parc.trips.editTrip': 'Edit Trip',
    'parc.trips.purposeBusiness': 'Business',
    'parc.trips.purposePersonal': 'Personal',
    'parc.trips.purposeMaintenance': 'Maintenance',
    'parc.trips.purposeDelivery': 'Delivery',
    'parc.trips.purposeOther': 'Other',
    'parc.trips.statusScheduled': 'Scheduled',
    'parc.trips.statusInProgress': 'In Progress',
    'parc.trips.statusCompleted': 'Completed',
    'parc.trips.statusCancelled': 'Cancelled',
    'parc.vehicles.fetchError': 'Failed to load vehicles',
    'parc.vehicles.registration': 'Registration Number',
    'parc.vehicles.brand': 'Brand',
    'parc.vehicles.model': 'Model',
    'parc.vehicles.year': 'Year',
    'parc.vehicles.status': 'Status',
    'parc.vehicles.newVehicle': 'New Vehicle',
    'parc.vehicles.title': 'Vehicles',
    'parc.vehicles.editVehicle': 'Edit Vehicle',
    'parc.vehicles.statusActive': 'Active',
    'parc.vehicles.statusMaintenance': 'Maintenance',
    'parc.vehicles.statusOutOfService': 'Out of Service',
    'parc.vehicles.mileage': 'Mileage',
    'parc.vehicles.fuelType': 'Fuel Type',
    'parc.vehicles.fuelDiesel': 'Diesel',
    'parc.vehicles.fuelGasoline': 'Gasoline',
    'parc.vehicles.fuelElectric': 'Electric',
    'parc.vehicles.fuelHybrid': 'Hybrid',
    'parc.vehicles.transmission': 'Transmission',
    'parc.vehicles.transManual': 'Manual',
    'parc.vehicles.transAutomatic': 'Automatic',
    'grid.filters.title': 'Filters',
    'grid.search.placeholder': 'Search...',
    'grid.filters.all': 'All',
    'grid.filters.clear': 'Clear',
    'grid.counter.item': 'item',
    'grid.counter.items': 'items',
    'grid.noData': 'No items match your search.',
    'grid.pagination.info': 'Showing {0}-{1} of {2} entries',
    'grid.pagination.pageSize': 'Items per page:',
    'parc.fuel.newTransaction': 'Log Refueling',
    'parc.fuel.editTransaction': 'Edit Transaction',
    'parc.fuel.station': 'Service Station',
    'parc.fuel.product': 'Product (e.g. Diesel, Gas)',
    'parc.fuel.liters': 'Liters',
    'parc.fuel.pricePerLiter': 'Price per Liter',
    'parc.fuel.cost': 'Total Cost (DZD)',
    'parc.fuel.odometer': 'Odometer (km)',
    'parc.fuel.fuelCard': 'Fuel Card',
    'parc.fuel.none': 'None',
    'parc.fuelcards.cardNumber': 'Card Number',
    'parc.fuelcards.vehicle': 'Assigned Vehicle',
    'parc.fuelcards.driver': 'Assigned Driver',
    'parc.fuelcards.globalCost': 'Global Cost Consumed',
    'parc.fuelcards.pricePerLiter': 'Base Price/Liter',
    'parc.fuelcards.newCard': 'New Card',
    'parc.fuelcards.editCard': 'Edit Card',
    'parc.fuelcards.addCard': 'Add Fuel Card',
    'parc.mileage.newLog': 'New Reading',
    'parc.mileage.editLog': 'Edit Reading',
    'parc.mileage.addLog': 'Add Odometer Reading',
    'parc.mileage.startDate': 'Start Date',
    'parc.mileage.endDate': 'End Date',
    'parc.mileage.startKm': 'Start KM',
    'parc.mileage.endKm': 'End KM',
    'parc.mileage.distance': 'Distance',
    'parc.mileage.errorEndKmLessThanStartKm': 'End odometer reading cannot be less than start odometer reading.',
    'parc.lookups.title': 'Reference Tables',
    'parc.lookups.proprietors': 'Owners / Proprietors',
    'parc.lookups.fuelTypes': 'Fuel Types',
    'parc.lookups.affectations': 'Departments / Services',
    'parc.lookups.docTypes': 'Document Types',
    'parc.lookups.mntTypes': 'Maintenance Types',
    'parc.gpsalerts.title': 'GPS Alerts',
    'parc.gpsalerts.newAlert': 'New Alert',
    'parc.gpsalerts.editAlert': 'Edit Alert',
    'parc.gpsalerts.date': 'Date & Time',
    'parc.gpsalerts.type': 'Type',
    'parc.gpsalerts.message': 'Message',
    'parc.gpsalerts.status': 'Status',
    'parc.gpsalerts.type.speeding': 'Speeding',
    'parc.gpsalerts.type.geofence': 'Geofence',
    'parc.gpsalerts.type.maintenance': 'Maintenance',
    'parc.gpsalerts.type.system': 'System',
    'parc.gpsalerts.type.other': 'Other',
    'parc.gpsalerts.status.active': 'Active',
    'parc.gpsalerts.status.resolved': 'Resolved',
    'parc.gpsalerts.status.ignored': 'Ignored',
    'parc.analysis.generalTitle': 'General Fleet Analysis',
    'parc.analysis.kmsTraveled': 'Kilometers Traveled',
    'parc.analysis.fuelExpenses': 'Fuel Expenses',
    'parc.analysis.availabilityIndex': 'Fleet Availability Index',
    'parc.analysis.optimizations': 'Optimizations & Estimates',
    'parc.analysis.avgConsumption': 'Average Fleet Consumption:',
    'parc.analysis.estAnnualFuel': 'Estimated Annual Fuel Cost:',
    'parc.analysis.nextOilChange': 'Estimated Next Oil Change Date:',
    'parc.analysis.warningTitle': 'Warning - Fleet Status',
    'parc.analysis.warningMaintOrOut': '{0}% of the fleet is in maintenance or out of service.',
    'parc.analysis.vehiclesActiveImmobilized': '{0} active / {1} immobilized',
    'parc.analysis.kmsCumulatedDesc': 'Cumulative fleet history mileage',
    'parc.analysis.litersConsumedTotal': '{0} Liters consumed in total',
    'parc.analysis.availabilityIndexDesc': 'This index shows the proportion of vehicles currently operational relative to total fleet size. A score above 90% indicates excellent maintenance planning.',
    'parc.analysis.insufficientData': 'Insufficient data',
    'parc.analysis.nextOilChangeValue': 'In 45 days (Estimated)',
    'parc.analysis.tcoTitle': 'TCO Analysis (Total Cost of Ownership)',
    'parc.analysis.globalTco': 'Global TCO',
    'parc.analysis.totalFuel': 'Total Fuel',
    'parc.analysis.totalMaint': 'Total Maintenance',
    'parc.analysis.totalRepairs': 'Total Repairs',
    'parc.analysis.costPerKm': 'Cost / km',
    'parc.dashboard.totalFleetCost': 'TOTAL FLEET COST',
    'parc.dashboard.fuelCostSummary': '⛽ {0}K DA fuel · 🔧 {1}K DA maint.',
    'parc.dashboard.activeFleet': 'ACTIVE FLEET',
    'parc.dashboard.activeVehiclesCount': '{0} / {1} veh.',
    'parc.dashboard.avgCostPerVehicle': 'Avg Cost: {0} DA / veh.',
    'parc.dashboard.mileageSummary': '{0} DA/km · {1} DA/L (avg.)',
    'parc.dashboard.activeAlerts': 'ACTIVE ALERTS',
    'parc.dashboard.alertsSummary': '📋 {0} doc. · 🔧 {1} maint. · ⛽ {2} conso.',
    'parc.dashboard.distributionByProprietor': 'Distribution by Owner',
    'parc.dashboard.oxxoProperty': 'OXXO OWNED',
    'parc.dashboard.vehiclesCount': '{0} veh.',
    'parc.dashboard.totalCostKDA': 'Total cost: {0} K DA',
    'parc.dashboard.cevitalRental': 'CEVITAL RENTAL',
    'parc.dashboard.otherOwner': 'OTHER OWNER',
    'parc.dashboard.monthlyCostsCumulated': 'Cumulative Monthly Costs',
    'parc.dashboard.monthlyCostsCumulatedDesc': 'Fuel + Maintenance & Repairs over last 6 months (DZD)',
    'parc.dashboard.expenseBreakdown': 'Expense Breakdown',
    'parc.dashboard.expenseBreakdownDesc': 'Cumulative overall volume by expense category',
    'parc.dashboard.top6Vehicles': 'Top 6 — Most Expensive Vehicles',
    'parc.dashboard.top6VehiclesDesc': 'Cumulative total cost (Fuel + Maintenance) in DA',
    'parc.dashboard.consumptionEfficiency': 'Fuel Consumption L/100km',
    'parc.dashboard.consumptionEfficiencyDesc': 'Average consumption per vehicle compared to thresholds (liters/100km)',
    'parc.dashboard.insufficientMileageData': 'Insufficient mileage data',
    'parc.dashboard.checkFuelMileageHistory': 'Please check fuel and mileage history to recalculate',
    'parc.dashboard.catVidange': 'Oil Change',
    'parc.dashboard.catRevision': 'Overhaul',
    'parc.dashboard.catBattery': 'Battery',
    'parc.dashboard.catBelt': 'Belt',
    'parc.dashboard.catInjection': 'Injection',
    'parc.alerts.typeConfiguration': 'Configuration',
    'parc.alerts.noTelegramChatId': 'No Telegram Chat ID configured.',
    'parc.alerts.techControlExpiringSoon': 'Technical control of assigned vehicle expires in 5 days.',
    'parc.alerts.dispatchError': 'Dispatch Error',
    'parc.alerts.noChatIdAssociated': 'No Telegram Chat ID associated with this driver.',
    'parc.alerts.telegramSentSuccess': 'Telegram alert successfully sent to {0}!',
    'parc.alerts.sendFailed': 'Send Failed',
    'parc.alerts.telegramContactError': 'Unable to contact Telegram server.',
    'parc.alerts.selectDriverAndMessage': 'Please select a driver and enter a message.',
    'parc.alerts.noChatId': 'No Chat ID',
    'parc.alerts.driverNoTelegram': 'This driver does not have a registered Telegram account.',
    'parc.alerts.customMessageSentSuccess': 'Custom message sent on Telegram to {0}.',
    'parc.alerts.sendErrorOccurred': 'An error occurred while sending.',
    'parc.alerts.consoleTitle': 'Alert Console & Bot Messages',
    'parc.alerts.telegramConfigInfo': 'To receive automatic notifications, configure the Telegram Chat ID in each driver profile. Drivers must first start the fleet bot.',
    'parc.alerts.activeFleetAlerts': 'Active Fleet Alerts',
    'parc.alerts.target': 'Target',
    'parc.alerts.notify': 'Notify',
    'parc.alerts.noAlertsPending': 'No pending alerts',
    'parc.alerts.sendManualMessage': 'Send Manual Message',
    'parc.alerts.sendManualMessageDesc': 'Send an instant directive to a driver on their personal Telegram channel.',
    'parc.alerts.recipientDriver': 'Recipient Driver',
    'parc.alerts.noTelegram': 'No Telegram',
    'parc.alerts.messagePlaceholder': 'Enter your instructions here...',
    'parc.alerts.sendMessage': 'Send Message',

    // Clôture Agence & Planning
    'academy.scheduler.closed_chip': "Closed",
    'academy.scheduler.closed_warning': "This slot is in a closed period. Modification and cancellation are not allowed.",
    'academy.scheduler.error_closed_planning': "Cannot modify: planning is closed until {0}.",
    'academy.financial.error_closed_invoices': "Cannot modify: agency is closed until {0}.",
    'academy.financial.invoice_locked_tooltip': "Invoice locked (agency closed period)",

    // Student Card & QR Attendance
    'academy.students.print_card': "Student Badge",
    'academy.students.card_title': "STUDENT ID CARD",
    'academy.students.card_subtitle': "Student Badge",
    'academy.students.print_badge': "Print Badge",
    'academy.students.student_code': "Student Code",
    'academy.students.emergency_short': "Emergency",
    'academy.attendance.scan_qr': "Scan QR Attendance",
    'academy.attendance.scanner_title': "QR Attendance Scanner",
    'academy.attendance.scanner_desc': "Scan the student badge with your camera or USB barcode reader to automatically record attendance.",
    'academy.attendance.manual_code_placeholder': "Enter or scan student code (e.g. BLO-STD-...)...",
    'academy.attendance.mark_present_success': "{0} marked Present successfully!",
    'academy.attendance.already_present': "{0} is already marked Present.",
    'academy.attendance.student_not_enrolled': "Student {0} is not enrolled in this session.",
    'academy.attendance.mark_all_present': "Mark All Present",
    'academy.attendance.mark_all_present_confirm': "Do you want to mark all enrolled students in this session as Present?",
    'academy.attendance.stats_present': "Present",
    'academy.attendance.stats_late': "Late",
    'academy.attendance.stats_absent': "Absent",
    'academy.attendance.stats_excused': "Excused",
    'academy.attendance.stats_rate': "Attendance Rate",
    'academy.attendance.search_student': "Search student by name, code...",
    'academy.attendance.select_session_hint': "Please select a session to manage attendance.",
    'academy.attendance.camera_start_error': "Unable to access camera. Please check your browser permissions.",
    'academy.attendance.camera_switch': "Switch Camera",
    'academy.menu.email_templates': 'Email Templates',
    'academy.email_templates.title': 'Email Templates & Notifications',
    'academy.email_templates.new_title': 'New Email Template',
    'academy.email_templates.edit_title': 'Edit Template: {0}',
    'academy.email_templates.btn_new': 'New Template',
    'academy.email_templates.btn_refresh': 'Refresh',
    'academy.email_templates.btn_back': 'Back to list',
    'academy.email_templates.btn_reset': 'Reset',
    'academy.email_templates.btn_save': 'Save changes',
    'academy.email_templates.btn_create': 'Create template',
    'academy.email_templates.filter_type': 'Filter by email type',
    'academy.email_templates.filter_type_all': 'All email types',
    'academy.email_templates.filter_lang': 'Filter by language',
    'academy.email_templates.filter_lang_all': 'All languages',
    'academy.email_templates.filter_status': 'Status',
    'academy.email_templates.filter_status_all': 'All statuses',
    'academy.email_templates.filter_active': 'Active only',
    'academy.email_templates.filter_inactive': 'Disabled only',
    'academy.email_templates.results': '{0} template(s) found',
    'academy.email_templates.col_status': 'Status',
    'academy.email_templates.col_lang': 'Language',
    'academy.email_templates.col_type': 'Email type',
    'academy.email_templates.col_name': 'Template name',
    'academy.email_templates.col_subject': 'Subject',
    'academy.email_templates.col_updated': 'Last update',
    'academy.email_templates.status_active': 'Active',
    'academy.email_templates.status_inactive': 'Disabled',
    'academy.email_templates.card_code': 'Code: {0}',
    'academy.email_templates.card_subject': 'Subject:',
    'academy.email_templates.card_vars': 'Variables ({0}):',
    'academy.email_templates.card_updated': 'Updated: {0}',
    'academy.email_templates.card_edit': 'Edit template',
    'academy.email_templates.card_delete': 'Delete',
    'academy.email_templates.search_placeholder': 'Search by name, subject or code...',
    'academy.email_templates.section1': '1. Template Configuration',
    'academy.email_templates.field_name': 'Template name',
    'academy.email_templates.field_name_placeholder': 'e.g. Payment reminder - Outstanding balance (English)',
    'academy.email_templates.field_code': 'Unique Code',
    'academy.email_templates.field_code_helper': 'e.g. DEBT_REMINDER_ADULT_EN',
    'academy.email_templates.field_type': 'Email type (Trigger event)',
    'academy.email_templates.field_lang': 'Template language',
    'academy.email_templates.lang_fr': '🇫🇷 French (FR)',
    'academy.email_templates.lang_ar': '🇸🇦 Arabic (العربية - AR)',
    'academy.email_templates.lang_en': '🇬🇧 English (EN)',
    'academy.email_templates.active_label': 'Active Template (Sending allowed)',
    'academy.email_templates.inactive_label': 'Template Disabled',
    'academy.email_templates.active_hint': 'Emails of this type will be sent automatically.',
    'academy.email_templates.inactive_hint': '⚠️ When disabled, the system will not send any email for this type.',
    'academy.email_templates.field_desc': 'Description / Internal notes',
    'academy.email_templates.field_desc_placeholder': 'Internal usage or notes about this template...',
    'academy.email_templates.section2': '2. Subject & Message Header',
    'academy.email_templates.field_subject': 'Email subject (Subject)',
    'academy.email_templates.field_subject_placeholder': 'e.g. [{agencyName}] Payment reminder - Balance due: {totalBalanceDue} {currency}',
    'academy.email_templates.field_subject_helper': 'You can include variables such as {agencyName}, {student.fullName}, etc.',
    'academy.email_templates.section3': '3. HTML Body & Variables',
    'academy.email_templates.tags_hint': 'Click a variable to insert it in the body:',
    'academy.email_templates.field_body': 'HTML message body',
    'academy.email_templates.field_body_placeholder': '<!DOCTYPE html><html><body>...</body></html>',
    'academy.email_templates.preview_title': 'Live Preview',
    'academy.email_templates.preview_hint': 'Variables {\'..\'} are automatically replaced with sample data below.',
    'academy.email_templates.preview_subject': 'Subject preview:',
    'academy.email_templates.preview_empty': '(No subject entered)',
    'academy.email_templates.confirm_delete_title': 'Delete this template?',
    'academy.email_templates.confirm_delete_msg': "Are you sure you want to delete template '{0}'?",
    'academy.email_templates.msg_created': 'Template created successfully.',
    'academy.email_templates.msg_updated': 'Template updated successfully.',
    'academy.email_templates.msg_deleted': 'Template deleted successfully.',
    'academy.email_templates.err_name': 'Please enter the template name.',
    'academy.email_templates.err_type': 'Please select an email type.',
    'academy.email_templates.err_subject': 'Please enter the email subject.',
    'academy.email_templates.err_body': 'The HTML body is required.',
    'academy.email_templates.lang_ar_flag': '🇸🇦 AR',
    'academy.email_templates.lang_en_flag': '🇬🇧 EN',
    'academy.email_templates.lang_fr_flag': '🇫🇷 FR',
    'academy.email_templates.lang_ar_rtl': '🇸🇦 Arabic (RTL)',
    'academy.email_templates.lang_en_chip': '🇬🇧 English',
    'academy.email_templates.lang_fr_chip': '🇫🇷 French',
    'academy.email_templates.grid_view': 'Grid view',
    'academy.email_templates.table_view': 'Table view',

    // Academy Subscription & SATIM
    'academy.menu.subscription': 'Subscription & Billing',
    'subscription.pageTitle': 'Subscription & Billing Management',
    'subscription.tabUsage': 'Usage & Quotas',
    'subscription.tabPlans': 'Plans & Pricing',
    'subscription.loading': 'Loading subscription details...',
    'subscription.successTitle': 'Subscription Activated!',
    'subscription.successDesc': 'Your SATIM payment was successfully approved (Ref: {0}). Your quotas have been updated.',
    'subscription.changePlan': 'Change or Upgrade Plan',
    'subscription.securedBy': 'Secured payments via',
    'subscription.plan': 'Selected Plan',
    'subscription.yearly': 'Yearly Billing',
    'subscription.monthly': 'Monthly Billing',
    'subscription.totalDue': 'Total amount to charge',
    'subscription.currentPlan': 'Current Plan',
    'subscription.chooseFree': 'Choose Free',
    'subscription.payWithSatim': 'Pay with SATIM',
    'subscription.upgradeNow': 'Upgrade My Subscription',
    'subscription.usage': 'Quota usage',
    'subscription.status.trial': 'Free Trial ({0} days left)',
    'subscription.status.active': 'Active Subscription',
    'subscription.status.expired': 'Subscription Expired',
    'subscription.quotasTitle': 'Live Quotas & Resource Usage',
    'subscription.quota.students': 'Students',
    'subscription.quota.studentsSub': 'Enrolled in academy',
    'subscription.quota.teachers': 'Teachers',
    'subscription.quota.teachersSub': 'Active instructors',
    'subscription.quota.users': 'Staff & Users',
    'subscription.quota.usersSub': 'Access accounts',
    'subscription.quota.agencies': 'Agencies & Branches',
    'subscription.quota.agenciesSub': 'Training centers',
    'subscription.quota.storage': 'Cloud Storage',
    'subscription.quota.storageSub': 'Photos, PDFs & Backups',
    'subscription.quota.sms': 'SMS Credits',
    'subscription.quota.smsSub': 'Notifications & Reminders',
    'subscription.invoicesTitle': 'Payment History & Invoices',
    'subscription.pricingTitle': 'Choose the right plan for your academy',
    'subscription.pricingSubtitle': 'Direct and secure payment via CIB or Edahabia card (SATIM). Instant activation.',
    'subscription.satim.testSandbox': 'Sandbox Test Mode (Quick Fill):',
    'subscription.satim.cardNumber': 'Card number (16 digits)',
    'subscription.satim.cardExpiry': 'Expiration date (MM/YY)',
    'subscription.satim.cardCvv': 'CVV2 (3 digits)',
    'subscription.satim.cardHolder': 'Cardholder full name',
    'subscription.satim.enterOtp': 'Verification code (OTP)',
    'subscription.satim.processing': 'Connecting to SATIM...',
    'subscription.satim.payNow': 'Proceed to Payment',
    'subscription.satim.validating': 'Verifying...',
    'subscription.satim.confirmPayment': 'Confirm & Charge',
    'subscription.satim.continueToPortal': 'Access My Academy',
    'subscription.satim.invalidCardNumber': 'Invalid card number (16 digits required).',
    'subscription.satim.invalidExpiry': 'Invalid expiration date (MM/YY).',
    'subscription.satim.invalidCvv': 'Invalid CVV2 code.',
    'subscription.satim.invalidOtp': 'Please enter the SMS code received.',
    'subscription.limit.studentsTitle': 'Student Limit Reached',
    'subscription.limit.teachersTitle': 'Teacher Limit Reached',
    'subscription.limit.storageTitle': 'Storage Full',
    'subscription.limit.featureTitle': 'Feature Locked',
    'subscription.limit.studentsDesc': 'Your current plan ({0}) is limited to {1} students. Please upgrade your plan to enroll more students.',
    'subscription.limit.teachersDesc': 'Your current plan ({0}) is limited to {1} teachers. Please upgrade to add more instructors.',
    'subscription.limit.featureDesc': 'Feature "{0}" is not available on the {1} plan. Unlock it by upgrading your subscription.',
    'subscription.limit.instantActivation': 'Instant activation via SATIM payment (CIB / Edahabia).',

    // CRM
    'crm.menu.dashboard': 'CRM Dashboard',
    'crm.menu.prescription': 'Prescription',
    'crm.menu.visits': 'Field Visits',
    'crm.menu.cdc': 'Specifications (CDC)',
    'crm.menu.pipeline': 'Projects & Pipeline',
    'crm.menu.opportunities': 'Projects & Opportunities',
    'crm.menu.leads': 'Prospects & Leads',
    'crm.menu.chantiers_sav': 'Sites & After-Sales',
    'crm.menu.chantiers': 'Active Sites (KAM)',
    'crm.menu.reclamations': 'Claims & After-Sales',
    'crm.menu.ecosystem': 'Prescription Ecosystem',
    'crm.menu.organisations': 'Organizations (BET / Developers)',
    'crm.menu.contacts': 'Contacts & Decision Makers',
    'crm.menu.activities': 'Activities & Meetings',
    'crm.cdc.deleteSuccess': 'Specifications document deleted successfully.',
    'crm.cdc.secIdent': 'Document Identification',
    'crm.cdc.titleField': 'Title / Subject of Specifications',
    'crm.cdc.unitsCount': 'Number of Units / Openings',
    'crm.cdc.estimatedBudget': 'Estimated Budget (DZD)',
    'crm.cdc.technicalConstraints': 'Technical Requirements (AEV, Acoustic, Thermal, Glazing)',
    'crm.chantiers.deleteSuccess': 'Site project deleted successfully.',
    'crm.chantiers.clientRequired': 'Client / Project owner name is required.',
    'crm.leads.deleteError': 'Error while deleting.',
    'crm.leads.status.new': 'New',
    'crm.leads.status.contacted': 'Contacted',
    'crm.leads.status.qualified': 'Qualified',
    'crm.leads.status.proposal': 'Proposal',
    'crm.leads.status.won': 'Won',
    'crm.leads.status.lost': 'Lost',
    'crm.opportunities.stageOnHold': 'On Hold',
    'crm.opportunities.transferBtn': 'Transfer to KAM',
    'crm.opportunities.refInterne': 'Internal File Reference',
    'crm.opportunities.organisation': 'Organization (BET / Developer)',
    'crm.opportunities.wilaya': 'Province / Region',
    'crm.opportunities.chantierAddress': 'Precise Site Address',
    'crm.opportunities.secSpecs': 'Technical Details & Cost Estimation',
    'crm.opportunities.gamme': 'Product Line',
    'crm.opportunities.units': 'Joinery Units',
    'crm.opportunities.unitPrice': 'Unit Price Excl. Tax (DZD)',
    'crm.opportunities.discount': 'Granted Discount (%)',
    'crm.opportunities.extraDiscount': 'Additional Discount (%)',
    'crm.opportunities.secTransfer': 'Commercial Follow-up & KAM Handover',
    'crm.opportunities.actionRequired': 'Next Required Action',
    'crm.opportunities.progress': 'Progress',
    'crm.opportunities.search': 'Search project, product line, prescriber...',
    'crm.organisations.deleteSuccess': 'Organization deleted successfully.',
    'crm.organisations.website': 'Website / Portfolio',
    'crm.organisations.search': 'Search an organization (Design office, Architect, Developer)...',
    'crm.reclamations.deleteSuccess': 'Claim deleted successfully.',
    'crm.visits.deleteSuccess': 'Visit deleted successfully.',
    'crm.visits.objectiveRequired': 'Visit objective is required.',
    'crm.visits.secPlanning': 'Visit Framing & Planning',
    'crm.visits.datetime': 'Planned Date & Time',
    'crm.visits.contact': 'Key Contact',
    'crm.visits.secTerrain': 'Field Execution & Geolocation Check-in',
    'crm.visits.checkinBtn': 'On-site Check-in (GPS)',
    'crm.visits.checkoutBtn': 'Close Visit & Calculate Duration',
    'crm.visits.checkinTime': 'Arrival Time (Check-in)',
    'crm.visits.checkoutTime': 'Departure Time (Check-out)',
    'crm.visits.duration': 'Meeting Duration (Minutes)',
    'crm.visits.checkinLat': 'GPS Latitude',
    'crm.visits.checkinLon': 'GPS Longitude',
    'crm.visits.topics': 'Topics Discussed',
    'crm.visits.commitments': 'Commitments Agreed (Both Sides)',
    'crm.visits.nextSteps': 'Next Steps / Follow-up',
    'crm.dashboard.prescripteurSubtitle': 'Upstream Prescription Management, BET Detection & CDC Tracking',
    'crm.dashboard.kamSubtitle': 'Operational Site Management, Orders & After-Sales Service',
    'crm.dashboard.globalSubtitle': 'Consolidated View of Commercial Performance',
    'crm.dashboard.viewPrescripteur': 'Prescriber View',
    'crm.dashboard.viewKam': 'KAM View (Sites)',
    'crm.dashboard.viewGlobal': 'Global View',
    'crm.dashboard.visitesTerrain': 'FIELD VISITS',
    'crm.dashboard.visitesSub': 'Completed vs Planned this month',
    'crm.dashboard.tauxVisiteCdc': 'Conversion Rate Visit → CDC',
    'crm.dashboard.cahiersCharges': 'SPECIFICATIONS (CDC)',
    'crm.dashboard.cdcSub': 'CDC signed / validated',
    'crm.dashboard.enCours': 'in progress',
    'crm.dashboard.tauxPreco': 'Recommendation integration rate',
    'crm.dashboard.projetsGagnes': 'WON PROJECTS',
    'crm.dashboard.cumulAnnee': 'Year to date',
    'crm.dashboard.ceMois': 'this month',
    'crm.dashboard.tauxCdcGagne': 'CDC Success Rate → Won',
    'crm.dashboard.ecosystemePrescription': 'PRESCRIPTION ECOSYSTEM',
    'crm.dashboard.ecosystemeSub': 'Active organizations (BET, Architects, Promoters)',
    'crm.dashboard.satisfactionPartenaires': 'Partner satisfaction',
    'crm.dashboard.alertesPrioritaires': 'Prescriber Alerts & Priority Actions',
    'crm.dashboard.visitesRetard': 'Overdue field visits',
    'crm.dashboard.enRetard': 'overdue',
    'crm.dashboard.contactsRelancer': 'Key BET contacts to follow up',
    'crm.dashboard.aRelancer': 'to follow up',
    'crm.dashboard.transfertsAttente': 'Won projects ready for transfer to KAM',
    'crm.dashboard.enAttente': 'pending',
    'crm.dashboard.raccourcis': 'Shortcuts & Quick Actions',
    'crm.dashboard.planifierVisite': 'Schedule a Visit',
    'crm.dashboard.nouveauCdc': 'New CDC',
    'crm.dashboard.projetsPipeline': 'Pipeline Projects',
    'crm.dashboard.chantiersActifs': 'ACTIVE SITES',
    'crm.dashboard.enDemarrage': 'starting up',
    'crm.dashboard.termines': 'completed',
    'crm.dashboard.projetsTransferes': 'project(s) transferred from Prescription',
    'crm.dashboard.commandesTraitees': 'PROCESSED ORDERS',
    'crm.dashboard.commandesRetardCount': 'overdue order(s)',
    'crm.dashboard.livraisonsConformes': 'Deliveries on schedule',
    'crm.dashboard.chiffreAffaires': 'TURNOVER / REVENUE',
    'crm.dashboard.margeMoy': 'Avg margin',
    'crm.dashboard.encaisse': 'Collected',
    'crm.dashboard.satisfactionClient': 'CUSTOMER SATISFACTION',
    'crm.dashboard.satisfactionChantiersSub': 'Overall satisfaction on delivered sites',
    'crm.dashboard.resolues': 'resolved',
    'crm.dashboard.activiteTerrain': 'Operational Field Activity',
    'crm.dashboard.visitesChantier': 'Site Visits',
    'crm.dashboard.heuresTerrain': 'Field Hours',
    'crm.dashboard.problemesResolus': 'Resolved Issues',
    'crm.dashboard.actionsKam': 'KAM Quick Actions',
    'crm.dashboard.gererChantiers': 'Manage Sites',
    'crm.dashboard.reclamationsSav': 'Complaints & After-Sales',
    'crm.dashboard.totalProspects': 'Total Leads',
    'crm.dashboard.contactsClients': 'Customer Contacts',
    'crm.dashboard.affairesGagnees': 'Won Deals',
    'crm.dashboard.projetsRecents': 'Recent Projects & Opportunities',
    'crm.dashboard.pistesRecentes': 'Recent Leads & Detections',
    'crm.organisations.title': 'Prescription Ecosystem & Organizations',
    'crm.organisations.newTitle': 'New Organization',
    'crm.organisations.editTitle': 'Edit Organization',
    'crm.organisations.secIdent': 'Identification & Typology',
    'crm.organisations.secCoords': 'Contact Details & Headquarters',
    'crm.organisations.secHistory': 'History & Partnership',
    'crm.organisations.nameRequired': 'Organization name is required.',
    'crm.organisations.confirmDelete': 'Are you sure you want to delete this organization?',
    'crm.organisations.projects': 'project(s)',
    'crm.organisations.contacts': 'contact(s)',
    'crm.organisations.searchPlaceholder': 'Search organization (BET, Architect, Developer...)...',
    'crm.organisations.historyNotes': 'Partnership notes, previously prescribed projects, recommendations',
    'crm.organisations.historyPlaceholder': 'Detail relationship history, loyalty, architectural preferences or preferred ranges here...',
    'crm.organisations.zoneGeoPlaceholder': 'e.g. Algiers Center, Oran, Constantine, National',
    'crm.organisations.typeBet': 'Technical Design Office (BET)',
    'crm.organisations.typeArchitecte': 'Architectural Firm',
    'crm.organisations.typePromoteur': 'Real Estate Developer',
    'crm.organisations.typeMo': 'Project Owner / Contracting Authority',
    'crm.organisations.typeEnt': 'General Construction Company',
    'crm.organisations.typeInstallateur': 'Installer / Partner Carpenter',
    'crm.organisations.typeAutre': 'Other Partner',
    'crm.organisations.specResidentiel': 'Residential (Housing, Towers)',
    'crm.organisations.specTertiaire': 'Commercial & Offices',
    'crm.organisations.specIndustriel': 'Industrial & Logistics',
    'crm.organisations.specMixte': 'Mixed-Use / Multipurpose',
    'crm.organisations.sizePetit': 'Small Structure (< 10 employees)',
    'crm.organisations.sizeMoyen': 'Medium Structure (10 - 50 employees)',
    'crm.organisations.sizeGrand': 'Large Structure (> 50 employees)',
    'crm.organisations.name': 'Company Name',
    'crm.organisations.type': 'Organization Typology',
    'crm.organisations.specialty': 'Main Specialty',
    'crm.organisations.size': 'Scale / Size',
    'crm.organisations.zoneGeo': 'Geographic Intervention Area',
    'crm.visits.title': 'Field & Prescription Visits',
    'crm.visits.newTitle': 'Schedule a Visit',
    'crm.visits.editTitle': 'Edit Visit',
    'crm.visits.secIdent': 'Visit Framework',
    'crm.visits.secReport': 'Debrief & Action Tracking',
    'crm.visits.subjectRequired': 'Visit subject is required.',
    'crm.visits.confirmDelete': 'Are you sure you want to delete this visit?',
    'crm.visits.search': 'Search visit, BET, project, contact...',
    'crm.visits.rolePrescripteur': 'Prescriber (Upstream / BET / Architect)',
    'crm.visits.roleKam': 'KAM (Site / Developer / Acceptance)',
    'crm.visits.typeProspection': 'Prospecting & Initial Detection',
    'crm.visits.typePreconisation': 'Product Presentation & Technical Recommendation',
    'crm.visits.typePrescription': 'Drafting / Validation of Prescription CDC',
    'crm.visits.typeSuiviChantier': 'Site Supervision & Installation Kick-off',
    'crm.visits.typeReunionTechnique': 'Technical Resolution Meeting',
    'crm.visits.typeReception': 'Post-Installation Inspection / Acceptance',
    'crm.visits.statusPlanned': 'Planned',
    'crm.visits.statusInProgress': 'In Progress (On-site)',
    'crm.visits.statusCompleted': 'Completed with Report',
    'crm.visits.statusRescheduled': 'Rescheduled',
    'crm.visits.statusCancelled': 'Cancelled',
    'crm.visits.noOrg': 'Free visit (No linked organization)',
    'crm.visits.noProject': 'Upstream prospecting (No identified project)',
    'crm.visits.objectivePlaceholder': 'Specify the specific objective (e.g. present Futura range, check layout...)',
    'crm.visits.checklistPlaceholder': 'Items verified (drawings submitted, samples shown, data sheets provided...)',
    'crm.visits.personsMetPlaceholder': 'e.g. Mr. Benali (Project Manager), Mrs. Mansouri (Interior Architect)...',
    'crm.visits.evalScore': 'Interest / Receptivity Rating:',
    'crm.visits.noGps': 'Not geolocated',
    'crm.visits.role': 'Visitor Role / Profile',
    'crm.visits.type': 'Visit Typology',
    'crm.visits.status': 'Visit Status',
    'crm.visits.subject': 'Visit Subject',
    'crm.visits.organisation': 'Organization / BET / Developer Visited',
    'crm.visits.project': 'Associated Project',
    'crm.visits.interlocuteur': 'Contact Person Met',
    'crm.visits.date': 'Visit Date & Time',
    'crm.visits.objective': 'Meeting Objective',
    'crm.visits.gps': 'GPS Coordinates / Field Check-in',
    'crm.visits.score': 'Receptivity Score (1-5 ⭐)',
    'crm.visits.personsMet': 'Persons Met & Roles',
    'crm.visits.checklist': 'Field Checklist / Validated Recommendations',
    'crm.visits.report': 'Detailed Visit Debrief',
    'crm.visits.nextAction': 'Next Action / Decided Follow-up',
    'crm.cdc.title': 'Prescription Specifications (CDC)',
    'crm.cdc.newTitle': 'New Specification (CDC)',
    'crm.cdc.editTitle': 'Edit Specification (CDC)',
    'crm.cdc.secProject': 'Project Attachment & Stakeholders',
    'crm.cdc.secSpecs': 'Technical Specifications & Recommended Ranges',
    'crm.cdc.titleRequired': 'CDC title is required.',
    'crm.cdc.confirmDelete': 'Are you sure you want to delete this specification?',
    'crm.cdc.search': 'Search CDC, project, BET, range...',
    'crm.cdc.selectProject': 'Select a project',
    'crm.cdc.selectOrg': 'Select an organization',
    'crm.cdc.selectContact': 'Select a contact',
    'crm.cdc.statusDraft': 'Draft (In preparation)',
    'crm.cdc.statusSubmitted': 'Submitted to BET / Architect',
    'crm.cdc.statusValidated': 'Approved by Project Supervision',
    'crm.cdc.statusSigned': 'Signed / Integrated into Contract',
    'crm.cdc.statusRejected': 'Rejected / Variant declined',
    'crm.cdc.constraintsPlaceholder': 'e.g. Reinforced acoustic insulation 38dB, 4S solar control glazing, textured shades, ceiling height...',
    'crm.cdc.prescripteur': 'Prescriber',
    'crm.cdc.signedOn': 'Signed on',
    'crm.cdc.notSigned': 'Not signed',
    'crm.cdc.units': 'units',
    'crm.cdc.cdcTitle': 'Specification Title',
    'crm.cdc.project': 'Associated Project',
    'crm.cdc.organisation': 'Prescriber / Drafting BET',
    'crm.cdc.contact': 'BET Contact Person',
    'crm.cdc.gamme': 'Recommended Product Range',
    'crm.cdc.volume': 'Estimated Volume (Joinery units / areas)',
    'crm.cdc.status': 'Specification Status',
    'crm.cdc.constraints': 'Technical Constraints & Required Specifications',
    'crm.cdc.dateCreation': 'Creation Date',
    'crm.cdc.dateSignature': 'Validation / Signature Date',
    'crm.gamme.productGamme': 'Product Range',
    'crm.gamme.urbania': 'URBANIA (High Performance Residential)',
    'crm.gamme.futura': 'FUTURA (Commercial & Sliding Openings)',
    'crm.gamme.dooria': 'DOORIA (Prestige Doors & Entrances)',
    'crm.gamme.mixte': 'MIXTE (Custom Hybrid Solutions)',
    'crm.gamme.urbaniaShort': 'URBANIA',
    'crm.gamme.futuraShort': 'FUTURA',
    'crm.gamme.dooriaShort': 'DOORIA',
    'crm.gamme.mixteShort': 'MIXTE',
    'crm.opportunities.secIdent': 'Deal Identification',
    'crm.opportunities.secGamme': 'Products, Ranges & Costing',
    'crm.opportunities.secFollowup': 'Stakeholders, Handover & Sales Follow-up',
    'crm.opportunities.titleField': 'Deal / Project Title',
    'crm.opportunities.typeProjet': 'Project Type',
    'crm.opportunities.segment': 'Market Segment',
    'crm.opportunities.priority': 'Sales Priority',
    'crm.opportunities.risk': 'Risk Level',
    'crm.opportunities.transferStatus': 'Handover Status (Prescription → KAM)',
    'crm.opportunities.commercialProgress': 'Sales Progress:',
    'crm.opportunities.actionPlaceholder': 'e.g. Send commercial proposal, technical window alignment with BET...',
    'crm.opportunities.notesPlaceholder': 'Exchange history, specific developer requirements, procurement schedule...',
    'crm.opportunities.typePromotion': 'Private Real Estate Development',
    'crm.opportunities.typeAadl': 'AADL Housing',
    'crm.opportunities.typeLpp': 'LPP Housing',
    'crm.opportunities.typePublic': 'Public Facility / Education / Healthcare',
    'crm.opportunities.typePrive': 'Commercial / Offices / Private',
    'crm.opportunities.typeIndustriel': 'Industrial & Hangars',
    'crm.opportunities.segmentPrive': 'Private Sector',
    'crm.opportunities.segmentPublic': 'Public Sector / State Contracts',
    'crm.opportunities.transferNone': 'In Prescription (Not transferred)',
    'crm.opportunities.transferPending': 'Ready for Transfer to KAM',
    'crm.opportunities.transferTransferred': 'Transferred & Handled by KAM',
    'crm.opportunities.transferDirectKam': 'Direct KAM Prospecting (No prescription)',
    'crm.opportunities.volumeEstime': 'Estimated Volume (Units)',
    'crm.opportunities.valeurEstimee': 'Estimated Deal Value (DZD)',
    'crm.opportunities.prescripteur': 'Lead Prescriber',
    'crm.opportunities.kamAssigne': 'Assigned KAM (Site Follow-up)',
    'crm.opportunities.dateCloture': 'Expected Closing Date',
    'crm.opportunities.prochaineAction': 'Next Commercial Action',
    'crm.stage.prospecting': 'Prospecting / Detection',
    'crm.stage.qualification': 'Technical Qualification BET',
    'crm.stage.proposal': 'Proposal / CDC Drafted',
    'crm.stage.negotiation': 'Negotiation & Alignment',
    'crm.stage.won': 'Deal Won (Signed)',
    'crm.stage.lost': 'Lost / Competition',
    'crm.stage.onHold': 'On Hold / Frozen Project',
    'crm.priority.low': 'Low',
    'crm.priority.medium': 'Medium',
    'crm.priority.high': 'High / Strategic',
    'crm.risk.low': 'Low Risk',
    'crm.risk.medium': 'Moderate Risk',
    'crm.risk.high': 'High Risk',
    'crm.chantiers.title': 'Operational Tracking of KAM Sites',
    'crm.chantiers.newTitle': 'New KAM Site',
    'crm.chantiers.editTitle': 'Active Site Tracking',
    'crm.chantiers.secIdent': 'Site & Deal Identification',
    'crm.chantiers.secPlanning': 'Schedule & Progress',
    'crm.chantiers.secOrders': 'Orders & Deliveries',
    'crm.chantiers.secFinancial': 'Financial Indicators & Collections',
    'crm.chantiers.titleRequired': 'Site title is required.',
    'crm.chantiers.confirmDelete': 'Are you sure you want to delete this site?',
    'crm.chantiers.search': 'Search active site, client, KAM...',
    'crm.chantiers.titleField': 'Site Name',
    'crm.chantiers.clientName': 'Client / Developer',
    'crm.chantiers.project': 'Initial Project (Handover)',
    'crm.chantiers.directKamProject': 'Direct KAM prospecting (No prescription)',
    'crm.chantiers.kamName': 'Responsible KAM',
    'crm.chantiers.wilaya': 'Province / Wilaya',
    'crm.chantiers.startDate': 'Start Date',
    'crm.chantiers.estimatedEndDate': 'Estimated Due Date',
    'crm.chantiers.operationalProgress': 'Operational Progress',
    'crm.chantiers.progressTitle': 'Site Progress',
    'crm.chantiers.satisfaction': 'Customer Satisfaction Score (0-100)',
    'crm.chantiers.satisfactionLabel': 'Satisfaction',
    'crm.chantiers.ordersCount': 'Processed Orders',
    'crm.chantiers.ordersPending': 'Pending Orders',
    'crm.chantiers.ordersLate': 'Late Orders',
    'crm.chantiers.deliveryStatus': 'Delivery Status',
    'crm.chantiers.totalCa': 'Total Turnover (DZD)',
    'crm.chantiers.marginPct': 'Realized Margin Rate (%)',
    'crm.chantiers.collectedPct': 'Collection / Payment Rate (%)',
    'crm.chantiers.client': 'Client',
    'crm.chantiers.assigned': 'Assigned',
    'crm.chantiers.cmdCount': 'order(s)',
    'crm.chantiers.enRetard': 'late',
    'crm.chantiers.margin': 'Margin',
    'crm.chantiers.collected': 'Collected',
    'crm.chantiers.reclamationCount': 'complaint(s)',
    'crm.chantiers.zeroReclamation': '0 complaints',
    'crm.chantiers.progress': 'Progress',
    'crm.chantiers.statusDemarrage': 'Starting Up (Preparation)',
    'crm.chantiers.statusEnCours': 'In Progress',
    'crm.chantiers.statusPose': 'Window/Door Installation in Progress',
    'crm.chantiers.statusReception': 'Acceptance Phase / Snagging',
    'crm.chantiers.statusCloture': 'Successfully Closed',
    'crm.chantiers.statusArrete': 'Halted / On Hold',
    'crm.chantiers.delivOnSchedule': 'On Schedule',
    'crm.chantiers.delivPartially': 'Partially Delivered',
    'crm.chantiers.delivDelivered': 'Fully Delivered on Site',
    'crm.chantiers.delivDelayed': 'Delivery Delayed (Factory/Transport)',
    'crm.reclamations.title': 'After-Sales & Site Customer Complaints',
    'crm.reclamations.newTitle': 'Report an Issue / Complaint',
    'crm.reclamations.editTitle': 'After-Sales Complaint Handling',
    'crm.reclamations.secIdent': 'Site Issue Details',
    'crm.reclamations.secResolution': 'Technical Resolution & Treatment',
    'crm.reclamations.subjectRequired': 'Complaint subject is required.',
    'crm.reclamations.confirmDelete': 'Are you sure you want to delete this complaint?',
    'crm.reclamations.search': 'Search complaint, site...',
    'crm.reclamations.subject': 'Subject / Summary of the dispute',
    'crm.reclamations.chantier': 'Concerned Site',
    'crm.reclamations.contact': 'Declaring Contact',
    'crm.reclamations.type': 'Nature of Issue',
    'crm.reclamations.severity': 'Severity Level',
    'crm.reclamations.dateReported': 'Report Date',
    'crm.reclamations.dateResolved': 'Resolution Date',
    'crm.reclamations.resolutionNotes': 'Resolution Notes & Corrective Actions',
    'crm.reclamations.resolutionPlaceholder': 'Detail replaced parts, technician intervention, snagging clearance...',
    'crm.reclamations.selectChantier': 'Select a site',
    'crm.reclamations.selectContact': 'Select a contact',
    'crm.reclamations.typeDefautPose': 'Installation / Fastening / Shimming Defect',
    'crm.reclamations.typeDefautProduit': 'Product Defect / Scratch / Glazing',
    'crm.reclamations.typeRetardLivraison': 'Site Delivery Delay',
    'crm.reclamations.typeAccessoiresManquants': 'Missing Accessories / Hardware',
    'crm.reclamations.typeAutre': 'Other Complaint',
    'crm.reclamations.sevLow': 'Low (Minor impact)',
    'crm.reclamations.sevMedium': 'Medium (Resolve within 48h)',
    'crm.reclamations.sevHigh': 'High (Blocks acceptance)',
    'crm.reclamations.sevCritical': 'Critical (Immediate halt of installation)',
    'crm.reclamations.statusOpen': 'Open (Pending)',
    'crm.reclamations.statusInvestigating': 'Under Factory / SAV Analysis',
    'crm.reclamations.statusInProgress': 'Technical Intervention Scheduled',
    'crm.reclamations.statusResolved': 'Resolved & Accepted',
    'crm.reclamations.statusClosed': 'File Closed',
    'crm.reclamations.client': 'Client',
    'crm.reclamations.reportedOn': 'Reported on',
    'crm.reclamations.resolvedOn': 'Resolved on',
    'crm.reclamations.notResolved': 'Unresolved',
    'crm.contacts.title': 'Contacts & Key Decision Makers',
    'crm.contacts.detailsSection': 'Personal & Professional Information',
    'crm.contacts.field.firstName': 'First Name',
    'crm.contacts.field.lastName': 'Last Name',
    'crm.contacts.field.fullName': 'Full Name',
    'crm.contacts.field.organisation': 'Attached Organization (BET / Developer / Owner)',
    'crm.contacts.field.company': 'Company / Firm',
    'crm.contacts.field.role': 'Role / Position',
    'crm.contacts.field.email': 'Email',
    'crm.contacts.field.phone': 'Phone',
    'crm.contacts.field.mobile': 'Mobile',
    'crm.contacts.field.prefComm': 'Preferred Communication Channel',
    'crm.contacts.field.prefHours': 'Preferred Hours',
    'crm.contacts.field.prefLang': 'Preferred Language',
    'crm.contacts.field.type': 'Contact Type',
    'crm.contacts.field.city': 'City',
    'crm.contacts.field.address': 'Address',
    'crm.contacts.noOrgDirect': 'None / Direct Company',
    'crm.contacts.prefEmail': 'Email',
    'crm.contacts.prefPhone': 'Phone',
    'crm.contacts.prefMeeting': 'In-person Meeting',
    'crm.contacts.prefWhatsapp': 'WhatsApp / SMS',
    'crm.contacts.hoursPlaceholder': 'e.g. Mornings 9am-12pm',
    'crm.contacts.influence': 'Decision-Making Influence Level:',
    'crm.contacts.noRole': 'Role not specified',
    'crm.contacts.dispo': 'Available',
    'crm.leads.newLeadBtn': 'New Lead',
    'crm.leads.informationSection': 'Lead Information',
    'crm.leads.field.name': 'Full Name',
    'crm.leads.field.company': 'Company',
    'crm.leads.field.email': 'Email',
    'crm.leads.field.phone': 'Phone',
    'crm.leads.field.status': 'Status',
    'crm.leads.field.source': 'Source',
    'crm.leads.field.value': 'Estimated Value (DZD)',
    'crm.leads.field.notes': 'Notes & Remarks',
    'crm.leads.sourceLabel': 'Source',
    'crm.leads.sourceDirect': 'Direct',
    'crm.leads.noData': 'No leads found. Click "New Lead" to get started.',
    'crm.leads.search': 'Search lead...',
    'crm.leads.col.code': 'Code',
    'crm.leads.col.name': 'Name',
    'crm.leads.col.company': 'Company',
    'crm.leads.col.email': 'Email',
    'crm.leads.col.phone': 'Phone',
    'crm.leads.col.value': 'Value',
    'crm.leads.col.status': 'Status',
    'crm.status.new': 'New',
    'crm.status.contacted': 'Contacted',
    'crm.status.qualified': 'Qualified',
    'crm.status.proposal': 'Proposal',
    'crm.status.won': 'Won',
    'crm.status.lost': 'Lost',
    'crm.source.website': 'Website',
    'crm.source.referral': 'Referral',
    'crm.source.social': 'Social Networks',
    'crm.source.event': 'Event / Exhibition',
    'crm.source.other': 'Other',
    'crm.type.client': 'Client',
    'crm.type.prospect': 'Prospect',
    'crm.type.partner': 'Partner',
    'crm.type.vendor': 'Vendor',
    'crm.dashboard.title': 'Commercial Dashboard & CRM',
    'crm.dashboard.subtitle': 'Commercial performance tracking, leads and opportunities pipeline',
    'crm.dashboard.customizeKpi': 'Configure KPIs',
    'crm.dashboard.newLead': 'New Lead',
    'crm.dashboard.newOpp': 'New Opportunity',
    'crm.dashboard.activeOpportunities': 'Deals in Negotiation',
    'crm.dashboard.noContact': 'No contact',
    'crm.dashboard.totalLeads': 'Leads',
    'crm.dashboard.newLeads': 'new',
    'crm.dashboard.totalContacts': 'Contacts & Customers',
    'crm.dashboard.totalOpportunities': 'Opportunities',
    'crm.dashboard.pipelineAmount': 'Sales Pipeline',
    'crm.dashboard.wonAmount': 'Revenue Won',
    'crm.dashboard.pendingActivities': 'Upcoming Activities',
    'crm.dashboard.recentLeads': 'Recent Leads',
    'crm.dashboard.recentOpportunities': 'Recent Opportunities',
    'crm.dashboard.noLeads': 'No leads recorded.',
    'crm.dashboard.noOpportunities': 'No opportunities recorded.',

    // CRM KPIs & Widgets (Parameterized)
    'crm.dashboard.kpi.total_leads': 'Total Leads',
    'crm.dashboard.kpi.desc.total_leads': 'All recorded sales leads',
    'crm.dashboard.kpi.new_leads': 'New Leads',
    'crm.dashboard.kpi.desc.new_leads': 'Recent leads to contact',
    'crm.dashboard.kpi.total_contacts': 'Customer Contacts',
    'crm.dashboard.kpi.desc.total_contacts': 'Active contacts directory',
    'crm.dashboard.kpi.total_opportunities': 'Active Opportunities',
    'crm.dashboard.kpi.desc.total_opportunities': 'Open deal pipelines',
    'crm.dashboard.kpi.pipeline_amount': 'Pipeline Value',
    'crm.dashboard.kpi.desc.pipeline_amount': 'Total weighted pipeline volume',
    'crm.dashboard.kpi.won_amount': 'Won Revenue',
    'crm.dashboard.kpi.desc.won_amount': 'Total closed-won deal revenue',
    'crm.dashboard.kpi.pending_activities': 'Pending Activities',
    'crm.dashboard.kpi.desc.pending_activities': 'Calls, follow-ups and meetings to handle',
    'crm.dashboard.widget.recent_leads': 'Recent Leads',
    'crm.dashboard.widget.desc.recent_leads': 'List and follow-up of recent leads',
    'crm.dashboard.widget.pipeline_stages': 'Sales Pipeline',
    'crm.dashboard.widget.desc.pipeline_stages': 'Breakdown of deal values by stage',
    'crm.dashboard.widget.recent_activities': 'Recent Activities',
    'crm.dashboard.widget.desc.recent_activities': 'History and upcoming actions',

    // CRM Stats Badges
    'crm.stats.newThisMonth': 'new',
    'crm.stats.toContact': 'To contact',
    'crm.stats.activeAccounts': 'Active accounts',
    'crm.stats.activeDeals': 'active deals',
    'crm.stats.weightedPipeline': 'Active pipeline',
    'crm.stats.closedWon': 'Secured revenue',
    'crm.stats.actionRequired': 'Follow-ups required',
    'crm.leads.pageTitle': 'Lead Management',
    'crm.leads.new': 'New Lead',
    'crm.leads.editTitle': 'Edit Lead',
    'crm.leads.newTitle': 'New Lead',
    'crm.leads.details': 'Lead Information',
    'crm.leads.name': 'Full Name',
    'crm.leads.company': 'Company',
    'crm.leads.email': 'Email',
    'crm.leads.phone': 'Phone',
    'crm.leads.status': 'Status',
    'crm.leads.source': 'Lead Source',
    'crm.leads.value': 'Estimated Value',
    'crm.leads.notes': 'Notes / Requirements',
    'crm.leads.confirmDelete': 'Are you sure you want to delete this lead?',
    'crm.leads.deleteSuccess': 'Lead deleted successfully.',
    'crm.leads.updateSuccess': 'Lead updated successfully.',
    'crm.leads.createSuccess': 'Lead created successfully.',
    'crm.leads.nameRequired': 'Lead name is required.',
    'crm.leads.searchPlaceholder': 'Search leads by name, company, email...',
    'crm.leads.empty': 'No leads found. Click "New Lead" to get started.',
    'crm.contacts.pageTitle': 'Contacts & Customers Directory',
    'crm.contacts.new': 'New Contact',
    'crm.contacts.editTitle': 'Edit Contact / Decision Maker',
    'crm.contacts.newTitle': 'New Contact & Decision Maker',
    'crm.contacts.details': 'Contact Information',
    'crm.contacts.firstName': 'First Name',
    'crm.contacts.lastName': 'Last Name',
    'crm.contacts.company': 'Company',
    'crm.contacts.role': 'Job Title / Role',
    'crm.contacts.email': 'Email',
    'crm.contacts.phone': 'Phone',
    'crm.contacts.mobile': 'Mobile',
    'crm.contacts.address': 'Address',
    'crm.contacts.city': 'City',
    'crm.contacts.type': 'Relationship Type',
    'crm.contacts.confirmDelete': 'Are you sure you want to delete this contact?',
    'crm.contacts.deleteSuccess': 'Contact deleted successfully.',
    'crm.contacts.updateSuccess': 'Contact updated successfully.',
    'crm.contacts.createSuccess': 'Contact created successfully.',
    'crm.contacts.lastNameRequired': 'Last name is required.',
    'crm.contacts.searchPlaceholder': 'Search contact, organization, email...',
    'crm.contacts.empty': 'No contacts found. Click "New Contact" to get started.',
    'crm.opportunities.pageTitle': 'Sales Opportunities',
    'crm.opportunities.new': 'New Opportunity',
    'crm.opportunities.editTitle': 'Edit Opportunity',
    'crm.opportunities.newTitle': 'New Opportunity',
    'crm.opportunities.details': 'Opportunity Details',
    'crm.opportunities.title': 'Opportunities & Deals Pipeline',
    'crm.opportunities.contact': 'Associated Contact',
    'crm.opportunities.stage': 'Pipeline Stage',
    'crm.opportunities.amount': 'Amount (DZD)',
    'crm.opportunities.probability': 'Probability (%)',
    'crm.opportunities.closeDate': 'Target Close Date',
    'crm.opportunities.closeTarget': 'Target',
    'crm.opportunities.notes': 'Notes, History & Special Requirements',
    'crm.opportunities.stageProspecting': 'Prospecting',
    'crm.opportunities.stageQualification': 'Qualification',
    'crm.opportunities.stageProposal': 'Proposal',
    'crm.opportunities.stageNegotiation': 'Negotiation',
    'crm.opportunities.stageWon': 'Won',
    'crm.opportunities.stageLost': 'Lost',
    'crm.opportunities.confirmDelete': 'Are you sure you want to delete this opportunity?',
    'crm.opportunities.deleteSuccess': 'Opportunity deleted successfully.',
    'crm.opportunities.updateSuccess': 'Opportunity updated successfully.',
    'crm.opportunities.createSuccess': 'Opportunity created successfully.',
    'crm.opportunities.titleRequired': 'Opportunity title is required.',
    'crm.opportunities.searchPlaceholder': 'Search deal, developer, project, prescriber...',
    'crm.opportunities.empty': 'No opportunities found. Click "New Opportunity" to get started.',
    'crm.activities.pageTitle': 'Activities & Tracking',
    'crm.activities.new': 'New Activity',
    'crm.activities.editTitle': 'Edit Activity',
    'crm.activities.newTitle': 'New Activity',
    'crm.activities.details': 'Activity details',
    'crm.activities.type': 'Activity Type',
    'crm.activities.subject': 'Subject / Title',
    'crm.activities.date': 'Date & Time',
    'crm.activities.status': 'Status',
    'crm.activities.priority': 'Priority',
    'crm.activities.contact': 'Associated Contact',
    'crm.activities.opportunity': 'Associated Opportunity',
    'crm.activities.notes': 'Notes / Meeting Summary',
    'crm.activities.relatedTo': 'Related to',
    'crm.activities.typeCall': 'Phone Call',
    'crm.activities.typeMeeting': 'Meeting / Appointment',
    'crm.activities.typeEmail': 'Email',
    'crm.activities.typeTask': 'Task',
    'crm.activities.typeNote': 'Note',
    'crm.activities.statusPending': 'Pending',
    'crm.activities.statusCompleted': 'Completed',
    'crm.activities.statusCancelled': 'Cancelled',
    'crm.activities.priorityLow': 'Low',
    'crm.activities.priorityMedium': 'Medium',
    'crm.activities.priorityHigh': 'High',
    'crm.activities.markPending': 'Mark as pending',
    'crm.activities.markCompleted': 'Mark as completed',
    'crm.activities.confirmDelete': 'Are you sure you want to delete this activity?',
    'crm.activities.deleteSuccess': 'Activity deleted successfully.',
    'crm.activities.updateSuccess': 'Activity updated successfully.',
    'crm.activities.createSuccess': 'Activity created successfully.',
    'crm.activities.subjectRequired': 'Activity subject is required.',
    'crm.activities.searchPlaceholder': 'Search activity by subject, contact, opportunity...',
    'crm.activities.empty': 'No activities found. Click "New Activity" to schedule a call, meeting or task.',

    // ProjectManagement
    'project.menu.dashboard': 'Projects Dashboard',
    'project.menu.projects': 'Project Portfolio',
    'project.menu.tasks': 'Tasks & Activities',
    'project.menu.milestones': 'Milestones & Deliverables',
    'project.menu.team': 'Team & Resources',
    'project.dashboard.title': 'Project Management Dashboard',
    'project.dashboard.customizeKpi': 'Configure KPIs',
    'project.dashboard.totalProjects': 'Projects',
    'project.dashboard.active': 'active',
    'project.dashboard.inProgress': 'In progress',
    'project.dashboard.completedRate': 'completed',
    'project.dashboard.actionNeeded': 'Action needed',
    'project.dashboard.onTrack': 'On track',
    'project.dashboard.budgetAllocated': 'Allocated budget',
    'project.dashboard.consumed': 'consumed',
    'project.dashboard.upcomingMilestones': 'To validate',
    'project.dashboard.recentProjects': 'Active Projects',
    'project.dashboard.recentTasks': 'Recent Tasks',
    'project.dashboard.overBudget': 'Over budget',
    'project.dashboard.tasksCompleted': 'Tasks Completed',
    'project.dashboard.budgetSpent': 'Budget Spent',
    'project.dashboard.milestones': 'Upcoming Milestones',
    'project.dashboard.overdueTasks': 'overdue tasks',
    'project.dashboard.activeProjectsTitle': 'Active Projects',
    'project.dashboard.recentTasksTitle': 'Priority Tasks',
    'project.dashboard.noProjects': 'No projects recorded.',
    'project.dashboard.noTasks': 'No tasks recorded.',

    // ProjectManagement KPIs & Widgets (Parameterized)
    'project.dashboard.kpi.total_projects': 'Total Projects',
    'project.dashboard.kpi.desc.total_projects': 'Complete project portfolio',
    'project.dashboard.kpi.active_projects': 'Active Projects',
    'project.dashboard.kpi.desc.active_projects': 'Currently active projects',
    'project.dashboard.kpi.total_tasks': 'Total Tasks',
    'project.dashboard.kpi.desc.total_tasks': 'Overall planned work volume',
    'project.dashboard.kpi.done_tasks': 'Completed Tasks',
    'project.dashboard.kpi.desc.done_tasks': 'Successfully completed tasks',
    'project.dashboard.kpi.overdue_tasks': 'Overdue Tasks',
    'project.dashboard.kpi.desc.overdue_tasks': 'Tasks not completed past due date',
    'project.dashboard.kpi.total_budget': 'Total Budget',
    'project.dashboard.kpi.desc.total_budget': 'Allocated total financial budget',
    'project.dashboard.kpi.total_spent': 'Budget Spent',
    'project.dashboard.kpi.desc.total_spent': 'Total committed expenditures',
    'project.dashboard.kpi.pending_milestones': 'Key Milestones',
    'project.dashboard.kpi.desc.pending_milestones': 'Major milestones pending validation',
    'project.dashboard.widget.project_progress': 'Project Progress',
    'project.dashboard.widget.desc.project_progress': 'Visual tracking of completion rate',
    'project.dashboard.widget.urgent_tasks': 'Priority Tasks',
    'project.dashboard.widget.desc.urgent_tasks': 'Critical tasks requiring attention',
    'project.projects.pageTitle': 'Project Portfolio',
    'project.projects.new': 'New Project',
    'project.projects.editTitle': 'Edit Project',
    'project.projects.newTitle': 'New Project',
    'project.projects.generalInfo': 'General Information',
    'project.projects.code': 'Code',
    'project.projects.title': 'Project Title',
    'project.projects.client': 'Client / Beneficiary',
    'project.projects.status': 'Status',
    'project.projects.priority': 'Priority',
    'project.projects.manager': 'Project Manager',
    'project.projects.startDate': 'Start Date',
    'project.projects.endDate': 'Target End Date',
    'project.projects.budget': 'Total Budget',
    'project.projects.spent': 'Actual Spent',
    'project.projects.progress': 'Progress',
    'project.projects.description': 'Description & Goals',
    'project.projects.statusPlanning': 'Planning',
    'project.projects.statusInProgress': 'In Progress',
    'project.projects.statusOnHold': 'On Hold',
    'project.projects.statusCompleted': 'Completed',
    'project.projects.statusCancelled': 'Cancelled',
    'project.projects.priorityLow': 'Low',
    'project.projects.priorityMedium': 'Medium',
    'project.projects.priorityHigh': 'High',
    'project.projects.priorityUrgent': 'Urgent',
    'project.projects.confirmDelete': 'Are you sure you want to delete this project?',
    'project.projects.deleteSuccess': 'Project deleted successfully.',
    'project.projects.updateSuccess': 'Project updated successfully.',
    'project.projects.createSuccess': 'Project created successfully.',
    'project.projects.titleRequired': 'Project title is required.',
    'project.projects.searchPlaceholder': 'Search projects by title, client, manager...',
    'project.projects.empty': 'No projects found. Click "New Project" to get started.',
    'project.tasks.pageTitle': 'Tasks Management',
    'project.tasks.new': 'New Task',
    'project.tasks.editTitle': 'Edit Task',
    'project.tasks.newTitle': 'New Task',
    'project.tasks.details': 'Task Details',
    'project.tasks.code': 'Code',
    'project.tasks.project': 'Associated Project',
    'project.tasks.title': 'Task Title',
    'project.tasks.status': 'Status',
    'project.tasks.priority': 'Priority',
    'project.tasks.assignee': 'Assignee',
    'project.tasks.dueDate': 'Due Date',
    'project.tasks.due': 'Due',
    'project.tasks.estimatedHours': 'Estimated Hours',
    'project.tasks.actualHours': 'Actual Hours',
    'project.tasks.progress': 'Progress',
    'project.tasks.hours': 'estimated',
    'project.tasks.description': 'Detailed Description',
    'project.tasks.statusTodo': 'To Do',
    'project.tasks.statusInProgress': 'In Progress',
    'project.tasks.statusReview': 'In Review',
    'project.tasks.statusDone': 'Done',
    'project.tasks.statusBlocked': 'Blocked',
    'project.tasks.priorityLow': 'Low',
    'project.tasks.priorityMedium': 'Medium',
    'project.tasks.priorityHigh': 'High',
    'project.tasks.priorityCritical': 'Critical',
    'project.tasks.markIncomplete': 'Mark as incomplete',
    'project.tasks.markDone': 'Mark as done',
    'project.tasks.confirmDelete': 'Are you sure you want to delete this task?',
    'project.tasks.deleteSuccess': 'Task deleted successfully.',
    'project.tasks.updateSuccess': 'Task updated successfully.',
    'project.tasks.createSuccess': 'Task created successfully.',
    'project.tasks.titleRequired': 'Task title is required.',
    'project.tasks.projectRequired': 'Please select an associated project.',
    'project.tasks.searchPlaceholder': 'Search tasks by title, project, assignee...',
    'project.tasks.empty': 'No tasks found. Click "New Task" to get started.',
    'project.milestones.pageTitle': 'Milestones & Key Dates',
    'project.milestones.new': 'New Milestone',
    'project.milestones.editTitle': 'Edit Milestone',
    'project.milestones.newTitle': 'New Milestone',
    'project.milestones.details': 'Milestone Details',
    'project.milestones.code': 'Code',
    'project.milestones.project': 'Associated Project',
    'project.milestones.title': 'Milestone Title',
    'project.milestones.date': 'Target Date',
    'project.milestones.status': 'Status',
    'project.milestones.description': 'Milestone Description',
    'project.milestones.statusPending': 'Pending',
    'project.milestones.statusReached': 'Reached',
    'project.milestones.statusDelayed': 'Delayed',
    'project.milestones.markPending': 'Mark as pending',
    'project.milestones.markReached': 'Mark as reached',
    'project.milestones.confirmDelete': 'Are you sure you want to delete this milestone?',
    'project.milestones.deleteSuccess': 'Milestone deleted successfully.',
    'project.milestones.updateSuccess': 'Milestone updated successfully.',
    'project.milestones.createSuccess': 'Milestone created successfully.',
    'project.milestones.titleRequired': 'Milestone title is required.',
    'project.milestones.projectRequired': 'Please select an associated project.',
    'project.milestones.searchPlaceholder': 'Search milestones by title, project...',
    'project.milestones.empty': 'No milestones found. Click "New Milestone" to define a key objective.',
    'project.team.pageTitle': 'Team & Project Resources',
    'project.team.new': 'New Member',
    'project.team.editTitle': 'Edit Member',
    'project.team.newTitle': 'New Member',
    'project.team.details': 'Team Member Details',
    'project.team.project': 'Assigned Project',
    'project.team.name': 'Full Name',
    'project.team.role': 'Role / Position',
    'project.team.email': 'Email Address',
    'project.team.allocation': 'Allocation Rate (%)',
    'project.team.confirmDelete': 'Are you sure you want to remove this member from the project?',
    'project.team.deleteSuccess': 'Member removed successfully.',
    'project.team.updateSuccess': 'Member updated successfully.',
    'project.team.createSuccess': 'Member added successfully.',
    'project.team.nameRequired': 'Member name is required.',
    'project.team.projectRequired': 'Please select an associated project.',
    'project.team.searchPlaceholder': 'Search team member by name, role, project...',
    'project.team.empty': 'No team members recorded. Click "New Member" to allocate a resource.',
  },
  ar: {
    'login.title': 'تسجيل الدخول',
    'login.subtitle': 'نظام الإدارة الحديث',
    'login.email': 'عنوان البريد الإلكتروني',
    'login.password': 'كلمة المرور',
    'login.agency': 'رمز الوكالة',
    'login.connect': 'تسجيل الدخول',
    'login.forgotPassword': 'نسيت كلمة المرور؟',
    'login.resetPassword': 'إعادة تعيين كلمة المرور',
    'login.resetPasswordTitle': 'نسيت كلمة المرور',
    'login.resetPasswordSubtitle': 'أدخل عنوان بريدك الإلكتروني لاستلام كلمة مرور جديدة',
    'login.resetPasswordButton': 'إرسال كلمة المرور الجديدة',
    'login.backToLogin': 'العودة إلى تسجيل الدخول',
    'login.language': 'اللغة',
    'login.copyright': '© 2026 BLOBTIC. جميع الحقوق محفوظة.',
    'login.sso.or': 'أو',
    'login.sso.continueWith': 'أو المتابعة باستخدام',
    'login.sso.google': 'المتابعة باستخدام Google',
    'login.sso.microsoft': 'المتابعة باستخدام Microsoft',
    'login.sso.oidc': 'المتابعة باستخدام {0}',
    'login.sso.authenticating': 'جاري تسجيل الدخول الموحد (SSO)...',
    'login.sso.selectAgencyTitle': 'اختر الوكالة',
    'login.sso.selectAgencyDesc': 'يرتبط حسابك بعدة وكالات. يرجى اختيار الوكالة التي تريد تسجيل الدخول إليها:',
    'login.sso.confirmAgency': 'الدخول إلى الوكالة',
    'login.sso.error': 'فشل تسجيل الدخول عبر SSO',
    'login.sso.noAccount': 'لا يوجد حساب BLOBTIC مرتبط بهذا البريد الإلكتروني.',
    'login.sso.backToLogin': 'العودة إلى صفحة تسجيل الدخول',
    // Theme Customizer
    'theme.settings.title': 'المظهر',
    'theme.mode.label': 'الوضع',
    'theme.mode.dark': 'وضع الظلام',
    'theme.mode.light': 'وضع النهار',
    'theme.color.label': 'اللون الرئيسي',
    'common.actions.customize': 'تخصيص',
    'common.backToList': 'الرجوع إلى القائمة',
    'common.code': 'الرمز',
    'common.stage': 'مرحلة مسار المبيعات',
    'common.address': 'العنوان',
    'common.french': 'الفرنسية',
    'common.arabic': 'العربية',
    'common.english': 'الإنجليزية',
    'common.confirm': 'تأكيد',
    'common.none': 'لا يوجد',
    'common.autoGenerated': '(تلقائي)',
    'common.notes': 'ملاحظات',
    'common.viewAll': 'عرض الكل',
    'crm.opportunities.transferredKam': 'تم التحويل إلى مدير الحسابات (ورشة نشطة)',
    'crm.opportunities.readyTransferKam': 'جاهز للتسليم لمدير الحسابات',
    'CLIENT': 'عميل',
    'client': 'عميل',
    'PROSPECT': 'عميل محتمل',
    'prospect': 'عميل محتمل',
    'PARTNER': 'شريك',
    'partner': 'شريك',
    'VENDOR': 'مورد',
    'vendor': 'مورد',
    'LOW': 'منخفض',
    'MEDIUM': 'متوسط',
    'HIGH': 'مرتفع',
    'CRITICAL': 'حرج',
    'BLOCKING': 'معطل',
    'OPEN': 'مفتوح',
    'INVESTIGATING': 'قيد الفحص',
    'IN_PROGRESS': 'قيد المعالجة',
    'RESOLVED': 'تم الحل',
    'CLOSED': 'مغلق',
    'DEFAUT_POSE': 'عيب في التركيب',
    'DEFAUT_PRODUIT': 'عيب في المنتج',
    'RETARD_LIVRAISON': 'تأخر في التسليم',
    'NON_CONFORMITE_VITRAGE': 'عدم مطابقة الزجاج',
    'ACCESSOIRES_MANQUANTS': 'ملحقات مفقودة',
    'AUTRE': 'أخرى',
    'PRESCRIPTEUR': 'موصف',
    'KAM': 'مدير الحسابات الرئيسية',
    'PROSPECTION': 'استكشاف',
    'PRESCRIPTION_TECHNIQUE': 'توصيف فني',
    'SUIVI_CHANTIER': 'متابعة الورشة',
    'REUNION_ETUDES': 'اجتماع دراسات',
    'NEGOCIATION': 'تفاوض',
    'SAV_LIVRAISON': 'خدمة ما بعد البيع والتسليم',
    'PLANIFIEE': 'مخطط لها',
    'REALISEE': 'منجزة',
    'ANNULEE': 'ملغاة',
    'REPORTEE': 'مؤجلة',
    'PLANNED': 'مخطط',
    'COMPLETED': 'مكتمل',
    'CANCELLED': 'ملغى',
    'RESCHEDULED': 'معاد جدولتها',
    'DRAFT': 'مسودة',
    'TRANSMIS': 'مرسل',
    'EN_REVISION': 'قيد المراجعة',
    'INTEGRE_AU_CCTP': 'مدرج في دفتر الشروط',
    'VALIDE': 'معتمد',
    'REJETE': 'مرفوض',
    'SUBMITTED': 'مقدم',
    'VALIDATED': 'معتمد',
    'SIGNED': 'موقع',
    'REJECTED': 'مرفوض',
    'URBANIA': 'أوربانيا',
    'FUTURA': 'فوتورا',
    'DOORIA': 'دوريا',
    'MIXTE': 'مختلط',
    'URBANIA_ALU': 'أوربانيا ألمنيوم',
    'FUTURA_PVC': 'فوتورا بي في سي',
    'DOORIA_INTERIEUR': 'دوريا أبواب داخلية',
    'MIXTE_ALU_BOIS': 'مختلط ألمنيوم-خشب',
    'DEMARRAGE': 'انطلاق',
    'PREPARATION': 'تحضير',
    'EN_COURS': 'قيد الإنجاز',
    'POSE': 'تركيب',
    'RECEPTION': 'استلام',
    'CLOTURE': 'مغلق',
    'EN_PAUSE': 'متوقف مؤقتاً',
    'ARRETE': 'متوقف',
    'LIVRE': 'تم التسليم',
    'ON_SCHEDULE': 'وفق الجدول',
    'PARTIALLY_DELIVERED': 'تسليم جزئي',
    'DELIVERED': 'تسليم كلي',
    'DELAYED': 'متأخر',
    'PROMOTION_IMMOBILIERE': 'ترقية عقارية',
    'PROMOTION': 'ترقية عقارية',
    'AADL': 'عدل',
    'LPP': 'الترقوي العمومي',
    'LOGEMENT_INDIVIDUEL': 'سكن فردي',
    'EQUIPEMENT_PUBLIC': 'منشأة عمومية',
    'TERTIAIRE': 'خدماتي',
    'PROSPECTING': 'استكشاف',
    'QUALIFICATION': 'تأهيل',
    'PROPOSAL': 'عرض / مقايسة',
    'PROPOSITION_DEVIS': 'عرض / مقايسة',
    'NEGOTIATION': 'تفاوض',
    'WON': 'محسوم لصالحنا',
    'LOST': 'خسارة',
    'ABANDONNE': 'ملغى',
    'A_L_ARRET': 'متوقف',
    'TRANSFERRED': 'محول لمدير الحسابات',
    'PENDING_TRANSFER': 'جاهز للتحويل',
    'NON_TRANSFERE': 'غير محول',
    'TRANSFERE_KAM': 'محول لمدير الحسابات',
    'FAIBLE': 'منخفض',
    'MOYENNE': 'متوسط',
    'ELEVEE': 'مرتفع',
    'MOYEN': 'متوسط',
    'ELEVE': 'مرتفع',
    'BET': 'مكتب دراسات',
    'ARCHITECTE': 'مهندس معماري',
    'PROMOTEUR': 'مطور عقاري',
    'MO': 'صاحب المشروع',
    'ENT': 'مقاولة عامة',
    'INSTALLATEUR': 'نجار / مركب',
    'PETITE': 'مؤسسة صغيرة',
    'GRANDE': 'شركة كبرى',
    'CALL': 'مكالمة',
    'MEETING': 'موعد',
    'TECH_MEETING': 'اجتماع تقني',
    'EMAIL': 'بريد إلكتروني',
    'VISIT_SHOWROOM': 'زيارة المعرض',
    'SITE_SURVEY': 'زيارة الورشة',
    'DEMO': 'عرض توضيحي',
    'common.languages': 'اللغات',
    'common.actions.userManual': 'دليل المستخدم',
    'common.actions.switchApp': 'تغيير التطبيق',
    'common.default_view_mode': 'العرض الافتراضي',
    'common.view_grid': 'عرض الشبكة',
    'common.view_table': 'عرض الجدول',
    'common.refresh': 'تحديث',
    'common.refreshed': 'تم تحديث البيانات بنجاح.',
    'error.email.required': 'البريد الإلكتروني مطلوب',
    'error.email.invalid': 'تنسيق البريد الإلكتروني غير صحيح',
    'error.password.required': 'كلمة المرور مطلوبة',
    'error.password.minLength': 'يجب أن تحتوي كلمة المرور على 6 أحرف على الأقل',
    'error.agency.required': 'رمز الوكالة مطلوب',
    'error.agency.minLength': 'يجب أن يحتوي رمز الوكالة على 3 أحرف على الأقل',
    'error.resetPassword.success': 'تم إرسال كلمة مرور جديدة إلى عنوان بريدك الإلكتروني',
    'error.resetPassword.failed': 'خطأ في إرسال كلمة المرور الجديدة',
    'error.resetPassword.userNotFound': 'لم يتم العثور على مستخدم بهذا العنوان الإلكتروني',
    'zoom.agency.title': 'اختيار الوكالة',
    'zoom.agency.placeholder': 'انقر على زر التكبير لاختيار وكالة',
    'zoom.agency.disabled': 'يرجى ملء البريد الإلكتروني وكلمة المرور أولاً',
    'language.fr': 'Français',
    'language.en': 'English',
    'language.ar': 'العربية',

    // Dialog Titles
    'dialog.changePassword.title': 'تغيير كلمة المرور',
    'dialog.forgotPassword.title': 'نسيت كلمة المرور',

    // Change Password Dialog
    'dialog.changePassword.step.email': 'لتغيير كلمة المرور، يجب علينا أولاً التحقق من هويتك. أدخل بريدك الإلكتروني لاستلام رمز التحقق.',
    'dialog.changePassword.step.verify': 'تم إرسال رمز التحقق إلى {0}. أدخل هذا الرمز للمتابعة.',
    'dialog.changePassword.step.new': 'أدخل كلمة المرور الحالية وكلمة المرور الجديدة.',
    'dialog.changePassword.success': 'تم تغيير كلمة المرور بنجاح!',
    'label.currentPassword': 'كلمة المرور الحالية',
    'label.newPassword': 'كلمة المرور الجديدة',
    'label.confirmPassword': 'تأكيد كلمة المرور الجديدة',
    'label.verificationCode': 'رمز التحقق',

    // Forgot Password Dialog
    'dialog.forgotPassword.steps.1': 'طلب الرمز',
    'dialog.forgotPassword.steps.2': 'التحقق من الرمز',
    'dialog.forgotPassword.steps.3': 'كلمة مرور جديدة',
    'dialog.forgotPassword.step1.desc': 'أدخل عنوان بريدك الإلكتروني لاستلام رمز التحقق.',
    'dialog.forgotPassword.step2.desc': 'أدخل رمز التحقق المرسل إلى بريدك الإلكتروني.',
    'dialog.forgotPassword.step3.desc': 'أدخل كلمة المرور الجديدة.',
    'dialog.forgotPassword.success': 'تمت إعادة تعيين كلمة المرور بنجاح!',

    // Common Actions
    'btn.cancel': 'إلغاء',
    'btn.sendCode': 'إرسال الرمز',
    'btn.verifyCode': 'التحقق من الرمز',
    'btn.verify': 'تحقق',
    'btn.back': 'رجوع',
    'btn.changePassword': 'تغيير كلمة المرور',
    'btn.reset': 'إعادة تعيين',
    'btn.close': 'إغلاق',

    // Validation & Messages
    'error.email.empty': 'يرجى إدخال عنوان بريدك الإلكتروني',
    'error.otp.send': 'خطأ في إرسال رمز التحقق. يرجى المحاولة مرة أخرى.',
    'error.otp.empty': 'يرجى إدخال رمز التحقق',
    'error.otp.length': 'يجب أن يتكون رمز التحقق من 6 أرقام',
    'error.password.empty': 'يرجى إدخال كلمة المرور',
    'error.currentPassword.empty': 'يرجى إدخال كلمة المرور الحالية',
    'error.newPassword.empty': 'يرجى إدخال كلمة المرور الجديدة',
    'error.password.mismatch': 'كلمتا المرور غير متطابقتين',
    'error.connection': 'خطأ في الاتصال. يرجى المحاولة مرة أخرى.',
    'success.codeSent': 'تم إرسال رمز التحقق',
    'success.codeSentDesc': 'تم إرسال رمز التحقق إلى بريدك الإلكتروني.',
    'success.codeVerified': 'تم التحقق من الرمز',
    'success.codeVerifiedDesc': 'تم التحقق من الرمز. يمكنك الآن إدخال كلمة المرور الجديدة.',
    'success.passwordChanged': 'تم تغيير كلمة المرور',

    // Password Rules
    'rule.password.length': 'يجب أن تحتوي كلمة المرور على 8 أحرف على الأقل',
    'rule.password.lower': 'يجب أن تحتوي كلمة المرور على حرف صغير واحد على الأقل',
    'rule.password.upper': 'يجب أن تحتوي كلمة المرور على حرف كبير واحد على الأقل',
    'rule.password.digit': 'يجب أن تحتوي كلمة المرور على رقم واحد على الأقل',
    'rule.password.special': 'يجب أن تحتوي كلمة المرور على حرف خاص واحد على الأقل (!@#$%^&*)',

    // Zoom Interface
    'zoom.title.default': 'تحديد - {0}',
    'zoom.title.agency': 'اختيار الوكالة',
    'zoom.title.user': 'اختيار المستخدم',
    'zoom.title.profile': 'اختيار الملف الشخصي',
    'zoom.title.employee': 'اختيار الموظف',
    'zoom.title.license': 'اختيار الترخيص',
    'zoom.search.placeholder': 'البحث في البيانات...',
    'zoom.action.refresh': 'تحديث البيانات',
    'zoom.action.export': 'تصدير البيانات',
    'zoom.action.exportCsv': 'تصدير إلى CSV',
    'zoom.action.exportExcel': 'تصدير إلى Excel',
    'zoom.status.results': '{0} نتيجة من إجمالي {1}',
    'zoom.status.filtered': 'تمت التصفية حسب: "{0}"',
    'zoom.error.loading': '❌ خطأ أثناء تحميل البيانات',
    'zoom.error.unknown': 'خطأ غير معروف',
    'zoom.error.validation': 'خطأ في التحقق',
    'zoom.error.advice': 'تحقق من الإعدادات وحاول مرة أخرى، أو اتصل بالمسؤول إذا استمرت المشكلة.',
    'zoom.empty.search': 'لم يتم العثور على نتائج لبحثك',
    'zoom.empty.data': 'لا توجد بيانات متاحة',
    'zoom.hint.select': 'انقر على صف للتحديد',
    'zoom.action.cancel': 'إلغاء',
    'zoom.action.select': 'تحديد',
    'zoom.tooltip.clear': 'مسح التحديد',
    'zoom.tooltip.open': 'فتح التحديد {0}',
    'zoom.tooltip.disabled': 'يرجى ملء البريد الإلكتروني وكلمة المرور أولاً',
    'zoom.check.fillRequired': 'يرجى ملء الحقول المطلوبة',

    // Backend Messages
    'LOGIN_SUCCESS': 'تم تسجيل الدخول بنجاح',
    'INVALID_PASSWORD': 'كلمة المرور غير صحيحة',
    'USER_NOT_FOUND': 'المستخدم غير موجود',
    'MISSING_PARAMETERS': 'معلمات مفقودة',
    'INVALID_DOSSIER': 'ملف غير صالح',
    'INVALID_LICENSE': 'ترخيص غير صالح',
    'OTP_SENT': 'تم إرسال رمز التحقق',
    'OTP_EXPIRED': 'انتهت صلاحية رمز التحقق',
    'INVALID_OTP': 'رمز التحقق غير صالح',
    'DOUBLE_AUTH_REQUIRED': 'المصادقة الثنائية مطلوبة',
    'SESSION_LOST': 'انتهت صلاحية الجلسة',
    'OTP_BLOCKED_RECENTLY': 'محاولات كثيرة جداً، يرجى الانتظار',
    'EMAIL_SEND_ERROR': 'خطأ في إرسال البريد الإلكتروني',
    'OTP_GENERATION_ERROR': 'خطأ في إنشاء رمز التحقق',
    'OTP_NOT_FOUND': 'لم يتم العثور على رمز التحقق',
    'OTP_BLOCKED': 'تم حظر رمز التحقق',
    'OTP_VALIDATED': 'تم التحقق من الرمز',
    'OTP_VALIDATION_ERROR': 'خطأ في التحقق من الرمز',
    'JWT_CONFIG_RETRIEVED': 'تم استرجاع إعدادات JWT',
    'JWT_CONFIG_ERROR': 'خطأ في إعدادات JWT',
    'PARAMETER_MANAGER_NULL': 'خطأ في النظام (الإعدادات)',
    'DOUBLE_AUTH_STATUS_RETRIEVED': 'تم استرجاع حالة المصادقة الثنائية',
    'DOUBLE_AUTH_STATUS_ERROR': 'خطأ في حالة المصادقة الثنائية',

    // Auth Context Messages
    'auth.error.noPending': 'لا يوجد اتصال معلق',
    'auth.success.login.title': 'تم تسجيل الدخول بنجاح',
    'auth.success.login.welcome': 'مرحباً {0}!',
    'auth.error.invalidResponse': 'استجابة تسجيل الدخول غير صالحة',
    'auth.error.loginTitle': 'خطأ في تسجيل الدخول',
    'auth.error.doubleAuthTitle': 'خطأ في المصادقة الثنائية',
    'auth.error.unexpected': 'حدث خطأ غير متوقع.',
    'auth.error.server': 'خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً.',
    'auth.error.network': 'لا يمكن الاتصال بالخادم. تحقق من اتصالك.',
    'auth.info.doubleAuthRequired': 'المصادقة الثنائية مطلوبة',
    'auth.info.otpSent': 'تم إرسال رمز التحقق إلى عنوان بريدك الإلكتروني.',
    'auth.error.credentialsTitle': 'بيانات الاعتماد غير صالحة',
    'auth.error.credentials': 'البريد الإلكتروني أو كلمة المرور أو الوكالة غير صحيحة.',
    'auth.error.otpBlockedRecentlyTitle': 'المصادقة الثنائية محظورة مؤقتاً',
    'auth.error.otpBlockedRecentlyDesc': 'محاولات حديثة كثيرة جداً. يرجى الانتظار 30 دقيقة قبل المحاولة مرة أخرى.',
    'auth.error.otpBlockedTitle': 'تم حظر رمز المصادقة الثنائية',
    'auth.error.otpBlockedDesc': 'محاولات غير صحيحة كثيرة جداً. تم حظر الرمز لأسباب أمنية.',
    'auth.error.otpExpiredTitle': 'انتهت صلاحية رمز المصادقة الثنائية',
    'auth.error.otpExpiredDesc': 'انتهت صلاحية الرمز. يرجى طلب رمز جديد.',
    'auth.error.invalidOtpTitle': 'رمز المصادقة الثنائية غير صحيح',
    'auth.error.invalidOtpDesc': 'الرمز المدخل غير صحيح. يرجى التحقق والمحاولة مرة أخرى.',
    'auth.success.logout.title': 'تسجيل الخروج',
    'auth.success.logout.message': 'تم تسجيل الخروج بنجاح.',
    'auth.session_expired.title': 'انتهت الجلسة',
    'auth.session_expired.message': 'انتهت صلاحية الجلسة أو غير صالحة. جاري التوجيه إلى صفحة تسجيل الدخول...',
    'auth.session_expired.auto_close_text': 'إغلاق تلقائي (جلسة منتهية أو غير صالحة)',

    // Double Auth Dialog
    'doubleAuth.title': 'المصادقة الثنائية',
    'doubleAuth.body': 'تم إرسال رمز التحقق إلى عنوان بريدك الإلكتروني. يرجى إدخال هذا الرمز لإكمال تسجيل الدخول.',
    'doubleAuth.code.label': 'رمز التحقق',
    'doubleAuth.code.placeholder': '123456',
    'doubleAuth.security.label': 'الأمان:',
    'doubleAuth.security.desc': 'تنتهي صلاحية الرمز خلال 10 دقائق. بعد 3 محاولات غير صحيحة، سيتم حظر الرمز لمدة 30 دقيقة.',
    'doubleAuth.btn.moreInfo': 'مزيد من المعلومات',
    'doubleAuth.btn.resend': 'إعادة إرسال الرمز',
    'doubleAuth.btn.verify': 'تحقق',
    'doubleAuth.tip.label': 'نصيحة:',
    'doubleAuth.alert.blockedDesc': 'انتظر 30 دقيقة أو اتصل بالمسؤول إذا استمرت المشكلة.',
    'doubleAuth.alert.expiredDesc': 'انقر على "إعادة إرسال الرمز" لاستلام رمز جديد.',
    'doubleAuth.delay.label': 'تأخير الأمان:',
    'doubleAuth.delay.wait': 'يمكنك طلب رمز جديد خلال',
    'doubleAuth.delay.now': 'الآن',
    'doubleAuth.delay.protectionLabel': 'حماية من سوء الاستخدام:',
    'doubleAuth.delay.protectionDesc': 'تأخير 60 ثانية بين الطلبات',
    'doubleAuth.attempts.label': 'المحاولات:',
    'doubleAuth.attempts.lastChance': 'آخر فرصة!',
    'doubleAuth.attempts.remaining': 'المحاولات المتبقية',
    'doubleAuth.error.required': 'الرمز مطلوب',
    'doubleAuth.error.requiredDesc': 'يرجى إدخال رمز التحقق',
    'doubleAuth.error.invalid': 'رمز غير صالح',
    'doubleAuth.error.invalidDesc': 'يجب أن يتكون رمز التحقق من 6 أرقام',
    'doubleAuth.success.resend': 'تم إعادة إرسال الرمز',
    'doubleAuth.success.resendDesc': 'تم إرسال رمز تحقق جديد إلى بريدك الإلكتروني.',
    'doubleAuth.error.resend': 'خطأ',
    'doubleAuth.error.resendDesc': 'تعذر إعادة إرسال الرمز. يرجى المحاولة مرة أخرى.',

    // Security Info Dialog
    'securityInfo.title': 'أمان المصادقة الثنائية',
    'securityInfo.protection.title': 'حماية معززة:',
    'securityInfo.protection.desc': 'حسابك محمي بمستويات أمان متعددة لمنع الوصول غير المصرح به.',
    'securityInfo.measures.title': '🔐 تدابير الأمان النشطة',
    'securityInfo.measure.email.title': 'رمز OTP عبر البريد الإلكتروني',
    'securityInfo.measure.email.desc': 'يتم إرسال رمز فريد من 6 أرقام إلى عنوان بريدك الإلكتروني المسجل',
    'securityInfo.measure.expiry.title': 'انتهاء الصلاحية التلقائي',
    'securityInfo.measure.expiry.desc': 'تنتهي صلاحية الرمز تلقائيًا بعد 10 دقائق لأسباب أمنية',
    'securityInfo.measure.limit.title': 'حد المحاولات',
    'securityInfo.measure.limit.desc': 'بعد 3 محاولات غير صحيحة، يتم حظر الرمز لمدة 30 دقيقة',
    'securityInfo.measure.abuse.title': 'حماية من سوء الاستخدام',
    'securityInfo.measure.abuse.desc': 'تأخير 60 ثانية بين طلبات الرموز الجديدة',
    'securityInfo.help.title': '⚠️ ماذا تفعل في حالة حدوث مشكلة؟',
    'securityInfo.help.expired.title': 'انتهت صلاحية الرمز',
    'securityInfo.help.expired.desc': 'انقر على "إعادة إرسال الرمز" لاستلام رمز جديد',
    'securityInfo.help.blocked.title': 'تم حظر الرمز',
    'securityInfo.help.blocked.desc': 'انتظر 30 دقيقة أو اتصل بالمسؤول إذا استمرت المشكلة',
    'securityInfo.help.email.title': 'لم يتم استلام البريد الإلكتروني',
    'securityInfo.help.email.desc': 'تحقق من مجلد الرسائل غير المرغوب فيها وتأكد من صحة عنوان البريد الإلكتروني',
    'securityInfo.tip.label': 'نصيحة:',
    'securityInfo.tip.desc': 'حافظ على تحديث عنوان بريدك الإلكتروني وتحقق من رسائل البريد الإلكتروني بانتظام للحصول على تجربة مثالية.',
    'securityInfo.btn.understand': 'فهمت',

    // Global / Sidebar
    'auth.logout': 'تسجيل الخروج',
    'sidebar.search.placeholder': 'بحث...',
    'common.actions.enterFullScreen': 'ملء الشاشة',
    'common.actions.exitFullScreen': 'خروج من وضع ملء الشاشة',
    'footer.copyright': '© 2026 BLOBTIC. جميع الحقوق محفوظة.',

    // Profiles
    'page.profiles.title': 'إدارة الملفات الشخصية',
    'page.profiles.new_title': 'ملف شخصي جديد',
    'page.profiles.edit_title': 'تعديل الملف الشخصي: {0}',
    'page.profiles.add_new': 'ملف شخصي جديد',
    'page.profiles.back_to_list': 'العودة إلى القائمة',
    'page.profiles.confirm_delete': 'هل أنت متأكد من حذف الملف الشخصي {0}؟',
    'page.profiles.search_placeholder': 'البحث عن ملف شخصي...',
    'page.profiles.view_grid': 'عرض الشبكة',
    'page.profiles.view_table': 'عرض الجدول',
    'page.profiles.status.admin': 'مسؤول',
    'page.profiles.status.standard': 'عادي',
    'page.profiles.details': 'تفاصيل الملف الشخصي',
    'page.profiles.zoom.label': 'اختر ملفاً شخصياً (زووم)',
    'page.profiles.zoom.helper': 'انقر على العدسة المكبرة للبحث عن ملف شخصي',
    'page.profiles.field.code': 'رمز الملف الشخصي',
    'page.profiles.field.label': 'تسمية الملف الشخصي',
    'page.profiles.field.level': 'المستوى',
    'page.profiles.field.admin': 'مسؤول',
    'page.profiles.validation.required': 'الرمز والتسمية مطلوبان',
    'page.profiles.popup.title': 'قائمة الملفات الشخصية',

    // Agency
    'page.agency.title': 'الوكالات',
    'page.agency.new_title': 'وكالة جديدة',
    'page.agency.edit_title': 'تعديل الوكالة: {0}',
    'page.agency.add_new': 'وكالة جديدة',
    'page.agency.back_to_list': 'العودة إلى القائمة',
    'page.agency.confirm_delete': 'هل أنت متأكد من حذف الوكالة {0}؟',
    'page.agency.search_placeholder': 'البحث عن وكالة...',
    'page.agency.view_grid': 'عرض الشبكة',
    'page.agency.view_table': 'عرض الجدول',
    'page.agency.zoom.label': 'البحث عن وكالة',
    'page.agency.zoom.helper': 'اكتب رمز الوكالة أو الاسم للبحث',
    'page.agency.details': 'تفاصيل الوكالة',
    'page.agency.field.code': 'الرمز',
    'page.agency.field.lib': 'الاسم / اسم الشركة',
    'page.agency.field.email': 'البريد الإلكتروني',
    'page.agency.field.phone': 'هاتف',
    'page.agency.field.address1': 'العنوان 1',
    'page.agency.field.address2': 'العنوان 2',
    'page.agency.field.pays_legal': 'البلد القانوني',
    'page.agency.field.legal_country': 'البلد القانوني',
    'page.agency.field.mobile_phone': 'الهاتف المحمول',
    'page.agency.field.start_date': 'تاريخ البدء',
    'page.agency.field.end_date': 'تاريخ الانتهاء',
    'page.agency.field.closed_plng': 'تاريخ غلق التخطيط / الوكالة',
    'page.agency.helper.closed_plng': 'يقفل التخطيط والفواتير السابقة أو المساوية لهذا التاريخ',
    'page.agency.field.memo': 'ملاحظة',
    'page.agency.validation.required': 'الرمز والاسم مطلوبان',
    'page.agency.validation.image_size': 'يجب أن يكون حجم الصورة أقل من 10 ميغابايت',
    'page.agency.fiscal_identifiers': 'المعرفات الجبائية والقانونية',
    'page.agency.field.nif': 'رقم التعريف الجبائي (NIF)',
    'page.agency.helper.nif': 'رقم التعريف الجبائي (15 رقم)',
    'page.agency.field.nis': 'رقم التعريف الإحصائي (NIS)',
    'page.agency.helper.nis': 'رقم التعريف الإحصائي (11 رقم)',
    'page.agency.field.rc': 'السجل التجاري (RC)',
    'page.agency.helper.rc': 'رقم السجل التجاري',
    'page.agency.field.ai': 'المادة الضريبية (AI)',
    'page.agency.helper.ai': 'رقم المادة الضريبية (13 رقم)',
    'page.agency.field.logo': 'شعار الوكالة',
    'page.agency.logo': 'الشعار',
    'page.agency.no_logo': 'لا يوجد شعار',
    'page.agency.upload_logo': 'تحميل الشعار',
    'page.agency.choose_image': 'اختر صورة',

    // Pages - Users
    'page.users.title': 'إدارة المستخدمين',
    'page.users.new_title': 'مستخدم جديد',
    'page.users.edit_title': 'تعديل المستخدم: {0}',
    'page.users.add_new': 'مستخدم جديد',
    'page.users.back_to_list': 'العودة إلى القائمة',
    'page.users.confirm_delete': 'هل أنت متأكد من حذف المستخدم {0}؟',
    'page.users.search_placeholder': 'البحث عن مستخدم...',
    'page.users.view_grid': 'عرض الشبكة',
    'page.users.view_table': 'عرض الجدول',
    'page.users.details': 'تفاصيل المستخدم',
    'page.users.zoom.label': 'اختر مستخدم',
    'page.users.zoom.helper': 'انقر للبحث عن مستخدم',
    'page.users.popup.title': 'قائمة المستخدمين',
    'page.users.field.code': 'رمز المستخدم',
    'page.users.field.lib': 'اسم المستخدم',
    'page.users.field.password': 'كلمة المرور',
    'page.users.field.profile': 'الملف الشخصي',
    'page.users.field.profile_lib': 'تسمية الملف الشخصي',
    'page.users.field.license': 'الترخيص',
    'page.users.field.license_lib': 'تسمية الترخيص',
    'page.users.field.start_date': 'تاريخ البدء',
    'page.users.field.end_date': 'تاريخ الانتهاء',
    'page.users.field.email': 'البريد الإلكتروني',
    'page.users.field.mobile': 'رقم الهاتف المحمول',
    'page.users.field.pass_date': 'تاريخ انتهاء كلمة المرور',
    'page.users.field.pass_days': 'أيام التنبيه قبل الانتهاء',
    'page.users.validation.required': 'الرمز والاسم مطلوبان',
    'page.users.field.memo': 'ملاحظة',
    'LOGIN_PASSWORD_EXPIRED': 'انتهت صلاحية كلمة المرور الخاصة بك.',
    'LOGIN_ACCOUNT_EXPIRED': 'الحساب منتهي الصلاحية.',
    'LOGIN_PASSWORD_EXPIRING_SOON': 'ستنتهي صلاحية كلمة المرور الخاصة بك قريبًا.',

    'page.sys_parameters.title': 'إعدادات النظام',
    'page.sys_parameters.subtitle': 'الإدارة المركزية لإعدادات التطبيق',
    'page.sys_parameters.action.reset': 'إعادة ضبط المصنع',
    'page.sys_parameters.confirm_reset': 'هل أنت متأكد أنك تريد إعادة تعيين كافة الإعدادات إلى القيم الافتراضية؟ لا يمكن التراجع عن هذا الإجراء.',
    'page.sys_parameters.success.reset': 'تم إعادة تعيين الإعدادات بنجاح',
    'page.sys_parameters.error.reset': 'خطأ في إعادة تعيين المعلمات',
    'page.sys_parameters.success.update': 'تم التحديث بنجاح',
    'page.sys_parameters.success.message': 'تم تحديث المعلمة {0} بنجاح',
    'page.sys_parameters.meta.info': 'تم التحديث: {0} بواسطة {1}',

    'action.update': 'تحديث',
    'action.save': 'حفظ',
    'action.refresh': 'تحديث',
    'SYSPAR_CODE': 'رمز المعلمة',

    'page.sys_parameters.group.messaging': 'المراسلة (SMTP)',
    'page.sys_parameters.group.security': 'الأمان والمصادقة',
    'page.sys_parameters.group.performance': 'الأداء والتخزين المؤقت',
    'page.sys_parameters.group.licensing': 'الترخيص والحدود',
    'page.sys_parameters.group.maintenance': 'الصيانة والتنظيف',
    'page.sys_parameters.group.system': 'النظام والاختبارات',
    'page.sys_parameters.group.gps': 'تتبع GPS',
    'page.sys_parameters.group.sso': 'المصادقة الأحادية SSO (جوجل، مايكروسوفت، OIDC)',
    'page.sys_parameters.group.other': 'إعدادات النظام الأخرى',

    // Parameter Labels (SYSPAR_LIB)
    'page.sys_parameters.lib.String_Envoi_Mail': 'تكوين SMTP لإرسال البريد الإلكتروني',
    'page.sys_parameters.lib.Session_Expiration_Minutes': 'مدة صلاحية الجلسة بالدقائق',
    'page.sys_parameters.lib.Sync_Notifications_Timer_Seconds': 'فترة تحديث الإشعارات والمحادثة بالثواني (0 = معطل)',
    'page.sys_parameters.lib.JWT_Expiration_Minutes': 'مدة صلاحية JWT بالدقائق',
    'page.sys_parameters.lib.Default_App_Name': 'اسم التطبيق الافتراضي',
    'page.sys_parameters.lib.Licensed_Applications': 'قائمة التطبيقات المرخصة',
    'page.sys_parameters.lib.ObjectPool_MaxSize': 'الحد الأقصى لحجم تجمع الكائنات',
    'page.sys_parameters.lib.DictionaryPool_MaxSize': 'الحد الأقصى لحجم تجمع القاموس',
    'page.sys_parameters.lib.ListPool_MaxSize': 'الحد الأقصى لحجم تجمع القائمة',
    'page.sys_parameters.lib.DataManager_Cache_Duration_Minutes': 'مدة التخزين المؤقت DataManager بالدقائق',
    'page.sys_parameters.lib.Compression_Threshold_Bytes': 'عتبة الضغط بالبايت',
    'page.sys_parameters.lib.Response_Cache_Duration_Seconds': 'مدة التخزين المؤقت للاستجابة بالثواني',
    'page.sys_parameters.lib.Pool_Hit_Rate_Threshold': 'عتبة معدل نجاح التجمع (%)',
    'page.sys_parameters.lib.Cache_Hit_Rate_Threshold': 'عتبة معدل نجاح ذاكرة التخزين المؤقت (%)',
    'page.sys_parameters.lib.Max_Creation_Time_Ms': 'الحد الأقصى لوقت الإنشاء بالمللي ثانية',
    'page.sys_parameters.lib.Alert_Cooldown_Minutes': 'تهدئة التنبيه بالدقائق',
    'page.sys_parameters.lib.License_Max_Users': 'الحد الأقصى للمستخدمين لكل ترخيص',
    'page.sys_parameters.lib.License_Max_Dossiers': 'الحد الأقصى للملفات لكل ترخيص',
    'page.sys_parameters.lib.License_Storage_Limit_GB': 'حد التخزين لكل ترخيص (GB)',
    'page.sys_parameters.lib.LoadTest_Iterations': 'عدد التكرارات لاختبارات التحميل',
    'page.sys_parameters.lib.LoadTest_Operations_Per_Iteration': 'عدد العمليات لكل تكرار',
    'page.sys_parameters.lib.LoadTest_Batch_Size': 'حجم الدفعة لاختبارات التحميل',
    'page.sys_parameters.lib.LoadTest_Batch_Delay_Ms': 'التأخير بين الدفعات بالمللي ثانية',
    'page.sys_parameters.lib.License_Monitoring_Interval_Minutes': 'فاصل مراقبة الترخيص بالدقائق',
    'page.sys_parameters.lib.Auto_Cleanup_Expired_OTP_Hours': 'فاصل تنظيف OTP منتهي الصلاحية بالساعات',
    'page.sys_parameters.lib.Auto_Cleanup_Expired_Sessions_Hours': 'فاصل تنظيف الجلسات منتهية الصلاحية بالساعات',
    'page.sys_parameters.lib.GPS_API_Key': 'مفتاح API لـ GPS',
    'page.sys_parameters.lib.GPS_Endpoint_URL': 'عنوان URL لنقطة نهاية GPS',
    'page.sys_parameters.lib.GPS_Provider': 'مزود GPS',
    'page.sys_parameters.lib.Log_Retention_Days': 'مدة الاحتفاظ بالسجلات بالأيام',
    'page.sys_parameters.lib.Performance_Metrics_Retention_Hours': 'الاحتفاظ بمقاييس الأداء بالساعات',
    'page.sys_parameters.lib.OTP_Expiration_Minutes': 'مدة صلاحية OTP بالدقائق',
    'page.sys_parameters.lib.OTP_Max_Attempts': 'محاولات OTP القصوى',
    'page.sys_parameters.lib.Password_Min_Length': 'الحد الأدنى لطول كلمة المرور',
    'page.sys_parameters.lib.Double_Authentication_Enabled': 'تمكين المصادقة الثنائية (0=معطل، 1=ممكن)',
    'page.sys_parameters.lib.WEB_SECURITY_ENABLED': 'تمكين أمان الويب (0=معطل، 1=ممكن)',
    'page.sys_parameters.lib.Double_Auth_Method': 'طريقة المصادقة الثنائية (OTP, SMS, EMAIL)',
    'page.sys_parameters.lib.Currency_Symbol': 'رمز العملة (دج,$, €, £, إلخ)',
    'page.sys_parameters.lib.Geo_Restriction_Enabled': 'تمكين التقيد الجغرافي حسب عنوان IP (0=معطل، 1=ممكن)',
    'page.sys_parameters.lib.Geo_Restriction_Mode': 'وضع التقيد الجغرافي (السماح للمحددة فقط / حظر المحددة فقط)',
    'page.sys_parameters.lib.Geo_Restriction_Countries': 'رموز البلدان ISO المسموح بها/المحظورة مفصولة بفواصل (مثال: DZ للجزائر)',

    'page.sys_parameters.lib.IDXDB_CACHE_ENABLED': 'ذاكرة التخزين المؤقت على جانب العميل (0=معطل, 1=مفعل)',
    'page.sys_parameters.lib.SSO_ENABLED': 'تمكين المصادقة الأحادية SSO عالمياً (0=معطل، 1=مفعل)',
    'page.sys_parameters.lib.SSO_DEFAULT_PROVIDER': 'مزود SSO الافتراضي (google, microsoft, oidc)',
    'page.sys_parameters.lib.SSO_GOOGLE_ENABLED': 'تمكين SSO جوجل وورك سبيس (0=معطل، 1=مفعل)',
    'page.sys_parameters.lib.SSO_GOOGLE_CLIENT_ID': 'معرف عميل OAuth 2.0 لـ Google',
    'page.sys_parameters.lib.SSO_GOOGLE_CLIENT_SECRET': 'المفتاح السري لعميل OAuth 2.0 لـ Google',
    'page.sys_parameters.lib.SSO_MICROSOFT_ENABLED': 'تمكين SSO مايكروسوفت إنترا (0=معطل، 1=مفعل)',
    'page.sys_parameters.lib.SSO_MICROSOFT_CLIENT_ID': 'معرف التطبيق / العميل لـ Microsoft Entra ID',
    'page.sys_parameters.lib.SSO_MICROSOFT_CLIENT_SECRET': 'المفتاح السري لـ Microsoft Entra ID',
    'page.sys_parameters.lib.SSO_MICROSOFT_TENANT_ID': 'معرف المستأجر Tenant ID لمايكروسوفت',
    'page.sys_parameters.lib.SSO_OIDC_ENABLED': 'تمكين مزود OpenID Connect العام (0=معطل، 1=مفعل)',
    'page.sys_parameters.lib.SSO_OIDC_NAME': 'الاسم المعروض لمزود OpenID Connect',
    'page.sys_parameters.lib.SSO_OIDC_AUTHORITY': 'عنوان URL الأساسي لسلطة OIDC',
    'page.sys_parameters.lib.SSO_OIDC_CLIENT_ID': 'معرف العميل لمزود OpenID Connect',
    'page.sys_parameters.lib.SSO_OIDC_CLIENT_SECRET': 'المفتاح السري لمزود OpenID Connect',
    // Dropdown Options
    'page.sys_parameters.option.enabled': 'مفعل',
    'page.sys_parameters.option.disabled': 'معطل',
    'page.sys_parameters.option.geo.allow_only': 'السماح للبلدان المحددة فقط',
    'page.sys_parameters.option.geo.block_only': 'حظر البلدان المحددة فقط',
    'page.sys_parameters.option.method.otp': 'OTP (كلمة مرور لمرة واحدة)',
    'page.sys_parameters.option.method.sms': 'رسالة نصية (SMS)',
    'page.sys_parameters.option.method.email': 'بريد إلكتروني',

    'page.parameters.title': 'إعدادات النظام (قديم)',
    'page.parameters.subtitle': 'إدارة التكوينات العامة للتطبيق',
    'page.parameters.table.code': 'الرمز',
    'page.parameters.table.label': 'التسمية',
    'page.parameters.table.value': 'القيمة',
    'page.parameters.table.type': 'النوع',
    'page.parameters.table.actions': 'الإجراءات',
    'page.parameters.table.empty': 'لم يتم العثور على أي معلمات',
    'page.parameters.action.refresh': 'تحديث',
    'page.parameters.action.edit': 'تعديل',
    'page.parameters.dialog.title': 'تعديل المعلمة {0}',
    'page.parameters.dialog.description': 'الوصف',
    'page.parameters.dialog.value': 'القيمة',
    'page.parameters.dialog.save': 'حفظ',
    'page.parameters.dialog.cancel': 'إلغاء',
    'page.parameters.success.title': 'تم تحديث المعلمة',
    'page.parameters.success.message': 'تم تعديل المعلمة {0}.',
    'page.parameters.error.title': 'خطأ',
    'page.parameters.error.update': 'تعذر تحديث المعلمة.',
    'page.parameters.error.load': 'تعذر تحميل المعلمات.',
    'traffic.table.country': 'البلد',
    'traffic.stats.distinct_countries': 'البلدان المختلفة',

    // Common / GenericPage
    'common.actions.new': 'جديد',
    'common.actions.edit': 'تعديل',
    'common.actions.delete': 'حذف',
    'common.actions.save': 'حفظ',
    'common.actions.update': 'تحديث',
    'common.actions.insert': 'إدراج',
    'common.actions.insert_copy': 'نسخ (إدراج كجديد)',
    'common.actions.duplicate': 'تكرار',
    'common.actions.confirm': 'تأكيد',
    'common.actions.close': 'إغلاق',
    'common.actions.cancel': 'إلغاء',
    // boubaker 23/08/2026 - print pages (GenericPage type print)
    'common.actions.print': 'طباعة',
    'common.actions.excel': 'إكسيل',
    'common.actions.word': 'وورد',
    'common.actions.filters': 'إظهار/إخفاء الفلاتر',
    'common.confirm.title.delete': 'تأكيد الحذف',
    'common.confirm.title.update': 'تأكيد التحديث',
    'common.confirm.title.insert': 'تأكيد الإدراج',
    'common.confirm.delete': 'هل أنت متأكد أنك تريد حذف هذا العنصر؟',
    'common.confirm.update': 'هل أنت متأكد أنك تريد تعديل هذا العنصر؟',
    'common.confirm.insert': 'هل أنت متأكد أنك تريد إدراج هذا العنصر كسجل جديد؟',
    'common.labels.lastUpdate': 'آخر تحديث:',
    'common.labels.user': 'المستخدم:',
    'common.messages.success': 'نجاح',
    'common.messages.warning': 'تحذير',
    'common.messages.error': 'خطأ',
    'common.messages.saved': 'تم التحديث بنجاح',
    'common.messages.created': 'تم الإنشاء بنجاح',
    'common.messages.deleted_success': 'تم الحذف بنجاح',
    'common.messages.save_error': 'خطأ أثناء الحفظ',
    'common.messages.delete_error': 'خطأ أثناء الحذف',
    'common.messages.comm_error': 'خطأ في الاتصال',

    // Dates
    'common.date.today': 'اليوم',
    'common.date.yesterday': 'أمس',
    'common.date_time': 'التاريخ والوقت',

    // Top Navigation & Actions
    'common.actions.chat': 'دردشة',
    'common.actions.notifications': 'إشعارات',
    'common.actions.lightMode': 'الوضع النهاري',
    'common.actions.darkMode': 'الوضع الليلي',
    'common.actions.profile': 'الملف الشخصي',
    'common.actions.markAllRead': 'تحديد الكل كمقروء',
    'common.actions.clear': 'مسح الكل',
    'common.messages.noNotifications': 'لا توجد إشعارات جديدة',
    'common.placeholder.message': 'اكتب رسالة...',

    // Menu Planning
    'menu.planning.commande': 'الطلب',
    'menu.planning.planning': 'التخطيط',
    'menu.planning.facturation': 'الفواتير',
    'menu.planning.editions': 'الإصدارات',
    'menu.planning.entite': 'الكيان',
    'menu.planning.administration': 'الإدارة',
    'menu.planning.admin.configMenu': 'إعدادات القائمة',
    'menu.planning.admin.configKpi': 'تعيين ملف المؤشرات',
    'admin.config_kpi.select_app': 'التطبيق',
    'admin.config_kpi.select_profile': 'اختر ملف المستخدم',
    'admin.config_kpi.search_profile': 'البحث عن ملف...',
    'admin.config_kpi.title': 'إعدادات المؤشرات',
    'admin.config_kpi.save': 'حفظ',
    'admin.config_kpi.saving': 'جارٍ الحفظ...',
    'admin.config_kpi.load_error': 'خطأ في تحميل المؤشرات',
    'admin.config_kpi.select_profile_required': 'يرجى اختيار ملف',
    'admin.config_kpi.save_success': 'تم حفظ إعدادات المؤشرات بنجاح',
    'admin.config_kpi.save_error': 'خطأ في حفظ الإعدادات',
    'admin.config_kpi.save_server_error': 'خطأ في الخادم أثناء الحفظ',
    'admin.config_kpi.kpi_count': '{app} — {enabled}/{total} نشط',
    'admin.config_kpi.mode_config': 'الإعدادات',
    'admin.config_kpi.mode_live': 'الوضع المباشر (تجريبي)',
    'admin.config_kpi.select_all': 'تحديد الكل',
    'admin.config_kpi.deselect_all': 'إلغاء التحديد',
    'admin.config_kpi.no_kpi': 'لا توجد مؤشرات متاحة لهذه التطبيق.',
    'admin.config_kpi.live_preview': 'معاينة ببيانات تجريبية — العرض الفعلي يعتمد على البيانات.',
    'admin.config_kpi.type_widget': 'أداة',
    'admin.config_kpi.type_card': 'بطاقة',
    'admin.config_kpi.order': 'الترتيب',
    'admin.config_kpi.width': 'العرض',
    'admin.config_kpi.span.quarter': '1/4 صف',
    'admin.config_kpi.span.third': '1/3 صف',
    'admin.config_kpi.span.half': '1/2 صف',
    'admin.config_kpi.span.two_thirds': '2/3 صف',
    'admin.config_kpi.span.full': 'صف كامل',
    'admin.config_kpi.widget.today_prereg': 'التسجيلات المسبقة اليوم',
    'admin.config_kpi.widget.today_schedule': 'جدول اليوم',
    'admin.config_kpi.widget.low_attendance': 'تنبيه حضور منخفض',
    'admin.config_kpi.widget.outstanding_debt': 'الديون المتبقية',
    'menu.planning.admin.securityWeb': 'أمان الويب',
    'menu.planning.admin.societe': 'الشركة',
    'menu.planning.admin.societe.agence': 'الوكالة',
    'menu.planning.admin.accessRights': 'حقوق الوصول',
    'menu.planning.admin.accessRights.profiles': 'الملفات الشخصية',
    'menu.planning.admin.accessRights.users': 'المستخدمون',
    'menu.planning.admin.accessRights.sysParams': 'معلمات النظام',
    'menu.planning.admin.accessRights.securityWeb': 'أمان الويب',

    // Page Security Web
    'page.security.title': 'أمان الويب (الملفات الشخصية والإجراءات)',
    'page.security.profile.label': 'الملف الشخصي',
    'page.security.table.selectProfile': 'اختر ملفاً شخصياً',
    'page.security.table.objectAction': 'كائن/إجراء',
    'page.security.table.type': 'النوع',
    'page.security.table.load': 'تحميل',
    'page.security.table.select': 'تحديد',
    'page.security.table.update': 'تحديث',
    'page.security.table.insert': 'إدراج',
    'page.security.table.delete': 'حذف',
    'page.security.table.execute': 'تنفيذ',
    'page.security.table.empty': 'لم يتم العثور على كائنات',
    'page.security.action.refresh': 'تحديث',
    'page.security.action.updateAll': 'تحديث شامل',
    'page.security.dialog.bulkTitle': 'تحديث شامل للحقوق',
    'page.security.dialog.apply': 'تطبيق على الكل',

    'common.profile': 'الملف الشخصي',
    'common.back_to_list': 'العودة إلى القائمة',
    'common.select': 'اختر...',
    'common.status': 'الحالة',
    'common.phone': 'الهاتف',
    'common.email': 'البريد الإلكتروني',
    'common.loading': 'جاري التحميل...',
    'common.loadingServer': 'جاري تحميل البيانات من الخادم',
    'common.loadingServerHint': 'يرجى الانتظار لحظة…',
    'common.saving': 'جاري الحفظ...',
    'common.save': 'حفظ',
    'common.success.saved': 'تم حفظ التغييرات بنجاح',
    'common.error.saving': 'خطأ في حفظ الحقوق',
    'common.error.loading': 'خطأ في تحميل البيانات',
    'menu.planning.admin.parameters': 'الإعدادات',
    'menu.planning.admin.userDossiers': 'تعيين الملفات',

    // Menu Maker
    'menu.maker.projects': 'المشاريع',
    'menu.maker.estimates': 'التقديرات',
    'menu.maker.design': 'التصميم',
    'menu.maker.production': 'الإنتاج',
    'menu.maker.materials': 'المواد',
    'menu.maker.customers': 'العملاء',
    'menu.maker.reports': 'التقارير',
    'menu.maker.libraries': 'المكتبات',
    'menu.maker.components': 'المكونات',
    'menu.maker.templates': 'القوالب',

    // Menu Parc
    'menu.parc.dashboard': 'لوحة القيادة',
    'menu.parc.vehicles': 'المركبات',
    'menu.parc.drivers': 'السائقين',
    'menu.parc.maintenance': 'الصيانة',
    'menu.parc.repairs': 'الإصلاحات',
    'menu.parc.trips': 'الرحلات',
    'menu.parc.documents': 'المستندات',
    'menu.parc.notifications': 'الإشعارات',
    'menu.parc.settings': 'الإعدادات',
    'menu.parc.gps': 'تتبع ونظام تحديد المواقع',
    'menu.parc.gps.tracking': 'تتبع المسار',
    'menu.parc.gps.alerts': 'تنبيهات GPS',
    'menu.parc.fuel': 'الوقود',
    'menu.parc.fuelcards': 'بطاقات الوقود',
    'menu.parc.mileage': 'عداد المسافات',
    'menu.parc.analyses': 'التحليلات والربحية',
    'menu.parc.analysis.general': 'التحليل العام',
    'menu.parc.analysis.tco': 'تحليل التكلفة الإجمالية TCO',
    'menu.parc.alerts': 'رسائل التنبيه',
    'menu.parc.lookups': 'الجداول المرجعية',

    // Menu Academy
    'academy.menu.dashboard': 'لوحة التحكم',
    'academy.menu.rooms': 'القاعات والمعدات',
    'academy.menu.room_rentals': 'تأجير القاعات',
    'academy.menu.teachers': 'الأساتذة',
    'academy.menu.students': 'الطلاب والتسجيلات',
    'academy.menu.course_templates': 'قوالب الدورات',
    'academy.menu.groups': 'المجموعات والدفعات',
    'academy.menu.scheduler': 'الجدول الأسبوعي',
    'academy.menu.planning_theorique_crud': 'إدارة التخطيط النظري',
    'academy.menu.planning_theorique_matrix': 'التخطيط النظري',
    'academy.menu.planning_generation': 'توليد الحصص',
    'academy.menu.planning_deletion': 'حذف الحصص',
    'academy.menu.attendance': 'الحضور والحصص',
    'academy.menu.financial': 'المالية والمدفوعات',
    'academy.menu.reports': 'التقارير والتحليلات',
    'academy.menu.quizzes': 'الاختبارات والتقييمات',
    'academy.menu.quizzes.list': 'قائمة الاختبارات والتقييمات',
    'academy.menu.quizzes.quiz_questions': 'أسئلة الاختبار',
    'academy.menu.quizzes.quiz_results': 'نتائج الاختبار',
    'academy.menu.quiz_questions': 'أسئلة الاختبار',
    'academy.menu.quiz_results': 'نتائج الاختبار',
    'academy.menu.planning_controls': 'ضوابط التخطيط',
    'academy.menu.discounts': 'سياسة التخفيضات',
    'academy.menu.discount_validations': 'التحقق من التخفيضات',
    'academy.menu.parameters': 'إعدادات الأكاديمية',
    'academy.menu.data_exchange': 'البيانات والتبادلات',
    'academy.menu.import_data': 'استيراد البيانات',
    'academy.menu.export_data': 'تصدير البيانات',

    // Import & Export Data - AR
    'academy.import.page_title': 'استيراد البيانات',
    'academy.import.refresh': 'تحديث',
    'academy.import.step1_title': '1. اختر الكيان المراد استيراده',
    'academy.import.in_database': 'في قاعدة البيانات',
    'academy.import.step2_title': '2. تحميل النموذج الفارغ',
    'academy.import.step2_desc': 'استخدم نموذجنا الجاهز للاستخدام الذي يحتوي على ترويسات مهيأة مسبقاً وسطر توضيحي لـ {0}.',
    'academy.import.btn_excel': 'نموذج إكسل (.xlsx)',
    'academy.import.btn_csv': 'نموذج CSV (.csv)',
    'academy.import.view_rules': 'عرض الأعمدة والقواعد ({0} حقول)',
    'academy.import.step3_title': '3. حدد الملف المراد استيراده',
    'academy.import.drop_title': 'اسحب وأفلت ملفك هنا، أو انقر للتصفح',
    'academy.import.file_selected': 'الملف المحدد: {0} ({1} ك.ب)',
    'academy.import.accepted_formats': 'الصيغ المقبولة: مايكروسوفت إكسل (.xlsx, .xls) أو CSV (.csv UTF-8)',
    'academy.import.update_existing': 'تحديث إذا كان الرمز موجوداً مسبقاً',
    'academy.import.btn_import': 'استيراد {0} سطر',
    'academy.import.importing': 'جاري الاستيراد...',
    'academy.import.preview_title': 'معاينة وتحقق ({0} أسطر)',
    'academy.import.preview_desc': 'تحقق من مطابقة البيانات قبل بدء حفظ البيانات.',
    'academy.import.filter_all': 'الكل ({0})',
    'academy.import.filter_valid': 'صحيحة ({0})',
    'academy.import.filter_errors': 'أخطاء ({0})',
    'academy.import.status_valid': 'صحيح',
    'academy.import.status_incomplete': 'غير مكتمل',
    'academy.import.col_index': '#',
    'academy.import.col_status': 'الحالة',
    'academy.import.preview_limit': 'العرض مقتصر على أول 100 سطر من أصل {0}. ستعالج عملية الاستيراد الملف بالكامل.',
    'academy.import.dialog_rules_title': 'هيكل ملف الاستيراد: {0}',
    'academy.import.dialog_rules_subtitle': 'الكيان المستهدف: {0}. المعرف المرجعي: {1}.',
    'academy.import.col_file_header': 'ترويسة الملف',
    'academy.import.col_data_type': 'نوع البيانات',
    'academy.import.col_required': 'إلزامي',
    'academy.import.col_sample': 'مثال',
    'academy.import.col_description': 'الوصف',
    'academy.import.required_yes': 'نعم',
    'academy.import.required_optional': 'اختياري',
    'academy.import.dialog_close': 'إغلاق',
    'academy.import.report_title': 'تقرير الاستيراد',
    'academy.import.report_processed': 'المعالج',
    'academy.import.report_created': 'المُنشأ',
    'academy.import.report_updated': 'المُحدَّث',
    'academy.import.report_errors': 'الأخطاء',
    'academy.import.report_errors_detail': 'تفاصيل الرفض:',
    'academy.import.report_row_error': 'السطر {0} ({1}): {2}',
    'academy.import.report_finish': 'إنهاء',
    'academy.import.msg_no_rows': 'الملف لا يحتوي على أية أسطر بيانات قابلة للاستخدام.',
    'academy.import.msg_rows_detected': 'تم اكتشاف وتحليل {0} سطر.',
    'academy.import.msg_success': 'تم الاستيراد بنجاح: {0} إنشاء، {1} تحديث.',
    'academy.import.msg_warning': 'اكتمل الاستيراد مع {0} تنبيه(ات)/خطأ(أخطاء).',
    'academy.import.required_missing': 'الحقل الإلزامي "{0}" مفقود.',

    'academy.export.page_title': 'تصدير البيانات',
    'academy.export.header_title': 'تصدير بيانات الوكالة',
    'academy.export.header_subtitle': 'قم بتنزيل كافة بيانات وكالتك المتصلة بصيغ مايكروسوفت إكسل (.xlsx) أو CSV القياسية.',
    'academy.export.refresh': 'تحديث',
    'academy.export.lines_count': '{0} سطر',
    'academy.export.columns_count': '{0} عمود مدرج',
    'academy.export.view_columns': 'عرض الأعمدة',
    'academy.export.generating': 'جاري إنشاء التصدير...',
    'academy.export.btn_excel': 'إكسل (.xlsx)',
    'academy.export.btn_csv': 'CSV (.csv)',
    'academy.export.dialog_title': 'الأعمدة المصدرة: {0}',
    'academy.export.col_header': 'العمود في التصدير',
    'academy.export.col_type': 'النوع',
    'academy.export.dialog_close': 'إغلاق',
    'academy.export.msg_no_data': 'لا توجد بيانات لتصديرها لـ "{0}" في هذه الوكالة.',
    'academy.export.msg_success': 'تم تصدير {0} سطر بنجاح ({1}).',

    // كيانات الأكاديمية - العناوين والأوصاف - AR
    'academy.entity.rooms.title': 'القاعات والمعدات',
    'academy.entity.rooms.desc': 'إدارة قاعات التدريس والمختبرات والمعدات التعليمية',
    'academy.entity.teachers.title': 'الأساتذة والمدربون',
    'academy.entity.teachers.desc': 'الهيئة التدريسية والمدربون الخارجيون والمتخصصون التربويون',
    'academy.entity.students.title': 'الطلاب والمتدربون',
    'academy.entity.students.desc': 'ملفات الطلاب المسجلين وبيانات الاتصال وحالات الطوارئ',
    'academy.entity.groups.title': 'المجموعات والدفعات',
    'academy.entity.groups.desc': 'أفواج الفصول الدراسية والمجموعات والدفعات الطلابية',
    'academy.entity.courses.title': 'بطاقات المواد والدورات',
    'academy.entity.courses.desc': 'دليل المواد والبرامج التدريبية والحجوم الساعية والأسعار',

    // ترويسات الأعمدة (الحقول) - AR
    'academy.field.ARM_ROOM_NUMBER.label': 'رقم / اسم القاعة',
    'academy.field.ARM_ROOM_NUMBER.desc': 'المعرف أو الرقم الفريد للقاعة',
    'academy.field.ARM_FLOOR.label': 'الطابق / المبنى',
    'academy.field.ARM_FLOOR.desc': 'الموقع داخل المؤسسة',
    'academy.field.ARM_CAPACITY.label': 'السعة الاستيعابية',
    'academy.field.ARM_CAPACITY.desc': 'أقصى عدد للأماكن والجلوس',
    'academy.field.ARM_EQUIPMENT.label': 'التجهيزات والمعدات',
    'academy.field.ARM_EQUIPMENT.desc': 'المعدات والوسائل المتاحة',
    'academy.field.ARM_STATUS.label': 'الحالة',
    'academy.field.ARM_STATUS.desc': 'نشطة، في الصيانة أو مؤرشفة',

    'academy.field.TCH_FULL_NAME.label': 'الاسم واللقب',
    'academy.field.TCH_FULL_NAME.desc': 'الاسم الكامل للأستاذ',
    'academy.field.TCH_EMAIL.label': 'البريد الإلكتروني',
    'academy.field.TCH_EMAIL.desc': 'البريد الإلكتروني المهني الفريد',
    'academy.field.TCH_PHONE.label': 'رقم الهاتف',
    'academy.field.TCH_PHONE.desc': 'رقم هاتف الاتصال',
    'academy.field.TCH_SPECIALIZATION.label': 'التخصص',
    'academy.field.TCH_SPECIALIZATION.desc': 'المادة أو مجال الاختصاص',
    'academy.field.TCH_HOURLY_RATE.label': 'السعر بالساعة (د.ج)',
    'academy.field.TCH_HOURLY_RATE.desc': 'الأجر الأساسي بالساعة',
    'academy.field.TCH_BIO.label': 'السيرة الذاتية / ملاحظات',
    'academy.field.TCH_BIO.desc': 'الملف التعريفي والخبرة',

    'academy.field.STD_FIRST_NAME.label': 'الاسم الشخصي',
    'academy.field.STD_FIRST_NAME.desc': 'الاسم الشخصي للطالب',
    'academy.field.STD_LAST_NAME.label': 'اللقب العائلي',
    'academy.field.STD_LAST_NAME.desc': 'اللقب العائلي للطالب',
    'academy.field.STD_EMAIL.label': 'البريد الإلكتروني',
    'academy.field.STD_EMAIL.desc': 'البريد الإلكتروني الشخصي للطالب (معرف فريد)',
    'academy.field.STD_PHONE.label': 'رقم الهاتف',
    'academy.field.STD_PHONE.desc': 'الهاتف المحمول',
    'academy.field.STD_DATE_OF_BIRTH.label': 'تاريخ الميلاد (س س س س-ش ش-ي ي)',
    'academy.field.STD_DATE_OF_BIRTH.desc': 'الصيغة YYYY-MM-DD',
    'academy.field.STD_EMERGENCY_CONTACT.label': 'جهة الاتصال في حالات الطوارئ',
    'academy.field.STD_EMERGENCY_CONTACT.desc': 'الشخص المطلوب الاتصال به عند الطوارئ',

    'academy.field.STD_CATEGORY.label': 'الفئة (كبار / صغار)',
    'academy.field.STD_CATEGORY.desc': 'فئة الطالب: كبار أو صغار',
    'academy.field.STD_GENDER.label': 'الجنس (ذكر / أنثى)',
    'academy.field.STD_GENDER.desc': 'جنس الطالب: ذكر أو أنثى',
    'academy.field.STD_ADDRESS.label': 'العنوان السكني',
    'academy.field.STD_ADDRESS.desc': 'العنوان السكني الكامل',
    'academy.field.STD_REGISTRATION_STATUS.label': 'حالة التسجيل',
    'academy.field.STD_REGISTRATION_STATUS.desc': 'نشط، مسجل مسبقاً، أو مرفوض',
    'academy.field.STD_FINANCIAL_HOLD.label': 'حظر مالي (1/0)',
    'academy.field.STD_FINANCIAL_HOLD.desc': '1 = محظور بسبب الديون، 0 = في وضعية نظامية',
    'academy.field.STD_PROFESSION.label': 'المهنة (للكبار)',
    'academy.field.STD_PROFESSION.desc': 'المهنة أو الوظيفة',
    'academy.field.STD_SCHOOL_LEVEL.label': 'المستوى الدراسي (للصغار)',
    'academy.field.STD_SCHOOL_LEVEL.desc': 'تحضيري، ابتدائي، متوسط، ثانوي، جامعي، أخرى',
    'academy.field.STD_BLOOD_GROUP.label': 'فصيلة الدم',
    'academy.field.STD_BLOOD_GROUP.desc': 'A+, A-, B+, B-, AB+, AB-, O+, O-',
    'academy.field.STD_HAS_HEALTH_ISSUES.label': 'مشاكل صحية (1/0)',
    'academy.field.STD_HAS_HEALTH_ISSUES.desc': '1 = نعم، 0 = لا',
    'academy.field.STD_HEALTH_CONDITIONS.label': 'الحالات المرضية',
    'academy.field.STD_HEALTH_CONDITIONS.desc': 'قائمة الحالات المرضية المعروفة',
    'academy.field.STD_HEALTH_OTHER_DETAIL.label': 'تفاصيل صحية أخرى',
    'academy.field.STD_HEALTH_OTHER_DETAIL.desc': 'ملاحظات صحية إضافية',
    'academy.field.STD_HAS_ALLERGIES.label': 'حساسية (1/0)',
    'academy.field.STD_HAS_ALLERGIES.desc': '1 = نعم، 0 = لا',
    'academy.field.STD_ALLERGY_DETAILS.label': 'تفاصيل الحساسية',
    'academy.field.STD_ALLERGY_DETAILS.desc': 'المواد المسببة للحساسية',
    'academy.field.STD_IMAGE_RIGHT.label': 'حقوق الصورة (1/0)',
    'academy.field.STD_IMAGE_RIGHT.desc': '1 = مسموح، 0 = مرفوض',
    'academy.field.STD_GUARDIAN_FIRST_NAME.label': 'اسم الولي الأول',
    'academy.field.STD_GUARDIAN_FIRST_NAME.desc': 'الاسم الأول للأب أو الولي الشرعي',
    'academy.field.STD_GUARDIAN_LAST_NAME.label': 'لقب الولي',
    'academy.field.STD_GUARDIAN_LAST_NAME.desc': 'اللقب العائلي للأب أو الولي',
    'academy.field.STD_GUARDIAN_RELATION.label': 'صلة القرابة',
    'academy.field.STD_GUARDIAN_RELATION.desc': 'أب، أم، وصي شرعي، أخرى',
    'academy.field.STD_GUARDIAN_PHONE.label': 'هاتف الولي',
    'academy.field.STD_GUARDIAN_PHONE.desc': 'رقم هاتف الولي المباشر',
    'academy.field.STD_GUARDIAN_EMAIL.label': 'بريد الولي الإلكتروني',
    'academy.field.STD_GUARDIAN_EMAIL.desc': 'عنوان البريد الإلكتروني للولي',
    'academy.field.STD_AUTHORIZED_PICKUP.label': 'الأشخاص المصرح لهم بالاستلام',
    'academy.field.STD_AUTHORIZED_PICKUP.desc': 'الأشخاص المصرح لهم باستلام التلميذ',
    'academy.field.STD_TIME_PREFERENCE.label': 'الفترة المفضلة',
    'academy.field.STD_TIME_PREFERENCE.desc': 'صباح، بعد الظهر، مساء، نهاية الأسبوع',

    'academy.field.GRP_NAME.label': 'اسم الفوج / الدفعة',
    'academy.field.GRP_NAME.desc': 'المعرف أو اسم الفوج',
    'academy.field.GRP_MAX_CAPACITY.label': 'السعة القصوى',
    'academy.field.GRP_MAX_CAPACITY.desc': 'أقصى عدد للطلاب في الفوج',
    'academy.field.GRP_STATUS.label': 'الحالة',
    'academy.field.GRP_STATUS.desc': 'قادم، جاري أو مكتمل',

    'academy.field.CRS_TITLE.label': 'عنوان المادة / الدورة',
    'academy.field.CRS_TITLE.desc': 'عنوان البرنامج التدريبي',
    'academy.field.CRS_TOTAL_HOURS.label': 'الحجم الساعي (ساعات)',
    'academy.field.CRS_TOTAL_HOURS.desc': 'المدة الإجمالية للتدريب بالساعات',
    'academy.field.CRS_PRICE.label': 'السعر الإرشادي (د.ج)',
    'academy.field.CRS_PRICE.desc': 'السعر المرجعي للدورة',
    'academy.field.CRS_DESCRIPTION.label': 'وصف البرنامج التدريبي',
    'academy.field.CRS_DESCRIPTION.desc': 'الأهداف والمهارات المكتسبة',

    // أنواع البيانات - AR
    'academy.dataType.string': 'نص',
    'academy.dataType.int': 'عدد صحيح',
    'academy.dataType.decimal': 'عدد عشري',
    'academy.dataType.date': 'تاريخ',
    'academy.dataType.boolean': 'منطقي',

    // سياسة التخفيضات التجارية (Commercial Discounts) - AR
    'academy.discounts.title': 'سياسة التخفيضات التجارية',
    'academy.discounts.subtitle': 'إدارة فئات الخصم، تعيين الطلاب وقواعد التحقق.',
    'academy.discounts.add_new': 'خصم جديد',
    'academy.discounts.back_to_list': 'العودة للقائمة',
    'academy.discounts.create_title': 'تخفيض تجاري جديد',
    'academy.discounts.edit_title': 'تعديل التخفيض: {0}',
    'academy.discounts.title_new': 'خصم تجاري جديد',
    'academy.discounts.title_edit': 'تعديل الخصم ({code})',
    'academy.discounts.total_count': 'إجمالي الخصومات المعرفة',
    'academy.discounts.auto_count': 'خصومات مطبقة تلقائياً',
    'academy.discounts.validation_count': 'تتطلب موافقة الإدارة / المدير',
    'academy.discounts.empty_title': 'لا توجد تخفيضات مهيأة',
    'academy.discounts.empty_subtitle': 'قم بإنشاء تخفيض جديد لتشجيع التسجيلات.',
    'academy.discounts.search_placeholder': 'البحث بالرمز، الاسم، الوصف...',
    'academy.discounts.code': 'رمز التخفيض',
    'academy.discounts.name': 'اسم التخفيض الكامل',
    'academy.discounts.percentage': 'النسبة المئوية (%)',
    'academy.discounts.min_courses': 'الحد الأدنى لعدد الدورات',
    'academy.discounts.application': 'طريقة التطبيق',
    'academy.discounts.validation': 'الموافقة المطلوبة',
    'academy.discounts.validation_role': 'الدور المطلوب للتحقق',
    'academy.discounts.role_auto': 'تلقائي (تحقق تلقائي)',
    'academy.discounts.role_planner': 'مخطط',
    'academy.discounts.role_admin': 'مسؤول',
    'academy.discounts.requires_val_check': 'يتطلب تحققاً رسمياً قبل التطبيق',
    'academy.discounts.is_auto_applied_check': 'تطبيق تلقائي إذا تحققت الشروط',
    'academy.discounts.auto_apply_check': 'تطبيق تلقائي عند الاستحقاق',
    'academy.discounts.is_active': 'تخفيض نشط',
    'academy.discounts.description': 'الوصف والشروط',
    'academy.discounts.desc_label': 'الوصف والمعايير',
    'academy.discounts.assigned_students': 'الطلاب المسندين',
    'academy.discounts.used_invoices': 'الفواتير المستخدمة',
    'academy.discounts.linked_invoices': 'الفواتير المرتبطة',
    'academy.discounts.auto_label': 'تلقائي',
    'academy.discounts.manual_label': 'يدوي / معين',
    'academy.discounts.no_validation': 'لا يوجد',
    'academy.discounts.btn_assign_students': 'إسناد للطلاب',
    'academy.discounts.assign_dialog_title': 'إسناد التخفيض للطلاب',
    'academy.discounts.assign_dialog_desc': 'حدد الطلاب المستفيدين من التخفيض "{0}" (-{1}%).',
    'academy.discounts.validation.code_required': 'رمز التخفيض مطلوب.',
    'academy.discounts.validation.name_required': 'اسم التخفيض مطلوب.',
    'academy.discounts.assign_success': 'تم إسناد التخفيض "{0}" بنجاح إلى {1} طالب.',
    'academy.discounts.assign_error': 'فشل إسناد الطلاب.',
    'academy.discounts.confirm_delete': 'هل أنت متأكد من حذف التخفيض "{0}"؟',
    'academy.discounts.msg_saved': 'تم حفظ الخصم بنجاح.',
    'academy.discounts.msg_deleted': 'تم حذف الخصم {code} بنجاح.',
    'academy.discounts.deleted_success': 'تم حذف التخفيض بنجاح.',
    'academy.discounts.msg_blocked_delete': 'إجراء محظور: الخصم {code} ({name}) معين لطلاب أو مستخدم في فواتير.',
    'academy.discounts.assign_search_placeholder': 'البحث عن طالب (الاسم، البريد الإلكتروني، الهاتف)...',
    'academy.discounts.assign_no_results': 'لا يوجد طلاب يطابقون بحثك.',
    'academy.discounts.assign_assigned_count': '{0} / {1} معين(ين)',
    'academy.discounts.assign_select_all': 'تحديد الكل',
    'academy.discounts.assign_deselect_all': 'إلغاء تحديد الكل',

    // التحقق من التخفيضات (Discount Validations) - AR
    'academy.discount_validations.title': 'التحقق من تخفيضات الفواتير',
    'academy.discount_validations.access_denied_title': 'الدخول مخصص للمسؤولين فقط',
    'academy.discount_validations.access_denied_desc': 'هذه الصفحة مخصصة فقط للإدارة للتحقق من طلبات التخفيضات التجارية المقدمة على الفواتير.',
    'academy.discount_validations.kpi_pending': 'قيد الانتظار',
    'academy.discount_validations.kpi_approved': 'تمت الموافقة',
    'academy.discount_validations.kpi_rejected': 'مرفوضة',
    'academy.discount_validations.kpi_total_pending': 'إجمالي التخفيضات قيد الانتظار',
    'academy.discount_validations.tab_pending': 'قيد الانتظار ({0})',
    'academy.discount_validations.tab_approved': 'تمت الموافقة ({0})',
    'academy.discount_validations.tab_rejected': 'مرفوضة ({0})',
    'academy.discount_validations.tab_all': 'الكل ({0})',
    'academy.discount_validations.search_placeholder': 'البحث بالمتعلم، الفاتورة، رمز التخفيض، السبب...',
    'academy.discount_validations.empty_title': 'لم يتم العثور على طلبات تخفيض',
    'academy.discount_validations.empty_pending_desc': 'تمت معالجة جميع طلبات التخفيض.',
    'academy.discount_validations.empty_filter_desc': 'لا توجد عناصر لهذا الفلتر.',
    'academy.discount_validations.col_date_requester': 'التاريخ ومقدم الطلب',
    'academy.discount_validations.col_invoice_student': 'الفاتورة والمتعلم',
    'academy.discount_validations.col_requested_discount': 'التخفيض المطلوب',
    'academy.discount_validations.col_financial_impact': 'الأثر المالي',
    'academy.discount_validations.col_justification': 'السبب / المبرر',
    'academy.discount_validations.col_status': 'الحالة',
    'academy.discount_validations.col_actions': 'إجراءات القرار',
    'academy.discount_validations.by_requester': 'بواسطة: {0}',
    'academy.discount_validations.gross_label': 'الإجمالي: ',
    'academy.discount_validations.net_label': 'الصافي: ',
    'academy.discount_validations.savings_label': 'التوفير: -',
    'academy.discount_validations.no_reason': 'لم يتم تحديد سبب',
    'academy.discount_validations.rejection_reason_label': 'سبب الرفض: ',
    'academy.discount_validations.status_pending': 'قيد الانتظار',
    'academy.discount_validations.status_approved': 'تمت الموافقة',
    'academy.discount_validations.status_rejected': 'مرفوضة',
    'academy.discount_validations.status_processed': 'تمت المعالجة',
    'academy.discount_validations.btn_approve': 'موافقة',
    'academy.discount_validations.btn_reject': 'رفض',
    'academy.discount_validations.dialog_approve_title': 'تأكيد الموافقة',
    'academy.discount_validations.dialog_approve_alert': 'بالموافقة على هذا الطلب، سيتم تحديث المبلغ الصافي للفاتورة {0} من {1} إلى {2} (-{3}%).',
    'academy.discount_validations.dialog_approve_confirm': 'تأكيد الموافقة',
    'academy.discount_validations.dialog_reject_title': 'رفض التخفيض',
    'academy.discount_validations.dialog_reject_alert': 'سيتم رفض التخفيض وستحتفظ الفاتورة {0} بمبلغها الأصلي ({1}).',
    'academy.discount_validations.dialog_reject_field_label': 'سبب الرفض',
    'academy.discount_validations.dialog_reject_placeholder': 'حدد سبب الرفض (مثال: مستند مفقود، لا تنطبق الشروط)...',
    'academy.discount_validations.dialog_reject_required': 'سبب الرفض إجباري.',
    'academy.discount_validations.dialog_reject_confirm': 'تأكيد الرفض',
    'academy.discount_validations.approve_success': 'تمت الموافقة على تخفيض -{0}% على الفاتورة {1}.',
    'academy.discount_validations.reject_info_title': 'تم رفض الطلب',
    'academy.discount_validations.reject_info_msg': 'تم رفض طلب التخفيض على الفاتورة {0}.',
    'academy.discount_validations.approve_error': 'فشل اعتماد التخفيض.',
    'academy.discount_validations.reject_error': 'فشل رفض التخفيض.',

    // إدارة تخفيض الفواتير (Financial Invoice Discounts) - AR
    'academy.financial.discount.dialog_title': 'إدارة تخفيض الفاتورة',
    'academy.financial.discount.student_label': 'المتعلم',
    'academy.financial.discount.initial_gross': 'المبلغ الإجمالي الأولي',
    'academy.financial.discount.already_paid': 'المدفوع مسبقاً',
    'academy.financial.discount.policy_label': 'سياسة التخفيضات التجارية',
    'academy.financial.discount.policy_helper': 'اختر تخفيضاً من الدليل أو أدخل نسبة مخصصة',
    'academy.financial.discount.none_option': 'بدون تخفيض (0% / حذف التخفيض)',
    'academy.financial.discount.custom_option': 'نسبة مئوية مخصصة...',
    'academy.financial.discount.custom_percent_label': 'نسبة التخفيض (%)',
    'academy.financial.discount.simulation_title': 'المحاكاة المالية',
    'academy.financial.discount.gross_label': 'المبلغ الإجمالي:',
    'academy.financial.discount.deduction_label': 'خصم التخفيض (-{0}%):',
    'academy.financial.discount.new_net_invoiced': 'الصافي المفوتر الجديد:',
    'academy.financial.discount.new_due_rest': 'الرصيد المتبقي الجديد:',
    'academy.financial.discount.justification_label': 'سبب أو مبرر التخفيض',
    'academy.financial.discount.justification_placeholder': 'حدد سبب هذا التخفيض (مثال: موافقة الإدارة، رعاية، إخوة، تسوية تجارية)...',
    'academy.financial.discount.justification_required': 'سبب أو مبرر التخفيض إجباري.',
    'academy.financial.discount.direct_admin_notice': 'بصفتك مسؤولاً، سيتم تطبيق هذا التخفيض مباشرة على الفاتورة.',
    'academy.financial.discount.direct_planner_notice': 'سيتم تطبيق هذا التخفيض (مخطط / تلقائي) فوراً على الفاتورة.',
    'academy.financial.discount.pending_admin_notice': 'يتطلب هذا التخفيض موافقة الإدارة (مسؤول). سيتم وضع طلبك قيد الانتظار وإرساله للمسؤولين.',
    'academy.financial.discount.btn_apply_direct': 'تطبيق فوري',
    'academy.financial.discount.btn_submit_pending': 'إرسال للموافقة',
    'academy.financial.discount.manage_tooltip': 'إدارة التخفيض على هذه الفاتورة',
    'academy.financial.discount.pending_tooltip': 'طلب قيد انتظار الموافقة (-{0}%) : {1}',
    'academy.financial.discount.pending_badge': 'قيد الانتظار (-{0}%)',
    'academy.financial.discount.validation_auto': 'تحقق تلقائي',
    'academy.financial.discount.validation_admin': 'تحقق المسؤول',
    'academy.financial.discount.success_applied': 'تم تطبيق التخفيض بنجاح على الفاتورة.',
    'academy.financial.discount.success_submitted': 'تم إرسال طلب التخفيض بنجاح للموافقة.',
    'academy.financial.no_discount': 'بدون تخفيض',

    // Locations de salle (Room Rentals) - AR
    'academy.room_rentals.title': 'تأجير القاعات',
    'academy.room_rentals.new_rental': 'تأجير جديد',
    'academy.room_rentals.create_title': 'حجز تأجير قاعة جديد',
    'academy.room_rentals.edit_title': 'تعديل التأجير: {name}',
    'academy.room_rentals.empty_title': 'لا توجد عمليات تأجير مسجلة',
    'academy.room_rentals.empty_subtitle': 'قم بإنشاء تأجير جديد لحجز قاعة.',
    'academy.room_rentals.search_placeholder': 'البحث عن تأجير، عميل، قاعة...',
    'academy.room_rentals.confirm_delete': 'هل أنت متأكد من حذف التأجير "{name}"؟',
    'academy.room_rentals.deleted_success': 'تم حذف التأجير بنجاح.',
    'academy.room_rentals.room': 'القاعة',
    'academy.room_rentals.client': 'العميل / المؤسسة',
    'academy.room_rentals.client_name': 'اسم العميل / المؤسسة',
    'academy.room_rentals.lib': 'الغرض من التأجير',
    'academy.room_rentals.dates': 'فترة التأجير',
    'academy.room_rentals.start_date': 'تاريخ ووقت البدء',
    'academy.room_rentals.end_date': 'تاريخ ووقت الانتهاء',
    'academy.room_rentals.code': 'الرمز المرجعي',
    'academy.room_rentals.price': 'السعر الإجمالي (دج)',
    'academy.room_rentals.deposit': 'العربون المدفوع (دج)',
    'academy.room_rentals.notes': 'ملاحظات والمعدات المطلوبة',
    'academy.room_rentals.notes_title': 'الملاحظات والتجهيزات المطلوبة',
    'academy.room_rentals.section_room': 'القاعة والتوقيت',
    'academy.room_rentals.section_client': 'العميل والتسعير',
    'academy.room_rentals.room_helper': 'اختر القاعة المتاحة لهذا التأجير',
    'academy.room_rentals.validation.room_required': 'يرجى اختيار قاعة.',
    'academy.room_rentals.validation.client_required': 'اسم العميل / المؤسسة إجباري.',
    'academy.room_rentals.validation.start_date_required': 'تاريخ البدء إجباري.',
    'academy.room_rentals.validation.end_date_required': 'تاريخ الانتهاء إجباري.',
    'academy.room_rentals.validation.date_order': 'يجب أن يكون تاريخ الانتهاء بعد تاريخ البدء.',

    // boubaker 25/08/2026 - مجموعات القائمة المعاد هيكلتها
    'academy.menu.resources': 'الموارد',
    'academy.menu.students_groups': 'الطلاب والمجموعات',
    'academy.menu.planning': 'التخطيط',
    'academy.menu.pedagogy': 'التعليم',
    'academy.menu.finance': 'المالية',
    'academy.menu.monitoring': 'المتابعة والمؤشرات',
    'academy.menu.system': 'النظام والسجلات',

    // boubaker 23/08/2026 - قائمة الطباعة أكاديمية
    'academy.menu.prints': 'الطباعات',
    'academy.menu.prints.planning': 'التخطيط',
    'academy.menu.prints.invoices': 'الفواتير',
    'academy.menu.prints.receipts': 'إيصالات الدفع',
    'academy.menu.prints.daily_cash': 'تقرير الصندوق اليومي',
    'academy.menu.prints.hub': 'مركز الإصدارات',
    'academy.menu.prints.group_documents': 'الوثائق والشهادات',
    'academy.menu.prints.group_lists': 'القوائم والحضور',
    'academy.menu.prints.group_correspondence': 'المراسلات والصحة',
    'academy.menu.prints.group_finance': 'المالية والتخطيط',
    'academy.menu.prints.attendance_sheet': 'ورقة الحضور',
    'academy.menu.prints.school_certificate': 'شهادة مدرسية',
    'academy.menu.prints.registration_form': 'بطاقة التسجيل',
    'academy.menu.prints.students_list': 'قائمة التلاميذ',
    'academy.menu.prints.information_sheet': 'استمارة معلومات',
    'academy.menu.prints.student_cards': 'شارة التلميذ',
    'academy.menu.prints.student_badge': 'شارة التلميذ',
    'academy.menu.prints.cancellation_certificate': 'شهادة الشطب',
    'academy.menu.prints.conduct_certificate': 'شهادة حسن السيرة والسلوك',
    'academy.menu.prints.parent_convocation': 'استدعاء ولي التلميذ',
    'academy.menu.prints.emergency_form': 'بطاقة الطوارئ والترخيص بالعلاج',

    // Keys added for Academy synchronization
    'academy.financial.table.total_amount': 'المبلغ الإجمالي',
    'academy.groups.add_course_template': 'إضافة نموذج مادة',
    'academy.groups.blocked_delete_has_sessions': 'لا يمكن حذف الفوج لوجود حصص مرتبطة به',
    'academy.groups.confirm_delete_with_sessions': 'تأكيد حذف الفوج والحصص التابعة له',
    'academy.groups.course_desc': 'وصف المادة',
    'academy.groups.course_hours': 'عدد الساعات',
    'academy.groups.course_preview': 'معاينة المادة',
    'academy.groups.course_price': 'سعر المادة',
    'academy.groups.course_title': 'عنوان المادة',
    'academy.groups.create_btn': 'إنشاء',
    'academy.groups.create_course_title': 'إنشاء مادة',
    'academy.groups.error.capacity': 'خطأ في السعة',
    'academy.groups.kanban.completed': 'مكتمل',
    'academy.groups.kanban.ongoing': 'قيد التقدم',
    'academy.groups.kanban.upcoming': 'قادم',
    'academy.groups.save_btn': 'حفظ',
    'academy.groups.save_course': 'حفظ المادة',
    'academy.groups.save_success': 'تم الحفظ بنجاح',
    'academy.parameters.datefmt_none': 'بدون',
    'academy.parameters.datefmt_yymm': 'س س ش ش',
    'academy.parameters.datefmt_yyyy': 'س س س س',
    'academy.parameters.datefmt_yyyy_mm': 'س س س س-ش ش',
    'academy.parameters.datefmt_yyyy_slash_mm': 'س س س س/ش ش',
    'academy.parameters.datefmt_yyyymmdd': 'س س س س ش ش ي ي',
    'academy.parameters.groupDeleteCascadeAlertFalse': 'الحذف المباشر معطل',
    'academy.parameters.groupDeleteCascadeAlertTrue': 'الحذف المتتالي مفعل',
    'academy.parameters.groupDeleteCascadeDesc': 'وصف الحذف المتتالي',
    'academy.parameters.groupDeleteCascadeDisabled': 'معطل',
    'academy.parameters.groupDeleteCascadeEnabled': 'مفعل',
    'academy.parameters.groupDeleteCascadeLabel': 'حذف الفوج بالحذف المتتالي',
    'academy.parameters.groupDeleteCascadeTitle': 'حذف الأفواج مع الحصص',
    'academy.parameters.msg_invoice_error': 'خطأ في حفظ الفاتورة',
    'academy.parameters.msg_invoice_saved': 'تم حفظ الفاتورة بنجاح',
    'academy.parameters.msg_payment_error': 'خطأ في حفظ الدفع',
    'academy.parameters.msg_payment_saved': 'تم حفظ الدفع بنجاح',
    'academy.parameters.numbering_agency_length': 'طول رمز الوكالة',
    'academy.parameters.numbering_agency_length_helper': 'مساعد طول الوكالة',
    'academy.parameters.numbering_date_format': 'صيغة التاريخ',
    'academy.parameters.numbering_invoice_desc': 'إعدادات ترقيم الفواتير',
    'academy.parameters.numbering_invoice_title': 'ترقيم الفواتير',
    'academy.parameters.numbering_next_counter': 'العداد القادم',
    'academy.parameters.numbering_order': 'ترتيب الترقيم',
    'academy.parameters.numbering_padding': 'عدد خانات الرقم',
    'academy.parameters.numbering_payment_desc': 'إعدادات ترقيم الإيصالات',
    'academy.parameters.numbering_payment_title': 'ترقيم الإيصالات',
    'academy.parameters.numbering_prefix': 'البادئة',
    'academy.parameters.numbering_preview': 'معاينة الترقيم',
    'academy.parameters.numbering_reset_btn': 'إعادة ضبط العداد',
    'academy.parameters.numbering_reset_confirm': 'تأكيد إعادة الضبط',
    'academy.parameters.numbering_reset_period': 'فترة إعادة الضبط',
    'academy.parameters.numbering_reset_success': 'تمت إعادة الضبط بنجاح',
    'academy.parameters.numbering_separator': 'الفاصل',
    'academy.parameters.numbering_suffix': 'اللاحقة',
    'academy.parameters.order_date_prefix_number': 'تاريخ - بادئة - رقم',
    'academy.parameters.order_prefix_agency_date_number_suffix': 'بادئة - وكالة - تاريخ - رقم - لاحقة',
    'academy.parameters.order_prefix_agency_number_date': 'بادئة - وكالة - رقم - تاريخ',
    'academy.parameters.order_prefix_agency_number_suffix': 'بادئة - وكالة - رقم - لاحقة',
    'academy.parameters.order_prefix_date_number_suffix': 'بادئة - تاريخ - رقم - لاحقة',
    'academy.parameters.order_prefix_number_date': 'بادئة - رقم - تاريخ',
    'academy.parameters.order_prefix_number_suffix': 'بادئة - رقم - لاحقة',
    'academy.parameters.reset_daily': 'يومياً',
    'academy.parameters.reset_manual': 'يدوياً',
    'academy.parameters.reset_monthly': 'شهرياً',
    'academy.parameters.reset_never': 'أبداً',
    'academy.parameters.reset_yearly': 'سنوياً',
    'academy.parameters.sep_dash': 'واصلة (-)',
    'academy.parameters.sep_dot': 'نقطة (.)',
    'academy.parameters.sep_none': 'بدون',
    'academy.parameters.sep_slash': 'خط مائل (/)',
    'academy.parameters.sep_underscore': 'شرطة سفلية (_)',
    'academy.scheduler.add_session': 'إضافة حصة',
    'academy.scheduler.subtitle': 'تخطيط الحصص والأفواج',
    'academy.teachers.busy_map': 'خريطة التوفر',
    'academy.teachers.fullname': 'الاسم واللقب',
    'academy.teachers.hour': 'ساعة',
    'academy.teachers.hourly_rate': 'السعر بالساعة',
    'academy.teachers.specialization': 'التخصص',

    // boubaker 23/08/2026 - صفحات طباعة أكاديمية
    'academy.print.planning': 'تخطيط الحصص',
    'academy.print.invoices': 'طباعة الفواتير',
    'academy.print.receipts': 'طباعة إيصالات الدفع',
    'academy.print.daily_cash': 'تقرير الصندوق اليومي',
    'academy.print.status_all': 'جميع الحالات',
    'academy.print.method_all': 'جميع طرق الدفع',
    'academy.print.col_invoice': 'رقم الفاتورة',
    'academy.print.col_student': 'التلميذ',
    'academy.print.col_issued': 'تاريخ الإصدار',
    'academy.print.col_due': 'تاريخ الاستحقاق',
    'academy.print.col_total': 'المجموع',
    'academy.print.col_paid': 'المدفوع',
    'academy.print.col_status': 'الحالة',
    'academy.print.col_receipt': 'رقم الإيصال',
    'academy.print.col_date': 'التاريخ',
    'academy.print.col_method': 'الطريقة',
    'academy.print.col_amount': 'المبلغ',
    'academy.print.total': 'المجموع الكلي',
    'academy.print.receipts_count': 'عدد الإيصالات',
    'academy.print.cash': 'نقدا',
    'academy.print.card': 'بطاقة بنكية',
    'academy.print.transfer': 'تحويل بنكي',
    'academy.print.no_data': 'لا توجد بيانات. عدّل عوامل التصفية ثم اضغط "تحميل".',
    'academy.print.status.Paid': 'مدفوعة',
    'academy.print.status.Unpaid': 'غير مدفوعة',
    'academy.print.status.Partial': 'جزئية',

    // boubaker 25/08/2026 - Indicateur : traçabilité des envois mail
    'academy.menu.indicateur': 'المؤشرات',
    'academy.menu.indicateur.mail_log': 'تتبع إرسال البريد',
    'academy.indicateur.mail_log': 'تتبع إرسال البريد الإلكتروني',
    'academy.indicateur.col_date': 'التاريخ',
    'academy.indicateur.col_to': 'المستلم',
    'academy.indicateur.col_subject': 'الموضوع / المحتوى',
    'academy.indicateur.col_status': 'الحالة',
    'academy.indicateur.col_smtp': 'خادم SMTP',
    'academy.indicateur.col_action': 'الإجراء',
    'academy.indicateur.view_body': 'عرض محتوى البريد',
    'academy.indicateur.mail.status': 'الحالة',
    'academy.indicateur.mail.to_filter': 'تصفية حسب البريد...',
    'academy.indicateur.status.sent': 'مرسل',
    'academy.indicateur.status.failed': 'فشل',
    'academy.indicateur.status.pending': 'قيد الانتظار',
    'academy.indicateur.col_type': 'النوع',
    'academy.indicateur.mail.type_filter': 'نوع البريد',
    'academy.indicateur.mail_type.all': 'جميع الأنواع',
    'academy.indicateur.mail_type.debt_reminder': 'تذكير بالدين',
    'academy.indicateur.mail_type.auth_otp': 'رمز OTP / تسجيل الدخول',
    'academy.indicateur.mail_type.change_password': 'تغيير كلمة المرور',
    'academy.indicateur.mail_type.reset_password': 'إعادة تعيين كلمة المرور',
    'academy.indicateur.mail_type.general': 'عام',

    // Academy Scheduler
    'academy.scheduler.title': 'مصفوفة تخطيط الحصص (يوم، أسبوع، شهر)',
    'academy.scheduler.view_day': 'عرض اليوم',
    'academy.scheduler.view_week': 'عرض الأسبوع',
    'academy.scheduler.view_month': 'عرض الشهر',
    'academy.scheduler.group_by': 'تجميع حسب:',
    'academy.scheduler.group_by_room': 'القاعات والمعدات',
    'academy.scheduler.group_by_group': 'المجموعات والدفعات',
    'academy.scheduler.group_by_teacher': 'الأساتذة',
    'academy.scheduler.select_date': 'التاريخ',
    'academy.scheduler.select_month': 'الشهر والسنة',
    'academy.scheduler.today': 'اليوم',
    'academy.scheduler.book_slot': '+ حجز حصة',
    'academy.scheduler.schedule_btn': 'جدولة حصة',
    'academy.scheduler.filters': 'تصفية:',
    'academy.scheduler.all_rooms': 'جميع القاعات',
    'academy.scheduler.all_teachers': 'جميع الأساتذة',
    'academy.scheduler.all_groups': 'جميع المجموعات',
    'academy.scheduler.session_details': 'تفاصيل الحصة',
    'academy.scheduler.cancel_session': 'إلغاء الحصة',
    'academy.scheduler.confirm_cancel': 'هل أنت تأكد من إلغاء هذه الحصة؟',
    'academy.scheduler.msg_session_cancelled': 'تم إلغاء الحصة وحذفها بنجاح.',

    // boubaker 22/08/2026 - تبويبات الجدول + الطباعة + السحب والإفلات
    'academy.scheduler.tab_new': 'جدول جديد',
    'academy.scheduler.load': 'تحميل',
    'academy.scheduler.start_date': 'تاريخ البداية',
    'academy.scheduler.end_date': 'تاريخ النهاية',
    'academy.scheduler.not_loaded': 'حدد المرشحات ثم انقر على "تحميل" لعرض الجدول.',
    'academy.scheduler.print': 'طباعة',
    'academy.scheduler.print_title': 'طباعة الجدول الزمني',
    'academy.scheduler.edit_session': 'تعديل',
    'academy.scheduler.save_changes': 'حفظ التعديلات',
    'academy.scheduler.session_updated': 'تم تحديث الحصة بنجاح.',
    'academy.scheduler.drag_hint': 'نصيحة: اسحب وأفلت الحصة لتغيير اليوم (عرض أسبوع/شهر) أو التوقيت (عرض يوم).',
    'academy.scheduler.print.col_day': 'اليوم',
    'academy.scheduler.print.col_start': 'البداية',
    'academy.scheduler.print.col_end': 'النهاية',
    'academy.scheduler.print.col_room': 'القاعة',
    'academy.scheduler.print.col_teacher': 'الأستاذ',
    'academy.scheduler.print.col_group': 'الفوج',
    'academy.scheduler.print.month_label': 'الشهر',

    // Absence & Session status management - AR
    'academy.scheduler.session_status': 'حالة الحصة',
    'academy.scheduler.status.scheduled': 'مجدولة',
    'academy.scheduler.status.completed': 'مكتملة',
    'academy.scheduler.status.cancelled': 'ملغاة',
    'academy.scheduler.status.postponed': 'مؤجلة',
    'academy.scheduler.status_reason': 'السبب / التبرير',
    'academy.scheduler.postponed_date': 'تاريخ التأجيل',
    'academy.scheduler.postponed_time': 'توقيت التأجيل',
    'academy.scheduler.teacher_absent': 'الأستاذ غائب',
    'academy.scheduler.postponed_to': 'مؤجلة إلى {0} {1}',
    'academy.teachers.absence_requests': 'طلبات الغياب',
    'academy.teachers.absence_requests_title': 'طلبات غياب الأساتذة',
    'academy.teachers.filter_all': 'الكل',
    'academy.teachers.filter_pending': 'قيد الانتظار',
    'academy.teachers.filter_approved': 'المقبولة',
    'academy.teachers.filter_rejected': 'المرفوضة',
    'academy.teachers.request_date': 'تاريخ الطلب',
    'academy.teachers.absence_reason': 'سبب الغياب',
    'academy.teachers.attached_document': 'الوثيقة التبريرية',
    'academy.teachers.view_document': 'عرض الوثيقة',
    'academy.teachers.admin_comment': 'تعليق الإدارة',
    'academy.teachers.admin_comment_placeholder': 'أدخل تعليقاً أو تعليمات للأستاذ...',
    'academy.teachers.approve_absence': 'قبول الغياب',
    'academy.teachers.reject_absence': 'رفض الطلب',
    'academy.teachers.update_session_prompt': 'تحديث الحصة المرتبطة إلى ملغاة',
    'academy.teachers.absence_approved_success': 'تم قبول طلب الغياب بنجاح.',
    'academy.teachers.absence_rejected_success': 'تم رفض طلب الغياب.',
    'academy.teachers.no_absence_requests': 'لا توجد طلبات غياب.',

    // Academy Theoretical Planning - AR
    'academy.planning_theorique.title': 'إدارة التخطيط النظري',
    'academy.planning_theorique.subtitle': 'تحديد الجداول النموذجية حسب القاعة، عدد الأسابيع ونوع اليوم',
    'academy.planning_theorique.new': 'تخطيط نظري جديد',
    'academy.planning_theorique.edit': 'تعديل التخطيط النظري',
    'academy.planning_theorique.confirmDelete': 'هل أنت متأكد من رغبتك في حذف هذا التخطيط النظري؟ سيتم أيضاً حذف الحصص المرتبطة به.',
    'academy.planning_theorique.col_room': 'القاعة',
    'academy.planning_theorique.col_num_week': 'عدد الأسابيع',
    'academy.planning_theorique.col_start_date': 'تاريخ البدء',
    'academy.planning_theorique.col_end_date': 'تاريخ الانتهاء',
    'academy.planning_theorique.col_day_type': 'نوع اليوم',
    'academy.planning_theorique.day_normal': 'عادي',
    'academy.planning_theorique.day_holiday': 'عطلة',
    'academy.planning_theorique.section_general': 'معلومات عامة',
    'academy.planning_theorique.field_room': 'قاعة التدريس',
    'academy.planning_theorique.field_num_week': 'عدد أسابيع الدورة',
    'academy.planning_theorique.field_start_date': 'تاريخ بدء الصلاحية',
    'academy.planning_theorique.field_end_date': 'تاريخ نهاية الصلاحية (اختياري)',
    'academy.planning_theorique.field_day_type': 'نوع اليوم',
    'academy.planning_theorique.room_required': 'يرجى اختيار قاعة.',
    'academy.planning_theorique.num_week_min': 'يجب أن يكون عدد الأسابيع 1 على الأقل.',
    'academy.planning_theorique.start_date_required': 'تاريخ البدء إجباري.',
    'academy.planning_theorique.end_before_start': 'لا يمكن أن يكون تاريخ الانتهاء قبل تاريخ البدء.',
    'academy.planning_theorique.open_matrix': 'عرض الجدول',
    'academy.planning_theorique.empty': 'لا يوجد تخطيط نظري مهيأ.',

    'academy.planning_theorique.manage_title': 'إدارة التخطيط النظري',
    'academy.planning_theorique.generation_title': 'توليد جدول الحصص الفعلي',
    'academy.planning_theorique.deletion_title': 'حذف حصص الجدول',
    'academy.planning_theorique.new_title': 'تخطيط نظري جديد',
    'academy.planning_theorique.edit_title': 'تعديل التخطيط النظري',
    'academy.planning_theorique.form_subtitle': 'تحديد القاعة ودورة الأسابيع وفترة صلاحية النموذج',
    'academy.planning_theorique.new_button': 'تخطيط جديد',
    'academy.planning_theorique.generate_button': 'توليد الحصص',
    'academy.planning_theorique.delete_button': 'حذف الحصص المحددة',

    'academy.planning_theorique.room': 'القاعة',
    'academy.planning_theorique.nbr_weeks': 'عدد الأسابيع',
    'academy.planning_theorique.start_date': 'تاريخ البدء',
    'academy.planning_theorique.end_date': 'تاريخ الانتهاء',
    'academy.planning_theorique.day_type': 'نوع اليوم',
    'academy.planning_theorique.type_normal': 'عادي',
    'academy.planning_theorique.type_holiday': 'عطلة',
    'academy.planning_theorique.vacs_count': 'عدد الفترات',
    'academy.planning_theorique.week': 'أسبوع',
    'academy.planning_theorique.weeks': 'أسابيع',
    'academy.planning_theorique.indefinite': 'غير محدد',
    'academy.planning_theorique.period': 'الفترة',
    'academy.planning_theorique.date': 'التاريخ',
    'academy.planning_theorique.hours': 'التوقيت',
    'academy.planning_theorique.status': 'الحالة',
    'academy.planning_theorique.group': 'المجموعة',
    'academy.planning_theorique.teacher': 'المدرس',
    'academy.planning_theorique.none': 'لا يوجد',
    'academy.planning_theorique.search_placeholder': 'بحث...',

    'academy.planning_theorique.room_helper': 'القاعة المخصصة لنموذج التخطيط هذا',
    'academy.planning_theorique.weeks_helper': 'عدد أسابيع الدورة (مثلاً 1 للتكرار الأسبوعي)',
    'academy.planning_theorique.end_date_helper': 'اتركه فارغاً إذا كان التخطيط سارياً لأجل غير مسمى',

    'academy.planning_theorique.msg_created': 'تم إنشاء التخطيط النظري بنجاح.',
    'academy.planning_theorique.msg_updated': 'تم تحديث التخطيط النظري بنجاح.',
    'academy.planning_theorique.msg_deleted': 'تم حذف التخطيط النظري بنجاح.',
    'academy.planning_theorique.no_data': 'لا يوجد أي تخطيط نظري مهيأ حالياً.',

    'academy.planning_theorique.validation.room_required': 'يرجى اختيار قاعة.',
    'academy.planning_theorique.validation.weeks_required': 'يجب أن يكون عدد الأسابيع 1 على الأقل.',
    'academy.planning_theorique.validation.start_date_required': 'تاريخ البدء مطلوب.',
    'academy.planning_theorique.validation.invalid_date_range': 'لا يمكن أن يكون تاريخ الانتهاء قبل تاريخ البدء.',
    'academy.planning_theorique.validation.hours_required': 'وقت البدء ووقت الانتهاء مطلوبان.',
    'academy.planning_theorique.validation.invalid_time_range': 'يجب أن يكون وقت الانتهاء بعد وقت البدء.',
    'academy.planning_theorique.validation.dates_required': 'يرجى إدخال تاريخ البدء وتاريخ الانتهاء.',

    'academy.planning_theorique.matrix_title': 'التخطيط النظري',
    'academy.planning_theorique.matrix_subtitle': 'الجدول الأسبوعي النموذجي للحصص',
    'academy.planning_theorique.matrix_config_btn': 'إعدادات العرض',
    'academy.planning_theorique.filter_popup_title': 'اختيار التخطيط النظري',
    'academy.planning_theorique.select_theo': 'اختر تخطيطاً نظرياً',
    'academy.planning_theorique.select_theo_helper': 'اختر التخطيط النظري حسب القاعة والفترة لعرض جدول الحصص',
    'academy.planning_theorique.apply_filter': 'تحميل الجدول',
    'academy.planning_theorique.filter_required': 'يرجى اختيار تخطيط نظري.',
    'academy.planning_theorique.slot_new': 'حصة نموذجية جديدة',
    'academy.planning_theorique.slot_edit': 'تعديل الحصة',
    'academy.planning_theorique.slot_group': 'الفوج',
    'academy.planning_theorique.slot_teacher': 'الأستاذ',
    'academy.planning_theorique.slot_start_time': 'وقت البدء',
    'academy.planning_theorique.slot_end_time': 'وقت الانتهاء',
    'academy.planning_theorique.slot_confirm_delete': 'حذف هذه الحصة النظرية؟',
    'academy.planning_theorique.matrix_no_data': 'يرجى اختيار تخطيط نظري لعرض جدوله.',
    'academy.planning_theorique.week_label': 'الأسبوع {0}',
    'academy.planning_theorique.filter_btn': 'عوامل التصفية',
    'academy.planning_theorique.refresh': 'تحديث',
    'academy.planning_theorique.filter_dialog_title': 'تصفية مصفوفة التخطيط النظري',
    'academy.planning_theorique.filter_dialog_desc': 'حدد الفترة والقاعات لتحميل المخططات المقابلة.',
    'academy.planning_theorique.filter_rooms': 'القاعات',
    'academy.planning_theorique.select_all': 'تحديد الكل',
    'academy.planning_theorique.deselect_all': 'إلغاء تحديد الكل',
    'academy.planning_theorique.load_btn': 'تحميل',
    'academy.planning_theorique.not_loaded_title': 'لا توجد بيانات معروضة',
    'academy.planning_theorique.not_loaded_desc': 'يرجى تطبيق عوامل التصفية لعرض مصفوفة التخطيط النظري.',
    'academy.planning_theorique.open_filters': 'فتح التصفية',
    'academy.planning_theorique.add_vac': 'إضافة فترة',
    'academy.planning_theorique.no_matrix_data': 'لا توجد فترات نظرية لهذا التخطيط.',
    'academy.planning_theorique.new_vac_title': 'فترة نظرية جديدة',
    'academy.planning_theorique.edit_vac_title': 'تعديل الفترة النظرية',
    'academy.planning_theorique.day_num': 'يوم الدورة',
    'academy.planning_theorique.start_time': 'وقت البدء',
    'academy.planning_theorique.end_time': 'وقت الانتهاء',
    'academy.planning_theorique.msg_vac_created': 'تمت إضافة الفترة النظرية بنجاح.',
    'academy.planning_theorique.msg_vac_updated': 'تم تعديل الفترة النظرية بنجاح.',
    'academy.planning_theorique.msg_vac_deleted': 'تم حذف الفترة النظرية بنجاح.',

    'academy.planning_theorique.gen_title': 'توليد الجدول الفعلي',
    'academy.planning_theorique.gen_subtitle': 'إنشاء الحصص الفعلية تلقائياً انطلاقاً من المخططات النظرية',
    'academy.planning_theorique.gen_step1': '1. اختيار المخططات النظرية',
    'academy.planning_theorique.gen_step2': '2. تحديد الفترة والخصائص المستهدفة',
    'academy.planning_theorique.gen_target_start': 'تاريخ بدء التوليد',
    'academy.planning_theorique.gen_target_end': 'تاريخ نهاية التوليد',
    'academy.planning_theorique.gen_btn_run': 'بدء التوليد',
    'academy.planning_theorique.gen_running': 'جارٍ توليد الحصص...',
    'academy.planning_theorique.gen_select_at_least_one': 'يرجى اختيار مخطط نظري واحد على الأقل.',
    'academy.planning_theorique.gen_dates_required': 'يرجى تحديد تاريخي البدء والانتهاء.',
    'academy.planning_theorique.gen_confirm': 'هل تريد توليد الحصص للمخططات المختارة بين {0} و {1}؟',
    'academy.planning_theorique.gen_success': 'تم توليد {0} حصة/حصص بنجاح!',
    'academy.planning_theorique.target_start_date': 'تاريخ البدء المستهدف',
    'academy.planning_theorique.target_end_date': 'تاريخ الانتهاء المستهدف',
    'academy.planning_theorique.all_rooms': 'جميع القاعات',
    'academy.planning_theorique.room_filter': 'تصفية حسب القاعة',
    'academy.planning_theorique.all_selected': 'تم تحديد جميع المخططات',
    'academy.planning_theorique.plannings_selected': 'مخطط(ات) محددة',
    'academy.planning_theorique.select_at_least_one': 'يرجى تحديد تخطيط نظري واحد على الأقل للتوليد.',
    'academy.planning_theorique.no_data_to_generate': 'لا يوجد تخطيط نظري متاح',
    'academy.planning_theorique.no_data_to_generate_desc': 'لم يتم العثور على أي تخطيط نظري نشط للفترة المحددة.',
    'academy.planning_theorique.generation_success_title': 'تم توليد جدول الحصص بنجاح!',
    'academy.planning_theorique.generation_success_subtitle': 'تم إنشاء {0} حصة/حصص للفترة من {1} إلى {2}.',
    'academy.planning_theorique.generation_success_msg': 'تم توليد {0} حصة/حصص بنجاح.',
    'academy.planning_theorique.generation_success_with_skipped': 'تم توليد {0} حصة/حصص بنجاح (تم تجاهل {1} حصة بسبب تعارض).',
    'academy.planning_theorique.sessions_generated': 'حصة/حصص تم توليدها',
    'academy.planning_theorique.conflicts_skipped': 'حصة/حصص تم تجاهلها بسبب التعارض',
    'academy.planning_theorique.view_scheduler': 'عرض جدول الحصص',

    'academy.planning_theorique.del_title': 'حذف الحصص والجدول',
    'academy.planning_theorique.del_subtitle': 'تصفية وحذف جماعي للحصص الفعلية',
    'academy.planning_theorique.del_filter_title': 'معايير تصفية الحصص المراد حذفها',
    'academy.planning_theorique.del_date_from': 'من',
    'academy.planning_theorique.del_date_to': 'إلى',
    'academy.planning_theorique.del_all_rooms': 'جميع القاعات',
    'academy.planning_theorique.del_all_teachers': 'جميع الأساتذة',
    'academy.planning_theorique.del_all_groups': 'جميع الأفواج',
    'academy.planning_theorique.del_btn_search': 'بحث عن الحصص',
    'academy.planning_theorique.del_btn_delete': 'حذف المحدد',
    'academy.planning_theorique.del_confirm': 'تنبيه: هل أنت متأكد من حذف {0} حصة/حصص محددة بشكل نهائي؟ لا يمكن التراجع عن هذا الإجراء.',
    'academy.planning_theorique.del_success': 'تم حذف {0} حصة/حصص بنجاح.',
    'academy.planning_theorique.del_no_sessions': 'لم يتم العثور على حصص مطابقة لهذه المعايير.',
    'academy.planning_theorique.del_select_to_delete': 'يرجى تحديد حصة واحدة على الأقل للحذف.',
    'academy.planning_theorique.all_groups': 'جميع المجموعات',
    'academy.planning_theorique.group_filter': 'تصفية حسب المجموعة',
    'academy.planning_theorique.all_teachers': 'جميع المدرسين',
    'academy.planning_theorique.teacher_filter': 'تصفية حسب المدرس',
    'academy.planning_theorique.sessions_selected': 'حصة (حصص) محددة',
    'academy.planning_theorique.select_at_least_one_session': 'يرجى تحديد حصة واحدة على الأقل للحذف.',
    'academy.planning_theorique.no_sessions_found': 'لم يتم العثور على أي حصص',
    'academy.planning_theorique.no_sessions_found_desc': 'لا توجد أي حصص جدول تطابق معايير البحث المحددة.',
    'academy.planning_theorique.deletion_success_title': 'تمت عملية الحذف بنجاح!',
    'academy.planning_theorique.deletion_success_subtitle': 'تم حذف {0} حصة/حصص نهائياً من الجدول.',
    'academy.planning_theorique.deletion_success_msg': 'تم حذف {0} حصة/حصص بنجاح.',
    'academy.planning_theorique.sessions_deleted': 'حصة/حصص محذوفة',

    // Common Aliases - AR
    'commun.error': 'خطأ',
    'commun.warning': 'تنبيه',
    'commun.success': 'نجاح',
    'commun.confirmation': 'تأكيد',
    'commun.cancel': 'إلغاء',
    'commun.save': 'حفظ',
    'commun.edit': 'تعديل',
    'commun.delete': 'حذف',
    'commun.back_to_list': 'العودة إلى القائمة',
    'commun.view_grid': 'عرض شبكي',
    'commun.view_table': 'عرض جدولي',

    // Academy Planning Controls
    'academy.planning_controls.title': 'ضوابط ورواعد التخطيط',
    'academy.planning_controls.subtitle': 'إعداد قواعد التحقق المطبقة عند إنشاء وتعديل وإلغاء الحصص.',
    'academy.planning_controls.add_new': 'ضابط جديد',
    'academy.planning_controls.active_count': 'الضوابط النشطة',
    'academy.planning_controls.blocking_count': 'القواعد الحاظرة (رفض)',
    'academy.planning_controls.warning_count': 'قواعد التحذير',
    'academy.planning_controls.rules_info': 'قواعد عمل الضوابط:',
    'academy.planning_controls.code': 'رمز الضابط',
    'academy.planning_controls.lib': 'عنوان الضابط',
    'academy.planning_controls.description': 'الوصف والتأثير',
    'academy.planning_controls.active': 'مفعل',
    'academy.planning_controls.blocking': 'حاظر (Block)',
    'academy.planning_controls.status': 'الحالة',
    'academy.planning_controls.status_disabled': 'معطل',
    'academy.planning_controls.status_blocking': 'حاظر',
    'academy.planning_controls.status_warning': 'تحذير',
    'academy.planning_controls.modal_title_add': 'ضابط تخطيط جديد',
    'academy.planning_controls.modal_title_edit': 'تعديل ضابط التخطيط',
    'academy.planning_controls.confirm_delete': 'هل أنت تأكد من حذف هذا الضابط؟',
    'academy.planning_controls.back_to_list': 'العودة للقائمة',
    'academy.planning_controls.msg_active_updated': 'تم تحديث الضابط {code}: مفعل = {active}',
    'academy.planning_controls.msg_blocking_updated': 'تم تحديث الضابط {code}: حاظر = {blocking}',
    'academy.planning_controls.msg_saved': 'تم حفظ ضابط التخطيط بنجاح.',
    'academy.planning_controls.msg_deleted': 'تم حذف الضابط {code}.',
    'academy.planning_controls.validation.code_required': 'رمز الضابط مطلوب.',
    'academy.planning_controls.validation.lib_required': 'عنوان الضابط مطلوب.',
    'academy.planning_controls.view_table': 'عرض الجدول',
    'academy.planning_controls.view_grid': 'عرض الشبكة',
    'academy.planning_controls.no_description': 'لم يتم تحديد وصف.',
    'academy.planning_controls.form_title_edit': 'تعديل الضابط ({code})',
    'academy.planning_controls.form_title_new': 'تحديد ضابط تحقق جديد',
    'academy.planning_controls.code_helper': 'رمز فريد لمعرف القاعدة (مثال: CTL_CUSTOM_101)',
    'academy.planning_controls.lib_helper': 'عنوان واضح يعرض أثناء التنبيهات أو الفحوصات',
    'academy.planning_controls.desc_helper': 'وصف توضيحي لتأثير هذا الضابط',
    'academy.planning_controls.active_desc': 'تفعيل أو تعطيل التحقق التلقائي لهذا الضابط',
    'academy.planning_controls.blocking_desc': 'في حالة التفعيل، يتم حظر الإجراء تماماً مع الرفض. وإلا يتم إظهار تحذير فقط.',
    'academy.common.yes': 'نعم',
    'academy.common.no': 'لا',

    // Academy Financial Management & Debt Control
    'academy.financial.title': 'الإدارة المالية ومتابعة الديون',
    'academy.financial.subtitle': 'متابعة الفواتير، تحصيل مدفوعات الطلاب وتقارير الصندوق.',
    'academy.financial.daily_report': 'حصيلة الصندوق (تقرير)',
    'academy.financial.receive_payment': 'تسجيل الدفع',
    'academy.financial.kpi.debt': 'إجمالي الديون غير المسددة',
    'academy.financial.kpi.monthly': 'إيرادات الشهر الحالي',
    'academy.financial.kpi.today': 'مقبوضات اليوم',
    'academy.financial.tab.debts': 'جدول ديون الطلاب',
    'academy.financial.tab.invoices': 'سجل الفواتير الصادرة',
    'academy.financial.tab.payments': 'سجل المدفوعات المستلمة',
    'academy.financial.debtors_list': 'قائمة الطلاب المدينين',
    'academy.financial.send_bulk_reminder': 'إرسال تذكير بالدين عبر البريد الإلكتروني ({0})',
    'academy.financial.bulk_reminder_sending': 'جارٍ إرسال رسائل التذكير بالدين...',
    'academy.financial.bulk_reminder_result': 'تم إرسال {0} رسالة تذكير بنجاح. تم تخطي {1} (بدون بريد).',
    'academy.financial.bulk_reminder_no_selection': 'يرجى اختيار متعلم مدين واحد على الأقل.',
    'academy.financial.rest_to_pay': 'المتبقي للدفع',
    'academy.financial.gross_amount': 'المبلغ الإجمالي',
    'academy.financial.applied_discount': 'الخصم المطبق',
    'academy.financial.net_invoiced': 'الصافي المفلتر',
    'academy.financial.amount_paid': 'المبلغ المدفوع',
    'academy.financial.invoice_details': 'تفاصيل الفاتورة الرسمية',
    'academy.financial.receipt_details': 'وصل استلام رسمي',
    'academy.financial.print_invoice': 'طباعة الفاتورة',
    'academy.financial.print_receipt': 'طباعة الوصل',
    'academy.financial.delete_payment_confirm': 'هل أنت تأكد من حذف الدفع {0} بمبلغ {1} {2} للطالب {3}؟',
    'academy.financial.msg_payment_deleted': 'تم حذف الدفع {0} بنجاح. تم تعديل رصيد الفاتورة.',
    'academy.financial.msg_bulk_reminder_sent': 'نجاح: تم إرسال تذكيرات الديون عبر البريد إلى {0} طالب!',
    'academy.financial.manual_discount_info': 'تعيين الخصومات اليدوية: الخصومات المعينة يتم تخصيصها للطلاب من صفحة "سياسة الخصومات" وتطبق مباشرة على الفواتير الصادرة.',
    'academy.financial.modal.receive_title': 'تسجيل دفع طالب',
    'academy.financial.modal.select_student': 'اختر الطالب',
    'academy.financial.modal.amount': 'مبلغ الدفع ({0})',
    'academy.financial.modal.method': 'طريقة الدفع',
    'academy.financial.modal.cash': 'نقداً (Cash)',
    'academy.financial.modal.card': 'بطاقة بنكية / TPE',
    'academy.financial.modal.transfer': 'تحويل / شيك',
    'academy.financial.modal.fifo_note': 'قاعدة الأولوية (FIFO): سيتم خصم المبلغ تلقائياً من أقدم فاتورة غير مسددة للطالب.',
    'academy.financial.modal.process': 'تأكيد الدفع',
    'academy.financial.report.title': 'تقرير حصيلة الصندوق اليومي',
    'academy.financial.report.date': 'تقرير يوم: {0}',
    'academy.financial.report.cash': 'مجموع النقدي',
    'academy.financial.report.card': 'مجموع البطاقة / TPE',
    'academy.financial.report.transfer': 'مجموع التحويل / الشيكات',
    'academy.financial.report.total': 'المجموع الكلي المقبوض: {0}',
    'academy.financial.report.print': 'طباعة التقرير',
    'academy.financial.table.student': 'الطالب',
    'academy.financial.table.invoiced': 'المبلغ المفوتر',
    'academy.financial.table.paid': 'المدفوع سابقتً',
    'academy.financial.table.badge': 'حالة الدين',
    'academy.financial.table.action': 'الإجراء',
    'academy.financial.table.invoice_num': 'رقم الفاتورة',
    'academy.financial.table.receipt_num': 'رقم الوصل',
    'academy.financial.table.due_date': 'تاريخ الاستحقاق',
    'academy.financial.table.status': 'الحالة',
    'academy.financial.table.amount': 'المبلغ',
    'academy.financial.table.method': 'الطريقة',
    'academy.financial.table.date': 'التاريخ والوقت',
    'academy.financial.success.overpayment': 'تم تسجيل الدفع بنجاح. تم إضافة مبلغ زائد قدره {0} إلى الرصيد.',
    'academy.financial.success.allocated': 'تم تسجيل الدفع وتخصيصه للفواتير بنجاح.',
    'academy.financial.error.no_invoices': 'لا توجد فواتير غير مسددة لهذا الطالب.',
    'academy.financial.error.payment': 'فشل تسجيل الدفع.',
    'academy.financial.phone_label': 'الهاتف:',
    'academy.financial.email_label': 'البريد:',
    'academy.financial.official': 'رسمي',
    'academy.financial.invoice_no': 'فاتورة رقم',
    'academy.financial.receipt_no': 'وصل رقم',
    'academy.financial.due_date_label': 'تاريخ الاستحقاق',
    'academy.financial.status_label': 'الحالة',
    'academy.financial.date_label': 'التاريخ',
    'academy.financial.mode_label': 'طريقة الدفع',
    'academy.financial.student_group_info': 'معلومات الطالب والمجموعة',
    'academy.financial.student_info': 'معلومات الطالب',
    'academy.financial.full_name': 'الاسم الكامل:',
    'academy.financial.course_group_label': 'الدورة / المجموعة:',
    'academy.financial.designation': 'البيان',
    'academy.financial.description': 'الوصف',
    'academy.financial.amount': 'المبلغ',
    'academy.financial.gross_amount_course': 'مبلغ الدورة الإجمالي',
    'academy.financial.payment_amount': 'المبلغ المدفوع',
    'academy.financial.total_paid_today': 'إجمالي المدفوع حتى اليوم',
    'academy.financial.stamp_signature': 'الختم والتوقيع',
    'academy.financial.direction': 'إدارة',
    'academy.financial.daily_report_sub': 'ملخص الحصيلة اليومية للصندوق',
    'academy.financial.delete_payment_tooltip': 'حذف هذا الدفع',

    // boubaker 23/08/2026 - حذف الفاتورة (رقابة على الخادم: الدفعات / الانتماء للمجموعة)
    'academy.financial.delete_invoice_tooltip': 'حذف هذه الفاتورة',
    'academy.financial.error.delete_invoice': 'فشل حذف الفاتورة.',
    'academy.financial.delete_invoice_confirm': 'هل أنت متأكد من حذف الفاتورة {0} للطالب {1}؟',
    'academy.financial.msg_invoice_deleted': 'تم حذف الفاتورة {0} بنجاح.',
    'academy.financial.error.delete_payment': 'فشل حذف الدفع.',
    'academy.financial.status.paid': 'مدفوعة',
    'academy.financial.status.partial': 'مدفوعة جزئياً',
    'academy.financial.status.unpaid': 'غير مدفوعة',
    'academy.financial.method.cash': 'نقداً (Cash)',
    'academy.financial.method.card': 'بطاقة بنكية / TPE',
    'academy.financial.method.transfer': 'تحويل / شيك',
    'academy.financial.invoice_type_title': 'فاتورة',
    'academy.financial.receipt_type_title': 'وصل استلام دُفعة',
    'academy.financial.report_type_title': 'تقرير حصيلة الصندوق اليومي',
    'academy.financial.issued_by': 'صادر بواسطة:',
    'academy.attendance.absent': 'غائب',
    'academy.attendance.excused': 'معذور',
    'academy.attendance.late': 'متأخر',
    'academy.attendance.present': 'حاضر',
    'academy.attendance.subtitle': 'تسجيل الحضور وقاعدة 75% للحصول على الشهادة.',
    'academy.attendance.title': 'تسجيل الحضور والغياب',
    'academy.reports.export_csv': 'تصدير CSV',
    'academy.reports.print_pdf': 'طباعة PDF',
    'academy.reports.subtitle': 'تقارير وتحليلات الأداء والأرقام.',
    'academy.reports.title': 'التقارير والتحليلات',
    'academy.search.hold_desc': 'حساب معلق مالياً بسبب ديون غير مسددة.',
    'academy.teachers.confirm_delete': 'هل أنت تأكد من حذف هذا الأستاذ؟',
    'academy.teachers.drawer.booked': 'حصة محجوزة',
    'common.delete': 'حذف',
    'common.edit': 'تعديل',
    'common.error': 'خطأ',

    // Academy Parameters
    'academy.parameters.matrix_title': 'معلمات مصفوفة التخطيط (الحصص والعرض)',
    'academy.parameters.matrix_desc': 'حدد المدى الزمني، مدة الحصة، أول يوم في الأسبوع ووضع العرض الافتراضي.',
    'academy.parameters.start_time': 'وقت البدء',
    'academy.parameters.end_time': 'وقت الانتهاء',
    'academy.parameters.slot_duration': 'مدة الحصة (دقيقة)',
    'academy.parameters.first_day': 'أول يوم في الأسبوع',
    'academy.parameters.default_view': 'عرض التخطيط الافتراضي',
    'academy.parameters.weekend_days': 'أيام العطلة الأسبوعية',
    'academy.parameters.weekend_color': 'لون خلفية العطلة الأسبوعية',

    // Academy Dashboard Refresh
    'academy.dashboard.refresh_tooltip': 'تحديث تلقائي',
    'academy.dashboard.refresh_tooltip_disabled': 'التحديث التلقائي معطل',
    'academy.dashboard.refresh_tooltip_sec': 'تحديث تلقائي كل {0} ثانية',
    'academy.dashboard.refresh_tooltip_min': 'تحديث تلقائي كل {0} دقيقة',

    // Academy Parameters
    'academy.parameters.pageTitle': 'إعدادات الأكاديمية',
    'academy.parameters.agencyTitle': 'إعدادات الوكالة #{0}',
    'academy.parameters.saveTooltip': 'حفظ هذه المعلمة',
    'academy.parameters.saveSuccess': 'تم حفظ المعلمة « {0} » بنجاح.',
    'academy.parameters.saveError': 'تعذر حفظ المعلمة « {0} ».',
    'academy.parameters.timerTitle': 'تحديث لوحة التحكم',
    'academy.parameters.timerDesc': 'تكرار التحديث التلقائي للإحصائيات بالثواني.',
    'academy.parameters.timerLabel': 'مؤقت التحديث (بالثواني)',
    'academy.parameters.timerUnit': 'ثانية',
    'academy.parameters.timerDisabledHelper': '0 = معطل (يتم تحميل البيانات عند الفتح فقط)',
    'academy.parameters.timerActiveHelper': 'يتم التحديث كل {0} ثانية',
    'academy.parameters.quickPresets': 'اختصارات سريعة :',
    'academy.parameters.chipDisabled': 'معطل (0 ث)',
    'academy.parameters.autoApproveTitle': 'تأكيد التسجيلات الأولية',
    'academy.parameters.autoApproveDesc': 'يحدد ما إذا كان الطلاب المسجلون أولياً عبر الإنترنت معتمدين تلقائياً.',
    'academy.parameters.autoApproveEnabled': 'الموافقة التلقائية مفعلة',
    'academy.parameters.autoApproveDisabled': 'موافقة يدوية (موصى بها)',
    'academy.parameters.autoApproveAlertTrue': 'ستصبح التسجيلات الأولية الجديدة نشطة فوراً بدون مراجعة.',
    'academy.parameters.autoApproveAlertFalse': 'تبقى التسجيلات الأولية قيد الانتظار حتى المراجعة اليدوية.',
    'academy.parameters.quizScoreTitle': 'الدرجة الأدنى لنجاح الاختبارات',
    'academy.parameters.quizScoreDesc': 'النسبة المئوية الأدنى المطلوبة لنجاح اختبارات التقييم.',
    'academy.parameters.quizScoreLabel': 'الدرجة الأدنى للنجاح (%)',
    'academy.parameters.quizScoreHelper': 'الطلاب الحاصلون على {0}% على الأقل يعتبرون ناجحين.',
    'academy.parameters.maxStudentsTitle': 'الحد الأقصى للطلاب لكل مجموعة',
    'academy.parameters.maxStudentsDesc': 'العدد الأقصى الموصى به للطلاب النشطين في المجموعة الواحدة.',
    'academy.parameters.maxStudentsLabel': 'الحد الأقصى للطلاب لكل فصل',
    'academy.parameters.maxStudentsUnit': 'طلاب',
    'academy.parameters.maxStudentsHelper': 'تنبيه عند تجاوز الفصل لـ {0} طالب.',
    'academy.parameters.modifiedBy': 'تم التعديل بواسطة :',
    'academy.parameters.lastUpdate': 'آخر تحديث :',
    'academy.parameters.systemUser': 'النظام',
    'academy.parameters.showUserEditionsTitle': 'إظهار المستخدم المتصل على المطبوعات',
    'academy.parameters.showUserEditionsDesc': 'عرض اسم وتخصص المستخدم المتصل تلقائياً في أسفل الفواتير والوصولات والتقارير المطبوعة.',
    'academy.parameters.showUserEditionsEnabled': 'إظهار اسم العون مفعل في أسفل المطبوعات',
    'academy.parameters.showUserEditionsDisabled': 'إظهار اسم العون معطل (مطبوعات بدون اسم العون)',


    // Academy Quizzes
    'academy.quizzes.title': 'الاختبارات والتقييمات',
    'academy.quizzes.add_new': 'اختبار جديد',
    'academy.quizzes.back_to_list': 'العودة إلى القائمة',
    'academy.quizzes.empty': 'لا يوجد اختبار بعد. اضغط على "اختبار جديد" للبدء.',
    'academy.quizzes.search_placeholder': 'البحث عن اختبار...',
    'academy.quizzes.list.grid': 'عرض شبكي',
    'academy.quizzes.list.table': 'عرض جدولي',
    'academy.quizzes.duration_label': '{0} دقيقة',
    'academy.quizzes.status_draft': 'مسودة',
    'academy.quizzes.status_active': 'نشط',
    'academy.quizzes.status_archived': 'مؤرشف',
    'academy.quizzes.confirm_delete': 'حذف الاختبار « {0} » وجميع أسئلته ونتائجه؟',
    'academy.quizzes.section_info': 'معلومات الاختبار',
    'academy.quizzes.section_levels': 'المستويات / سلم النقاط',
    'academy.quizzes.add_level': 'إضافة مستوى',
    'academy.quizzes.levels_empty': 'لا يوجد مستوى محدد. أضف نطاقات النقاط لعرض مستوى مخصص.',
    'academy.quizzes.level.code': 'الرمز',
    'academy.quizzes.level.title': 'العنوان',
    'academy.quizzes.level.min': 'الحد الأدنى',
    'academy.quizzes.level.max': 'الحد الأقصى',
    'academy.quizzes.level.message': 'الرسالة',
    'academy.quizzes.level.add': 'إضافة',
    'academy.quizzes.level.update': 'تحديث',
    'academy.quizzes.table.code': 'الرمز',
    'academy.quizzes.table.title': 'العنوان',
    'academy.quizzes.table.duration': 'المدة',
    'academy.quizzes.table.status': 'الحالة',
    'academy.quizzes.table.validity': 'الصلاحية',
    'academy.quizzes.actions.questions': 'الأسئلة',
    'academy.quizzes.actions.results': 'النتائج',
    'academy.quizzes.actions.share': 'مشاركة الرابط',
    'academy.quizzes.actions.edit': 'تعديل',
    'academy.quizzes.actions.delete': 'حذف',
    'academy.quizzes.form.title_new': 'اختبار جديد',
    'academy.quizzes.form.title_edit': 'تعديل: {0}',
    'academy.quizzes.form.code': 'الرمز',
    'academy.quizzes.form.title': 'العنوان',
    'academy.quizzes.form.duration': 'المدة (بالدقائق)',
    'academy.quizzes.form.expiry_days': 'الانتهاء (أيام)',
    'academy.quizzes.form.status': 'الحالة',
    'academy.quizzes.form.display': 'طريقة العرض',
    'academy.quizzes.display_list': 'قائمة',
    'academy.quizzes.display_assistant': 'مساعد (سؤال في كل مرة)',
    'academy.quizzes.form.start_date': 'تاريخ البداية',
    'academy.quizzes.form.end_date': 'تاريخ النهاية',
    'academy.quizzes.form.whatsapp': 'واتساب (للتواصل)',
    'academy.quizzes.form.register_link': 'رابط التسجيل',
    'academy.quizzes.form.description': 'الوصف',
    'academy.quizzes.val_title': 'عنوان الاختبار مطلوب.',
    'academy.quizzes.val_duration': 'يجب أن تكون المدة أكبر من 0.',
    'academy.quizzes.val_date_range': 'يجب أن يكون تاريخ البداية قبل تاريخ النهاية.',
    'academy.quizzes.error_load': 'خطأ في تحميل الاختبارات',
    'academy.quizzes.error_delete': 'خطأ في حذف الاختبار',
    'academy.quizzes.error_save_level': 'خطأ في حفظ المستوى',
    'academy.quizzes.error_delete_level': 'خطأ في حذف المستوى',
    'academy.quizzes.error_load_levels': 'خطأ في تحميل المستويات',
    'academy.quizzes.share.title': 'مشاركة: {0}',
    'academy.quizzes.share.error': 'تعذر إنشاء الرابط العام.',
    'academy.quizzes.share.error_generic': 'حدث خطأ ما.',
    'academy.quizzes.share.url_label': 'الرابط العام للاختبار',
    'academy.quizzes.share.copy': 'نسخ',
    'academy.quizzes.share.copied': 'تم النسخ!',
    'academy.quizzes.share.whatsapp': 'مشاركة عبر واتساب',
    'academy.quizzes.share.whatsapp_text': 'اختبار مستوى - قم بإجراء الاختبار: ',
    'academy.quizzes.share.regenerate': 'توليد رابط جديد',
    'academy.quizzes.share.generate': 'توليد الرابط',
    'academy.quizzes.share.generating': 'جارٍ التوليد...',
    'academy.quizzes.share.empty_title': 'رابط عام',
    'academy.quizzes.share.empty_desc': 'قم بتوليد رابط عام لمشاركته مع المرشحين.',
    'academy.quiz_questions.title': 'أسئلة الاختبار',
    'academy.quiz_questions.back_to_quizzes': 'العودة إلى الاختبارات',
    'academy.quiz_questions.add_new': 'سؤال جديد',
    'academy.quiz_questions.select_quiz': 'الاختبار',
    'academy.quiz_questions.select_quiz_placeholder': 'اختر اختباراً',
    'academy.quiz_questions.no_quiz_selected': 'اختر اختباراً لإدارة أسئلته.',
    'academy.quiz_questions.search_placeholder': 'البحث عن سؤال...',
    'academy.quiz_questions.q_count': '{0} سؤال',
    'academy.quiz_questions.total_points': '{0} نقطة إجمالاً',
    'academy.quiz_questions.table.order': 'الترتيب',
    'academy.quiz_questions.table.text': 'السؤال',
    'academy.quiz_questions.table.correct': 'الإجابة الصحيحة',
    'academy.quiz_questions.table.points': 'النقاط',
    'academy.quiz_questions.dialog.new': 'سؤال جديد',
    'academy.quiz_questions.dialog.edit': 'تعديل السؤال',
    'academy.quiz_questions.form.text': 'نص السؤال',
    'academy.quiz_questions.form.order': 'الترتيب',
    'academy.quiz_questions.form.points': 'النقاط',
    'academy.quiz_questions.confirm_delete': 'حذف هذا السؤال؟',
    'academy.quiz_questions.val_text': 'نص السؤال مطلوب.',
    'academy.quiz_questions.error_load': 'خطأ في تحميل الأسئلة',
    'academy.quiz_questions.error_save': 'خطأ في حفظ السؤال',
    'academy.quiz_questions.error_delete': 'خطأ في حذف السؤال',
    'academy.quiz_questions.table.type': 'النوع',
    'academy.quiz_questions.table.manual': 'يدوي',
    'academy.quiz_questions.form.type': 'نوع السؤال',
    'academy.quiz_questions.form.type_single': 'اختيار واحد (راديو)',
    'academy.quiz_questions.form.type_multi': 'اختيار متعدد (مربعات)',
    'academy.quiz_questions.form.type_boolean': 'صح / خطأ',
    'academy.quiz_questions.form.type_text': 'إجابة نصية حرة',
    'academy.quiz_questions.form.options': 'الخيارات',
    'academy.quiz_questions.form.option_label': 'نص الخيار',
    'academy.quiz_questions.form.correct': 'صحيحة',
    'academy.quiz_questions.form.add_option': 'إضافة خيار',
    'academy.quiz_questions.form.multiple_correct': 'عدة إجابات صحيحة',
    'academy.quiz_questions.form.true_label': 'صح',
    'academy.quiz_questions.form.false_label': 'خطأ',
    'academy.quiz_questions.form.text_hint': 'سؤال إجابة حرة: يكتب المرشح إجابته. يتم تقييمه يدوياً ولا يُحتسب في النتيجة التلقائية.',
    'academy.quiz_questions.val_options': 'أضف خيارين على الأقل مع نص لكل منهما.',
    'academy.quiz_questions.val_correct': 'اختر إجابة صحيحة واحدة على الأقل.',
    'academy.quiz_public.text_answer_placeholder': 'إجابتك...',
    'academy.quiz_results.title': 'نتائج الاختبار',
    'academy.quiz_results.back_to_quizzes': 'العودة إلى الاختبارات',
    'academy.quiz_results.select_quiz': 'الاختبار',
    'academy.quiz_results.select_quiz_placeholder': 'اختر اختباراً',
    'academy.quiz_results.no_quiz_selected': 'اختر اختباراً لعرض النتائج.',
    'academy.quiz_results.search_placeholder': 'البحث عن مشارك...',
    'academy.quiz_results.count': '{0} محاولة',
    'academy.quiz_results.avg': 'المعدل: {0}',
    'academy.quiz_results.table.ref': 'المرجع',
    'academy.quiz_results.table.name': 'المشارك',
    'academy.quiz_results.table.phone': 'الهاتف',
    'academy.quiz_results.table.email': 'البريد',
    'academy.quiz_results.table.score': 'النقاط',
    'academy.quiz_results.table.level': 'المستوى',
    'academy.quiz_results.table.duration': 'المدة',
    'academy.quiz_results.table.date': 'التاريخ',
    'academy.quiz_results.error_load': 'خطأ في تحميل النتائج',
    'academy.menu.traffic': 'سجل API',
    'traffic.title': 'سجل API',
    'traffic.error_load': 'خطأ في تحميل السجل',
    'traffic.refresh': 'تحديث',
    'traffic.close': 'إغلاق',
    'traffic.details': 'التفاصيل',
    'traffic.details_title': 'تفاصيل الطلب',
    'traffic.anomaly': 'شذوذ',
    'traffic.anomaly_flags': 'الحالات الشاذة المكتشفة',
    'traffic.request_body': 'محتوى الطلب',
    'traffic.correlation_id': 'معرف الارتباط',
    'traffic.search_placeholder': 'بحث (مسار، IP، بريد...)',
    'traffic.stats.total': 'الطلبات',
    'traffic.stats.anomalies': 'الحالات الشاذة',
    'traffic.stats.server_errors': 'أخطاء 5xx',
    'traffic.stats.auth_failures': 'فشل تسجيل الدخول',
    'traffic.stats.not_found': '404',
    'traffic.stats.rate_limited': 'تقييد المعدل',
    'traffic.stats.avg_duration': 'متوسط المدة',
    'traffic.stats.distinct_ips': 'عناوين IP',
    'traffic.filter.period': 'الفترة',
    'traffic.filter.last_hour': 'آخر ساعة',
    'traffic.filter.last_24h': 'آخر 24 ساعة',
    'traffic.filter.last_7d': 'آخر 7 أيام',
    'traffic.filter.last_30d': 'آخر 30 يومًا',
    'traffic.filter.status': 'الحالة',
    'traffic.filter.all': 'الكل',
    'traffic.filter.anomalies_only': 'الشذوذ فقط',
    'traffic.filter.count': '{0} سجل',
    'traffic.table.date': 'التاريخ',
    'traffic.table.method': 'الطريقة',
    'traffic.table.path': 'المسار',
    'traffic.table.status': 'الحالة',
    'traffic.table.duration': 'المدة',
    'traffic.table.ip': 'IP',
    'traffic.table.user': 'المستخدم',
    'traffic.table.source': 'المصدر',
    'traffic.table.anomalies': 'الشذوذ',
    'traffic.table.user_agent': 'User-Agent',
    'academy.menu.user_actions': 'إجراءات المستخدم',
    'useraction.title': 'إجراءات المستخدم',
    'useraction.error_load': 'خطأ في تحميل الإجراءات',
    'useraction.refresh': 'تحديث',
    'useraction.close': 'إغلاق',
    'useraction.details': 'التفاصيل',
    'useraction.details_title': 'تفاصيل الإجراء',
    'useraction.request_body': 'محتوى الطلب',
    'useraction.correlation_id': 'معرف الارتباط',
    'useraction.search_placeholder': 'بحث (مسار، بريد، IP...)',
    'useraction.stats.total': 'الإجراءات',
    'useraction.stats.connected': 'متصل',
    'useraction.stats.public': 'عام',
    'useraction.stats.internal': 'داخلي',
    'useraction.stats.avg_duration': 'متوسط المدة',
    'useraction.stats.server_errors': 'أخطاء 5xx',
    'useraction.stats.distinct_users': 'المستخدمون',
    'useraction.stats.distinct_ips': 'عناوين IP',
    'useraction.stats.distinct_okeys': 'OKeys',
    'useraction.filter.period': 'الفترة',
    'useraction.filter.last_hour': 'آخر ساعة',
    'useraction.filter.last_24h': 'آخر 24 ساعة',
    'useraction.filter.last_7d': 'آخر 7 أيام',
    'useraction.filter.last_30d': 'آخر 30 يومًا',
    'useraction.filter.status': 'الحالة',
    'useraction.filter.all': 'الكل',
    'useraction.filter.connected_only': 'المتصلون فقط',
    'useraction.filter.source': 'المصدر',
    'useraction.filter.method': 'الطريقة',
    'useraction.filter.okey': 'OKey',
    'useraction.filter.count': '{0} سجل',
    'useraction.table.date': 'التاريخ',
    'useraction.table.method': 'الطريقة',
    'useraction.table.path': 'المسار',
    'useraction.table.okey': 'OKey',
    'useraction.table.status': 'الحالة',
    'useraction.table.duration': 'المدة',
    'useraction.table.ip': 'IP',
    'useraction.table.user': 'المستخدم',
    'useraction.table.source': 'المصدر',
    'useraction.table.user_agent': 'User-Agent',
    'useraction.table.token_hash': 'الرمز (hash)',
    'useraction.table.error': 'خطأ',
    'useraction.table.cnx': 'الاتصال',
    'useraction.times.start': 'البداية',
    'useraction.times.end': 'النهاية',
    'academy.menu.user_connexions': 'اتصالات المستخدم',
    'userconnex.title': 'اتصالات المستخدم',
    'userconnex.refresh': 'تحديث',
    'userconnex.back_to_list': 'العودة إلى القائمة',
    'userconnex.close': 'إغلاق',
    'userconnex.detail': 'التفاصيل',
    'userconnex.load_actions': 'تحميل الإجراءات',
    'userconnex.actions': 'إجراءات',
    'userconnex.actions_title': 'إجراءات الاتصال',
    'userconnex.actions_empty': 'لا توجد إجراءات مسجلة لهذا الاتصال.',
    'userconnex.details_title': 'تفاصيل الاتصال',
    'userconnex.error_no_user': 'يرجى اختيار مستخدم.',
    'userconnex.error_load': 'خطأ في تحميل الاتصالات',
    'userconnex.error_load_actions': 'خطأ في تحميل الإجراءات',
    'userconnex.no_data': 'لا توجد اتصالات لهذا المستخدم في هذه الفترة.',
    'userconnex.search_placeholder': 'بحث (مسار، حالة...)',
    'userconnex.filter.user': 'المستخدم',
    'userconnex.filter.user_placeholder': 'ابحث عن مستخدم',
    'userconnex.filter.start': 'تاريخ البداية',
    'userconnex.filter.end': 'تاريخ النهاية',
    'userconnex.filter.load': 'تحميل',
    'userconnex.filter.count': '{0} اتصال',
    'userconnex.table.cnx': 'الاتصال',
    'userconnex.table.start': 'البداية',
    'userconnex.table.end': 'النهاية',
    'userconnex.table.state': 'الحالة',
    'userconnex.table.app': 'التطبيق',
    'userconnex.table.dossier': 'الملف',
    'userconnex.table.actions_count': 'الإجراءات',
    'userconnex.table.date': 'التاريخ',
    'userconnex.table.method': 'الطريقة',
    'userconnex.table.path': 'المسار',
    'userconnex.table.status': 'الحالة',
    'userconnex.table.duration': 'المدة',
    'userconnex.field.num': 'رقم الاتصال',
    'userconnex.field.start': 'تاريخ البداية',
    'userconnex.field.end': 'تاريخ النهاية',
    'userconnex.field.last_activity': 'آخر نشاط',
    'userconnex.field.app': 'التطبيق',
    'userconnex.field.dossier': 'الملف',
    'userconnex.field.ip': 'IP',
    'userconnex.field.host': 'المضيف',
    'userconnex.field.computer': 'الكمبيوتر',
    'userconnex.field.language': 'اللغة',
    'userconnex.field.referer': 'المصدر',
    'userconnex.field.wid': 'معرّف الجلسة',
    'userconnex.field.text': 'النص',
    'common.copyright': '© 2026 BLOBTIC. جميع الحقوق محفوظة.',
    'academy.prereg.page.error_contact_admin_help': 'يرجى الاتصال بإدارة المؤسسة للحصول على رابط تسجيل أولي صالح.',
    'academy.prereg.page.establishment_label': 'المؤسسة: {0}',
    'academy.quiz_public.offered_by': 'اختبار مقدم من {0}',
    'academy.students.portal_section': 'الوصول إلى بوابة الطالب وولي الأمر',
    'academy.students.portal_active': 'تفعيل الوصول إلى البوابة',
    'academy.students.portal_password': 'كلمة مرور البوابة',
    'academy.students.portal_generate_pwd': 'إنشاء تلقائي',
    'academy.students.portal_copy_link': 'نسخ الرابط العام',
    'academy.students.portal_link_copied': 'تم نسخ رابط البوابة!',
    'academy.students.portal_badge_active': 'البوابة مفعلة',
    'academy.students.portal_badge_inactive': 'البوابة معطلة',
    'academy.students.portal_login_hint': 'يمكن للطالب أو ولي أمره تسجيل الدخول بالبريد الإلكتروني أو الهاتف أو كود الطالب مع كلمة المرور هذه.',
    'academy.teachers.portal_section': 'الوصول إلى بوابة الأستاذ',
    'academy.teachers.portal_active': 'تفعيل الوصول إلى البوابة',
    'academy.teachers.portal_can_attendance': 'السماح بتسجيل الحضور ومسح الشارات',
    'academy.teachers.portal_password': 'كلمة مرور البوابة',
    'academy.teachers.portal_generate_pwd': 'إنشاء تلقائي',
    'academy.teachers.portal_copy_link': 'نسخ الرابط العام',
    'academy.teachers.portal_link_copied': 'تم نسخ رابط البوابة!',
    'academy.teachers.portal_badge_active': 'البوابة مفعلة',
    'academy.teachers.portal_badge_inactive': 'البوابة معطلة',
    'academy.teachers.portal_login_hint': 'يمكن للأستاذ تسجيل الدخول بالبريد الإلكتروني أو الهاتف مع كلمة المرور هذه.',
    'academy.students.validation.first_name_required': 'الاسم الأول مطلوب.',
    'academy.students.validation.last_name_required': 'اسم العائلة مطلوب.',
    'academy.students.validation.gender_required': 'الجنس مطلوب.',
    'academy.students.validation.dob_required': 'تاريخ الميلاد مطلوب.',
    'academy.students.validation.health_issues_required': 'يرجى تحديد المشاكل الصحية.',
    'academy.students.validation.allergies_required': 'يرجى تحديد الحساسية.',
    'academy.students.validation.guardian_first_name_required': 'الاسم الأول للولي مطلوب.',
    'academy.students.validation.guardian_last_name_required': 'اسم عائلة الولي مطلوب.',
    'academy.students.validation.guardian_relation_required': 'صلة القرابة بالطفل مطلوبة.',
    'academy.students.validation.guardian_phone_required': 'هاتف الولي مطلوب.',
    'academy.students.validation.image_right_required': 'الموافقة على الحقوق الصورية مطلوبة.',
    'academy.teachers.validation.email_required': 'البريد الإلكتروني مطلوب.',
    'academy.teachers.validation.phone_required': 'رقم الهاتف مطلوب.',
    'academy.teachers.validation.specialization_required': 'التخصص مطلوب.',
    'academy.teachers.validation.hourly_rate_required': 'مبلغ التسعيرة مطلوب.',
    'academy.prereg.page.health_question': 'هل يعاني الطفل من أي مشاكل صحية أو أمراض مزمنة؟',
    'academy.prereg.page.health_explanation': 'تساعد هذه المعلومات طاقم الأكاديمية على ضمان سلامة وراحة طفلك أثناء جميع الأنشطة.',
    'academy.prereg.page.health_specify_label': '(إذا كان نعم) حدد الحالة:',
    'academy.prereg.page.condition_asthma': 'الربو',
    'academy.prereg.page.condition_diabetes': 'السكري',
    'academy.prereg.page.condition_epilepsy': 'الصرع',
    'academy.prereg.page.condition_other': 'آخر',
    'academy.prereg.page.health_placeholder': 'يرجى تحديد الحالة الطبية أو الاحتياطات اللازمة...',
    'academy.prereg.page.allergies_header': 'الحساسية المعروفة',
    'academy.prereg.page.allergies_question': 'هل يعاني الطفل من أي حساسيات معروفة (غذائية، دوائية، إلخ)؟',
    'academy.prereg.page.allergies_explanation': 'يرجى تدوين أي حساسيات طعام أو بيئة لتفادي المخاطر أثناء الوجبات والأنشطة.',
    'academy.prereg.page.allergies_placeholder': 'حدد الحساسيات (مثل: الفول السوداني، اللكتوز، البنسلين...)',
    'academy.prereg.page.image_rights_header': 'حقوق استخدام الصور',
    'academy.prereg.page.image_rights_question': 'هل تسمح بالتقاط ونشر صور/فيديوهات الطالب؟',
    'academy.prereg.page.image_rights_explanation': 'إذن لالتقاط صور وفيديوهات الطالب ضمن الأنشطة التعليمية والمنشورات الرسمية للأكاديمية.',
    'academy.prereg.page.yes': 'نعم',
    'academy.prereg.page.no': 'لا',
    'academy.prereg.time_slot.morning': 'الصباح (08:00 - 12:00)',
    'academy.prereg.time_slot.afternoon': 'بعد الظهر (13:00 - 17:00)',
    'academy.prereg.time_slot.evening': 'المساء (17:00 - 20:00)',
    'academy.prereg.time_slot.weekend': 'عطلة نهاية الأسبوع',
    'academy.prereg.time_slot.flexible': 'مرن / حسب الطلب',
    'academy.prereg.page.select_course_required': 'يرجى اختيار دورة تدريبية.',
    'academy.prereg.page.select_time_preference_required': 'يرجى اختيار التوقيت المفضل.',
    'academy.quizzes.form.auto_next': 'الانتقال التلقائي إلى السؤال التالي عند اختيار إجابة',
    'academy.quizzes.form.max_attempts_link': 'الحد الأقصى لإجمالي المحاولات عبر الرابط',
    'academy.quizzes.form.max_attempts_user': 'الحد الأقصى للمحاولات لكل شخص (IP / الهاتف)',
    'academy.quiz_public.public_portal': 'بوابة الاختبارات العامة',
    'academy.quiz_public.error_invalid_token': 'رابط اختبار غير صالح أو منتهي الصلاحية.',
    'academy.quiz_public.error_load_failed': 'تعذر تحميل الاختبار.',
    'academy.quiz_public.error_no_token': 'لا يوجد رمز اختبار.',
    'academy.quiz_public.error_submit_failed': 'فشل إرسال الإجابات.',
    'academy.quiz_public.error_submit_generic': 'حدث خطأ أثناء الإرسال.',
    'academy.quiz_public.error_contact_required': 'يرجى إدخال الاسم ورقم الهاتف.',
    'academy.quiz_public.error_all_questions': 'يرجى الإجابة على جميع الأسئلة.',
    'academy.quiz_public.error_name_invalid': 'يرجى إدخال اسم كامل صحيح (3 أحرف على الأقل).',
    'academy.quiz_public.error_phone_invalid': 'رقم الهاتف غير صحيح (8 أرقام على الأقل).',
    'academy.quiz_public.error_email_invalid': 'البريد الإلكتروني غير صحيح (مثال: name@domain.com).',
    'academy.quiz_public.duration_label': 'المدة: {0} دقيقة',
    'academy.quiz_public.questions_count': '{0} سؤال',
    'academy.quiz_public.contact_header': 'معلوماتك',
    'academy.quiz_public.full_name': 'الاسم الكامل',
    'academy.quiz_public.phone': 'الهاتف',
    'academy.quiz_public.email': 'البريد الإلكتروني',
    'academy.quiz_public.age_band': 'الفئة العمرية',
    'academy.quiz_public.age_band_under_12': 'أقل من 12 سنة',
    'academy.quiz_public.age_band_12_15': '12 - 15 سنة',
    'academy.quiz_public.age_band_16_25': '16 - 25 سنة',
    'academy.quiz_public.age_band_26_35': '26 - 35 سنة',
    'academy.quiz_public.age_band_36_50': '36 - 50 سنة',
    'academy.quiz_public.age_band_over_50': 'أكثر من 50 سنة',
    'academy.quiz_public.start_btn': 'ابدأ الاختبار',
    'academy.quiz_public.submit_btn': 'إرسال إجاباتي',
    'academy.quiz_public.continue_btn': 'متابعة',
    'academy.quiz_public.details_subtitle': 'أدخل معلوماتك لعرض نتيجتك.',
    'academy.quiz_public.submitting_btn': 'جارٍ الإرسال...',
    'academy.quiz_public.prev_btn': 'السابق',
    'academy.quiz_public.next_btn': 'التالي',
    'academy.quiz_public.question_of': 'السؤال {0} من {1}',
    'academy.quiz_public.result_title': 'انتهى الاختبار!',
    'academy.quiz_public.result_ref': 'المرجع: {0}',
    'academy.quiz_public.result_score': 'النقاط: {0} / {1}',
    'academy.quiz_public.result_level': 'مستواك: {0}',
    'academy.quiz_public.whatsapp_btn': 'التواصل مع المؤسسة',
    'academy.quiz_public.whatsapp_result_text': 'أجريت اختبار المستوى (مرجع {0}). أرغب في التسجيل.',
    'academy.quiz_public.register_btn': 'التسجيل في دورة تدريبية',

    // Academy Application Strings
    'academy.dashboard.title': 'لوحة التحكم والمراقبة الرئيسية',
    'academy.dashboard.subtitle': 'ملخص فوري لصحة الأكاديمية ونشاطها.',
    'academy.dashboard.kpi.active_students': 'إجمالي الطلاب النشطين',
    'academy.dashboard.kpi.today_prereg': 'التسجيلات المسبقة اليوم',
    'academy.dashboard.kpi.today_schedule': 'جدول اليوم',
    'academy.dashboard.kpi.low_attendance': 'تنبيه حضور منخفض',
    'academy.dashboard.kpi.on_hold': '— {0} موقوف{1}',
    'academy.dashboard.kpi.teachers': 'إجمالي الأساتذة',
    'academy.dashboard.kpi.rooms': 'إجمالي القاعات المتاحة',
    'academy.dashboard.kpi.monthly_revenue': 'مداخيل الشهر الحالي',
    'academy.dashboard.kpi.courses': 'الدورات والتدريب',
    'academy.dashboard.kpi.groups': 'المجموعات الدراسية',
    'academy.dashboard.kpi.sessions': 'الحصص المبرمجة',
    'academy.dashboard.kpi.quizzes': 'الاختبارات والتقييمات',
    'academy.dashboard.kpi.revenue': 'الإيرادات المحصلة',
    'academy.dashboard.kpi.pending_invoices': 'الفواتير المعلقة',
    'academy.dashboard.kpi.pending_prereg': 'التسجيلات المسبقة المعلقة',
    'academy.dashboard.kpi.accepted_prereg': 'التسجيلات المسبقة المقبولة',
    'academy.dashboard.kpi.refused_prereg': 'التسجيلات المسبقة المرفوضة',
    'academy.dashboard.kpi.desc.active_students': 'الطلاب المسجلون والنشطون',
    'academy.dashboard.kpi.desc.teachers': 'المعلمون النشطون',
    'academy.dashboard.kpi.desc.courses': 'الكتالوج المتوفر',
    'academy.dashboard.kpi.desc.groups': 'المجموعات الجارية',
    'academy.dashboard.kpi.desc.rooms': 'القاعات النشطة',
    'academy.dashboard.kpi.desc.sessions': 'جميع الحصص المخططة',
    'academy.dashboard.kpi.desc.quizzes': 'الاختبارات المتاحة',
    'academy.dashboard.kpi.desc.revenue': 'المدفوعات المستلمة',
    'academy.dashboard.kpi.desc.pending_invoices': 'غير مدفوعة أو جزئية',
    'academy.dashboard.kpi.desc.pending_prereg': 'للمعالجة',
    'academy.dashboard.kpi.desc.accepted_prereg': 'مؤكدة',
    'academy.dashboard.kpi.desc.refused_prereg': 'مرفوضة',
    'academy.dashboard.kpi.desc.today_prereg': 'مستلمة اليوم',
    'academy.dashboard.kpi.desc.today_schedule': 'الحصص والحضور',
    'academy.dashboard.kpi.desc.low_attendance': 'أمس، أقل من 75% حضور',
    'academy.dashboard.widget.outstanding_debt': 'إجمالي الديون المتبقية (للتحصيل)',
    'academy.dashboard.schedule.today': 'جدول حصص اليوم',
    'academy.dashboard.alerts.low_attendance': 'تنبيه انخفاض نسب الحضور (<75%)',
    'academy.dashboard.refresh_btn': 'تحديث ({0}د {1}ث)',
    'academy.dashboard.init_btn': 'تهيئة',
    'academy.dashboard.init_tooltip': 'إعادة تعيين ذاكرة المحلية',
    'academy.dashboard.kpi.sub_active_students': 'المسجلون في المجموعات النشطة',
    'academy.dashboard.kpi.sub_teachers': 'أعضاء هيئة التدريس النشطون',
    'academy.dashboard.kpi.sub_rooms': 'قاعات التعليم المتاحة',
    'academy.dashboard.kpi.sub_monthly_revenue': 'المبالغ المحصلة في الشهر الحالي',
    'academy.dashboard.widget.debt_desc': 'إجمالي الفواتير غير المحصلة لجميع الطلاب المسجلين.',
    'academy.dashboard.widget.manage_debtors': 'اضغط لإدارة تذكيرات الديون والمدفوعات ←',
    'academy.dashboard.status.healthy': 'سليم',
    'academy.dashboard.status.critical': 'تنبيه ديون حرجة',
    'academy.dashboard.status.warning': 'تحذير ديون متوسطة',
    'academy.groups.status.upcoming': 'قادمة',
    'academy.groups.status.ongoing': 'جارية',
    'academy.groups.status.completed': 'مكتملة',
    'academy.groups.title': 'تخطيط المجموعات والدفعات',
    'academy.groups.subtitle': 'لوحة كانبان للدفعات والدورات والفوترة التلقائية.',
    'academy.groups.add_new': 'إنشاء دفعة',
    'academy.groups.list.grid': 'شبكة',
    'academy.groups.list.table': 'جدول',
    'academy.groups.confirm_delete': 'هل تريد حذف الدفعة « {0} »؟',
    'academy.groups.section_info': 'معلومات الدفعة',
    'academy.groups.section_dossier': 'معلومات الملف',
    'academy.groups.back_to_list': 'العودة إلى القائمة',
    'academy.groups.search_placeholder': 'البحث بالاسم، الدورة، الأستاذ...',
    'academy.groups.table.name': 'الاسم',
    'academy.groups.table.course': 'قالب الدورة',
    'academy.groups.table.teacher': 'الأستاذ',
    'academy.groups.table.status': 'الحالة',
    'academy.groups.table.enrolled': 'المسجلون',
    'academy.groups.table.actions': 'إجراءات',
    'academy.groups.form.dossier_code': 'رمز الملف',
    'academy.groups.form.dossier_lib': 'الملف',
    'academy.groups.val_name': 'اسم المجموعة إلزامي.',
    'academy.groups.val_course': 'يرجى اختيار قالب دورة.',
    'academy.groups.val_teacher': 'يرجى اختيار أستاذ.',
    'academy.groups.val_capacity': 'يجب أن تكون السعة أكبر من 0.',
    'academy.groups.course': 'الدورة:',
    'academy.groups.instructor': 'الأستاذ:',
    'academy.groups.unassigned': 'غير معين',
    'academy.groups.timeline': 'الفترة:',
    'academy.groups.enrolled_count': 'المسجلون: {0} / {1} طالب',
    'academy.groups.bulk_enroll': 'تسجيل جماعي',
    'academy.groups.move_stage': 'تغيير المرحلة:',
    'academy.groups.complete_group': 'إغلاق المجموعة',
    'academy.groups.create_title': 'إنشاء دفعة تعليمية جديدة',
    'academy.groups.edit_title': 'تعديل الدفعة',
    'academy.groups.name_label': 'اسم المجموعة (مثال: Fullstack Web - سبتمبر 2026)',
    'academy.groups.course_template': 'قالب الدورة',
    'academy.groups.primary_teacher': 'الأستاذ الرئيسي',
    'academy.groups.max_capacity': 'السعة القصوى (طلاب)',
    'academy.groups.start_date': 'تاريخ البداية',
    'academy.groups.expected_end_date': 'تاريخ النهاية المتوقع',
    'academy.groups.enroll_title': 'تسجيل جماعي: {0}',
    'academy.groups.enroll_desc': 'حدد طالباً لتسجيله فوراً، أو ألغ التحديد لإزالته. يتم إنشاء فاتورة تلقائياً ({0}).',
    'academy.groups.enroll_search_placeholder': 'البحث عن طالب (الاسم، البريد الإلكتروني، الهاتف)...',
    'academy.groups.enroll_no_results': 'لا يوجد طلاب يطابقون بحثك.',
    'academy.groups.enroll_assigned_count': '{0} / {1} مسجل(ين)',
    'academy.groups.confirm_enroll': 'تأكيد التسجيلات',
    'academy.groups.enroll_success': 'تم تسجيل {0} طالب(اً) بنجاح. تم إنشاء الفواتير.',
    'academy.groups.enroll_none_selected': 'لم يتم اختيار طلاب جدد للتسجيل.',
    'academy.groups.error.enroll': 'فشل تسجيل طالب أو أكثر.',
    'academy.groups.error.unenroll': 'فشل إلغاء التسجيل.',
    'academy.groups.error.unenroll_has_payments': 'لا يمكن إلغاء تسجيل هذا الطالب: فاتورة مرتبطة تحتوي على مدفوعات.',
    'academy.students.status.enrolled': 'مسجل',
    'academy.students.status.dropped': 'منسحب',
    'days.sun': 'الأحد',
    'days.mon': 'الإثنين',
    'days.tue': 'الثلاثاء',
    'days.wed': 'الأربعاء',
    'days.thu': 'الخميس',
    'days.fri': 'الجمعة',
    'days.sat': 'السبت',
    'days.short.sun': 'أحد',
    'days.short.mon': 'إثن',
    'days.short.tue': 'ثلا',
    'days.short.wed': 'أرب',
    'days.short.thu': 'خميس',
    'days.short.fri': 'جمعة',
    'days.short.sat': 'سبت',
    'days.single.sun': 'ح',
    'days.single.mon': 'ن',
    'days.single.tue': 'ث',
    'days.single.wed': 'ر',
    'days.single.thu': 'خ',
    'days.single.fri': 'ج',
    'days.single.sat': 'س',
    'academy.dashboard.schedule.today_title': 'جدول اليوم ({0} حصص)',
    'academy.dashboard.schedule.full_scheduler': 'الجدول الكامل',
    'academy.dashboard.schedule.empty': 'لا توجد حصص مبرمجة لليوم.',
    'academy.dashboard.schedule.instructor': 'الأستاذ:',
    'academy.dashboard.schedule.room': 'القاعة:',
    'academy.dashboard.schedule.att_recorded': 'تم تسجيل الحضور',
    'academy.dashboard.schedule.take_att': 'تسجيل الحضور',
    'academy.dashboard.alerts.title': 'تنبيه انخفاض نسب الحضور (<75%)',
    'academy.dashboard.alerts.subtitle': 'حصص الأمس التي يقل معدل الحضور فيها عن الحد الأدنى المطلوب.',
    'academy.dashboard.alerts.all_clear': 'كل شيء ممتاز!',
    'academy.dashboard.alerts.all_clear_sub': 'لا توجد تنبيهات لانخفاض الحضور لحصص الأمس.',
    'academy.dashboard.alerts.rate': 'نسبة الحضور {0}%',
    'academy.dashboard.alerts.present_count': 'الحاضرون: {0} / {1} طالب',
    'academy.dashboard.prereg.stats_pending': 'التسجيلات الأولية قيد الانتظار',
    'academy.dashboard.prereg.stats_accepted': 'التسجيلات الأولية المقبولة',
    'academy.dashboard.prereg.stats_refused': 'التسجيلات الأولية المرفوضة',
    'academy.dashboard.prereg.stats_sub_pending': 'طلبات عامة بانتظار المراجعة',
    'academy.dashboard.prereg.stats_sub_accepted': 'طلبات تمت الموافقة عليها وتسجيلها',
    'academy.dashboard.prereg.stats_sub_refused': 'طلبات رفضتها الإدارة',
    'academy.dashboard.prereg.today_title': 'التسجيلات الأولية لليوم ({0})',
    'academy.dashboard.prereg.today_empty': 'لم يتم استلام أي تسجيل أولي اليوم.',
    'academy.dashboard.prereg.view_all': 'عرض الطلاب',
    'academy.dashboard.prereg.course': 'التكوين',
    'academy.dashboard.prereg.time_preference': 'الوقت المفضل',

    'academy.search.placeholder': 'البحث الشامل عن طالب (الاسم أو البريد)...',
    'academy.search.debt': 'الدين:',
    'academy.search.financial_overview': 'الملخص المالي',
    'academy.search.invoiced': 'المفطورة',
    'academy.search.total_paid': 'المبلغ المدفوع',
    'academy.search.rest_to_pay': 'المبلغ المتبقي',
    'academy.search.dob': 'تاريخ الميلاد',
    'academy.search.emergency_contact': 'جهة الاتصال في الطوارئ',
    'academy.search.hold_active': 'الحظر المالي مفعّل',
    'academy.students.title': 'إدارة الطلاب والتسجيلات',
    'academy.students.subtitle': 'ملفات الطلاب، سجل التسجيلات وحسابات الحظر المالي.',
    'academy.students.add_new': 'تسجيل طالب',
    'academy.students.firstname': 'الاسم الأول',
    'academy.students.lastname': 'اللقب',
    'academy.students.email': 'البريد الإلكتروني',
    'academy.students.phone': 'الهاتف',
    'academy.students.financial_hold': 'الحظر المالي',
    'academy.students.hold_badge': 'حظر مالي',
    'academy.students.active_badge': 'نشط / نظامي',
    'academy.students.search_placeholder': 'البحث عن طالب...',
    'academy.students.table.name': 'اسم الطالب',
    'academy.students.prereg_link': 'رابط التسجيل الأولي',

    // Attendance & Absence Justification (AR)
    'academy.attendance.justified': 'غياب مبرر',
    'academy.attendance.unjustified': 'غير مبرر',
    'academy.attendance.justify': 'تبرير الغياب',
    'academy.attendance.view_justification': 'عرض التبرير',
    'academy.attendance.justification_details': 'تفاصيل تبرير الغياب',
    'academy.attendance.reason_label': 'سبب الغياب',
    'academy.attendance.reason_placeholder': 'حدد السبب الطبي أو الشخصي...',
    'academy.attendance.doc_label': 'المستند المرفق (شهادة طبية، وصفة...)',
    'academy.attendance.download_doc': 'تحميل المستند',
    'academy.attendance.no_doc': 'لا يوجد مستند مرفق',
    'academy.attendance.submission_date': 'تاريخ التبرير',
    'academy.attendance.save_justification': 'حفظ التبرير',
    'academy.attendance.justification_saved': 'تم حفظ التبرير بنجاح.',
    'academy.attendance.filter_all': 'الكل',
    'academy.attendance.filter_present': 'حاضرون',
    'academy.attendance.filter_late': 'متأخرون',
    'academy.attendance.filter_absent': 'كل الغائبين',
    'academy.attendance.filter_absent_justified': 'غائبون مبررون',
    'academy.attendance.filter_absent_unjustified': 'غائبون غير مبررين',
    'academy.attendance.filter_excused': 'معذورون',

    // Pre-Registration Modal & Page (AR)
    'academy.prereg.modal.title': 'إنشاء رابط التسجيل الأولي للطلاب',
    'academy.prereg.modal.subtitle': 'أنشئ رابط تسجيل عام لوكالتك لمشاركته على وسائل التواصل الاجتماعي، موقع الويب أو عبر الرسائل/واتساب.',
    'academy.prereg.modal.error_generate': 'تعذر إنشاء الرابط العام.',
    'academy.prereg.modal.error_generic': 'حدث خطأ أثناء إنشاء الرابط.',
    'academy.prereg.modal.custom_link_title': 'رابط عام مخصص',
    'academy.prereg.modal.custom_link_desc': 'سيقوم النموذج بتوجيه الطالب عبر مساعد ذكي (ناشئ / بالغ) وتسجيل طلبه تلقائياً في حساب وكالتك.',
    'academy.prereg.modal.btn_generate': 'إنشاء الرابط العام',
    'academy.prereg.modal.btn_generating': 'جاري الإنشاء...',
    'academy.prereg.modal.success': 'تم إنشاء الرابط العام بنجاح لوكالتك!',
    'academy.prereg.modal.url_label': 'رابط النموذج العام',
    'academy.prereg.modal.tooltip_copy': 'نسخ الرابط',
    'academy.prereg.modal.tooltip_copied': 'تم النسخ!',
    'academy.prereg.modal.btn_copy': 'نسخ الرابط',
    'academy.prereg.modal.btn_copied': 'تم نسخ الرابط!',
    'academy.prereg.modal.btn_whatsapp': 'مشاركة عبر واتساب',
    'academy.prereg.modal.whatsapp_text': 'نموذج التسجيل الأولي: ',
    'academy.prereg.modal.is_existing_notice': 'رابط التسجيل الأولي الفعّال لوكالتك:',
    'academy.prereg.modal.btn_regenerate': 'إنشاء رابط جديد',
    'academy.prereg.page.public_portal': 'البوابة العامة للتسجيل الأولي للطلاب',
    'academy.prereg.page.status_junior': 'الحالة: ناشئ (أقل من 16 سنة)',
    'academy.prereg.page.status_adult': 'الحالة: بالغ (16 سنة فأكثر)',
    'academy.prereg.page.step1_title': 'المعلومات الشخصية',
    'academy.prereg.page.step2_junior_title': 'ولي الأمر والمرافقة',
    'academy.prereg.page.step2_adult_title': 'المهنة والطوارئ',
    'academy.prereg.page.step3_title': 'الصحة والطب',
    'academy.prereg.page.step4_title': 'اختيار الدورة',
    'academy.prereg.page.step5_title': 'التأكيد',
    'academy.prereg.page.step_mobile_label': 'الخطوة {0} من {1}: {2}',
    'academy.prereg.page.first_name': 'الاسم الأول',
    'academy.prereg.page.last_name': 'اللقب',
    'academy.prereg.page.gender': 'الجنس',
    'academy.prereg.page.gender_male': 'ذكر',
    'academy.prereg.page.gender_female': 'أنثى',
    'academy.prereg.page.dob': 'تاريخ الميلاد',
    'academy.prereg.page.phone': 'رقم الهاتف',
    'academy.prereg.page.email': 'عنوان البريد الإلكتروني',
    'academy.prereg.page.address': 'عنوان الإقامة',
    'academy.prereg.page.guardian_header': 'ولي الأمر الشرعي والشخص المسؤول',
    'academy.prereg.page.guardian_first_name': 'الاسم الأول لولي الأمر',
    'academy.prereg.page.guardian_last_name': 'لقب ولي الأمر',
    'academy.prereg.page.guardian_relation': 'صلة القرابة',
    'academy.prereg.page.guardian_relation_father': 'الأب',
    'academy.prereg.page.guardian_relation_mother': 'الأم',
    'academy.prereg.page.guardian_relation_legal': 'ولي أمر شرعي',
    'academy.prereg.page.guardian_relation_uncle': 'عم/عمة/خال/خالة',
    'academy.prereg.page.guardian_relation_grandparent': 'الجد/الجدة',
    'academy.prereg.page.guardian_phone': 'رقم هاتف ولي الأمر',
    'academy.prereg.page.guardian_email': 'البريد الإلكتروني للولي / المسؤول',
    'academy.students.form.guardian_email': 'البريد الإلكتروني للولي',
    'academy.prereg.page.authorized_pickup': 'الأشخاص المخولون لاستلام الطفل',
    'academy.prereg.page.authorized_pickup_placeholder': 'الصيغة: الاسم واللقب + صلة القرابة (شخص واحد في كل سطر)',
    'academy.prereg.page.adult_info_header': 'المعلومات المهنية والتصريح بالطوارئ',
    'academy.prereg.page.profession': 'المهنة / الوضع الحالي',
    'academy.prereg.page.profession_placeholder': 'مثال: طالب جامعي، مهندس، أستاذ...',
    'academy.prereg.page.emergency_contact': 'جهة الاتصال في حالات الطوارئ (الاسم والهاتف)',
    'academy.prereg.page.health_header': 'المعلومات الطبية (اختياري)',
    'academy.prereg.page.health_issues_check': 'وجود مشاكل صحية خاصة (ربو، سكري، إلخ)',
    'academy.prereg.page.health_issues_detail': 'تفاصيل المشاكل الصحية',
    'academy.prereg.page.allergies_check': 'وجود حساسيات معروفة',
    'academy.prereg.page.allergies_detail': 'تفاصيل الحساسية',
    'academy.prereg.page.course_selection_header': 'اختيار الدورة التدريبية المطلوبة',
    'academy.prereg.page.no_courses': 'لا توجد دورة محددة مخصصة حالياً. يمكنك اختيارها مع الإدارة لاحقاً.',
    'academy.prereg.page.summary_header': 'ملخص طلبك',
    'academy.prereg.page.terms_consent': 'أقر بصحة المعلومات المقدمة وأطلب التسجيل الأولي لدى المؤسسة.',
    'academy.prereg.page.terms_required_error': 'يرجى قبول شروط التسجيل.',
    'academy.prereg.page.success_title': 'تم استلام طلب التسجيل الأولي!',
    'academy.prereg.page.success_msg': 'تم إرسال طلب التسجيل الأولي بنجاح إلى إدارة {0}.',
    'academy.prereg.page.reference_code': 'المرجع: {0}',
    'academy.prereg.page.next_steps_title': 'الخطوات القادمة:',
    'academy.prereg.page.next_steps_msg': 'سيقوم الفريق الإداري بمراجعة ملفك والتواصل معك قريباً لإتمام تسجيلك النهائي.',
    'academy.prereg.page.new_registration': 'تسجيل جديد',
    'academy.prereg.page.btn_prev': 'السابق',
    'academy.prereg.page.btn_next': 'التالي',
    'academy.prereg.page.btn_submit': 'تأكيد التسجيل الأولي',
    'academy.prereg.page.btn_submitting': 'جاري التأكيد...',
    'academy.prereg.page.error_invalid_token': 'رابط التسجيل الأولي غير صالحة أو منتهي الصلاحية.',
    'academy.prereg.page.error_load_failed': 'تعذر تحميل معلومات الوكالة.',
    'academy.prereg.page.error_no_token': 'لم يتم توفير رمز التسجيل الأولي.',
    'academy.prereg.page.error_submit_failed': 'فشل إرسال التسجيل الأولي.',
    'academy.prereg.page.error_submit_generic': 'حدث خطأ أثناء التأكيد.',
    'academy.prereg.page.recommended_courses': 'الدورات الموصى بها لعمرك',
    'academy.prereg.page.other_courses': 'دورات أخرى متاحة',
    'academy.prereg.page.recommended_badge': 'موصى به',
    'academy.prereg.page.programs_header': 'البرامج / المجموعات المتاحة',
    'academy.prereg.page.select_program': 'اختر مجموعة (اختياري)',
    'academy.prereg.page.schedules_label': 'الأوقات المجدولة',
    'academy.prereg.page.time_preference': 'الوقت المفضل',
    'academy.prereg.page.time_preference_required': 'يرجى اختيار وقت مفضل.',
    'academy.prereg.page.price_label': 'السعر: {0}',
    'academy.prereg.page.hours_label': '{0} ساعة',
    'academy.prereg.page.loi_18_07_consent': 'أوافق على معالجة بياناتي الشخصية وفقاً للقانون الجزائري 18-07 المتعلق بحماية البيانات ذات الطابع الشخصي.',
    'academy.prereg.page.loi_18_07_required': 'موافقة القانون 18-07 إلزامية.',
    'academy.prereg.page.summary_course': 'الدورة المختارة',
    'academy.prereg.page.summary_program': 'المجموعة المختارة',
    'academy.prereg.page.summary_time_pref': 'الوقت المفضل',
    'academy.prereg.page.no_programs': 'لا توجد مجموعة مجدولة لهذه الدورة. يرجى اختيار وقت أدناه.',

    'academy.students.table.contact': 'معلومات الاتصال',
    'academy.students.table.emergency': 'جهة الاتصال في الطوارئ',
    'academy.students.table.financial': 'الملخص المالي',
    'academy.students.table.status': 'الحالة والحظر',
    'academy.students.dob_prefix': 'تاريخ الميلاد:',
    'academy.students.modal.edit_title': 'تعديل بيانات الطالب',
    'academy.students.modal.add_title': 'تسجيل طالب جديد',
    'academy.students.modal.first_name': 'الاسم الأول',
    'academy.students.modal.last_name': 'اللقب',
    'academy.students.modal.email': 'البريد الإلكتروني',
    'academy.students.modal.phone': 'رقم الهاتف',
    'academy.students.modal.dob': 'تاريخ الميلاد',
    'academy.students.modal.emergency': 'جهة الاتصال في الطوارئ (ولي الأمر)',
    'academy.students.modal.emergency_helper': 'مثال: اسم ورقم هاتف ولي الأمر',
    'academy.students.modal.save': 'حفظ بيانات الطالب',
    'academy.students.drawer.history': 'سجل التسجيلات الزمني',
    'academy.students.drawer.enrolled_on': 'تم التسجيل بتاريخ:',

    // ── StudentsPage — مفاتيح جديدة (قائمة، نموذج، جدول) ─────────────────
    'academy.students.new_student': 'طالب جديد',
    'academy.students.back_to_list': 'العودة إلى القائمة',
    'academy.students.no_students': 'لا يوجد طلاب مسجلون',
    'academy.students.confirm_delete': 'حذف "{0} {1}"؟',
    'academy.students.confirm_edit': 'هل تريد تعديل بيانات الطالب {0} {1}؟',
    'academy.students.confirm_accept_title': 'تأكيد التسجيل الأولي',
    'academy.students.confirm_accept_msg': 'هل تريد قبول وتأكيد التسجيل الأولي للطالب {0} {1}؟',
    'academy.students.success_accept_title': 'تم تأكيد التسجيل الأولي!',
    'academy.students.success_accept_msg': 'الطالب {0} {1} أصبح نشطاً الآن.',
    'academy.students.error_accept': 'فشل التأكيد.',
    'academy.students.confirm_refuse_title': 'رفض التسجيل الأولي',
    'academy.students.confirm_refuse_msg': 'هل أنت تأكد من رغبتك في رفض التسجيل الأولي للطالب {0} {1}؟',
    'academy.students.success_refuse_title': 'تم رفض التسجيل الأولي',
    'academy.students.success_refuse_msg': 'تم رفض طلب {0} {1}.',
    'academy.students.error_refuse': 'فشل الرفض.',
    'academy.students.val_firstname': 'الاسم إجباري.',
    'academy.students.val_lastname': 'اللقب إجباري.',
    'academy.students.val_gender': 'الجنس إجباري.',
    'academy.students.val_dob': 'تاريخ الميلاد إجباري.',
    'academy.students.val_health': 'يرجى توضيح المشاكل الصحية.',
    'academy.students.val_allergies': 'يرجى توضيح الحساسية.',
    'academy.students.val_guardian_first': 'اسم ولي الأمر إجباري.',
    'academy.students.val_guardian_last': 'لقب ولي الأمر إجباري.',
    'academy.students.val_guardian_rel': 'صلة القرابة بالطفل إجبارية.',
    'academy.students.val_guardian_phone': 'رقم هاتف ولي الأمر إجباري.',
    'academy.students.val_image_right': 'الموافقة على الحق في الصورة إجبارية.',
    'academy.students.table.registration_status': 'التسجيل',
    'academy.students.action_accept': 'قبول وتأكيد',
    'academy.students.action_refuse': 'رفض',
    'academy.students.list.grid': 'شبكة',
    'academy.students.list.table': 'جدول',
    'academy.students.table.actions': 'إجراءات',
    'academy.students.table.dob_prefix': 'ت.م:',
    'academy.students.table.invoiced': 'المفوتر',
    'academy.students.table.total_paid': 'إجمالي المدفوع',
    'academy.students.table.rest_to_pay': 'المبلغ المتبقي',
    'academy.students.form.title_new': 'طالب جديد / تسجيل',
    'academy.students.form.title_edit': 'تعديل: {0} {1}',
    'academy.students.form.category': 'الفئة',
    'academy.students.form.adult': 'بالغ',
    'academy.students.form.junior': 'ناشئ',
    'academy.students.form.photo': 'الصورة',
    'academy.students.form.change_photo': 'تغيير الصورة',
    'academy.students.form.attachment': 'ملف مرفق',
    'academy.students.form.attach_file': 'إرفاق ملف',
    'academy.students.form.view_file': 'اضغط لفتح الملف',
    'academy.students.form.dossier': 'الملف',
    'academy.students.form.dos_code': 'رمز الملف',
    'academy.students.form.dos_lib': 'تسمية الملف',
    'academy.students.form.last_modified': 'آخر تعديل',
    'academy.students.form.modified_by': 'بواسطة',
    'academy.students.form.emergency_contact': 'جهة الاتصال الطارئة',
    'academy.students.financial_snapshot': 'لمحة مالية',
    'academy.students.card.invoiced': 'مفوتر',
    'academy.students.card.paid': 'مدفوع',
    'academy.students.card.rest': 'المتبقي',

    // Form — حقول تفصيلية
    'academy.students.form.photo_caption': 'صورة الملف',
    'academy.students.form.file_label': 'ملف مرفق (هوية، طبي، غيره…)',
    'academy.students.form.section_personal': 'المعلومات الشخصية',
    'academy.students.form.registration_status': 'حالة التسجيل',
    'academy.students.field.course_template': 'قالب الدورة (تسجيل أولي)',
    'academy.students.field.time_preference': 'الوقت المفضل',
    'academy.students.field.loi_consent': 'موافقة القانون 18-07',
    'academy.students.form.section_prereg': 'معلومات التسجيل الأولي',
    'academy.students.status.active': 'نشط',
    'academy.students.status.preregistered': 'تسجيل أولي',
    'academy.students.status.refused': 'مرفوض',
    'academy.students.form.firstname': 'اللقب',
    'academy.students.form.lastname': 'الاسم',
    'academy.students.form.gender': 'الجنس',
    'academy.students.form.gender_male': 'ذكر',
    'academy.students.form.gender_female': 'أنثى',
    'academy.students.form.dob': 'تاريخ الميلاد',
    'academy.students.form.address': 'العنوان',
    'academy.students.form.tab_adult': 'بالغ (+16)',
    'academy.students.form.tab_junior': 'ناشئ / طفل',
    'academy.students.form.section_adult': 'معلومات البالغ',
    'academy.students.form.profession': 'المهنة',
    'academy.students.form.email': 'البريد الإلكتروني (اختياري)',
    'academy.students.form.phone': 'رقم الهاتف',
    'academy.students.form.section_junior': 'معلومات الناشئ',
    'academy.students.form.school_level': 'المستوى الدراسي',
    'academy.students.form.blood_group': 'فصيلة الدم',
    'academy.students.form.blood_group_none': 'غير محدد',
    'academy.students.form.health_issues': 'هل يعاني الطفل من مشاكل صحية؟',
    'academy.students.form.health_specify': '(إذا نعم) تحديد:',
    'academy.students.form.health_other_detail': 'تحديد المرض',
    'academy.students.form.health_other_detail_placeholder': 'وصف المرض أو المشكلة الصحية...',
    'academy.students.form.health_condition.asthma': 'الربو',
    'academy.students.form.health_condition.diabetes': 'السكري',
    'academy.students.form.health_condition.epilepsy': 'الصرع',
    'academy.students.form.health_condition.other': 'أخرى',
    'academy.students.form.guardian_relation.parent': 'الأب / الأم',
    'academy.students.form.guardian_relation.sibling': 'الأخ / الأخت',
    'academy.students.form.guardian_relation.grandparent': 'الجد / الجدة',
    'academy.students.form.guardian_relation.uncle_aunt': 'العم / العمة',
    'academy.students.form.guardian_relation.guardian': 'ولي / كافل',
    'academy.students.form.guardian_relation.other': 'أخرى',
    'academy.students.form.school_level.primary_1': 'السنة 1 ابتدائي',
    'academy.students.form.school_level.primary_2': 'السنة 2 ابتدائي',
    'academy.students.form.school_level.primary_3': 'السنة 3 ابتدائي',
    'academy.students.form.school_level.primary_4': 'السنة 4 ابتدائي',
    'academy.students.form.school_level.primary_5': 'السنة 5 ابتدائي',
    'academy.students.form.school_level.middle_1': 'السنة 1 متوسط',
    'academy.students.form.school_level.middle_2': 'السنة 2 متوسط',
    'academy.students.form.school_level.middle_3': 'السنة 3 متوسط',
    'academy.students.form.school_level.middle_4': 'السنة 4 متوسط',
    'academy.students.form.allergies': 'حساسية',
    'academy.students.form.allergy_details': 'تحديد الحساسية',
    'academy.students.form.allergy_details_placeholder': 'وصف الحساسية (أغذية، أدوية، إلخ)...',
    'academy.students.form.image_right': 'حق الصورة',
    'academy.students.form.yes': 'نعم',
    'academy.students.form.no': 'لا',
    'academy.students.form.guardian_title': 'الولي / الشخص المسؤول',
    'academy.students.form.guardian_firstname': 'اللقب',
    'academy.students.form.guardian_lastname': 'الاسم',
    'academy.students.form.guardian_relation': 'صلة القرابة',
    'academy.students.form.guardian_phone': 'هاتف',
    'academy.students.form.authorized_pickup': 'الأشخاص المصرح لهم باستلام الطفل',
    'academy.students.form.authorized_pickup_hint': 'الصيغة: الاسم + صلة القرابة',
    'academy.students.form.authorized_pickup_placeholder': 'الاسم + صلة القرابة (شخص واحد في كل سطر)',
    'academy.students.section_dossier': 'معلومات الملف',


    'academy.teachers.title': 'إدارة الأساتذة والهيئة التدريسية',
    'academy.teachers.subtitle': 'متابعة الأساتذة، أسعار الساعة وجدول الانشغال.',
    'academy.teachers.add_new': 'إضافة أستاذ',
    'academy.teachers.busy_map_summary': 'جدول الانشغال الأسبوعي ({0} حصة محجوزة):',
    'academy.teachers.view_profile': 'عرض الملف والجدول',
    'academy.teachers.modal.edit_title': 'تعديل بيانات الأستاذ',
    'academy.teachers.modal.add_title': 'إضافة أستاذ جديد',
    'academy.teachers.modal.fullname': 'الاسم الكامل',
    'academy.teachers.modal.email': 'البريد الإلكتروني',
    'academy.teachers.modal.phone': 'رقم الهاتف',
    'academy.teachers.modal.specialization': 'التخصص (مثال: تطوير الويب، الذكاء الاصطناعي)',
    'academy.teachers.modal.hourly_rate': 'سعر الساعة ({0})',
    'academy.teachers.modal.bio': 'نبذة / ملاحظات',
    'academy.teachers.modal.save': 'حفظ بيانات الأستاذ',
    'academy.teachers.drawer.groups': 'المجموعات والدفعات المسندة',
    'academy.teachers.drawer.timeline': 'جدول الانشغال المرئي الأسبوعي',
    'academy.teachers.drawer.free': 'متاح',
    'academy.teachers.form.title_new': 'إضافة أستاذ جديد',
    'academy.teachers.form.title_edit': 'تعديل بيانات الأستاذ: {0} {1}',
    'academy.teachers.form.firstname': 'الاسم',
    'academy.teachers.form.lastname': 'اللقب',
    'academy.teachers.form.gender': 'الجنس',
    'academy.teachers.form.gender_male': 'ذكر',
    'academy.teachers.form.gender_female': 'أنثى',
    'academy.teachers.form.dob': 'تاريخ الميلاد',
    'academy.teachers.form.email': 'البريد الإلكتروني',
    'academy.teachers.form.phone': 'رقم الهاتف',
    'academy.teachers.form.specialization': 'التخصصات / التدريبات',
    'academy.teachers.form.hire_date': 'تاريخ التوظيف',
    'academy.teachers.form.status': 'الحالة',
    'academy.teachers.form.dossier_code': 'رمز الملف',
    'academy.teachers.form.dossier_lib': 'اسم الملف',
    'academy.teachers.form.rate_type': 'نوع التعريفة',
    'academy.teachers.form.rate_type.hourly': 'ساعة',
    'academy.teachers.form.rate_type.daily': 'يوم',
    'academy.teachers.form.rate_type.monthly': 'شهر',
    'academy.teachers.form.rate_type.percentage': 'نسبة مئوية',
    'academy.teachers.form.rate_type.fixed': 'مبلغ ثابت',
    'academy.teachers.form.rate_amount': 'قيمة التعريفة',
    'academy.teachers.form.bio': 'نبذة / ملاحظات',
    'academy.teachers.form.cv_file': 'السيرة الذاتية / مرفق',
    'academy.teachers.form.attach_cv': 'إرفاق السيرة الذاتية (PDF/Doc)',
    'academy.teachers.form.view_cv': 'عرض السيرة الذاتية',
    'academy.teachers.form.photo_caption': 'صورة الملف الشخصي',
    'academy.teachers.form.avatar_url': 'رابط الصورة / الأفاتار',
    'academy.teachers.table.teacher': 'الأستاذ',
    'academy.teachers.table.contact': 'معلومات الاتصال',
    'academy.teachers.table.specialization': 'التخصص',
    'academy.teachers.table.rate': 'التعريفة',
    'academy.teachers.table.status': 'الحالة',
    'academy.teachers.table.cv': 'السيرة الذاتية',
    'academy.teachers.search_placeholder': 'البحث بالاسم، التخصص، البريد الإلكتروني...',
    'academy.teachers.back_to_list': 'العودة إلى القائمة',
    'academy.teachers.active_badge': 'مفعل',
    'academy.teachers.inactive_badge': 'غير مفعل',
    'academy.teachers.section_personal': 'المعلومات الشخصية',
    'academy.teachers.section_professional': 'المعلومات المهنية',
    'academy.teachers.section_dossier': 'معلومات الملف',

    'academy.rooms.title': 'إدارة القاعات والموارد',
    'academy.rooms.subtitle': 'إدارة القاعات الدراسية والتجهيزات وسعة الاستيعاب.',
    'academy.rooms.add_new': 'إضافة قاعة',
    'academy.rooms.back_to_list': 'العودة إلى القائمة',
    'academy.rooms.confirm_delete': 'هل أنت تأكد من رغبتك في حذف هذه القاعة؟',
    'academy.rooms.search_placeholder': 'البحث برقم القاعة، الطابق، التجهيزات...',
    'academy.rooms.room_number': 'رقم القاعة',
    'academy.rooms.floor': 'الطابق',
    'academy.rooms.capacity': 'السعة',
    'academy.rooms.equipment': 'التجهيزات',
    'academy.rooms.status': 'الحالة',
    'academy.rooms.status.active': 'مفعلة',
    'academy.rooms.status.maintenance': 'تحت الصيانة',
    'academy.rooms.status.archived': 'مؤرشفة',
    'academy.rooms.section_info': 'معلومات القاعة',
    'academy.rooms.section_dossier': 'معلومات الملف',
    'academy.rooms.form.title_new': 'قاعة جديدة',
    'academy.rooms.form.title_edit': 'تعديل القاعة: {0}',
    'academy.rooms.form.dossier_code': 'رمز الملف',
    'academy.rooms.form.dossier_lib': 'الملف',
    'academy.rooms.min_capacity': 'السعة الأدنى',
    'academy.rooms.all_capacities': 'جميع السعات',
    'academy.rooms.capacity_plus': '{0}+ طالب',
    'academy.rooms.view_grid': 'شبكة',
    'academy.rooms.view_table': 'جدول',
    'academy.rooms.room_prefix': 'القاعة {0}',
    'academy.rooms.location': 'الموقع:',
    'academy.rooms.capacity_label': 'السعة: {0} طالب',
    'academy.rooms.weekly_util': 'نسبة الاستغلال الأسبوعية',
    'academy.rooms.modal.edit_title': 'تعديل بيانات القاعة',
    'academy.rooms.modal.add_title': 'إنشاء قاعة جديدة',
    'academy.rooms.modal.number': 'رقم القاعة (مثال: A-101)',
    'academy.rooms.modal.floor': 'الطابق (مثال: الطابق الأول)',
    'academy.rooms.modal.capacity': 'السعة (عدد صحيح)',
    'academy.rooms.modal.equipment': 'التجهيزات (مفصولة بفواصل)',
    'academy.rooms.modal.equipment_helper': 'مثال: جهاز عرض، سبورة، أجهزة كمبيوتر، واي فاي',
    'academy.rooms.modal.save': 'حفظ القاعة',

    'academy.courses.title': 'قوالب الدورات',
    'academy.courses.subtitle': 'إدارة البرامج التدريبية والأسعار والمدة.',
    'academy.courses.add_new': 'قالب جديد',
    'academy.courses.back_to_list': 'العودة إلى القائمة',
    'academy.courses.confirm_delete': 'هل تريد حذف القالب « {0} »؟',
    'academy.courses.section_info': 'معلومات القالب',
    'academy.courses.section_dossier': 'معلومات الملف',
    'academy.courses.search_placeholder': 'البحث بالعنوان، الوصف...',
    'academy.courses.hours_label': '{0} س',
    'academy.courses.course_preview': 'معاينة القالب',
    'academy.courses.list.grid': 'شبكة',
    'academy.courses.list.table': 'جدول',
    'academy.courses.table.title': 'العنوان',
    'academy.courses.table.hours': 'المدة (س)',
    'academy.courses.table.price': 'السعر',
    'academy.courses.table.description': 'الوصف',
    'academy.courses.form.title_new': 'قالب دورة جديد',
    'academy.courses.form.title_edit': 'تعديل: {0}',
    'academy.courses.form.title': 'عنوان الدورة',
    'academy.courses.form.hours': 'إجمالي الساعات',
    'academy.courses.form.price': 'السعر ({0})',
    'academy.courses.form.description': 'الوصف والبرنامج',
    'academy.courses.form.dossier_code': 'رمز الملف',
    'academy.courses.form.dossier_lib': 'الملف',
    'academy.courses.val_title': 'العنوان إلزامي.',
    'academy.courses.val_hours': 'يجب أن تكون المدة أكبر من 0.',
    'academy.courses.val_price': 'لا يمكن أن يكون السعر سالباً.',
    'academy.courses.form.start_date': 'تاريخ البداية (الصلاحية)',
    'academy.courses.form.end_date': 'تاريخ النهاية (الصلاحية)',
    'academy.courses.form.show_price': 'عرض السعر على رابط التسجيل الأولي',
    'academy.courses.form.age_band': 'الفئة العمرية (التسمية)',
    'academy.courses.form.age_min': 'الحد الأدنى للعمر',
    'academy.courses.form.age_max': 'الحد الأقصى للعمر',
    'academy.courses.val_age_range': 'يجب أن يكون الحد الأدنى للعمر أقل من أو يساوي الحد الأقصى.',
    'academy.courses.val_date_range': 'يجب أن يكون تاريخ البداية قبل أو يساوي تاريخ النهاية.',
    'academy.courses.table.age_band': 'الفئة العمرية',
    'academy.courses.table.validity': 'الصلاحية',

    'academy.scheduler.day_label': 'يوم الأسبوع',
    'academy.scheduler.matrix_title': 'القاعات (محور Y) \\ الأوقات (محور X)',
    'academy.scheduler.cap_floor': 'السعة: {0} | {1}',
    'academy.scheduler.modal.title': 'مصفوفة برمجة الحصص',
    'academy.scheduler.modal.select_room': 'اختر القاعة',
    'academy.scheduler.modal.select_group': 'اختر المجموعة',
    'academy.scheduler.modal.select_teacher': 'تعيين الأستاذ الرئيسي',
    'academy.scheduler.modal.start_time': 'وقت البداية',
    'academy.scheduler.modal.end_time': 'وقت النهاية',
    'academy.scheduler.modal.recurring': 'حصص أسبوعية متكررة',
    'academy.scheduler.modal.repeat_weeks': 'تكرار لمدة N أسابيع',
    'academy.scheduler.modal.confirm': 'تأكيد الجدول',

    'academy.attendance.teacher_mode': 'وضع تسجيل الدخول الخاص بالأستاذ',
    'academy.attendance.my_sessions': 'حصصي اليوم',
    'academy.attendance.all_sessions': 'جميع الحصص المبرمجة',
    'academy.attendance.taken': 'تم تسجيل الحضور',
    'academy.attendance.pending': 'في الانتظار',
    'academy.attendance.room_instructor': 'القاعة: {0} • الأستاذ: {1}',
    'academy.attendance.enrolled_count': '{0} طالب مسجل',
    'academy.attendance.table.student': 'الطالب',
    'academy.attendance.table.pass_rate': 'نسبة نجاح الدورة',
    'academy.attendance.table.toggle': 'تسجيل الحضور (حاضر / غائب / متأخر / معذور)',
    'academy.attendance.passing': 'مستوفي للشروط (>75%)',
    'academy.attendance.at_risk': 'في خطر الحرمان',
    'academy.attendance.select_prompt': 'اختر حصة لتسجيل الحضور.',
    'academy.attendance.no_sessions': 'لا توجد حصص مبرمجة.',
    'academy.attendance.no_students': 'لا يوجد طلاب مسجلون في هذه المجموعة.',

    'academy.reports.teacher_perf_title': 'معدلات حضور الأساتذة',
    'academy.reports.teacher_perf_sub': 'الأساتذة الأعلى نسبة حضور لطلابهم في الحصص.',
    'academy.reports.rate_label': 'نسبة {0}%',
    'academy.reports.room_util_title': 'معدلات استغلال القاعات',
    'academy.reports.room_util_sub': 'كفاءة الحجز الأسبوعية (الساعات المحجوزة مقارنة بسعة 40 ساعة).',
    'academy.reports.booked_label': 'محجوز {0}%',
    'academy.reports.churn_title': 'تقرير الانسحاب والانقطاع عن الدراسة',
    'academy.reports.churn_sub': 'قائمة الطلاب الذين ألغوا تسجيلهم أو انقطعوا عن الحضور.',
    'academy.reports.zero_churn': 'لا يوجد أي انسحاب للطلاب!',
    'academy.reports.zero_churn_sub': 'جميع الطلاب المسجلين يحضرون الحصص بانتظام.',
    'academy.reports.table.student': 'الطالب',
    'academy.reports.table.group': 'المجموعة / الدفعة',
    'academy.reports.table.enrollment_date': 'تاريخ التسجيل',
    'academy.reports.table.status': 'الحالة',

    'academy.common.group_number': 'مجموعة #{0}',
    'academy.common.room': 'قاعة',
    'academy.common.teacher': 'أستاذ',
    'academy.common.group': 'مجموعة',
    'academy.common.group_session': 'حصة جماعية',
    'academy.common.general': 'عام',
    'academy.common.unassigned_floor': 'طابق غير محدد',
    'academy.common.students_count': '{0} طالب',
    'academy.teachers.confirm_deactivate': 'هل تريد إلغاء تفعيل هذا الأستاذ؟',
    'academy.attendance.error.hold_block': 'تعذر تسجيل حضور الطالب (حظر مالي).',
    'academy.scheduler.error.conflict': 'تم اكتشاف تعارض في الجدول.',
    'academy.scheduler.tooltip.session': 'المجموعة: {0} | الأستاذ: {1} | {2} طالب',
    'academy.groups.error.status_change': 'تعذر تغيير حالة المجموعة.',
    'academy.reports.capacity_short': 'سعة',

    // Menu Default
    'menu.default.dashboard': 'لوحة القيادة',

    // Page - Menu Config
    'page.menuConfig.title': 'تكوين القائمة',
    'page.menuConfig.profil.label': 'الملف الشخصي',
    'page.menuConfig.profil.placeholder': 'اختر ملفاً شخصياً',
    'page.menuConfig.profil.helper': 'انقر على العدسة للبحث',
    'page.menuConfig.structure.title': 'هيكل القائمة لـ {0}',
    'page.menuConfig.structure.empty': 'يرجى اختيار ملف شخصي',
    'page.menuConfig.success.save': 'تم حفظ تكوين القائمة',
    'page.menuConfig.error.load': 'تعذر تحميل تكوين القائمة',
    'page.menuConfig.error.save': 'خطأ أثناء الحفظ',
    'page.menuConfig.accessDenied': 'تم رفض الوصول. للمسؤولين فقط.',

    // Menu Maker - Missing
    'menu.maker.profiles': 'الملفات الشخصية',
    'menu.maker.dashboard': 'لوحة القيادة',

    // Maker - Dashboard
    'maker.dashboard.title': 'لوحة القيادة',
    'maker.dashboard.welcome': 'مرحباً بك في BLOBTIC Maker - برنامج تصنيع الأبواب والنوافذ',
    'maker.dashboard.stats.projects': 'المشاريع',
    'maker.dashboard.stats.estimates': 'التقديرات',
    'maker.dashboard.stats.customers': 'العملاء',
    'maker.dashboard.stats.materials': 'المواد',
    'maker.dashboard.quick_actions': 'إجراءات سريعة',
    'maker.dashboard.recent_activity': 'النشاط الأخير',
    'maker.dashboard.no_activity': 'لا يوجد نشاط حديث',
    'maker.dashboard.loading': 'جاري التحميل...',

    // Maker - Profiles
    'maker.gamme.title': 'المجموعات',
    'maker.profiles.title': 'إدارة القطاعات',
    'maker.profiles.description': 'القطاعات هي قطع من المواد (ألومنيوم، بي في سي، إلخ) تستخدم لتصنيع إطارات النوافذ والأبواب.',
    'maker.profiles.add': 'إضافة قطاع',
    'maker.profiles.edit': 'تعديل السجل',
    'maker.profiles.table.name': 'الاسم',
    'maker.profiles.table.code': 'الكود',
    'maker.profiles.table.material': 'المادة',
    'maker.profiles.table.dimensions': 'الأبعاد (العرض×الارتفاع×العمق)',
    'maker.profiles.table.price': 'السعر',
    'maker.profiles.table.stock': 'المخزون',
    'maker.profiles.table.actions': 'إجراءات',
    'maker.profiles.loading': 'جاري التحميل...',
    'maker.profiles.empty': 'لم يتم العثور على قطاعات',
    'maker.profiles.delete.title': 'تأكيد الحذف',
    'maker.profiles.delete.confirm': 'هل أنت متأكد من أنك تريد حذف هذا القطاع؟',
    'maker.profiles.delete.action': 'حذف',

    // Maker - Projects
    'maker.projects.title': 'المشاريع',
    'maker.projects.new': 'مشروع جديد',
    'maker.projects.search': 'بحث عن مشاريع...',
    'maker.projects.filter.status': 'كل الحالات',
    'maker.projects.filter.active': 'نشط',
    'maker.projects.filter.inactive': 'غير نشط',
    'maker.projects.loading': 'جاري تحميل المشاريع...',
    'maker.projects.empty.title': 'لم يتم العثور على مشاريع',
    'maker.projects.empty.desc': 'أنشئ مشروعك الأول للبدء',
    'maker.projects.create': 'إنشاء مشروع',
    'maker.projects.card.noDesc': 'لا يوجد وصف',
    'maker.projects.meta.created': 'تم الإنشاء في',

    // Maker - Fields
    'maker.field.name': 'الاسم',
    'maker.field.description': 'الوصف',
    'maker.field.customer': 'العميل',
    'maker.field.project': 'المشروع',
    'maker.field.code': 'الكود',
    'maker.field.unitPrice': 'سعر الوحدة',
    'maker.field.unit': 'الوحدة',
    'maker.field.stockQuantity': 'كمية المخزون',
    'maker.field.active': 'نشط',
    'maker.field.width': 'العرض (مم)',
    'maker.field.height': 'الارتفاع (مم)',
    'maker.field.depth': 'العمق (مم)',
    'maker.field.company': 'الشركة',
    'maker.field.email': 'البريد الإلكتروني',
    'maker.field.phone': 'الهاتف',
    'maker.field.address': 'العنوان',
    'maker.field.city': 'المدينة',
    'maker.field.country': 'البلد',
    'maker.field.postalCode': 'الرمز البريدي',
    'maker.field.materialType': 'نوع المادة',
    'maker.field.photo': 'صورة',

    // Maker - Actions & Messages
    'action.create': 'إنشاء',
    'maker.action.upload_photo': 'رفع صورة',
    'action.generating': 'جاري الإنشاء...',
    'maker.msg.success.create': 'تم الإنشاء بنجاح',
    'maker.msg.success.generate': 'تمت العملية بنجاح!',
    'maker.msg.error.create': 'فشل الإنشاء',
    'maker.msg.error.generate': 'فشلت العملية',
    'maker.msg.error.required': 'حقل مطلوب',
    'maker.msg.noProjects': 'لا توجد مشاريع متاحة',
    'maker.msg.failedSave': 'فشل الحفظ: {0}',

    // Maker - Customers
    'maker.customers.title': 'العملاء',
    'maker.customers.new': 'عميل جديد',
    'maker.customers.search': 'بحث عن عميل',
    'maker.customers.edit': 'تعديل العميل',
    'maker.customers.loading': 'جاري تحميل العملاء...',
    'maker.customers.empty': 'لم يتم العثور على عملاء. أنشئ واحداً للبدء.',
    'maker.customers.delete.title': 'تأكيد الحذف',
    'maker.customers.delete.confirm': 'هل أنت متأكد من أنك تريد حذف {0}؟',
    'maker.customers.delete.action': 'حذف',

    // Maker - Materials
    'maker.materials.title': 'المواد',
    'maker.materials.add': 'إضافة مادة',
    'maker.materials.edit': 'تعديل المادة',
    'maker.materials.search': 'بحث عن مواد...',
    'maker.materials.filter.all': 'كل الأنواع',
    'maker.materials.table.code': 'الكود',
    'maker.materials.table.name': 'الاسم',
    'maker.materials.table.type': 'النوع',
    'maker.materials.table.price': 'سعر الوحدة',
    'maker.materials.table.unit': 'الوحدة',
    'maker.materials.table.stock': 'المخزون',
    'maker.materials.table.status': 'الحالة',
    'maker.materials.table.actions': 'إجراءات',
    'maker.materials.loading': 'جاري التحميل...',
    'maker.materials.empty': 'لم يتم العثور على مواد',
    'maker.materials.type.aluminum': 'ألومنيوم',
    'maker.materials.type.pvc': 'بي في سي',
    'maker.materials.type.wood': 'خشب',
    'maker.materials.type.steel': 'حديد',
    'maker.materials.type.unknown': 'غير معروف',
    'maker.materials.delete.title': 'تأكيد الحذف',
    'maker.materials.delete.confirm': 'هل أنت متأكد من أنك تريد حذف {0}؟',

    // Maker - Estimates
    'maker.estimates.title': 'التقديرات',
    'maker.estimates.new': 'تقدير جديد',
    'maker.estimates.loading': 'جاري تحميل التقديرات...',
    'maker.estimates.empty.title': 'لا توجد تقديرات بعد',
    'maker.estimates.empty.desc': 'أنشئ وأدر تقديرات لمشاريعك',
    'maker.estimates.generate': 'إنشاء تقدير',
    'maker.estimates.table.number': 'رقم التقدير',
    'maker.estimates.table.project': 'المشروع',
    'maker.estimates.table.date': 'التاريخ',
    'maker.estimates.table.subtotal': 'المجموع الفرعي',
    'maker.estimates.table.tax': 'الضريبة',
    'maker.estimates.table.total': 'المجموع',
    'maker.estimates.table.status': 'الحالة',
    'maker.estimates.table.validity': 'صالح حتى',

    // Maker - Production
    'maker.production.title': 'تخطيط الإنتاج',
    'maker.production.tabs.projects': 'المشاريع',
    'maker.production.tabs.bom': 'قائمة المواد (BOM)',
    'maker.production.tabs.cutting': 'خطة القطع',
    'maker.production.select_project': 'اختر مشروعاً',
    'maker.production.loading': 'جاري التحميل...',
    'maker.production.door_count': 'أبواب: {0}',
    'maker.production.window_count': 'نوافذ: {0}',
    'maker.production.bom.generate': 'إنشاء BOM',
    'maker.production.cutting.optimize': 'تحسين القطع',
    'maker.production.bom.total_materials': 'إجمالي المواد',
    'maker.production.bom.total_cost': 'إجمالي التكلفة',
    'maker.production.bom.print': 'طباعة BOM',
    'maker.production.cutting.efficiency': 'متوسط الكفاءة',
    'maker.production.cutting.waste': 'إجمالي الهدر',
    'maker.production.cutting.required': 'مطلوب: {0}',
    'maker.production.cutting.stock': 'مخزون: {0}',
    'maker.production.cutting.waste_label': 'هدر: {0}',
    'maker.production.cutting.layout': 'مخطط القطع (طول المخزون: {0})',

    // Maker - Design Canvas
    'maker.design.canvas.select': 'تحديد',
    'maker.design.canvas.vsplit': 'تقسيم عمودي',
    'maker.design.canvas.hsplit': 'تقسيم أفقي',
    'maker.design.canvas.sash': 'وشاح',
    'maker.design.canvas.delete': 'حذف',
    'maker.design.canvas.clear': 'مسح',
    'maker.design.canvas.dimensions': 'الأبعاد',
    'maker.design.canvas.splitters': 'فواصل',
    'maker.design.canvas.sashes': 'أوشحة',
    'maker.design.canvas.title': 'رسم فني ثنائي الأبعاد',

    // Maker - Templates
    'maker.design.templates.prompt': 'اختر نموذجاً محدداً مسبقاً أو أنشئ هندسة مخصصة',
    'maker.design.templates.custom': 'إنشاء هندسة مخصصة',
    'maker.design.template.custom.name': 'مخصص',
    'maker.design.template.custom.desc': 'رسم هندسة مخصصة',
    'maker.design.template.casement_single': 'مفصلي مفرد',
    'maker.design.template.casement_single.desc': 'نافذة مفصلية قياسية بجزء واحد',
    'maker.design.template.casement_double': 'مفصلي مزدوج',
    'maker.design.template.casement_double.desc': 'نافذة بجزئين',
    'maker.design.template.tilt_turn': 'قلاب ومفصلي',
    'maker.design.template.tilt_turn.desc': 'وظيفة الإمالة والدوران',
    'maker.design.template.sliding': 'نافذة سحاب',
    'maker.design.template.sliding.desc': 'نافذة سحاب أفقية',
    'maker.design.template.round': 'نافذة دائرية',
    'maker.design.template.round.desc': 'نافذة دائرية الشكل',
    'maker.design.template.french': 'نافذة فرنسية',
    'maker.design.template.french.desc': 'نافذة على الطراز الفرنسي',
    'maker.design.template.door_single': 'باب مفرد',
    'maker.design.template.door_single.desc': 'باب قياسي مفرد',
    'maker.design.template.door_double': 'باب مزدوج',
    'maker.design.template.door_double.desc': 'باب بجزئين',
    'maker.design.template.door_sliding': 'باب سحاب',
    'maker.design.template.door_sliding.desc': 'نظام باب سحاب',
    'maker.design.template.door_folding': 'باب قابل للطي',
    'maker.design.template.door_folding.desc': 'نظام باب قابل للطي',

    // Maker - Components
    'maker.design.components.prompt': 'إضافة أجهزة، مصاريع، حواجز وملحقات',
    'maker.design.components.pool': 'مجموعة من {0}',
    'maker.design.components.cat.hardware': 'أجهزة',
    'maker.design.components.cat.shutter': 'مصاريع',
    'maker.design.components.cat.screen': 'حواجز',
    'maker.design.components.cat.roller': 'مصاريع دوارة',
    // Component Items
    'maker.component.handle_std': 'مقبض قياسي',
    'maker.component.handle_std.desc': 'مقبض قياسي للنافذة/الباب',
    'maker.component.handle_prem': 'مقبض فاخر',
    'maker.component.handle_prem.desc': 'مقبض بتشطيب فاخر',
    'maker.component.lock_multi': 'قفل متعدد النقاط',
    'maker.component.lock_multi.desc': 'نظام قفل بـ 3 نقاط',
    'maker.component.lock_dead': 'قفل بمزلاج',
    'maker.component.lock_dead.desc': 'قفل بمزلاج قياسي',
    'maker.component.hinge_butt': 'مفصلة تناكبية',
    'maker.component.hinge_butt.desc': 'مفصلة تناكبية قياسية',
    'maker.component.hinge_conc': 'مفصلة مخفية',
    'maker.component.hinge_conc.desc': 'نظام مفصلة مخفية',
    'maker.component.stay': 'محدد فتح',
    'maker.component.stay.desc': 'محدد فتح النافذة',
    'maker.component.shutter_louver': 'مصراع بفتحات تهوية',
    'maker.component.shutter_louver.desc': 'مصراع بفتحات قابلة للتعديل',
    'maker.component.shutter_panel': 'مصراع لوحي',
    'maker.component.shutter_panel.desc': 'مصراع لوحي صلب',
    'maker.component.shutter_batten': 'مصراع خشبي',
    'maker.component.shutter_batten.desc': 'نمط خشبي تقليدي',
    'maker.component.screen_std': 'حاجز حشرات قياسي',
    'maker.component.screen_std.desc': 'شبكة حاجز حشرات قياسية',
    'maker.component.screen_retr': 'حاجز قابل للسحب',
    'maker.component.screen_retr.desc': 'حاجز قابل للسحب للأعلى',
    'maker.component.screen_mag': 'حاجز مغناطيسي',
    'maker.component.screen_mag.desc': 'حاجز بتثبيت مغناطيسي',
    'maker.component.roller_man': 'مصراع دوار يدوي',
    'maker.component.roller_man.desc': 'مصراع دوار بتشغيل يدوي',
    'maker.component.roller_elec': 'مصراع دوار كهربائي',
    'maker.component.roller_elec.desc': 'مصراع دوار بمحرك',
    'maker.component.roller_solar': 'مصراع دوار شمسي',
    'maker.component.roller_solar.desc': 'مصراع دوار يعمل بالطاقة الشمسية',

    // Maker - Shapes
    'maker.shape.rectangular': 'مستطيل',
    'maker.shape.round': 'دائري',
    'maker.shape.trapezoid': 'شبه منحرف',
    'maker.shape.custom': 'مخصص',

    // Design Sheet
    'maker.design.sheet.title': 'ورقة التصميم وعروض الأسعار',
    'maker.design.sheet.empty.title': 'لم يتم تحديد تصميم',
    'maker.design.sheet.empty.desc': 'حدد نافذة أو بابًا لعرض ورقة التصميم',
    'maker.design.sheet.section.dimensions': 'الأبعاد',
    'maker.design.sheet.section.specs': 'المواصفات',
    'maker.design.sheet.section.components': 'المكونات',
    'maker.design.sheet.section.quotation': 'عرض السعر',
    'maker.design.sheet.label.width': 'العرض (مم)',
    'maker.design.sheet.label.height': 'الارتفاع (مم)',
    'maker.design.sheet.label.area': 'المساحة',
    'maker.design.sheet.label.type': 'النوع',
    'maker.design.sheet.label.shape': 'الشكل',
    'maker.design.sheet.label.material': 'المادة',
    'maker.design.sheet.label.profile': 'القطاع',
    'maker.design.sheet.quotation.unitPrice': 'سعر الوحدة',
    'maker.design.sheet.quotation.quantity': 'الكمية',
    'maker.design.sheet.quotation.discount': 'الخصم (%)',
    'maker.design.sheet.quotation.basePrice': 'السعر الأساسي',
    'maker.design.sheet.quotation.subtotal': 'المجموع الفرعي',
    'maker.design.sheet.quotation.total': 'الإجمالي',
    'maker.design.sheet.quotation.notes': 'أضف ملاحظات أو تعليمات خاصة...',
    'maker.design.sheet.noComponents': 'لم تتم إضافة أي مكونات',

    // Maker - Design
    'maker.design.title': 'استوديو التصميم',
    'maker.design.select_project': 'اختر مشروعاً',
    'maker.design.new': 'تصميم جديد',
    'maker.design.templates': 'القوالب',
    'maker.design.components': 'المكونات',
    'maker.design.export_cnc': 'تصدير CNC',
    'maker.design.reports': 'التقارير',
    'maker.design.tab.design': 'عرض التصميم',
    'maker.design.tab.sheet': 'الورقة والتقدير',
    'maker.design.tab.windows': 'نوافذ ({0})',
    'maker.design.tab.doors': 'أبواب ({0})',
    'maker.design.items_list': 'العناصر',
    'maker.design.no_items': 'لم يتم العثور على عناصر',
    'maker.design.select_prompt': 'يرجى اختيار مشروع للبدء في التصميم',
    'maker.design.select_item_prompt': 'يرجى اختيار عنصر من القائمة أو إنشاء عنصر جديد',
    'maker.design.no_item_selected': 'لم يتم اختيار أي عنصر',
    'maker.design.properties': 'الخصائص',
    'maker.design.field.width': 'العرض (مم)',
    'maker.design.field.height': 'الارتفاع (مم)',
    'maker.design.field.material': 'المادة',
    'maker.design.field.profile': 'الملف الشخصي',
    'maker.design.action.save': 'حفظ',
    'maker.design.delete.confirm': 'هل أنت متأكد من أنك تريد حذف هذا التصميم؟',
    'maker.design.modal.title': 'إنشاء {0} جديد',
    'maker.design.modal.type': 'النوع',
    'maker.design.modal.shape': 'الشكل',
    'maker.design.modal.cancel': 'إلغاء',
    'maker.design.modal.create': 'إنشاء',
    'maker.design.validation.project': 'يرجى اختيار مشروع',
    'maker.design.validation.dimensions': 'يجب أن تكون الأبعاد موجبة وأقل من 5000 مم',
    'maker.design.validation.profile': 'يرجى اختيار ملف شخصي للمادة المحددة',

    // Maker - Reports
    'maker.reports.title': 'التقارير والتحليلات',
    'maker.reports.type.label': 'نوع التقرير',
    'maker.reports.type.sales': 'تقرير المبيعات',
    'maker.reports.type.projects': 'تقرير المشاريع',
    'maker.reports.type.materials': 'تقرير المواد',
    'maker.reports.type.customers': 'تقرير العملاء',
    'maker.reports.type.production': 'تقرير الإنتاج',
    'maker.reports.export': 'تصدير CSV',
    'maker.reports.date.from': 'من',
    'maker.reports.date.to': 'إلى',
    'maker.reports.stat.revenue': 'الإيرادات',
    'maker.reports.stat.estimates': 'التقديرات',
    'maker.reports.stat.approved': 'موافق عليه',
    'maker.reports.stat.pending': 'قيد الانتظار',
    'maker.reports.stat.avg_value': 'متوسط القيمة',
    'maker.reports.stat.total_projects': 'إجمالي المشاريع',
    'maker.reports.stat.completed': 'مكتمل',
    'maker.reports.stat.total_items': 'إجمالي العناصر',
    'maker.reports.stat.total_materials': 'إجمالي المواد',
    'maker.reports.stat.stock_value': 'قيمة المخزون',
    'maker.reports.stat.active': 'نشط',
    'maker.reports.stat.total_customers': 'إجمالي العملاء',
    'maker.reports.stat.project_value': 'قيمة المشروع',
    'maker.reports.stat.doors': 'أبواب',
    'maker.reports.stat.windows': 'نوافذ',
    'maker.reports.table.estimate': 'رقم التقدير',
    'maker.reports.table.date': 'التاريخ',
    'maker.reports.table.project': 'المشروع',
    'maker.reports.table.status': 'الحالة',
    'maker.reports.table.total': 'الإجمالي',
    'maker.reports.table.customer': 'العميل',
    'maker.reports.table.created': 'تاريخ الإنشاء',
    'maker.reports.table.items': 'العناصر',
    'maker.reports.table.name': 'الاسم',
    'maker.reports.table.code': 'الكود',
    'maker.reports.table.stock': 'المخزون',
    'maker.reports.table.value': 'القيمة',
    'maker.reports.table.usage': 'الاستخدام',
    'maker.reports.table.company': 'الشركة',
    'maker.reports.table.estimates': 'التقديرات',
    'maker.reports.dist.material': 'توزيع المواد',


    // Maker - Design Types
    'maker.design.type.casement': 'مفصلي',
    'maker.design.type.tilt_turn': 'قلاب ومفصلي',
    'maker.design.type.sliding': 'سحاب',
    'maker.design.type.french': 'فرنسي',
    'maker.design.type.italian': 'إيطالي',
    'maker.design.type.awning': 'مظلة',
    'maker.design.type.parallel_sliding': 'سحاب متوازي',
    'maker.design.type.pivot': 'محوري',
    'maker.design.type.accordion': 'أكورديون',
    'maker.design.type.lift_sliding': 'رفع وسحب',
    'maker.design.type.single': 'مفرد',
    'maker.design.type.double': 'مزدوج',
    'maker.design.type.folding': 'قابل للطي',
    'maker.design.type.revolving': 'دوار',
    'maker.design.type.unknown': 'غير معروف',
    // Module Parc (Extrait auto)
    'parc.alerts.fetchError': 'فشل في تحميل تنبيهات GPS',
    'parc.alerts.resolveError': 'فشل في حل التنبيه',
    'parc.alerts.vehicle': 'المركبة',
    'parc.alerts.type': 'نوع التنبيه',
    'parc.alerts.message': 'الرسالة',
    'parc.alerts.time': 'الوقت',
    'parc.alerts.status': 'الحالة',
    'parc.alerts.statusResolved': 'تم الحل',
    'parc.alerts.statusActive': 'نشط',
    'common.actions.refresh': 'تحديث',
    'parc.alerts.title': 'تنبيهات GPS',
    'parc.alerts.resolveAlertTitle': 'حل تنبيه GPS',
    'parc.dashboard.fetchError': 'فشل في تحميل بيانات لوحة القيادة',
    'parc.dashboard.title': 'لوحة القيادة',
    'parc.dashboard.overview': 'نظرة عامة على أسطولك',
    'parc.dashboard.vehicles': 'المركبات',
    'parc.dashboard.totalRegistered': 'إجمالي المسجلين',
    'parc.dashboard.drivers': 'السائقين',
    'parc.dashboard.maintenances': 'الصيانات',
    'parc.dashboard.pendingOrInProgress': 'قيد الانتظار أو قيد التقدم',
    'parc.dashboard.repairs': 'الإصلاحات',
    'parc.dashboard.toCheckOrInProgress': 'للتحقق أو قيد التقدم',
    'parc.dashboard.vehicleStatus': 'حالة المركبات',
    'common.status.noData': 'لا توجد بيانات متاحة',
    'parc.dashboard.monthlyPerformance': 'الأداء الشهري (الرحلات)',
    'parc.dashboard.trips': 'الرحلات',
    'parc.dashboard.alerts': 'تنبيهات',
    'parc.dashboard.noAlerts': 'لا توجد تنبيهات في الوقت الحالي',
    'parc.dashboard.recentActivity': 'النشاط الأخير',
    'parc.dashboard.noRecentActivity': 'لا يوجد نشاط أخير',
    'parc.documents.fetchError': 'فشل في تحميل البيانات',
    'common.actions.deleteConfirm': 'هل أنت متأكد أنك تريد حذف هذا العنصر؟',
    'common.actions.actions': 'إجراءات',
    'common.messages.deleted': 'تم الحذف بنجاح',
    'parc.documents.docTitle': 'العنوان',
    'parc.documents.type': 'النوع',
    'parc.documents.vehicle': 'المركبة',
    'parc.documents.driver': 'السائق',
    'parc.documents.expiryDate': 'تاريخ الانتهاء',
    'parc.documents.newDocument': 'مستند جديد',
    'parc.documents.title': 'المستندات',
    'parc.documents.editDocument': 'تعديل المستند',
    'parc.documents.typeInsurance': 'تأمين',
    'parc.documents.typeTechControl': 'مراقبة تقنية',
    'parc.documents.typeRegistration': 'تسجيل',
    'parc.documents.typeContract': 'إيجار/عقد',
    'parc.documents.typeOther': 'أخرى',
    'parc.documents.assignVehicle': 'تخصيص للمركبة',
    'common.labels.none': 'غير متوفر',
    'academy.menu.email_templates': 'نماذج البريد الإلكتروني',
    'academy.email_templates.title': 'نماذج البريد الإلكتروني والإشعارات',
    'academy.email_templates.new_title': 'نموذج بريد جديد',
    'academy.email_templates.edit_title': 'تعديل النموذج: {0}',
    'academy.email_templates.btn_new': 'نموذج جديد',
    'academy.email_templates.btn_refresh': 'تحديث',
    'academy.email_templates.btn_back': '← العودة إلى القائمة',
    'academy.email_templates.btn_reset': 'إعادة تعيين',
    'academy.email_templates.btn_save': 'حفظ التغييرات',
    'academy.email_templates.btn_create': 'إنشاء النموذج',
    'academy.email_templates.filter_type': 'تصفية حسب نوع البريد',
    'academy.email_templates.filter_type_all': 'جميع أنواع البريد',
    'academy.email_templates.filter_lang': 'تصفية حسب اللغة',
    'academy.email_templates.filter_lang_all': 'جميع اللغات',
    'academy.email_templates.filter_status': 'الحالة',
    'academy.email_templates.filter_status_all': 'جميع الحالات',
    'academy.email_templates.filter_active': 'النشطة فقط',
    'academy.email_templates.filter_inactive': 'المعطلة فقط',
    'academy.email_templates.results': 'تم العثور على {0} نموذج',
    'academy.email_templates.col_status': 'الحالة',
    'academy.email_templates.col_lang': 'اللغة',
    'academy.email_templates.col_type': 'نوع البريد',
    'academy.email_templates.col_name': 'اسم النموذج',
    'academy.email_templates.col_subject': 'الموضوع',
    'academy.email_templates.col_updated': 'آخر تحديث',
    'academy.email_templates.status_active': 'نشط',
    'academy.email_templates.status_inactive': 'معطل',
    'academy.email_templates.card_code': 'الكود: {0}',
    'academy.email_templates.card_subject': 'الموضوع:',
    'academy.email_templates.card_vars': 'متغيرات ({0}):',
    'academy.email_templates.card_updated': 'تحديث: {0}',
    'academy.email_templates.card_edit': 'تعديل النموذج',
    'academy.email_templates.card_delete': 'حذف',
    'academy.email_templates.search_placeholder': 'البحث بالاسم أو الموضوع أو الكود...',
    'academy.email_templates.section1': '1. إعدادات النموذج',
    'academy.email_templates.field_name': 'اسم النموذج',
    'academy.email_templates.field_name_placeholder': 'مثال: تذكير بالدفع (عربي)',
    'academy.email_templates.field_code': 'كود فريد',
    'academy.email_templates.field_code_helper': 'مثال: DEBT_REMINDER_ADULT_AR',
    'academy.email_templates.field_type': 'نوع البريد (حدث التشغيل)',
    'academy.email_templates.field_lang': 'لغة النموذج',
    'academy.email_templates.lang_fr': '🇫🇷 فرنسي (FR)',
    'academy.email_templates.lang_ar': '🇸🇦 عربي (العربية - AR)',
    'academy.email_templates.lang_en': '🇬🇧 إنجليزي (English - EN)',
    'academy.email_templates.active_label': 'نموذج نشط (الإرسال مسموح)',
    'academy.email_templates.inactive_label': 'نموذج معطل',
    'academy.email_templates.active_hint': 'ستُرسل رسائل بريد هذا النوع تلقائيًا.',
    'academy.email_templates.inactive_hint': '⚠️ عند التعطيل، لن يُرسل النظام أي بريد لهذا النوع.',
    'academy.email_templates.field_desc': 'وصف / ملاحظات داخلية',
    'academy.email_templates.field_desc_placeholder': 'استخدام داخلي أو ملاحظات حول هذا النموذج...',
    'academy.email_templates.section2': '2. الموضوع & رأس الرسالة',
    'academy.email_templates.field_subject': 'موضوع البريد',
    'academy.email_templates.field_subject_placeholder': 'مثال: [{agencyName}] تذكير بالدفع - الرصيد: {totalBalanceDue} {currency}',
    'academy.email_templates.field_subject_helper': 'يمكنك تضمين متغيرات مثل {agencyName}، {student.fullName}، إلخ.',
    'academy.email_templates.section3': '3. جسم الرسالة HTML & المتغيرات',
    'academy.email_templates.tags_hint': 'انقر على متغير لإدراجه في الجسم:',
    'academy.email_templates.field_body': 'كود HTML للرسالة',
    'academy.email_templates.field_body_placeholder': '<!DOCTYPE html><html><body>...</body></html>',
    'academy.email_templates.preview_title': 'معاينة مباشرة',
    'academy.email_templates.preview_hint': 'يتم استبدال المتغيرات {\'..\'} تلقائيًا ببيانات نموذجية.',
    'academy.email_templates.preview_subject': 'معاينة الموضوع:',
    'academy.email_templates.preview_empty': '(لا يوجد موضوع)',
    'academy.email_templates.confirm_delete_title': 'حذف هذا النموذج؟',
    'academy.email_templates.confirm_delete_msg': 'هل تريد حذف النموذج \'{0}\' بالتأكيد؟',
    'academy.email_templates.msg_created': 'تم إنشاء النموذج بنجاح.',
    'academy.email_templates.msg_updated': 'تم تحديث النموذج بنجاح.',
    'academy.email_templates.msg_deleted': 'تم حذف النموذج بنجاح.',
    'academy.email_templates.err_name': 'يرجى إدخال اسم النموذج.',
    'academy.email_templates.err_type': 'يرجى اختيار نوع البريد.',
    'academy.email_templates.err_subject': 'يرجى إدخال موضوع البريد.',
    'academy.email_templates.err_body': 'جسم HTML مطلوب.',
    'academy.email_templates.lang_ar_flag': '🇸🇦 AR',
    'academy.email_templates.lang_en_flag': '🇬🇧 EN',
    'academy.email_templates.lang_fr_flag': '🇫🇷 FR',
    'academy.email_templates.lang_ar_rtl': '🇸🇦 عربي (RTL)',
    'academy.email_templates.lang_en_chip': '🇬🇧 إنجليزي',
    'academy.email_templates.lang_fr_chip': '🇫🇷 فرنسي',
    'academy.email_templates.grid_view': 'عرض شبكة',
    'academy.email_templates.table_view': 'عرض جدول',

    // Academy Subscription & SATIM
    'academy.menu.subscription': 'الاشتراك والفوترة',
    'subscription.pageTitle': 'إدارة الاشتراك والفوترة',
    'subscription.tabUsage': 'الاستهلاك والحصص',
    'subscription.tabPlans': 'الباقات والأسعار',
    'subscription.loading': 'جاري تحميل تفاصيل الاشتراك...',
    'subscription.successTitle': 'تم تفعيل الاشتراك!',
    'subscription.successDesc': 'تمت معالجة دفع SATIM بنجاح (المرجع: {0}). تم تحديث حصص باقتك.',
    'subscription.changePlan': 'تغيير أو ترقية الباقة',
    'subscription.securedBy': 'دفع آمن ومحمي عبر',
    'subscription.plan': 'الباقة المحددة',
    'subscription.yearly': 'فوترة سنوية',
    'subscription.monthly': 'فوترة شهرية',
    'subscription.totalDue': 'المبلغ الإجمالي المستحق',
    'subscription.currentPlan': 'الباقة الحالية',
    'subscription.chooseFree': 'اختيار المجاني',
    'subscription.payWithSatim': 'الدفع عبر SATIM',
    'subscription.upgradeNow': 'ترقية باقة اشتراكي',
    'subscription.usage': 'استهلاك الحصة',
    'subscription.status.trial': 'فترة تجريبية مجانية (متبقي {0} يوم)',
    'subscription.status.active': 'اشتراك نشط',
    'subscription.status.expired': 'اشتراك منتهي الصلاحية',
    'subscription.quotasTitle': 'الحصص والاستهلاك المباشر',
    'subscription.quota.students': 'المتعلمين',
    'subscription.quota.studentsSub': 'المسجلون في الأكاديمية',
    'subscription.quota.teachers': 'الأساتذة',
    'subscription.quota.teachersSub': 'المعلمون النشطون',
    'subscription.quota.users': 'الموظفون والمستخدمون',
    'subscription.quota.usersSub': 'حسابات الوصول',
    'subscription.quota.agencies': 'الفروع والوكالات',
    'subscription.quota.agenciesSub': 'مراكز التدريب',
    'subscription.quota.storage': 'التخزين السحابي',
    'subscription.quota.storageSub': 'الصور وملفات PDF والنسخ',
    'subscription.quota.sms': 'رصيد الرسائل القصيرة',
    'subscription.quota.smsSub': 'الإشعارات والتذكيرات',
    'subscription.invoicesTitle': 'سجل الدفعات والفواتير',
    'subscription.pricingTitle': 'اختر الباقة المناسبة لأكاديميتك',
    'subscription.pricingSubtitle': 'دفع إلكتروني مباشر وآمن ببطاقة CIB أو الذهبية (SATIM). تفعيل فوري.',
    'subscription.satim.testSandbox': 'وضع الاختبار التجريبي (تعبئة سريعة):',
    'subscription.satim.cardNumber': 'رقم البطاقة (16 رقمًا)',
    'subscription.satim.cardExpiry': 'تاريخ انتهاء الصلاحية (MM/YY)',
    'subscription.satim.cardCvv': 'رمز الأمان CVV2 (3 أرقام)',
    'subscription.satim.cardHolder': 'اسم حامل البطاقة الكامل',
    'subscription.satim.enterOtp': 'رمز التحقق (OTP)',
    'subscription.satim.processing': 'الاتصال بخادم SATIM...',
    'subscription.satim.payNow': 'متابعة الدفع',
    'subscription.satim.validating': 'جاري التحقق...',
    'subscription.satim.confirmPayment': 'تأكيد وخصم المبلغ',
    'subscription.satim.continueToPortal': 'الدخول إلى أكاديميتي',
    'subscription.satim.invalidCardNumber': 'رقم البطاقة غير صالح (مطلوب 16 رقمًا).',
    'subscription.satim.invalidExpiry': 'تاريخ انتهاء غير صالح (MM/YY).',
    'subscription.satim.invalidCvv': 'رمز الأمان CVV2 غير صالح.',
    'subscription.satim.invalidOtp': 'يرجى إدخال رمز SMS المستلم.',
    'subscription.limit.studentsTitle': 'تم الوصول للحد الأقصى للطلاب',
    'subscription.limit.teachersTitle': 'تم الوصول للحد الأقصى للأساتذة',
    'subscription.limit.storageTitle': 'مساحة التخزين ممتلئة',
    'subscription.limit.featureTitle': 'ميزة مقفلة',
    'subscription.limit.studentsDesc': 'باقتك الحالية ({0}) محددة بـ {1} طالبًا. لتسجيل طلاب جدد، يرجى ترقية باقتك.',
    'subscription.limit.teachersDesc': 'باقتك الحالية ({0}) محددة بـ {1} أستاذًا. يرجى الترقية لإضافة معلمين جدد.',
    'subscription.limit.featureDesc': 'الميزة "{0}" غير متوفرة في باقة {1}. يمكنك فتحها عن طريق ترقية اشتراكك.',
    'subscription.limit.instantActivation': 'تفعيل فوري عن طريق الدفع عبر SATIM (CIB / الذهبية).',
    'parc.documents.assignDriver': 'تخصيص للسائق',
    'common.messages.unknownError': 'خطأ غير معروف',
    'parc.drivers.fetchError': 'فشل في تحميل السائقين',
    'parc.drivers.firstName': 'الاسم الأول',
    'parc.drivers.lastName': 'اسم العائلة',
    'parc.drivers.licenseNumber': 'رقم الرخصة',
    'parc.drivers.licenseType': 'نوع الرخصة',
    'parc.drivers.experienceYears': 'الخبرة (بالسنوات)',
    'parc.drivers.status': 'الحالة',
    'parc.drivers.newDriver': 'سائق جديد',
    'parc.drivers.title': 'السائقين',
    'parc.drivers.editDriver': 'تعديل السائق',
    'parc.drivers.statusActive': 'نشط',
    'parc.drivers.statusInactive': 'غير نشط',
    'parc.drivers.statusSuspended': 'موقوف',
    'parc.drivers.statusOnLeave': 'في إجازة',
    'parc.maintenance.fetchError': 'فشل في تحميل بيانات الصيانة',
    'parc.maintenance.mainTitle': 'العنوان',
    'parc.maintenance.vehicle': 'المركبة',
    'parc.maintenance.type': 'النوع',
    'parc.maintenance.category': 'الفئة',
    'parc.maintenance.status': 'الحالة',
    'parc.maintenance.priority': 'الأولوية',
    'parc.maintenance.newMaintenance': 'صيانة جديدة',
    'parc.maintenance.title': 'الصيانة',
    'parc.maintenance.editMaintenance': 'تعديل الصيانة',
    'parc.maintenance.typePreventive': 'وقائية',
    'parc.maintenance.typeCorrective': 'مصححة',
    'parc.maintenance.typeEmergency': 'طوارئ',
    'parc.maintenance.catEngine': 'المحرك',
    'parc.maintenance.catTransmission': 'ناقل الحركة',
    'parc.maintenance.catBrakes': 'الفرامل',
    'parc.maintenance.catElectrical': 'كهرباء',
    'parc.maintenance.catBody': 'الهيكل',
    'parc.maintenance.catTires': 'الإطارات',
    'parc.maintenance.catOther': 'أخرى',
    'parc.maintenance.priorityLow': 'منخفضة',
    'parc.maintenance.priorityMedium': 'متوسطة',
    'parc.maintenance.priorityHigh': 'عالية',
    'parc.maintenance.priorityUrgent': 'عاجلة',
    'parc.maintenance.statusScheduled': 'مجدولة',
    'parc.maintenance.statusInProgress': 'قيد التنفيذ',
    'parc.maintenance.statusCompleted': 'مكتملة',
    'parc.maintenance.statusCancelled': 'ملغاة',
    'parc.maintenance.cost': 'التكلفة',
    'parc.repairs.fetchError': 'فشل في تحميل البيانات',
    'parc.repairs.vehicle': 'المركبة',
    'parc.repairs.date': 'التاريخ',
    'parc.repairs.garage': 'المرآب',
    'parc.repairs.cost': 'التكلفة',
    'parc.repairs.status': 'الحالة',
    'parc.repairs.newRepair': 'إصلاح جديد',
    'parc.repairs.title': 'الإصلاحات',
    'parc.repairs.editRepair': 'تعديل الإصلاح',
    'parc.repairs.statusPlanned': 'مخطط له',
    'parc.repairs.statusInProgress': 'قيد التنفيذ',
    'parc.repairs.statusCompleted': 'مكتمل',
    'parc.repairs.statusCancelled': 'ملغى',
    'parc.repairs.description': 'الوصف',
    'parc.gpsTracking.fetchError': 'فشل في تحميل مواقع GPS',
    'parc.gpsTracking.vehicle': 'المركبة',
    'parc.gpsTracking.brand': 'العلامة التجارية',
    'parc.gpsTracking.latitude': 'خط العرض',
    'parc.gpsTracking.longitude': 'خط الطول',
    'parc.gpsTracking.speedCol': 'السرعة (كم/س)',
    'parc.gpsTracking.lastUpdate': 'آخر تحديث',
    'parc.gpsTracking.title': 'تتبع GPS',
    'parc.gpsTracking.unknownVehicle': 'مركبة غير معروفة',
    'parc.gpsTracking.speedVal': 'السرعة:',
    'parc.gpsTracking.updatedVal': 'تم التحديث:',
    'parc.trips.fetchError': 'فشل في تحميل بيانات الرحلات',
    'parc.trips.tripTitle': 'العنوان',
    'parc.trips.vehicle': 'المركبة',
    'parc.trips.driver': 'السائق',
    'parc.trips.purpose': 'الغرض',
    'parc.trips.status': 'الحالة',
    'parc.trips.newTrip': 'رحلة جديدة',
    'parc.trips.title': 'الرحلات',
    'parc.trips.editTrip': 'تعديل الرحلة',
    'parc.trips.purposeBusiness': 'عمل',
    'parc.trips.purposePersonal': 'شخصي',
    'parc.trips.purposeMaintenance': 'صيانة',
    'parc.trips.purposeDelivery': 'توصيل',
    'parc.trips.purposeOther': 'أخرى',
    'parc.trips.statusScheduled': 'مجدولة',
    'parc.trips.statusInProgress': 'قيد التنفيذ',
    'parc.trips.statusCompleted': 'مكتملة',
    'parc.trips.statusCancelled': 'ملغاة',
    'parc.vehicles.fetchError': 'فشل في تحميل المركبات',
    'parc.vehicles.registration': 'رقم التسجيل',
    'parc.vehicles.brand': 'العلامة التجارية',
    'parc.vehicles.model': 'الموديل',
    'parc.vehicles.year': 'السنة',
    'parc.vehicles.status': 'الحالة',
    'parc.vehicles.newVehicle': 'مركبة جديدة',
    'parc.vehicles.title': 'المركبات',
    'parc.vehicles.editVehicle': 'تعديل المركبة',
    'parc.vehicles.statusActive': 'نشطة',
    'parc.vehicles.statusMaintenance': 'صيانة',
    'parc.vehicles.statusOutOfService': 'خارج الخدمة',
    'parc.vehicles.mileage': 'عدد الأميال',
    'parc.vehicles.fuelType': 'نوع الوقود',
    'parc.vehicles.fuelDiesel': 'ديزل',
    'parc.vehicles.fuelGasoline': 'بنزين',
    'parc.vehicles.fuelElectric': 'كهربائي',
    'parc.vehicles.fuelHybrid': 'هجين',
    'parc.vehicles.transmission': 'ناقل الحركة',
    'parc.vehicles.transManual': 'يدوي',
    'parc.vehicles.transAutomatic': 'أوتوماتيكي',
    'grid.filters.title': 'تصفية',
    'grid.search.placeholder': 'بحث...',
    'grid.filters.all': 'الكل',
    'grid.filters.clear': 'مسح',
    'grid.counter.item': 'عنصر',
    'grid.counter.items': 'عناصر',
    'grid.noData': 'لا توجد نتائج مطابقة لبحثك.',
    'grid.pagination.info': 'عرض {0}-{1} من {2} مدخلات',
    'grid.pagination.pageSize': 'العناصر في الصفحة:',
    'parc.fuel.newTransaction': 'تسجيل تعبئة وقود',
    'parc.fuel.editTransaction': 'تعديل المعاملة',
    'parc.fuel.station': 'محطة الخدمة',
    'parc.fuel.product': 'المنتج (مثال: مازوت، بنزين)',
    'parc.fuel.liters': 'اللترات',
    'parc.fuel.pricePerLiter': 'السعر للتر الواحد',
    'parc.fuel.cost': 'التكلفة الإجمالية (دج)',
    'parc.fuel.odometer': 'المسافة المقطوعة (عداد المسافة)',
    'parc.fuel.fuelCard': 'بطاقة الوقود',
    'parc.fuel.none': 'لا توجد',
    'parc.fuelcards.cardNumber': 'رقم البطاقة',
    'parc.fuelcards.vehicle': 'المركبة المعينة',
    'parc.fuelcards.driver': 'السائق المعين',
    'parc.fuelcards.globalCost': 'التكلفة الإجمالية المستهلكة',
    'parc.fuelcards.pricePerLiter': 'سعر الأساس/لتر',
    'parc.fuelcards.newCard': 'بطاقة جديدة',
    'parc.fuelcards.editCard': 'تعديل البطاقة',
    'parc.fuelcards.addCard': 'إضافة بطاقة وقود',
    'parc.mileage.newLog': 'قراءة جديدة',
    'parc.mileage.editLog': 'تعديل القراءة',
    'parc.mileage.addLog': 'إضافة قراءة لعداد المسافة',
    'parc.mileage.startDate': 'تاريخ البدء',
    'parc.mileage.endDate': 'تاريخ الانتهاء',
    'parc.mileage.startKm': 'عداد البدء',
    'parc.mileage.endKm': 'عداد النهاية',
    'parc.mileage.distance': 'المسافة',
    'parc.mileage.errorEndKmLessThanStartKm': 'لا يمكن أن يكون عداد النهاية أقل من عداد البدء.',
    'parc.lookups.title': 'جداول المراجع',
    'parc.lookups.proprietors': 'المالكون',
    'parc.lookups.fuelTypes': 'أنواع الوقود',
    'parc.lookups.affectations': 'التعيينات / الخدمات',
    'parc.lookups.docTypes': 'أنواع المستندات',
    'parc.lookups.mntTypes': 'أنواع الصيانة',
    'parc.gpsalerts.title': 'تنبيهات GPS',
    'parc.gpsalerts.newAlert': 'تنبيه جديد',
    'parc.gpsalerts.editAlert': 'تعديل التنبيه',
    'parc.gpsalerts.date': 'التاريخ والوقت',
    'parc.gpsalerts.type': 'النوع',
    'parc.gpsalerts.message': 'الرسالة',
    'parc.gpsalerts.status': 'الحالة',
    'parc.gpsalerts.type.speeding': 'السرعة الزائدة',
    'parc.gpsalerts.type.geofence': 'السياج الجغرافي',
    'parc.gpsalerts.type.maintenance': 'الصيانة',
    'parc.gpsalerts.type.system': 'النظام',
    'parc.gpsalerts.type.other': 'أخرى',
    'parc.gpsalerts.status.active': 'نشط',
    'parc.gpsalerts.status.resolved': 'تم الحل',
    'parc.gpsalerts.status.ignored': 'تم تجاهله',
    'parc.analysis.generalTitle': 'التحليل العام للأسطول',
    'parc.analysis.kmsTraveled': 'المسافة المقطوعة (كم)',
    'parc.analysis.fuelExpenses': 'نفقات الوقود',
    'parc.analysis.availabilityIndex': 'مؤشر توفر الأسطول',
    'parc.analysis.optimizations': 'التحسينات والتقديرات',
    'parc.analysis.avgConsumption': 'متوسط استهلاك الأسطول:',
    'parc.analysis.estAnnualFuel': 'تكلفة الوقود السنوية المقدرة:',
    'parc.analysis.nextOilChange': 'التاريخ المتوقع لتغيير الزيت القادم:',
    'parc.analysis.warningTitle': 'تنبيه - حالة الأسطول',
    'parc.analysis.warningMaintOrOut': '{0}٪ من الأسطول قيد الصيانة أو خارج الخدمة.',
    'parc.analysis.vehiclesActiveImmobilized': '{0} نشط / {1} معطل',
    'parc.analysis.kmsCumulatedDesc': 'تراكمي للمسافة الإجمالية للأسطول',
    'parc.analysis.litersConsumedTotal': 'إجمالي المستهلك {0} لتر',
    'parc.analysis.availabilityIndexDesc': 'يوضح هذا المؤشر نسبة المركبات العاملة حاليًا مقارنة بحجم الأسطول الإجمالي. تشير النسبة التي تتجاوز 90٪ إلى تخطيط صيانة ممتاز.',
    'parc.analysis.insufficientData': 'بيانات غير كافية',
    'parc.analysis.nextOilChangeValue': 'خلال 45 يومًا (تقديري)',
    'parc.analysis.tcoTitle': 'تحليل تكلفة الملكية الإجمالية (TCO)',
    'parc.analysis.globalTco': 'تكلفة الملكية الإجمالية',
    'parc.analysis.totalFuel': 'إجمالي الوقود',
    'parc.analysis.totalMaint': 'إجمالي الصيانة',
    'parc.analysis.totalRepairs': 'إجمالي الإصلاحات',
    'parc.analysis.costPerKm': 'التكلفة / كم',

    // Clôture Agence & Planning
    'academy.scheduler.closed_chip': "مغلق",
    'academy.scheduler.closed_warning': "هذه الحصة تقع ضمن فترة مغلقة. التعديل والإلغاء غير متاحين.",
    'academy.scheduler.error_closed_planning': "لا يمكن التعديل: التخطيط مغلق حتى {0}.",
    'academy.financial.error_closed_invoices': "لا يمكن التعديل: الوكالة مغلقة حتى {0}.",
    'academy.financial.invoice_locked_tooltip': "فاتورة مقفلة (فترة الوكالة مغلقة)",

    // Student Card & QR Attendance
    'academy.students.print_card': "شارة التلميذ",
    'academy.students.card_title': "شارة التلميذ",
    'academy.students.card_subtitle': "شارة التلميذ",
    'academy.students.print_badge': "طباعة الشارة",
    'academy.students.student_code': "رمز الطالب",
    'academy.students.emergency_short': "طوارئ",
    'academy.attendance.scan_qr': "مسح الحضور QR",
    'academy.attendance.scanner_title': "ماسح الحضور بالرمز المربع",
    'academy.attendance.scanner_desc': "امسح بطاقة الطالب بالكاميرا أو قارئ الباركود لتسجيل الحضور تلقائيًا.",
    'academy.attendance.manual_code_placeholder': "أدخل أو امسح رمز الطالب (مثال: BLO-STD-...)...",
    'academy.attendance.mark_present_success': "تم تسجيل حضور {0} بنجاح!",
    'academy.attendance.already_present': "{0} مسجل حاضر بالفعل.",
    'academy.attendance.student_not_enrolled': "الطالب {0} غير مسجل في هذه الحصة.",
    'academy.attendance.mark_all_present': "تسجيل الجميع حاضر",
    'academy.attendance.mark_all_present_confirm': "هل ترغب في تسجيل جميع طلاب هذه الحصة كحاضرين؟",
    'academy.attendance.stats_present': "حاضرون",
    'academy.attendance.stats_late': "متأخرون",
    'academy.attendance.stats_absent': "غائبون",
    'academy.attendance.stats_excused': "معذورون",
    'academy.attendance.stats_rate': "نسبة الحضور",
    'academy.attendance.search_student': "البحث عن طالب بالاسم، الرمز...",
    'academy.attendance.select_session_hint': "يرجى اختيار حصة لإدارة الحضور.",
    'academy.attendance.camera_start_error': "تعذر الوصول إلى الكاميرا. يرجى التحقق من أذونات المتصفح.",
    'academy.attendance.camera_switch': "تبديل الكاميرا",

    // CRM
    'crm.menu.dashboard': 'لوحة تحكم إدارة العملاء (CRM)',
    'crm.menu.prescription': 'المواصفات الفنية',
    'crm.menu.visits': 'الزيارات الميدانية',
    'crm.menu.cdc': 'دفاتر الشروط الفنية',
    'crm.menu.pipeline': 'المشاريع والفرص',
    'crm.menu.opportunities': 'المشاريع والفرص',
    'crm.menu.leads': 'العملاء المحتملون (Leads)',
    'crm.menu.chantiers_sav': 'الأوراش وخدمة ما بعد البيع',
    'crm.menu.chantiers': 'الأوراش النشطة (KAM)',
    'crm.menu.reclamations': 'الشكاوى وخدمة ما بعد البيع',
    'crm.menu.ecosystem': 'منظومة المواصفات',
    'crm.menu.organisations': 'المنظمات ومكاتب الدراسات',
    'crm.menu.contacts': 'جهات الاتصال وصناع القرار',
    'crm.menu.activities': 'الأنشطة والمواعيد',
    'crm.cdc.deleteSuccess': 'تم حذف دفتر الشروط بنجاح.',
    'crm.cdc.secIdent': 'بيانات تعريف الوثيقة',
    'crm.cdc.titleField': 'عنوان / موضوع دفتر الشروط',
    'crm.cdc.unitsCount': 'عدد الوحدات / الفتحات',
    'crm.cdc.estimatedBudget': 'الميزانية التقديرية (دج)',
    'crm.cdc.technicalConstraints': 'المتطلبات التقنية (العزل الهوائي والمائي، الصوتي، الحراري، الزجاج)',
    'crm.chantiers.deleteSuccess': 'تم حذف الورشة بنجاح.',
    'crm.chantiers.clientRequired': 'اسم العميل / صاحب المشروع مطلوب.',
    'crm.leads.deleteError': 'حدث خطأ أثناء الحذف.',
    'crm.leads.status.new': 'جديد',
    'crm.leads.status.contacted': 'تم الاتصال',
    'crm.leads.status.qualified': 'مؤهل',
    'crm.leads.status.proposal': 'عرض تجاري',
    'crm.leads.status.won': 'محسوم لصالحنا',
    'crm.leads.status.lost': 'خسارة',
    'crm.opportunities.stageOnHold': 'متوقف مؤقتاً',
    'crm.opportunities.transferBtn': 'تحويل إلى مدير الحسابات الرئيسية (KAM)',
    'crm.opportunities.refInterne': 'المرجع الداخلي للملف',
    'crm.opportunities.organisation': 'المنظمة (مكتب دراسات / مطور عقاري)',
    'crm.opportunities.wilaya': 'الولاية / المنطقة',
    'crm.opportunities.chantierAddress': 'العنوان الدقيق للورشة',
    'crm.opportunities.secSpecs': 'التفاصيل التقنية وتقدير المقايسة',
    'crm.opportunities.gamme': 'تشكيلة المنتجات',
    'crm.opportunities.units': 'وحدات النجارة / النوافذ',
    'crm.opportunities.unitPrice': 'السعر الفردي بدون رسوم (دج)',
    'crm.opportunities.discount': 'الخصم الممنوح (%)',
    'crm.opportunities.extraDiscount': 'خصم إضافي (%)',
    'crm.opportunities.secTransfer': 'المتابعة التجارية والتسليم لمدير الحسابات',
    'crm.opportunities.actionRequired': 'الإجراء التالي المطلوب',
    'crm.opportunities.progress': 'نسبة التقدم',
    'crm.opportunities.search': 'البحث عن مشروع، تشكيلة، موصف...',
    'crm.organisations.deleteSuccess': 'تم حذف المنظمة بنجاح.',
    'crm.organisations.website': 'الموقع الإلكتروني / معرض الأعمال',
    'crm.organisations.search': 'البحث عن منظمة (مكتب دراسات، مهندس معماري، مطور)...',
    'crm.reclamations.deleteSuccess': 'تم حذف الشكوى بنجاح.',
    'crm.visits.deleteSuccess': 'تم حذف الزيارة بنجاح.',
    'crm.visits.objectiveRequired': 'الهدف من الزيارة إلزامي.',
    'crm.visits.secPlanning': 'تأطير وتخطيط الزيارة',
    'crm.visits.datetime': 'التاريخ والوقت المخطط',
    'crm.visits.contact': 'جهة الاتصال الرئيسية',
    'crm.visits.secTerrain': 'التنفيذ الميداني والتسجيل الجغرافي',
    'crm.visits.checkinBtn': 'تسجيل الحضور في الموقع (GPS)',
    'crm.visits.checkoutBtn': 'إنهاء الزيارة وحساب المدة',
    'crm.visits.checkinTime': 'وقت الوصول (Check-in)',
    'crm.visits.checkoutTime': 'وقت المغادرة (Check-out)',
    'crm.visits.duration': 'مدة المقابلة (بالدقائق)',
    'crm.visits.checkinLat': 'خط العرض (GPS Latitude)',
    'crm.visits.checkinLon': 'خط الطول (GPS Longitude)',
    'crm.visits.topics': 'المواضيع المطروحة',
    'crm.visits.commitments': 'الالتزامات المتفق عليها (من الطرفين)',
    'crm.visits.nextSteps': 'الخطوات التالية / المتابعة',
    'crm.dashboard.prescripteurSubtitle': 'إدارة التوصيف الهندسي القبلي، رصد مكاتب الدراسات ومتابعة دفاتر الشروط',
    'crm.dashboard.kamSubtitle': 'الإدارة التشغيلية لورشات البناء، الطلبيات وخدمة ما بعد البيع',
    'crm.dashboard.globalSubtitle': 'نظرة شاملة وموحدة على الأداء التجاري',
    'crm.dashboard.viewPrescripteur': 'عرض الموصف (Prescripteur)',
    'crm.dashboard.viewKam': 'عرض مدير الحسابات الرئيسية (KAM)',
    'crm.dashboard.viewGlobal': 'عرض شامل',
    'crm.dashboard.visitesTerrain': 'الزيارات الميدانية',
    'crm.dashboard.visitesSub': 'المنجزة مقابل المخططة هذا الشهر',
    'crm.dashboard.tauxVisiteCdc': 'معدل التحويل: زيارة ← دفتر الشروط',
    'crm.dashboard.cahiersCharges': 'دفاتر الشروط (CDC)',
    'crm.dashboard.cdcSub': 'دفاتر الشروط الموقعة / المعتمدة',
    'crm.dashboard.enCours': 'قيد الإنجاز',
    'crm.dashboard.tauxPreco': 'نسبة إدراج التوصيات الفنية',
    'crm.dashboard.projetsGagnes': 'المشاريع المحسومة',
    'crm.dashboard.cumulAnnee': 'إجمالي السنة',
    'crm.dashboard.ceMois': 'هذا الشهر',
    'crm.dashboard.tauxCdcGagne': 'نسبة نجاح دفتر الشروط ← مشروع محسوم',
    'crm.dashboard.ecosystemePrescription': 'منظومة التوصيف الهندسي',
    'crm.dashboard.ecosystemeSub': 'المنظمات النشطة (مكاتب دراسات، مهندسون معماريون، مرقون)',
    'crm.dashboard.satisfactionPartenaires': 'رضا الشركاء',
    'crm.dashboard.alertesPrioritaires': 'تنبيهات وإجراءات الموصف ذات الأولوية',
    'crm.dashboard.visitesRetard': 'زيارات ميدانية متأخرة',
    'crm.dashboard.enRetard': 'متأخر',
    'crm.dashboard.contactsRelancer': 'جهات اتصال رئيسية في مكاتب الدراسات بحاجة للمتابعة',
    'crm.dashboard.aRelancer': 'للمتابعة',
    'crm.dashboard.transfertsAttente': 'مشاريع محسومة جاهزة للتحويل لمدير الحسابات (KAM)',
    'crm.dashboard.enAttente': 'قيد الانتظار',
    'crm.dashboard.raccourcis': 'اختصارات وإجراءات سريعة',
    'crm.dashboard.planifierVisite': 'جدولة زيارة',
    'crm.dashboard.nouveauCdc': 'دفتر شروط جديد',
    'crm.dashboard.projetsPipeline': 'مشاريع في قمع المبيعات',
    'crm.dashboard.chantiersActifs': 'ورشات البناء النشطة',
    'crm.dashboard.enDemarrage': 'قيد الانطلاق',
    'crm.dashboard.termines': 'مكتملة',
    'crm.dashboard.projetsTransferes': 'مشروع/مشاريع محولة من قسم التوصيف',
    'crm.dashboard.commandesTraitees': 'الطلبيات المعالجة',
    'crm.dashboard.commandesRetardCount': 'طلبية/طلبيات متأخرة',
    'crm.dashboard.livraisonsConformes': 'تسليمات مطابقة للجدول',
    'crm.dashboard.chiffreAffaires': 'رقم الأعمال',
    'crm.dashboard.margeMoy': 'متوسط الهامش',
    'crm.dashboard.encaisse': 'المحصل',
    'crm.dashboard.satisfactionClient': 'رضا العملاء',
    'crm.dashboard.satisfactionChantiersSub': 'الرضا الإجمالي عن ورشات البناء المسلمة',
    'crm.dashboard.resolues': 'محلولة',
    'crm.dashboard.activiteTerrain': 'النشاط الميداني التشغيلي',
    'crm.dashboard.visitesChantier': 'زيارات الورشة',
    'crm.dashboard.heuresTerrain': 'ساعات الميدان',
    'crm.dashboard.problemesResolus': 'مشاكل محلولة',
    'crm.dashboard.actionsKam': 'إجراءات سريعة لمدير الحسابات',
    'crm.dashboard.gererChantiers': 'إدارة ورشات البناء',
    'crm.dashboard.reclamationsSav': 'الشكاوى وخدمة ما بعد البيع',
    'crm.dashboard.totalProspects': 'إجمالي العملاء المحتملين',
    'crm.dashboard.contactsClients': 'جهات اتصال العملاء',
    'crm.dashboard.affairesGagnees': 'الصفقات المحسومة',
    'crm.dashboard.projetsRecents': 'أحدث المشاريع والفرص',
    'crm.dashboard.pistesRecentes': 'أحدث العملاء المحتملين والاكتشافات',
    'crm.organisations.title': 'منظومة التوصيف والمنظمات',
    'crm.organisations.newTitle': 'منظمة جديدة',
    'crm.organisations.editTitle': 'تعديل المنظمة',
    'crm.organisations.secIdent': 'التعريف والتصنيف',
    'crm.organisations.secCoords': 'معلومات الاتصال والمقر',
    'crm.organisations.secHistory': 'السجل والشراكة',
    'crm.organisations.nameRequired': 'الاسم التجاري للمنظمة مطلوب.',
    'crm.organisations.confirmDelete': 'هل أنت متأكد من حذف هذه المنظمة؟',
    'crm.organisations.projects': 'مشروع/مشاريع',
    'crm.organisations.contacts': 'جهة/جهات اتصال',
    'crm.organisations.searchPlaceholder': 'بحث عن منظمة (مكتب دراسات، معماري، مرقي عقاري...)...',
    'crm.organisations.historyNotes': 'ملاحظات الشراكة، المشاريع الموصوفة سابقاً، والتوصيات الفنية',
    'crm.organisations.historyPlaceholder': 'فصّل هنا السجل العلائقي، مستوى الولاء، التفضيلات المعمارية أو مجموعات المنتجات المفضلة...',
    'crm.organisations.zoneGeoPlaceholder': 'مثال: الجزائر الوسطى، وهران، قسنطينة، وطني',
    'crm.organisations.typeBet': 'مكتب دراسات تقنية (BET)',
    'crm.organisations.typeArchitecte': 'مكتب هندسة معمارية',
    'crm.organisations.typePromoteur': 'مرقي عقاري',
    'crm.organisations.typeMo': 'صاحب العمل / الجهة الآمرة',
    'crm.organisations.typeEnt': 'شركة مقاولات وبناء عامة',
    'crm.organisations.typeInstallateur': 'مركّب / نجار شريك',
    'crm.organisations.typeAutre': 'شريك آخر',
    'crm.organisations.specResidentiel': 'سكني (سكنات، أبراج)',
    'crm.organisations.specTertiaire': 'خدمي وإداري',
    'crm.organisations.specIndustriel': 'صناعي ولوجستي',
    'crm.organisations.specMixte': 'مختلط / متعدد الاستخدامات',
    'crm.organisations.sizePetit': 'هيكل صغير (< 10 موظفين)',
    'crm.organisations.sizeMoyen': 'هيكل متوسط (10 - 50 موظفاً)',
    'crm.organisations.sizeGrand': 'هيكل كبير (> 50 موظفاً)',
    'crm.organisations.name': 'الاسم التجاري / الشركة',
    'crm.organisations.type': 'نوع المنظمة',
    'crm.organisations.specialty': 'التخصص الرئيسي',
    'crm.organisations.size': 'الحجم / السعة',
    'crm.organisations.zoneGeo': 'النطاق الجغرافي للنشاط',
    'crm.visits.title': 'الزيارات الميدانية والتوصيف',
    'crm.visits.newTitle': 'جدولة زيارة',
    'crm.visits.editTitle': 'تعديل الزيارة',
    'crm.visits.secIdent': 'تأطير الزيارة',
    'crm.visits.secReport': 'التقرير ومتابعة الإجراءات',
    'crm.visits.subjectRequired': 'موضوع الزيارة مطلوب.',
    'crm.visits.confirmDelete': 'هل أنت متأكد من حذف هذه الزيارة؟',
    'crm.visits.search': 'بحث عن زيارة، مكتب دراسات، مشروع، محاور...',
    'crm.visits.rolePrescripteur': 'موصف (قبلي / مكتب دراسات / معماري)',
    'crm.visits.roleKam': 'مدير حسابات (ورشة / مرقي / استلام)',
    'crm.visits.typeProspection': 'استكشاف ورصد أولي',
    'crm.visits.typePreconisation': 'عرض مجموعات المنتجات والتوصيف التقني',
    'crm.visits.typePrescription': 'إعداد / اعتماد دفتر شروط التوصيف',
    'crm.visits.typeSuiviChantier': 'متابعة ورشة البناء وبدء التركيب',
    'crm.visits.typeReunionTechnique': 'اجتماع تقني لمعالجة المشاكل',
    'crm.visits.typeReception': 'زيارة نهاية التركيب / الاستلام',
    'crm.visits.statusPlanned': 'مجدولة',
    'crm.visits.statusInProgress': 'جارية (عين المكان)',
    'crm.visits.statusCompleted': 'منجزة مع التقرير',
    'crm.visits.statusRescheduled': 'مؤجلة',
    'crm.visits.statusCancelled': 'ملغاة',
    'crm.visits.noOrg': 'زيارة حرة (بدون منظمة مرتبطة)',
    'crm.visits.noProject': 'استكشاف قبلي (بدون مشروع محدد)',
    'crm.visits.objectivePlaceholder': 'حدد الهدف الملموس (مثال: تقديم تشكيلة Futura، فحص المخططات...)',
    'crm.visits.checklistPlaceholder': 'العناصر المفحوصة (تسليم المخططات، عرض العينات، تسليم البطاقات التقنية...)',
    'crm.visits.personsMetPlaceholder': 'مثال: السيد بن علي (مدير المشروع)، السيدة منصوري (مهندسة داخلية)...',
    'crm.visits.evalScore': 'تقييم الاهتمام والتجاوب:',
    'crm.visits.noGps': 'غير محدد جغرافياً',
    'crm.visits.role': 'صفة / ملف الزائر',
    'crm.visits.type': 'نوع الزيارة',
    'crm.visits.status': 'حالة الزيارة',
    'crm.visits.subject': 'موضوع الزيارة',
    'crm.visits.organisation': 'المنظمة / مكتب الدراسات / المرقي المزار',
    'crm.visits.project': 'المشروع المرتبط',
    'crm.visits.interlocuteur': 'المحاور الذي تمت مقابلته',
    'crm.visits.date': 'تاريخ وتوقيت الزيارة',
    'crm.visits.objective': 'الهدف من اللقاء',
    'crm.visits.gps': 'إحداثيات GPS / التسجيل الميداني',
    'crm.visits.score': 'درجة التجاوب (1-5 ⭐)',
    'crm.visits.personsMet': 'الأشخاص الحاضرون ووظائفهم',
    'crm.visits.checklist': 'قائمة التحقق الميدانية / التوصيات المعتمدة',
    'crm.visits.report': 'التقرير المفصل للزيارة',
    'crm.visits.nextAction': 'الإجراء القادم / المتابعة المقررة',
    'crm.cdc.title': 'دفاتر الشروط (CDC) للتوصيف',
    'crm.cdc.newTitle': 'دفتر شروط جديد',
    'crm.cdc.editTitle': 'تعديل دفتر الشروط',
    'crm.cdc.secProject': 'الربط بالمشروع والفاعلين',
    'crm.cdc.secSpecs': 'المواصفات التقنية ومجموعات المنتجات الموصى بها',
    'crm.cdc.titleRequired': 'عنوان دفتر الشروط مطلوب.',
    'crm.cdc.confirmDelete': 'هل أنت متأكد من حذف دفتر الشروط هذا؟',
    'crm.cdc.search': 'بحث عن دفتر شروط، مشروع، مكتب دراسات، تشكيلة...',
    'crm.cdc.selectProject': 'اختر مشروعاً',
    'crm.cdc.selectOrg': 'اختر منظمة',
    'crm.cdc.selectContact': 'اختر جهة اتصال',
    'crm.cdc.statusDraft': 'مسودة (قيد الصياغة)',
    'crm.cdc.statusSubmitted': 'مقدم لمكتب الدراسات / المعماري',
    'crm.cdc.statusValidated': 'معتمد من إدارة المشروع',
    'crm.cdc.statusSigned': 'موقع / مدمج في العقد',
    'crm.cdc.statusRejected': 'مرفوض / بديل مستبعد',
    'crm.cdc.constraintsPlaceholder': 'مثال: عزل صوتي مقوى 38 ديسيبل، زجاج عاكس للطاقة الشمسية 4S، دهانات ذات ملمس خاص، ارتفاع السقف...',
    'crm.cdc.prescripteur': 'الموصف',
    'crm.cdc.signedOn': 'تم التوقيع بتاريخ',
    'crm.cdc.notSigned': 'غير موقع',
    'crm.cdc.units': 'وحدات',
    'crm.cdc.cdcTitle': 'عنوان دفتر الشروط',
    'crm.cdc.project': 'المشروع المرتبط',
    'crm.cdc.organisation': 'الموصف / مكتب الدراسات الصائغ',
    'crm.cdc.contact': 'محاور مكتب الدراسات',
    'crm.cdc.gamme': 'مجموعة المنتجات الموصى بها',
    'crm.cdc.volume': 'الحجم التقديري (وحدات النوافذ والأبواب / المساحات)',
    'crm.cdc.status': 'حالة دفتر الشروط',
    'crm.cdc.constraints': 'القيود التقنية والمواصفات المطلوبة',
    'crm.cdc.dateCreation': 'تاريخ الإنشاء',
    'crm.cdc.dateSignature': 'تاريخ الاعتماد / التوقيع',
    'crm.gamme.productGamme': 'مجموعة المنتجات',
    'crm.gamme.urbania': 'URBANIA (أداء سكني عالي)',
    'crm.gamme.futura': 'FUTURA (خدمي وواجهات سحب)',
    'crm.gamme.dooria': 'DOORIA (أبواب ومداخل فاخرة)',
    'crm.gamme.mixte': 'MIXTE (حلول هجينة مخصصة)',
    'crm.gamme.urbaniaShort': 'URBANIA',
    'crm.gamme.futuraShort': 'FUTURA',
    'crm.gamme.dooriaShort': 'DOORIA',
    'crm.gamme.mixteShort': 'MIXTE',
    'crm.opportunities.secIdent': 'تعريف الصفقة',
    'crm.opportunities.secGamme': 'المنتجات، المجموعات والتقدير المالي',
    'crm.opportunities.secFollowup': 'الفاعلون، التسليم والمتابعة التجارية',
    'crm.opportunities.titleField': 'عنوان الصفقة / المشروع',
    'crm.opportunities.typeProjet': 'نوع المشروع',
    'crm.opportunities.segment': 'قطاع السوق',
    'crm.opportunities.priority': 'الأولوية التجارية',
    'crm.opportunities.risk': 'مستوى المخاطرة',
    'crm.opportunities.transferStatus': 'حالة التسليم (التوصيف ← مدير الحسابات)',
    'crm.opportunities.commercialProgress': 'التقدم التجاري:',
    'crm.opportunities.actionPlaceholder': 'مثال: إرسال العرض المالي، المواءمة التقنية للنوافذ مع مكتب الدراسات...',
    'crm.opportunities.notesPlaceholder': 'سجل التبادلات، متطلبات المرقي المحددة، جدول التوريد...',
    'crm.opportunities.typePromotion': 'ترقية عقارية خاصة',
    'crm.opportunities.typeAadl': 'سكنات عدل (AADL)',
    'crm.opportunities.typeLpp': 'سكنات ترقوية عمومية (LPP)',
    'crm.opportunities.typePublic': 'تجهيزات عمومية / تعليم / صحة',
    'crm.opportunities.typePrive': 'خدمي / مكاتب / خاص',
    'crm.opportunities.typeIndustriel': 'صناعي ومستودعات',
    'crm.opportunities.segmentPrive': 'القطاع الخاص',
    'crm.opportunities.segmentPublic': 'القطاع العام / صفقات الدولة',
    'crm.opportunities.transferNone': 'قيد التوصيف (غير محول)',
    'crm.opportunities.transferPending': 'جاهز للتحويل لمدير الحسابات (KAM)',
    'crm.opportunities.transferTransferred': 'محول ومستلم من قبل مدير الحسابات',
    'crm.opportunities.transferDirectKam': 'استكشاف مباشر من مدير الحسابات (بدون توصيف)',
    'crm.opportunities.volumeEstime': 'الحجم التقديري (وحدات النوافذ)',
    'crm.opportunities.valeurEstimee': 'القيمة التقديرية للصفقة (د.ج)',
    'crm.opportunities.prescripteur': 'الموصف المسؤول',
    'crm.opportunities.kamAssigne': 'مدير الحسابات المعين (متابعة الورشة)',
    'crm.opportunities.dateCloture': 'التاريخ المتوقع للحسم',
    'crm.opportunities.prochaineAction': 'الإجراء التجاري التالي',
    'crm.stage.prospecting': 'استكشاف / رصد',
    'crm.stage.qualification': 'تأهيل تقني من مكتب الدراسات',
    'crm.stage.proposal': 'عرض مالي / صياغة دفتر الشروط',
    'crm.stage.negotiation': 'تفاوض ومواءمة',
    'crm.stage.won': 'صفقة محسومة (موقعة)',
    'crm.stage.lost': 'خسارة / منافسة',
    'crm.stage.onHold': 'معلقة / مشروع مجمد',
    'crm.priority.low': 'منخفضة',
    'crm.priority.medium': 'متوسطة',
    'crm.priority.high': 'عالية / استراتيجية',
    'crm.risk.low': 'مخاطرة منخفضة',
    'crm.risk.medium': 'مخاطرة معتدلة',
    'crm.risk.high': 'مخاطرة عالية',
    'crm.chantiers.title': 'المتابعة التشغيلية لورشات البناء (KAM)',
    'crm.chantiers.newTitle': 'ورشة بناء جديدة (KAM)',
    'crm.chantiers.editTitle': 'متابعة ورشة البناء النشطة',
    'crm.chantiers.secIdent': 'تعريف ورشة البناء والصفقة',
    'crm.chantiers.secPlanning': 'الجدول الزمني والتقدم',
    'crm.chantiers.secOrders': 'الطلبيات والتسليمات',
    'crm.chantiers.secFinancial': 'المؤشرات المالية والتحصيلات',
    'crm.chantiers.titleRequired': 'اسم ورشة البناء مطلوب.',
    'crm.chantiers.confirmDelete': 'هل أنت متأكد من حذف ورشة البناء هذه؟',
    'crm.chantiers.search': 'بحث عن ورشة نشطة، عميل، مدير حسابات...',
    'crm.chantiers.titleField': 'اسم ورشة البناء',
    'crm.chantiers.clientName': 'العميل / المرقي',
    'crm.chantiers.project': 'المشروع الأولي (التسليم)',
    'crm.chantiers.directKamProject': 'استكشاف مباشر من مدير الحسابات (بدون توصيف)',
    'crm.chantiers.kamName': 'مدير الحسابات المسؤول',
    'crm.chantiers.wilaya': 'الولاية',
    'crm.chantiers.startDate': 'تاريخ الانطلاق',
    'crm.chantiers.estimatedEndDate': 'الأجل المتوقع',
    'crm.chantiers.operationalProgress': 'التقدم التشغيلي',
    'crm.chantiers.progressTitle': 'تقدم ورشة البناء',
    'crm.chantiers.satisfaction': 'درجة رضا العميل (0-100)',
    'crm.chantiers.satisfactionLabel': 'مستوى الرضا',
    'crm.chantiers.ordersCount': 'الطلبيات المعالجة',
    'crm.chantiers.ordersPending': 'الطلبيات قيد التنفيذ',
    'crm.chantiers.ordersLate': 'الطلبيات المتأخرة',
    'crm.chantiers.deliveryStatus': 'حالة التسليمات',
    'crm.chantiers.totalCa': 'إجمالي رقم الأعمال (د.ج)',
    'crm.chantiers.marginPct': 'نسبة الهامش المحقق (%)',
    'crm.chantiers.collectedPct': 'نسبة التحصيل / المدفوعات (%)',
    'crm.chantiers.client': 'العميل',
    'crm.chantiers.assigned': 'مخصص',
    'crm.chantiers.cmdCount': 'طلبية/طلبيات',
    'crm.chantiers.enRetard': 'متأخرة',
    'crm.chantiers.margin': 'الهامش',
    'crm.chantiers.collected': 'المحصل',
    'crm.chantiers.reclamationCount': 'شكوى/شكاوى',
    'crm.chantiers.zeroReclamation': '0 شكاوى',
    'crm.chantiers.progress': 'التقدم',
    'crm.chantiers.statusDemarrage': 'قيد الانطلاق (التحضير)',
    'crm.chantiers.statusEnCours': 'قيد التنفيذ',
    'crm.chantiers.statusPose': 'قيد تركيب النوافذ والأبواب',
    'crm.chantiers.statusReception': 'مرحلة الاستلام / رفع التحفظات',
    'crm.chantiers.statusCloture': 'مختتم بنجاح',
    'crm.chantiers.statusArrete': 'متوقف',
    'crm.chantiers.delivOnSchedule': 'مطابق للجدول الزمني',
    'crm.chantiers.delivPartially': 'مسلم جزئياً',
    'crm.chantiers.delivDelivered': 'مسلم بالكامل في الورشة',
    'crm.chantiers.delivDelayed': 'تأخر التسليم من المصنع / النقل',
    'crm.reclamations.title': 'خدمة ما بعد البيع وشكاوى العملاء',
    'crm.reclamations.newTitle': 'تسجيل خلل / شكوى',
    'crm.reclamations.editTitle': 'معالجة شكوى خدمة ما بعد البيع',
    'crm.reclamations.secIdent': 'تفاصيل الخلل في الورشة',
    'crm.reclamations.secResolution': 'المعالجة والحل التقني',
    'crm.reclamations.subjectRequired': 'موضوع الشكوى مطلوب.',
    'crm.reclamations.confirmDelete': 'هل أنت متأكد من حذف هذه الشكوى؟',
    'crm.reclamations.search': 'بحث عن شكوى، ورشة...',
    'crm.reclamations.subject': 'موضوع / وصف موجز للنزاع',
    'crm.reclamations.chantier': 'ورشة البناء المعنية',
    'crm.reclamations.contact': 'الشخص المصرح',
    'crm.reclamations.type': 'طبيعة الخلل',
    'crm.reclamations.severity': 'مستوى الخطورة',
    'crm.reclamations.dateReported': 'تاريخ الإبلاغ',
    'crm.reclamations.dateResolved': 'تاريخ الحل',
    'crm.reclamations.resolutionNotes': 'ملاحظات الحل والإجراءات التصحيحية',
    'crm.reclamations.resolutionPlaceholder': 'فصّل القطع المستبدلة، تدخل فني الصيانة، رفع التحفظات...',
    'crm.reclamations.selectChantier': 'اختر ورشة بناء',
    'crm.reclamations.selectContact': 'اختر جهة اتصال',
    'crm.reclamations.typeDefautPose': 'عيب في التركيب / التثبيت / الضبط',
    'crm.reclamations.typeDefautProduit': 'عيب في المنتج / خدوش / الزجاج',
    'crm.reclamations.typeRetardLivraison': 'تأخر تسليم الورشة',
    'crm.reclamations.typeAccessoiresManquants': 'ملحقات / أقفال مفقودة',
    'crm.reclamations.typeAutre': 'شكوى أخرى',
    'crm.reclamations.sevLow': 'منخفضة (أثر طفيف)',
    'crm.reclamations.sevMedium': 'متوسطة (للمعالجة خلال 48 ساعة)',
    'crm.reclamations.sevHigh': 'عالية (تعطل الاستلام)',
    'crm.reclamations.sevCritical': 'حرجة (توقف فوري للتركيب)',
    'crm.reclamations.statusOpen': 'مفتوحة (قيد الانتظار)',
    'crm.reclamations.statusInvestigating': 'قيد التحليل من المصنع / الصيانة',
    'crm.reclamations.statusInProgress': 'تدخل تقني مجدول',
    'crm.reclamations.statusResolved': 'تم الحل والاستلام',
    'crm.reclamations.statusClosed': 'ملف مغلق',
    'crm.reclamations.client': 'العميل',
    'crm.reclamations.reportedOn': 'تاريخ الإبلاغ',
    'crm.reclamations.resolvedOn': 'تم الحل بتاريخ',
    'crm.reclamations.notResolved': 'غير محلول',
    'crm.contacts.title': 'جهات الاتصال وصناع القرار',
    'crm.contacts.detailsSection': 'المعلومات الشخصية والمهنية',
    'crm.contacts.field.firstName': 'الاسم',
    'crm.contacts.field.lastName': 'اللقب',
    'crm.contacts.field.fullName': 'الاسم الكامل',
    'crm.contacts.field.organisation': 'المنظمة التابع لها (مكتب دراسات / مرقي / صاحب عمل)',
    'crm.contacts.field.company': 'الشركة / المؤسسة',
    'crm.contacts.field.role': 'الوظيفة / المنصب',
    'crm.contacts.field.email': 'البريد الإلكتروني',
    'crm.contacts.field.phone': 'الهاتف',
    'crm.contacts.field.mobile': 'المحمول',
    'crm.contacts.field.prefComm': 'قناة الاتصال المفضلة',
    'crm.contacts.field.prefHours': 'الأوقات المفضلة',
    'crm.contacts.field.prefLang': 'اللغة المفضلة',
    'crm.contacts.field.type': 'نوع جهة الاتصال',
    'crm.contacts.field.city': 'المدينة',
    'crm.contacts.field.address': 'العنوان',
    'crm.contacts.noOrgDirect': 'لا يوجد / شركة مباشرة',
    'crm.contacts.prefEmail': 'البريد الإلكتروني',
    'crm.contacts.prefPhone': 'الهاتف',
    'crm.contacts.prefMeeting': 'لقاء حضوري',
    'crm.contacts.prefWhatsapp': 'واتساب / رسائل قصيرة',
    'crm.contacts.hoursPlaceholder': 'مثال: الصباح 9:00 - 12:00',
    'crm.contacts.influence': 'مستوى التأثير في اتخاذ القرار:',
    'crm.contacts.noRole': 'الدور غير محدد',
    'crm.contacts.dispo': 'الأوقات',
    'crm.leads.newLeadBtn': 'عميل محتمل جديد',
    'crm.leads.informationSection': 'معلومات العميل المحتمل',
    'crm.leads.field.name': 'الاسم الكامل',
    'crm.leads.field.company': 'الشركة',
    'crm.leads.field.email': 'البريد الإلكتروني',
    'crm.leads.field.phone': 'الهاتف',
    'crm.leads.field.status': 'الحالة',
    'crm.leads.field.source': 'المصدر',
    'crm.leads.field.value': 'القيمة التقديرية (د.ج)',
    'crm.leads.field.notes': 'ملاحظات',
    'crm.leads.sourceLabel': 'المصدر',
    'crm.leads.sourceDirect': 'مباشر',
    'crm.leads.noData': 'لا يوجد عملاء محتملون. انقر فوق "عميل محتمل جديد" للبدء.',
    'crm.leads.search': 'بحث عن عميل محتمل...',
    'crm.leads.col.code': 'الرمز',
    'crm.leads.col.name': 'الاسم',
    'crm.leads.col.company': 'الشركة',
    'crm.leads.col.email': 'البريد الإلكتروني',
    'crm.leads.col.phone': 'الهاتف',
    'crm.leads.col.value': 'القيمة',
    'crm.leads.col.status': 'الحالة',
    'crm.status.new': 'جديد',
    'crm.status.contacted': 'تم التواصل',
    'crm.status.qualified': 'مؤهل',
    'crm.status.proposal': 'عرض مقدم',
    'crm.status.won': 'محسوم',
    'crm.status.lost': 'خاسر',
    'crm.source.website': 'الموقع الإلكتروني',
    'crm.source.referral': 'توصية / إحالة',
    'crm.source.social': 'شبكات التواصل',
    'crm.source.event': 'حدث / معرض',
    'crm.source.other': 'آخر',
    'crm.type.client': 'عميل',
    'crm.type.prospect': 'عميل محتمل',
    'crm.type.partner': 'شريك',
    'crm.type.vendor': 'مورد',
    'crm.dashboard.title': 'لوحة التحكم التجارية وإدارة علاقات العملاء (CRM)',
    'crm.dashboard.subtitle': 'متابعة الأداء التجاري، محفظة العملاء المحتملين والفرص',
    'crm.dashboard.customizeKpi': 'تخصيص مؤشرات الأداء',
    'crm.dashboard.newLead': 'عميل محتمل جديد',
    'crm.dashboard.newOpp': 'فرصة جديدة',
    'crm.dashboard.activeOpportunities': 'صفقات قيد التفاوض',
    'crm.dashboard.noContact': 'بدون جهة اتصال',
    'crm.dashboard.totalLeads': 'العملاء المحتملون',
    'crm.dashboard.newLeads': 'جدد',
    'crm.dashboard.totalContacts': 'جهات الاتصال والزبائن',
    'crm.dashboard.totalOpportunities': 'الفرص التجارية',
    'crm.dashboard.pipelineAmount': 'قيمة مسار المبيعات',
    'crm.dashboard.wonAmount': 'المبيعات المنجزة',
    'crm.dashboard.pendingActivities': 'المواعيد والأنشطة القادمة',
    'crm.dashboard.recentLeads': 'آخر العملاء المحتملين',
    'crm.dashboard.recentOpportunities': 'أحدث الفرص',
    'crm.dashboard.noLeads': 'لا يوجد عملاء محتملون مسجلون.',
    'crm.dashboard.noOpportunities': 'لا توجد فرص تجارية مسجلة.',

    // CRM KPIs & Widgets (Parameterized)
    'crm.dashboard.kpi.total_leads': 'إجمالي العملاء المحتملين',
    'crm.dashboard.kpi.desc.total_leads': 'جميع العملاء المحتملين المسجلين',
    'crm.dashboard.kpi.new_leads': 'العملاء المحتملون الجدد',
    'crm.dashboard.kpi.desc.new_leads': 'العملاء الجدد المطلوب الاتصال بهم',
    'crm.dashboard.kpi.total_contacts': 'جهات الاتصال والزبائن',
    'crm.dashboard.kpi.desc.total_contacts': 'دليل جهات الاتصال النشطة',
    'crm.dashboard.kpi.total_opportunities': 'الفرص التجارية النشطة',
    'crm.dashboard.kpi.desc.total_opportunities': 'ملفات الصفقات المفتوحة',
    'crm.dashboard.kpi.pipeline_amount': 'قيمة مسار المبيعات',
    'crm.dashboard.kpi.desc.pipeline_amount': 'القيمة الإجمالية للفرص التجارية',
    'crm.dashboard.kpi.won_amount': 'الصفقات الرابحة',
    'crm.dashboard.kpi.desc.won_amount': 'إجمالي الإيرادات المحققة',
    'crm.dashboard.kpi.pending_activities': 'الأنشطة المعلقة',
    'crm.dashboard.kpi.desc.pending_activities': 'المهام، المتابعات والمواعيد قيد الانتظار',
    'crm.dashboard.widget.recent_leads': 'أحدث العملاء المحتملين',
    'crm.dashboard.widget.desc.recent_leads': 'قائمة ومتابعة أحدث العملاء',
    'crm.dashboard.widget.pipeline_stages': 'مراحل مسار المبيعات',
    'crm.dashboard.widget.desc.pipeline_stages': 'توزيع مبالغ الصفقات حسب المرحلة',
    'crm.dashboard.widget.recent_activities': 'الأنشطة الحديثة',
    'crm.dashboard.widget.desc.recent_activities': 'سجل الأنشطة والإجراءات القادمة',

    // CRM Stats Badges
    'crm.stats.newThisMonth': 'جدد',
    'crm.stats.toContact': 'للتواصل',
    'crm.stats.activeAccounts': 'حسابات نشطة',
    'crm.stats.activeDeals': 'صفقات جارية',
    'crm.stats.weightedPipeline': 'المحفظة النشطة',
    'crm.stats.closedWon': 'إيرادات محققة',
    'crm.stats.actionRequired': 'متابعات مطلوبة',
    'crm.leads.pageTitle': 'إدارة العملاء المحتملين',
    'crm.leads.new': 'عميل محتمل جديد',
    'crm.leads.editTitle': 'تعديل العميل المحتمل',
    'crm.leads.newTitle': 'عميل محتمل جديد',
    'crm.leads.details': 'بيانات العميل المحتمل',
    'crm.leads.name': 'الاسم الكامل',
    'crm.leads.company': 'الشركة / المؤسسة',
    'crm.leads.email': 'البريد الإلكتروني',
    'crm.leads.phone': 'رقم الهاتف',
    'crm.leads.status': 'الحالة',
    'crm.leads.source': 'مصدر الاتصال',
    'crm.leads.value': 'القيمة التقديرية',
    'crm.leads.notes': 'ملاحظات ومتطلبات',
    'crm.leads.confirmDelete': 'هل أنت متأكد من حذف هذا العميل المحتمل؟',
    'crm.leads.deleteSuccess': 'تم حذف العميل المحتمل بنجاح.',
    'crm.leads.updateSuccess': 'تم تحديث العميل المحتمل بنجاح.',
    'crm.leads.createSuccess': 'تم إنشاء العميل المحتمل بنجاح.',
    'crm.leads.nameRequired': 'اسم العميل المحتمل مطلوب.',
    'crm.leads.searchPlaceholder': 'بحث عن عميل محتمل بالاسم، الشركة، البريد...',
    'crm.leads.empty': 'لم يتم العثور على أي عملاء محتملين. انقر فوق "عميل محتمل جديد" للبدء.',
    'crm.contacts.pageTitle': 'دليل جهات الاتصال والزبائن',
    'crm.contacts.new': 'جهة اتصال جديدة',
    'crm.contacts.editTitle': 'تعديل جهة الاتصال / صانع القرار',
    'crm.contacts.newTitle': 'جهة اتصال وصانع قرار جديد',
    'crm.contacts.details': 'بيانات جهة الاتصال',
    'crm.contacts.firstName': 'الاسم الشخصي',
    'crm.contacts.lastName': 'اللقب',
    'crm.contacts.company': 'الشركة',
    'crm.contacts.role': 'الوظيفة / المنصب',
    'crm.contacts.email': 'البريد الإلكتروني',
    'crm.contacts.phone': 'الهاتف الثابت',
    'crm.contacts.mobile': 'الهاتف المحمول',
    'crm.contacts.address': 'العنوان',
    'crm.contacts.city': 'المدينة',
    'crm.contacts.type': 'نوع العلاقة',
    'crm.contacts.confirmDelete': 'هل أنت متأكد من حذف جهة الاتصال هذه؟',
    'crm.contacts.deleteSuccess': 'تم حذف جهة الاتصال بنجاح.',
    'crm.contacts.updateSuccess': 'تم تحديث جهة الاتصال بنجاح.',
    'crm.contacts.createSuccess': 'تم إنشاء جهة الاتصال بنجاح.',
    'crm.contacts.lastNameRequired': 'اللقب مطلوب.',
    'crm.contacts.searchPlaceholder': 'بحث عن جهة اتصال، منظمة، بريد إلكتروني...',
    'crm.contacts.empty': 'لم يتم العثور على جهات اتصال. انقر فوق "جهة اتصال جديدة" للبدء.',
    'crm.opportunities.pageTitle': 'الفرص التجارية والمبيعات',
    'crm.opportunities.new': 'فرصة تجارية جديدة',
    'crm.opportunities.editTitle': 'تعديل الفرصة',
    'crm.opportunities.newTitle': 'فرصة جديدة',
    'crm.opportunities.details': 'تفاصيل الفرصة',
    'crm.opportunities.title': 'فرص الأعمال وقمع المبيعات',
    'crm.opportunities.contact': 'جهة الاتصال المرتبطة',
    'crm.opportunities.stage': 'مرحلة قمع المبيعات',
    'crm.opportunities.amount': 'المبلغ (د.ج)',
    'crm.opportunities.probability': 'الاحتمالية (%)',
    'crm.opportunities.closeDate': 'تاريخ الإغلاق المتوقع',
    'crm.opportunities.closeTarget': 'الموعد',
    'crm.opportunities.notes': 'ملاحظات، سجل ومتطلبات خاصة',
    'crm.opportunities.stageProspecting': 'استكشاف',
    'crm.opportunities.stageQualification': 'تأهيل',
    'crm.opportunities.stageProposal': 'تقديم عرض',
    'crm.opportunities.stageNegotiation': 'تفاوض',
    'crm.opportunities.stageWon': 'تم الفوز بها',
    'crm.opportunities.stageLost': 'خسارة',
    'crm.opportunities.confirmDelete': 'هل أنت متأكد من حذف هذه الفرصة؟',
    'crm.opportunities.deleteSuccess': 'تم حذف الفرصة بنجاح.',
    'crm.opportunities.updateSuccess': 'تم تحديث الفرصة بنجاح.',
    'crm.opportunities.createSuccess': 'تم إنشاء الفرصة بنجاح.',
    'crm.opportunities.titleRequired': 'عنوان الفرصة مطلوب.',
    'crm.opportunities.searchPlaceholder': 'بحث عن صفقة، مرقي عقاري، مشروع، موصف...',
    'crm.opportunities.empty': 'لم يتم العثور على أي فرص تجارية. انقر فوق "فرصة تجارية جديدة" للبدء.',
    'crm.activities.pageTitle': 'الأنشطة والمتابعات',
    'crm.activities.new': 'نشاط جديد',
    'crm.activities.editTitle': 'تعديل النشاط',
    'crm.activities.newTitle': 'نشاط جديد',
    'crm.activities.details': 'تفاصيل النشاط',
    'crm.activities.type': 'نوع النشاط',
    'crm.activities.subject': 'الموضوع / العنوان',
    'crm.activities.date': 'التاريخ والتوقيت',
    'crm.activities.status': 'الحالة',
    'crm.activities.priority': 'الأولوية',
    'crm.activities.contact': 'جهة الاتصال المرتبطة',
    'crm.activities.opportunity': 'الفرصة المرتبطة',
    'crm.activities.notes': 'ملاحظات / ملخص اللقاء',
    'crm.activities.relatedTo': 'مرتبط بـ',
    'crm.activities.typeCall': 'مكالمة هاتفية',
    'crm.activities.typeMeeting': 'اجتماع / موعد',
    'crm.activities.typeEmail': 'بريد إلكتروني',
    'crm.activities.typeTask': 'مهمة',
    'crm.activities.typeNote': 'ملاحظة',
    'crm.activities.statusPending': 'قيد الانتظار',
    'crm.activities.statusCompleted': 'مكتمل',
    'crm.activities.statusCancelled': 'ملغى',
    'crm.activities.priorityLow': 'منخفضة',
    'crm.activities.priorityMedium': 'متوسطة',
    'crm.activities.priorityHigh': 'عالية',
    'crm.activities.markPending': 'تعيين كقيد الانتظار',
    'crm.activities.markCompleted': 'تعيين كمكتمل',
    'crm.activities.confirmDelete': 'هل أنت متأكد من حذف هذا النشاط؟',
    'crm.activities.deleteSuccess': 'تم حذف النشاط بنجاح.',
    'crm.activities.updateSuccess': 'تم تحديث النشاط بنجاح.',
    'crm.activities.createSuccess': 'تم إنشاء النشاط بنجاح.',
    'crm.activities.subjectRequired': 'موضوع النشاط مطلوب.',
    'crm.activities.searchPlaceholder': 'بحث عن نشاط بالموضوع، جهة الاتصال، الفرصة...',
    'crm.activities.empty': 'لم يتم العثور على أنشطة. انقر فوق "نشاط جديد" لجدولة مكالمة، اجتماع أو مهمة.',

    // ProjectManagement
    'project.menu.dashboard': 'لوحة تحكم المشاريع',
    'project.menu.projects': 'محفظة المشاريع',
    'project.menu.tasks': 'المهام والأنشطة',
    'project.menu.milestones': 'المراحل والإنجازات',
    'project.menu.team': 'الفريق والموارد',
    'project.dashboard.title': 'لوحة تحكم إدارة المشاريع',
    'project.dashboard.customizeKpi': 'تخصيص مؤشرات الأداء',
    'project.dashboard.totalProjects': 'المشاريع',
    'project.dashboard.active': 'نشطة',
    'project.dashboard.inProgress': 'قيد التنفيذ',
    'project.dashboard.completedRate': 'مكتملة',
    'project.dashboard.actionNeeded': 'يتطلب تدخلاً',
    'project.dashboard.onTrack': 'على المسار',
    'project.dashboard.budgetAllocated': 'المبلغ المخصص',
    'project.dashboard.consumed': 'مستهلك',
    'project.dashboard.upcomingMilestones': 'للاعتماد',
    'project.dashboard.recentProjects': 'المشاريع الجارية',
    'project.dashboard.recentTasks': 'المهام الحديثة',
    'project.dashboard.overBudget': 'تجاوز الميزانية',
    'project.dashboard.tasksCompleted': 'المهام المكتملة',
    'project.dashboard.budgetSpent': 'الميزانية المستهلكة',
    'project.dashboard.milestones': 'المراحل القادمة',
    'project.dashboard.overdueTasks': 'مهام متأخرة',
    'project.dashboard.activeProjectsTitle': 'المشاريع الجارية',
    'project.dashboard.recentTasksTitle': 'المهام ذات الأولوية',
    'project.dashboard.noProjects': 'لا توجد مشاريع مسجلة.',
    'project.dashboard.noTasks': 'لا توجد مهام مسجلة.',

    // ProjectManagement KPIs & Widgets (Parameterized)
    'project.dashboard.kpi.total_projects': 'إجمالي المشاريع',
    'project.dashboard.kpi.desc.total_projects': 'محفظة المشاريع الكاملة',
    'project.dashboard.kpi.active_projects': 'المشاريع الجارية',
    'project.dashboard.kpi.desc.active_projects': 'المشاريع النشطة حالياً',
    'project.dashboard.kpi.total_tasks': 'إجمالي المهام',
    'project.dashboard.kpi.desc.total_tasks': 'حجم العمل الإجمالي المخطط له',
    'project.dashboard.kpi.done_tasks': 'المهام المكتملة',
    'project.dashboard.kpi.desc.done_tasks': 'المهام المنجزة بنجاح',
    'project.dashboard.kpi.overdue_tasks': 'المهام المتأخرة',
    'project.dashboard.kpi.desc.overdue_tasks': 'المهام غير المنجزة بعد تاريخ الاستحقاق',
    'project.dashboard.kpi.total_budget': 'الميزانية الإجمالية',
    'project.dashboard.kpi.desc.total_budget': 'الميزانية المالية المخصصة',
    'project.dashboard.kpi.total_spent': 'الميزانية المستهلكة',
    'project.dashboard.kpi.desc.total_spent': 'إجمالي المصروفات المنفقة',
    'project.dashboard.kpi.pending_milestones': 'المراحل الرئيسية',
    'project.dashboard.kpi.desc.pending_milestones': 'المراحل الرئيسية قيد الاعتماد',
    'project.dashboard.widget.project_progress': 'تقدم المشاريع',
    'project.dashboard.widget.desc.project_progress': 'متابعة مرئية لنسب الإنجاز',
    'project.dashboard.widget.urgent_tasks': 'المهام ذات الأولوية',
    'project.dashboard.widget.desc.urgent_tasks': 'المهام الحرجة التي تتطلب تدخلاً',
    'project.projects.pageTitle': 'محفظة المشاريع',
    'project.projects.new': 'مشروع جديد',
    'project.projects.editTitle': 'تعديل المشروع',
    'project.projects.newTitle': 'مشروع جديد',
    'project.projects.generalInfo': 'المعلومات العامة',
    'project.projects.code': 'الرمز',
    'project.projects.title': 'عنوان المشروع',
    'project.projects.client': 'العميل / المستفيد',
    'project.projects.status': 'الحالة',
    'project.projects.priority': 'الأولوية',
    'project.projects.manager': 'مدير المشروع',
    'project.projects.startDate': 'تاريخ البداية',
    'project.projects.endDate': 'تاريخ الانتهاء المتوقع',
    'project.projects.budget': 'الميزانية الإجمالية',
    'project.projects.spent': 'المصروفات الفعلية',
    'project.projects.progress': 'نسبة التقدم',
    'project.projects.description': 'وصف وأهداف المشروع',
    'project.projects.statusPlanning': 'تخطيط',
    'project.projects.statusInProgress': 'قيد التنفيذ',
    'project.projects.statusOnHold': 'معلق',
    'project.projects.statusCompleted': 'مكتمل',
    'project.projects.statusCancelled': 'ملغى',
    'project.projects.priorityLow': 'منخفضة',
    'project.projects.priorityMedium': 'متوسطة',
    'project.projects.priorityHigh': 'عالية',
    'project.projects.priorityUrgent': 'عاجلة',
    'project.projects.confirmDelete': 'هل أنت متأكد من حذف هذا المشروع؟',
    'project.projects.deleteSuccess': 'تم حذف المشروع بنجاح.',
    'project.projects.updateSuccess': 'تم تحديث المشروع بنجاح.',
    'project.projects.createSuccess': 'تم إنشاء المشروع بنجاح.',
    'project.projects.titleRequired': 'عنوان المشروع مطلوب.',
    'project.projects.searchPlaceholder': 'بحث عن مشروع بالعنوان، العميل، المدير...',
    'project.projects.empty': 'لم يتم العثور على مشاريع. انقر فوق "مشروع جديد" للبدء.',
    'project.tasks.pageTitle': 'إدارة المهام',
    'project.tasks.new': 'مهمة جديدة',
    'project.tasks.editTitle': 'تعديل المهمة',
    'project.tasks.newTitle': 'مهمة جديدة',
    'project.tasks.details': 'تفاصيل المهمة',
    'project.tasks.code': 'الرمز',
    'project.tasks.project': 'المشروع المرتبط',
    'project.tasks.title': 'عنوان المهمة',
    'project.tasks.status': 'الحالة',
    'project.tasks.priority': 'الأولوية',
    'project.tasks.assignee': 'المعين له',
    'project.tasks.dueDate': 'الأجل المحدد',
    'project.tasks.due': 'الأجل',
    'project.tasks.estimatedHours': 'الساعات المقدرة',
    'project.tasks.actualHours': 'الساعات الفعلية',
    'project.tasks.progress': 'نسبة الإنجاز',
    'project.tasks.hours': 'مقدرة',
    'project.tasks.description': 'الوصف التفصيلي',
    'project.tasks.statusTodo': 'للقيام بها',
    'project.tasks.statusInProgress': 'قيد التنفيذ',
    'project.tasks.statusReview': 'قيد المراجعة',
    'project.tasks.statusDone': 'مكتملة',
    'project.tasks.statusBlocked': 'محظورة',
    'project.tasks.priorityLow': 'منخفضة',
    'project.tasks.priorityMedium': 'متوسطة',
    'project.tasks.priorityHigh': 'عالية',
    'project.tasks.priorityCritical': 'حرجة',
    'project.tasks.markIncomplete': 'تعيين كغير مكتملة',
    'project.tasks.markDone': 'تعيين كمكتملة',
    'project.tasks.confirmDelete': 'هل أنت متأكد من حذف هذه المهمة؟',
    'project.tasks.deleteSuccess': 'تم حذف المهمة بنجاح.',
    'project.tasks.updateSuccess': 'تم تحديث المهمة بنجاح.',
    'project.tasks.createSuccess': 'تم إنشاء المهمة بنجاح.',
    'project.tasks.titleRequired': 'عنوان المهمة مطلوب.',
    'project.tasks.projectRequired': 'يرجى اختيار المشروع المرتبط.',
    'project.tasks.searchPlaceholder': 'بحث عن مهمة بالعنوان، المشروع، المعين له...',
    'project.tasks.empty': 'لم يتم العثور على أي مهام. انقر فوق "مهمة جديدة" للبدء.',
    'project.milestones.pageTitle': 'المراحل والإنجازات الرئيسية',
    'project.milestones.new': 'مرحلة جديدة',
    'project.milestones.editTitle': 'تعديل المرحلة',
    'project.milestones.newTitle': 'مرحلة جديدة',
    'project.milestones.details': 'تفاصيل المرحلة',
    'project.milestones.code': 'الرمز',
    'project.milestones.project': 'المشروع المرتبط',
    'project.milestones.title': 'عنوان المرحلة',
    'project.milestones.date': 'التاريخ المستهدف',
    'project.milestones.status': 'الحالة',
    'project.milestones.description': 'وصف المرحلة',
    'project.milestones.statusPending': 'قيد الانتظار',
    'project.milestones.statusReached': 'تم تحقيقها',
    'project.milestones.statusDelayed': 'متأخرة',
    'project.milestones.markPending': 'تعيين كقيد الانتظار',
    'project.milestones.markReached': 'تعيين كتم تحقيقها',
    'project.milestones.confirmDelete': 'هل أنت متأكد من حذف هذه المرحلة؟',
    'project.milestones.deleteSuccess': 'تم حذف المرحلة بنجاح.',
    'project.milestones.updateSuccess': 'تم تحديث المرحلة بنجاح.',
    'project.milestones.createSuccess': 'تم إنشاء المرحلة بنجاح.',
    'project.milestones.titleRequired': 'عنوان المرحلة مطلوب.',
    'project.milestones.projectRequired': 'يرجى اختيار المشروع المرتبط.',
    'project.milestones.searchPlaceholder': 'بحث عن مرحلة بالعنوان، المشروع...',
    'project.milestones.empty': 'لم يتم العثور على مراحل. انقر فوق "مرحلة جديدة" لتحديد هدف رئيسي.',
    'project.team.pageTitle': 'فريق وموارد المشروع',
    'project.team.new': 'عضو جديد',
    'project.team.editTitle': 'تعديل بيانات العضو',
    'project.team.newTitle': 'عضو جديد',
    'project.team.details': 'بيانات عضو الفريق',
    'project.team.project': 'المشروع المخصص له',
    'project.team.name': 'الاسم الكامل',
    'project.team.role': 'الدور / الوظيفة',
    'project.team.email': 'البريد الإلكتروني',
    'project.team.allocation': 'نسبة التفرغ (%)',
    'project.team.confirmDelete': 'هل أنت متأكد من إزالة هذا العضو من المشروع؟',
    'project.team.deleteSuccess': 'تمت إزالة العضو بنجاح.',
    'project.team.updateSuccess': 'تم تحديث بيانات العضو بنجاح.',
    'project.team.createSuccess': 'تمت إضافة العضو بنجاح.',
    'project.team.nameRequired': 'اسم العضو مطلوب.',
    'project.team.projectRequired': 'يرجى اختيار المشروع المرتبط.',
    'project.team.searchPlaceholder': 'بحث عن عضو بالاسم، الدور، المشروع...',
    'project.team.empty': 'لم يتم تسجيل أي أعضاء في الفريق. انقر فوق "عضو جديد" لتخصيص مورد.',
  },
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Récupérer la langue sauvegardée ou utiliser 'fr' par défaut
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLang = localStorage.getItem('blobtic_language');
    return (savedLang as Language) || 'fr';
  });

  // Fonction pour changer la langue et la sauvegarder
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('blobtic_language', lang);

    // Configurer le sens de lecture (RTL pour l'arabe)
    const direction = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = direction;
    document.documentElement.lang = lang;
  };

  // Initialiser le sens de lecture au chargement
  useEffect(() => {
    const direction = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
  }, [language]);

  // Fonction de traduction avec support d'interpolation (positionnel {0}, nommé {name} et objets)
  const t = (key: string, ...args: any[]): string => {
    const langDict = translations[language] as Record<string, string>;
    let translation = langDict[key];

    // Fallback automatique pour alias commun.* -> common.*
    if (!translation && key.startsWith('commun.')) {
      translation = langDict['common.' + key.substring(7)];
    }
    if (!translation) {
      translation = key;
    }

    if (args.length > 0) {
      // 1. Si le premier argument est un objet de paramètres (ex: { name: 'Salle 1', code: 'REM10' })
      if (typeof args[0] === 'object' && args[0] !== null && !Array.isArray(args[0])) {
        const obj = args[0] as Record<string, any>;
        Object.keys(obj).forEach((paramKey) => {
          const val = obj[paramKey] !== undefined && obj[paramKey] !== null ? String(obj[paramKey]) : '';
          const regex = new RegExp(`\\{${paramKey}\\}`, 'g');
          translation = translation.replace(regex, val);
        });
        return translation;
      }

      // 2. Remplacement par index positionnel {0}, {1}, ...
      translation = translation.replace(/{(\d+)}/g, (match: string, index: number) => {
        return typeof args[index] !== 'undefined' && args[index] !== null ? String(args[index]) : match;
      });

      // 3. Si un seul argument scalaire est fourni (ex: t('key', formData.code)),
      //    remplacer également les placeholders nommés uniques du template ({code}, {name}, etc.)
      if (args.length === 1 && typeof args[0] !== 'object') {
        const val = args[0] !== undefined && args[0] !== null ? String(args[0]) : '';
        translation = translation.replace(/\{[a-zA-Z0-9_]+\}/g, val);
      }
    }

    return translation;
  };

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
